/* ------------------------------------------------------------------------
 * SentriPol analysis engine and report generators
 *
 * Lifted VERBATIM from the single-file build — not rewritten, not tidied.
 * The detector behaviour in here is the product, and it carries a long tail
 * of corrections derived from real policy paper: a London Market Reform
 * Contract stating cover as sums insured; triggers stated in a declarations
 * table; the rule that a limit is a multiple of 500 and at least $1,000, so a
 * policy number cannot be printed as a limit; a declarations schedule vetoing
 * its own policy wording. 1,053 assertions cover that behaviour and they only
 * keep covering it while the code stays byte-identical.
 *
 * Cross-module calls still go through the `window.*` registry the original
 * used. That is deliberate: rewiring ~190 functions into an import graph is a
 * separate change with its own risk, and this one moves the code and nothing
 * else. See MIGRATION.md.
 *
 * From index.html, script block 3 (13269 lines).
 * ---------------------------------------------------------------------- */

// ---- deferred DOM binding ------------------------------------------------
// The single-file build bound three handlers at parse time, against elements
// that were already in index.html. A module is imported before React has
// mounted anything, and under Node there is no DOM at all, so binding is
// queued here and performed by spAttachLegacyHandlers() once the form exists.
// The handler bodies are unchanged, including their use of `this`, which
// addEventListener still binds to the element.
const _spPendingHandlers = [];

/* Created at load, before anything can read it.
 *
 * The assembled sources are published so the panels that VERIFY a claim can
 * check it against the same evidence the classifiers used. The engine's
 * cross-module convention is the window registry, and registry values get read
 * as bare globals — where a bare read of a property that has never been set is
 * a ReferenceError, not undefined. That turns a panel which should degrade into
 * "Report Generation Error: _spSources is not defined", and it happens on
 * exactly the runs where the property was never assigned: any path that reaches
 * a renderer without having built sources first.
 *
 * Declaring it here costs nothing and removes the failure mode rather than the
 * symptom. Same reasoning applies to any future value published this way. */
if (typeof window !== 'undefined' && window._spSources === undefined) window._spSources = [];
function spOn(id, ev, fn) { _spPendingHandlers.push([id, ev, fn]); }
function spAttachLegacyHandlers() {
  let bound = 0;
  _spPendingHandlers.forEach(([id, ev, fn]) => {
    const el = typeof document !== 'undefined' && document.getElementById(id);
    if (!el) return;
    const flag = '_spBound_' + ev;
    if (el[flag]) return;
    el[flag] = 1;
    el.addEventListener(ev, fn);
    bound++;
  });
  return bound;
}


// ===================================================================
// SentriPol™ v2.23 — Trigger & Retro Date Verification (Section 5C)
// v2.12: Compact PDF Export — email-friendly optimized PDF generation (html2canvas + jsPDF)
// v2.17: EB Renewal Package & SBC Multi-Plan Extraction
// v2.11: EB Benefits Extraction Engine — replaces Best-in-Class with actual extracted coverages/limits
//        (carriers, deductibles, copays, premiums, plan types) from uploaded EB documents, matching P&C approach
// v2.10: Overhaul OCR limit extraction — handles bare numbers without $ prefix, split-$ tabular PDFs,
//        minimum value threshold to prevent false $100 from terrorism endorsements.
//        Fixes AmTrustCyber, PHLY, AXA XL/Indian Harbor declarations parsing.
// v2.9: Fix Section 5 vs Section 8 contradiction (MEETS in benchmarking but VERIFY in gap analysis),
//       Add BELOW STANDARD handling in gap analysis, BOP/WC page context filter for D&O deep scan
// v2.2: Added E&O / Professional Liability deep scan detection (35 indicator patterns, 12 exclusion patterns)
//       Cross-detection for E&O embedded in ELL, D&O, Management Liability, Package Policies
// ===================================================================

// ===== TOAST NOTIFICATION SYSTEM =====
function showToast(message, type) {
  const container = document.getElementById('toastContainer');
  const toast = document.createElement('div');
  toast.className = 'toast' + (type === 'error' ? ' toast-error' : type === 'warning' ? ' toast-warning' : '');
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 4000);
}

// ===== PDF TEXT EXTRACTION ENGINE =====
// Extracts text from PDF files page-by-page using pdf.js
// If a PDF is scanned (image-based with little/no text), automatically runs OCR via Tesseract.js
// Rebuild the LINES of a PDF page from where the text actually sits.
//
// This ran as `content.items.map(i => i.str).join(' ')` — every text item on a
// page joined with a space, so a 322-page policy came out of the browser with
// ZERO newlines in it. A declarations table, a forms schedule and a paragraph
// of prose all arrive as one enormous line.
//
// That is invisible until something needs structure, and then it is total.
// Every line-anchored reader in this engine is inert on that text: the forms
// schedule (which states the trigger), the coverage-block attribution behind
// retro dates, the property deductible rows, the applicability-list veto. On
// Aspen View the browser reported General Liability as Claims-Made with a
// retro date belonging to another coverage, while the same documents read
// through a layout-preserving extractor produced the correct answer — the
// difference was never the code, it was the shape of the text it was given.
//
// PDF.js gives each item a transform whose [4] is x and [5] is y. Grouping by
// y and sorting by x reconstructs the line the reader of the paper document
// sees, which is the thing every one of those heuristics was written against.
// One shape for every reader, whichever extractor produced the text.
//
// The browser goes to the BACKEND first for every PDF, and the backend runs
// LlamaParse, which returns markdown — tables as pipe rows, headings as
// "### **Name**", some tables as HTML. Every line-based reader below expects
// a printed row per line with whitespace between columns, which is what a
// reconstructed PDF.js page gives. spTableLines reduces both to that shape.
//
// It runs on a copy. The EB plan-table readers need the markdown grid intact
// and read the original text.
const spNorm = (t) => ((typeof window !== 'undefined' && window.spRetroAnchor)
  ? window.spRetroAnchor.spTableLines(t) : String(t || ''));

const SP_LINE_TOLERANCE = 2;   // points; a line is ~10pt, so this only merges jitter

function spItemsToText(items) {
  const cells = [];
  for (const it of (items || [])) {
    const str = it && it.str;
    if (!str || !str.trim()) continue;
    const tr = it.transform || [];
    cells.push({ y: Number(tr[5]) || 0, x: Number(tr[4]) || 0, str });
  }
  if (!cells.length) return '';
  // Cluster by y rather than rounding it. Cells of one table row are not
  // emitted at an identical baseline — on this policy "Coverage   Building"
  // and "Blanket Group 001" sit on the same printed line but differ by a
  // fraction of a point, so rounding to the nearest integer split them into
  // two lines and the pairing that says WHICH group covers the buildings was
  // lost. Clustering with a tolerance keeps a printed row together.
  cells.sort((a, b) => b.y - a.y);          // PDF y grows upward; the page reads downward
  const rows = [];
  let cur = [cells[0]], refY = cells[0].y;
  for (let i = 1; i < cells.length; i++) {
    if (Math.abs(cells[i].y - refY) <= SP_LINE_TOLERANCE) cur.push(cells[i]);
    else { rows.push(cur); cur = [cells[i]]; refY = cells[i].y; }
  }
  rows.push(cur);
  return rows
    .map(r => r.sort((a, b) => a.x - b.x).map(c => c.str).join(' ')
      .replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

if (typeof window !== 'undefined') window.spItemsToText = spItemsToText;

async function extractPdfText(file, progressCallback) {
  if (!file.name.toLowerCase().endsWith('.pdf')) return null;

  // BACKEND FIRST — for every PDF, not just scanned ones. Two reasons:
  //   1. pdf.js walks every page on the main thread to build the text layer,
  //      which locks the tab on a 150-200pp policy (and a BOP program is
  //      several of those).
  //   2. LlamaParse returns the declarations and forms schedule as markdown
  //      TABLES. The BOP CORE extraction reads that grid directly, so pdf.js
  //      text silently produces worse limits even when it doesn't freeze.
  // Results are cached server-side by content hash, so re-uploads are instant.
  // pdf.js below remains the fallback when the backend is unavailable.
  try {
    if (progressCallback) progressCallback(`Parsing ${file.name} on the backend (LlamaParse)…`);
    const res = (typeof window.spBackendExtract === 'function') ? await window.spBackendExtract(file, 15) : null;
    if (res && res.text && res.text.trim().length > 40) {
      console.log(`[extract] ${file.name}: backend ${res.engine || 'llamaparse'}, ${res.chars} chars, ${res.num_pages} pages`);
      if (progressCallback) progressCallback(`${file.name} parsed (${res.num_pages || '?'} pages).`);
      // LlamaParse marks every page boundary with a `---` rule, so the page
      // structure survives the markdown and can be given back.
      //
      // Returning the whole document as one page is why EVERY citation in this
      // report read "(p.1)" — a 322-page policy, and each finding cited to
      // page one. That is not a small cosmetic loss: a citation exists so a
      // broker can turn to the page and check, and "p.1" sends them to the
      // wrong place 321 times out of 322.
      //
      // Spot-checked against the real PDF: the retro date block lands on page
      // 187, the blanket schedule on 42 and the forms schedule on 5, matching
      // the paper exactly.
      //
      // Gated on the count agreeing with the PDF's own page count, within a
      // couple of pages for trailing blanks. Where it does not agree the whole
      // document stays on one page rather than carrying page numbers that
      // might be wrong — a citation that cannot be trusted is worse than one
      // that is obviously approximate.
      /* CAPTURED SO A HARNESS CAN READ WHAT PRODUCTION READ.
       *
       * Every check in this repository ran against text extracted locally,
       * which turned out not to be the shape the product receives — the
       * pagination differed, and with it four limits and a phantom coverage.
       * A harness that reads local extractions certifies the wrong thing.
       *
       * This holds the backend's own text in memory, where the parsed pages
       * already live, and adds no storage: window.spSaveParsed() writes it to
       * a file the harness can be pointed at. Nothing is sent anywhere and
       * nothing is written unless someone calls it. */
      if (typeof window !== 'undefined') {
        window.spParsedCapture = window.spParsedCapture || [];
        window.spParsedCapture.push({ fileName: file.name, engine: res.engine || 'llamaparse',
                                      num_pages: Number(res.num_pages) || 0,
                                      chars: Number(res.chars) || String(res.text).length,
                                      text: String(res.text) });
      }
      /* PAGES THE PARSER NUMBERED, WHERE THE BACKEND SENT THEM.
       *
       * With LLAMAPARSE_RESULT=json the backend returns `pages` — each one
       * numbered, with its markdown and with the tables the parser separated
       * into rows. Both facts are then known rather than inferred: the page
       * boundary does not have to be recovered from form feeds, markdown
       * rules or page footers, and a table does not have to be flattened and
       * its cells rebuilt from whitespace.
       *
       * `text` is still sent and still used by everything that reads a whole
       * document, so this is additive. Where `pages` is absent — the markdown
       * endpoint, pdftotext, the pypdf loop — nothing changes. */
      if (Array.isArray(res.pages) && res.pages.length) {
        const supplied = res.pages.map((p, i) => ({
          page: Number(p.page) || i + 1,
          text: p.md || p.text || '',
          tables: Array.isArray(p.tables) ? p.tables : [],
          printedPage: p.printed_page || null,
          backend: true
        }));
        if (typeof console !== 'undefined') {
          const tbl = supplied.reduce((n, p) => n + p.tables.length, 0);
          console.log(`[extract] ${file.name}: ${supplied.length} pages from the parser`
            + ` (${tbl} table${tbl === 1 ? '' : 's'} as rows) — no page or cell boundaries inferred`);
        }
        return { fileName: file.name, pages: supplied,
                 // Which parser produced this, carried so a later refusal can
                 // name it instead of guessing.
                 engine: res.engine || null,
                 totalPages: Number(res.num_pages) || supplied.length };
      }

      const _pag = spPaginateBackendText(res.text, res.num_pages);
      const pagesOut = _pag.pages;
      if (typeof console !== 'undefined') {
        console.log(`[extract] ${file.name}: ${pagesOut.length} pages inferred from the text (${_pag.how})`);
        if (_pag.how === 'footers') {
          console.log(`[extract] ${file.name}: page rules unusable — recovered ${pagesOut.length} pages from form footers (numbering approximate)`);
        } else if (_pag.how === 'one-page') {
          console.warn(`[extract] ${file.name}: NO USABLE PAGE BOUNDARIES. The whole document is one page, and every reader that works page by page will degrade. Limits from this file should not be trusted.`);
        }
      }
      return { fileName: file.name, pages: pagesOut,
               engine: res.engine || null,
               totalPages: Number(res.num_pages) || pagesOut.length };
    }
  } catch (be) {
    console.warn(`[extract] ${file.name}: backend parse failed, falling back to pdf.js:`, be && be.message);
    if (progressCallback) progressCallback(`Backend parse unavailable — reading ${file.name} in-browser…`);
  }

  if (typeof pdfjsLib === 'undefined') return null;


  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages = [];
    let totalTextChars = 0;
    const scannedPageIndices = []; // Track pages that need OCR

    // PASS 1: Extract text layer from all pages
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const text = spItemsToText(content.items);
      pages.push({ page: i, text: text });
      totalTextChars += text.trim().length;
      // A page with <30 chars of text is likely scanned
      if (text.trim().length < 30) {
        scannedPageIndices.push(i - 1); // 0-indexed
      }
    }

    // PASS 2: If >60% of pages are scanned, run OCR on those pages
    // v2.11: Removed pdf.numPages > 2 gate — screenshot-to-PDF files (1-2 pages) are
    // image-based and need OCR. For short PDFs with text layers, scannedRatio will be
    // low (0%) so they still skip OCR correctly.
    const scannedRatio = scannedPageIndices.length / pdf.numPages;
    const isScannedPdf = scannedRatio > 0.6;

    if (isScannedPdf) {
      // Prefer SERVER-side OCR — in-browser Tesseract freezes the tab on large
      // scans. Falls through to bounded browser OCR only if the backend fails.
      try {
        if (progressCallback) progressCallback(`Server OCR: extracting ${file.name} (scanned) — this runs on the backend...`);
        const res = (typeof window.spBackendExtract === 'function') ? await window.spBackendExtract(file, 15) : null;
        if (res && res.text && res.text.trim().length > 40) {
          console.log(`[extract] ${file.name}: server OCR ${res.ocr_pages}/${res.scanned_pages} scanned pages, ${res.chars} chars`);
          if (progressCallback) progressCallback(`Server OCR: ${file.name} done (${res.ocr_pages} pages).`);
          return { fileName: file.name, pages: [{ page: 1, text: res.text, ocr: true }], totalPages: res.num_pages || pdf.numPages };
        }
      } catch (be) {
        console.warn(`[extract] ${file.name}: server OCR failed, falling back to browser OCR:`, be && be.message);
      }
    }

    if (isScannedPdf && typeof Tesseract !== 'undefined') {
      if (progressCallback) progressCallback(`OCR: Initializing for ${file.name} (${scannedPageIndices.length} scanned pages)...`);
      console.log(`[OCR] ${file.name}: ${scannedPageIndices.length}/${pdf.numPages} pages are scanned — running OCR`);

      // Initialize Tesseract worker once for this PDF
      const worker = await Tesseract.createWorker('eng', 1, {
        logger: m => { /* suppress verbose logs */ }
      });

      // Smart page selection: OCR key pages (declarations, TOC, first/last sections)
      // For large PDFs (>50 pages), adaptively sample to cap at ~60-70 pages
      let pagesToOcr = [];
      if (pdf.numPages > 40) {
        // Large scanned PDF: OCR only a bounded, declarations-focused sample.
        // Deep sampling (60+ pages) rendered to canvas + Tesseract OOM-crashes the
        // browser tab; the declarations up front carry what classification/gap
        // analysis needs. Hard cap keeps memory bounded.
        const keyPages = new Set();
        for (let i = 0; i < Math.min(12, pdf.numPages); i++) keyPages.add(i);          // declarations / cover
        for (let i = Math.max(0, pdf.numPages - 4); i < pdf.numPages; i++) keyPages.add(i); // a few endorsement pages
        const step = Math.max(8, Math.floor(pdf.numPages / 6));                          // sparse middle probes
        for (let i = 12; i < pdf.numPages - 4; i += step) keyPages.add(i);
        pagesToOcr = [...keyPages].filter(i => scannedPageIndices.includes(i)).sort((a, b) => a - b).slice(0, 18); // hard cap
      } else {
        // Smaller PDFs: OCR the scanned pages, but still cap to keep memory bounded.
        pagesToOcr = [...scannedPageIndices].slice(0, 25);
      }

      // Helper: OCR a single page and store result
      async function ocrPage(worker, pdf, idx, pages, file, progressCallback, label) {
        try {
          const pg = await pdf.getPage(idx + 1);
          const vp = pg.getViewport({ scale: 1.5 });
          const cvs = document.createElement('canvas');
          const ctx = cvs.getContext('2d');
          cvs.width = vp.width;
          cvs.height = vp.height;
          await pg.render({ canvasContext: ctx, viewport: vp }).promise;
          // Per-page OCR timeout so one slow/hung page can't stall the whole run.
          const _rec = await Promise.race([
            worker.recognize(cvs),
            new Promise((_, rej) => setTimeout(() => rej(new Error('ocr page timeout')), 25000))
          ]);
          const ocrText = _rec.data.text;
          cvs.width = 0; cvs.height = 0;  // release canvas memory now — prevents tab OOM on large scanned PDFs
          if (ocrText && ocrText.trim().length > 20) {
            // Normalize OCR text: fix common dollar amount artifacts
            // - "g$" or "s$" → "$" (misread characters before dollar sign)
            // - "$ 5,000" keep as-is (handled by limit parser)
            // - Rejoin lines where a dollar amount was split: "$ 5\n,000" → "$ 5,000"
            // - OCR periods-as-commas: "$5.000" → "$5,000" (when . precedes exactly 3 digits)
            let cleaned = ocrText
              .replace(/[a-z]\$/gi, '$')  // remove stray letter before $
              .replace(/(\$\s*\d+)\s*\n\s*([,.]\d{3})/g, '$1$2') // rejoin split amounts
              .replace(/(\$\s*[\d,]*\d)\.(\d{3})(?=[^\d]|$)/g, '$1,$2'); // period→comma thousands
            pages[idx].text = cleaned;
            pages[idx].ocr = true;
          }
          return (pages[idx] && pages[idx].text) || ocrText || '';
        } catch (ocrErr) {
          console.warn(`[OCR] Failed on page ${idx + 1} of ${file.name}:`, ocrErr);
          return '';
        }
      }

      // PASS 2A: OCR sampled pages
      let ocrCount = 0;
      // Declarations PAGE detection: requires page to BE a declarations page,
      // not just reference "the Declarations" in body text. Must match a heading/title
      // pattern AND contain dollar amounts or limit-like content.
      function isDeclarationsPage(text) {
        if (!text || text.length < 50) return false;
        // Must have a heading-style declarations reference (all-caps or near title)
        const hasDecHeading = /(?:^|\n)\s*(?:COMMERCIAL\s+GENERAL\s+LIABILITY|PROPERTY|INLAND\s+MARINE|UMBRELLA|EXCESS|AUTO|WORKERS|EQUIPMENT|BUSINESS\s+OWNERS|PACKAGE)[\s\S]{0,40}DECLARATIONS/im.test(text)
          || /DECLARATIONS\s+PAGE/i.test(text)
          || /SCHEDULE\s+OF\s+(?:COVERAGES|FORMS|LIMITS)/i.test(text)
          || /COVERAGE\s+SUMMARY/i.test(text)
          || /POLICY\s+DECLARATIONS/i.test(text);
        // Must also have dollar amounts (actual limits, not just references)
        const hasDollarAmounts = (text.match(/\$\s*[\d,]{3,}/g) || []).length >= 2;
        // Must also have insured/policy number type content OR limit labels
        const hasDecContent = /(?:NAMED\s+INSURED|POLICY\s+(?:NUMBER|PERIOD)|EACH\s+OCCURRENCE|AGGREGATE|(?:PER|EACH)\s+CLAIM)/i.test(text);
        return hasDecHeading && (hasDollarAmounts || hasDecContent);
      }
      const decPages = []; // Track pages that ARE declarations pages

      for (const idx of pagesToOcr) {
        ocrCount++;
        if (progressCallback) progressCallback(`OCR: Processing ${file.name} \u2014 page ${idx + 1} of ${pdf.numPages} (${ocrCount}/${pagesToOcr.length})...`);
        const text = await ocrPage(worker, pdf, idx, pages, file, progressCallback, '');
        if (isDeclarationsPage(text)) {
          decPages.push(idx);
        }
      }

      // PASS 2B: Declarations-adjacent follow-up \u2014 OCR nearby pages we missed
      // Only triggers for pages that ARE declarations pages (structured limit tables)
      // Cap follow-up to prevent runaway scanning
      if (decPages.length > 0 && decPages.length <= 15) {
        const alreadyOcrd = new Set(pagesToOcr);
        const followUp = new Set();
        for (const dp of decPages) {
          for (let offset = -2; offset <= 3; offset++) {
            const neighbor = dp + offset;
            if (neighbor >= 0 && neighbor < pdf.numPages && !alreadyOcrd.has(neighbor) && scannedPageIndices.includes(neighbor)) {
              followUp.add(neighbor);
            }
          }
        }
        // Cap follow-up tightly — memory-bounded (was 30, which contributed to OOM)
        const followUpSorted = [...followUp].sort((a, b) => a - b).slice(0, 6);
        if (followUpSorted.length > 0) {
          console.log(`[OCR] ${file.name}: Found declarations on pages ${decPages.map(p=>p+1).join(',')} \u2014 OCR'ing ${followUpSorted.length} adjacent pages`);
          for (const idx of followUpSorted) {
            ocrCount++;
            if (progressCallback) progressCallback(`OCR: Scanning declarations area \u2014 page ${idx + 1} (${ocrCount} total)...`);
            await ocrPage(worker, pdf, idx, pages, file, progressCallback, '');
          }
        }
      } else if (decPages.length > 15) {
        console.log(`[OCR] ${file.name}: ${decPages.length} declarations-like pages found \u2014 skipping follow-up (already well-sampled)`);
      }

      await worker.terminate();
      if (progressCallback) progressCallback(`OCR: Completed ${file.name} (${ocrCount} pages processed)`);
      console.log(`[OCR] ${file.name}: Completed — ${ocrCount} pages OCR'd`);
    }

    return { fileName: file.name, pages: pages, totalPages: pdf.numPages };
  } catch (err) {
    console.warn('PDF extraction failed for', file.name, err);
    return null;
  }
}

// Extract text from all uploaded PDF files
async function extractAllPdfTexts(files, progressCallback) {
  // Bounded-concurrency pool. Sequential extraction of a multi-policy program
  // is what makes the progress bar look frozen — six uncached documents at
  // ~30s each is minutes of a stationary "Parsing…". Three at a time is the
  // measured sweet spot: more than that overwhelms the single-worker dev
  // backend and starts dropping documents to the in-browser fallback.
  const CONCURRENCY = 3;
  const pdfs = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
  const results = new Array(pdfs.length);
  let next = 0, done = 0;
  const inFlight = new Map();          // fileName -> start time

  // The old progress message only changed when a document FINISHED, so a
  // single large uncached document showed one stationary line for the whole
  // parse. A 50-page benefit guide took ~4 minutes on a cold LlamaParse job and
  // was reported as the app hanging. Tick every second with the file being
  // parsed and its elapsed time, so a slow parse is visibly a slow parse.
  const started = Date.now();
  let ticker = null;
  if (progressCallback && pdfs.length) {
    ticker = setInterval(() => {
      if (!inFlight.size) return;
      const now = Date.now();
      const oldest = [...inFlight.entries()].sort((a, b) => a[1] - b[1])[0];
      const secs = Math.round((now - oldest[1]) / 1000);
      const name = oldest[0].length > 42 ? oldest[0].slice(0, 39) + '…' : oldest[0];
      let msg = `Parsing ${name} — ${secs}s`;
      if (pdfs.length > 1) msg += ` (${done} of ${pdfs.length} done)`;
      // Past ~45s this is a cold cloud parse, not a stall. Say so.
      if (secs >= 45) msg += ' · first parse of a large document takes a few minutes; the result is cached, so re-runs are instant';
      progressCallback(msg);
    }, 1000);
  }

  async function worker() {
    while (true) {
      const i = next++;
      if (i >= pdfs.length) return;
      inFlight.set(pdfs[i].name, Date.now());
      try { results[i] = await extractPdfText(pdfs[i], null); }
      catch (e) { console.warn('[extract] failed:', pdfs[i].name, e && e.message); results[i] = null; }
      inFlight.delete(pdfs[i].name);
      // Record that this document really was parsed and what came out of it.
      // The upload chip otherwise judges a file by its FILENAME alone and
      // labels anything without a carrier naming convention "MANUAL REVIEW" —
      // including a 50-page benefits guide that parsed cleanly and was fully
      // analysed.
      if (results[i]) {
        const pgs = results[i].totalPages || (results[i].pages || []).length || 0;
        const chars = (results[i].pages || []).reduce((n, pg) => n + ((pg.text || '').length), 0);
        let covs = [];
        try { covs = Array.from(detectCoveragesFromPdfText([results[i]])); } catch (e) { covs = []; }
        window._parsedDocs = window._parsedDocs || {};
        window._parsedDocs[pdfs[i].name] = { pages: pgs, chars: chars, coverages: covs };
      }
      done++;
      if (progressCallback) progressCallback(`Parsed ${done} of ${pdfs.length} document(s)…`);
    }
  }
  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pdfs.length) }, worker));
  } finally {
    if (ticker) clearInterval(ticker);
  }
  if (pdfs.length) {
    console.info(`[extract] ${pdfs.length} document(s) in ${Math.round((Date.now() - started) / 1000)}s`);
    // Refresh the upload chips so they show what parsing found.
    try { if (typeof renderFileList === 'function') renderFileList(); } catch (e) {}
  }
  return results.filter(Boolean);
}

// ===== DEEP POLICY TEXT SCANNER =====
// Scans extracted PDF text for D&O language, coverage indicators, and exclusion patterns
// This catches D&O embedded in ELL/EPLI, Management Liability packages, etc.

const DO_TEXT_INDICATORS = [
  // Direct D&O language
  { pattern: /directors?\s+(and|&)\s+officers?/i, type: 'D&O Direct', weight: 10 },
  { pattern: /d\s*&\s*o\s+(liability|coverage|insurance)/i, type: 'D&O Direct', weight: 10 },
  { pattern: /management\s+liability/i, type: 'Management Liability', weight: 8 },
  // ELL / Educators Legal Liability with D&O language
  { pattern: /educators?\s+legal\s+liability/i, type: 'ELL', weight: 9 },
  { pattern: /educators?\s+wrongful\s+act/i, type: 'ELL-D&O', weight: 10 },
  // NOTE: "Insuring Agreement A" reduced from 7→3 — too generic, matches EPLI/GL insuring agreements too
  { pattern: /insuring\s+agreement\s+a/i, type: 'Insuring Agreement A', weight: 3 },
  // Board / officer / trustee language indicating D&O coverage
  { pattern: /directors?,?\s+officers?,?\s+(regents?|trustees?|school\s+board)/i, type: 'Board Coverage', weight: 10 },
  { pattern: /trustees?\s+(and|or|,)\s+(their\s+)?functional\s+equivalents?/i, type: 'Board Coverage', weight: 9 },
  { pattern: /board\s+member\s+liability/i, type: 'Board Coverage', weight: 9 },
  // NOTE: "duly elected" reduced from 9→4 — standard GL "Who Is An Insured" language commonly says
  // "duly elected or appointed director" without indicating actual D&O coverage
  { pattern: /duly\s+elected.{0,30}(directors?|officers?|trustees?)/i, type: 'Board Coverage', weight: 4 },
  // Public officials / entity coverage
  { pattern: /public\s+official(s)?\s+(and\s+)?management/i, type: 'Public Officials', weight: 8 },
  { pattern: /entity\s+coverage/i, type: 'Entity Coverage', weight: 7 },
  { pattern: /side\s+a\s+(dic|coverage|protection)/i, type: 'Side A', weight: 7 },
  // Fiduciary / executive liability
  { pattern: /executive\s+liability/i, type: 'Executive Liability', weight: 7 },
  { pattern: /fiduciary\s+liability/i, type: 'Fiduciary', weight: 6 },
  // Wrongful act definitions that cover D&O
  { pattern: /wrongful\s+act.{0,100}(breach\s+of\s+duty|misstatement|misleading)/i, type: 'Wrongful Act Def', weight: 8 },
  // NOTE: "wrongful act... director/officer" reduced from 9→4 — EPLI policies define "employment practices wrongful act"
  // and then list director/officer in the insured definitions; this is NOT D&O coverage
  { pattern: /wrongful\s+act.{0,100}(director|officer|board|trustee)/i, type: 'Wrongful Act Def', weight: 4 },
  // NOTE: "non-monetary relief" reduced from 7→3 — common in EPLI/GL policies, not specific to D&O
  { pattern: /non-?monetary\s+relief/i, type: 'D&O Provision', weight: 3 },
];

// Exclusion patterns that could void D&O coverage (fatal flaws)
const DO_EXCLUSION_PATTERNS = [
  { pattern: /exclud(e|es|ed|ing).{0,80}criminal\s+act/i, type: 'Criminal Acts Exclusion', severity: 'CRITICAL',
    note: 'May void D&O coverage for any claim alleging criminal conduct — even if unproven' },
  { pattern: /exclud(e|es|ed|ing).{0,80}dishonest(y)?/i, type: 'Dishonesty Exclusion', severity: 'HIGH',
    note: 'Could exclude claims involving alleged fraud or dishonesty by directors/officers' },
  { pattern: /exclud(e|es|ed|ing).{0,80}personal\s+profit/i, type: 'Personal Profit Exclusion', severity: 'HIGH',
    note: 'Excludes claims where directors/officers gained improper personal profit' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(bodily\s+injury|property\s+damage)/i, type: 'BI/PD Exclusion', severity: 'MODERATE',
    note: 'Standard D&O exclusion — verify GL provides adequate backstop' },
  { pattern: /exclud(e|es|ed|ing).{0,80}prior\s+(and|or)\s+pending/i, type: 'Prior/Pending Exclusion', severity: 'HIGH',
    note: 'Excludes claims related to prior or pending litigation — check retroactive date' },
  { pattern: /exclud(e|es|ed|ing).{0,80}insured\s+vs\.?\s+insured/i, type: 'Insured vs Insured Exclusion', severity: 'HIGH',
    note: 'Excludes claims by one insured against another — common in D&O, may limit internal disputes' },
  { pattern: /exclud(e|es|ed|ing).{0,80}regulatory/i, type: 'Regulatory Exclusion', severity: 'CRITICAL',
    note: 'May exclude regulatory proceedings — a major exposure for directors/officers' },
  { pattern: /exclud(e|es|ed|ing).{0,80}sexual\s+(abuse|molest|assault|misconduct)/i, type: 'Sexual Misconduct Exclusion', severity: 'CRITICAL',
    note: 'Fatal flaw if Sexual Abuse coverage appears to exist but is excluded here' },
  { pattern: /exclud(e|es|ed|ing).{0,80}employment\s+practice/i, type: 'Employment Practices Exclusion', severity: 'HIGH',
    note: 'D&O may exclude employment practices claims — verify separate EPLI coverage' },
  { pattern: /exclud(e|es|ed|ing).{0,80}wage\s+(and|&)\s+hour/i, type: 'Wage & Hour Exclusion', severity: 'HIGH',
    note: 'Wage & hour claims excluded — verify if EPLI covers this gap' },
  { pattern: /exclud(e|es|ed|ing).{0,80}antitrust/i, type: 'Antitrust Exclusion', severity: 'MODERATE',
    note: 'Antitrust/competition law claims excluded from D&O coverage' },
  { pattern: /exclud(e|es|ed|ing).{0,80}pollution/i, type: 'Pollution Exclusion', severity: 'MODERATE',
    note: 'Environmental/pollution claims excluded — verify separate environmental liability coverage' },
];


// ===== E&O / PROFESSIONAL LIABILITY DEEP TEXT SCANNER =====
// Mirrors the D&O deep scan approach — scans extracted PDF text for Professional Liability / E&O language
// Catches E&O embedded in ELL, D&O packages, Management Liability, Package Policies, etc.

const EO_TEXT_INDICATORS = [
  // Direct E&O / Professional Liability language
  { pattern: /errors?\s+(and|&)\s+omissions?/i, type: 'E&O Direct', weight: 10 },
  { pattern: /professional\s+liability/i, type: 'Professional Liability Direct', weight: 10 },
  { pattern: /professional\s+indemnity/i, type: 'Professional Indemnity', weight: 10 },
  { pattern: /e\s*&\s*o\s+(liability|coverage|insurance|policy)/i, type: 'E&O Direct', weight: 10 },
  { pattern: /professional\s+services\s+(liability|coverage|insurance)/i, type: 'Professional Services', weight: 9 },
  // Negligent act / wrongful act professional service definitions
  { pattern: /negligent\s+act[,\s]+(error|omission)/i, type: 'Negligent Act Definition', weight: 9 },
  { pattern: /negligent\s+(act|error|omission).{0,100}professional\s+services?/i, type: 'Negligent Act - Prof Svc', weight: 10 },
  { pattern: /wrongful\s+(act|error|omission).{0,100}professional/i, type: 'Wrongful Act - Prof', weight: 9 },
  { pattern: /wrongful\s+professional\s+act/i, type: 'Wrongful Prof Act', weight: 10 },
  // Insuring agreement language typical of E&O policies
  { pattern: /insuring\s+agreement.{0,200}(professional\s+services?|wrongful\s+act|negligent\s+act)/i, type: 'Insuring Agreement - E&O', weight: 9 },
  { pattern: /(claim|loss|damage).{0,80}(arising|resulting).{0,80}(professional\s+services?|rendering.{0,40}services?)/i, type: 'E&O Claim Trigger', weight: 8 },
  // Education-specific professional liability
  { pattern: /educators?\s+professional\s+liability/i, type: 'Educators Prof Liability', weight: 10 },
  { pattern: /educators?\s+e\s*&\s*o/i, type: 'Educators E&O', weight: 10 },
  { pattern: /school\s+(leaders?|administrators?)\s+professional/i, type: 'School Admin Prof Liability', weight: 9 },
  { pattern: /school\s+(leaders?|administrators?)\s+e\s*&\s*o/i, type: 'School Admin E&O', weight: 9 },
  { pattern: /educators?\s+legal\s+liability.{0,200}professional/i, type: 'ELL-E&O', weight: 9 },
  // Miscellaneous professional liability embedded in other policies
  { pattern: /professional\s+liability\s+endorsement/i, type: 'E&O Endorsement', weight: 8 },
  { pattern: /professional\s+liability\s+(coverage\s+)?part/i, type: 'E&O Coverage Part', weight: 8 },
  { pattern: /added\s+professional\s+liability/i, type: 'Added Prof Liability', weight: 8 },
  { pattern: /supplemental\s+professional\s+liability/i, type: 'Supplemental Prof Liability', weight: 8 },
  // Technology / Miscellaneous E&O
  { pattern: /technology\s+(?:errors?|e\s*&\s*o|professional)/i, type: 'Tech E&O', weight: 8 },
  { pattern: /miscellaneous\s+professional\s+liability/i, type: 'Misc Prof Liability', weight: 8 },
  // Consulting / advisory professional services
  { pattern: /consulting\s+professional\s+liability/i, type: 'Consulting E&O', weight: 8 },
  { pattern: /advisory\s+services?.{0,60}(liability|coverage)/i, type: 'Advisory E&O', weight: 7 },
  // Failure to perform / render professional services
  { pattern: /failure\s+to\s+(render|perform).{0,60}professional\s+services?/i, type: 'Failure to Render', weight: 9 },
  // Duty of care / standard of care
  { pattern: /(duty|standard)\s+of\s+care.{0,100}professional/i, type: 'Standard of Care', weight: 7 },
  { pattern: /breach\s+of\s+(duty|standard).{0,60}(professional|care)/i, type: 'Breach of Duty', weight: 8 },
  // Coverage limits language typical of E&O
  { pattern: /per\s+claim.{0,40}aggregate.{0,100}professional/i, type: 'E&O Limits', weight: 6 },
  { pattern: /claims[\-\s]made.{0,80}professional/i, type: 'Claims-Made E&O', weight: 7 },
  { pattern: /retroactive\s+date.{0,100}professional/i, type: 'Retro Date E&O', weight: 7 },
  // D&O policies that include E&O coverage grants
  { pattern: /management\s+liability.{0,200}professional\s+(services?|liability)/i, type: 'Mgmt Liability-E&O', weight: 8 },
  { pattern: /directors?.{0,40}officers?.{0,200}professional\s+(services?|liability)/i, type: 'D&O-E&O Bundle', weight: 8 },
  // Package / portfolio policies embedding E&O
  { pattern: /(package|portfolio|forefront).{0,200}professional\s+liability/i, type: 'Package-E&O', weight: 7 },
];

const EO_EXCLUSION_PATTERNS = [
  { pattern: /exclud(e|es|ed|ing).{0,80}bodily\s+injury.{0,80}professional/i, type: 'BI Exclusion on E&O', severity: 'MODERATE',
    note: 'Standard E&O exclusion — verify GL provides BI backstop for professional services' },
  { pattern: /exclud(e|es|ed|ing).{0,80}criminal\s+act.{0,80}(professional|e&o|errors)/i, type: 'Criminal Acts on E&O', severity: 'HIGH',
    note: 'Criminal acts exclusion could void professional liability for intentional acts alleged as professional misconduct' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(prior\s+(acts?|knowledge)|prior\s+and\s+pending)/i, type: 'Prior Acts / Knowledge', severity: 'HIGH',
    note: 'Prior acts / prior knowledge exclusion — verify retroactive date provides adequate tail' },
  { pattern: /exclud(e|es|ed|ing).{0,80}insured\s+vs\.?\s+insured.{0,80}(professional|e&o)?/i, type: 'Insured vs Insured on E&O', severity: 'MODERATE',
    note: 'Insured vs insured exclusion may limit coverage for internal disputes involving professional services' },
  { pattern: /exclud(e|es|ed|ing).{0,80}contractual\s+(liability|obligation).{0,80}(professional|e&o)?/i, type: 'Contractual Liability Exclusion', severity: 'HIGH',
    note: 'Contractual liability exclusion on E&O could void coverage for breach of service agreements, SLAs, and professional guarantees' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(intellectual\s+property|patent|copyright|trademark)/i, type: 'IP Exclusion on E&O', severity: 'MODERATE',
    note: 'IP exclusion on professional liability — may leave coverage gap for IP infringement in professional work product' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(data\s+breach|cyber|network\s+security)/i, type: 'Cyber Exclusion on E&O', severity: 'HIGH',
    note: 'Cyber/data breach exclusion on E&O may leave gap — verify standalone cyber liability covers professional technology failures' },
  { pattern: /exclud(e|es|ed|ing).{0,80}regulatory.{0,40}(proceeding|action|investigation)/i, type: 'Regulatory Exclusion on E&O', severity: 'HIGH',
    note: 'Regulatory proceedings exclusion on E&O — professionals facing license board or regulatory body actions may have no defense coverage' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(pollution|environmental)/i, type: 'Pollution Exclusion on E&O', severity: 'MODERATE',
    note: 'Pollution exclusion on E&O — environmental consultants or professionals with environmental exposure may face coverage gap' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(sexual\s+abuse|molestation|misconduct)/i, type: 'Sexual Misconduct on E&O', severity: 'CRITICAL',
    note: 'FATAL FLAW for education/healthcare — sexual misconduct exclusion on professional liability leaves critical gap' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(wage|salary|benefit|compensation).{0,40}(dispute|claim)/i, type: 'Employment Exclusion on E&O', severity: 'MODERATE',
    note: 'Employment compensation exclusion — verify EPLI covers this gap' },
  { pattern: /exclud(e|es|ed|ing).{0,80}(punitive|exemplary)\s+damages?/i, type: 'Punitive Damages Exclusion', severity: 'MODERATE',
    note: 'Punitive damages excluded from E&O — common but may leave exposure in jurisdictions that allow insurable punitive damages' },
];

// Section heading patterns for citation purposes
const SECTION_HEADING_PATTERNS = [
  /(?:section|part|article)\s+[IVXLCDM0-9]+[.:]/i,
  /(?:insuring\s+agreement|coverage\s+(?:form|part)|general\s+conditions|exclusions|definitions|conditions)/i,
  /(?:endorsement|rider|amendment|schedule)\s*(?:#|no\.?|number)?\s*[0-9]*/i,
];

function findNearestSection(text, matchIndex) {
  // Look backwards from the match position for the nearest section heading
  const preceding = text.substring(Math.max(0, matchIndex - 500), matchIndex);
  let bestSection = 'General Provisions';
  for (const sp of SECTION_HEADING_PATTERNS) {
    const matches = [...preceding.matchAll(new RegExp(sp.source, 'gi'))];
    if (matches.length > 0) {
      bestSection = matches[matches.length - 1][0].trim();
      break;
    }
  }
  return bestSection;
}

function deepScanForDO(pdfTexts) {
  const findings = {
    doDetected: false,
    doType: null,           // 'standalone', 'embedded_ell', 'embedded_mgmt', 'embedded_exec'
    doLocation: null,       // { fileName, page, section, context }
    indicators: [],         // all D&O indicator matches with locations
    exclusions: [],         // D&O-related exclusions found
    coverageTerms: [],      // key coverage terms found (limits, retentions, etc.)
  };

  if (!pdfTexts || pdfTexts.length === 0) return findings;

  let totalScore = 0;

  pdfTexts.forEach(pdfDoc => {
    pdfDoc.pages.forEach(pageData => {
      /* SCAN WHAT THE POLICY SAYS, NOT WHAT IT QUOTES.
       *
       * This scored its indicators against raw page text, with no test of
       * where on the page a phrase sat. Tulsa Honor Academy's report carried
       *
       *     D&O / Management Liability: Standalone D&O Policy
       *     Primary location: Excess Policy.PDF, Page 40, General Provisions
       *
       * on the strength of a parenthetical inside Terrorism Risk Insurance Act
       * notice text listing the lines the Act does not reach:
       *
       *     professional liability insurance (except Directors and Officers
       *     Liability); or farm owners multiple peril insurance.
       *
       * The package policy's own declarations state what was bought -- General
       * Liability $20,136 and Educators Legal Liability $17,633, summing to the
       * $37,769 total premium. There is no management liability coverage part.
       * A federal statute's carve-back was reported as a standalone policy, in
       * the section headed Embedded Coverage Detection, with no hedge.
       *
       * spOwnText blanks the furniture spans -- notices, underlying schedules,
       * forms schedules, exclusion sections -- so an indicator only scores
       * where the policy is speaking for itself. The ad-hoc page filters below
       * (isEPLIPage, isGLPage, ...) predate this and are left alone; they
       * discount a page by subject, which is a different question.
       */
      const text = spOwnText(pageData.text);
      const pageNum = pageData.page;

      // Scan for D&O indicators
      // Context filter: if this page is primarily an EPLI, GL, BOP, or WC page, discount non-Direct D&O indicators
      const isEPLIPage = /employment\s+practices/i.test(text);
      const isGLPage = /commercial\s+general\s+liability/i.test(text) || (/who\s+is\s+an\s+insured/i.test(text));
      const isBOPPage = /business\s*owners?/i.test(text) || /businessowners/i.test(text) || /\bBOP\b/.test(text) || /spectrum\s+business/i.test(text);
      const isWCPage = /workers?[\s']*s?\s*compensation/i.test(text) || /employers?\s+liability/i.test(text);
      const isNonDOPage = isEPLIPage || isGLPage || isBOPPage || isWCPage;
      
      DO_TEXT_INDICATORS.forEach(indicator => {
        const match = indicator.pattern.exec(text);
        if (match) {
          // Skip low-weight indicators on EPLI/GL pages — they're incidental mentions, not D&O coverage
          const effectiveWeight = (isNonDOPage && indicator.type !== 'D&O Direct') 
            ? Math.min(indicator.weight, 2) 
            : indicator.weight;
          totalScore += effectiveWeight;
          const section = findNearestSection(text, match.index);
          const context = text.substring(Math.max(0, match.index - 60), Math.min(text.length, match.index + match[0].length + 60)).trim();
          findings.indicators.push({
            type: indicator.type,
            weight: indicator.weight,
            page: pageNum,
            section: section,
            context: context,
            fileName: pdfDoc.fileName
          });

          // Track primary D&O location (highest weight match)
          if (!findings.doLocation || indicator.weight > (findings.doLocation.weight || 0)) {
            findings.doLocation = {
              fileName: pdfDoc.fileName,
              page: pageNum,
              section: section,
              context: context,
              weight: indicator.weight,
              type: indicator.type
            };
          }
        }
      });

      // Scan for D&O-related exclusions
      DO_EXCLUSION_PATTERNS.forEach(excl => {
        const match = excl.pattern.exec(text);
        if (match) {
          const section = findNearestSection(text, match.index);
          const context = text.substring(Math.max(0, match.index - 40), Math.min(text.length, match.index + match[0].length + 80)).trim();
          findings.exclusions.push({
            type: excl.type,
            severity: excl.severity,
            note: excl.note,
            page: pageNum,
            section: section,
            context: context,
            fileName: pdfDoc.fileName
          });
        }
      });
    });
  });

  // Determine if D&O is detected
  // Threshold raised to 20 (from 15) to reduce false positives from GL/EPLI language
  // that mentions directors/officers in standard insured definitions or employment practices wrongful acts.
  // A genuine D&O policy will have "directors and officers" (10) + management liability (8) or similar = 18+ easily.
  // Standard GL/EPLI with incidental director/officer mentions should score below 20.
  const types = findings.indicators.map(i => i.type);
  const hasDirectDO = types.includes('D&O Direct');
  
  if (hasDirectDO || totalScore >= 20) {
    findings.doDetected = true;
    // Classify the type
    if (hasDirectDO) {
      findings.doType = 'standalone';
    } else if (types.includes('ELL') || types.includes('ELL-D&O')) {
      findings.doType = 'embedded_ell';
    } else if (types.includes('Management Liability') || types.includes('Executive Liability')) {
      findings.doType = 'embedded_mgmt';
    } else if (types.includes('Board Coverage') || types.includes('Public Officials')) {
      findings.doType = 'embedded_board';
    } else {
      findings.doType = 'inferred';
    }
  } else if (totalScore >= 12) {
    // Partial — possible D&O, flag for review (raised from 8 to 12)
    findings.doDetected = true;
    findings.doType = 'possible';
  }

  return findings;
}

// Global store for deep scan results
let deepScanResults = null;

// ===== DEEP SCAN FOR E&O / PROFESSIONAL LIABILITY =====
let deepScanEOResults = null;

// A coverage part can be PRINTED on a schedule without being bought. The Base
// Power cyber policy lists
//
//     TECHNOLOGY E&O AND MEDIA LIABILITY COVERAGES   LIMIT     RETENTION
//     Q. Technology Errors and Omissions             N/A       N/A
//     R. Media Liability                             $3,000,000  $25,000
//
// and the scan read the coverage part being NAMED as evidence it exists —
// reporting a standalone Professional Liability policy for an insured whose
// schedule shows the part was not purchased. Media Liability on the next line
// carries a limit and a retention; Technology E&O carries neither.
//
// So a hit on a schedule row only counts when a live limit sits with it. This
// is the same rule the BOP detectors use: presence needs declarations-grade
// evidence — a live limit adjacent — not the coverage part appearing in a list.
const SP_DEAD_LIMIT = /\b(n\s*\/\s*a|not\s+(?:covered|purchased|applicable|included)|none|nil|\$?\s*0(?:\.00)?)\b/i;
const SP_LIVE_LIMIT = /\$\s*[1-9][\d,]{2,}/;

// Two more ways a policy names a coverage WITHOUT granting it, both of which
// the Base Power set demonstrates:
//
//   1. An EXCLUSION. The GL carries "EXCLUSION – ENGINEERS, ARCHITECTS OR
//      SURVEYORS PROFESSIONAL LIABILITY" and the standard professional
//      services exclusion. The words are there precisely because the coverage
//      is not.
//   2. A CARRIER'S LIST OF COVERAGE PARTS. The auto policy's notice
//      endorsements enumerate every part the carrier writes — "MEDICAL
//      PROFESSIONAL LIABILITY COVERAGE PART", "LIQUOR LIABILITY COVERAGE
//      PART" — which says what is available, not what was bought. Density is
//      what identifies it, the same test the BOP detectors use.
const SP_EXCLUSION_CONTEXT = /(exclusion|excluded|does\s+not\s+apply|shall\s+not\s+apply|we\s+will\s+not\s+pay|not\s+covered|no\s+coverage)/i;
// The carrier describing its OWN services to policyholders — "loss control
// services, at no charge to its Texas commercial automobile liability, general
// liability and professional liability policyholders" — names the line of
// business it writes, not what this insured bought.
const SP_CARRIER_MARKETING = /(at\s+no\s+(?:charge|cost)|policyholders|loss\s+control\s+services|risk\s+management\s+services|value[- ]added\s+services)/i;
// Limit-sharing boilerplate: "covered under either or both of the Contractors
// Pollution Liability and/or the Professional Liability Coverage Parts" names
// the parts that COULD attach to this form. The declarations say which did.
const SP_CONDITIONAL_PART = /(either\s+or\s+both|and\/or\s+the|if\s+attached|where\s+applicable|Coverage\s+Parts?\s+(?:shown|indicated|attached))/i;
// Employee Benefits Liability is a real grant, and a narrow one: negligent
// acts in ADMINISTERING the benefit program. Reporting it as professional
// liability overstates it; ignoring it misses coverage the insured has.
const SP_EBL_CONTEXT = /(employee\s+benefit\s+program|benefits?\s+administration|administration\s+of\s+your\s+.employee)/i;
// The "insured contract" definition carves out an architect's or engineer's
// professional services. It names the coverage in order to describe what an
// indemnity agreement may NOT pick up — a definition, not a grant.
const SP_DEFINITION_CONTEXT = /(insured\s+contract|assumes\s+liability\s+for|other\s+words\s+and\s+phrases)/i;
function spInCoveragePartList(text, at) {
  const win = text.slice(Math.max(0, at - 400), at + 400);
  return (win.match(/COVERAGE\s+PART/gi) || []).length >= 3;
}

// `line` is the text the match sits on. Returns null when the line is not a
// schedule row at all (ordinary policy prose is not a limit statement).
function spScheduleLimitState(line) {
  const l = String(line || '');
  // A schedule row: an item letter/number, a coverage name, then limit cells.
  if (!/^\s*(?:[A-Z]\.|\d+\.|Item\s+\d+)?\s*\S/.test(l)) return null;
  const tail = l.slice(Math.max(0, l.length - 60));
  if (SP_LIVE_LIMIT.test(tail)) return 'live';
  if (SP_DEAD_LIMIT.test(tail)) return 'dead';
  return null;                                   // not a limit line either way
}

function deepScanForEO(pdfTexts) {
  const findings = {
    eoDetected: false,
    eoType: null,           // 'standalone', 'embedded_ell', 'embedded_do', 'embedded_mgmt', 'embedded_pkg', 'endorsement'
    eoLocation: null,       // { fileName, page, section, context }
    indicators: [],         // all E&O indicator matches with locations
    exclusions: [],         // E&O-related exclusions found
    notInForce: [],         // coverage parts printed on a schedule with no limit
    eblDetected: false,     // Employee Benefits Liability — a narrow, real grant
  };

  if (!pdfTexts || pdfTexts.length === 0) return findings;

  let totalScore = 0;
  // Files whose schedule shows the E&O coverage part with no limit. Pages are
  // walked in order, so the declarations are seen before the form wording —
  // and the wording for a part that was NOT purchased must not then evidence
  // it. That wording is printed in every copy of the form whether the coverage
  // was bought or not.
  const deadForEO = new Set();

  pdfTexts.forEach(pdfDoc => {
    pdfDoc.pages.forEach(pageData => {
      /* SCAN WHAT THE POLICY SAYS, NOT WHAT IT QUOTES.
       *
       * This scored its indicators against raw page text, with no test of
       * where on the page a phrase sat. Tulsa Honor Academy's report carried
       *
       *     D&O / Management Liability: Standalone D&O Policy
       *     Primary location: Excess Policy.PDF, Page 40, General Provisions
       *
       * on the strength of a parenthetical inside Terrorism Risk Insurance Act
       * notice text listing the lines the Act does not reach:
       *
       *     professional liability insurance (except Directors and Officers
       *     Liability); or farm owners multiple peril insurance.
       *
       * The package policy's own declarations state what was bought -- General
       * Liability $20,136 and Educators Legal Liability $17,633, summing to the
       * $37,769 total premium. There is no management liability coverage part.
       * A federal statute's carve-back was reported as a standalone policy, in
       * the section headed Embedded Coverage Detection, with no hedge.
       *
       * spOwnText blanks the furniture spans -- notices, underlying schedules,
       * forms schedules, exclusion sections -- so an indicator only scores
       * where the policy is speaking for itself. The ad-hoc page filters below
       * (isEPLIPage, isGLPage, ...) predate this and are left alone; they
       * discount a page by subject, which is a different question.
       */
      const text = spOwnText(pageData.text);
      const pageNum = pageData.page;

      // Scan for E&O indicators
      EO_TEXT_INDICATORS.forEach(indicator => {
        const match = indicator.pattern.exec(text);
        if (match) {
          // The schedule row this sits on, if it is one.
          const lineStart = text.lastIndexOf('\n', match.index) + 1;
          let lineEnd = text.indexOf('\n', match.index);
          if (lineEnd < 0) lineEnd = text.length;
          const line = text.slice(lineStart, lineEnd);
          const limitState = spScheduleLimitState(line);
          if (limitState === 'dead') {
            // Printed, not purchased. Recorded so the report can SAY that —
            // it is a finding in its own right — but it earns no score.
            findings.notInForce.push({
              type: indicator.type,
              page: pageNum,
              fileName: pdfDoc.fileName,
              section: findNearestSection(text, match.index),
              line: line.replace(/\s+/g, ' ').trim().slice(0, 160)
            });
            deadForEO.add(pdfDoc.fileName);
            return;
          }
          // Named inside an exclusion, or inside the carrier's list of the
          // coverage parts it writes: neither is a grant.
          const around = text.slice(Math.max(0, match.index - 400), match.index + match[0].length + 400);
          const isExclusion = SP_EXCLUSION_CONTEXT.test(around);
          const isPartList = spInCoveragePartList(text, match.index) || SP_CONDITIONAL_PART.test(around);
          const isMarketing = SP_CARRIER_MARKETING.test(around);
          const isDefinition = SP_DEFINITION_CONTEXT.test(around);
          // ...unless the surrounding text is an Employee Benefits Liability
          // insuring agreement, which IS a grant and often sits near the word
          // "exclusion" simply because policy prose does.
          const isEBL = SP_EBL_CONTEXT.test(around) && /insuring\s+agreement|we\s+will\s+pay/i.test(around);
          // The form wording for a part the schedule showed as not purchased.
          const inForce = isEBL
            || (!deadForEO.has(pdfDoc.fileName) && !isExclusion && !isPartList
                && !isMarketing && !isDefinition);
          if (isEBL) findings.eblDetected = true;
          if (inForce) totalScore += indicator.weight;
          const section = findNearestSection(text, match.index);
          const context = text.substring(Math.max(0, match.index - 60), Math.min(text.length, match.index + match[0].length + 60)).trim();
          findings.indicators.push({
            type: indicator.type,
            weight: indicator.weight,
            page: pageNum,
            section: section,
            context: context,
            fileName: pdfDoc.fileName,
            inForce: inForce
          });

          // Track primary E&O location (highest weight match). Only a hit that
          // counts may be cited as the location of the coverage.
          // An EBL insuring agreement outranks any other hit as the place to
          // cite: it is the grant, the rest is vocabulary.
          const locWeight = indicator.weight + (isEBL ? 20 : 0);
          if (inForce && (!findings.eoLocation || locWeight > (findings.eoLocation.weight || 0))) {
            findings.eoLocation = {
              fileName: pdfDoc.fileName,
              page: pageNum,
              section: section,
              context: context,
              weight: locWeight,
              type: indicator.type
            };
          }
        }
      });

      // Scan for E&O-related exclusions
      EO_EXCLUSION_PATTERNS.forEach(excl => {
        const match = excl.pattern.exec(text);
        if (match) {
          const section = findNearestSection(text, match.index);
          const context = text.substring(Math.max(0, match.index - 40), Math.min(text.length, match.index + match[0].length + 80)).trim();
          findings.exclusions.push({
            type: excl.type,
            severity: excl.severity,
            note: excl.note,
            page: pageNum,
            section: section,
            context: context,
            fileName: pdfDoc.fileName
          });
        }
      });
    });
  });

  // Determine if E&O is detected (threshold: score >= 15 indicates strong E&O language)
  if (totalScore >= 15) {
    findings.eoDetected = true;
    const types = findings.indicators.filter(i => i.inForce !== false).map(i => i.type);
    if (findings.eblDetected) {
      // Narrow and specific: name it for what it is rather than promoting it
      // to professional liability the insured does not have.
      findings.eoType = 'embedded_ebl';
    } else if (types.includes('E&O Direct') || types.includes('Professional Liability Direct') || types.includes('Professional Indemnity')) {
      findings.eoType = 'standalone';
    } else if (types.includes('Educators Prof Liability') || types.includes('Educators E&O') || types.includes('ELL-E&O') || types.includes('School Admin Prof Liability')) {
      findings.eoType = 'embedded_ell';
    } else if (types.includes('D&O-E&O Bundle') || types.includes('Mgmt Liability-E&O')) {
      findings.eoType = 'embedded_do';
    } else if (types.includes('E&O Endorsement') || types.includes('Added Prof Liability') || types.includes('Supplemental Prof Liability')) {
      findings.eoType = 'endorsement';
    } else if (types.includes('Package-E&O')) {
      findings.eoType = 'embedded_pkg';
    } else if (types.includes('Negligent Act Definition') || types.includes('Negligent Act - Prof Svc') || types.includes('Wrongful Act - Prof') || types.includes('Wrongful Prof Act')) {
      findings.eoType = 'embedded_language';
    } else {
      findings.eoType = 'inferred';
    }
  } else if (findings.eblDetected) {
    // An Employee Benefits Liability insuring agreement is a coverage grant
    // even when it is the ONLY professional-liability-flavoured language in
    // the program — which is exactly how it appears: one insuring agreement
    // inside the GL policy, worth a single indicator. It must not fall through
    // to "possible", which reads as an unresolved maybe.
    findings.eoDetected = true;
    findings.eoType = 'embedded_ebl';
  } else if (totalScore >= 8) {
    findings.eoDetected = true;
    findings.eoType = 'possible';
  }

  return findings;
}



// ===== STATE DATA: ALL 50 STATES + DC =====
const STATE_PROFILES = {
  "AL": { name:"Alabama", abbr:"AL", primaryCatastrophe:"Hurricane / Tornado", severity:"HIGH", stateTrust:"Alabama Insurance Underwriting Association (AIUA)", trustRisk:"Moderate coastal wind exposure; limited availability in Mobile/Baldwin counties", historicalEvents:["Hurricane Ivan (2004)","Hurricane Sally (2020)","April 2011 Super Outbreak"], assessmentRisk:"Post-hurricane assessments possible for coastal policyholders", coverageConcerns:"Wind/hail deductibles, flood exclusions, tornado sub-limits", recommendation:"Verify wind/hail deductible percentages; confirm flood coverage for coastal operations" },
  "AK": { name:"Alaska", abbr:"AK", primaryCatastrophe:"Earthquake / Winter Storm", severity:"HIGH", stateTrust:"Alaska FAIR Plan", trustRisk:"Earthquake coverage critical; limited market availability in remote areas", historicalEvents:["1964 Great Alaska Earthquake (9.2)","2018 Anchorage Earthquake (7.1)"], assessmentRisk:"Earthquake surcharges common; limited carrier appetite", coverageConcerns:"Earthquake deductibles (10-20%), winter storm damage, remote location surcharges", recommendation:"Mandate earthquake coverage with reasonable deductibles; verify business interruption for weather delays" },
  "AZ": { name:"Arizona", abbr:"AZ", primaryCatastrophe:"Wildfire / Extreme Heat", severity:"MODERATE", stateTrust:"Arizona FAIR Plan", trustRisk:"Wildfire exposure increasing in WUI areas; dust storm risk in central regions", historicalEvents:["Wallow Fire (2011)","Telegraph Fire (2021)","Monsoon flooding events"], assessmentRisk:"Wildfire surcharges for properties in WUI zones", coverageConcerns:"Wildfire exclusions in high-risk zones, heat damage to infrastructure, monsoon flooding", recommendation:"Confirm wildfire coverage if near WUI zones; verify HVAC/infrastructure heat resilience coverage" },
  "AR": { name:"Arkansas", abbr:"AR", primaryCatastrophe:"Tornado / Flood", severity:"HIGH", stateTrust:"Arkansas FAIR Plan", trustRisk:"Tornado Alley proximity; significant flood risk along river systems", historicalEvents:["2011 Joplin Tornado path","2019 Arkansas River flooding","2014 Tornado outbreak"], assessmentRisk:"Moderate assessment risk for catastrophic wind events", coverageConcerns:"Tornado deductibles, flood zone determinations, hail damage sub-limits", recommendation:"Review wind/hail deductible structure; confirm NFIP or private flood for at-risk locations" },
  "CA": { name:"California", abbr:"CA", primaryCatastrophe:"Wildfire / Earthquake", severity:"EXTREME", stateTrust:"California FAIR Plan", trustRisk:"CRITICAL — FAIR Plan is insurer of last resort; massive exposure concentration. Non-renewal crisis ongoing; many carriers withdrawing from state entirely. FAIR Plan assessments a significant risk.", historicalEvents:["Camp Fire (2018) — $16.5B","Wine Country Fires (2017)","1994 Northridge Earthquake — $44B","Woolsey Fire (2018)","2020 August Complex Fire (largest in state history)","2025 Los Angeles Palisades Fire"], assessmentRisk:"EXTREME — FAIR Plan assessments can be substantial; earthquake authority assessments separate", coverageConcerns:"Wildfire non-renewals, earthquake sub-limits, business interruption adequacy, brush zone surcharges, utility-caused fire liability, mudslide/debris flow after fires", recommendation:"Urgently verify wildfire coverage status; confirm earthquake coverage through CEA or separate policy; review BI waiting periods; assess FAIR Plan as backstop only" },
  "CO": { name:"Colorado", abbr:"CO", primaryCatastrophe:"Wildfire / Hail", severity:"HIGH", stateTrust:"Colorado FAIR Plan", trustRisk:"Hailstorm capital of the US; WUI wildfire exposure in Front Range", historicalEvents:["Marshall Fire (2021) — $2B","2017 Colorado hailstorms — $2.3B","Black Forest Fire (2013)"], assessmentRisk:"High hail frequency drives elevated premiums statewide", coverageConcerns:"Hail damage deductibles, cosmetic damage exclusions, wildfire WUI exclusions, roof age limitations", recommendation:"Negotiate hail deductible percentages; confirm ACV vs RCV roof coverage; verify wildfire coverage for mountain operations" },
  "CT": { name:"Connecticut", abbr:"CT", primaryCatastrophe:"Hurricane / Winter Storm", severity:"MODERATE", stateTrust:"Connecticut FAIR Plan", trustRisk:"Coastal wind exposure; nor'easter frequency", historicalEvents:["Hurricane Sandy (2012)","Hurricane Irene (2011)","2011 October Snowstorm"], assessmentRisk:"Moderate coastal assessment potential", coverageConcerns:"Named storm deductibles for coastal properties, flood exclusions, aging infrastructure", recommendation:"Review named storm deductible triggers; verify flood coverage for coastal facilities" },
  "DE": { name:"Delaware", abbr:"DE", primaryCatastrophe:"Hurricane / Flood", severity:"MODERATE", stateTrust:"Delaware FAIR Plan", trustRisk:"Low-elevation flood risk; coastal storm exposure", historicalEvents:["Hurricane Sandy (2012)","Nor'easter events","Inland flooding"], assessmentRisk:"Low to moderate assessment risk", coverageConcerns:"Flood zone compliance, coastal wind deductibles, storm surge", recommendation:"Confirm flood zone designations; review wind/storm surge sub-limits" },
  "DC": { name:"District of Columbia", abbr:"DC", display:"District of Columbia (DC)", regulator:"DC Department of Insurance, Securities and Banking (DISB)", regulatorSite:"disb.dc.gov", primaryCatastrophe:"Hurricane / Flood", severity:"MODERATE", stateTrust:"DC Property Insurance Facility", trustRisk:"Urban flood risk from Potomac/Anacostia; federal facility proximity concerns", historicalEvents:["Hurricane Isabel (2003)","Derecho (2012)","Urban flooding events"], assessmentRisk:"Low assessment risk; federal backstop considerations", coverageConcerns:"Urban flood exposure, terrorism coverage (TRIA), aging building stock", recommendation:"Verify TRIA compliance; confirm flood coverage for low-lying properties; review building ordinance coverage" },
  "FL": { name:"Florida", abbr:"FL", primaryCatastrophe:"Hurricane", severity:"EXTREME", stateTrust:"Citizens Property Insurance Corporation", trustRisk:"CRITICAL — Citizens is the largest property insurer in FL with 1.2M+ policies. Massive assessment risk. If Citizens becomes insolvent after a major hurricane, ALL FL policyholders (including those with private carriers) face emergency assessments up to 45% of premium. Carrier withdrawals and insolvencies ongoing.", historicalEvents:["Hurricane Andrew (1992) — $27B","Hurricane Irma (2017) — $50B","Hurricane Michael (2018) — $25B","Hurricane Ian (2022) — $110B","Hurricane Milton (2024) — $30B+","2004 Four-Hurricane Season"], assessmentRisk:"EXTREME — Citizens assessments, Florida Hurricane Catastrophe Fund (FHCF) assessments, and Florida Insurance Guaranty Association (FIGA) assessments all possible simultaneously", coverageConcerns:"Named storm deductibles (2-10% of TIV), flood exclusions, loss of rents/BI adequacy, assignment of benefits (AOB) litigation, roof age restrictions, building code compliance, percentage deductibles vs flat deductibles, sinkhole coverage exclusions", recommendation:"Immediately verify hurricane deductible structure (percentage vs flat); confirm flood coverage separately; review Citizens exposure; assess carrier financial stability (Demotech ratings); verify roof condition compliance; review BI/loss of rents adequacy for 30+ day displacement" },
  "GA": { name:"Georgia", abbr:"GA", primaryCatastrophe:"Hurricane / Tornado", severity:"HIGH", stateTrust:"Georgia Underwriting Association", trustRisk:"Coastal hurricane exposure; inland tornado risk in spring", historicalEvents:["Hurricane Michael (2018)","2011 Tornado outbreak","2020 Hurricane Zeta"], assessmentRisk:"Moderate assessment risk for coastal events", coverageConcerns:"Wind/hail deductibles, coastal named storm triggers, tornado sub-limits", recommendation:"Review named storm deductible for coastal operations; verify wind/hail coverage inland" },
  "HI": { name:"Hawaii", abbr:"HI", primaryCatastrophe:"Hurricane / Volcanic Activity", severity:"HIGH", stateTrust:"Hawaii Property Insurance Association (HPIA)", trustRisk:"Hurricane exposure for all islands; volcanic risk on Big Island", historicalEvents:["Hurricane Iniki (1992)","2018 Kilauea eruption","Hurricane Lane (2018)"], assessmentRisk:"HPIA assessments possible; limited carrier market", coverageConcerns:"Hurricane deductibles, volcanic eruption exclusions, lava flow, tsunami, limited carrier options", recommendation:"Verify hurricane coverage adequacy; confirm volcanic/lava exclusions; review tsunami coverage availability" },
  "ID": { name:"Idaho", abbr:"ID", primaryCatastrophe:"Wildfire / Winter Storm", severity:"MODERATE", stateTrust:"Idaho FAIR Plan", trustRisk:"Wildfire risk in forested areas; winter storm damage in mountains", historicalEvents:["2020 Wildfire season","Winter storm events","Pioneer Fire (2016)"], assessmentRisk:"Low to moderate", coverageConcerns:"Wildfire coverage in rural areas, winter storm damage, distance from fire response", recommendation:"Verify wildfire coverage for properties near forests; confirm snow load provisions" },
  "IL": { name:"Illinois", abbr:"IL", primaryCatastrophe:"Tornado / Flood", severity:"MODERATE", stateTrust:"Illinois FAIR Plan", trustRisk:"Tornado corridor exposure; Mississippi/Illinois River flood risk", historicalEvents:["2013 Washington IL tornado","2019 Mississippi flooding","Derecho (2020)"], assessmentRisk:"Low to moderate assessment risk", coverageConcerns:"Tornado/wind deductibles, flood zone compliance, sewer backup", recommendation:"Confirm wind/hail deductible structure; verify flood and sewer backup coverage" },
  "IN": { name:"Indiana", abbr:"IN", primaryCatastrophe:"Tornado / Winter Storm", severity:"MODERATE", stateTrust:"Indiana FAIR Plan", trustRisk:"Tornado Alley fringe; winter storm frequency", historicalEvents:["2012 Henryville Tornado","Ice storms","2019 Memorial Day tornadoes"], assessmentRisk:"Low to moderate", coverageConcerns:"Wind/hail deductibles, equipment breakdown from ice storms, business interruption", recommendation:"Review wind/hail deductibles; verify equipment breakdown coverage for ice damage" },
  "IA": { name:"Iowa", abbr:"IA", primaryCatastrophe:"Tornado / Derecho", severity:"HIGH", stateTrust:"Iowa FAIR Plan", trustRisk:"Central US severe weather corridor; derecho risk", historicalEvents:["2020 Derecho — $11B","2008 Cedar Rapids flooding","Tornado outbreaks"], assessmentRisk:"Moderate post-derecho assessment pressure", coverageConcerns:"Wind/hail deductibles, crop-adjacent property damage, flood along rivers", recommendation:"Review derecho/straight-line wind coverage; verify wind/hail deductible levels post-2020" },
  "KS": { name:"Kansas", abbr:"KS", primaryCatastrophe:"Tornado / Hail", severity:"HIGH", stateTrust:"Kansas FAIR Plan", trustRisk:"Core Tornado Alley; highest tornado frequency in US", historicalEvents:["2007 Greensburg EF5 Tornado","2012 Wichita tornadoes","Annual severe hail events"], assessmentRisk:"Elevated premium environment; limited surplus lines options", coverageConcerns:"Tornado deductibles, hail damage frequency, crop/ag intersection, business interruption", recommendation:"Negotiate favorable wind/hail deductibles; verify BI covers extended rebuilding timelines in tornado-prone areas" },
  "KY": { name:"Kentucky", abbr:"KY", primaryCatastrophe:"Tornado / Flood", severity:"MODERATE", stateTrust:"Kentucky FAIR Plan", trustRisk:"Appalachian flood risk; tornado exposure in western KY", historicalEvents:["2021 Western KY Tornadoes","2022 Eastern KY Floods","Ice storms"], assessmentRisk:"Low to moderate", coverageConcerns:"Tornado sub-limits in western KY, flash flood exposure in eastern KY, mining area subsidence", recommendation:"Review tornado deductibles for western KY operations; verify flood coverage in Appalachian areas" },
  "LA": { name:"Louisiana", abbr:"LA", primaryCatastrophe:"Hurricane", severity:"EXTREME", stateTrust:"Louisiana Citizens Property Insurance Corporation", trustRisk:"CRITICAL — Carrier exodus post-2020/2021 hurricane seasons. Citizens growing rapidly. Assessment risk similar to FL. Multiple insolvencies since 2020.", historicalEvents:["Hurricane Katrina (2005) — $125B","Hurricane Laura (2020)","Hurricane Ida (2021) — $36B","Hurricane Delta (2020)","2016 Louisiana Floods","Hurricane Rita (2005)"], assessmentRisk:"EXTREME — Citizens assessments, LCPIC assessments, and Guaranty Fund assessments all possible", coverageConcerns:"Named storm deductibles (2-5%), flood exclusions, subsidence/settlement, levee failure, business interruption for extended outages, mold exclusions, additional living expense limits, MARITIME: Jones Act/MEL/USL&H compliance for waterfront employers, hull named storm exclusions, P&I pollution gaps, OPA 90 COFR requirements, offshore decommissioning financial assurance (BOEM), dredging subsurface utility exposure, marine cargo delay risks at Port of New Orleans/South Louisiana", recommendation:"Urgently verify named storm deductible structure; confirm flood coverage (NFIP + excess); review carrier solvency (check LDOI domestic carrier ratings); verify BI for 60+ day displacement scenarios; assess levee-proximity risk; FOR MARITIME OPERATIONS: verify Jones Act/USL&H/MEL compliance, confirm hull named windstorm coverage, review P&I pollution scope vs. OPA 90, verify BOEM financial assurance for offshore operators" },
  "ME": { name:"Maine", abbr:"ME", primaryCatastrophe:"Winter Storm / Nor'easter", severity:"MODERATE", stateTrust:"Maine FAIR Plan", trustRisk:"Coastal nor'easter exposure; ice dam and heavy snow risks", historicalEvents:["1998 Ice Storm","Nor'easter events","Coastal flooding"], assessmentRisk:"Low assessment risk", coverageConcerns:"Ice dam coverage, winter storm damage, coastal wind, frozen pipe exclusions", recommendation:"Verify frozen pipe/ice dam coverage; review nor'easter wind provisions" },
  "MD": { name:"Maryland", abbr:"MD", primaryCatastrophe:"Hurricane / Flood", severity:"MODERATE", stateTrust:"Maryland FAIR Plan / Maryland Joint Insurance Association", trustRisk:"Chesapeake Bay storm surge; coastal wind exposure", historicalEvents:["Hurricane Sandy (2012)","2016 Ellicott City floods","Hurricane Isabel (2003)"], assessmentRisk:"Low to moderate", coverageConcerns:"Coastal flood zones, storm surge, tidal flooding, wind/hail in eastern shore", recommendation:"Confirm flood zone designations; verify tidal/storm surge coverage for waterfront properties" },
  "MA": { name:"Massachusetts", abbr:"MA", primaryCatastrophe:"Hurricane / Nor'easter", severity:"MODERATE", stateTrust:"Massachusetts FAIR Plan", trustRisk:"Coastal wind and flood exposure; significant FAIR Plan usage in coastal areas", historicalEvents:["Hurricane Bob (1991)","2018 Merrimack Valley gas explosions","Nor'easter bomb cyclones"], assessmentRisk:"Moderate FAIR Plan assessment exposure", coverageConcerns:"Named storm deductibles, flood zone compliance, aging building stock, coastal erosion", recommendation:"Review named storm deductible triggers; verify flood coverage for coastal properties" },
  "MI": { name:"Michigan", abbr:"MI", primaryCatastrophe:"Winter Storm / Flood", severity:"MODERATE", stateTrust:"Michigan Basic Property Insurance Association", trustRisk:"Heavy snow and ice storm damage; Great Lakes flooding", historicalEvents:["2014 Polar Vortex","Detroit area flooding (2021)","Lake Michigan flooding"], assessmentRisk:"Low assessment risk", coverageConcerns:"Ice dam/frozen pipe coverage, sewer backup, Great Lakes erosion, winter storm BI", recommendation:"Confirm ice/frozen pipe provisions; verify sewer backup limits" },
  "MN": { name:"Minnesota", abbr:"MN", primaryCatastrophe:"Tornado / Winter Storm", severity:"MODERATE", stateTrust:"Minnesota FAIR Plan", trustRisk:"Northern tornado exposure; extreme winter conditions", historicalEvents:["2011 North Minneapolis Tornado","Polar Vortex events","Spring flooding"], assessmentRisk:"Low to moderate", coverageConcerns:"Wind/hail deductibles, extreme cold damage, spring flooding, snow load", recommendation:"Review wind/hail deductibles; verify cold weather equipment breakdown coverage" },
  "MS": { name:"Mississippi", abbr:"MS", primaryCatastrophe:"Hurricane / Tornado", severity:"EXTREME", stateTrust:"Mississippi Windstorm Underwriting Association (MWUA)", trustRisk:"Coastal hurricane and inland tornado exposure; limited market in southern counties", historicalEvents:["Hurricane Katrina (2005)","Hurricane Zeta (2020)","2011 Tornado Super Outbreak"], assessmentRisk:"High assessment risk for coastal wind events", coverageConcerns:"Wind-only coverage on coast, wind/water delineation disputes, tornado corridor", recommendation:"Verify wind/water coverage delineation; confirm coastal wind coverage source and deductible; review tornado provisions inland" },
  "MO": { name:"Missouri", abbr:"MO", primaryCatastrophe:"Tornado / Earthquake", severity:"HIGH", stateTrust:"Missouri FAIR Plan", trustRisk:"Tornado Alley exposure; New Madrid Seismic Zone earthquake risk", historicalEvents:["2011 Joplin EF5 Tornado — $2.8B","2019 Jefferson City Tornado","New Madrid earthquakes (1811-1812)"], assessmentRisk:"Moderate tornado assessment; earthquake exposure often excluded", coverageConcerns:"Tornado deductibles, New Madrid earthquake exclusions, flood along Missouri/Mississippi rivers", recommendation:"Urgently verify earthquake coverage (often excluded); review tornado deductible structure; confirm flood provisions" },
  "MT": { name:"Montana", abbr:"MT", primaryCatastrophe:"Wildfire / Winter Storm", severity:"MODERATE", stateTrust:"Montana FAIR Plan", trustRisk:"Expanding wildfire risk in WUI areas; extreme winter conditions", historicalEvents:["2017 Montana wildfires","Winter storm events","2021 fire season"], assessmentRisk:"Low to moderate", coverageConcerns:"Wildfire coverage in rural/WUI areas, winter storm damage, distance from services", recommendation:"Verify wildfire coverage for rural properties; confirm snow/ice damage provisions" },
  "NE": { name:"Nebraska", abbr:"NE", primaryCatastrophe:"Tornado / Hail", severity:"HIGH", stateTrust:"Nebraska FAIR Plan", trustRisk:"Central Tornado Alley; severe hail corridor", historicalEvents:["2004 Hallam EF4 tornado","Annual severe hail events","2019 Missouri River flooding"], assessmentRisk:"Moderate for wind/hail catastrophes", coverageConcerns:"Wind/hail deductibles, tornado sub-limits, agricultural exposure intersection", recommendation:"Review wind/hail deductible percentages; verify tornado business interruption adequacy" },
  "NV": { name:"Nevada", abbr:"NV", primaryCatastrophe:"Wildfire / Earthquake", severity:"MODERATE", stateTrust:"Nevada FAIR Plan", trustRisk:"WUI wildfire in Reno/Tahoe; minor seismic activity", historicalEvents:["2021 Caldor Fire threat to Tahoe","Flash floods in Las Vegas","Earthquake swarms"], assessmentRisk:"Low to moderate", coverageConcerns:"Wildfire near Tahoe/Reno, flash flood in Las Vegas, earthquake sub-limits", recommendation:"Verify wildfire coverage for northern NV properties; confirm flash flood coverage in southern NV" },
  "NH": { name:"New Hampshire", abbr:"NH", primaryCatastrophe:"Winter Storm / Flood", severity:"LOW", stateTrust:"New Hampshire FAIR Plan", trustRisk:"Moderate winter storm exposure; localized flooding", historicalEvents:["Ice storms","Spring river flooding","2017 October windstorm"], assessmentRisk:"Low", coverageConcerns:"Ice dam/frozen pipe, spring flooding, wind damage in mountains", recommendation:"Verify winter weather provisions; confirm flood coverage for river-adjacent properties" },
  "NJ": { name:"New Jersey", abbr:"NJ", primaryCatastrophe:"Hurricane / Flood", severity:"HIGH", stateTrust:"New Jersey FAIR Plan / NJ Property-Liability Insurance Guaranty Association", trustRisk:"Coastal flood and wind exposure; significant FAIR Plan usage on shore", historicalEvents:["Hurricane Sandy (2012) — $30B NJ","Hurricane Irene (2011)","Ida remnants flooding (2021)"], assessmentRisk:"Moderate to high post-Sandy assessment environment", coverageConcerns:"Named storm deductibles, flood zone compliance, coastal erosion, inland flooding from remnant storms", recommendation:"Verify flood zone designations and coverage; review named storm deductible structure; confirm coverage for inland flooding" },
  "NM": { name:"New Mexico", abbr:"NM", primaryCatastrophe:"Wildfire / Flood", severity:"MODERATE", stateTrust:"New Mexico FAIR Plan", trustRisk:"Wildfire risk in forested mountains; flash flood in desert areas", historicalEvents:["Hermits Peak/Calf Canyon Fire (2022)","Las Conchas Fire (2011)","Flash flood events"], assessmentRisk:"Low to moderate", coverageConcerns:"Wildfire in mountain communities, flash flood, drought-related subsidence", recommendation:"Verify wildfire coverage for mountain properties; confirm flash flood provisions" },
  "NY": { name:"New York", abbr:"NY", primaryCatastrophe:"Hurricane / Flood", severity:"HIGH", stateTrust:"New York Property Insurance Underwriting Association (NYPIUA)", trustRisk:"Coastal hurricane exposure (NYC/Long Island); urban flooding; significant FAIR Plan usage", historicalEvents:["Hurricane Sandy (2012) — $19B NY","Hurricane Irene (2011)","2021 Ida remnant flooding"], assessmentRisk:"Moderate to high for coastal events", coverageConcerns:"Named storm deductibles, flood zone compliance, coastal wind, terrorism (TRIA for NYC), building ordinance", recommendation:"Verify TRIA for NYC properties; review flood and named storm deductibles; confirm building ordinance coverage for older structures" },
  "NC": { name:"North Carolina", abbr:"NC", primaryCatastrophe:"Hurricane / Flood", severity:"HIGH", stateTrust:"North Carolina Insurance Underwriting Association (NC Wind Pool)", trustRisk:"Coastal hurricane and inland flooding; significant wind pool usage on Outer Banks", historicalEvents:["Hurricane Florence (2018) — $24B","Hurricane Matthew (2016)","Hurricane Dorian (2019)"], assessmentRisk:"Moderate to high for coastal wind events", coverageConcerns:"Named storm deductibles, inland flooding from stalled hurricanes, wind pool limitations, mountain mudslides", recommendation:"Review wind pool coverage if coastal; verify inland flood coverage; confirm named storm deductible structure" },
  "ND": { name:"North Dakota", abbr:"ND", primaryCatastrophe:"Flood / Winter Storm", severity:"MODERATE", stateTrust:"North Dakota FAIR Plan", trustRisk:"Red River spring flooding; extreme winter conditions", historicalEvents:["2009 Red River Flood","2011 Souris River Flood","Blizzard events"], assessmentRisk:"Low", coverageConcerns:"Spring flood coverage, extreme cold damage, snow load on roofs", recommendation:"Verify flood coverage for Red River basin; confirm cold weather equipment provisions" },
  "OH": { name:"Ohio", abbr:"OH", primaryCatastrophe:"Tornado / Winter Storm", severity:"MODERATE", stateTrust:"Ohio FAIR Plan", trustRisk:"Tornado risk in western OH; winter storm and ice damage", historicalEvents:["2019 Dayton Tornado Outbreak","Ice storms","Ohio River flooding"], assessmentRisk:"Low to moderate", coverageConcerns:"Wind/hail deductibles, ice storm equipment damage, river flooding", recommendation:"Review wind/hail deductibles; verify flood coverage near rivers" },
  "OK": { name:"Oklahoma", abbr:"OK", primaryCatastrophe:"Tornado / Earthquake", severity:"EXTREME", stateTrust:"Oklahoma Property and Casualty Insurance Company", trustRisk:"CRITICAL — Heart of Tornado Alley. Induced seismicity from oil/gas wastewater injection has dramatically increased earthquake risk since 2009. Dual tornado + earthquake exposure is nearly unique nationally.", historicalEvents:["2013 Moore EF5 Tornado — $2B","2013 El Reno EF5 (widest tornado ever)","1999 Bridge Creek-Moore F5","2011 Prague Earthquake (5.7)","2016 Pawnee Earthquake (5.8)","Annual severe hail events"], assessmentRisk:"HIGH — Tornado losses frequent; earthquake insurance market rapidly evolving", coverageConcerns:"Tornado deductibles, induced seismicity earthquake exclusions, hail frequency, safe room requirements, business interruption for tornado damage (extended rebuilding), oil/gas pollution liability intersection", recommendation:"Urgently verify earthquake coverage (many policies now exclude induced seismicity); review tornado deductible structure (percentage vs flat); confirm hail deductible levels; verify BI covers 6+ month rebuilding; assess safe room provisions" },
  "OR": { name:"Oregon", abbr:"OR", primaryCatastrophe:"Wildfire / Earthquake", severity:"HIGH", stateTrust:"Oregon FAIR Plan", trustRisk:"Cascadia Subduction Zone mega-earthquake risk; increasing wildfire in eastern OR", historicalEvents:["2020 Oregon Wildfires (Labor Day)","Cascadia Subduction Zone threat","2021 Heat Dome"], assessmentRisk:"Moderate for wildfire; extreme potential for Cascadia event", coverageConcerns:"Wildfire coverage availability, Cascadia earthquake deductibles, volcanic eruption (Mt. Hood)", recommendation:"Verify earthquake coverage for Cascadia risk; confirm wildfire coverage in eastern/southern OR; review volcanic exclusions" },
  "PA": { name:"Pennsylvania", abbr:"PA", primaryCatastrophe:"Flood / Winter Storm", severity:"MODERATE", stateTrust:"Pennsylvania FAIR Plan", trustRisk:"Inland flooding significant; winter storm ice damage", historicalEvents:["Hurricane Ida remnants (2021)","2011 Susquehanna River flooding","Ice storms"], assessmentRisk:"Low to moderate", coverageConcerns:"Flood zone compliance, sewer backup, ice storm damage, aging infrastructure", recommendation:"Verify flood coverage for river valleys; confirm sewer backup limits; review building ordinance coverage" },
  "RI": { name:"Rhode Island", abbr:"RI", primaryCatastrophe:"Hurricane / Flood", severity:"MODERATE", stateTrust:"Rhode Island FAIR Plan", trustRisk:"Coastal hurricane exposure; limited geography concentrates risk", historicalEvents:["1938 New England Hurricane","Hurricane Bob (1991)","2010 Providence flooding"], assessmentRisk:"Low to moderate", coverageConcerns:"Named storm deductibles, coastal flood, small-state concentration risk", recommendation:"Review named storm deductible triggers; verify flood coverage for coastal properties" },
  "SC": { name:"South Carolina", abbr:"SC", primaryCatastrophe:"Hurricane / Flood", severity:"HIGH", stateTrust:"South Carolina Wind & Hail Underwriting Association", trustRisk:"Coastal hurricane exposure; inland flooding from stalled storms", historicalEvents:["Hurricane Hugo (1989)","2015 SC Flood Event","Hurricane Florence (2018)","Hurricane Matthew (2016)"], assessmentRisk:"Moderate to high for coastal wind events", coverageConcerns:"Named storm deductibles, wind pool limitations, inland flooding, coastal erosion", recommendation:"Review wind pool coverage for coastal properties; verify inland flood coverage; confirm named storm deductible percentage" },
  "SD": { name:"South Dakota", abbr:"SD", primaryCatastrophe:"Tornado / Hail", severity:"MODERATE", stateTrust:"South Dakota FAIR Plan", trustRisk:"Moderate tornado/hail corridor; winter storm exposure", historicalEvents:["2003 Manchester SD tornado","Severe hail events","Blizzards"], assessmentRisk:"Low to moderate", coverageConcerns:"Wind/hail deductibles, winter storm damage, limited contractor availability for repairs", recommendation:"Review wind/hail deductibles; verify winter storm provisions" },
  "TN": { name:"Tennessee", abbr:"TN", primaryCatastrophe:"Tornado / Flood", severity:"HIGH", stateTrust:"Tennessee FAIR Plan", trustRisk:"Tornado risk statewide; flash flooding in Nashville basin and eastern TN", historicalEvents:["2020 Nashville Tornado","2010 Nashville Flood — $2B","2021 Waverly Flash Flood"], assessmentRisk:"Moderate for tornado/flood events", coverageConcerns:"Tornado deductibles, flash flood exclusions, sewer backup, landslide in eastern TN", recommendation:"Verify tornado deductible structure; confirm flood and sewer backup coverage; review BI for tornado displacement" },
  "TX": { name:"Texas", abbr:"TX", primaryCatastrophe:"Hurricane / Tornado / Hail", severity:"EXTREME", stateTrust:"Texas Windstorm Insurance Association (TWIA)", trustRisk:"CRITICAL — TWIA is the wind insurer of last resort for 14 coastal TX counties. $5.6B+ in exposure. Assessment risk to all TX policyholders. Post-Harvey market hardening continues. North TX hail frequency among highest nationally.", historicalEvents:["Hurricane Harvey (2017) — $125B","2021 Winter Storm Uri — $80B+","2019 Imelda Flooding","Hurricane Ike (2008) — $30B","2016 TX Hailstorms — $3.5B","2011 Bastrop Complex Fire","2023 Permian Basin Induced Seismicity Events","TX City Refinery Explosion (2005) — $1.5B+","Deepwater Horizon Gulf Impact (2010)"], assessmentRisk:"EXTREME — TWIA assessments can affect ALL TX property policyholders; Uri demonstrated freeze risk; energy sector adds environmental & industrial catastrophe exposure", coverageConcerns:"Named storm deductibles (coastal), wind/hail percentage deductibles (North TX), TWIA coverage gaps (no flood/BI), freeze damage exclusions post-Uri, water damage sub-limits, equipment breakdown from power grid failure, cosmetic hail damage exclusions. ENERGY-SPECIFIC: Pollution Legal Liability (PLL/EIL) for E&P operations — CGL sudden-and-accidental limitation insufficient for oilfield gradual pollution; Control of Well / OEE seepage & pollution sublimits; NORM (Naturally Occurring Radioactive Material) exposure liability; Underground Resource Damage / subsurface trespass from hydraulic fracturing (frac hits); RRC P-5/P-5PB bonding and well plugging financial assurance; MSA contractual liability under TX Oilfield Anti-Indemnity Act (§127); Pipeline operator PHMSA compliance; Refinery/petrochemical BI exposure along Houston Ship Channel and Gulf Coast corridor; Induced seismicity liability from saltwater disposal wells; Produced water disposal and TCEQ environmental compliance", recommendation:"Verify TWIA WPI-8 certification if coastal; review wind/hail deductible percentages (2-5% common in North TX); confirm freeze/cold weather coverage post-Uri; verify equipment breakdown for grid failure; review water damage sub-limits and exclusions. FOR ENERGY OPERATIONS: Verify standalone PLL/EIL (not just CGL pollution exception); confirm Control of Well / OEE with adequate seepage & pollution limits; verify NORM liability carve-back to nuclear exclusion; review underground resource damage coverage; confirm RRC bonding compliance (P-5/P-5PB); audit MSA indemnity clauses against TX Anti-Indemnity Act; verify pipeline PHMSA coverage; confirm refinery/petrochemical BI with adequate indemnity periods" },
  "UT": { name:"Utah", abbr:"UT", primaryCatastrophe:"Earthquake / Wildfire", severity:"MODERATE", stateTrust:"Utah FAIR Plan", trustRisk:"Wasatch Fault earthquake risk; WUI wildfire in mountain areas", historicalEvents:["2020 Magna Earthquake (5.7)","Wildfire seasons","Great Salt Lake environmental risk"], assessmentRisk:"Low to moderate", coverageConcerns:"Earthquake deductibles along Wasatch Front, wildfire in WUI, subsidence", recommendation:"Verify earthquake coverage with reasonable deductibles; confirm wildfire coverage for mountain properties" },
  "VT": { name:"Vermont", abbr:"VT", primaryCatastrophe:"Flood / Winter Storm", severity:"MODERATE", stateTrust:"Vermont FAIR Plan", trustRisk:"River valley flooding; winter storm damage", historicalEvents:["Hurricane Irene (2011) — devastating VT floods","2023 July flooding","Ice storms"], assessmentRisk:"Low", coverageConcerns:"Flood coverage in river valleys, ice dam/frozen pipe, bridge/road washout affecting access", recommendation:"Verify flood coverage for river-adjacent properties; confirm winter weather provisions" },
  "VA": { name:"Virginia", abbr:"VA", primaryCatastrophe:"Hurricane / Flood", severity:"MODERATE", stateTrust:"Virginia FAIR Plan", trustRisk:"Coastal exposure in Hampton Roads; inland flooding in Appalachian areas", historicalEvents:["Hurricane Isabel (2003)","2018 tidal flooding","Appalachian flooding events"], assessmentRisk:"Low to moderate", coverageConcerns:"Coastal flood/wind in Hampton Roads, tidal flooding, inland flash flooding", recommendation:"Review flood coverage for Hampton Roads; verify tidal flood provisions; confirm Appalachian flood coverage" },
  "WA": { name:"Washington", abbr:"WA", primaryCatastrophe:"Earthquake / Wildfire", severity:"HIGH", stateTrust:"Washington FAIR Plan", trustRisk:"Cascadia Subduction Zone mega-earthquake and volcanic risk; eastern WA wildfire", historicalEvents:["1949 Olympia Earthquake","2020 wildfire season","Mount St. Helens (1980)"], assessmentRisk:"Moderate; extreme potential for Cascadia event", coverageConcerns:"Cascadia earthquake deductibles, volcanic eruption exclusions, wildfire in eastern WA, lahar zones", recommendation:"Verify earthquake coverage for Cascadia risk; review volcanic and lahar exclusions; confirm wildfire coverage for eastern WA" },
  "WV": { name:"West Virginia", abbr:"WV", primaryCatastrophe:"Flood / Winter Storm", severity:"MODERATE", stateTrust:"West Virginia FAIR Plan", trustRisk:"Flash flooding in narrow valleys; ice storm damage", historicalEvents:["2016 WV Floods","Ice storms","Appalachian flooding"], assessmentRisk:"Low", coverageConcerns:"Flash flood coverage, mine subsidence, landslide, ice storm damage", recommendation:"Verify flood coverage; review mine subsidence provisions if applicable" },
  "WI": { name:"Wisconsin", abbr:"WI", primaryCatastrophe:"Tornado / Winter Storm", severity:"MODERATE", stateTrust:"Wisconsin FAIR Plan", trustRisk:"Moderate tornado exposure; significant winter storm frequency", historicalEvents:["2005 Stoughton tornado","Polar Vortex events","Spring flooding"], assessmentRisk:"Low to moderate", coverageConcerns:"Wind/hail deductibles, ice dam/frozen pipe, spring flooding", recommendation:"Review wind/hail deductibles; verify winter weather provisions and flood coverage" },
  "WY": { name:"Wyoming", abbr:"WY", primaryCatastrophe:"Wildfire / Winter Storm", severity:"LOW", stateTrust:"Wyoming FAIR Plan", trustRisk:"WUI wildfire in limited areas; extreme winter conditions", historicalEvents:["Wildfire seasons","Blizzard events","Wind events"], assessmentRisk:"Low", coverageConcerns:"Wildfire in WUI, extreme wind damage, winter storm", recommendation:"Verify wildfire coverage for WUI properties; confirm wind and winter storm provisions" }
};

// State name-to-abbreviation mapping
const STATE_NAME_MAP = {};
Object.values(STATE_PROFILES).forEach(s => {
  STATE_NAME_MAP[s.name.toUpperCase()] = s.abbr;
  STATE_NAME_MAP[s.abbr] = s.abbr;
});
// Handle DC alternate names
STATE_NAME_MAP["DISTRICT OF COLUMBIA"] = "DC";
STATE_NAME_MAP["WASHINGTON DC"] = "DC";
STATE_NAME_MAP["WASHINGTON D.C."] = "DC";
STATE_NAME_MAP["D.C."] = "DC";

/* Washington, DC and the District of Columbia are one jurisdiction (see
 * locations.js). These are the engine's ends of that rule: one code per
 * jurisdiction in any list, and "District of Columbia (DC)" wherever a
 * report names it. */
function spCanonStates(states) {
  const one = (typeof window !== 'undefined' && window.spOneRecordPerJurisdiction) || (l => Array.from(new Set(l)));
  return one((states || []).filter(Boolean));
}
function spStateDisplayName(abbr) {
  const sp = STATE_PROFILES[abbr];
  return sp ? (sp.display || sp.name) : abbr;
}

// ===== COVERAGE DETECTION PATTERNS =====
const COVERAGE_PATTERNS = {
  "EPLI": ["employment practices", "epli", "educators legal liability", "employment practice",
    // CHUBB: ForeFront Portfolio bundles EPLI
    "forefront", "employment-related practices", "bop45208",
    // Hartford: generic EPLI naming
    "employment practices liability insurance",
    // Berkshire GUARD: included in Commercial Package
    "employment-related practices liability",
    // AmTrust: EPLI add-on
    "amtrust epli",
    // GuideOne: FaithGuard optional
    "faithguard epli",
    // NOTE: "employee benefits liability" REMOVED from EPLI patterns — it is a separate coverage type
    // and was causing false EPLI detection in Employee Benefits Liability policies
    // Liberty Mutual: Helmsman
    "helmsman epli",
    // AIG: Management Liability
    "management solutions epli",
    // CNA: CNA Connect EPLI
    "cna connect epli",
    // Zurich
    "zurich epli",
    // Nationwide
    "nationwide epli"],
  "Cyber Liability": ["cyber", "data breach", "network security", "cyber liability", "privacy liability", "media liability",
    // CHUBB: Cyber ERM product
    "cyber erm", "ransomware encounter", "integrity+",
    // AmTrust: AmTrustCyber standalone product
    "amtrustcyber", "technology insurance company",
    // Hartford: CyberChoice (third-party name)
    "cyberchoice",
    // GuideOne: Cyber Suite by HSB
    "cyber suite", "data compromise", "computer attack",
    // Travelers: CyberRisk, Corvus Smart Cyber
    "cyberrisk", "smart cyber", "smart tech e&o", "corvus",
    // AIG: CyberEdge
    "cyberedge", "aig cyber",
    // CNA: NetProtect
    "netprotect", "cna cyber",
    // Liberty Mutual: Helmsman cyber
    "helmsman cyber",
    // Zurich: Zurich Cyber
    "zurich cyber",
    // Nationwide: Cyber coverage
    "nationwide cyber",
    // General: common form names
    "privacy and network", "cyber extortion", "cyber event"],
  "General Liability": ["general liability", "cgl", "commercial general", "gl policy", "general liab",
    // CHUBB: integrated in BOP/Customarq
    "customarq", "bop47635", "liability enhancement",
    // Hartford: GL Choice product
    "gl choice", "general liability choice", "hs 24 50", "hs2450",
    // AmTrust: FI Advantage Liability
    "fi advantage", "fi liability", "mkt0462", "mkt5555",
    // GuideOne: ISO CG forms in FaithGuard
    "cg 00 01", "cg0001",
    // Sentry: standard ISO CG series
    "cg series",
    // Liberty Mutual: Helmsman GL
    "helmsman gl", "liberty mutual gl",
    // Zurich: ISO-based GL
    "zurich gl",
    // Nationwide: commercial GL
    "nationwide gl",
    // AIG: ISO CG series
    "aig gl",
    // CNA: CNA Connect GL
    "cna gl",
    // Erie: ISO-based
    "erie gl",
    // Cincinnati Financial
    "cincinnati gl",
    // Hanover
    "hanover gl",
    // Selective
    "selective gl"],
  "Property": ["property", "building", "bpp", "business personal property", "commercial property",
    // CHUBB: Customarq Package includes property
    "bop47663", "property enhancement",
    // Hartford: Property Choice / Property Choice Elite
    "property choice", "pce 00", "pce00", "property choice elite", "spice endorsement",
    // AmTrust: BP 00 03 BOP form
    "bp 00 03", "bp0003",
    // GuideOne: CP 00 10 ISO form, church property
    "cp 00 10", "cp0010", "minister property", "stained glass",
    // Sentry: CP series ISO, Select Property
    "sentry select property", "cp series",
    // Berkshire GUARD: in CPP
    "guard property",
    // Liberty Mutual: Helmsman Property, LM Shield
    "helmsman property", "lm shield",
    // AIG: Lexington (surplus lines property)
    "lexington", "aig property",
    // CNA: CNA Connect / CNA Paramount
    "cna paramount", "cna property",
    // Erie: Erie commercial property
    "erie property",
    // Cincinnati Financial
    "cincinnati property",
    // Hanover: Hanover Prestige
    "hanover prestige", "hanover property",
    // Selective: FlexPack property
    "flexpack", "selective property",
    // Nationwide: commercial property
    "nationwide property",
    // Zurich
    "zurich property"],
  "Commercial Auto": ["auto", "vehicle", "fleet", "commercial auto", "business auto", "hired auto",
    "hired and non-owned", "hired & non-owned", "non-owned auto", "HNOA", "hired non-owned",
    "hired/non-owned", "non-owned vehicle", "non-owned automobile",
    // Hartford: HA prefix forms, Business Auto Broad Form
    "ha 00 25", "ha0025", "ha 99 16", "ha9916", "business auto broad",
    // AmTrust: CA990187 expansion
    "ca990187", "ca 00 01", "ca0001",
    // Travelers: standard auto forms
    "travelers auto",
    // Liberty Mutual: Helmsman auto
    "helmsman auto",
    // Nationwide: commercial auto programs
    "nationwide auto",
    // Erie: commercial auto
    "erie auto",
    // General ISO form
    "commercial automobile"],
  "Umbrella/Excess": ["umbrella", "excess", "excess liability", "umbrella liability",
    // CHUBB: Excess Casualty in Customarq add-on
    "excess casualty",
    // Hartford: Hartford Excess Solutions, A/B umbrella forms
    "excess solutions", "hartford excess",
    // AmTrust: follow-form excess up to $10M
    "follow-form excess", "follow form excess",
    // Berkshire GUARD: Commercial Umbrella in CPP
    "commercial umbrella",
    // AIG: AIG Excess Casualty
    "aig excess", "aig umbrella",
    // Liberty Mutual: Helmsman umbrella
    "helmsman umbrella",
    // CNA: excess and umbrella
    "cna umbrella",
    // General: commercial excess liability (from Glatfelter training)
    "commercial excess liability", "commercial excess"],
  "Workers Compensation": ["workers comp", "work comp", "wc ", "workers' comp", "workman comp",
    // AmTrust: WC990001B declarations form
    "wc990001", "wc000404",
    // Berkshire GUARD: AmGUARD underwriting
    "amguard", "guard workers",
    // Sentry: WB-41-143 form
    "wb-41-143", "wb41143",
    // Liberty Mutual: Helmsman WC
    "helmsman wc", "liberty mutual wc",
    // Zurich: WC solutions
    "zurich wc",
    // Erie: WC
    "erie wc",
    // Cincinnati Financial: WC
    "cincinnati wc",
    // General
    "workers compensation", "employer liability", "employers liability"],
  "Professional Liability": ["professional liability", "e&o", "errors and omissions", "errors & omissions", "malpractice",
    // CHUBB: in ForeFront Portfolio
    "professional indemnity",
    // GuideOne: available for nonprofits
    "professional coverage",
    // AIG: Professional Indemnity
    "aig professional",
    // CNA: CNA LawyerGuard, HealthPro, Accountants
    "lawyerguard", "healthpro",
    // Hanover: Professionals programs
    "hanover professional"],
  "D&O": ["directors", "officers", "d&o", "directors and officers", "directors & officers", "management liability",
    // CHUBB: ForeFront Portfolio (D&O, EPLI, Fiduciary)
    "forefront portfolio",
    // GuideOne: D&O Liability in FaithGuard, Religious Expression, Educators Legal Liability
    "religious expression",
    // AIG: AIG Management Liability
    "aig d&o", "aig management",
    // CNA: management liability
    "cna d&o",
    // General: board liability, public officials
    "board member liability", "public officials", "public officials and management"],
  "Sexual Abuse/Molestation": ["sexual abuse", "molestation", "sam ", "abuse & molestation", "abuse and molestation", "abusive conduct",
    // GuideOne: specific church/nonprofit coverage
    "sexual misconduct", "pastoral counseling",
    // Wright Specialty / Munich Re education programs
    "abuse or molestation liability", "abuse or molestation",
    "alleged participant abuse", "pe gl ok5"],
  "Active Shooter": ["active shooter", "active assailant", "violent event", "workplace violence",
    "crisis response", "crisis management"],
  "Equipment Breakdown": ["equipment breakdown", "boiler", "machinery", "mechanical breakdown",
    // Hartford: often included in Property Choice Elite
    "equipment failure"],
  "Crime/Fidelity": ["crime", "fidelity", "employee dishonesty", "forgery", "theft bond",
    // CHUBB: BOP49348 Employee Dishonesty & ERISA
    "bop49348", "erisa fidelity",
    // Hartford: CrimeShield Advanced
    "crimeshield", "crime shield",
    // Berkshire GUARD: built into CPP
    "commercial crime",
    // GuideOne: Crime Coverage in FaithGuard
    "theft coverage",
    // CNA: crime coverage
    "cna crime",
    // Hanover
    "hanover crime"],
  "Flood Insurance": ["flood", "nfip", "wright flood", "wrightflood",
    // Wright Flood products
    "resiflood", "focusflood", "excess flood",
    // Wright form numbers
    "ffl99", "wfl 99", "wfl99",
    // General flood
    "flood insurance", "flood declarations", "flood coverage"],
  "Package Policy": ["package policy", "portfolio", "glatfelter", "business owner", "bop",
    // CHUBB: Customarq Package, Benchmarq Package
    "customarq", "benchmarq", "chubb bop",
    // Hartford: Spectrum BOP
    "spectrum", "spectrum bop", "hartford bop",
    // AmTrust: BOP Silver/Gold/Platinum, CPP
    "amtrust bop", "silver enhancement", "gold enhancement", "platinum enhancement", "commercial package policy", "cpp",
    // Berkshire GUARD: BOP and Commercial Package
    "guard bop", "guard insurance", "berkshire hathaway guard", "amguard", "eastguard", "norguard", "westguard",
    // GuideOne: FaithGuard church package
    "faithguard", "faith guard", "guideone", "guidevantage",
    // Sentry: BOP, industry programs
    "sentry bop", "sentry insurance", "hortica",
    // Travelers: Master Pac, BOP 2.0, Power Pac, CMP
    "master pac", "masterpac", "bop 2.0", "bop2.0", "power pac", "powerpac", "power pac premier",
    "power pac manufacturers", "power pac wholesalers", "power pac medical", "power pac dental",
    "power pac professional", "power pac technology", "power pac restaurant",
    "master pac express", "travelers bop", "travelers cmp",
    "commercial multiple peril", "trvd",
    // Liberty Mutual: Helmsman package, LM Shield
    "helmsman", "liberty mutual bop", "liberty mutual commercial",
    // AIG: Forefront, AIG Package
    "aig package", "aig bop",
    // CNA: CNA Connect, CNA Paramount
    "cna connect", "cna paramount", "cna bop",
    // Zurich: Zurich commercial package
    "zurich bop", "zurich package",
    // Nationwide: Nationwide commercial package
    "nationwide bop", "nationwide package",
    // Erie: Erie Package, ErieSecure Business
    "erie package", "eriesecure", "erie bop",
    // Cincinnati Financial: Cincinnati package
    "cincinnati package", "cincinnati bop", "cincinnati financial",
    // Hanover: Hanover Prestige, Hanover package
    "hanover package", "hanover bop", "hanover prestige",
    // Selective: FlexPack
    "flexpack", "selective bop", "selective package",
    // Church Mutual: CM Advantage, special ministry package
    "church mutual", "cm advantage", "ministry package", "church mutual bop",
    // PGU (existing trained carrier)
    "pgu"],
  // ===== LOUISIANA / MARITIME / MARINE COVERAGE CATEGORIES =====
  "Marine General Liability": ["marine general liability", "marine gl", "marine liability", "maritime liability",
    "marina liability", "dockside liability", "shoreside liability", "wharfingers liability",
    "terminal operators liability", "stevedores liability", "port authority liability",
    "shipyard liability", "boatyard liability", "waterfront liability",
    // Carrier-specific products
    "marine business liability", "marine commercial liability",
    "angler liability", "wade fishing liability", "charter legal liability"],
  "Hull & Machinery": ["hull and machinery", "hull & machinery", "hull insurance", "commercial hull",
    "hull coverage", "vessel damage", "vessel insurance", "hull physical damage",
    "craft physical damage", "watercraft physical damage",
    // Vessel types commonly insured in LA
    "tugboat insurance", "pushboat insurance", "barge insurance", "tug and barge",
    "tug & barge", "crew boat", "supply boat", "supply vessel", "offshore supply",
    "commercial fishing vessel", "fishing vessel insurance", "shrimp boat",
    "oyster boat", "charter boat insurance", "airboat insurance", "airboat tour",
    "ferry insurance", "ferry vessel", "passenger vessel", "research vessel",
    "dredge insurance", "dredge vessel", "dive boat", "pilot boat",
    "pleasure craft", "yacht insurance",
    // Hull form references
    "american hull", "american institute hull", "aih"],
  "Protection & Indemnity": ["protection and indemnity", "protection & indemnity", "p&i",
    "p & i", "p and i", "p&i club", "p&i insurance",
    "vessel liability", "vessel p&i", "marine p&i",
    // P&I club names active in Gulf/LA market
    "skuld", "gard", "britannia", "north of england", "steamship mutual",
    "standard club", "uk p&i", "american club p&i", "west of england",
    "london p&i", "shipowners club",
    // Coverage components
    "collision liability", "running down clause", "crew injury liability",
    "cargo liability", "wreck removal", "pollution indemnity"],
  "Maritime Employers Liability": ["maritime employers liability", "maritime employer's liability",
    "maritime employer liability", "mel ", "mel coverage", "mel policy", "mel insurance",
    // Coverage scope
    "non-owned vessel liability", "borrowed servant", "non-owned vessel",
    "maritime employer"],
  "Jones Act / Crew Coverage": ["jones act", "jones act coverage", "jones act insurance",
    "merchant marine act", "seaman coverage", "seaman injury",
    "maintenance and cure", "maintenance cure wages", "maintenance, cure",
    "death on the high seas", "dohsa",
    // General maritime law
    "general maritime law", "unseaworthiness", "vessel negligence",
    "admiralty law", "admiralty coverage"],
  "USL&H / Longshore": ["longshore", "usl&h", "uslh", "longshore and harbor",
    "longshore & harbor", "harbor workers", "harbor worker",
    "longshore harbor workers compensation", "longshore act",
    "federal workers compensation", "federal wc maritime",
    // Specific exposures
    "stevedore", "ship repair", "shipbuilder", "ship-breaker",
    "dock contractor", "harbor construction", "waterfront worker"],
  "Marine Builders Risk": ["marine builders risk", "marine builder's risk", "marine builder risk",
    "vessel construction", "vessel under construction", "boat builder",
    "boat builders risk", "boat builder's risk",
    "vessel repair", "vessel refit", "vessel retrofit", "drydock", "dry dock",
    "shipyard construction", "marine construction"],
  "Marine Cargo / Freight": ["marine cargo", "ocean cargo", "cargo insurance", "freight insurance",
    "inland marine", "transit coverage", "goods in transit",
    "warehouse to warehouse", "bill of lading",
    // Cargo types common in LA
    "bulk cargo", "container cargo", "project cargo", "breakbulk",
    "petroleum cargo", "chemical cargo", "lng cargo", "grain cargo",
    "commodity coverage"],
  "Marine Pollution Liability": ["marine pollution", "vessel pollution", "pollution liability marine",
    "oil pollution", "opa 90", "oil pollution act", "cofr",
    "certificate of financial responsibility", "spill liability",
    "bunker spill", "bilge discharge", "ballast water",
    // Environmental marine
    "environmental marine", "marine environmental", "coastal pollution",
    "wetland contamination", "waterway pollution",
    "pollution remediation marine", "pollution cleanup marine"],
  "Offshore Energy / OEE": ["offshore energy", "operators extra expense", "oee",
    "control of well", "well control", "blowout coverage", "blowout insurance",
    "offshore platform", "offshore rig", "drilling rig insurance",
    "oil and gas insurance", "oil & gas insurance", "energy insurance",
    // Offshore-specific
    "offshore construction", "offshore pipeline", "subsea",
    "jack-up", "semi-submersible", "drillship",
    "offshore workers compensation", "outer continental shelf",
    // LA-specific energy terms
    "riggers legal liability", "oil lease property", "olp",
    "rig physical damage", "platform removal", "decommissioning"],
  "Dredging Insurance": ["dredging", "dredge operation", "dredge contractor",
    "dredging contractor", "dredging insurance", "dredge equipment",
    "hydraulic dredge", "mechanical dredge", "cutter suction dredge",
    "hopper dredge", "pipeline dredge",
    // Related marine construction
    "marine contractor", "marine construction contractor",
    "dock construction", "pier construction", "bulkhead construction",
    "seawall construction", "levee construction", "levee repair",
    "coastal restoration", "beach nourishment", "channel deepening"],
  // ===== TEXAS / ONSHORE ENERGY / OIL & GAS COVERAGE CATEGORIES =====
  "Control of Well / OEE (Onshore)": ["control of well", "operators extra expense", "oee onshore",
    "well control onshore", "blowout onshore", "blowout preventer", "bop stack",
    "well blowout", "well control insurance", "well control coverage",
    "cratering", "seepage and pollution", "underground blowout",
    "redrilling", "recompletion", "well kill",
    // Carrier-specific products
    "energy package", "onshore energy", "land rig", "land drilling",
    "exploration and production", "e&p insurance", "e&p coverage",
    "upstream energy", "upstream insurance"],
  "Pollution Legal Liability (PLL/EIL)": ["pollution legal liability", "pll", "environmental impairment",
    "environmental impairment liability", "eil", "environmental liability",
    "environmental insurance", "pollution liability", "site pollution",
    "gradual pollution", "pollution cleanup", "remediation coverage",
    "environmental remediation", "environmental cleanup",
    // TX-specific environmental
    "tceq", "texas commission on environmental quality", "tceq compliance",
    "produced water disposal", "saltwater disposal", "swd well",
    "injection well liability", "underground injection control",
    "norm disposal", "norm contamination",
    // Carrier-specific
    "zurich environmental", "aig environmental", "cna environmental",
    "berkley environmental", "ironshore environmental"],
  "Rig & Drilling Equipment": ["rig physical damage", "rig insurance", "rig equipment",
    "drilling rig insurance", "drilling equipment", "land rig insurance",
    "rig transit", "rig mobilization", "rig demobilization",
    "workover rig", "pulling unit", "completion rig",
    "coiled tubing", "snubbing unit", "wireline unit",
    "mud pump", "drill pipe", "tool string",
    // Rig physical damage
    "derrick coverage", "mast coverage", "drawworks",
    "drilling contractor insurance", "drilling contractor equipment"],
  "Oil Lease Property (OLP)": ["oil lease property", "olp", "lease equipment",
    "lease property", "wellhead equipment", "christmas tree",
    "pumping unit", "pump jack", "pumpjack", "rod pump",
    "tank battery", "oil storage tank", "field storage",
    "separator", "heater treater", "dehydration unit",
    "saltwater disposal system", "flow line", "gathering line",
    // TX-specific lease equipment
    "permian basin", "eagle ford", "barnett shale", "haynesville",
    "delaware basin", "midland basin", "spraberry", "wolfcamp"],
  "Pipeline Operators Insurance": ["pipeline insurance", "pipeline operator", "pipeline liability",
    "pipeline coverage", "pipeline property", "pipeline physical damage",
    "gathering system", "transmission pipeline", "distribution pipeline",
    "pipeline integrity", "pipeline right of way", "row insurance",
    "pig launching", "pipeline inspection", "smart pig",
    // Midstream operators
    "midstream insurance", "midstream operator", "midstream coverage",
    "gas gathering", "oil gathering", "ngl pipeline",
    // TX pipeline-specific
    "phmsa", "railroad commission pipeline", "rrc pipeline"],
  "Gas Processing & Compression": ["gas processing", "gas plant", "processing plant",
    "gas compressor", "compressor station", "compression facility",
    "ngl processing", "ngl recovery", "ngl fractionation",
    "cryogenic plant", "turbo expander", "amine plant",
    "gas treating", "acid gas removal", "h2s removal",
    "gas dehydration", "glycol unit", "molecular sieve"],
  "Terminal & Tank Farm": ["terminal insurance", "tank farm", "tank farm insurance",
    "storage terminal", "petroleum terminal", "crude oil terminal",
    "ngl terminal", "lpg terminal", "propane terminal",
    "tank farm property", "aboveground storage tank", "ast coverage", "ast insurance",
    "petroleum storage", "bulk storage", "cushing",
    "tank gauging", "terminal operator", "loading rack"],
  "LNG / Gas Storage": ["lng facility", "lng storage", "lng terminal",
    "liquefied natural gas", "lng insurance", "lng coverage",
    "gas storage", "underground gas storage", "salt dome storage",
    "salt cavern storage", "depleted reservoir storage",
    "lng export", "lng import", "lng carrier",
    "cng facility", "compressed natural gas"],
  "Refinery & Petrochemical": ["refinery", "refinery insurance", "refinery property",
    "petrochemical", "petrochemical plant", "petrochemical insurance",
    "petroleum refining", "crude oil refining", "distillation",
    "cracking unit", "catalytic cracker", "fcc unit",
    "alkylation unit", "reformer", "coker",
    // TX-specific refinery corridors
    "gulf coast refinery", "houston ship channel", "texas city",
    "port arthur refinery", "baytown refinery", "beaumont refinery",
    "corpus christi refinery", "deer park"],
  "NORM Liability": ["norm", "naturally occurring radioactive material",
    "norm exposure", "norm contamination", "norm disposal",
    "norm waste", "technologically enhanced norm", "tenorm",
    "radioactive scale", "radium 226", "radium 228",
    "norm remediation", "norm decontamination",
    "radiation exposure", "radiation liability"],
  "Underground Resource Damage": ["underground resource", "underground resource damage",
    "underground trespass", "subsurface trespass", "mineral trespass",
    "reservoir damage", "reservoir trespass", "cross-flow",
    "wellbore communication", "zonal isolation failure",
    "formation damage", "formation fracture", "frac hit",
    "induced seismicity", "injection-induced seismicity",
    "subsidence", "surface subsidence",
    // TX rule of capture / trespass
    "rule of capture", "hydraulic fracturing trespass", "frac trespass"],
  "RRC Compliance & Bonding": ["railroad commission", "rrc", "rrc bonding",
    "p-5 bond", "p-5pb bond", "p5 bond", "organization report",
    "rrc financial assurance", "well plugging bond",
    "plugging assurance", "plugging bond", "orphan well",
    "well plug", "well abandonment", "p&a bond",
    "statewide blanket bond", "individual well bond",
    // TX RRC compliance
    "rrc compliance", "rrc permit", "rrc filing",
    "h-10 filing", "w-2 production report", "proration"],
  "MSA / Contractual Liability (Energy)": ["master service agreement", "msa",
    "energy msa", "oilfield msa", "drilling msa",
    "contractual liability energy", "indemnity agreement",
    "mutual indemnity", "knock-for-knock", "knock for knock",
    "additional insured energy", "ai endorsement energy",
    "contractual risk transfer", "hold harmless",
    "oilfield service agreement", "well service agreement",
    // TX anti-indemnity statute
    "texas anti-indemnity", "tex. civ. prac. rem. code 127",
    "texas oilfield anti-indemnity act"],
  "Business Interruption (Energy)": ["business interruption energy", "bi energy",
    "production interruption", "loss of production income",
    "well loss of production", "loss of revenue well",
    "contingent business interruption", "cbi energy",
    "supply chain interruption", "feedstock interruption",
    "turnaround insurance", "planned shutdown", "turnaround coverage"],
  // ===== TRANSPORTATION / LOGISTICS COVERAGE CATEGORIES =====
  "Motor Carrier Liability": ["motor carrier liability", "commercial auto liability", "automobile liability",
    "bodily injury and property damage liability", "bipd", "public liability", "primary liability coverage",
    "for-hire carrier", "motor carrier", "public liability endorsement",
    "mcs-90", "mcs 90", "49 cfr 387", "fmcsa liability",
    "commercial motor vehicle", "cmv liability", "trucking liability"],
  "Motor Truck Cargo": ["motor truck cargo", "cargo insurance", "freight coverage", "goods in transit",
    "inland transit", "care custody and control", "all risk cargo", "named perils cargo",
    "broad form cargo", "cargo policy", "mtc policy", "truck cargo",
    "cargo loss", "cargo theft", "cargo damage", "shipper's interest"],
  "MCS-90 Compliance": ["mcs-90", "mcs 90", "endorsement for motor carrier policies",
    "financial responsibility endorsement", "motor carrier endorsement",
    "49 cfr 387.15", "motor carrier act of 1980", "49 usc 13906",
    "bmc-91", "bmc 91", "bmc-91x", "fmcsa filing", "operating authority insurance"],
  "Bobtail / Non-Trucking Liability": ["bobtail insurance", "bobtail liability", "non-dispatch coverage",
    "tractor without trailer", "bobtailing", "deadhead coverage",
    "non-trucking liability", "ntl", "personal use liability", "off-dispatch liability",
    "not-for-hire liability", "bobtail", "non-trucking", "personal use — truck"],
  "Trailer Interchange": ["trailer interchange", "trailer interchange agreement",
    "non-owned trailer", "physical damage — trailer interchange", "uiia trailer interchange",
    "interchange agreement", "trailer physical damage", "non-owned trailer physical damage",
    "uiia", "trailer damage coverage"],
  "Garagekeepers Liability": ["garagekeepers", "garage keeper's liability", "garagekeepers legal liability",
    "care custody or control of customer vehicles", "customers' vehicles",
    "direct primary garagekeepers", "legal liability form garagekeepers",
    "garage operations", "towing company liability", "auto dealer liability"],
  "Refrigeration Breakdown": ["refrigeration breakdown", "reefer breakdown", "reefer endorsement",
    "temperature-controlled cargo", "spoilage coverage", "refrigeration unit failure",
    "mechanical breakdown of refrigeration", "reefer", "cold chain", "temperature controlled",
    "spoilage insurance", "perishable cargo"],
  "Warehousing Legal Liability": ["warehousing legal liability", "warehouse legal liability",
    "warehouseman's legal liability", "goods in storage", "bailee's liability",
    "public warehouse liability", "wll", "warehouse operators liability",
    "storage liability", "warehouseman liability", "bailee coverage"],
  "Freight Broker E&O": ["freight broker e&o", "transportation e&o", "broker errors and omissions",
    "professional liability — freight broker", "miscommunication", "freight broker professional",
    "bmc-84", "bmc-85", "bmc 84", "bmc 85", "broker surety bond",
    "freight broker liability", "transportation professional liability"],
  "Contingent Cargo": ["contingent cargo", "contingent cargo liability", "broker's contingent cargo",
    "secondary cargo coverage", "contingent on failure of carrier's policy",
    "contingent cargo insurance", "broker cargo", "contingent freight"],
  // ===== CONSTRUCTION COVERAGE CATEGORIES =====
  "Builders Risk": ["builders risk", "course of construction", "installation coverage",
    "property under construction", "all risk — builders risk", "reporting form builders risk",
    "completed value form", "soft costs coverage", "delay in opening", "testing coverage",
    "builder's risk", "construction property", "builders all risk"],
  "Wrap-Up Programs (OCIP/CCIP)": ["ocip", "owner controlled insurance program", "wrap-up",
    "controlled insurance program", "cip", "enrolled parties", "project-specific policy",
    "ccip", "contractor controlled insurance program", "contractor wrap-up",
    "gc-controlled program", "wrap-up enrollment", "cip enrollment"],
  "Subcontractor Default Insurance (SDI)": ["subcontractor default insurance", "sdi",
    "first-party default coverage", "subcontractor default", "self-insured retention — sdi",
    "copayment — sdi", "prequalification program", "default declaration",
    "notice of default", "subcontractor performance"],
  "Contractors Pollution Liability": ["contractors pollution liability", "cpl",
    "pollution liability — contractors", "environmental impairment liability — contractor",
    "contractor's environmental liability", "clean-up costs", "pollution conditions",
    "pollutant — contractor operations", "contractor pollution", "construction pollution"],
  "Installation Floater": ["installation floater", "installation coverage — inland marine",
    "property being installed", "materials in transit for installation",
    "contractor's equipment — installation", "inland marine installation",
    "equipment installation coverage", "materials floater"],
  "Equipment Floater": ["contractor's equipment", "equipment floater", "construction equipment — scheduled",
    "mobile equipment", "leased or rented equipment", "loaned equipment",
    "inland marine — contractor's equipment", "scheduled equipment", "heavy equipment floater",
    "contractor equipment coverage", "tools and equipment"],
  // ===== REAL ESTATE COVERAGE CATEGORIES =====
  "Property Managers Professional Liability": ["property manager professional liability", "property management e&o",
    "property managers errors and omissions", "property management professional", "real estate professional liability",
    "real estate e&o", "real estate errors and omissions", "property manager e&o"],
  "Tenant Discrimination Liability": ["tenant discrimination", "fair housing liability", "fair housing act",
    "housing discrimination", "tenant screening liability", "HUD violation", "disparate impact"],
  "Environmental / Mold Liability": ["environmental liability", "mold liability", "mold remediation",
    "asbestos liability", "lead paint liability", "environmental impairment", "premises pollution",
    "environmental site liability", "pollution — premises", "indoor air quality"],
  "Habitability / Code Compliance": ["habitability", "building code compliance", "code violation",
    "building inspection", "certificate of occupancy", "ADA compliance — property",
    "fire code violation", "life safety code"],
  "Loss of Rents / Rental Income": ["loss of rents", "rental income", "rental value insurance",
    "loss of rental income", "fair rental value", "business income — rental",
    "rent loss coverage", "tenant default"],
  "Ordinance or Law Coverage": ["ordinance or law", "building ordinance", "demolition cost",
    "increased cost of construction", "ordinance and law", "undamaged portion"],
  "Railroad Protective Liability": ["railroad protective liability", "rrp", "rpl",
    "railroad named insured", "contractor working near railroad", "50-foot rule",
    "railway right-of-way", "railroad property — contractor operations",
    "railroad protection", "railroad protective"],
  "Products-Completed Operations": ["products and completed operations", "completed operations",
    "pco", "completed work liability", "work after project completion", "your work — completed",
    "products-completed operations aggregate", "completed operations aggregate",
    "pco aggregate", "completed operations coverage"],
  "Contractors Professional Liability (Design-Build)": ["contractors professional liability",
    "cprl", "design-build liability", "professional liability — contractors",
    "errors and omissions — contractor", "professional services liability",
    "negligent act error or omission in professional services",
    "rectification costs", "cppi", "pspl", "design-build professional"],
  // ===== HEALTHCARE / MEDICAL MALPRACTICE COVERAGE CATEGORIES =====
  "Medical Professional Liability": ["medical professional liability", "professional liability — physician",
    "malpractice insurance", "professional services — medical", "negligent act error or omission — medical",
    "bodily injury to patients", "failure to diagnose", "failure to treat",
    "misdiagnosis", "wrongful death — medical", "medical incident",
    "physician malpractice", "med mal", "healthcare professional liability"],
  "Hospital Professional Liability": ["hospital professional liability", "hpl",
    "institutional professional liability", "healthcare facility liability",
    "vicarious liability — hospital", "employed physicians", "credentialed providers",
    "hospital liability", "facility professional liability", "nurses professional liability"],
  "Clinical Trial Liability": ["clinical trial", "clinical research", "experimental treatment",
    "research subjects", "irb", "investigational drug", "protocol deviation",
    "clinical trial liability endorsement", "research liability", "investigational device"],
  "Telemedicine/Telehealth Coverage": ["telemedicine", "telehealth", "virtual care",
    "remote patient monitoring", "e-prescribing", "asynchronous care",
    "digital health", "teleprescribing", "hipaa-compliant platform",
    "multi-state licensure", "online consultation", "virtual medicine", "telemed"],
  "HIPAA/Privacy Breach Liability": ["hipaa", "privacy breach", "protected health information",
    "phi", "cyber liability — healthcare", "data breach — medical",
    "breach notification — healthcare", "ransomware — health",
    "electronic medical records", "cybersecurity incident — healthcare",
    "hitech", "fines and penalties — hipaa", "regulatory defense — hipaa"],
  "Sexual Abuse/Molestation (SAM)": ["sexual abuse and molestation", "sam coverage", "abusive conduct",
    "abuse or molestation endorsement", "sexual misconduct — healthcare",
    "inappropriate touching", "prior acts — abuse",
    "separate limits — abuse and molestation", "abuse molestation healthcare"],
  "Medical Directors Liability": ["medical director", "medical director liability",
    "physician executive liability", "cmo liability", "chief medical officer",
    "medical staff liability", "credentialing liability", "peer review liability",
    "medical staff privileging"],
  "Managed Care E&O": ["managed care e&o", "managed care errors and omissions",
    "utilization review", "pre-authorization denial", "coverage determination",
    "managed care services", "mco liability", "health plan liability",
    "hmo liability", "ipa liability", "aco liability"],
  // "vicarious liability" alone is NOT this coverage. Every CGL carries a
  // vicarious-liability clause — "9. Vicarious liability coverage - The
  // coverage provided under this Policy shall apply on behalf of..." sits in
  // the Base Power energy insured's GL form — and that one phrase was enough
  // to classify the account as healthcare and print the hospital malpractice
  // benchmark table. The healthcare staffing coverage names staffing.
  "Vicarious Liability (Staffing)": ["employer's liability — vicarious",
    "staffing liability", "staffing agency liability", "leased employee — healthcare",
    "temporary employee — healthcare",
    "independent contractor — vicarious liability", "liability for acts of contracted providers",
    "locum tenens", "agency nurse", "temporary staff liability"],
  "Medicare/Medicaid Defense": ["medicare and medicaid defense", "government investigation — healthcare",
    "false claims act", "qui tam", "billing compliance",
    "rac audit", "oig investigation", "kickback allegations",
    "stark law", "anti-kickback statute", "cms audit", "medicare fraud"],
  // ===== INDUSTRIAL / MANUFACTURING COVERAGE CATEGORIES =====
  "Product Recall Insurance": ["product recall", "recall expenses", "product withdrawal",
    "voluntary recall", "government-mandated recall", "recall — bodily injury trigger",
    "recall expense coverage", "first-party recall costs", "third-party recall costs",
    "cpsc recall", "fda recall", "brand rehabilitation", "crisis management — recall"],
  "Product Contamination": ["product contamination", "accidental contamination",
    "malicious tampering", "product extortion", "product contamination insurance",
    "contamination — bodily injury trigger", "government advisory — contamination",
    "recall expense — contamination", "food contamination", "adulterated product"],
  "Equipment Breakdown / Boiler & Machinery": ["equipment breakdown", "boiler and machinery",
    "b&m", "mechanical breakdown — manufacturing", "electrical breakdown",
    "pressure vessel", "boiler explosion", "power surge", "operator error — equipment",
    "motor burnout", "equipment failure — sudden and accidental",
    "object breakdown", "boiler & machinery"],
  "Supply Chain / Contingent BI": ["contingent business interruption", "cbi",
    "contingent time element", "dependent property", "contributing property",
    "recipient property", "leader property", "direct supplier",
    "named dependent property", "contingent bi — worldwide", "supply chain disruption",
    "supply chain insurance", "supplier disruption"],
  "Environmental Impairment Liability": ["environmental impairment liability",
    "eil", "pollution legal liability — manufacturing", "pll manufacturing",
    "pollutant release — manufacturing", "cleanup costs — manufacturing",
    "remediation — manufacturing", "third-party environmental liability",
    "environmental cleanup — plant", "pollution conditions — manufacturing"],
  "OSHA Defense Coverage": ["osha defense", "osha proceedings", "osha citation defense",
    "regulatory defense — osha", "occupational safety defense", "workplace safety defense",
    "government investigation — workers safety", "osha appeal",
    "safety violation defense", "osha compliance coverage"],
  "Manufacturers E&O": ["manufacturers errors and omissions", "manufacturers e&o",
    "manufacturing professional liability", "product specification error",
    "manufacturing defect — financial loss", "late delivery — manufacturer",
    "non-conforming product", "failure to perform to specification",
    "pure economic loss — manufacturing", "manufacturers professional"],
  "Stock Throughput (STP)": ["stock throughput", "stp", "ocean cargo — stock throughput",
    "warehouse-to-warehouse", "raw materials to finished goods", "all-risk — stock throughput",
    "goods in all stages", "storage and transit — combined",
    "end-to-end inventory coverage", "throughput policy"],
  "Foreign Product Liability": ["foreign product liability", "products manufactured abroad",
    "import liability", "worldwide products liability", "coverage territory — worldwide",
    "suit in foreign jurisdiction", "products manufactured in", "cpsc — imported",
    "importer liability", "export products liability"],
  "Inland Marine (Goods in Process)": ["goods in process", "work in process",
    "wip coverage", "installation floater — manufacturing", "process coverage",
    "inland marine — manufacturers", "property in transit — manufacturing",
    "property at outside processors", "goods in transit — manufacturing"],
  // ===== EDUCATION VERTICAL COVERAGE PATTERNS =====
  "Educators Legal Liability (ELL)": ["educators legal liability", "ell coverage", "ell policy",
    "school board legal liability", "school board liability", "sbl coverage", "sbl policy",
    "educators professional liability", "educators e&o", "school leaders e&o",
    // United Educators (UE): PSL + SBL packages
    "public school liability", "ue psl", "ue sbl",
    // GuideOne: FaithGuard Education packages
    "guidone education", "faithguard education",
    // Travelers: School Board Liability
    "travelers school board",
    // INSURICA: Education packages
    "school professional liability"],
  "Student Accident Insurance": ["student accident", "student accident insurance",
    "student athletic insurance", "scholastic accident", "school accident plan",
    "voluntary student accident", "mandatory student accident",
    "catastrophic student", "student injury coverage",
    // A-G Specialty / K&K: Student accident plans
    "k&k student", "a-g student accident",
    // Markel: Student accident
    "markel student accident",
    // What carriers actually call it. Aspen View Academy uploaded a Zurich
    // "Blanket Accident Insurance Policy" and the report told the school it had
    // a CRITICAL GAP in student accident cover: the phrase "student accident"
    // appears nowhere in that document, because blanket accident is the market
    // name for the K-12 product. Zurich, Chubb, Berkley and A-G all write it
    // that way.
    "blanket accident", "blanket student accident", "blanket accident insurance",
    "accident medical expense", "student blanket accident",
    "participant accident", "special risk accident"],
  "Tuition Refund Insurance": ["tuition refund", "tuition insurance", "tuition protection",
    "tuition reimbursement insurance", "enrollment refund",
    // AWG Dewar: Tuition Refund Plans
    "dewar tuition", "college refund",
    // GradGuard: Tuition Insurance
    "gradguard tuition"],
  "Athletic Participant Liability": ["athletic participant", "sports liability",
    "athletic liability", "student athlete coverage", "sports participant",
    "concussion coverage", "traumatic brain injury", "tbi coverage", "cte coverage",
    "athletic medical excess", "ncaa catastrophic"],
  "Field Trip / Off-Premises Liability": ["field trip", "off-premises liability",
    "off-site activity", "excursion coverage", "school trip insurance",
    "out-of-school activity", "off-campus liability"],
  "School Bus / Fleet Auto": ["school bus", "student transportation",
    "school fleet", "pupil transportation", "school bus fleet",
    "school vehicle", "school auto fleet"]
};

// Exposed for the headless test harness: a coverage pattern that is too loose
// silently reclassifies an account (an energy CGL's "vicarious liability
// coverage" clause rendered the hospital malpractice benchmarks), and that is
// only catchable by asserting on the patterns themselves.
try { window.COVERAGE_PATTERNS = COVERAGE_PATTERNS; } catch (e) { /* no window */ }

// ===== COMMON EXCLUSION / CONTRADICTION PATTERNS =====
// These are fatal flaws: policy says coverage exists but exclusionary language effectively voids it
const EXCLUSION_ALERTS = [
  {
    coverage: "Sexual Abuse/Molestation",
    exclusion: "Criminal Acts Exclusion",
    impact: "FATAL FLAW — Sexual abuse/molestation is inherently criminal. A criminal acts exclusion effectively voids any abuse coverage, leaving the organization completely exposed.",
    recommendation: "Immediately verify whether the Sexual Abuse/Molestation coverage contains a carve-back or exception to the criminal acts exclusion. If no carve-back exists, this coverage is illusory and must be replaced with standalone SAM coverage."
  },
  {
    coverage: "EPLI",
    exclusion: "Bodily Injury Exclusion",
    impact: "Potential conflict — EPLI claims involving workplace violence, assault, or physical harassment may be denied under a broad bodily injury exclusion.",
    recommendation: "Verify whether EPLI policy contains bodily injury carve-back for employment-related physical acts. If not, a gap exists for physical workplace misconduct claims."
  },
  {
    coverage: "Cyber Liability",
    exclusion: "Infrastructure/Utility Failure Exclusion",
    impact: "Modern cyber events (ransomware, DDoS) may trigger infrastructure failures. A broad infrastructure exclusion could limit recovery for system-wide outages caused by cyber attacks.",
    recommendation: "Confirm cyber policy covers losses when infrastructure failure is caused by a covered cyber event. Request carrier clarification email if ambiguous."
  },
  {
    coverage: "General Liability",
    exclusion: "Abuse/Molestation Exclusion",
    impact: "GL policies increasingly add absolute abuse/molestation exclusions. Without standalone SAM coverage, the organization has zero liability protection for these claims.",
    recommendation: "If GL excludes abuse/molestation, standalone SAM policy is Suggested, not mandatory. Verify the GL exclusion language and confirm standalone coverage is in force with adequate limits. Email Request to Underwriter to State Coverage Intent."
  },
  {
    coverage: "Property",
    exclusion: "Flood/Earth Movement Exclusion",
    impact: "Standard property policies exclude flood and earthquake. In catastrophe-prone states, this is a critical gap that must be addressed with separate flood (NFIP or private) and earthquake policies.",
    recommendation: "Verify separate flood and earthquake coverage is in force for all locations, especially in FEMA-designated flood zones and seismically active areas."
  },
  {
    coverage: "Umbrella/Excess",
    exclusion: "Underlying Coverage Requirement",
    impact: "If any underlying policy (GL, Auto, EL) lapses or has limits reduced below the umbrella's scheduled underlying limits, the umbrella may not respond — creating an uninsured gap.",
    recommendation: "Confirm all underlying policies match the umbrella's scheduled underlying limits exactly. Any mismatch voids umbrella coverage for that exposure."
  },
  {
    coverage: "Workers Compensation",
    exclusion: "Independent Contractor Exclusion",
    impact: "Misclassification of workers as independent contractors is a top WC audit issue. If the policy excludes contractors who are later reclassified as employees, retroactive exposure is significant.",
    recommendation: "Review worker classification practices. If using independent contractors or volunteers, verify WC policy extends coverage or obtain separate coverage for these individuals."
  },
  // ===== MARITIME / MARINE EXCLUSION ALERTS =====
  {
    coverage: "Hull & Machinery",
    exclusion: "Named Storm / Hurricane Exclusion",
    impact: "FATAL FLAW — Louisiana vessels face extreme hurricane exposure. A named storm exclusion on hull coverage leaves vessels completely unprotected during the most likely cause of catastrophic loss.",
    recommendation: "Immediately verify whether hull policy includes named windstorm coverage. If excluded, obtain separate Named Windstorm hull coverage or negotiate removal. Critical for Gulf of Mexico operations."
  },
  {
    coverage: "Protection & Indemnity",
    exclusion: "Pollution / Environmental Damage Exclusion",
    impact: "FATAL FLAW — If P&I excludes pollution events, vessel operators have no coverage for oil spills, bunker discharge, or environmental damage — a primary exposure in Louisiana waters under OPA 90.",
    recommendation: "Verify P&I pollution coverage scope. If excluded, standalone Marine Pollution Liability (OPA 90 compliance) is MANDATORY for any vessel operating in navigable U.S. waters. Confirm COFR (Certificate of Financial Responsibility) compliance."
  },
  {
    coverage: "Jones Act / Crew Coverage",
    exclusion: "Specified Crew Limitation",
    impact: "Some P&I carriers limit Jones Act coverage to specified/named crew only. Contractors, temporary workers, or employees performing non-navigational duties on vessels may be excluded, creating uncovered exposure.",
    recommendation: "Verify Jones Act coverage extends to ALL persons qualifying as \u2018seamen\u2019 under federal law — not just named crew. If limited, obtain Maritime Employers Liability (MEL) to cover non-owned vessel workers and non-specified crew."
  },
  {
    coverage: "USL&H / Longshore",
    exclusion: "State Workers Comp Only (No Federal Coverage)",
    impact: "CRITICAL — If workers are engaged in maritime employment on navigable waters or adjoining areas (docks, piers, terminals), state WC alone is insufficient. USL&H benefits are roughly 2x state WC. Failure to carry USL&H exposes the employer to per-day federal fines and possible imprisonment.",
    recommendation: "Immediately verify whether the WC policy carries a Longshore and Harbor Workers' Compensation Act endorsement for all maritime-exposed employees. If absent, add immediately — USL&H is federally mandated for qualifying waterfront workers."
  },
  {
    coverage: "Marine Pollution Liability",
    exclusion: "Gradual Pollution / Known Condition Exclusion",
    impact: "Louisiana operations involving older vessels, aging pipelines, or legacy offshore facilities face significant gradual pollution exposure. A known/gradual exclusion may void coverage for the most probable pollution events.",
    recommendation: "Review pollution policy trigger carefully — \u2018sudden and accidental\u2019 vs. \u2018claims-made\u2019 vs. \u2018occurrence\u2019. For legacy operations, consider Pollution Legal Liability (PLL) or Environmental Impairment Liability (EIL) for gradual conditions."
  },
  {
    coverage: "Offshore Energy / OEE",
    exclusion: "Decommissioning / Plug & Abandon Exclusion",
    impact: "BOEM requires financial assurance for decommissioning (2024 final rule). If OEE excludes decommissioning costs, the operator bears full financial exposure — which can exceed $10M+ per platform.",
    recommendation: "Verify whether energy policy covers or excludes decommissioning obligations. If excluded, confirm BOEM financial assurance compliance through separate surety bonds or trust arrangements."
  },
  {
    coverage: "Dredging Insurance",
    exclusion: "Subsurface / Underground Utilities Exclusion",
    impact: "Louisiana waterways contain extensive underground pipelines, cables, and utility infrastructure. A subsurface exclusion on dredging coverage leaves the contractor exposed for damage to third-party infrastructure.",
    recommendation: "Verify dredging policy covers subsurface property damage. If excluded, require pre-dredge utility surveys and confirm separate CGL or Marine GL provides coverage for third-party subsurface damage."
  },
  {
    coverage: "Marine Cargo / Freight",
    exclusion: "Delay / Loss of Market Exclusion",
    impact: "Standard marine cargo policies exclude losses from shipping delays. For perishable goods, time-sensitive commodities (petroleum, chemicals), or contracted delivery schedules common in LA ports, delay losses can be substantial.",
    recommendation: "If delay exposure is significant, negotiate delay coverage endorsement or obtain separate Business Interruption / Consequential Loss coverage for supply chain delays."
  },
  // ===== TEXAS / ONSHORE ENERGY / OIL & GAS EXCLUSION ALERTS =====
  {
    coverage: "Pollution Legal Liability (PLL/EIL)",
    exclusion: "Sudden & Accidental Limitation",
    impact: "FATAL FLAW — Texas oil & gas operations (produced water disposal, saltwater injection wells, NORM contamination, tank battery leaks) frequently involve gradual pollution events. A sudden-and-accidental-only pollution limitation voids coverage for the most probable pollution exposures.",
    recommendation: "Immediately verify whether pollution coverage is standalone PLL/EIL with gradual pollution trigger, or merely CGL pollution exception (sudden & accidental only). For E&P operations, standalone Pollution Legal Liability (PLL) with gradual pollution coverage is MANDATORY."
  },
  {
    coverage: "Underground Resource Damage",
    exclusion: "Underground Resource / Subsurface Trespass Exclusion",
    impact: "FATAL FLAW — Hydraulic fracturing (frac hits), wellbore communication, and induced seismicity claims are increasing in Texas. An underground resource exclusion leaves operators exposed for reservoir damage, neighbor-well interference, and subsurface trespass lawsuits.",
    recommendation: "Verify energy policy covers underground resource damage and subsurface trespass. If excluded, obtain separate Underground Resource Damage coverage. Review frac hit / wellbore communication exposure with engineering data."
  },
  {
    coverage: "MSA / Contractual Liability (Energy)",
    exclusion: "Contractual Liability Exclusion for MSA Indemnities",
    impact: "CRITICAL — Texas oilfield MSAs routinely require mutual indemnity (knock-for-knock) and additional insured endorsements. If the CGL contractual liability exclusion is not properly carved back for insured contracts, the operator/contractor has no coverage for MSA-assumed liabilities. Note: Texas Oilfield Anti-Indemnity Act (Tex. Civ. Prac. & Rem. Code §127) voids certain indemnity provisions.",
    recommendation: "Verify CGL contractual liability exclusion includes proper insured contract exception. Confirm additional insured endorsements match MSA requirements. Review all MSA indemnity clauses against Texas Oilfield Anti-Indemnity Act for enforceability."
  },
  {
    coverage: "NORM Liability",
    exclusion: "Radioactive Material / Nuclear Exclusion",
    impact: "FATAL FLAW — NORM (Naturally Occurring Radioactive Material) is pervasive in Texas oil & gas production (pipe scale, sludge, equipment contamination). Standard nuclear/radioactive exclusions may void coverage for NORM worker exposure claims and NORM disposal/remediation costs.",
    recommendation: "Immediately verify whether energy policy contains a NORM-specific carve-back to the nuclear/radioactive exclusion. If no carve-back exists, obtain standalone NORM Liability coverage. NORM worker exposure monitoring and disposal procedures should be documented."
  },
  {
    coverage: "Control of Well / OEE (Onshore)",
    exclusion: "Seepage & Pollution Sublimit / Exclusion",
    impact: "CRITICAL — Many Control of Well / OEE policies contain seepage & pollution sublimits far below the main policy limit, or impose short discovery periods (48–72 hours). A well blowout in a populated area or near water sources can generate pollution costs exceeding $50M+.",
    recommendation: "Verify seepage & pollution sublimit on Control of Well policy. If sublimited, negotiate full-limit pollution coverage or obtain separate PLL/EIL to fill the gap. Confirm discovery period is adequate for subsurface migration (which may take days to detect)."
  },
  {
    coverage: "Business Interruption (Energy)",
    exclusion: "Wellbore / Downhole Equipment Exclusion",
    impact: "Texas operators\u2019 primary revenue source is well production. BI policies that exclude wellbore/downhole equipment failures leave operators without income protection for the most common production interruption causes (pump failure, tubing collapse, sand production, paraffin plugging).",
    recommendation: "Verify BI coverage scope for production operations. If wellbore/downhole failures are excluded, obtain separate Loss of Production Income (LOPI) coverage or Well Control BI extension."
  },
  {
    coverage: "Rig & Drilling Equipment",
    exclusion: "Wear & Tear / Gradual Deterioration Exclusion on Rig Equipment",
    impact: "Drilling equipment operates in extreme conditions. Broad wear/tear/corrosion exclusions can be used to deny claims where corrosion contributed to a catastrophic failure (drill string washout, BOP failure, mast collapse), even when the proximate cause was covered.",
    recommendation: "Review rig physical damage exclusion language carefully. Confirm whether sudden/accidental mechanical breakdown is covered. Consider adding Equipment Breakdown coverage for rigs and drilling equipment."
  },
  // ===== TRANSPORTATION / LOGISTICS EXCLUSION ALERTS =====
  {
    coverage: "Motor Carrier Liability",
    exclusion: "Radius Limitation Exclusion",
    impact: "FATAL FLAW — If a motor carrier's policy contains a radius limitation and a driver travels beyond the stated radius (e.g., policy states 500-mile radius but driver completes a 700-mile interstate haul), the auto liability coverage is void for that trip. Accident claims arising outside the radius will be denied in full.",
    recommendation: "Immediately audit all route patterns against the policy's scheduled radius of operation. If routes regularly exceed the stated radius, require an endorsement removing or expanding the radius limitation, or replace with a policy that provides nationwide coverage."
  },
  {
    coverage: "Motor Carrier Liability",
    exclusion: "Unlisted / Unauthorized Driver Exclusion",
    impact: "CRITICAL — Lease operators, owner-operators, and substituted drivers are frequently involved in claims. If the driver is not on the approved schedule — a common occurrence with large fleets using owner-operator leases — the carrier\'s insurer will deny the claim, leaving the carrier exposed.",
    recommendation: "Verify the policy driver schedule is current and complete. If using owner-operators or lease drivers, confirm the policy extends coverage to leased drivers or that owner-operators carry their own primary auto liability. Update driver schedules within 30 days of any new hire."
  },
  {
    coverage: "Motor Truck Cargo",
    exclusion: "Absolute Pollution Exclusion (Hazmat Haulers)",
    impact: "FATAL FLAW — Motor carriers transporting hazardous materials (Appendix B or hazardous substances per 49 CFR § 171.8) face catastrophic pollution exposure. An absolute pollution exclusion on the CGL means NO coverage for environmental cleanup, third-party bodily injury, or property damage from a hazmat spill. FMCSA minimum financial responsibility for hazmat carriers is $1M–$5M — but the absolute pollution exclusion may void this coverage for the core exposure.",
    recommendation: "Verify whether a pollution buy-back or Hazardous Materials Pollution Liability endorsement is attached to the auto or CGL policy. If not, and the carrier hauls any listed hazardous material, a standalone Pollution Legal Liability (PLL) policy covering transportation pollution is MANDATORY."
  },
  {
    coverage: "Motor Truck Cargo",
    exclusion: "Unexplained Loss / Mysterious Disappearance Exclusion",
    impact: "HIGH — Cargo theft remains a top exposure for motor carriers. However, the unexplained loss/mysterious disappearance exclusion means that if cargo is missing and the carrier cannot prove a covered cause (e.g., documented theft vs. an inventory discrepancy), the claim is denied. Entire trailer thefts detected only at delivery can be difficult to distinguish from 'mysterious disappearance' in the insurer's view.",
    recommendation: "Ensure cargo tracking systems (GPS, electronic seals, ELD data) create a documentary record of loss events. Train drivers to report thefts immediately to police and document at the point of loss — not at delivery. Review MTC policy for whether a 'full unit theft' carve-back applies."
  },
  {
    coverage: "Bobtail / Non-Trucking Liability",
    exclusion: "Coercion Exclusion (Hours of Service Violation)",
    impact: "HIGH — FMCSA anti-coercion rules (49 CFR § 390.6) prohibit motor carriers from forcing drivers to violate HOS rules. Some policy forms exclude claims arising from coerced dispatch. If a driver involved in an accident was operating under coercive HOS violation, the carrier's liability policy may deny the claim AND FMCSA may levy penalties up to $16,000 per violation.",
    recommendation: "Audit policy for coercion exclusion language. Implement documented HOS compliance program. Ensure dispatch records, ELD logs, and driver communications create a clear record that dispatch decisions were within safety regulations. Remove any policy exclusions that are broader than the statutory coercion prohibition."
  },
  {
    coverage: "MCS-90 Compliance",
    exclusion: "MCS-90 Not Additional Coverage Misconception",
    impact: "CRITICAL — Many motor carriers and even some brokers mistakenly believe MCS-90 provides additional coverage. It does NOT. If the underlying liability policy is void (e.g., excluded driver, excluded route, excluded commodity), the MCS-90 forces the insurer to pay the judgment to the public — then the insurer sues the carrier for reimbursement. This means the carrier has NO protection and owes the full judgment amount to its own insurer.",
    recommendation: "Educate the insured: MCS-90 is a guarantee to the public, not a coverage enhancement. The only way to protect the carrier is ensuring the underlying auto liability policy has no voidable exclusions for foreseeable operations. Verify that policy exclusions do not create situations where MCS-90 triggers insurer reimbursement rights against the insured."
  },
  {
    coverage: "Warehousing Legal Liability",
    exclusion: "Trailer Interchange — No Written Agreement in Place",
    impact: "HIGH — Motor carriers routinely take possession of trailers without confirming a formal UIIA or carrier-specific interchange agreement is in place. Without the written agreement, any physical damage to the trailer is uninsured under the trailer interchange coverage, and the carrier faces direct liability to the trailer owner.",
    recommendation: "Implement a trailer intake procedure confirming written interchange agreements before accepting non-owned trailers. Maintain a current file of all trailer interchange agreements. Verify UIIA enrollment if applicable. Confirm minimum $50,000 trailer interchange limits for Amazon Relay and similar platforms requiring higher coverage."
  },
  
    // ===== REAL ESTATE EXCLUSION ALERTS =====
    { coverage: "General Liability", exclusion: "Tenant Bodily Injury — Maintenance Negligence", 
      condition: "always",
      impact: "VERIFY — Many GL policies for property owners contain exclusions for injuries arising from failure to maintain common areas, stairways, elevators, parking structures, or shared amenities. If the property owner's GL excludes injuries from maintenance negligence, the most common source of tenant/visitor claims is effectively uninsured.",
      recommendation: "Review GL policy for maintenance-related exclusion language. Verify that premises operations coverage extends to all common areas, elevators, parking garages, and amenities. Consider a separate Premises Operations endorsement if the base policy restricts maintenance coverage.",
      citation: "[Exclusion Analysis, Sec. 6: \"GL — Tenant BI/Maintenance Negligence Exclusion\"]" },
    { coverage: "Property Managers Professional Liability", exclusion: "Fair Housing / Tenant Discrimination Gap",
      condition: "always",
      impact: "CRITICAL — Property managers face significant exposure under the Fair Housing Act (FHA), state fair housing laws, and HUD regulations. Tenant screening, rental application denials, accommodation requests (ADA/FHA), and eviction procedures all carry discrimination risk. Without dedicated coverage, defense costs alone in a HUD complaint can exceed $100K.",
      recommendation: "Verify whether the Property Managers Professional Liability or GL policy includes Fair Housing Act defense coverage. If not, a standalone Tenant Discrimination Liability endorsement or policy is essential for any entity managing residential properties.",
      citation: "[Exclusion Analysis, Sec. 6: \"Fair Housing / Tenant Discrimination Gap\"]" },
    { coverage: "Commercial Property", exclusion: "Ordinance or Law Exclusion — Undamaged Portion",
      condition: "always",
      impact: "VERIFY — Standard commercial property policies often exclude the cost of demolishing and rebuilding the undamaged portion of a building when local building codes require it after a partial loss. For older buildings and mixed-use developments, this exclusion can turn a partial property loss into a total financial loss — the owner must demolish and rebuild at current code but the policy only pays for the damaged portion.",
      recommendation: "Verify whether Ordinance or Law Coverage (Coverage A: undamaged portion, Coverage B: demolition cost, Coverage C: increased cost of construction) is included or endorsed onto the property policy. This is critical for any building over 20 years old or in a jurisdiction with aggressive building code enforcement.",
      citation: "[Exclusion Analysis, Sec. 6: \"Ordinance or Law — Undamaged Portion\"]" },
    { coverage: "General Liability", exclusion: "Swimming Pool / Recreational Amenity Exclusion",
      condition: "always",
      impact: "VERIFY — Properties with pools, fitness centers, playgrounds, or recreational amenities may have GL exclusions for injuries arising from these amenities. Drowning claims, equipment injury, and slip-and-fall in pool areas are among the highest-severity premises liability claims in real estate.",
      recommendation: "Verify GL coverage extends to all recreational amenities. Review for swimming pool exclusions, fitness center liability gaps, and playground equipment exclusions. Consider a separate Recreational Amenity Liability endorsement if needed.",
      citation: "[Exclusion Analysis, Sec. 6: \"Recreational Amenity Exclusion\"]" },
    // ===== CONSTRUCTION EXCLUSION ALERTS =====
  {
    coverage: "Builders Risk",
    exclusion: "EIFS Exclusion — Sweeping Scope",
    impact: "FATAL FLAW — The ISO EIFS exclusion does not only exclude EIFS installers — it excludes ALL contractors performing ANY work on a structure where EIFS is present ANYWHERE on the building. Roofers, window installers, HVAC contractors, plumbers, and garage door contractors can all be denied coverage if moisture intrusion is linked to EIFS on the building, even if the contractor never touched the EIFS system.",
    recommendation: "Inspect the policy for an EIFS exclusion or any 'exterior insulation and finish systems' exclusion language. If present, determine whether the insured performs any work on structures with EIFS. If so, this exclusion must be removed or a carve-back endorsement obtained. Consider standalone Contractors Pollution Liability with moisture intrusion coverage as a gap-filler."
  },
  {
    coverage: "Products-Completed Operations",
    exclusion: "Faulty Workmanship / Damage to Your Work Exclusion",
    impact: "FATAL FLAW — The standard CGL provides a critical exception: damage to the GC's work caused by a subcontractor IS covered. However, endorsements CG 22 94 and CG 22 95 remove this subcontractor exception entirely. For a general contractor subcontracting 60–80% of work, this endorsement effectively eliminates the primary value of completed operations coverage — all subcontractor-caused damage is excluded.",
    recommendation: "Immediately verify whether an exclusion removing the subcontractor exception is attached to any subcontractor's or GC's CGL policy. If present, renegotiate removal or replace with a policy maintaining the standard subcontractor exception. This exclusion represents one of the most significant hidden coverage gaps in construction insurance."
  },
  {
    coverage: "Contractors Pollution Liability",
    exclusion: "Earth Movement / Subsidence Exclusion",
    impact: "HIGH — Foundation contractors, grading contractors, site preparation firms, and excavation contractors face significant earth movement exposure. A standard CGL earth movement exclusion, combined with absolute pollution exclusions for soil contamination, can leave a contractor with zero coverage for the most likely claims arising from their operations.",
    recommendation: "Verify whether earth movement exclusion applies to ongoing operations or only completed operations. Obtain Contractors Pollution Liability (CPL) with earth movement and subsidence coverage. For residential projects in clay-soil or high-shrink-swell regions, confirm buy-back endorsement is available."
  },
  {
    coverage: "Builders Risk",
    exclusion: "Residential Construction Exclusion",
    impact: "CRITICAL — Residential construction exclusions vary dramatically by insurer and endorsement form. Some exclude only new construction; others exclude all residential work including minor renovations. The most dangerous impact: this exclusion on a subcontractor\'s policy eliminates the general contractor\'s additional insured coverage entirely for residential projects — even if the GC's own policy has no such exclusion.",
    recommendation: "Identify all residential project exposures. Verify all subcontractor policies for residential construction exclusions before project enrollment. If the GC requires additional insured coverage from subs for residential projects, the sub's policy must be free of residential exclusions or provide a carve-back. Consider OCIP/CCIP for residential projects to control this exposure."
  },
  {
    coverage: "Contractors Pollution Liability",
    exclusion: "Mold / Fungi or Bacteria Exclusion",
    impact: "HIGH — The fungi/bacteria exclusion is among the most broadly drafted exclusions in commercial insurance. It excludes coverage not just for mold contractors, but for ANY contractor whose work resulted in conditions allowing mold growth — roofers, plumbers, HVAC, window installers. The 'concurrent cause' language means even if covered events (e.g., windstorm) also contributed, the exclusion applies.",
    recommendation: "Verify mold/fungi exclusion scope in all CGL policies for moisture-intensive trades. Obtain CPL with mold buy-back endorsement (sublimited, typically $250K–$1M). Confirm whether the property policy also contains a fungi exclusion that would prevent coverage under commercial property."
  },
  {
    coverage: "Wrap-Up Programs (OCIP/CCIP)",
    exclusion: "Cross-Suit / Insured vs. Insured Exclusion",
    impact: "HIGH — In OCIP/CCIP programs, multiple contractors are insured under the same policy. If the project owner sues the GC, and the GC cross-claims against a subcontractor, both are insured parties — the insured vs. insured exclusion may bar the cross-claim. This creates a situation where the OCIP/CCIP effectively protects the project from third-party claims, but leaves enrolled parties without recourse against each other.",
    recommendation: "Review OCIP/CCIP insured vs. insured exclusion carefully. Determine whether carve-backs exist for owner vs. contractor claims. Confirm enrolled parties understand they cannot rely on the wrap-up policy for inter-party disputes. Consider requiring enrolled contractors to maintain their own CGL with severability provisions for claims against other enrolled parties."
  },
  {
    coverage: "Subcontractor Default Insurance (SDI)",
    exclusion: "Subcontractor Default Insurance SIR / 5-Day Notice Rule",
    impact: "HIGH — SDI policies contain strict notice-of-loss provisions (commonly 5 business days from declaration of default). GCs often delay formalizing default declarations while attempting to resolve issues informally. Late notice after the 5-day window can void coverage for pre-notice costs, which may represent the majority of the default loss.",
    recommendation: "Establish a formal default tracking protocol within the project management system. Train project managers on the SDI 5-day notice requirement. Document all communications with a potentially defaulting subcontractor from the first material failure. Pre-populate default declaration templates to reduce notice delay."
  },
  // ===== HEALTHCARE EXCLUSION ALERTS =====
  {
    coverage: "Medical Professional Liability",
    exclusion: "Criminal Acts vs. SAM Coverage Contradiction",
    impact: "FATAL FLAW — Sexual abuse and molestation in healthcare settings is both a criminal act AND a professional liability exposure. A broad criminal acts exclusion on the MPL policy, combined with the absence of a separate Sexual Abuse/Molestation (SAM) endorsement, means the healthcare entity has zero coverage for its most severe patient safety liability. Courts consistently find that the employer\'s negligence (negligent credentialing, supervision, retention) is a SEPARATE covered occurrence — but only if a properly structured SAM endorsement or standalone policy is in force.",
    recommendation: "Verify whether the MPL or Hospital Professional Liability policy contains a SAM endorsement with separate limits. If the policy contains a criminal acts exclusion with no SAM carve-back, standalone Sexual Abuse/Molestation coverage is MANDATORY. Confirm the SAM limits are outside the MPL aggregate."
  },
  {
    coverage: "HIPAA/Privacy Breach Liability",
    exclusion: "HIPAA Regulatory Action Exclusion",
    impact: "FATAL FLAW — Standard medical professional liability policies routinely exclude HIPAA regulatory fines and penalties. HHS OCR HIPAA enforcement actions impose penalties ranging from $100 to $2 million per violation category per year. A healthcare entity relying on its MPL policy for HIPAA breach coverage will discover coverage is void at the worst possible moment.",
    recommendation: "Verify whether a standalone Healthcare Cyber/Privacy Liability policy with explicit HIPAA regulatory action coverage is in force. The cyber policy must specifically state that HIPAA fines and penalties are covered (where insurable by law). HHS OCR enforcement costs must also be covered — including mandatory corrective action plans."
  },
  {
    coverage: "Medical Professional Liability",
    exclusion: "Scope of Practice Exclusion",
    impact: "HIGH — Allied health providers (NPs, PAs, CRNAs, RTs, PTs) frequently operate at or near the boundaries of their licensed scope, particularly in understaffed facilities. If a CRNA administers anesthesia beyond their supervising arrangement or an NP prescribes a controlled substance outside their state\'s prescriptive authority, any resulting claim may be denied as outside the scope of practice.",
    recommendation: "Audit all allied health provider credentialing files and licensure status. Verify that the MPL policy definition of 'professional services' encompasses the full scope of licensed practice for each provider type employed or contracted. For multi-state telehealth providers, confirm scope of practice coverage extends to all states of licensure."
  },
  {
    coverage: "Medical Professional Liability",
    exclusion: "Claims-Made Tail Gap — Retroactive Date Limitation",
    impact: "CRITICAL — Claims-made policies only cover claims made AND reported during the active policy period, AND only for incidents occurring AFTER the retroactive date. When a provider changes insurers without negotiating matching retroactive dates, or fails to purchase tail (ERP) or nose coverage, acts committed during the prior policy period become completely uninsured. Healthcare statute of limitations for minors can extend 20+ years.",
    recommendation: "When switching MPL carriers, verify the new policy's retroactive date matches or precedes the prior policy's inception date (or purchase prior acts coverage). If canceling a claims-made policy without purchasing tail coverage, all prior acts become uninsured. Require proof of tail or prior acts coverage from all physicians and allied health providers upon termination."
  },
  {
    coverage: "Clinical Trial Liability",
    exclusion: "Experimental Treatment Exclusion",
    impact: "HIGH — Academic medical centers, cancer treatment facilities, and specialty practices routinely employ off-label medications and investigational protocols. An experimental treatment exclusion on the standard MPL policy means these clinical activities — which may represent a significant portion of patient care — are excluded from the policy\'s coverage.",
    recommendation: "Identify all clinical trial and investigational protocol activities. Verify whether the MPL policy includes or excludes experimental treatment coverage. Obtain a standalone Clinical Trial Liability endorsement or policy. Ensure IRB approval documentation and informed consent processes are current — most clinical trial liability policies require documented IRB approval."
  },
  {
    coverage: "Managed Care E&O",
    exclusion: "Medicare/Medicaid Exclusion — Regulatory Defense Gap",
    impact: "HIGH — Medicare/Medicaid fraud enforcement (OIG, DOJ, CMS) is a primary regulatory exposure for any healthcare entity receiving government reimbursement. Standard MPL and managed care E&O policies typically exclude government investigation defense costs. A provider under OIG investigation faces legal defense costs of $500K–$5M+ before resolution, all uninsured under standard coverage.",
    recommendation: "Verify whether the MPL or managed care E&O policy includes regulatory defense for Medicare/Medicaid billing investigations. If not, obtain Medicare/Medicaid Defense Coverage as a standalone endorsement or policy. Implement robust billing compliance program with documented training — most policies require an active compliance program."
  },
  {
    coverage: "Telemedicine/Telehealth Coverage",
    exclusion: "Non-Compliant Platform Exclusion (HIPAA/HITECH)",
    impact: "HIGH — Telehealth providers using consumer-grade video conferencing (e.g., FaceTime, non-HIPAA Zoom) to deliver clinical services may find their professional liability coverage void for incidents involving those sessions. During COVID-19 enforcement discretion periods, many providers adopted non-compliant platforms — but those enforcement waivers have expired.",
    recommendation: "Audit all telemedicine platforms for HIPAA BAA (Business Associate Agreement) compliance. Ensure only HIPAA-compliant telehealth platforms are used for patient care. Obtain written confirmation from all technology vendors that BAAs are in place. Verify telemedicine professional liability policy covers all states where the provider holds licensure."
  },
  // ===== INDUSTRIAL / MANUFACTURING EXCLUSION ALERTS =====
  {
    coverage: "Product Recall Insurance",
    exclusion: "Sistership / Product Recall Exclusion on CGL",
    impact: "FATAL FLAW — The sistership exclusion is among the least-understood gaps in manufacturing insurance. Virtually every manufacturer believes their CGL covers product liability — and it does for bodily injury and property damage TO THIRD PARTIES. However, the CGL specifically and completely excludes all costs associated with recalling, withdrawing, inspecting, or replacing the manufacturer\'s own products. A $50M product recall event generates NO recovery from a CGL policy.",
    recommendation: "Verify the insured has standalone Product Recall Insurance. Confirm the CGL explicitly contains the sistership/recall exclusion (standard ISO language). Obtain Product Recall coverage with adequate limits — for food/beverage manufacturers, limits of $25M–$100M are not unusual. Ensure product recall policy covers both first-party costs (recall expenses) and third-party costs (customer recall costs)."
  },
  {
    coverage: "Product Contamination",
    exclusion: "Known Defect / Failure to Warn Exclusion",
    impact: "HIGH — Manufacturers who receive quality control alerts, safety complaints, or regulatory warnings and continue production without corrective action face known defect exclusion denials. CPSC safety databases, FDA 483 observations, and internal warranty claim data can all be cited as evidence of prior knowledge. This exclusion can void coverage for the most financially significant product liability events.",
    recommendation: "Establish a formal product safety committee with documented corrective action procedures. All quality control incidents, warranty claims, and regulatory notifications should be logged and addressed with documented remediation. Policy inception applications must accurately disclose known defects — material misrepresentation can void the entire policy."
  },
  {
    coverage: "Environmental Impairment Liability",
    exclusion: "Gradual Pollution Exclusion (Manufacturing Operations)",
    impact: "FATAL FLAW — Manufacturing operations involve continuous processes that frequently produce gradual pollution releases — chemical solvents, volatile organic compounds, wastewater discharges, heavy metal contamination of soil and groundwater. The 1985 Absolute Pollution Exclusion on the CGL bars ALL coverage for these events. State variation is critical: some states limit the pollution exclusion to traditional environmental pollutants; others apply it to any irritant or contaminant (including dust, fumes, and lubricants).",
    recommendation: "Determine whether the CGL carries an absolute or total pollution exclusion. If so, obtain standalone Environmental Impairment Liability (EIL) or Pollution Legal Liability (PLL) covering gradual releases, on-site cleanup, off-site migration, and third-party bodily injury/property damage. Coverage limits should reflect the facility\'s CERCLA/RCRA exposure."
  },
  {
    coverage: "OSHA Defense Coverage",
    exclusion: "OSHA Willful Citation — Employers Liability Interaction",
    impact: "HIGH — When OSHA issues a willful citation (finding that the employer knowingly violated a safety standard), the willful act exclusion in the employers liability policy (Coverage B of the WC policy) may bar defense and indemnity coverage for related employee injury claims. OSHA willful penalties ($156,259 per violation as of 2025) are also not insurable fines — creating both uninsured fine exposure AND uncovered civil liability.",
    recommendation: "Verify whether employers liability policy contains a willful act exclusion and how broadly it is drafted. Obtain standalone OSHA Defense Coverage for citation defense costs. Establish a documented safety compliance program — insurers offering OSHA defense coverage typically require evidence of an active safety management system."
  },
  {
    coverage: "Product Recall Insurance",
    exclusion: "Product Guarantee / Failure to Perform Exclusion",
    impact: "HIGH — Contract manufacturers and precision manufacturers operating under performance warranties (OEM supplier agreements, aerospace/defense specs, medical device certifications) frequently face claims for products that fail to perform to specification WITHOUT causing bodily injury or property damage to third parties. The CGL does not cover these 'pure economic loss' warranty claims, and the products guarantee exclusion on the products liability policy reinforces this gap.",
    recommendation: "Identify all OEM supply agreements, performance warranties, and contractual specifications. Obtain Manufacturers E&O coverage for pure economic loss arising from specification failures, non-conforming products, and delivery failures. Verify the product recall policy covers government advisory triggers, not just bodily injury triggers — mislabeled or off-spec products may require recall without BI evidence."
  },
  {
    coverage: "Supply Chain / Contingent BI",
    exclusion: "Supply Chain Single-Source Concentration Gap",
    impact: "HIGH — Most Contingent Business Interruption policies cover only DIRECT, SCHEDULED suppliers. Second-tier and third-tier suppliers — which may represent the actual single-point-of-failure in a manufacturer\'s supply chain (e.g., a sole-source microchip fabricator in Taiwan) — are typically excluded unless specifically added. The COVID-19 pandemic exposed this gap across virtually all manufacturing sectors.",
    recommendation: "Conduct a supply chain mapping exercise identifying all single-source suppliers and sole-source components. Add critical tier-2 and tier-3 suppliers to the dependent property schedule. Confirm the CBI policy covers international locations and requires only physical damage (not pandemic/government action triggers). Obtain Supply Chain Risk Interruption coverage with broader triggers for concentration risk."
  }
];

// Generate exclusion alerts based on detected coverages
// v2.21: Accepts optional `endorsements` data so we can demote FATAL FLAW alerts
// when a CARVE_BACK or GRANT endorsement actually fixes the contradiction.
function getExclusionAlerts(detectedCoverages, vertical, endorsements) {
  const alerts = [];
  // v2.21: Carve-back/grant aware demotion helper.
  function findCarveBack(coverageKey) {
    if (!endorsements || !endorsements.byCoverage) return null;
    const eList = endorsements.byCoverage[coverageKey] || [];
    return eList.find(e => e.tag === 'CARVE_BACK' || e.tag === 'GRANT') || null;
  }
  const vl = vertical.toLowerCase();
  const isEducation = vl.includes('school') || vl.includes('education') || vl.includes('daycare') || vl.includes('tutor');
  const isMaritime = vl.includes('maritime') || vl.includes('marine') || vl.includes('shipping') || vl.includes('dredg') || vl.includes('offshore') || vl.includes('oil') || vl.includes('gas') || vl.includes('port') || vl.includes('vessel') || vl.includes('boat') || vl.includes('fishing') || vl.includes('tug') || vl.includes('barge') || vl.includes('shipyard');
  // Renewable / storage / retail-electric verticals contain the word "energy"
  // but carry none of the upstream oil & gas exposures. Without this guard a
  // home-battery installer is handed Control of Well, NORM Liability and Rig &
  // Drilling Equipment benchmarks — 'Renewable Energy' had this defect before
  // 'Battery Storage & Distributed Energy' was added.
  const isRenewable = vl.includes('renewable') || vl.includes('solar') || vl.includes('wind')
    || vl.includes('battery') || vl.includes('distributed energy') || vl.includes('photovoltaic')
    || vl.includes('retail electricity') || vl.includes('electricity provider') || vl.includes('microgrid');
  const isEnergy = (vl.includes('oil') || vl.includes('gas') || vl.includes('energy') || vl.includes('drilling') || vl.includes('refinery') || vl.includes('petrochemical') || vl.includes('pipeline') || vl.includes('midstream') || vl.includes('upstream') || vl.includes('downstream') || vl.includes('petroleum') || vl.includes('wellhead') || vl.includes('e&p') || vl.includes('exploration') || vl.includes('production') || vl.includes('frac') || vl.includes('oilfield') || vl.includes('well service') || vl.includes('compressor') || vl.includes('gathering')) && !isRenewable;
  const isTransportation = vl.includes('trucking') || vl.includes('fleet') || vl.includes('logistics') || vl.includes('freight') || vl.includes('carrier') || vl.includes('warehousing') || vl.includes('distribution') || vl.includes('last mile') || vl.includes('freight broker') || vl.includes('motor carrier') || vl.includes('trucking/fleet') || vl.includes('transport');
  const isConstruction = vl.includes('contractor') || vl.includes('construction') || vl.includes('builder') || vl.includes('subcontractor') || vl.includes('trade') || vl.includes('general contractor') || vl.includes('specialty trade') || vl.includes('residential builder') || vl.includes('heavy/highway') || vl.includes('design-build');
  const isRealEstate = vl.includes('real estate') || vl.includes('property management') || vl.includes('residential development') || vl.includes('reit') || vl.includes('landlord') || vl.includes('commercial property management') || vl.includes('real estate development') || vl.includes('real estate investment');
  const isHealthcare = vl.includes('hospital') || vl.includes('medical') || vl.includes('physician') || vl.includes('healthcare') || vl.includes('clinic') || vl.includes('nursing') || vl.includes('dental') || vl.includes('behavioral') || vl.includes('ambulatory') || vl.includes('telemedicine') || vl.includes('telehealth') || vl.includes('home health') || vl.includes('long-term care') || vl.includes('clinical lab');
  const isManufacturing = vl.includes('manufactur') || vl.includes('industrial') || vl.includes('factory') || vl.includes('plant') || vl.includes('assembly') || vl.includes('processing') || vl.includes('food/beverage') || vl.includes('pharmaceutical') || vl.includes('chemical manufacturing') || vl.includes('automotive manufacturing') || vl.includes('electronics manufacturing');
const isOGSupplier = vl.includes('services supplier') || vl.includes('supplier') || vl.includes('rigging') || vl.includes('lifting') || vl.includes('hoist') || vl.includes('wire rope') || vl.includes('sling') || vl.includes('rental') || (vl.includes('services') && (vl.includes('oil') || vl.includes('gas') || vl.includes('energy'))) || (vl.includes('oilfield') && (vl.includes('service') || vl.includes('supply') || vl.includes('equipment')));
  
  EXCLUSION_ALERTS.forEach(ea => {
    // Always show SAM/GL exclusion warnings for education verticals
    if (ea.coverage === 'Sexual Abuse/Molestation' && isEducation) {
      alerts.push(ea);
    } else if (ea.coverage === 'General Liability' && ea.exclusion.includes('Abuse') && isEducation) {
      alerts.push(ea);
    } else if (detectedCoverages.includes(ea.coverage)) {
      alerts.push(ea);
    }
    // v2.21: Tag carve-back demotion onto the alert in-place if a relevant endorsement exists.
    if (alerts.includes(ea) && !ea._carveBackChecked) {
      ea._carveBackChecked = true;
      const carveBack = findCarveBack(ea.coverage);
      if (carveBack) {
        ea._demoted = true;
        ea._carveBackEndorsement = carveBack;
      }
    }
    // Always show Property flood/earthquake and Umbrella underlying alerts
    if (ea.coverage === 'Property' && ea.exclusion.includes('Flood')) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    if (ea.coverage === 'Umbrella/Excess' && ea.exclusion.includes('Underlying')) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show maritime exclusion alerts for maritime/marine verticals (contextual filter: only if vertical is actually maritime)
    const maritimeCoverages = ['Hull & Machinery', 'Protection & Indemnity', 'Jones Act / Crew Coverage', 'USL&H / Longshore', 'Marine Pollution Liability', 'Offshore Energy / OEE', 'Dredging Insurance', 'Marine Cargo / Freight'];
    if (isMaritime && !isEducation && !isHealthcare && !isConstruction && !isOGSupplier && maritimeCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show energy exclusion alerts for oil & gas / energy verticals
    const energyCoverages = ['Pollution Legal Liability (PLL/EIL)', 'Underground Resource Damage', 'MSA / Contractual Liability (Energy)', 'NORM Liability', 'Control of Well / OEE (Onshore)', 'Business Interruption (Energy)', 'Rig & Drilling Equipment'];
    if (isEnergy && !isOGSupplier && energyCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show transportation exclusion alerts for transportation/logistics verticals
    const transportationCoverages = ['Motor Carrier Liability', 'Motor Truck Cargo', 'MCS-90 Compliance', 'Bobtail / Non-Trucking Liability', 'Trailer Interchange', 'Garagekeepers Liability', 'Refrigeration Breakdown', 'Warehousing Legal Liability', 'Freight Broker E&O', 'Contingent Cargo'];
    if (isTransportation && transportationCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show construction exclusion alerts for construction verticals
    const constructionCoverages = ['Builders Risk', 'Wrap-Up Programs (OCIP/CCIP)', 'Subcontractor Default Insurance (SDI)', 'Contractors Pollution Liability', 'Installation Floater', 'Equipment Floater', 'Railroad Protective Liability', 'Products-Completed Operations', 'Contractors Professional Liability (Design-Build)'];
    if (isConstruction && constructionCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show healthcare exclusion alerts for healthcare verticals
    const healthcareCoverages = ['Medical Professional Liability', 'Hospital Professional Liability', 'Clinical Trial Liability', 'Telemedicine/Telehealth Coverage', 'HIPAA/Privacy Breach Liability', 'Sexual Abuse/Molestation (SAM)', 'Medical Directors Liability', 'Managed Care E&O', 'Vicarious Liability (Staffing)', 'Medicare/Medicaid Defense'];
    if (isHealthcare && healthcareCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
    // Always show manufacturing exclusion alerts for manufacturing/industrial verticals
    const manufacturingCoverages = ['Product Recall Insurance', 'Product Contamination', 'Equipment Breakdown / Boiler & Machinery', 'Supply Chain / Contingent BI', 'Environmental Impairment Liability', 'OSHA Defense Coverage', 'Manufacturers E&O', 'Stock Throughput (STP)', 'Foreign Product Liability', 'Inland Marine (Goods in Process)'];
    if (isManufacturing && manufacturingCoverages.includes(ea.coverage)) {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
  });
  return alerts;
}

// ===== EB EXCLUSION ALERTS =====
const EB_EXCLUSION_ALERTS = [
  {
    coverage: "Group Health",
    exclusion: "Pre-Existing Condition Limitations",
    terms: ['pre-existing condition', 'preexisting condition', 'pre existing condition'],
    impact: "FATAL FLAW: While ACA prohibits pre-existing exclusions for fully insured plans, self-funded plans with grandfathered status or level-funded arrangements may still contain this language, creating coverage denials.",
    recommendation: "Verify plan's ACA compliance status. If self-funded or grandfathered, confirm no pre-existing condition exclusions exist that violate ACA mandates."
  },
  {
    coverage: "Long-Term Disability",
    exclusion: "Mental/Nervous Limitation",
    terms: ['mental/nervous', 'mental or nervous', 'mental, nervous', 'nervous disorder', 'mental illness limitation'],
    impact: "FATAL FLAW: Mental health claims are among the most common LTD claims. A 24-month cap effectively eliminates long-term protection for the most frequent disability cause, contradicting the policy's stated purpose.",
    recommendation: "Negotiate removal of mental/nervous limitation or extend to match overall benefit period. Mental Health Parity and Addiction Equity Act (MHPAEA) may apply."
  },
  {
    coverage: "Life/AD&D",
    exclusion: "Contestability & Suicide Exclusion",
    terms: ['contestability', 'incontestability', 'suicide'],
    impact: "Standard provision but often misunderstood by employees. The 2-year contestability period applies to late enrollees and new hires, potentially denying claims for material misrepresentation on enrollment.",
    recommendation: "Ensure employees understand enrollment requirements. Review Evidence of Insurability (EOI) thresholds and guaranteed issue amounts."
  },
  {
    coverage: "Dental",
    exclusion: "Missing Tooth Clause",
    terms: ['missing tooth', 'missing teeth'],
    impact: "Common exclusion that denies coverage for implants, bridges, or dentures replacing teeth lost before enrollment. Employees discover this only when filing claims.",
    recommendation: "Review missing tooth clause. Consider plans without this exclusion or negotiate a waiver for competitive positioning."
  },
  {
    coverage: "Group Health",
    exclusion: "Out-of-Network Emergency Balance Billing",
    terms: ['balance billing', 'balance bill', 'surprise billing', 'no surprises act'],
    impact: "No Surprises Act provides federal protections, but plan language may still expose members to balance billing in certain circumstances, particularly for air ambulance and certain ancillary providers.",
    recommendation: "Verify No Surprises Act compliance language in plan documents. Confirm air ambulance and ancillary provider protections are included."
  },
  {
    coverage: "Short-Term Disability",
    exclusion: "Elective Procedure Exclusion",
    terms: ['elective procedure', 'elective surgery', 'cosmetic procedure', 'cosmetic surgery'],
    impact: "Broad 'elective procedure' language can be used to deny claims for complications arising from surgeries that were initially elective but resulted in genuine disability.",
    recommendation: "Review definition of 'elective' to ensure it does not inadvertently exclude complications from medically necessary procedures."
  }
];

// ===== EB exclusion grounding =====
// Section 6 used to print a hardcoded citation for every alert — an invented
// article, section, page range AND quotation, attributed to the client's own
// plan document. Those strings were identical for every client and were never
// checked against the upload: the Arlington Classics guide contains no
// "pre-existing" language, no Article III and no Section 3.04, yet the report
// quoted all three. Nothing may claim to be FROM a document unless it was
// found IN that document.
//
// LlamaParse writes one "---" between pages, so a character offset maps back
// to a page. Where the page prints its own number just above that separator,
// that is the number a broker will look for, so it is preferred.
function ebPageForOffset(text, offset) {
  const parsed = (text.slice(0, offset).match(/\n---\n/g) || []).length + 1;
  const after = text.slice(offset);
  const nextSep = after.indexOf('\n---\n');
  const chunk = (nextSep < 0 ? after : after.slice(0, nextSep)).replace(/\s+$/, '');
  const printed = /(?:^|\n)\s*(\d{1,3})\s*$/.exec(chunk);
  return { parsed: parsed, printed: printed ? parseInt(printed[1], 10) : null };
}

// Find an exclusion's language in the uploaded documents. Returns the real
// quote, file and page — or null, in which case the alert is not reported at
// all: an alert with no located language has nothing to cite.
// ===== Section 7 heading and intro =========================================
// Copy spec: "Section 7 Rewrite — Speak Broker, Not Engineer" (2026-09-03).
// The intro read like engineering documentation — "A rule that could not find
// its language emits nothing, so this list is what the paper says" — which a
// working broker has to translate before they can use it. The wording below is
// the broker's own language and answers the four questions they actually have:
// is the AI making this up, can I use it with a client, what did it miss, and
// how is it defensible. The document count is the only variable.
window.spSection7Intro = function(docCount, escapeHtml){
  const n = Number(docCount) || 0;
  const docs = n + ' uploaded document' + (n === 1 ? '' : 's');
  const pols = n + ' uploaded polic' + (n === 1 ? 'y' : 'ies');
  return {
    // The section no longer reads BETWEEN policies: one pass runs per policy,
    // on that policy's own paper, so the copy must not promise cross-policy
    // analysis it does not perform.
    subtitle: '<p style="font-size:13px;font-style:italic;color:var(--text-secondary);margin:2px 0 8px;">'
      + 'Findings quoted from your ' + docs + ', one policy at a time.</p>',
    intro: '<p style="font-size:13px;margin:0 0 10px;"><strong>Policy-by-policy review — ' + pols + '.</strong> '
      + 'Each policy is read on its own: its coverage grants, exclusions, endorsements and conditions, and '
      + 'nothing from any other document. Every finding below is quoted directly from that policy’s '
      + 'language, with the file, section and page listed. This is what you can point to in a client meeting '
      + '— not inference, not a hypothetical, not “what the coverage mix suggests.” If SentriPol '
      + 'couldn’t find the exact wording in the paperwork, it isn’t flagged here. Nothing below is '
      + 'guessed.</p>'
  };
};

// ===== Cross-policy contradiction analysis =================================
// The 53-alert catalogue reads ONE policy at a time and is written around ISO
// forms. The contradictions that actually bite a program live BETWEEN policies
// and, on a London-market or specialty placement, in wording the catalogue has
// never heard of: what the property policy excludes, the cyber policy may
// exclude from the other side, and the gap between them is where the loss
// lands.
//
// Every rule below is EVIDENCED. A finding is emitted only when the language
// is located in the uploaded documents, and it is cited with the file, page,
// section and the policy's own words. No rule fires on the coverage mix alone.

// Which kind of policy a document is. Read from the declarations vocabulary
// rather than the file name, which brokers name however they like.
window.spClassifyPolicy = function(doc){
  // Scored, not first-match. Every policy in a program mentions the others:
  // the GL's forms schedule lists a cyber exclusion, the property MRC lists
  // LMA5401, and a first-match classifier read both as cyber policies. What
  // identifies a policy is its OWN declarations vocabulary.
  const head = ((doc.pages || []).slice(0, 8).map(p => p.text || '').join(' ')).toLowerCase();
  const all = (doc.pages || []).map(p => p.text || '').join(' ').toLowerCase();
  const name = String(doc.fileName || '').toLowerCase();
  const count = (hay, re) => (hay.match(re) || []).length;
  const score = {
    wc: 3 * count(head, /workers'? compensation|employers liability/g) + 2 * count(name, /workers|comp\b/g)
        + 2 * count(all, /wc \d{2} \d{2} \d{2}|experience rating plan/g),
    cyber: 3 * count(head, /network security and privacy|breach response|cyber extortion|ransomware/g)
        + 2 * count(name, /cyber/g) + count(all, /technology wrongful act|data recovery and computer replacement/g),
    auto: 3 * count(head, /commercial automobile|business auto|covered autos|auto declarations/g)
        + 2 * count(name, /auto|fleet/g),
    umbrella: 3 * count(head, /umbrella liability|excess liability declarations|schedule of underlying insurance/g)
        + 2 * count(name, /umbrella|excess/g),
    property: 3 * count(head, /market reform contract|schedule of values|causes of loss|property damage declarations|total insured value/g)
        + 2 * count(name, /property|prop\b/g) + count(all, /schedule of values|basis of indemnity/g),
    gl: 3 * count(head, /commercial general liability|general aggregate limit|products-completed operations/g)
        + 2 * count(name, /general liability|\bgl\b/g) + count(all, /cg 00 01|coverage a[ \-–]+bodily injury/g)
  };
  let best = 'other', bestScore = 0;
  Object.keys(score).forEach(k => { if (score[k] > bestScore) { bestScore = score[k]; best = k; } });
  return bestScore >= 2 ? best : 'other';
};

// Locate language inside ONE class of policy. Returns the grounded citation
// the report prints, or null.
function spFindInKind(pdfTexts, kind, terms, requireNear){
  const docs = (pdfTexts || []).filter(d => window.spClassifyPolicy(d) === kind);
  if (!docs.length) return null;
  return groundEBExclusion({ terms: terms }, docs, requireNear ? { requireNear: requireNear } : null);
}

// A peril one policy grants and another excludes. `regardless of any other
// cause or event contributing concurrently` is the wording that closes the
// argument, so it is called out where present.
// PDF text extraction splits the first letter off a word often enough to
// matter — the Base Power property policy reads "r egardless of any other
// cause" and "N otwithstanding any provision" — so the first letter tolerates
// a stray space. Missing this flips the finding from "the exclusion forecloses
// the argument" to "there may be room to argue".
const SP_CONCURRENT = /r\s?egardless\s+of\s+any\s+other\s+cause|w\s?hether\s+or\s+not[\s\S]{0,40}concurrently|n\s?otwithstanding\s+any\s+provision\s+to\s+the\s+contrary/i;

window.spCrossPolicyFindings = function(pdfTexts, opts){
  const o = opts || {};
  const out = [];
  const kinds = {};
  (pdfTexts || []).forEach(d => { kinds[window.spClassifyPolicy(d)] = true; });
  const cite = h => h ? { file: h.fileName, page: h.page, pageEnd: h.pageEnd, section: h.section, quote: h.quote } : null;

  // --- 1. Cyber-caused physical damage: excluded from both sides ---------
  const propCyber = spFindInKind(pdfTexts, 'property',
    ['property cyber and data exclusion', 'lma5401', 'excludes any: (a) cyber loss', 'cyber loss'],
    /exclud|does not (?:insure|apply)/i);
  const cyberPD = spFindInKind(pdfTexts, 'cyber',
    ['property damage', 'injury or damage to, destruction, impairment, or loss of use of any tangible property'],
    /exclusion|does not apply|this policy does not/i);
  if (propCyber && cyberPD) {
    out.push({
      id: 'cyber-physical-gap',
      severity: 'CRITICAL',
      title: 'Cyber-caused physical damage — excluded by the property policy AND the cyber policy',
      mechanism: 'The property policy excludes loss arising from a cyber act; the cyber policy excludes damage to tangible property. A physical loss set off by a network event falls between them.',
      evidence: [
        Object.assign({ label: 'Property policy excludes cyber-caused loss' }, cite(propCyber)),
        Object.assign({ label: 'Cyber policy excludes tangible property damage' }, cite(cyberPD))
      ],
      concurrent: SP_CONCURRENT.test(propCyber.context || propCyber.quote || ''),
      action: 'Ask the property market to write back cyber-caused physical damage — an affirmative grant for physical damage following a cyber event, or a carve-back to '
        + (( /LMA\s?5\d{3}[A-Z]?/i.exec(propCyber.quote || '') || /LMA\s?5\d{3}[A-Z]?/i.exec(propCyber.context || '') || [] )[0]
            ? 'the ' + ( /LMA\s?5\d{3}[A-Z]?/i.exec(propCyber.quote || '') || /LMA\s?5\d{3}[A-Z]?/i.exec(propCyber.context || '') )[0].toUpperCase().replace(/\s/g, '') + ' exclusion already on the policy'
            : 'the property cyber exclusion already on the policy')
        + ', or add difference-in-conditions cover. Until then a remote compromise that damages insured equipment is uninsured on both towers.'
    });
  }

  // --- 2. Cyber-caused bodily injury -------------------------------------
  const cyberBI = spFindInKind(pdfTexts, 'cyber',
    ['bodily injury', 'any physical injury, sickness, disease'],
    /exclusion|does not apply|this policy does not/i);
  if (cyberBI) {
    // "p. Electronic Data Damages arising out of the loss of..." is the whole
    // heading an ISO exclusion gets; requiring the word "exclusion" nearby
    // missed it on a page that titles the section once, at the top.
    const glCyber = spFindInKind(pdfTexts, 'gl',
      ['electronic data', 'access or disclosure of confidential', 'cyber'],
      /exclusion|does not apply|damages arising out of/i);
    // A carve-back reverses the finding, so it has to be read, not assumed.
    // This GL's electronic-data exclusion ends "However, this exclusion does
    // not apply to liability for damages because of 'bodily injury'" — which
    // means the GL DOES respond, and calling the gap critical would send the
    // broker to the market for cover the insured already has.
    const glCarveBack = !!(glCyber &&
      /(?:this )?exclusion does not apply[^.]{0,160}bodily injury/i.test(glCyber.context || glCyber.quote || ''));
    out.push({
      id: 'cyber-bi-gap',
      severity: (glCyber && !glCarveBack) ? 'CRITICAL' : (glCarveBack ? 'MODERATE' : 'HIGH'),
      title: glCarveBack
        ? 'Bodily injury from a cyber event — the GL answers, not the cyber policy'
        : 'Bodily injury arising from a cyber event — excluded on the cyber policy',
      mechanism: glCarveBack
        ? 'The cyber policy excludes bodily injury, so a network event that injures someone must be picked up by the general liability policy — and it is: the GL\'s electronic-data exclusion carves bodily injury back in. The program answers, on the GL rather than the cyber tower.'
        : glCyber
        ? 'The cyber policy excludes bodily injury, and the general liability policy carries a cyber/electronic-data exclusion of its own with no bodily-injury carve-back. Injury caused by a network event has no home on either.'
        : 'The cyber policy excludes bodily injury, so injury caused by a network event must be picked up by the general liability policy. Confirm the GL carries no cyber or electronic-data exclusion.',
      evidence: [Object.assign({ label: 'Cyber policy excludes bodily injury' }, cite(cyberBI))]
        .concat(glCyber ? [Object.assign({
          label: glCarveBack ? 'GL electronic-data exclusion — bodily injury carved back IN'
                             : 'GL cyber / electronic data exclusion' }, cite(glCyber))] : []),
      action: glCarveBack
        ? 'No action beyond recording it: the GL carve-back is what makes the program answer. Re-check it at renewal — the carve-back is an ISO option, not a given.'
        : glCyber
        ? 'Obtain an affirmative bodily-injury-from-cyber grant, or remove the GL cyber exclusion, before renewal.'
        : 'Confirm in writing that the GL responds to bodily injury arising from a cyber event; the cyber policy will not.'
    });
  }

  // --- 3. Coverage parts that share one set of limits --------------------
  const shared = spFindInKind(pdfTexts, 'gl',
    ['apply once for the entire policy', 'not separately for each coverage part', 'regardless of the number of coverage parts'], null)
    || spFindInKind(pdfTexts, 'property',
    ['apply once for the entire policy', 'not separately for each coverage part'], null);
  if (shared) {
    out.push({
      id: 'shared-limits',
      severity: 'HIGH',
      title: 'Two coverage parts, one set of limits',
      mechanism: 'The declarations present a limit block per coverage part, which reads as separate towers. The limits clause says otherwise: they apply once for the entire policy. A loss under one part erodes the aggregate available to the other.',
      evidence: [Object.assign({ label: 'Limits of Insurance clause' }, cite(shared))],
      action: 'Show the insured the aggregate they actually have, not the sum of the blocks on the declarations. Price a separate tower or a higher aggregate if the exposure warrants it.'
    });
  }

  // --- 4. Professional services excluded, with no E&O in force -----------
  const profExcl = spFindInKind(pdfTexts, 'gl',
    ['rendering of or failure to render any professional services', 'engineers, architects or surveyors professional liability', 'cg 22 43'],
    /exclusion|does not apply/i);
  const eo = o.eoScan || null;
  const eoInForce = !!(eo && eo.eoDetected && eo.eoType !== 'embedded_ebl');
  if (profExcl && !eoInForce) {
    const notPurchased = (eo && (eo.notInForce || []).length) ? eo.notInForce[0] : null;
    out.push({
      id: 'professional-gap',
      severity: 'HIGH',
      title: 'Professional services excluded on the GL, with no professional liability in force',
      mechanism: 'The general liability policy excludes bodily injury, property damage and personal and advertising injury arising out of professional services'
        + (notPurchased ? ', and the only professional liability coverage part in the program is listed on a schedule with no limit' : '')
        + '. A claim pleaded as negligent design, sizing or specification has no responding policy.',
      evidence: [Object.assign({ label: 'GL professional services exclusion' }, cite(profExcl))]
        .concat(notPurchased ? [{ label: 'Listed with no limit', file: notPurchased.fileName, page: notPurchased.page, section: notPurchased.section, quote: notPurchased.line }] : []),
      action: 'Quote standalone professional / technology E&O sized to the design and specification work performed, or obtain the carrier\'s price to switch on the coverage part already printed on the schedule.'
    });
  }

  // --- 5. The same peril excluded on one policy and covered on another ---
  // Reported the other way up from rule 1: where a policy INSURES a peril that
  // a second policy excludes outright, the broker should know which tower
  // answers before the loss decides for them.
  [['communicable disease', ['communicable disease'], /exclusion|does not insure|does not apply/i],
   ['defective material, workmanship or design', ['defects of material, workmanship, design', 'leg 2/96'], null]
  ].forEach(([label, terms, near]) => {
    const hit = spFindInKind(pdfTexts, 'property', terms, near);
    if (!hit) return;
    out.push({
      id: 'peril-' + label.replace(/[^a-z]+/gi, '-').toLowerCase(),
      severity: 'MODERATE',
      title: 'Property policy excludes ' + label,
      mechanism: label === 'defective material, workmanship or design'
        ? 'A "consequences" defects wording excludes the cost of putting right the defective part itself. On a program whose principal loss scenario is a defective component, that is the half of the claim the insured most expects to recover.'
        : 'The peril is excluded outright on the property policy. Where another policy in the program is assumed to respond, confirm it in writing.',
      evidence: [Object.assign({ label: 'Property policy wording' }, cite(hit))],
      action: 'Confirm which policy in the program answers for this peril, and record the answer with the file.'
    });
  });

  // Nothing fires on the coverage mix: a rule with no located language emits
  // nothing at all, so an empty result means the documents did not evidence a
  // contradiction — not that none exists in a policy nobody uploaded.
  return out;
};

// ===== P&C exclusion grounding =============================================
// Section 7 printed a CITATION for every alert it raised — "[GL Policy, Common
// Policy Conditions, Sec. IV — Exclusions, p. 8–12: 'This policy does not
// apply to any claim arising out of criminal acts...']" — with the page range
// and the quote written into the alert definition, not read from the uploaded
// policy. The alert fires on the coverage mix and the vertical, so that
// citation was attached to documents nobody had checked.
//
// The alerts are still worth raising: they are the exclusions that most often
// void a coverage grant. What has to change is the claim being made. An alert
// whose language is LOCATED in the upload is a finding about this policy and
// is quoted with its real file and page; an alert that cannot be located is
// not reported.
//
// Terms are derived from the exclusion's own name so all 53 alerts are
// searchable without hand-writing a term list for each, with an override map
// where policies word it differently from the label.
const PC_EXCLUSION_TERM_OVERRIDES = {
  'Criminal Acts Exclusion': ['criminal act', 'criminal, dishonest', 'dishonest, fraudulent, criminal'],
  'Prior Acts / Prior Knowledge Exclusion': ['prior acts', 'prior knowledge', 'known circumstance'],
  'Professional Services Exclusion': ['professional services', 'rendering or failure to render professional'],
  'Employment-Related Practices Exclusion': ['employment-related practices', 'employment related practices'],
  'Pollution Exclusion': ['pollution exclusion', 'pollutants', 'discharge, dispersal'],
  'Absolute Pollution Exclusion': ['absolute pollution', 'pollutants', 'discharge, dispersal, seepage'],
  'Communicable Disease Exclusion': ['communicable disease', 'virus or bacteria'],
  'Cyber Exclusion': ['cyber', 'electronic data', 'access or disclosure'],
  'War & Terrorism Exclusion': ['war', 'terrorism', 'hostile or warlike'],
  'Contractual Liability Exclusion': ['contractual liability', 'insured contract'],
  'Wear and Tear Exclusion': ['wear and tear', 'gradual deterioration'],
  'Prior or Pending Litigation Exclusion': ['prior or pending', 'pending litigation'],
  'Insured vs. Insured Exclusion': ['insured versus insured', 'insured vs. insured', 'claim by or on behalf of any insured'],
};

window.pcExclusionTerms = function(ea){
  if (Array.isArray(ea.terms) && ea.terms.length) return ea.terms;
  const override = PC_EXCLUSION_TERM_OVERRIDES[ea.exclusion];
  if (override) return override;
  const label = String(ea.exclusion || '');
  const base = label
    .replace(/\b(exclusions?|limitations?|endorsements?|clauses?|provisions?|requirements?|conditions?|sublimits?|deductibles?|warrant(?:y|ies)|caps?)\b/ig, '')
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!base) return [];
  // "Sexual Abuse/Molestation" searches for either half — a policy names one or
  // the other, rarely the slashed pair.
  const parts = base.split(/\s*[\/&]\s*|\s+and\s+/).map(x => x.trim()).filter(x => x.length >= 5);
  const terms = [base].concat(parts.filter(p => p !== base));
  return [...new Set(terms)];
};

// One grounding implementation for both report types — the EB one already
// quotes the matched LINE with its printed page number.
// The language has to be EXCLUSIONARY, not merely present. "Independent
// contractor" sits in every definitions section and "flood" in every list of
// natural perils; quoting one of those as an exclusion finding is a false
// positive wearing a real citation, which is worse than the canned citation it
// replaced.
const PC_EXCLUSION_CONTEXT = /(exclusion|excluded|we\s+will\s+not\s+pay|does\s+not\s+apply|shall\s+not\s+apply|not\s+covered|no\s+coverage\s+(?:is\s+)?(?:provided|afforded)|this\s+insurance\s+does\s+not)/i;

// A COVERAGE GRANT IS NOT AN EXCLUSION.
//
// Page 265 of Aspen View's package is the ABUSE LIABILITY COVERAGE
// ENDORSEMENT:
//
//     This endorsement modifies insurance provided under the following:
//        SEXUAL MISCONDUCT LIABILITY COVERAGE
//     A. Paragraph 1. Insuring Agreement of Section I - Coverage SL is
//        replaced by the following:
//        COVERAGE - SL - SEXUAL MISCONDUCT LIABILITY AND ABUSE LIABILITY
//        1. Insuring Agreement
//           a. We will pay those sums that the insured becomes legally
//              obligated to pay...
//
// That is the policy GRANTING abuse liability cover, and Section 7 reported it
// as "General Liability vs. Abuse/Molestation Exclusion". The requireNear test
// passed because the word "exclusion" appears further down the same
// endorsement, as every coverage form carries its own exclusions list.
//
// Telling a school it has an abuse exclusion when the endorsement in front of
// you buys abuse cover is the most damaging inversion this section can
// produce: it is confident, specific, cited to a real page, and the opposite
// of the truth.
//
// So the immediate context is read for which KIND of provision the match sits
// in. Grant language between the heading and the match, with no operative
// exclusionary wording in between, means the match is inside a grant.
const PC_GRANT_CONTEXT = /\b(?:insuring\s+agreement|we\s+will\s+pay\s+those\s+sums|coverage\s+is\s+extended|is\s+added\s+to\s+(?:the\s+)?(?:coverages?|insuring)|following\s+coverage\s+is\s+added|liability\s+coverage\s+endorsement)\b/i;
const PC_OPERATIVE_EXCLUSION = /\b(?:this\s+insurance\s+does\s+not\s+apply|we\s+will\s+not\s+pay|is\s+excluded|the\s+following\s+exclusion\s+is\s+added|shall\s+not\s+apply)\b/i;

// Decided by the ENDORSEMENT'S TITLE, not by keywords in a window.
//
// A keyword window is both too small and too credulous here. The abuse
// endorsement's insuring agreement runs for pages, so a 700-character
// lookback never reaches "Insuring Agreement" — and what it does reach is the
// standard duty-to-defend sentence, "no duty to defend ... any suit seeking
// damages to which Coverage SL does not apply", which reads as exclusionary
// and is not.
//
// The heading settles it in one line. "ABUSE LIABILITY COVERAGE ENDORSEMENT"
// names a coverage; inside it, only an operative exclusion sentence on the
// match's OWN LINE is an exclusion. Everything else in a coverage form is the
// grant, its conditions, or its definitions.
function spInCoverageGrant(text, index) {
  const src = String(text || '');
  const before = src.slice(Math.max(0, index - 8000), index);
  const lines = before.split('\n');
  let title = null;
  for (let i = lines.length - 1; i >= 0; i--) {
    const l = lines[i].replace(/[*_#]/g, '').replace(/\s+/g, ' ').trim();
    if (l.length < 8 || l.length > 90) continue;
    if (!/^[A-Z][A-Z0-9 \-\u2013\u2014&\/().,'"]+$/.test(l)) continue;
    title = l; break;
  }
  if (!title) return false;
  const grantTitle = /\bCOVERAGE\b|\bINSURING\s+AGREEMENT\b/.test(title) && !/\bEXCLUSION/.test(title);
  if (!grantTitle) return false;
  const lineStart = src.lastIndexOf('\n', index) + 1;
  let lineEnd = src.indexOf('\n', index);
  if (lineEnd < 0) lineEnd = src.length;
  return !PC_OPERATIVE_EXCLUSION.test(src.slice(lineStart, lineEnd));
}
if (typeof window !== 'undefined') window.spInCoverageGrant = spInCoverageGrant;

window.groundPCExclusion = function(ea, pdfTexts){
  const terms = window.pcExclusionTerms(ea);
  if (!terms.length) return null;
  return groundEBExclusion({ terms: terms }, pdfTexts, {
    requireNear: PC_EXCLUSION_CONTEXT,
    rejectWhere: spInCoverageGrant
  });
};

function groundEBExclusion(ea, pdfTexts, opts) {
  if (!ea.terms || !ea.terms.length || !pdfTexts || !pdfTexts.length) return null;
  // `opts.requireNear` — a regex the surrounding text must satisfy. Used by the
  // P&C caller to insist the match sits in EXCLUSIONARY language: the phrase
  // "independent contractor" appears in every definitions section, and quoting
  // one as an exclusion finding is a false positive wearing a real citation.
  const near = opts && opts.requireNear;
  for (const doc of pdfTexts) {
    // Keep each page's start offset while joining, so a citation can name the
    // page the language is ON. Joining the pages and counting markdown
    // separators — which is what the EB grounder does for LlamaParse output —
    // resolves every P&C policy to "p. 1", because a policy PDF has no `---`
    // separators at all. A citation with the wrong page is worse than none.
    let text = '', starts = null;
    if (doc.pages && doc.pages.length) {
      starts = [];
      doc.pages.forEach((pg, i) => {
        starts.push({ at: text.length, page: (pg.page != null ? pg.page : i + 1) });
        text += (pg.text || '') + '\n';
      });
    } else {
      text = doc.text || '';
    }
    const pageAt = off => {
      if (!starts) return null;
      let p = starts[0].page;
      for (const s2 of starts) { if (s2.at <= off) p = s2.page; else break; }
      return p;
    };
    if (!text) continue;
    const low = text.toLowerCase();
    for (const term of ea.terms) {
      const needle = String(term).toLowerCase();
      let at = low.indexOf(needle);
      const reject = opts && opts.rejectWhere;
      if (near || reject) {
        // Walk the occurrences until one sits in exclusionary language AND is
        // not inside a coverage grant. An endorsement that BUYS a coverage
        // carries its own exclusions list further down, so the word
        // "exclusion" being somewhere nearby is not enough to prove that this
        // occurrence is one.
        while (at >= 0 && (
          (near && !near.test(text.slice(Math.max(0, at - 240), at + needle.length + 240))) ||
          (reject && reject(text, at))
        )) {
          at = low.indexOf(needle, at + needle.length);
        }
      }
      if (at < 0) continue;
      // Quote the LINE the term sits in. Walking back to the previous full stop
      // crossed page separators and headings and produced quotes like
      // "2 --- ## Limitations Benefits for a pre-existing condition…" — text
      // that appears nowhere on the page as a sentence.
      const lineStart = text.lastIndexOf('\n', at) + 1;
      let lineEnd = text.indexOf('\n', at);
      if (lineEnd < 0) lineEnd = text.length;
      let raw = text.slice(lineStart, lineEnd);
      let rel = at - lineStart;
      if (raw.length > 320) {                       // long paragraph: the sentence around the match
        let a2 = raw.lastIndexOf('.', rel);
        a2 = (a2 < 0 || rel - a2 > 300) ? Math.max(0, rel - 150) : a2 + 1;
        let b2 = raw.indexOf('.', rel + term.length);
        b2 = (b2 < 0 || b2 - rel > 300) ? Math.min(raw.length, rel + term.length + 170) : b2 + 1;
        raw = (a2 > 0 ? '…' : '') + raw.slice(a2, b2) + (b2 < raw.length ? '…' : '');
      }
      let quote = raw.replace(/\s+/g, ' ').replace(/^[#>*\s|-]+/, '').trim();
      // A heading-only line ("War", "Communicable Disease Exclusion") is a
      // pointer, not the language. Pull the following lines in until there is
      // enough of the clause to read.
      if (quote.length < 60) {
        const more = text.slice(lineEnd, lineEnd + 400).split('\n')
          .map(x => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
        for (const nxt of more) {
          quote += ' ' + nxt;
          if (quote.length >= 140) break;
        }
        quote = quote.trim();
      }
      // Real page for the start of the quote, and for its end — an exclusion
      // that runs over a page break cites the range it actually occupies.
      const realStart = pageAt(at);
      const realEnd = pageAt(lineEnd);
      const pg = ebPageForOffset(text, at);
      const startPage = realStart != null ? realStart : (pg.printed || pg.parsed);
      const endPage = realEnd != null ? realEnd : startPage;
      return {
        fileName: doc.fileName || 'uploaded document',
        page: startPage,
        pageEnd: (endPage && endPage !== startPage) ? endPage : null,
        pageNote: (realStart != null || pg.printed) ? '' : ' (parsed page)',
        // The section the language sits in, read backwards from the match —
        // "Sec. V — Exclusions" rather than a bare page number.
        section: (typeof findNearestSection === 'function')
          ? String(findNearestSection(text, at) || '').replace(/\s+/g, ' ').trim() : null,
        term: term,
        quote: quote.length > 320 ? quote.slice(0, 317) + '…' : quote,
        // Wider window for callers that must read PAST the quoted line — a
        // carve-back usually sits in the sentence after the exclusion, and
        // reading only the quote would miss it and invert the finding.
        context: text.slice(Math.max(0, at - 200), at + 700).replace(/\s+/g, ' ').trim()
      };
    }
  }
  return null;
}

function getEBExclusionAlerts(detectedCoverages, vertical) {
  const alerts = [];
  EB_EXCLUSION_ALERTS.forEach(ea => {
    // Always show Group Health and LTD exclusion warnings
    if (ea.coverage === 'Group Health' || ea.coverage === 'Long-Term Disability') {
      alerts.push(ea);
    } else if (detectedCoverages.includes(ea.coverage)) {
      alerts.push(ea);
    }
    // Always show Life/AD&D and STD warnings as they affect all benefits programs
    if (ea.coverage === 'Life/AD&D' || ea.coverage === 'Short-Term Disability') {
      if (!alerts.find(a => a === ea)) alerts.push(ea);
    }
  });
  return alerts;
}

// ===== FALLBACK COVERAGE HEURISTICS =====
// When a file doesn't match known patterns, attempt keyword-based classification
const FALLBACK_KEYWORDS = {
  "General Liability": ["liability", "liab", "gl", "cgl", "occurrence", "claims-made"],
  "Property": ["property", "building", "real estate", "structure", "premises"],
  "Workers Compensation": ["worker", "wc", "comp", "employer", "occupational"],
  "Commercial Auto": ["auto", "vehicle", "fleet", "motor", "truck", "transportation"],
  "Umbrella/Excess": ["umbrella", "excess", "surplus"],
  "Professional Liability": ["professional", "e&o", "malpractice", "errors"],
  "Cyber Liability": ["cyber", "data", "breach", "network", "privacy", "digital"],
  "EPLI": ["employment", "epli", "workplace", "harassment", "discrimination"],
  "D&O": ["director", "officer", "d&o", "board", "management"],
  "Crime/Fidelity": ["crime", "fidelity", "dishonesty", "theft", "bond", "forgery"],
  "Equipment Breakdown": ["equipment", "boiler", "machinery", "mechanical"],
  "Flood Insurance": ["flood", "nfip", "water damage"],
  "Package Policy": ["package", "bop", "portfolio", "bundle", "multi-line"],
  // Sexual Abuse / Molestation fallback keywords (deep text scan)
  "Sexual Abuse/Molestation": ["abuse or molestation", "sexual abuse", "molestation liability", "alleged participant", "pe gl ok5", "abusive conduct"],
  // Maritime/Marine fallback keywords
  "Marine General Liability": ["marine", "maritime", "dockside", "shoreside", "wharf", "terminal"],
  "Hull & Machinery": ["hull", "vessel", "tugboat", "barge", "pushboat", "watercraft"],
  "Protection & Indemnity": ["p&i", "indemnity marine", "vessel liability"],
  "Maritime Employers Liability": ["mel", "maritime employer", "non-owned vessel"],
  "Jones Act / Crew Coverage": ["jones act", "seaman", "maintenance cure", "admiralty"],
  "USL&H / Longshore": ["longshore", "usl&h", "harbor worker", "stevedore"],
  "Marine Builders Risk": ["marine builder", "boat builder", "drydock", "vessel construction"],
  "Marine Cargo / Freight": ["cargo", "freight", "transit", "inland marine"],
  "Marine Pollution Liability": ["marine pollution", "oil spill", "opa 90", "vessel pollution"],
  "Offshore Energy / OEE": ["offshore", "oee", "well control", "blowout", "drilling rig"],
  "Dredging Insurance": ["dredge", "dredging", "channel", "levee", "coastal restoration"],
  // Energy / Oil & Gas fallback keywords
  "Control of Well / OEE (Onshore)": ["well control", "blowout", "oee", "e&p", "upstream", "exploration"],
  "Pollution Legal Liability (PLL/EIL)": ["pll", "eil", "environmental impairment", "pollution liability", "remediation"],
  "Rig & Drilling Equipment": ["rig", "drilling equipment", "derrick", "workover"],
  "Oil Lease Property (OLP)": ["olp", "lease property", "wellhead", "pump jack", "tank battery"],
  "Pipeline Operators Insurance": ["pipeline", "gathering system", "midstream", "transmission line"],
  "Gas Processing & Compression": ["gas plant", "compressor", "ngl", "processing plant"],
  "Terminal & Tank Farm": ["tank farm", "petroleum terminal", "storage terminal", "loading rack"],
  "LNG / Gas Storage": ["lng", "gas storage", "liquefied natural gas"],
  "Refinery & Petrochemical": ["refinery", "petrochemical", "refining", "distillation"],
  "NORM Liability": ["norm", "radioactive material", "tenorm"],
  "Underground Resource Damage": ["underground resource", "subsurface trespass", "frac hit", "reservoir damage"],
  "RRC Compliance & Bonding": ["rrc", "railroad commission", "plugging bond", "p-5"],
  "MSA / Contractual Liability (Energy)": ["msa", "knock-for-knock", "oilfield service"],
  "Business Interruption (Energy)": ["production interruption", "loss of production", "turnaround"],
  // Transportation / Logistics fallback keywords
  "Motor Carrier Liability": ["motor carrier", "for-hire carrier", "commercial auto trucking", "bipd", "fmcsa"],
  "Motor Truck Cargo": ["motor truck cargo", "cargo", "freight coverage", "goods in transit", "mtc"],
  "MCS-90 Compliance": ["mcs-90", "mcs90", "financial responsibility endorsement", "bmc-91"],
  "Bobtail / Non-Trucking Liability": ["bobtail", "non-trucking", "ntl", "deadhead"],
  "Trailer Interchange": ["trailer interchange", "non-owned trailer", "uiia"],
  "Garagekeepers Liability": ["garagekeepers", "garage keepers", "customer vehicles"],
  "Refrigeration Breakdown": ["refrigeration breakdown", "reefer", "temperature-controlled", "spoilage"],
  "Warehousing Legal Liability": ["warehousing legal", "warehouseman", "bailee", "goods in storage"],
  "Freight Broker E&O": ["freight broker", "broker e&o", "transportation professional", "bmc-84"],
  "Contingent Cargo": ["contingent cargo", "secondary cargo", "contingent on failure"],
  // Construction fallback keywords
  "Builders Risk": ["builders risk", "course of construction", "construction property"],
  "Wrap-Up Programs (OCIP/CCIP)": ["ocip", "ccip", "wrap-up", "controlled insurance program"],
  "Subcontractor Default Insurance (SDI)": ["subcontractor default", "sdi", "first-party default"],
  "Contractors Pollution Liability": ["contractors pollution", "cpl", "construction pollution"],
  "Installation Floater": ["installation floater", "property being installed", "materials floater"],
  "Equipment Floater": ["equipment floater", "contractor's equipment", "mobile equipment"],
  "Railroad Protective Liability": ["railroad protective", "rrp", "rpl", "railway right-of-way"],
  "Products-Completed Operations": ["completed operations", "pco", "products-completed"],
  "Contractors Professional Liability (Design-Build)": ["contractors professional", "design-build", "cprl", "rectification"],
  // Healthcare fallback keywords
  "Medical Professional Liability": ["medical professional", "malpractice", "med mal", "physician liability"],
  "Hospital Professional Liability": ["hospital professional", "hpl", "hospital liability"],
  "Clinical Trial Liability": ["clinical trial", "research subjects", "investigational", "irb"],
  "Telemedicine/Telehealth Coverage": ["telemedicine", "telehealth", "virtual care", "digital health"],
  "HIPAA/Privacy Breach Liability": ["hipaa", "phi", "protected health information", "hitech"],
  "Sexual Abuse/Molestation (SAM)": ["sam coverage", "abuse molestation healthcare"],
  "Medical Directors Liability": ["medical director", "credentialing", "peer review", "medical staff"],
  "Managed Care E&O": ["managed care", "utilization review", "mco liability", "hmo liability"],
  // Same reason as the primary list: the bare phrase is ordinary GL wording.
  "Vicarious Liability (Staffing)": ["locum tenens", "agency nurse", "staffing liability", "staffing agency liability"],
  "Medicare/Medicaid Defense": ["medicare defense", "medicaid defense", "false claims act", "oig investigation"],
  // Manufacturing / Industrial fallback keywords
  "Product Recall Insurance": ["product recall", "recall expense", "product withdrawal"],
  "Product Contamination": ["product contamination", "accidental contamination", "malicious tampering"],
  "Equipment Breakdown / Boiler & Machinery": ["boiler and machinery", "pressure vessel", "mechanical breakdown manufacturing"],
  "Supply Chain / Contingent BI": ["contingent business interruption", "dependent property", "supply chain insurance"],
  "Environmental Impairment Liability": ["environmental impairment", "eil", "pollution legal manufacturing"],
  "OSHA Defense Coverage": ["osha defense", "osha citation", "safety violation defense"],
  "Manufacturers E&O": ["manufacturers e&o", "non-conforming product", "manufacturing professional"],
  "Stock Throughput (STP)": ["stock throughput", "stp", "warehouse-to-warehouse", "throughput policy"],
  "Foreign Product Liability": ["foreign product", "worldwide products", "import liability"],
  "Inland Marine (Goods in Process)": ["goods in process", "work in process", "wip coverage"]
};

// ===== PDF TEXT-BASED COVERAGE DETECTION =====
// Context-aware scanner: detects coverages from PDF text but filters out false positives
// from exclusion clauses, definitions, standard GL sub-components, and boilerplate language.
// v2.5 — Fixes false positive bug where exclusion/definition text triggered coverage detection.
// v2.5.1 — Boilerplate endorsement listing filter (Nuclear Energy Exclusion, Earthquake Exclusion)
// v2.5.2 — ISO auto forms triplet detection (Motor Carrier false positive fix), Equipment Rental vertical, SAM benchmark scoping
// v2.6 — Tesseract.js OCR engine for scanned PDF support (browser-based, no server required).
//         Smart page sampling with declarations-adjacent follow-up for large package policies.
//         Scanned pages detected via text-layer analysis; OCR'd text integrated into existing coverage pipeline.
//          + negative context window expanded to 500 chars (catches USL&H in WC exclusion text)

// Negative context patterns — if a match appears within this text window, it's likely
// referenced in an exclusion/definition/limitation rather than as actual coverage.
const NEGATIVE_CONTEXT_PHRASES = [
  'exclusion', 'excluded', 'does not apply', 'this insurance does not',
  'not covered', 'we will not pay', 'we do not cover', 'no coverage for',
  'exception', 'limitation', 'this policy does not', 'not applicable',
  'is excluded', 'are excluded', 'shall not apply', 'will not apply',
  'not included', 'specifically excluded', 'precluded', 'barred',
  'this coverage does not apply', 'unless otherwise', 'waiver of',
  'this endorsement does not provide', 'does not include',
  'section does not apply', 'paragraph does not apply'
];

// Definition-section markers — matches near these are in definitions, not coverages
const DEFINITION_MARKERS = [
  'definitions', 'as used in this', 'means:', 'means the', 'is defined as',
  'for the purpose of this', 'the term', 'as defined in', 'shall mean'
];

// Standard GL aggregate sublimit labels — NOT standalone coverages
const GL_SUBLIMIT_PATTERNS = [
  /products[\s\u2013\u2014–-]*completed\s+operations\s+aggregate/i,
  /personal\s+(?:&|and)\s+adv(?:ertising)?\s+injury/i,
  /damage\s+to\s+(?:rented|premises)/i,
  /med(?:ical)?\s+exp(?:ense)?\s+\(any\s+one\s+person\)/i
];

/**
 * Check if a regex match position is inside a negative context (exclusion, definition, etc.).
 * Looks at a window of text around the match position for negative context phrases.
 * @param {string} txt - Full text
 * @param {number} matchStart - Start index of the match
 * @param {number} matchEnd - End index of the match
 * @param {number} windowSize - Characters to look before/after (default 500)
 * @returns {boolean} true if match is in negative context
 */
/* A CLAUSE THAT AMENDS A COVERAGE PART IS NOT A GRANT OF IT.
 *
 * An endorsement body refers to the part it edits:
 *
 *     C. The Representations Condition in the Public Management Liability
 *        Coverage Part and Public Management Liability Coverage Part
 *        (Claims Made) is replaced by the following:
 *
 * That named a management liability coverage part on a program that carries
 * none, and every existing guard let it through: it is not an applicability
 * list, not an exclusion title, not an underlying schedule, and the negative
 * vocabulary has nothing to say about an amendment. So Section 6 reported
 * "D&O / Management Liability — Coverage detected", and the Title IX row,
 * which reads across to D&O, reported the same.
 *
 * spInGrantPosition already answers false here, but requiring it of every
 * detection is too strict to use: measured over one four-policy program it
 * would also drop Commercial Property from the package policy, which is real
 * and whose limits the report reads correctly. So this asks the narrower
 * question — is the coverage named as the OBJECT of an amendment — which is
 * what the sentence above actually is.
 */
const _SP_AMEND_VERB = /\b(?:is|are)\s+(?:replaced|amended|deleted|added|revised)\b|\breplaced\s+by\b|\bis\s+deleted\s+in\s+its\s+entirety\b/i;
const _SP_CLAUSE_NOUN = /\b(?:condition|provision|clause|paragraph|subparagraph|section|definition|exclusion|endorsement)\b/i;

function spAmendsAClauseOf(text, index) {
  /* Read a window, not the line. The clause wraps, and in the document that
   * prompted this the coverage name sits at the end of one line while the
   * verb that governs it — "is replaced by the following" — begins the next.
   * A line-scoped test saw a clause noun with no verb and let it through.
   *
   * The window is bounded tightly on both sides so an amendment elsewhere on
   * the page cannot vouch for an unrelated mention. */
  const t = String(text || '');
  const before = t.slice(Math.max(0, index - 200), index);
  const after = t.slice(index, index + 200);
  if (!_SP_AMEND_VERB.test(before + ' ' + after)) return false;
  // The clause noun has to come BEFORE the coverage name: "the Representations
  // Condition in the X Coverage Part". Where it comes after, the sentence is
  // doing something else and this says nothing.
  return _SP_CLAUSE_NOUN.test(before);
}

if (typeof window !== 'undefined') window.spAmendsAClauseOf = spAmendsAClauseOf;

function isInNegativeContext(txt, matchStart, matchEnd, windowSize = 500) {
  const before = txt.substring(Math.max(0, matchStart - windowSize), matchStart).toLowerCase();
  const after = txt.substring(matchEnd, Math.min(txt.length, matchEnd + windowSize)).toLowerCase();
  const window = before + ' ' + after;

  // Check exclusion / limitation phrases
  for (const phrase of NEGATIVE_CONTEXT_PHRASES) {
    if (window.includes(phrase)) return true;
  }
  // Check definition markers
  for (const marker of DEFINITION_MARKERS) {
    if (window.includes(marker)) return true;
  }
  return false;
}

/**
 * Check if a match is just a standard GL sublimit line (not standalone coverage).
 * e.g., "Products – Completed Operations Aggregate $2,000,000" is a GL line item.
 */
function isGLSublimitLine(txt, matchStart, matchEnd) {
  // Get a broader window around the match
  const windowStart = Math.max(0, matchStart - 100);
  const windowEnd = Math.min(txt.length, matchEnd + 200);
  const window = txt.substring(windowStart, windowEnd);

  for (const pat of GL_SUBLIMIT_PATTERNS) {
    if (pat.test(window)) return true;
  }
  // Check if it's inside a GL limits schedule ("each occurrence", "general aggregate" nearby)
  const lowerWindow = window.toLowerCase();
  const isGLSchedule = (
    (lowerWindow.includes('each occurrence') || lowerWindow.includes('general aggregate')) &&
    (lowerWindow.includes('personal') || lowerWindow.includes('damage to') || lowerWindow.includes('med exp'))
  );
  return isGLSchedule;
}

/**
 * Check if a match is inside a boilerplate endorsement listing.
 * ISO endorsements commonly list every possible coverage part/form they modify.
 * e.g., "NUCLEAR ENERGY LIABILITY EXCLUSION ENDORSEMENT... modifies: COMMERCIAL AUTO COVERAGE PART,
 *        COMMERCIAL GENERAL LIABILITY COVERAGE PART, ... RAILROAD PROTECTIVE LIABILITY COVERAGE PART..."
 * If multiple "COVERAGE PART" or "COVERAGE FORM" mentions appear near the match, it's boilerplate.
 */
/* The guard for this already existed — see spInUnderlyingSchedule further
 * down, written when an umbrella's Schedule of Underlying Insurance produced a
 * second General Liability row. It was never applied in DETECTION, only in
 * limit reading, so the coverage still appeared; it just had no limits of its
 * own to show.
 *
 * Tulsa Honor Academy's excess policy attaches a "Schedule of Controlling
 * Underlying Insurance" naming Employers' Liability, Commercial General
 * Liability at $1,000,000 / $3,000,000 / $3,000,000 and Automobile Liability
 * at $1,000,000 combined single limit, each with its own carrier and policy
 * number. Read as the excess policy's own coverages, one placement reported
 * Workers Compensation, Commercial Auto, D&O, Educators Legal Liability,
 * Professional Liability and Sexual Abuse/Molestation — six coverages it does
 * not grant — and Section 6 benchmarked them as though it did.
 *
 * A guard that exists and is applied in one of two places is the same defect
 * as no guard, discovered later.
 */

/* A COVERAGE NAMED IN AN EXCLUSION TITLE IS EXCLUDED, NOT CARRIED.
 *
 * Tulsa Honor Academy's property policy attaches LMA 5401, "Property Cyber and
 * Data Exclusion". The engine reported Cyber Liability as a coverage of that
 * property policy, with limits. A policy that explicitly excludes a peril was
 * reported as insuring it — which is the most dangerous direction a coverage
 * detector can be wrong in, because it closes a gap that is real.
 *
 * "Deleted", "removed" and "does not apply" reverse it: an exclusion that has
 * been struck is coverage granted, and those lines must not veto.
 */
/* WHERE A COVERAGE NAME SITS DECIDES WHETHER IT IS A GRANT.
 *
 * A policy names a coverage in two very different kinds of place. It heads the
 * coverage part it is granting — "DIRECTORS & OFFICERS AND ENTITY LIABILITY
 * COVERAGE PART", a declarations row with a limit beside it. And it names the
 * same coverage in prose it is carving out: "d. Workers' Compensation And
 * Similar Laws" in an exclusion list, "professional liability insurance
 * (except Directors and Officers Liability)" inside a definition. Tulsa Honor
 * Academy's excess policy has five mentions of Workers' Compensation and one
 * of D&O, and every one of them is the second kind — yet both appeared in the
 * limits with real dollar figures beside them.
 *
 * This answers only the narrow question: is THIS mention in a position where a
 * policy grants cover? It is deliberately conservative about what counts, and
 * it is never consulted alone — see the withdrawal gate in
 * extractDeclarationsLimits.
 */
/* A TEST SEAM, AND THE ONLY ONE IN THIS FILE.
 *
 * spInGrantPosition is a chain of vetoes, each added to fix one defect, and
 * each silently covering cases nobody wrote down. Narrowing one of them to fix
 * a single answer moved three others, and only a rerun of a real policy caught
 * it — twice.
 *
 * Toggling a guard from a test is how its coupling gets measured: disable one,
 * count how many answers move, and record the number. Then the next narrowing
 * says out loud how many things it changed. The defaults are all true and
 * production never writes to this.
 */
const SP_GRANT_GUARDS = {
  underlyingSchedule: true, exclusionTitle: true, boilerplateList: true,
  exclusionWord: true, lengthCap: true, finiteVerb: true, prefixMarker: true
};
if (typeof window !== 'undefined') window.spGrantGuards = SP_GRANT_GUARDS;
const spGuard = (name) => SP_GRANT_GUARDS[name] !== false;

/* THE SLICE OF A PAGE THAT BELONGS TO ONE COVERAGE PART.
 *
 * A management liability page heads several coverage parts and states a limit
 * under each, in identical words. An extractor given the whole page and told
 * to find "aggregate ... $" finds the first one, whoever it belongs to. That
 * is how an EPL row took $5,000,000 from the D&O part six lines above it, on a
 * page where EPL is named in the declarations' list of parts and given no
 * limit of its own.
 *
 * Where the text heads coverage parts, a coverage owns the slice from its own
 * heading to the next one. Where it heads none — a monoline policy, one part
 * for the whole document — the slice is everything, which is the property
 * migration's rule that a heading narrows and does not exclude. And where it
 * heads parts but none of them is this one, the answer is nothing: the page
 * has said whose figures these are.
 */
function spSectionForCoverage(text, labelRe) {
  const t = String(text || '');
  if (!t) return '';
  const lines = t.split('\n');
  const heads = [];
  let at = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && trimmed.length <= 80 && /\bcoverage\s+(?:part|form)\b/i.test(trimmed)) {
      heads.push({ at, line: trimmed });
    }
    at += line.length + 1;
  }
  if (!heads.length) return t;
  const mine = heads.findIndex(h => labelRe.test(h.line));
  if (mine < 0) return '';
  const end = mine + 1 < heads.length ? heads[mine + 1].at : t.length;
  return t.slice(heads[mine].at, end);
}

if (typeof window !== 'undefined') window.spSectionForCoverage = spSectionForCoverage;

/* Writes whatever the backend returned for this session to a file, for the
 * real-programs harness. Console-only, deliberately: it is a debugging tool
 * for whoever holds the documents, not a product feature. */
if (typeof window !== 'undefined') {
  window.spSaveParsed = function (name) {
    const cap = window.spParsedCapture || [];
    if (!cap.length) { console.warn('[capture] nothing parsed yet — upload the documents first'); return; }
    const blob = new Blob([JSON.stringify(cap, null, 1)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name || 'parsed-capture.json';
    a.click();
    console.log(`[capture] wrote ${cap.length} documents, ${cap.reduce((n, c) => n + c.text.length, 0)} chars`);
  };
}

/* HOW BACKEND TEXT BECOMES PAGES — the whole decision, in one function.
 *
 * It lived inside extractPdfText, which needs a File and a fetch, so nothing
 * could test it and no harness could reproduce it. A user then ran the real
 * product and got limits nobody recognised, because production was reading
 * whole policies as one page while every check in the repository read text
 * that already had page breaks. The decision that differed was unreachable
 * from a test.
 *
 * Returns { pages, how } where `how` is why the pages look as they do:
 *   'rules'     the parser's own page rules, and they agree with the PDF
 *   'footers'   recovered from the document's form footers, numbering approximate
 *   'one-page'  neither was available; every reader that works page by page
 *               is about to degrade, and the caller should say so
 */
function spPaginateBackendText(text, declaredPages) {
  const raw = String(text || '');
  /* A FORM FEED IS A PAGE BREAK, AND IT IS NOT A GUESS.
   *
   * pdftotext -layout emits \f between pages — the extractor telling us where
   * the page ended, rather than a rule inferred from markdown or a footer
   * recovered from the text. When it is present it beats everything below it,
   * and the page numbers are the document's own. */
  const ff = raw.split('\f');
  if (ff.length > 1) {
    const pages = ff.filter((t, i) => t.trim() || i < ff.length - 1);
    if (pages.length > 1) {
      return { how: 'form-feeds',
               pages: pages.map((t, i) => ({ page: i + 1, text: t, backend: true })) };
    }
  }
  const mdPages = raw.split(/\n\s*---\s*\n/);
  const declared = Number(declaredPages) || 0;
  const rulesUsable = mdPages.length > 1 && (!declared || Math.abs(mdPages.length - declared) <= 2);
  if (rulesUsable) {
    return { how: 'rules', pages: mdPages.map((t, i) => ({ page: i + 1, text: t, backend: true })) };
  }
  const recovered = spRecoverPages(raw);
  if (recovered) {
    return { how: 'footers',
             pages: recovered.map((t, i) => ({ page: i + 1, text: t, backend: true, pageApprox: true })) };
  }
  return { how: 'one-page', pages: [{ page: 1, text: raw, backend: true }] };
}

if (typeof window !== 'undefined') window.spPaginateBackendText = spPaginateBackendText;

/* PAGES RECOVERED FROM THE DOCUMENT'S OWN FOOTERS.
 *
 * A policy reaches the engine as one blob whenever the parser's page rules do
 * not agree with the PDF's page count, and every reader that works page by
 * page degrades at once. On Tulsa Honor Academy the same four documents, read
 * as one page each instead of many, gave the auto CSL as $3,000,000 instead of
 * $1,000,000, the ELL aggregate as $5,000,000 instead of $3,000,000, and the
 * building sum insured as "Values, subject to $25,000 minimum". Same bytes,
 * same code — only the pagination differed.
 *
 * Insurance paper stamps its own page numbers, because a policy is assembled
 * from forms that each carry a footer: "CA DS 03 11 20  Page 3 of 14",
 * "PF-55501D (07/21)  Page 2 of 4". Across these four documents 290 of 324
 * pages carry one. That is a page boundary the document itself asserts, and it
 * survives whatever the parser does to the layout.
 *
 * The recovered numbering is the FORM's, not the policy's — form after form
 * restarts at page 1 — so it is marked approximate and citations say so. A
 * boundary in roughly the right place is worth a great deal; a page number is
 * worth much less.
 */
// The marker itself. A footer sits at the end of a long line of padding —
// "CA DS 03 11 20  (c) Insurance Services Office, Inc., 2019      Page 3 of 14"
// — so the page ends at the end of the LINE the marker is on, not at the end
// of the marker. Anchoring the pattern to the start of the line found one
// footer in forty-eight, because the padding is longer than any sane cap.
const _SP_PAGE_FOOTER = /\bpage\s+\d{1,3}\s+of\s+\d{1,3}\b/gi;

function spRecoverPages(text) {
  const t = String(text || '');
  if (!t) return null;
  const ends = [];
  let m;
  const re = new RegExp(_SP_PAGE_FOOTER.source, 'gi');
  while ((m = re.exec(t)) !== null) {
    let eol = t.indexOf('\n', m.index + m[0].length);
    if (eol < 0) eol = t.length;
    /* A FOOTER ENDS ITS LINE. PROSE DOES NOT.
     *
     * "see page 1 of 2 of the schedule" is a sentence; "CA DS 03 11 20 ...
     * Page 3 of 14" is a page boundary, and what separates them is not how
     * many there are but where they sit. Counting was the first rule and it
     * rejected a two-page document while a long enough prose document would
     * have passed. */
    if (eol - (m.index + m[0].length) > 15) { if (re.lastIndex <= m.index) re.lastIndex = m.index + 1; continue; }
    if (!ends.length || eol > ends[ends.length - 1]) ends.push(eol);
    if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
  }
  if (ends.length < 2) return null;
  const pages = [];
  let from = 0;
  for (const end of ends) {
    const body = t.slice(from, end);
    if (body.trim()) pages.push(body);
    from = end;
  }
  const tail = t.slice(from);
  if (tail.trim()) pages.push(tail);
  /* A CAP, BECAUSE EVERY CONSUMER LOOPS THESE.
   *
   * Recovering boundaries from footers is only as well behaved as the text.
   * A document that stamps a marker on every few lines would produce thousands
   * of pages, and each of the twelve limit specs and nineteen coverage stances
   * walks all of them. Beyond a few hundred the pagination has stopped being
   * information; the tail is merged rather than multiplied.
   */
  const MAX = 400;
  if (pages.length > MAX) {
    const head = pages.slice(0, MAX - 1);
    head.push(pages.slice(MAX - 1).join('\n'));
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(`[extract] ${pages.length} footer boundaries — keeping ${MAX} and merging the rest`);
    }
    return head;
  }
  return pages.length > 1 ? pages : null;
}

if (typeof window !== 'undefined') window.spRecoverPages = spRecoverPages;

/* A PAGE WHOSE LABELS AND VALUES WERE EXTRACTED SEPARATELY.
 *
 * The browser asks the backend for LlamaParse. When LlamaParse is
 * unavailable the backend answers with pypdf instead, and pypdf reads a
 * declarations page column by column rather than row by row:
 *
 *     Each Occurrence                            <- six labels, no amounts
 *     Damage to Premises Rented to You
 *     Medical Expense
 *     Personal and Advertising Injury
 *     General Aggregate
 *     Products - Completed Operations Aggregate
 *     ...
 *     $1,000,000  $1,000,000  $3,000,000        <- and the amounts, detached
 *     $3,000,000  $10,000     $1,000,000
 *
 * Nothing on the page says which amount belongs to which label, and the order
 * does not carry it either: the true reading is 1M, 1M, 10K, 1M, 3M, 3M and
 * the amounts arrive as 1M, 1M, 3M, 3M, 10K, 1M. Pairing them by position
 * gets four of the six wrong — and that is exactly what a report showed: each
 * occurrence as $10,000 and medical expense as $3,000,000.
 *
 * So this page is not read for limits. A coverage with no limit says so on the
 * page and a broker goes and looks; a coverage with the wrong limit reads as a
 * finding. The whole of this week's work has been that one asymmetry.
 *
 * The fix for the figures themselves is upstream — the backend degrading to
 * pypdf without saying so — and the ledger now names the engine so the next
 * person sees it in one line rather than four days.
 */
function spColumnSplitPage(text) {
  const lines = String(text || '').split('\n');
  let labelOnly = 0, bareAmounts = 0;
  for (const raw of lines) {
    const l = raw.trim();
    if (!l || l.length > 60) continue;
    if (/^\$\s*[\d][\d,]*(?:\.\d{2})?$/.test(l)) { bareAmounts++; continue; }
    // A limit's name, carrying no figure of its own.
    if (!/[$\d]/.test(l)
        && /\b(?:each\s+occurrence|aggregate|medical\s+expense|personal\s+and\s+advertising|damage\s+to\s+premises|products\b|combined\s+single|each\s+claim|each\s+act|limit)\b/i.test(l)) {
      labelOnly++;
    }
  }
  return labelOnly >= 3 && bareAmounts >= 3;
}

if (typeof window !== 'undefined') window.spColumnSplitPage = spColumnSplitPage;


/* WHERE ON THE PAGE A MENTION SITS, DECIDED ONCE.
 *
 * A policy names a coverage for several reasons and only one of them is a
 * grant. The engine has learned each of the others the hard way and answered
 * each with its own guard -- underlyingSchedule, exclusionTitle,
 * boilerplateList, exclusionWord -- every one a backwards scan from the match,
 * every one added a client late. Four guards asking four versions of one
 * question: what part of the document is this?
 *
 * This answers it once. A page is cut into spans, each typed by the heading
 * that opened it, and everything outside a furniture span is DECLARATIONS --
 * the policy speaking for itself.
 *
 *   underlying-schedule   what sits beneath an excess or umbrella layer;
 *                         another carrier's contract, printed for attachment
 *   applicability-list    the parts an endorsement COULD modify. NOT given a
 *                         span: it has no structural ender, and an opener that
 *                         ran to the next one blanked most of an
 *                         endorsement-heavy auto policy, losing the document
 *                         for Commercial Auto entirely. isInBoilerplateListing
 *                         reads it with a bounded window and keeps that job.
 *   exclusion             a section taking coverage away
 *   forms-schedule        a list of what is attached, by form number
 *   notice                guaranty association, privacy and policyholder
 *                         notices; statutory text naming coverages and stating
 *                         amounts that belong to nobody in this program
 *
 * The other three guards are NOT region tests and are deliberately untouched:
 * lengthCap, prefixMarker and finiteVerb ask whether a line is a heading or a
 * sentence, a different question that stays where it is.
 */
const SP_REGION = {
  DECLARATIONS: 'declarations',
  UNDERLYING_SCHEDULE: 'underlying-schedule',
  APPLICABILITY_LIST: 'applicability-list',
  EXCLUSION: 'exclusion',
  FORMS_SCHEDULE: 'forms-schedule',
  NOTICE: 'notice'
};

const _SP_REGION_OPENERS = [
  { type: SP_REGION.UNDERLYING_SCHEDULE,
    src: String.raw`\bschedule\s+of\s+(?:controlling\s+)?underlying\s+insurance\b|\bunderlying\s+insurance\s+schedule\b`,
    flags: 'gi' },
  { type: SP_REGION.FORMS_SCHEDULE,
    src: String.raw`\bforms?\s+(?:and\s+endorsements?\s+)?schedule\b|\bschedule\s+of\s+forms\b|\bcoverage\s+form\s+index\b|\blisting\s+of\s+forms\b`,
    flags: 'gi' },
  { type: SP_REGION.NOTICE,
    src: String.raw`\bnotice\s+of\s+protection\s+provided\s+by\b|\bpolicyholder\s+notice\b|\bprivacy\s+(?:notice|policy)\b|\bguaranty\s+association\b|\binsurance\s+protection\s+association\b|\bterrorism\s+risk\s+insurance\s+act\b|\bthe\s+act\s+does\s+not\s+apply\s+to\b|\bdisclosure\s+(?:notice|pursuant)\b`,
    flags: 'gi' },
  { type: SP_REGION.EXCLUSION,
    src: String.raw`^[^\S\n]*(?:[A-Z0-9][.)]\s+)?(?:THE\s+FOLLOWING\s+)?EXCLUSIONS?\b[^\n]*$`,
    flags: 'gm' }
];

/* A furniture span runs to the next opener, or to a heading that plainly
 * returns the document to its own voice. Without an ender, a schedule printed
 * near the top of a declarations page would swallow the limits below it --
 * which is exactly the umbrella page whose own Each Occurrence sits ABOVE its
 * schedule of underlying insurance and must keep being read. */
const _SP_REGION_END = /\b(?:limits?\s+of\s+insurance\s+declarations|coverage\s+part\s+declarations|in\s+witness\s+whereof|declarations\s+page)\b/i;

function spPageRegions(text) {
  const t = String(text || '');
  if (!t) return [];
  const marks = [];
  for (const o of _SP_REGION_OPENERS) {
    const re = new RegExp(o.src, o.flags);
    let m;
    while ((m = re.exec(t)) !== null) {
      marks.push({ type: o.type, start: m.index });
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  }
  if (!marks.length) return [];
  marks.sort((a, b) => a.start - b.start);
  const out = [];
  for (let i = 0; i < marks.length; i++) {
    const next = i + 1 < marks.length ? marks[i + 1].start : t.length;
    const span = t.slice(marks[i].start, next);
    const ender = span.search(_SP_REGION_END);
    out.push({ type: marks[i].type, start: marks[i].start,
               end: ender > 0 ? marks[i].start + ender : next });
  }
  return out;
}

function spRegionAt(text, index) {
  for (const r of spPageRegions(text)) {
    if (index >= r.start && index < r.end) return r.type;
  }
  return SP_REGION.DECLARATIONS;
}

/* The same text with every furniture span blanked, offsets and line breaks
 * preserved so a caller may keep using offsets it already holds. Spaces rather
 * than deletion, because a reader that counts lines must still count the same
 * ones. */
function spOwnText(text) {
  const t = String(text || '');
  const regions = spPageRegions(t);
  if (!regions.length) return t;
  const chars = t.split('');
  for (const r of regions) {
    if (r.type === SP_REGION.DECLARATIONS) continue;
    for (let i = r.start; i < r.end && i < chars.length; i++) {
      if (chars[i] !== '\n') chars[i] = ' ';
    }
  }
  return chars.join('');
}

/* A Title IX claim against a school is defended under whichever management
 * liability form the program bought. Charter schools commonly carry an
 * Educators Legal Liability coverage part and no separate D&O policy, so the
 * row must price from either. Order is preference, not precedence: D&O first
 * where a program has both. */
/* SCORE THE SAME PAGE WHATEVER PARSER READ IT.
 *
 * Every v2.20 scoper scored a page by testing regexes of the shape
 * `label ... :? ... $value` against the raw page text. That shape survives
 * pdftotext, which separates a label from its amount with spaces, and dies on
 * LlamaParse, which separates them with a pipe:
 *
 *   pdftotext    Policy Aggregate Limit of Liability:        $2,000,000
 *   llamaparse   | Policy Aggregate Limit of Liability: | $2,000,000 | |
 *
 * The cyber scorer gives its single largest signal, 10 of a possible 26, to
 * /policy\s+aggregate\s+limit\s+of\s+liability\s*:?\s*\$/. Against pdftotext
 * the Aspen cyber declarations page scores 23 and parses. Against LlamaParse
 * it scores 13, the same page loses to three others, and the row came back
 * empty from a document that states its aggregate in plain sight on page 3.
 * 23 minus that 10 is exactly the 13 production logged.
 *
 * The engine already owns the normaliser -- spTableLines, which turns a cell
 * boundary into whitespace without destroying it. Scoring now runs on that, so
 * a page ranks on what it says rather than on which parser read it. Cached on
 * the page object: a scoper walks every page of every document, six times over.
 */
const _SP_SCORE_TEXT = new WeakMap();
function spScoreText(page) {
  if (!page || typeof page !== 'object') return String((page && page.text) || page || '');
  let v = _SP_SCORE_TEXT.get(page);
  if (v === undefined) {
    const raw = String(page.text || '');
    const norm = (typeof window !== 'undefined' && window.spRetroAnchor
                  && window.spRetroAnchor.spTableLines);
    try { v = norm ? norm(raw) : raw; } catch (_e) { v = raw; }
    _SP_SCORE_TEXT.set(page, v);
  }
  return v;
}

if (typeof window !== 'undefined') window.spScoreText = spScoreText;

const _SP_TITLE_IX_KEYS = ['D&O', 'Educators Legal Liability (ELL)'];

/* A ROW RESOLVES ITS COVERAGE ONCE.
 *
 * The Title IX row satisfied its STATUS through either D&O or Educators Legal
 * Liability, and read its LIMITS from D&O alone. On a charter school carrying
 * an ELL coverage part and no D&O policy the two halves disagreed: the status
 * said VERIFY off the ELL, and the limits cell said "limits not parsed" off an
 * empty D&O -- while a complete ELL declarations page, Coverage A $1,000,000
 * each wrongful act and $3,000,000 aggregate with a Coverage B, three
 * deductibles and a retroactive date, sat parsed and unused.
 *
 * `keys` is the row's coverages in preference order, declared once and read by
 * both halves, so a row cannot again be satisfied by one coverage and priced
 * from another. A key only wins if it carries a REAL figure: an empty slot
 * must not shadow a populated one later in the list.
 */
function spResolveBenchmarkLimits(declLimits, row, CT) {
  if (!row || !declLimits) return null;
  const order = (row.keys && row.keys.length) ? row.keys : [row.key, row.type];
  for (const key of order) {
    if (!key) continue;
    if (declLimits[key] && spHasRealLimits(declLimits[key])) return declLimits[key];
    if (!CT) continue;
    const k = Object.keys(declLimits).find(x => CT.sameTerm(x, key));
    if (k && spHasRealLimits(declLimits[k])) return declLimits[k];
  }
  // Nothing carried a real figure. Fall back to the original resolution so a
  // metadata-only slot still reaches a caller that wants to report on it.
  const direct = declLimits[row.key] || declLimits[row.type];
  if (direct) return direct;
  if (!CT) return null;
  const k = Object.keys(declLimits).find(x => CT.sameTerm(x, row.key) || CT.sameTerm(x, row.type));
  return k ? declLimits[k] : null;
}

if (typeof window !== 'undefined') {
  window.spResolveBenchmarkLimits = spResolveBenchmarkLimits;
  window.SP_TITLE_IX_KEYS = _SP_TITLE_IX_KEYS;
}

if (typeof window !== 'undefined') {
  window.spPageRegions = spPageRegions;
  window.spRegionAt = spRegionAt;
  window.spOwnText = spOwnText;
  window.SP_REGION = SP_REGION;
}
function spInGrantPosition(text, index, matchedTerm) {
  const t = String(text || '');
  const ls = t.lastIndexOf('\n', index) + 1;
  let le = t.indexOf('\n', index);
  if (le < 0) le = t.length;
  const line = t.slice(ls, le).trim();
  if (!line) return false;
  // Where the existing guards already say it is not a grant, it is not one.
  if (spGuard('underlyingSchedule') && spInUnderlyingSchedule(t, index)) return false;
  if (spGuard('exclusionTitle') && spInExclusionTitle(t, index)) return false;
  /* A LIST OF COVERAGE PARTS IS EITHER AN ENDORSEMENT'S REACH OR THE POLICY'S
   * OWN CONTENTS, AND ONLY ONE SENTENCE TELLS THEM APART.
   *
   * Nearly every ISO endorsement opens by listing every coverage part it COULD
   * modify — Farm, Farm Umbrella, Equipment Breakdown, Liquor Liability — and
   * each line reads exactly like a coverage part heading. Tulsa Honor
   * Academy's auto policy names EQUIPMENT BREAKDOWN COVERAGE PART twice, in
   * two such lists, and nowhere else.
   *
   * But a declarations page opens with the same shape and means the opposite.
   * Matrix Head Start's D&O declarations list the parts the policy BOUGHT —
   * Directors & Officers, Employment Practices, Fiduciary, Crime, Kidnap
   * Ransom & Extortion, Workplace Violence Expense — and counting coverage
   * part mentions in a window cannot distinguish the two. Suppressing that
   * list reported Workplace Violence Expense Coverage as absent on a policy
   * that grants it with a $250,000 aggregate.
   *
   * The endorsement says which it is, in words, immediately above the list.
   */
  const lead = t.slice(Math.max(0, index - 900), index);
  const isEndorsementList =
    /modifies\s+insurance\s+provided\s+under\s+the\s+following/i.test(lead)
    || /this\s+endorsement\s+changes\s+the\s+policy/i.test(lead);
  if (spGuard('boilerplateList') && isEndorsementList
      && isInBoilerplateListing(t, index, index + 1)) return false;
  if (spGuard('exclusionWord') && /\bexclusion(?:s)?\b/i.test(line)) return false;
  // Prose is not a field. A heading or a declarations row is short.
  if (spGuard('lengthCap') && line.length > 110) return false;
  /* A HEADING IS THE COVERAGE NAME. A SENTENCE MERELY CONTAINS IT.
   *
   * Tulsa Honor Academy's D&O row reported a $5,000,000 aggregate. The figure
   * is the TRIA statutory threshold, quoted in a terrorism notice — "Property
   * and casualty insurance losses resulting from the acts, in the aggregate,
   * do not exceed $5,000,000" — and the only thing keeping the row alive was
   * this line being read as a grant:
   *
   *     C. The Representations Condition in the Public Management Liability
   *        Coverage Part and Public Management Liability Coverage Part
   *        (Claims Made) is replaced by the following:
   *
   * It contains "Coverage Part" and fits the length cap, so it passed. It is
   * an endorsement amending a coverage part that this policy does not carry.
   *
   * On a real heading the coverage name STARTS the line. Whatever precedes it
   * is an item marker or a form number — "3.", "(b)", "CA DS 03 (11-20)",
   * "COMMERCIAL" — never prose. Lowercase words in front of the name are the
   * tell, and they cost nothing to check.
   */
  // Measured on the RAW line: `line` above is trimmed, and trimming shifts
  // every offset by the indent, so "   3.  Equipment Breakdown" read its
  // prefix from the wrong place.
  const before = t.slice(ls, index);
  const prefixIsMarkerOnly = /^\s*(?:\(?[A-Za-z0-9]{1,4}[.)])?\s*$/.test(before) || !/[a-z]/.test(before);
  if (prefixIsMarkerOnly || !spGuard('prefixMarker')) {
    // The policy's own vocabulary for granting.
    if (/\bcoverage\s+is\s+provided\b/i.test(line)) return true;
    /* A HEADING IS A NAME. A SENTENCE HAS A VERB.
     *
     * The endorsement sentence wraps, and its second line begins with the
     * coverage name, so nothing in front of it gives it away:
     *
     *     Management Liability Coverage Part (Claims Made) is replaced by
     *     the following:
     *
     * That line alone kept a $5,000,000 D&O aggregate — the TRIA statutory
     * threshold, quoted in a terrorism notice — on a policy with no
     * management liability coverage. No coverage part heading contains a
     * finite verb; every sentence about one does. */
    if (spGuard('finiteVerb')
        && /\b(?:is|are|was|were|shall|will|may|must|means?|does|do|has|have|includes?|applies)\b/i.test(line)) return false;
    if (/\bcoverage\s+(?:part|form|section|agreement)\b/i.test(line)) return true;
    if (/\b(?:insuring\s+agreement|declarations?)\b/i.test(line)) return true;
    /* A LINE THAT IS ESSENTIALLY THE COVERAGE'S NAME IS A HEADING.
     *
     * "Workplace Violence Expense Coverage" heads the section granting it, but
     * says "Coverage" rather than "Coverage Part", so none of the tests above
     * recognised it and the only stance left was an exclusion. Strip the
     * coverage's name and see what is left: on a heading it is a word or two
     * of scaffolding, on a sentence it is the sentence. */
    const rest = line.replace(new RegExp(String(matchedTerm || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), '')
      .replace(/\b(?:coverage|expense|liability|insurance|part|form|section|endorsement|extension)\b/gi, '')
      .replace(/[^A-Za-z0-9]+/g, '').trim();
    if (matchedTerm && rest.length === 0) return true;
  }
  // A named coverage with a limit stated beside it is a declarations row.
  if (/\$\s*[\d,]{3,}/.test(line)) return true;
  if (/\blimits?\s+of\s+(?:liability|insurance)\b/i.test(line)) return true;
  return false;
}

function spInExclusionTitle(txt, at) {
  const t = String(txt || '');
  const ls = t.lastIndexOf('\n', at) + 1;
  let le = t.indexOf('\n', at);
  if (le < 0) le = t.length;
  const line = t.slice(ls, le);
  if (!/\bexclusion(?:s)?\b/i.test(line)) return false;
  if (/\$\s*\d/.test(line)) return false;                       // a line with a limit on it
  if (/\b(?:deleted|removed|struck|does\s+not\s+apply|amended|carve[- ]?back)\b/i.test(line)) return false;
  /* A SCOPING PREAMBLE IS NOT A TITLE.
   *
   * Matrix Head Start's D&O policy adds Workplace Violence Expense Coverage by
   * endorsement — a $250,000 aggregate with no retention — and the report said
   * "Not covered — excluded by endorsement", quoting:
   *
   *     3. Solely with respect to Workplace Violence Expense Coverage,
   *        Section IV, Exclusion (C), Bodily Injury ... is deleted.
   *
   * which is a carve-BACK: it removes an exclusion, broadening the cover. The
   * words that say so — "is deleted" — sit on the next physical line, so the
   * veto above never saw them. Reporting a purchased coverage as a gap is the
   * worst direction this can fail in.
   *
   * These openers introduce a clause that modifies something else. A title
   * never begins with one. */
  if (/^\s*(?:\(?[A-Za-z0-9]{1,4}[.)])?\s*(?:solely|except|provided|notwithstanding|subject\s+to|in\s+consideration|it\s+is\s+agreed|with\s+respect\s+to|the\s+following)\b/i.test(line)) return false;
  return true;
}
if (typeof window !== 'undefined') {
  window.spInGrantPosition = spInGrantPosition;
  window.spInExclusionTitle = spInExclusionTitle;
}

function isInBoilerplateListing(txt, matchStart, matchEnd) {
  // Look at a wide window (800 chars before, 400 after)
  const windowStart = Math.max(0, matchStart - 800);
  const windowEnd = Math.min(txt.length, matchEnd + 400);
  const windowText = txt.substring(windowStart, windowEnd).toUpperCase();

  // Count how many "COVERAGE PART" or "COVERAGE FORM" mentions are in the window
  const cpMatches = (windowText.match(/COVERAGE\s+(?:PART|FORM)/g) || []).length;

  // If 4+ coverage part/form mentions in the window, it's a boilerplate listing
  // (Real declarations rarely list more than 2-3 coverage parts on the same page section)
  if (cpMatches >= 4) return true;

  // Also check for endorsement boilerplate markers
  const lowerWindow = windowText.toLowerCase();
  const boilerplateMarkers = [
    'this endorsement modifies insurance provided under the following',
    'this endorsement changes the policy',
    'modifies insurance provided under',
    'applies to the following coverage',
    'schedule of coverage forms',
    'to which this',
    'does not apply (if any)'
  ];
  const hasBoilerplate = boilerplateMarkers.some(m => lowerWindow.includes(m));
  if (hasBoilerplate && cpMatches >= 2) return true;

  // ISO Auto Forms Triplet: Auto Dealers + Business Auto + Motor Carrier Coverage Form
  // These three forms are commonly listed together in ISO commercial auto endorsement
  // conditions/duties text. If 2+ of the 3 appear near each other, it's boilerplate,
  // not evidence of actual Motor Carrier coverage.
  const isoAutoForms = [
    /AUTO\s+DEALERS?\s+COVERAGE\s+FORM/,
    /BUSINESS\s+AUTO\s+COVERAGE\s+FORM/,
    /MOTOR\s+CARRIER\s+COVER[\s\-]*AGE\s+FORM/
  ];
  const autoFormsFound = isoAutoForms.filter(p => p.test(windowText)).length;
  if (autoFormsFound >= 2) return true;

  // Also catch "Business Auto and Motor Carrier Coverage Forms" (ISO conditions text)
  // e.g., "Duties In The Event Of Accident ... in the Business Auto and Motor Carrier Coverage Forms"
  // Also handles hyphenated line breaks like "Cover- age Form"
  if (/BUSINESS\s+AUTO\s+(?:AND|&)\s+MOTOR\s+CARRIER\s+COVER[\s\-]*AGE\s+FORM/i.test(windowText)) return true;
  // "Auto Dealers and Business Auto Coverage Forms" near "Motor Carrier" — also ISO boilerplate
  if (/AUTO\s+DEALERS?\s+(?:AND|&)\s+BUSINESS\s+AUTO\s+COVER/i.test(windowText) && /MOTOR\s+CARRIER/i.test(windowText)) return true;

  return false;
}

/**
 * Determines if a coverage type is "specialized" (industry-specific) vs core.
 * Specialized coverages require stronger positive context signals to avoid false positives.
 */
const CORE_COVERAGES = new Set([
  'General Liability', 'Umbrella/Excess', 'Workers Compensation', 'EPLI',
  'Cyber Liability', 'Sexual Abuse/Molestation', 'Crime/Fidelity',
  'Educators Legal Liability (ELL)', 'D&O', 'Professional Liability',
  'Property', 'Commercial Auto', 'Equipment Breakdown', 'Flood Insurance',
  'Active Shooter', 'Student Accident Insurance', 'Inland Marine',
  'Employee Benefits Liability', 'Counseling Professional Liability',
  'Police Professional Liability'
]);

/* A TRAIL, SO A FREEZE LEAVES EVIDENCE.
 *
 * A user reported that the tab locks up. Two causes were found by measuring —
 * a quadratic regex on markdown table rows, and a detector re-run dozens of
 * times per report — and a third was still guessed at, because a frozen tab
 * produces no information at all. Reasoning about which stage hangs, from a
 * screenshot of the result, is how the last two days went wrong.
 *
 * Each stage announces itself BEFORE doing its work, so the last line in the
 * console is the stage that did not finish. Timings print on completion. This
 * costs one console.log per stage and answers the only question that matters
 * when something hangs: where.
 */
let _spStageAt = 0, _spStageName = '';
function spStage(name) {
  if (typeof console === 'undefined' || !console.log) return;
  const now = Date.now();
  if (_spStageName) console.log(`[stage] ${_spStageName}: ${now - _spStageAt} ms`);
  if (name) console.log(`[stage] ${name}\u2026`);
  _spStageName = name; _spStageAt = now;
}
if (typeof window !== 'undefined') window.spStage = spStage;

/* MEASURED INSIDE, AND ANSWERED ONCE PER INPUT.
 *
 * The first version of this trail logged the gap BETWEEN stages, which is not
 * the same thing: the pipeline awaits a backend parse between detections, so
 * "detect coverages: 16034 ms" was mostly network. An instrument that reports
 * someone else's waiting is worse than none, because it sends the next hour
 * in the wrong direction.
 *
 * And the detector is asked eight times for the same documents in one run.
 * Same input, same answer — keyed on the array it was handed, so a caller that
 * builds a fresh array still pays, and the common case of passing the same one
 * does not.
 */
const _SP_DETECT_MEMO = new WeakMap();

function detectCoveragesFromPdfText(pdfTexts) {
  if (pdfTexts && typeof pdfTexts === 'object') {
    const hit = _SP_DETECT_MEMO.get(pdfTexts);
    if (hit) return hit;
  }
  const _t0 = Date.now();
  const _out = detectCoveragesFromPdfTextUncached(pdfTexts);
  if (typeof console !== 'undefined' && console.log) {
    const pages = (pdfTexts || []).reduce((n, d) => n + ((d && d.pages) ? d.pages.length : 0), 0);
    console.log(`[perf] detect coverages: ${Date.now() - _t0} ms over ${pages} page(s)`);
  }
  if (pdfTexts && typeof pdfTexts === 'object') _SP_DETECT_MEMO.set(pdfTexts, _out);
  return _out;
}

// A forms-and-endorsements schedule row: a form number, and usually an
// edition date beside it. "CX 02 04  09-08", "CG 00 01 04 13", "PE GL 100
// (01-20)". Used to keep a schedule of attached forms from being read as a
// statement about whether a coverage was bought.
const _SP_FORM_SCHEDULE_ROW = /\b[A-Z]{2,4}\s?\d{2}\s?\d{2}\b|\([01]\d[-\/]\d{2}\)/;

function detectCoveragesFromPdfTextUncached(pdfTexts) {
  const found = new Set();
  if (!pdfTexts || pdfTexts.length === 0) return found;

  // ===== CORE COVERAGE PATTERNS =====
  // These are common coverage types that appear in most policies.
  // They use context-aware matching but don't require declarations-section confirmation.
  const CORE_TEXT_INDICATORS = {
    'General Liability': [
      /commercial\s+general\s+liability/i,
      /general\s+liability\s+coverage\s+part/i,
      /general\s+liability\s+(?:coverage|declarations|policy)/i,
      /each\s+occurrence[^\n]{0,60}general\s+aggregate/i,
      /cg\s*00\s*01/i
    ],
    'Umbrella/Excess': [
      /commercial\s+excess\s+liability/i,
      /umbrella\s+liability/i,
      // A London cover note says "Interest: Excess Liability Insurance" and
      // "Excess: As per Schedule of Underlying(s)". Requiring the words
      // "policy/coverage/declarations" or "scheduled underlying insurance"
      // missed a real $20M excess placement by one word.
      /excess\s+liability\s+(?:policy|coverage|declarations|insurance)/i,
      /excess\s+follow[\s-]*form/i,
      /scheduled?\s+(?:of\s+)?underlying/i,
      /schedule\s+of\s+underlying/i,
      /excess\s+of\s+(?:the\s+)?underlying/i
    ],
    // Kidnap & Ransom / special crime. Absent entirely until now, so a genuine
    // K&R policy came back "Coverage Not Recognized" — and, worse, the BOP gap
    // logic could report the coverage as missing while the insured held it.
    'Kidnap & Ransom': [
      /kidnap(?:ping)?\s*(?:,|&|and)\s*ransom/i,
      /\bk\s*&\s*r\s+(?:policy|coverage|insurance)/i,
      /malicious\s+detention/i,
      /products\s+extortion/i,
      /express\s+kidnap/i,
      /virtual\s+kidnap/i,
      /hijack\s+insurance/i,
      /kidnap[^.\n]{0,80}extortion[^.\n]{0,80}(?:detention|hijack|insurance)/i,
      /cyber\s+extortion\s+assistance/i
    ],
    'Workers Compensation': [
      /workers[\u2019']?\s*compensation\s+(?:and|&)\s+employers/i,
      /workers[\u2019']?\s*compensation\s+(?:coverage|declarations|policy)/i,
      /workers[\u2019']?\s*compensation\s+(?:part|form)/i,
      /bodily\s+injury\s+by\s+accident.{0,60}disease/i,
      /wc\s*00\s*00/i
    ],
    'EPLI': [
      /employment\s+practices\s+liability/i
    ],
    'Cyber Liability': [
      /cyber\s+(?:liability|coverage|insurance)\s+(?:policy|coverage|declarations|endorsement|form)/i,
      /network\s+security\s+(?:liability|coverage)\s+(?:policy|endorsement|form)/i,
      /data\s+breach\s+(?:response|coverage|liability)\s+(?:policy|endorsement|form)/i,
      /cyber\s+event\s+management/i
    ],
    'Sexual Abuse/Molestation': [
      /abuse\s+(?:or|&|and)\s+molestation/i,
      /sexual\s+abuse[\s\/]*molestation/i,
      /molestation\s+liability/i,
      /alleged\s+participant\s+abuse/i,
      /pe\s*gl\s*ok5/i,
      /sexual\s+misconduct\s+liability/i
    ],
    'Crime/Fidelity': [
      /commercial\s+crime/i,
      /crime\s+coverage\s+part/i,
      /crime\s+(?:coverage|declarations|policy)\s+(?:part|form|page)/i,
      /employee\s+(?:theft|dishonesty)[^\n]{0,60}\$/i,
      /forgery\s+(?:or|&|and)\s+alteration/i,
      /fidelity\s+(?:bond|coverage)/i,
      /inside\s+the\s+premises[\s\-]+theft/i
    ],
    'Educators Legal Liability (ELL)': [
      /educators\s+legal\s+liability/i,
      /school\s+board\s+legal\s+liability/i,
      /educators[\u2019']?\s*wrongful\s+act/i
    ],
    'D&O': [
      /directors\s+(?:&|and)\s+officers\s+(?:liability|coverage|insurance)/i,
      /d\s*&\s*o\s+(?:liability|coverage|insurance)/i,
      // NOTE: "management liability" must be in a declarations heading context, not a cross-reference in EPLI forms
      /(?:declarations|coverage\s+part|coverage\s+form)[:\s]+management\s+liability/i,
      /management\s+liability\s+(?:declarations|coverage\s+part)/i
    ],
    'Professional Liability': [
      /errors\s+(?:&|and)\s+omissions/i,
      /professional\s+liability\s+(?:coverage|policy|declarations)/i,
      /e\s*&\s*o\s+(?:liability|coverage|insurance)/i
    ],
    'Property': [
      /commercial\s+property\s+(?:coverage|declarations|policy)/i,
      /property\s+coverage\s+part/i,
      /property\s+(?:coverage|declarations)\s+(?:form|page)/i,
      /building\s+limit[^\n]{0,40}\$/i,
      /business\s+personal\s+property[^\n]{0,40}\$/i,
      /causes\s+of\s+loss[\s\-]+(?:basic|broad|special)/i,
      /cp\s*00\s*(?:10|30|40)/i
    ],
    'Commercial Auto': [
      /commercial\s+auto(?:mobile)?\s+(?:coverage|declarations|policy)/i,
      /business\s+auto\s+(?:coverage|declarations|policy)/i,
      /ca\s*00\s*01/i,
      /hired\s+(?:auto|car)\s+(?:and|&)\s+non[\s\-]*owned/i
    ],
    'Equipment Breakdown': [
      /equipment\s+breakdown\s+(?:coverage|protection|endorsement)/i,
      /boiler\s+(?:and|&)\s+machinery/i
    ],
    'Flood Insurance': [
      /flood\s+(?:insurance|coverage|declarations|policy)\s+(?:part|form|page|program)/i,
      /national\s+flood\s+insurance\s+program/i
    ],
    'Active Shooter': [
      /active\s+(?:shooter|assailant)\s+(?:coverage|response|insurance)/i,
      /violent\s+event\s+(?:coverage|response)/i
    ],
    'Student Accident Insurance': [
      /student\s+accident\s+(?:insurance|coverage|plan)/i,
      /scholastic\s+accident/i,
      // "Blanket Accident Insurance Policy" is the same product under the name
      // the carriers use. Anchored on the noun phrase so a stray "blanket" in a
      // property form cannot claim it.
      /blanket\s+accident\s+(?:insurance|policy|plan|coverage)/i,
      /blanket\s+student\s+accident/i,
      /accident\s+medical\s+expense\s+benefit/i,
      /participant\s+accident\s+(?:insurance|coverage|policy)/i
    ],
    'Inland Marine': [
      /inland\s+marine\s+(?:coverage|policy|declarations)/i,
      /inland\s+marine\s+coverage\s+part/i,
      /scheduled\s+equipment\s+(?:floater|coverage)/i
    ],
    'Employee Benefits Liability': [
      /employee\s+benefits\s+liability/i
    ],
    'Counseling Professional Liability': [
      /counseling\s+professional\s+liability/i
    ],
    'Police Professional Liability': [
      /police\s+professional\s+liability/i
    ]
  };

  // ===== SPECIALIZED COVERAGE PATTERNS =====
  // Industry-specific coverages (Maritime, Energy, Transportation, Construction, Healthcare, Manufacturing, Education).
  // These REQUIRE that the match appears in a positive context:
  //   - Table of contents / coverage parts listing
  //   - Declarations page
  //   - Endorsement title/header
  //   - Coverage form title
  // AND the match must NOT be in an exclusion/definition/limitation context.
  const SPECIALIZED_TEXT_INDICATORS = {
    // ===== MARITIME / MARINE =====
    'Hull & Machinery': [
      /hull\s+(?:&|and)\s+machinery\s+(?:coverage|policy|declarations|insurance)/i,
      /hull\s+insurance\s+(?:policy|coverage)/i
    ],
    'Protection & Indemnity': [
      /protection\s+(?:&|and)\s+indemnity\s+(?:coverage|policy|club|insurance)/i
    ],
    'Maritime Employers Liability': [
      /maritime\s+employers?\s+liability\s+(?:coverage|policy|endorsement|insurance)/i
    ],
    'Jones Act / Crew Coverage': [
      /jones\s+act\s+(?:coverage|endorsement|liability|insurance)/i,
      /crew\s+(?:coverage|liability)\s+(?:endorsement|form|policy)/i
    ],
    'USL&H / Longshore': [
      /longshore\s+(?:&|and)\s+harbor\s+workers/i,
      /usl\s*(?:&|and)?\s*h\s+(?:coverage|endorsement|act)/i
    ],
    'Marine Builders Risk': [
      /marine\s+builders?\s+risk\s+(?:coverage|policy|insurance)/i
    ],
    'Marine Cargo / Freight': [
      /marine\s+cargo\s+(?:coverage|policy|insurance)/i,
      /ocean\s+cargo\s+(?:coverage|policy|insurance)/i,
      /institute\s+cargo\s+clauses/i
    ],
    'Marine Pollution Liability': [
      /marine\s+pollution\s+(?:liability|coverage|insurance)/i,
      /vessel\s+pollution\s+(?:liability|coverage)/i,
      /opa\s+90\s+(?:compliance|coverage)/i
    ],
    'Marine General Liability': [
      /marine\s+general\s+liability\s+(?:coverage|policy|declarations)/i
    ],
    'Offshore Energy / OEE': [
      /offshore\s+energy\s+(?:coverage|policy|insurance)/i,
      /oee\s+(?:coverage|policy|insurance)/i
    ],
    'Dredging Insurance': [
      /dredging\s+(?:insurance|coverage|liability)\s+(?:policy|endorsement|form)/i
    ],

    // ===== ENERGY / OIL & GAS =====
    'Control of Well / OEE (Onshore)': [
      /control\s+of\s+(?:well|the\s+well)\s+(?:coverage|insurance|policy|endorsement)/i,
      /well\s+control\s+(?:coverage|insurance|policy)/i,
      /operators?\s+extra\s+expense\s+(?:coverage|policy)/i
    ],
    'Pollution Legal Liability (PLL/EIL)': [
      /pollution\s+legal\s+liability\s+(?:coverage|policy|endorsement)/i,
      /pll\s+(?:coverage|policy)/i,
      /eil\s+(?:coverage|policy)/i
    ],
    'Rig & Drilling Equipment': [
      /rig\s+(?:insurance|coverage|physical\s+damage)\s+(?:policy|endorsement|form)/i,
      /drilling\s+(?:equipment|rig)\s+(?:insurance|coverage)\s+(?:policy|form)/i
    ],
    'Oil Lease Property (OLP)': [
      /oil\s+lease\s+property\s+(?:coverage|policy|insurance)/i,
      /olp\s+(?:coverage|policy)/i
    ],
    'Pipeline Operators Insurance': [
      /pipeline\s+(?:operators?\s+)?(?:insurance|coverage|liability)\s+(?:policy|endorsement|form)/i
    ],
    'Gas Processing & Compression': [
      /gas\s+(?:processing|compression)\s+(?:plant|facility)\s+(?:insurance|coverage)/i
    ],
    'Terminal & Tank Farm': [
      /tank\s+farm\s+(?:insurance|coverage|liability)\s+(?:policy|endorsement)/i,
      /petroleum\s+terminal\s+(?:insurance|coverage|policy)/i,
      /storage\s+terminal\s+(?:insurance|coverage)\s+(?:policy|endorsement)/i
    ],
    'Refinery & Petrochemical': [
      /refinery\s+(?:insurance|coverage|liability)\s+(?:policy|endorsement)/i,
      /petrochemical\s+(?:insurance|coverage|liability)\s+(?:policy|endorsement)/i
    ],
    'NORM Liability': [
      /norm\s+(?:liability|coverage|disposal)\s+(?:policy|endorsement|insurance)/i,
      /naturally\s+occurring\s+radioactive\s+(?:material|coverage)/i
    ],
    'Underground Resource Damage': [
      /underground\s+resource\s+damage\s+(?:coverage|endorsement|liability)/i
    ],
    'RRC Compliance & Bonding': [
      /railroad\s+commission\s+(?:compliance|bond)\s+(?:coverage|endorsement)/i,
      /rrc\s+(?:compliance|bond|filing)\s+(?:coverage|endorsement)/i
    ],
    'MSA / Contractual Liability (Energy)': [
      /master\s+service\s+agreement\s+(?:coverage|endorsement|insurance)/i,
      /knock[\s-]*for[\s-]*knock\s+(?:coverage|endorsement|liability)/i
    ],
    'Business Interruption (Energy)': [
      /loss\s+of\s+production\s+(?:income|revenue)\s+(?:coverage|policy)/i,
      /production\s+interruption\s+(?:coverage|insurance|policy)/i
    ],

    // ===== TRANSPORTATION / LOGISTICS =====
    'Motor Carrier Liability': [
      /motor\s+carrier\s+liability\s+(?:policy|endorsement|coverage|insurance)/i,
      /motor\s+carrier\s+insurance\s+(?:policy|endorsement|coverage)/i,
      /fmcsa\s+(?:compliance|authority|filing)\s+(?:endorsement|certificate)/i
    ],
    'Motor Truck Cargo': [
      /motor\s+truck\s+cargo\s+(?:coverage|policy|insurance)/i,
      /mtc\s+(?:coverage|policy)/i
    ],
    'MCS-90 Compliance': [
      /mcs[\s-]*90\s+(?:endorsement|compliance|form)/i,
      /bmc[\s-]*91\s+(?:endorsement|form)/i
    ],
    'Bobtail / Non-Trucking Liability': [
      /bobtail\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement)/i,
      /non[\s-]*trucking\s+(?:liability|coverage)\s+(?:policy|endorsement)/i
    ],
    'Trailer Interchange': [
      /trailer\s+interchange\s+(?:coverage|insurance|endorsement|policy)/i
    ],
    'Garagekeepers Liability': [
      /garagekeepers?\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement|form)/i
    ],
    'Refrigeration Breakdown': [
      /refrigeration\s+breakdown\s+(?:coverage|endorsement|insurance)/i,
      /reefer\s+(?:breakdown|coverage)\s+(?:endorsement|policy)/i
    ],
    'Warehousing Legal Liability': [
      /warehousing\s+legal\s+liability\s+(?:policy|endorsement|coverage)/i,
      /warehouseman(?:'s)?\s+(?:liability|coverage)\s+(?:policy|endorsement)/i
    ],
    'Freight Broker E&O': [
      /freight\s+broker\s+(?:e&o|errors|liability|coverage)\s+(?:policy|endorsement)/i,
      /bmc[\s-]*84\s+(?:endorsement|filing)/i
    ],
    'Contingent Cargo': [
      /contingent\s+(?:cargo|freight)\s+(?:coverage|insurance|liability)\s+(?:policy|endorsement)/i
    ],

    // ===== CONSTRUCTION =====
    'Builders Risk': [
      /builders?\s+risk\s+(?:coverage|policy|insurance|declarations)/i,
      /course\s+of\s+construction\s+(?:coverage|policy)/i
    ],
    'Wrap-Up Programs (OCIP/CCIP)': [
      /ocip\s+(?:program|policy|coverage)/i,
      /ccip\s+(?:program|policy|coverage)/i,
      /wrap[\s-]*up\s+(?:program|insurance|liability)\s+(?:policy|endorsement)/i,
      /controlled\s+insurance\s+program\s+(?:policy|coverage)/i
    ],
    'Subcontractor Default Insurance (SDI)': [
      /subcontractor\s+default\s+insurance\s+(?:policy|coverage|endorsement)/i
    ],
    'Contractors Pollution Liability': [
      /contractors?\s+pollution\s+liability\s+(?:policy|coverage|endorsement)/i,
      /cpl\s+(?:coverage|policy)\s+(?:endorsement|form)/i
    ],
    'Installation Floater': [
      /installation\s+floater\s+(?:coverage|policy|endorsement)/i
    ],
    'Equipment Floater': [
      /equipment\s+floater\s+(?:coverage|policy|endorsement)/i,
      /contractors?\s+equipment\s+(?:floater|coverage)\s+(?:policy|endorsement)/i
    ],
    'Railroad Protective Liability': [
      /railroad\s+protective\s+(?:liability|coverage)\s+(?:part|policy|endorsement|form|insurance)/i,
      /railroad\s+protective\s+liability\s+coverage\s+part/i
    ],
    'Products-Completed Operations': [
      /products[\s-]*completed\s+operations\s+(?:coverage|liability|endorsement|policy)\s+(?:part|form|endorsement)/i,
      /pco\s+(?:coverage|endorsement)\s+(?:form|part)/i
    ],

    // ===== HEALTHCARE =====
    'Medical Professional Liability': [
      /medical\s+(?:professional|malpractice)\s+(?:liability|insurance|coverage)\s+(?:policy|endorsement|form|declarations)/i
    ],
    'Hospital Professional Liability': [
      /hospital\s+professional\s+(?:liability|insurance|coverage)\s+(?:policy|form)/i
    ],
    'HIPAA/Privacy Breach Liability': [
      /hipaa\s+(?:liability|coverage|breach)\s+(?:policy|endorsement|insurance)/i
    ],
    'Clinical Trial Liability': [
      /clinical\s+trial\s+(?:liability|insurance|coverage)\s+(?:policy|endorsement|form)/i
    ],
    'Telemedicine/Telehealth Coverage': [
      /telemedicine\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement)/i,
      /telehealth\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement)/i
    ],
    'Managed Care E&O': [
      /managed\s+care\s+(?:e&o|errors|liability)\s+(?:policy|endorsement|coverage)/i
    ],
    'Vicarious Liability (Staffing)': [
      // "Vicarious liability coverage - The coverage provided under this Policy
      // shall apply..." is standard GL indemnity wording, present on an energy
      // insured's CGL. It is only the healthcare staffing coverage when it says
      // so, so the phrase has to sit with staffing/locum/leased-provider terms.
      /vicarious\s+liability[^.]{0,120}\b(?:staffing|locum|leased\s+(?:employee|provider)|independent\s+contractor\s+physician|allied\s+health|credential)/i,
      /\b(?:staffing|locum\s+tenens|leased\s+provider)[^.]{0,80}vicarious\s+liability/i,
      /staffing\s+(?:agency\s+)?liability\s+(?:coverage|endorsement|policy)/i
    ],
    'Medicare/Medicaid Defense': [
      /medicare\s+(?:defense|audit)\s+(?:coverage|endorsement|policy)/i,
      /false\s+claims\s+act\s+(?:defense|coverage)\s+(?:endorsement|policy)/i
    ],

    // ===== MANUFACTURING =====
    'Product Recall Insurance': [
      /product\s+recall\s+(?:insurance|coverage|expense)\s+(?:policy|endorsement)/i
    ],
    'Product Contamination': [
      /product\s+contamination\s+(?:insurance|coverage|liability)\s+(?:policy|endorsement)/i
    ],
    'Supply Chain / Contingent BI': [
      /contingent\s+business\s+interruption\s+(?:coverage|endorsement|policy)/i,
      /supply\s+chain\s+(?:insurance|coverage)\s+(?:policy|endorsement)/i
    ],
    'Environmental Impairment Liability': [
      /environmental\s+impairment\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement)/i
    ],
    'OSHA Defense Coverage': [
      /osha\s+(?:defense|citation)\s+(?:coverage|insurance)\s+(?:policy|endorsement)/i
    ],
    'Manufacturers E&O': [
      /manufacturers?\s+(?:e&o|errors|professional)\s+(?:liability|coverage|policy)/i
    ],
    'Stock Throughput (STP)': [
      /stock\s+throughput\s+(?:policy|coverage|insurance)/i,
      /stp\s+(?:policy|coverage|insurance)/i
    ],
    'Foreign Product Liability': [
      /foreign\s+product\s+(?:liability|coverage|insurance)\s+(?:policy|endorsement)/i
    ],

    // ===== EDUCATION (specialized) =====
    'Athletic Participant Liability': [
      /athletic\s+(?:participant|liability)\s+(?:coverage|endorsement|insurance|policy)/i,
      /sports\s+(?:liability|participant)\s+(?:coverage|endorsement|insurance)/i,
      /concussion\s+(?:coverage|liability)\s+(?:endorsement|policy)/i
    ],
    'Tuition Refund Insurance': [
      /tuition\s+(?:refund|protection|insurance)\s+(?:policy|coverage|endorsement)/i
    ],
    'Field Trip / Off-Premises Liability': [
      /field\s+trip\s+(?:liability|coverage|insurance)\s+(?:endorsement|policy)/i,
      /off[\s-]*premises\s+(?:liability|coverage)\s+(?:endorsement|policy)/i
    ],
    'School Bus / Fleet Auto': [
      /school\s+bus\s+(?:liability|coverage|insurance|fleet)\s+(?:policy|endorsement)/i,
      /pupil\s+transportation\s+(?:liability|coverage|insurance)/i,
      /student\s+transportation\s+(?:liability|coverage)\s+(?:policy|endorsement)/i
    ],
    'FERPA / Student Data Breach': [
      /ferpa\s+(?:compliance|coverage|liability|breach)\s+(?:endorsement|policy|insurance)/i,
      /student\s+(?:data|records?)\s+(?:breach|privacy)\s+(?:coverage|endorsement|liability)/i
    ],
    'Title IX Defense': [
      /title\s+ix\s+(?:defense|compliance|coverage|liability)\s+(?:endorsement|policy)/i
    ]
  };

  // ===== POSITIVE CONTEXT: Table of Contents / Coverage Parts Listings =====
  // These patterns identify text that is a coverage-parts listing or TOC,
  // where even short mentions of coverage names ARE actual coverages.
  const TOC_PATTERNS = [
    /table\s+of\s+contents/i,
    /coverage\s+parts?\s+(?:included|listing|schedule)/i,
    /forms?\s+(?:and\s+)?endorsements?\s+(?:schedule|listing|made\s+part)/i,
    /policy\s+(?:declarations|dec)\s+page/i,
    /common\s+(?:policy\s+)?declarations/i,
    /coverage\s+parts?\s+page/i
  ];

  /**
   * Check if a match occurs on a page that contains a TOC or declarations listing.
   * If so, it's a strong positive signal.
   */
  function isInTocOrDeclarations(pageText) {
    const lower = pageText.toLowerCase();
    return TOC_PATTERNS.some(p => p.test(lower));
  }

  /**
   * Is this match in the document's own TITLE?
   *
   * A policy that calls itself "Kidnap, Extortion, Products Extortion,
   * Malicious Detention and Hijack Insurance" IS that coverage — the title is
   * the strongest evidence a document can offer. But such policies open with
   * "words in bold have the special meaning defined in DEFINITIONS", which put
   * the title inside isInNegativeContext()'s 500-char window and threw the
   * evidence away. A real K&R policy came back "Coverage Not Recognized".
   *
   * Titles only: the top of page 1, or a markdown heading from the parser —
   * and never a heading that is announcing an exclusion.
   */
  /**
   * A coverage the insured did NOT buy still prints on the carrier's premium
   * summary with a zero premium: "Total Directors and Officers Liability
   * Premium: $ 0". That line is proof of ABSENCE. The same table shows
   * "Total Employment Practices Liability Premium: $ 21" — a real, if small,
   * premium — so the amount is what separates a coverage held from one merely
   * listed.
   */
  function isZeroPremiumLine(line) {
    if (!/premium/i.test(line)) return false;
    const amts = line.match(/\$\s*[\d,]+(?:\.\d{2})?/g);
    if (!amts || !amts.length) return false;
    return parseFloat(amts[amts.length - 1].replace(/[^0-9.]/g, '') || '0') === 0;
  }

  /**
   * Policy wording refers to itself constantly — "...regardless of the number
   * of years this Data Breach Coverage Form remains in force...". That is the
   * BOP's own built-in form describing itself in a conditions paragraph, not
   * evidence of a separate standalone policy, and treating it as coverage
   * would suppress a real Section 5 gap. Genuine evidence sits at the start of
   * a heading, a declarations row or a schedule entry — not buried mid-sentence
   * in a block of prose.
   */
  function isBuriedInProse(line, offsetInLine) {
    // Line LENGTH is the wrong test — the Travelers flood advisory is exactly
    // 150 characters and sat on whichever threshold was chosen. What separates
    // evidence from prose is the match's POSITION IN A SENTENCE.
    //
    // A label or table cell runs to one or two words of lead-in and ends on a
    // separator: "| Total Employment Practices Liability Premium:". Running
    // prose has several words and ends mid-sentence on a lowercase word or a
    // comma: "For further information about Flood Insurance, contact your
    // agent or company representative, or contact the National Flood Insurance
    // Program" — an advisory notice, not coverage.
    const before = line.slice(0, offsetInLine).trim();
    const words = (before.match(/\S+/g) || []).length;
    return words > 2 && /[a-z,;]$/.test(before);
  }

  function isPolicyTitleMatch(pageText, pageIdx, at) {
    const nl = pageText.indexOf('\n', at);
    const line = pageText.slice(pageText.lastIndexOf('\n', at) + 1, nl < 0 ? pageText.length : nl);
    if (/exclusion|excluded|not\s+covered|limitation|shall\s+not\s+apply/i.test(line)) return false;
    if (/^\s{0,3}#{1,3}\s/.test(line)) return true;        // "# Kidnap ... Insurance"
    return pageIdx === 0 && at < 600;                       // cover page / cover note
  }

  // ===== SCAN LOGIC =====
  // CRITICAL: Scan page-by-page, NOT as concatenated full text.
  // Concatenating all pages causes context leakage where exclusion clauses
  // on one page poison the detection of legitimate coverages on nearby pages.
  // A policy form carries the WORDING for every coverage part its carrier
  // writes; the declarations say which ones were bought. Base Power's cyber
  // policy is the case in point:
  //
  //     Q. Technology Errors and Omissions   N/A          N/A
  //     R. Media Liability                   $3,000,000   $25,000
  //
  // and forty pages later, "Q. Technology Errors and Omissions — We will pay
  // on your behalf damages and claim expenses…", which reads exactly like a
  // grant. Reading that as coverage made Section 2B report "EPLI or
  // Professional Liability / E&O — DETECTED" for an insured whose own
  // schedule shows the part was never purchased, suppressing a real gap.
  //
  // So the declarations decide. When a document's schedule names a coverage
  // with no limit beside it, and nowhere else in that same document does a
  // schedule row give it a live limit, the coverage is vetoed for that
  // document. Another policy in the set can still carry it for real — the
  // veto is per document, never across the program.
  const spFirstLimitToken = (text, from) => {
    const win = text.slice(from, from + 60);
    const live = win.search(SP_LIVE_LIMIT);
    const dead = win.search(SP_DEAD_LIMIT);
    if (live < 0 && dead < 0) return null;
    if (live < 0) return 'dead';
    if (dead < 0) return 'live';
    return live < dead ? 'live' : 'dead';
  };
  const spDeclaredDeadIn = (pages, patterns) => {
    let sawDead = false;
    for (const pg of pages) {
      const t = pg.text || '';
      for (const pat of patterns) {
        const re = new RegExp(pat.source, pat.flags.includes('g') ? pat.flags : pat.flags + 'g');
        let m;
        while ((m = re.exec(t)) !== null) {
          if (m[0].length === 0) { re.lastIndex++; continue; }
          /* A FORMS SCHEDULE LISTS TITLES, NOT LIMITS.
           *
           * The row beside a form name carries its number and edition date,
           * and reading forward from the name finds one of those digits:
           *
           *   Excess Liability Coverage Form | | CX 02 04 | 09-08 |
           *   Oklahoma Changes - Cancellation And Nonrenewal | | CX 21 01 | 0
           *
           * That trailing 0 read as a declination, so the excess policy
           * declared its OWN coverage dead and the veto is document-wide —
           * every later page was skipped before any guard ran, and a $3,000,000
           * umbrella placement never appeared among the detected coverages at
           * all. A coverage cannot be declined by its forms schedule; that page
           * is the list of what is attached. */
          if (_SP_FORM_SCHEDULE_ROW.test(t.slice(m.index, m.index + 120))) {
            if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
            continue;
          }
          const state = spFirstLimitToken(t, m.index + m[0].length);
          if (state === 'live') return false;   // bought somewhere in this document
          if (state === 'dead') sawDead = true;
        }
      }
    }
    return sawDead;
  };

  pdfTexts.forEach(f => {
    // Support both page-array and flat-text formats
    const pages = (f.pages && Array.isArray(f.pages)) ? f.pages : (f.text ? [{ text: f.text }] : []);
    if (pages.length === 0) return;

    const vetoed = new Set();
    for (const [coverage, patterns] of Object.entries(CORE_TEXT_INDICATORS)) {
      if (spDeclaredDeadIn(pages, patterns)) vetoed.add(coverage);
    }
    for (const [coverage, patterns] of Object.entries(SPECIALIZED_TEXT_INDICATORS)) {
      if (spDeclaredDeadIn(pages, patterns)) vetoed.add(coverage);
    }

    // --- PASS 1 & 2: Scan each page individually ---
    // This prevents exclusion text on page N from blocking legitimate
    // coverage detection on page N+1.
    pages.forEach((pg, pageIdx) => {
      const pageText = pg.text || '';
      if (pageText.length < 20) return; // Skip near-empty pages

      // PASS 1: Core coverages (context-aware, page-level)
      for (const [coverage, patterns] of Object.entries(CORE_TEXT_INDICATORS)) {
        if (found.has(coverage)) continue;
        if (vetoed.has(coverage)) continue;   // this document's schedule says not bought
        for (const pat of patterns) {
          // Examine EVERY occurrence on the page, not just the first. A
          // coverage named once in an exclusion and again in its own grant was
          // previously dropped on the strength of whichever came first —
          // Workers Comp carried a special case for exactly this, which is now
          // the rule for every coverage rather than an exception for one.
          const globalRe = new RegExp(pat.source, pat.flags.includes('g') ? pat.flags : pat.flags + 'g');
          let m, confirmed = false;
          while ((m = globalRe.exec(pageText)) !== null) {
            if (m[0].length === 0) { globalRe.lastIndex++; continue; }
            const a = m.index, b = a + m[0].length;
            const lineStart = pageText.lastIndexOf('\n', a) + 1;
            const nlAfter = pageText.indexOf('\n', a);
            const line = pageText.slice(lineStart, nlAfter < 0 ? pageText.length : nlAfter);
            if (isZeroPremiumLine(line)) continue;            // priced at $0 = not bought
            // The carrier describing the lines of business it writes — "loss
            // control services, at no charge to its Texas commercial
            // automobile liability, general liability and professional
            // liability policyholders" — names its own products, not this
            // insured's. Screened before the cover-page title heuristic,
            // which would otherwise accept it as a policy title purely for
            // sitting early on page one.
            if (SP_CARRIER_MARKETING.test(pageText.slice(Math.max(0, a - 200), b + 200))) continue;
            // Another policy's cover, and an exclusion of this one. Both skip
            // the OCCURRENCE, not the coverage: a coverage named once in an
            // underlying schedule and again in its own grant is still carried.
            if (spInUnderlyingSchedule(pageText, a)) continue;
            if (spInExclusionTitle(pageText, a)) continue;
            if (isPolicyTitleMatch(pageText, pageIdx, a)) { confirmed = true; break; }
            if (isBuriedInProse(line, a - lineStart)) continue;
            // Named as the thing an endorsement edits, not as cover granted.
            if (spAmendsAClauseOf(pageText, a)) continue;
            if (!isInNegativeContext(pageText, a, b) && !isInBoilerplateListing(pageText, a, b)) {
              confirmed = true; break;
            }
          }
          if (confirmed) { found.add(coverage); break; }
        }
      }

      // PASS 2: Specialized coverages (strict context — page-level)
      for (const [coverage, patterns] of Object.entries(SPECIALIZED_TEXT_INDICATORS)) {
        if (found.has(coverage)) continue;
        if (vetoed.has(coverage)) continue;
        for (const pat of patterns) {
          const re = new RegExp(pat.source, pat.flags.replace('g', ''));
          const match = re.exec(pageText);
          if (match) {
            const matchStart = match.index;
            const matchEnd = matchStart + match[0].length;

            // Products-Completed Operations: skip if it's a GL sublimit line
            if (coverage === 'Products-Completed Operations') {
              if (isGLSublimitLine(pageText, matchStart, matchEnd)) continue;
            }

            // Specialized coverages: must NOT be in negative context or boilerplate listing
            if (!isInNegativeContext(pageText, matchStart, matchEnd) &&
                !isInBoilerplateListing(pageText, matchStart, matchEnd)) {
              found.add(coverage);
              break;
            }
          }
        }
      }

      // PASS 3: TOC / Coverage Parts scan (page-level)
      // If this page is a TOC or declarations listing, scan for coverage part names
      // using simpler patterns (just the coverage name in the listing context).
      if (isInTocOrDeclarations(pageText)) {
        const TOC_COVERAGE_MAP = {
          'General Liability': /(?:commercial\s+)?general\s+liability\s+coverage\s+part/i,
          'Property': /(?:commercial\s+)?property\s+coverage\s+part/i,
          'Commercial Auto': /(?:commercial|business)\s+auto(?:mobile)?\s+coverage\s+part/i,
          'Crime/Fidelity': /(?:commercial\s+)?crime\s+coverage\s+part/i,
          'Workers Compensation': /workers[\u2019']?\s*compensation\s+coverage/i,
          'Equipment Breakdown': /equipment\s+breakdown\s+coverage/i,
          'Umbrella/Excess': /(?:commercial\s+)?(?:umbrella|excess)\s+(?:liability\s+)?coverage/i,
          'Inland Marine': /inland\s+marine\s+coverage/i,
          'Railroad Protective Liability': /railroad\s+protective\s+liability\s+coverage/i,
          'Builders Risk': /builders?\s+risk\s+coverage/i,
          'Employee Benefits Liability': /employee\s+benefits\s+liability\s+coverage/i
        };

        for (const [cov, tocPat] of Object.entries(TOC_COVERAGE_MAP)) {
          if (!found.has(cov) && tocPat.test(pageText)) {
            found.add(cov);
          }
        }
      }
    });
  });

  return found;
}

function detectCoverages(files) {
  const found = new Set();
  files.forEach(f => {
    const fn = f.name.toLowerCase();
    for (const [coverage, patterns] of Object.entries(COVERAGE_PATTERNS)) {
      for (const p of patterns) {
        if (fn.includes(p.toLowerCase())) { found.add(coverage); break; }
      }
    }
  });
  // Cross-detection: D&O is commonly embedded in ELL/EPLI policies for education
  // Strategy: check file names for ELL/education indicators AND check if vertical is education-sector
  // Education EPLI policies almost always embed D&O under "Insuring Agreement A — Educators Legal Liability"
  // covering directors, officers, trustees, regents, school board members under "Educators Wrongful Act"
  if (found.has('EPLI') && !found.has('D&O')) {
    const ellFilePatterns = ['educators legal', 'management liability',
      'public officials', 'board member', 'wrongful act', 'educators liability',
      'school liability', 'school board', 'trustee', 'regent',
      'd&o', 'd-o', 'directors', 'officers'];
    const hasELLFile = files.some(f => {
      const fn = f.name.toLowerCase();
      return ellFilePatterns.some(p => fn.includes(p));
    });
    // Also trigger for education-sector verticals — EPLI in education virtually always includes ELL with D&O
    const eduVerticals = ['education', 'school', 'charter', 'university', 'college',
      'k-12', 'k12', 'academy', 'institute', 'learning', 'district', 'autism',
      'special education', 'early childhood', 'head start', 'montessori', 'stem'];
    const verticalLower = (window._currentVertical || '').toLowerCase();
    const orgNameLower = (window._currentOrgName || '').toLowerCase();
    const isEduContext = eduVerticals.some(v => verticalLower.includes(v) || orgNameLower.includes(v))
      || files.some(f => eduVerticals.some(v => f.name.toLowerCase().includes(v)));
    if (hasELLFile || isEduContext) {
      found.add('D&O (Embedded in ELL/EPLI)');
    }
  }

  // Cross-detection: E&O / Professional Liability commonly embedded in ELL, D&O, Management Liability packages
  // Strategy: check file names for professional liability/E&O indicators, check vertical context
  if (!found.has('Professional Liability')) {
    const eoFilePatterns = ['e&o', 'e-o', 'eo ', 'errors', 'omissions', 'professional liability',
      'prof liab', 'professional indemnity', 'prof indemnity', 'malpractice',
      'negligent act', 'wrongful act', 'professional services'];
    const hasEOFile = files.some(f => {
      const fn = f.name.toLowerCase();
      return eoFilePatterns.some(p => fn.includes(p));
    });
    // Check if E&O might be embedded in ELL, D&O, or Management Liability policies
    const eoEmbedPatterns = ['ell', 'educators legal', 'management liability', 'forefront',
      'd&o', 'directors', 'package', 'portfolio', 'bop'];
    const hasEOEmbedFile = files.some(f => {
      const fn = f.name.toLowerCase();
      return eoEmbedPatterns.some(p => fn.includes(p));
    });
    // Education sector E&O is almost always embedded in ELL or bundled in management liability
    const verticalLower2 = (window._currentVertical || '').toLowerCase();
    const orgNameLower2 = (window._currentOrgName || '').toLowerCase();
    const eduVerticals2 = ['education', 'school', 'charter', 'university', 'college',
      'k-12', 'k12', 'academy', 'institute', 'learning', 'district', 'autism',
      'special education', 'early childhood', 'head start', 'montessori', 'stem'];
    const isEduContext2 = eduVerticals2.some(v => verticalLower2.includes(v) || orgNameLower2.includes(v))
      || files.some(f => eduVerticals2.some(v => f.name.toLowerCase().includes(v)));
    if (hasEOFile) {
      found.add('Professional Liability');
    } else if (isEduContext2 && (hasEOEmbedFile || found.has('EPLI') || found.has('D&O') || found.has('D&O (Embedded in ELL/EPLI)'))) {
      found.add('Professional Liability (Embedded — Verify in ELL/D&O)');
    }
  }
  return Array.from(found);
}

// Detect unrecognized files that don't match any known pattern
function detectUnrecognizedFiles(files) {
  const unrecognized = [];
  files.forEach(f => {
    const fn = f.name.toLowerCase();
    let matched = false;
    for (const [coverage, patterns] of Object.entries(COVERAGE_PATTERNS)) {
      for (const p of patterns) {
        if (fn.includes(p.toLowerCase())) { matched = true; break; }
      }
      if (matched) break;
    }
    if (!matched) {
      // Try fallback heuristic keywords
      const suggestions = [];
      for (const [coverage, keywords] of Object.entries(FALLBACK_KEYWORDS)) {
        for (const kw of keywords) {
          if (fn.includes(kw)) { suggestions.push(coverage); break; }
        }
      }
      unrecognized.push({ file: f, suggestions: [...new Set(suggestions)] });
    }
  });
  return unrecognized;
}

// ===== GLOBAL STATE =====
let uploadedFiles = [];
let detectedCoverages = [];
let currentAnalysis = null;

// ===== FILE UPLOAD HANDLING =====
// Resolved lazily: React mounts the dropzone, so these do not exist when the
// module is imported. The original held them in consts captured at parse time.
const _spEl = id => (typeof document !== 'undefined' ? document.getElementById(id) : null);
const uploadZone = { addEventListener(ev, fn) { spOn('uploadZone', ev, fn); },
                     classList: { add(c) { const e = _spEl('uploadZone'); if (e) e.classList.add(c); },
                                  remove(c) { const e = _spEl('uploadZone'); if (e) e.classList.remove(c); } } };
const fileInput = { addEventListener(ev, fn) { spOn('fileInput', ev, fn); },
                    click() { const e = _spEl('fileInput'); if (e) e.click(); } };

uploadZone.addEventListener('click', () => fileInput.click());
uploadZone.addEventListener('dragover', (e) => { e.preventDefault(); uploadZone.classList.add('dragover'); });
uploadZone.addEventListener('dragleave', () => uploadZone.classList.remove('dragover'));

// v2.18: Enhanced drop handler — supports native files AND Gmail-style DownloadURL drags.
// Gmail/Outlook webmail don't ship the actual file contents via dataTransfer.files;
// they ship a "DownloadURL" entry in dataTransfer.types that points to the attachment.
// This handler checks for both and fetches the attachment when needed.
uploadZone.addEventListener('drop', async (e) => {
  e.preventDefault();
  uploadZone.classList.remove('dragover');

  // Path 1: Native files from Finder/desktop — works everywhere
  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
    handleFiles(e.dataTransfer.files);
    return;
  }

  // Path 2: Webmail drag — DownloadURL entry (Chrome/Edge on Mac and Windows)
  const types = Array.from(e.dataTransfer.types || []);
  const dlType = types.find(t => t === 'DownloadURL' || t === 'downloadurl');
  if (dlType) {
    try {
      const raw = e.dataTransfer.getData(dlType);
      // Format is "mime-type:filename:url" — split on first two colons only
      const firstColon = raw.indexOf(':');
      const secondColon = raw.indexOf(':', firstColon + 1);
      const mime = raw.substring(0, firstColon);
      const filename = raw.substring(firstColon + 1, secondColon);
      const url = raw.substring(secondColon + 1);
      showToast(`Fetching "${filename}" from email...`, 'info');
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const file = new File([blob], filename, { type: mime || blob.type || 'application/pdf' });
      const dt = new DataTransfer();
      dt.items.add(file);
      handleFiles(dt.files);
      showToast(`Loaded "${filename}" — no local download needed`, 'success');
      return;
    } catch (err) {
      console.warn('DownloadURL drop failed:', err);
      showToast('Could not load attachment directly from email. Please save it to your Mac first, then drag it in.', 'error');
      return;
    }
  }

  // Path 3: text/uri-list (some clients) — try as last resort
  const uriType = types.find(t => t === 'text/uri-list');
  if (uriType) {
    try {
      const uri = e.dataTransfer.getData(uriType).trim().split('\n')[0];
      if (uri && uri.startsWith('http')) {
        showToast('Attempting to fetch link...', 'info');
        const resp = await fetch(uri);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const filename = (uri.split('/').pop() || 'attachment.pdf').split('?')[0];
        const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
        const dt = new DataTransfer();
        dt.items.add(file);
        handleFiles(dt.files);
        showToast(`Loaded "${filename}"`, 'success');
        return;
      }
    } catch (err) {
      console.warn('URI drop failed:', err);
    }
  }

  // Nothing usable in the drop payload
  showToast('Drag-from-email is blocked by browser security. Please save the attachment to your Mac, then drag it in (or delete it after).', 'error');
});
fileInput.addEventListener('change', (e) => handleFiles(e.target.files));

function handleFiles(filesList) {
  for (const f of filesList) {
    if (!uploadedFiles.find(uf => uf.name === f.name)) {
      uploadedFiles.push(f);
    }
  }
  detectedCoverages = detectCoverages(uploadedFiles);
  renderFileList();
}

function removeFile(idx) {
  uploadedFiles.splice(idx, 1);
  detectedCoverages = detectCoverages(uploadedFiles);
  renderFileList();
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function renderFileList() {
  const _fl = _spEl('fileList');
  if (uploadedFiles.length === 0) { if (_fl) _fl.innerHTML = ''; return; }
  let html = '';
  const unrecognized = detectUnrecognizedFiles(uploadedFiles);
  const unrecognizedNames = new Set(unrecognized.map(u => u.file.name));
  uploadedFiles.forEach((f, i) => {
    /* A parsed document has been read and analysed — that is a fact about the
     * document, and it outranks whatever its filename does or does not say.
     *
     * Once it is parsed, ANALYZED is the only badge the row carries. The
     * coverage chips were a pre-analysis guess from the filename, and a row
     * that showed both put a guess next to a fact and left the broker to work
     * out which was which: the Auto policy listed eight chips, wrapping over
     * three lines, while the authoritative per-document coverage table is
     * Section 2 of the report. Before parsing the chips are the only signal
     * there is, so they stay for unparsed files, along with the suggestion and
     * manual-review badges. */
    const parsed = (window._parsedDocs || {})[f.name];
    const fc = Array.from(new Set([...detectCoverages([f]), ...((parsed && parsed.coverages) || [])]));
    const isUnrecognized = unrecognizedNames.has(f.name) && !parsed;
    const unrec = unrecognized.find(u => u.file.name === f.name);
    const analysed = parsed
      ? `<span class="coverage-badge" style="background:#e8f5e9;color:#1b5e20;border:1px solid #a5d6a7;" title="Parsed and analysed — ${parsed.pages} page(s), ${parsed.chars.toLocaleString()} characters of text">✅ ANALYZED${parsed.pages ? ` · ${parsed.pages} pages` : ''}</span>`
      : '';
    html += `<div class="file-item${isUnrecognized ? ' file-unrecognized' : ''}">
      <span class="file-name">${escapeHtml(f.name)}</span>
      ${analysed}
      ${!parsed && fc.length ? fc.map(c => `<span class="coverage-badge">${c}</span>`).join('') : ''}
      ${isUnrecognized && unrec && unrec.suggestions.length > 0 ? `<span class="coverage-badge badge-suggest" title="Suggested by keyword heuristic — manual review required">💡 Possible: ${unrec.suggestions.join(', ')}</span>` : ''}
      ${isUnrecognized && unrec && unrec.suggestions.length === 0 ? '<span class="coverage-badge badge-unknown" title="Not yet parsed and no coverage type in the filename">⚠️ MANUAL REVIEW</span>' : ''}
      <span class="file-size">${formatBytes(f.size)}</span>
      <button class="file-remove" onclick="removeFile(${i})" title="Remove">&times;</button>
    </div>`;
  });
  const stillUnrecognized = unrecognized.filter(u => !((window._parsedDocs || {})[u.file.name]));
  if (stillUnrecognized.length > 0) {
    html += `<div style="font-size:11px; color:var(--warning); margin-top:6px; padding:6px 8px; background:var(--warning-bg); border-radius:4px;">⚠️ ${stillUnrecognized.length} file(s) did not match known carrier/coverage patterns. Suggested classifications are marked with 💡. Files marked MANUAL REVIEW require broker identification.</div>`;
  }
  if (_fl) _fl.innerHTML = html;
}

function escapeHtml(str) {
  const d = document.createElement('div'); d.textContent = str; return d.innerHTML;
}

// ===== STATE DETECTION =====
function detectStates(text) {
  if (!text) return [];
  const found = new Set();
  let canon = (typeof window !== 'undefined' && window.spCanonDCText) ? window.spCanonDCText(text) : text;
  // "1400 Minnesota Ave NE": a street named for a state is not that state,
  // and NE/NW/SE/SW after a street is a DC quadrant, not Nebraska.
  const _stNames = Object.values(STATE_PROFILES).map(s => s.name).sort((a, b) => b.length - a.length).join('|');
  canon = canon
    .replace(/(\b(?:Ave|Avenue|St|Street|Rd|Road|Blvd|Pl|Place|Dr|Drive|Way|Ter|Terrace|Ct|Court|Pkwy|Parkway)\.?)\s+(?:N\.?E|N\.?W|S\.?E|S\.?W)\b\.?/gi, '$1 ')
    .replace(new RegExp('\\b(?:' + _stNames + ')\\s+(?:Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Pl|Place|Dr|Drive|Way|Ter|Terrace|Ct|Court|Pkwy|Parkway)\\b\\.?', 'gi'), ' ');
  const upper = canon.toUpperCase();
  // Check full state names first
  Object.values(STATE_PROFILES).forEach(s => {
    if (upper.includes(s.name.toUpperCase())) found.add(s.abbr);
  });
  // Check abbreviations (word-bounded)
  const words = upper.replace(/[^A-Z\s.]/g, ' ').split(/\s+/);
  words.forEach(w => {
    const clean = w.replace(/\./g, '');
    if (STATE_NAME_MAP[clean]) found.add(STATE_NAME_MAP[clean]);
    if (STATE_NAME_MAP[w]) found.add(STATE_NAME_MAP[w]);
  });
  // DC variants
  if (upper.includes("DISTRICT OF COLUMBIA") || upper.includes("WASHINGTON DC") || upper.includes("WASHINGTON D.C.") || upper.includes("D.C.")) found.add("DC");
  return Array.from(found);
}

function parseStates(input) {
  if (!input.trim()) return [];
  // "Washington, DC" is one jurisdiction, so it is folded before the comma split.
  const _dc = (typeof window !== 'undefined' && window.spCanonDCText) ? window.spCanonDCText(input) : input;
  const parts = _dc.replace(/\bDistrict\s+of\s+Columbia\b(?:\s*\(\s*D\.?\s?C\.?\s*\))?/gi, 'DC')
    .split(/[,;]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
  const result = [];
  parts.forEach(p => {
    const clean = p.replace(/\./g, '');
    if (STATE_NAME_MAP[clean]) result.push(STATE_NAME_MAP[clean]);
    else {
      // Try partial match
      const match = Object.values(STATE_PROFILES).find(s => s.name.toUpperCase().startsWith(clean));
      if (match) result.push(match.abbr);
    }
  });
  return spCanonStates(result);
}


// ===== WEBSITE RISK ANALYSIS ENGINE =====
// Simulates fetching and analyzing the policyholder's website for operational risks
// that may create coverage gaps or trigger policy exclusions

const WEBSITE_RISK_PATTERNS = {
  // Events & Gatherings
  events: {
    label: 'Events & Gatherings',
    patterns: [
      { regex: /fundrais(ing|er)/i, risk: 'Fundraising events', severity: 'HIGH', exclusionType: 'Event Liability / Special Events' },
      { regex: /gala|banquet|dinner\s*event|awards\s*(ceremony|dinner|night)/i, risk: 'Formal gala/banquet events', severity: 'HIGH', exclusionType: 'Special Events / Liquor Liability' },
      { regex: /auction|silent\s*auction|live\s*auction/i, risk: 'Auction events (property handling risk)', severity: 'MODERATE', exclusionType: 'Bailee / Property in Care' },
      { regex: /festival|fair|carnival|celebration|fiesta/i, risk: 'Large public gathering/festival', severity: 'HIGH', exclusionType: 'Event Liability / Crowd Control' },
      { regex: /concert|performance|recital|theater|theatre/i, risk: 'Entertainment/performance events', severity: 'MODERATE', exclusionType: 'Entertainment Liability' },
      { regex: /5k|marathon|run\/walk|walkathon|race\s*event|fun\s*run/i, risk: 'Athletic/running events', severity: 'HIGH', exclusionType: 'Athletic Participant Liability' },
      { regex: /golf\s*(tournament|outing|classic)/i, risk: 'Golf tournament/outing', severity: 'MODERATE', exclusionType: 'Athletic Participant Liability' },
      { regex: /\b(?:summer|day|overnight|sports|band|science|art)\s+camps?\b|\bcamps?\b(?!\w)/i, risk: 'Camp programs (youth supervision)', severity: 'HIGH', exclusionType: 'Youth Supervision / SAM' },
      { regex: /field\s*trip|excursion|off[\s-]*site|off[\s-]*campus/i, risk: 'Off-site activities/field trips', severity: 'HIGH', exclusionType: 'Off-Premises Liability' },
      { regex: /prom|homecoming|dance|school\s*dance/i, risk: 'Dance/prom events', severity: 'MODERATE', exclusionType: 'Event Liability / Alcohol' },
      { regex: /(open\s*house|community\s*day|family\s*(night|day|event))/i, risk: 'Community open events', severity: 'MODERATE', exclusionType: 'Premises Liability' },
      { regex: /(more\s*than|over|exceed)\s*(\d{3,})\s*(attendee|guest|participant|people|person)/i, risk: 'Large attendance events', severity: 'HIGH', exclusionType: 'Event Liability — Attendance Threshold' },
      { regex: /(\d{3,})\s*(attendee|guest|participant|people|person)/i, risk: 'Events with significant attendance', severity: 'MODERATE', exclusionType: 'Event Liability — Attendance Threshold' }
    ]
  },
  // Alcohol & Beverages
  alcohol: {
    label: 'Alcohol & Beverage Service',
    patterns: [
      { regex: /alcohol(ic)?\s*(beverage|drink|service|served|bar)/i, risk: 'Alcoholic beverages served at events', severity: 'CRITICAL', exclusionType: 'Liquor Liability Exclusion' },
      { regex: /wine\s*(tasting|pour|bar|served|service|selection)/i, risk: 'Wine service/tasting', severity: 'CRITICAL', exclusionType: 'Liquor Liability Exclusion' },
      { regex: /beer|cocktail|spirits|champagne|mimosa/i, risk: 'Alcohol referenced in activities', severity: 'HIGH', exclusionType: 'Liquor Liability Exclusion' },
      { regex: /open\s*bar|cash\s*bar|hosted\s*bar|BYOB/i, risk: 'Bar service at events', severity: 'CRITICAL', exclusionType: 'Liquor Liability Exclusion' },
      { regex: /liquor\s*license|beer\s*and\s*wine\s*license/i, risk: 'Liquor license held', severity: 'HIGH', exclusionType: 'Liquor Liability Exclusion' },
      { regex: /happy\s*hour|wine\s*wednesday|tasting\s*event/i, risk: 'Recurring alcohol events', severity: 'HIGH', exclusionType: 'Liquor Liability Exclusion' }
    ]
  },
  // Recreational & Athletic Activities
  athletics: {
    label: 'Athletic & Recreational Activities',
    patterns: [
      { regex: /swimming|pool|aquatic/i, risk: 'Swimming/aquatic activities', severity: 'HIGH', exclusionType: 'Aquatic Activity Exclusion' },
      { regex: /playground|play\s*area|climbing\s*(wall|structure)/i, risk: 'Playground/climbing equipment', severity: 'MODERATE', exclusionType: 'Premises Liability — Attractive Nuisance' },
      { regex: /trampoline|bounce\s*house|inflatable/i, risk: 'Trampolines/inflatables', severity: 'CRITICAL', exclusionType: 'Trampoline / Inflatable Exclusion' },
      { regex: /sport(s)?\s*(league|team|program|activit)/i, risk: 'Organized sports programs', severity: 'HIGH', exclusionType: 'Athletic Participant Liability' },
      { regex: /football|basketball|soccer|baseball|softball|lacrosse|hockey|wrestling|gymnastics|cheer(leading)?/i, risk: 'Contact/competitive sports', severity: 'HIGH', exclusionType: 'Athletic Participant / CTE Liability' },
      { regex: /martial\s*arts|karate|boxing|MMA/i, risk: 'Martial arts/combat sports', severity: 'HIGH', exclusionType: 'Combat Sports Exclusion' },
      { regex: /rope(s)?\s*course|zip\s*line|adventure|rappel/i, risk: 'High-adventure activities', severity: 'CRITICAL', exclusionType: 'High-Hazard Activity Exclusion' },
      { regex: /horse|equestrian|riding|pony/i, risk: 'Equestrian/horseback activities', severity: 'HIGH', exclusionType: 'Animal Liability / Equine Exclusion' },
      { regex: /boat(ing)?|canoe|kayak|paddl/i, risk: 'Watercraft activities', severity: 'HIGH', exclusionType: 'Watercraft Exclusion' },
      { regex: /gym(nasium)?|fitness\s*center|weight\s*room/i, risk: 'Gymnasium/fitness facility', severity: 'MODERATE', exclusionType: 'Athletic Facility Liability' }
    ]
  },
  // Transportation & Vehicles
  transportation: {
    label: 'Transportation & Vehicle Use',
    patterns: [
      { regex: /school\s*bus|bus\s*fleet|transportation\s*(service|fleet|department)/i, risk: 'Student/client transportation', severity: 'HIGH', exclusionType: 'Auto Liability / Fleet' },
      { regex: /van\s*(pool|service)|shuttle/i, risk: 'Van/shuttle service', severity: 'HIGH', exclusionType: 'Auto Liability / Hired & Non-Owned' },
      { regex: /driver|cdl|commercial\s*vehicle/i, risk: 'Commercial vehicle operations', severity: 'MODERATE', exclusionType: 'Auto Liability' },
      { regex: /field\s*trip\s*transport|charter\s*bus/i, risk: 'Charter transportation for events', severity: 'HIGH', exclusionType: 'Hired Auto / Charter Liability' }
    ]
  },
  // Property & Facilities
  facilities: {
    label: 'Facility & Property Risks',
    patterns: [
      { regex: /historic\s*(building|property|structure)|landmark/i, risk: 'Historic property (replacement cost issues)', severity: 'MODERATE', exclusionType: 'Historic Property Valuation' },
      { regex: /rental\s*(income|property|space)|lease\s*(space|to|out)/i, risk: 'Property leased to third parties', severity: 'MODERATE', exclusionType: 'Leased Premises Liability' },
      { regex: /construction|renovation|building\s*project|expansion/i, risk: 'Active construction/renovation', severity: 'HIGH', exclusionType: 'Builders Risk / Construction Exclusion' },
      { regex: /kitchen|food\s*(service|prep|program|pantry)|cafeteria/i, risk: 'Food service operations', severity: 'MODERATE', exclusionType: 'Food Service / Products Liability' },
      { regex: /daycare|childcare|early\s*childhood|pre[\s-]?k(indergarten)?/i, risk: 'Childcare/daycare operations', severity: 'HIGH', exclusionType: 'Youth Supervision / SAM / Daycare Liability' },
      { regex: /dorm(itory)?|residential|boarding|housing/i, risk: 'Residential housing/dormitory', severity: 'HIGH', exclusionType: 'Residential Operations / Student Housing' }
    ]
  },
  // Volunteers & Third Parties
  volunteers: {
    label: 'Volunteer & Third-Party Exposure',
    patterns: [
      { regex: /volunteer(s|ing)?\s*(program|opportunity|needed|welcome)/i, risk: 'Volunteer programs', severity: 'MODERATE', exclusionType: 'Volunteer Injury / Workers Comp Gap' },
      { regex: /mentor(ing|ship)?\s*(program)?/i, risk: 'Mentoring programs (unsupervised contact)', severity: 'HIGH', exclusionType: 'SAM / Volunteer Screening' },
      { regex: /intern(ship)?\s*(program|opportunity)/i, risk: 'Internship programs', severity: 'MODERATE', exclusionType: 'Employment Practices / Workers Comp' },
      { regex: /contractor|vendor\s*on[\s-]*site|third[\s-]*party\s*(service|vendor)/i, risk: 'Third-party vendors on-site', severity: 'MODERATE', exclusionType: 'Independent Contractor / Additional Insured' }
    ]
  },
  // Digital & Cyber
  digital: {
    label: 'Digital & Data Exposure',
    patterns: [
      { regex: /online\s*(portal|payment|donation|registration|store)/i, risk: 'Online payment/transaction processing', severity: 'HIGH', exclusionType: 'Cyber / PCI-DSS Compliance' },
      { regex: /student\s*(record|data|information|portal)/i, risk: 'Student data collection/storage', severity: 'HIGH', exclusionType: 'FERPA / Data Breach Liability' },
      { regex: /patient\s*(record|data|portal|information)/i, risk: 'Patient data/health records', severity: 'CRITICAL', exclusionType: 'HIPAA / PHI Breach Liability' },
      { regex: /mobile\s*app|app\s*download/i, risk: 'Mobile application', severity: 'MODERATE', exclusionType: 'Technology E&O / Cyber' }
    ]
  },
  // Animals
  animals: {
    label: 'Animal-Related Exposure',
    patterns: [
      { regex: /\b(animals?|pets?|dogs?|puppies|cats?|kittens?|hamsters?|rabbits?|parakeets?|reptiles?|therapy\s+animals?|service\s+animals?|emotional\s+support\s+animals?)\b/i, risk: 'Animals on premises', severity: 'MODERATE', exclusionType: 'Animal Liability Exclusion' },
      { regex: /petting\s*zoo|farm\s*animal|livestock|barnyard/i, risk: 'Animal interaction activities', severity: 'HIGH', exclusionType: 'Animal Liability / Petting Zoo Exclusion' }
    ]
  },
  // International
  international: {
    label: 'International Exposure',
    patterns: [
      { regex: /international\s*(travel|trip|program|student|exchange)/i, risk: 'International programs/travel', severity: 'HIGH', exclusionType: 'Foreign Liability / Travel Coverage' },
      { regex: /study\s*abroad|mission\s*trip|overseas/i, risk: 'Study abroad/mission trips', severity: 'HIGH', exclusionType: 'Foreign Liability / War & Terrorism' }
    ]
  }
};

// Analyze website content for operational risks
// Website / operational risk detection.
//
// `sources` is a labelled list — [{label, text, grounded}] — so a risk records
// WHERE it was actually seen. Sources marked `grounded:false` (the vertical
// exposure profile) can only ever produce a CHECKLIST item: something common
// for this vertical that the broker should confirm, never a finding about this
// client. Previously the vertical template was the ONLY input and every hit was
// rendered as an observed risk, so any charter school produced the same 29
// "findings" — petting zoo, pony rides, open bar — whether or not the site said
// anything of the kind.
function analyzeWebsiteContent(sources, vertical) {
  // Every source is real: this site, or the broker's profile note. There is no
  // ungrounded source to match against, and no industry list to fall back on.
  const real = (sources || []).filter(s => s && String(s.text || '').trim() && s.grounded !== false);
  const grounded = [];

  // A readable quote around the first hit, so the evidence column shows the
  // document's own words rather than the keywords the regex happened to match.
  function snippet(text, re, at) {
    let m;
    if (typeof at === 'number' && at >= 0) {
      const rx = new RegExp(re.source || re, 'gi'); rx.lastIndex = at; m = rx.exec(text);
    } else {
      m = new RegExp(re, 'i').exec(text);
    }
    if (!m) return null;
    const s0 = Math.max(0, m.index - 60), e0 = Math.min(text.length, m.index + m[0].length + 90);
    return (s0 > 0 ? '…' : '') + text.slice(s0, e0).replace(/\s+/g, ' ').trim() + (e0 < text.length ? '…' : '');
  }

  // Attendance threshold, read from the source that actually matched. This used
  // to assign `pattern.severity`, mutating the shared WEBSITE_RISK_PATTERNS
  // catalog — so once one client tripped 500+ attendees, that pattern stayed
  // CRITICAL for every later analysis in the session.
  function attendance(text, baseSeverity) {
    const numMatch = /(\d{3,})\s*(?:attendee|guest|participant|people|person)/i.exec(text || '');
    if (!numMatch) return { note: '', severity: baseSeverity };
    const num = parseInt(numMatch[1], 10);
    if (num >= 500) return { note: ' (' + num + '+ attendees — commonly excluded threshold)', severity: 'CRITICAL' };
    if (num >= 200) return { note: ' (' + num + '+ attendees — verify event sublimits)', severity: baseSeverity };
    return { note: '', severity: baseSeverity };
  }

  // A KEYWORD IS NOT A FINDING.
  //
  // On basepowercompany.com the animal pattern matched the word "Cat" in a
  // customer testimonial — "…peace of mind as hurricane season starts. Highly
  // recommend!" Cat Jason B. at home in Arlington, Texas" — and the report told
  // an electricity company it had animals on the premises, with an Animal
  // Liability Exclusion to verify and the testimonial as its evidence.
  //
  // Two guards, applied to every occurrence until one survives:
  //   1. A capitalised match sitting beside another capitalised word is a
  //      PROPER NAME ("Cat Jason", "Baker Ridge"), not the noun.
  //   2. Patterns whose words double as ordinary English (`needsContext`)
  //      must appear near operational language — "our", "on site", "we have",
  //      "allowed", "policy" — before they count.
  const NAME_LIKE = /^[A-Z][a-z]+$/;
  const OPERATIONAL_NEAR = /\b(our|we\s+(?:have|offer|provide|host|keep|allow|welcome)|on[-\s]?site|on[-\s]?premises?|at\s+(?:the\s+)?(?:facility|campus|school|clinic|office)|program|policy|allowed|permitted|therapy|service|boarding|kennel|shelter|adopt\w*|visitors?\s+may)\b/i;
  // A SINGLE COMMON WORD is not an operational fact about this insured.
  //
  // The construction pattern matched "expansion" inside another testimonial —
  // "This is the future for power outages and costly expansion of power
  // grids" — and reported ACTIVE CONSTRUCTION/RENOVATION at HIGH, with a
  // Builders Risk exclusion to verify, for a company whose customer was
  // talking about the national grid. The word was real; the activity was not
  // the insured's.
  //
  // So context is required whenever the thing that matched is ONE word.
  // A multi-word phrase ("petting zoo", "summer camp", "online payment
  // portal", "student records") names the activity on its own and stands
  // without it — which is what keeps this from suppressing real findings.
  const ALWAYS_NEEDS_CONTEXT = /animals?|pets?|dogs?|puppies|cats?|kittens?|hamsters?|rabbits?|parakeets?|reptiles?/i;

  function meaningfulMatch(text, re) {
    const rx = new RegExp(re.source || re, 'gi');
    const alwaysNeeds = ALWAYS_NEEDS_CONTEXT.test(re.source || String(re));
    let m;
    while ((m = rx.exec(text)) !== null) {
      const word = m[0].trim();
      const before = text.slice(Math.max(0, m.index - 40), m.index);
      const after = text.slice(m.index + m[0].length, m.index + m[0].length + 40);
      // 1. proper name — "Cat Jason B.", "Fox Ridge Elementary"
      const capitalised = NAME_LIKE.test(word);
      const nextWordCapped = /^\s+[A-Z][a-z]+/.test(after);
      const prevWordCapped = /[A-Z][a-z]+\s+$/.test(before);
      if (capitalised && (nextWordCapped || prevWordCapped)) continue;
      // 2. operational context — required for a one-word match, and for the
      //    patterns whose words are common names whatever their length.
      const singleWord = !/\s/.test(word);
      if ((singleWord || alwaysNeeds) && !OPERATIONAL_NEAR.test(before + m[0] + after)) continue;
      return m.index;
    }
    return -1;
  }

  Object.entries(WEBSITE_RISK_PATTERNS).forEach(([category, categoryData]) => {
    categoryData.patterns.forEach(pattern => {
      const hitIn = list => list.find(s => meaningfulMatch(String(s.text), pattern.regex) >= 0);

      const realHit = hitIn(real);
      if (realHit) {
        const text = String(realHit.text);
        const a = pattern.risk.includes('attendance') ? attendance(text, pattern.severity)
                                                      : { note: '', severity: pattern.severity };
        grounded.push({
          category: categoryData.label,
          risk: pattern.risk + a.note,
          severity: a.severity,
          exclusionType: pattern.exclusionType,
          evidence: snippet(text, pattern.regex, meaningfulMatch(text, pattern.regex)) || '',
          source: realHit.label,
          grounded: true
        });
      }
      // No hit in the real sources means this risk was not observed. It is not
      // then asserted from somewhere else — the vertical checklist below is
      // built from the industry, not from a match against invented copy.
    });
  });

  const severityOrder = { 'CRITICAL': 0, 'HIGH': 1, 'MODERATE': 2 };
  const dedupeSort = arr => {
    const seen = new Set();
    return arr.filter(r => {
      const key = r.risk.replace(/\s*\(.*\)/, '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3));
  };

  // No checklist. A risk is in 7C because a real source says so, or it is not
  // in 7C at all.
  return dedupeSort(grounded);
}

// The labelled inputs 7C reasons over. Real sources first: only these can
// produce a finding about this client. The vertical exposure profile is carried
// as an explicitly ungrounded source so it can raise a checklist item and
// nothing stronger.
// ===== Marketing voice is not the insured's operations =====================
//
// A company website is two kinds of text sitting side by side: what the
// business DOES, and what other people SAY about it. Only the first is
// evidence about the risk. Two findings on basepowercompany.com came out of
// the second half — "Cat Jason B. at home in Arlington, Texas" produced
// animals on the premises, and a customer's "costly expansion of power grids"
// produced active construction at HIGH with a Builders Risk exclusion.
//
// This strips the review voice before any analysis reads the text, so the fix
// holds for every consumer of it — the profile and vertical detectors, the
// NAICS pivot, the BOP review and Section 7C — rather than being re-litigated
// per section.
//
// Subtractive, deliberately. A whitelist of "operations / about / services"
// headings would silently drop the body copy on a one-page site, and a section
// that never gets read is a finding that never gets made; a false negative in
// a coverage review costs more than a little marketing noise left behind.
const SP_TESTIMONIAL_HEADING = /^[^\n]{0,80}\b(testimonials?|customer\s+stor(?:y|ies)|success\s+stor(?:y|ies)|reviews?|what\s+(?:our\s+)?(?:customers?|clients?|members?|families|people)\s+(?:are\s+)?say|hear\s+from\s+(?:our\s+)?(?:customers?|clients?)|rated\s+\d(?:\.\d)?\s+(?:stars?|out\s+of)|trustpilot|google\s+reviews?|yelp)\b[^\n]{0,80}$/im;

// `opts.headingsOnly` — drop announced review SECTIONS but leave quoted text
// alone. Used for uploaded POLICY documents: a policy is quotation-heavy
// ("bodily injury" means..., quoted statutory language, named endorsements),
// and stripping a quote that happens to be followed by a capitalised word
// would delete operative language. Losing policy text is a false negative in
// a coverage review, which costs more than any marketing noise it removes.
// A review CARD, which neither rule below catches. It has no announcing
// heading, so the section rule misses it, and the sentence that would be
// quoted sits in a separate element, so the attributed-quote rule misses it
// too. What survives into the extracted text is the card's furniture:
//
//   "4.8 stars Bob A. at home in Waxahachie, Texas · Waxahachie, TX
//    Read Bob's story"
//
// That fragment named an operating state. SentriPol reported Texas on the
// strength of where a CUSTOMER lives — a customer is not an operation, and a
// review carousel is not a location schedule.
//
// Each pattern is narrow on purpose. "Bob A." — a first name and an initial —
// is a review convention business copy almost never uses, and it must sit
// beside a location or a story link before anything is removed, so an
// ordinary sentence mentioning a person survives.
const SP_REVIEW_CARD = [
  // A star rating anywhere: "4.8 stars", "5 star", "4.9 / 5 stars".
  /\b\d(?:\.\d)?\s*(?:\/\s*5\s*)?stars?\b/gi,
  // The card's call to action: "Read Bob's story".
  /\bRead\s+[A-Z][a-z]+(?:'s|\u2019s)?\s+(?:full\s+)?stor(?:y|ies)\b/gi,
  // "at home in <city>, <state>" — the caption under a customer photo. The
  // phrase is the anchor rather than the name, because the names vary more
  // than the furniture does: "Bob A. at home in Waxahachie, Texas" and
  // "R B & Mike at home in Pflugerville, Texas & Mike · Pflugerville, TX" are
  // the same card, and a pattern keyed on "Name Initial." caught only the
  // first. Any short name-ish prefix and any separator-led location tail are
  // swept with it, so the card leaves nothing behind to match on.
  /(?:\b[A-Z][\w.'&\u2019-]*(?:\s+[A-Z&][\w.'&\u2019-]*){0,3}\s+)?at\s+home\s+in\s+[^,\n]{1,40},\s*(?:[A-Z][a-z]+|[A-Z]{2})\b(?:[^.\n]{0,40}?[\u00b7\u2022|]\s*[^,\n]{1,40},\s*[A-Z]{2}\b)?/g,
  // The same card without the phrase: "Dana R. from Austin, TX".
  /\b[A-Z][a-z]+\s+[A-Z]\.\s*(?:from|in|of)\s+[A-Z][A-Za-z.\- ]{1,30},\s*(?:[A-Z][a-z]+|[A-Z]{2})\b/g,
  // The bare attribution when a separator carries the location.
  /\b[A-Z][a-z]+\s+[A-Z]\.\s*[\u00b7\u2022|,]\s*[A-Z][A-Za-z.\- ]{1,30},\s*[A-Z]{2}\b/g,
  // Badges that only ever appear on a review.
  /\bverified\s+(?:customer|buyer|purchaser|reviewer)\b/gi
];

window.spStripTestimonials = function(text, opts){
  const headingsOnly = !!(opts && opts.headingsOnly);
  let t = String(text == null ? '' : text);
  if (!t) return '';

  // Review cards first: they are neither an announced section nor an
  // attributed quote, so nothing below would reach them. Not applied in
  // headingsOnly mode — that is for policy documents, where this furniture
  // does not occur and the patterns could only do harm.
  if (!headingsOnly) {
    SP_REVIEW_CARD.forEach(re => { t = t.replace(re, ' '); });
    t = t.replace(/[ \t]{2,}/g, ' ');
  }

  // 1. A heading that announces reviews, through to the next heading or a
  //    double blank line. Markdown headings, or a short standalone line.
  const lines = t.split('\n');
  const out = [];
  let dropping = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // What ENDS a review block. A prose line is not a heading just because it
    // is short and unpunctuated — "Amazing! Cat Jason B. in Arlington, Texas"
    // read as one and ended the block after a single line, leaving the rest of
    // the reviews in the text.
    const tl = line.trim();
    const isHeading = /^#{1,6}\s/.test(line)
      || (tl.length >= 3 && tl.length <= 60 && tl === tl.toUpperCase() && /[A-Z]/.test(tl))
      || (tl.length > 0 && tl.length <= 60 && !/[.!?,;:]/.test(tl) && tl.split(/\s+/).length <= 6);
    if (dropping) {
      // A new heading that is NOT itself a testimonial heading ends the block.
      if (isHeading && !SP_TESTIMONIAL_HEADING.test(line)) dropping = false;
      else continue;
    }
    if (SP_TESTIMONIAL_HEADING.test(line)) { dropping = true; continue; }
    out.push(line);
  }
  t = out.join('\n');

  if (headingsOnly) return t.replace(/\n{3,}/g, '\n\n').trim();

  // 2. An attributed quote anywhere in the body: quoted text followed by a
  //    person's name, an initial, a star rating or a location. This is the
  //    shape the Base Power reviews take — the quote ends, then "Cat Jason B.
  //    at home in Arlington, Texas".
  t = t.replace(/[“"][^“”"]{25,400}[”"]\s*[-–—]?\s*[A-Z][A-Za-z.'-]*(?:\s+[A-Z][A-Za-z.'-]*){0,3}(?:\s*,\s*[A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){0,2})?/g, ' ');
  // The same thing without quote marks: "... recommend! Cat Jason B. at home in Dallas, Texas"
  t = t.replace(/[.!?]\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\s+[A-Z]\.\s+(?:at\s+home\s+)?in\s+[A-Z][A-Za-z .'-]{2,40}/g, '. ');
  // Star ratings and review furniture.
  t = t.replace(/[★☆]{2,}|\b\d(?:\.\d)?\s*\/\s*5\s*stars?\b|\bverified\s+(?:customer|buyer|review)\b/gi, ' ');

  // Tidy what removal leaves behind: doubled spaces, orphaned punctuation
  // where a quote used to sit, and runs of blank lines.
  return t.replace(/\s+([.,;:!?])/g, '$1')
          .replace(/([.!?])\s*[.,;:]+/g, '$1')
          .replace(/[ \t]{2,}/g, ' ')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
};

function buildWebsiteRiskSources(siteText, orgOverview, url) {
  const sources = [];
  const site = window.spStripTestimonials(siteText).trim();
  if (site) {
    const host = (window._siteFetchMeta && window._siteFetchMeta.host)
      || (String(url || '').replace(/^\w+:\/\//, '').split('/')[0] || 'company website');
    sources.push({ label: 'Company website (' + host + ')', text: site, grounded: true });
  }
  // The generated placeholder overview just echoes the vertical back, so it is
  // not independent evidence — only broker-entered text counts.
  const ov = window.spStripTestimonials(orgOverview).trim();
  if (ov && !window._spOrgOverviewGenerated) {
    sources.push({ label: 'Organization profile (broker-entered)', text: ov, grounded: true });
  }
  // Nothing else. Every source 7C reasons over is text somebody can open and
  // read — this site, or the broker's own profile note.
  return sources;
}

// 7C reports what this website says about this insured's operations, and
// nothing else.
//
// Two things used to pad it out. `simulateWebsiteContent` invented website
// copy from the vertical — "Petting zoo at Spring carnival", "Open bar at
// annual awards banquet", "600 attendees" — and ran the risk regexes over it,
// so a row's provenance was a sentence nobody had written. Replacing that with
// a truthful per-vertical checklist fixed the fabrication but not the premise:
// an exposure typical of the sector, absent from this insured's own text, is
// still not a finding about this insured. For Base Power it printed nine rows
// whose "Possible Exposure" and "Policy Exclusion to Verify" columns held the
// same string, and asked the broker to chase questions the documents had never
// raised.
//
// A risk appears in 7C when the website or the broker's profile note says so,
// quoted and attributed, or it does not appear.


// Real text typed or pasted by the user is independent company information,
// so it clears the "auto-generated" flag and counts for the site cross-check.
spOn('orgOverview', 'input', function(){
  window._spOrgOverviewGenerated = false;
});

// ===== AUTO-FETCH BUTTON =====
// Fetches the company's ACTUAL website through the address-gated server-side
// fetcher. That text is what makes the vertical cross-check meaningful: a
// vertical read off policy paper says what forms the carrier attached, while
// the company's own site says what the company does. When the fetch fails we
// fall back to the synthesised overview — but that text is generated FROM the
// vertical on screen, so it is flagged (_spOrgOverviewGenerated) and the
// cross-check refuses to treat it as corroboration.
spOn('autoFetchBtn', 'click', async function() {
  const btn = this;
  const name = document.getElementById('policyholderName').value.trim();
  const url = document.getElementById('websiteUrl').value.trim();
  const vertical = document.getElementById('vertical').value.trim();
  const statesField = document.getElementById('states');

  if (!name) { showToast('Please enter a Policyholder Name first.', 'error'); return; }

  const restore = () => { btn.disabled = false; btn.textContent = '⚡ Auto-Fetch Organization Info & Detect States'; };
  btn.disabled = true;

  let siteText = '';
  let fetchErr = '';
  if (url) {
    btn.textContent = '🌐 Fetching ' + url.replace(/^https?:\/\//,'').split('/')[0] + '…';
    try {
      const site = await window.spFetchSite(url, true);
      siteText = (site && site.text) ? site.text : '';
      if (siteText.trim()) {
        // Two different consumers, two different texts. The cross-check wants
        // recall, so it keeps the FULL scrape in _scrapedSite. The Organization
        // Profile field is human-facing and is rendered into the report
        // narrative, so it gets the few descriptive lines the backend distilled
        // — dumping 16k chars of nav labels in there makes the report worse.
        window._scrapedSite = siteText;
        window._siteFetchMeta = { host:site.host, pages:(site.pages||[]).map(pg=>pg.url), title:site.title,
                                  chars:siteText.length, profileLines:(site.profile||[]).length };
        const profile = (site.profile_text||'').trim();
        document.getElementById('orgOverview').value = profile || siteText.slice(0, 900);
        window._spOrgOverviewGenerated = false;   // real, independent company text
        const n = (site.pages||[]).length;
        showToast('Fetched ' + n + ' page(s) from ' + site.host + ' — profile trimmed to ' + (site.profile||[]).length
          + ' line(s); the cross-check still runs on all ' + siteText.length.toLocaleString() + ' chars.');
      }
    } catch (e) {
      fetchErr = (e && e.message) ? e.message : String(e);
      console.warn('[site] fetch failed:', fetchErr);
    }
  }

  if (!siteText.trim()) {
    // Fallback: synthesised overview. Flagged, because it echoes the vertical
    // back and therefore cannot corroborate it.
    const verticalText = vertical || 'organization';
    const statesList = parseStates(statesField.value);
    const stateNames = statesList.map(a => STATE_PROFILES[a]?.name || a).join(', ') || 'the United States';
    document.getElementById('orgOverview').value = generateOrgOverview(name, verticalText, stateNames, url);
    window._spOrgOverviewGenerated = true;
    window._scrapedSite = '';
    window._siteFetchMeta = null;
    if (!url) showToast('No website URL — using a placeholder overview. The vertical cross-check needs real site text, so paste the company overview or add the URL.', 'warning');
    else showToast('Could not fetch ' + url + ' (' + fetchErr + '). Using a placeholder overview — it cannot corroborate the vertical, so paste the real overview if you have it.', 'error');
  }

  // Detect states from the URL, file names and any REAL site text (never the
  // generated overview, which just echoes back states already entered).
  let detectText = (url || '') + ' ' + siteText;
  uploadedFiles.forEach(f => { detectText += ' ' + f.name; });
  const detected = detectStates(detectText);
  const existing = parseStates(statesField.value);
  const newStates = detected.filter(s => !existing.includes(s));
  if (newStates.length > 0) {
    const combined = [...existing, ...newStates];
    statesField.value = combined.map(a => STATE_PROFILES[a]?.name || a).join(', ');
    showToast(`Additional state(s) detected: ${newStates.map(a => STATE_PROFILES[a]?.name || a).join(', ')}`);
  }

  // Operational-risk pre-scan — on the real page text when we have it.
  if (url) {
    const previewRisks = analyzeWebsiteContent(
      buildWebsiteRiskSources(siteText, document.getElementById('orgOverview').value, url), vertical);
    if (previewRisks.length > 0) {
      const critCount = previewRisks.filter(r => r.severity === 'CRITICAL').length;
      const highCount = previewRisks.filter(r => r.severity === 'HIGH').length;
      let riskMsg = `Website scan: ${previewRisks.length} operational risk(s) detected`;
      if (critCount > 0) riskMsg += ` (${critCount} CRITICAL)`;
      else if (highCount > 0) riskMsg += ` (${highCount} HIGH)`;
      if (!siteText.trim()) riskMsg += ' — placeholder content, not the live site';
      showToast(riskMsg, critCount > 0 ? 'error' : 'warning');
    }
  }

  restore();
});

function generateOrgOverview(name, vertical, states, url) {
  const urlDomain = url ? url.replace(/https?:\/\//, '').replace(/\/$/, '') : '';
  return `${name} is a ${vertical} serving communities across ${states}. ` +
    `Established as a trusted institution in the ${vertical.toLowerCase()} sector, ${name} provides essential services to its stakeholders, ` +
    `maintaining a commitment to operational excellence and community engagement. ` +
    `With operations spanning ${states}, the organization faces a diverse regulatory landscape requiring comprehensive insurance coverage.\n\n` +
    `As a ${vertical.toLowerCase()} entity, ${name} faces unique risk exposures including professional liability, regulatory compliance obligations, ` +
    `property and casualty risks, and employment-related exposures. The organization's geographic footprint across ${states} requires careful ` +
    `attention to state-specific regulatory requirements and catastrophic risk profiles. Understanding these exposures is critical to ` +
    `ensuring adequate protection for the organization's assets, operations, and stakeholders.\n\n` +
    `The operational scope of ${name} includes managing facilities, employing staff, serving clients or students, ` +
    `and maintaining compliance with applicable federal and state regulations. Key regulatory bodies governing the organization's ` +
    `operations include state departments of insurance, federal agencies (OSHA, EEOC, ADA), and industry-specific regulators ` +
    `relevant to the ${vertical.toLowerCase()} sector.\n\n` +
    `This analysis examines the current insurance program for ${name} to identify coverage gaps, regulatory compliance risks, ` +
    `and opportunities to strengthen the organization's risk management posture. The review considers industry benchmarks, ` +
    `state-specific requirements, and emerging risk trends relevant to ${vertical.toLowerCase()} operations in ${states}.`;
}

// ===== FORM SUBMISSION =====
spOn('analysisForm', 'submit', async function(e) {
  e.preventDefault();

  const lob = document.getElementById('lob').value;
  const reviewType = document.getElementById('reviewType').value;
  const policyholderName = document.getElementById('policyholderName').value.trim();
  // Industry vertical(s) + state(s) are AUTO-DETECTED (evidence-grounded) after
  // PDF text extraction below — no longer accepted as user input.
  let vertical = '';
  let verticals = [];
  let states = [];
  let detectedProfile = null;

  if (!lob || !reviewType || !policyholderName) {
    showToast('Please fill in all required fields.', 'error'); return;
  }

  window._currentOrgName = policyholderName;
  // Initial coverage detection (re-run after vertical is detected below).
  detectedCoverages = detectCoverages(uploadedFiles);

  // Show loading
  const overlay = document.getElementById('loadingOverlay');
  overlay.classList.add('active');

  const isQuickScreenMode = reviewType === 'quickscreen';
  const steps = isQuickScreenMode ? [
    'Scanning policy documents...',
    'Evaluating coverage adequacy...',
    'Generating instant verdict...'
  ] : [
    'Extracting policy document text...',
    'Scanning for embedded coverages (D&O, E&O, ELL, Management Liability)...',
    'Reviewing state regulatory requirements...',
    'Benchmarking coverage against industry standards...',
    'Identifying gaps, exclusions, and fatal flaws...',
    'Generating comprehensive report...'
  ];
  const stepDelay = isQuickScreenMode ? 500 : 800;
  let stepIdx = 0;
  document.getElementById('loadingStep').textContent = steps[0];
  const stepInterval = setInterval(() => {
    stepIdx++;
    if (stepIdx < steps.length) {
      document.getElementById('loadingStep').textContent = steps[stepIdx];
    }
  }, stepDelay);

  try {
    // PHASE 1: Extract PDF text content (real async work during loading)
    // Pass progress callback to update loading overlay for OCR status
    const ocrProgress = (msg) => {
      const stepEl = document.getElementById('loadingStep');
      if (stepEl) stepEl.textContent = msg;
    };
    const pdfTexts = await extractAllPdfTexts(uploadedFiles, ocrProgress);

    // ===== AUTO-DETECT industry verticals + states (evidence-grounded) =====
    // No user input: infer purely from the policy document text + the
    // organization/website overview. spDetectProfile only returns items whose
    // trigger term is literally present in a source (with the snippet), so a
    // vertical/state can never be hallucinated.
    {
      // Cross-checking a vertical requires INDEPENDENT company text. If the
      // broker never pressed Auto-Fetch (or the overview on screen is the
      // synthesised placeholder, which just echoes the vertical back), pull the
      // real site now — best-effort, never fatal to the review.
      const _wsUrl = document.getElementById('websiteUrl').value.trim();
      if (_wsUrl && !(window._scrapedSite||'').trim()){
        try {
          const stepEl0=document.getElementById('loadingStep');
          if (stepEl0) stepEl0.textContent='Fetching company website for the vertical cross-check…';
          const _site = await window.spFetchSite(_wsUrl, true);
          if (_site && (_site.text||'').trim()){
            window._scrapedSite = _site.text;
            window._siteFetchMeta = { host:_site.host, pages:(_site.pages||[]).map(pg=>pg.url), title:_site.title,
                                      chars:_site.text.length, profileLines:(_site.profile||[]).length };
            const _ov=document.getElementById('orgOverview');
            // Only overwrite an empty field or the synthesised placeholder —
            // never text a human typed.
            if (!_ov.value.trim() || window._spOrgOverviewGenerated){
              _ov.value = (_site.profile_text||'').trim() || _site.text.slice(0, 900);
              window._spOrgOverviewGenerated = false;
            }
          }
        } catch(e){ console.warn('[site] cross-check fetch failed:', e && e.message); window._siteFetchError = (e&&e.message)||String(e); }
      }
      const _wsText = ((window._scrapedSite||'') + ' ' + document.getElementById('orgOverview').value + ' ' + document.getElementById('websiteUrl').value).trim();
      const _fileNames = uploadedFiles.map(f => f.name);
      const _sources = spBuildSources(pdfTexts, _wsText, _fileNames);
      // Published for the panels that verify a claim against the same evidence
      // the classifiers used — spNaicsQuotePosition asks whether the model's
      // supporting quote landed in a labelled declarations field, and it can
      // only ask that of the sources the page actually assembled.
      //
      // The property is CREATED at module load (see the initialiser beside the
      // other window globals), not here. Everything in this build reaches it as
      // `window._spSources`, but the engine's registry convention is that these
      // are read as bare globals too, and a bare read of a property that does
      // not exist yet is a ReferenceError rather than undefined — a report that
      // dies instead of a panel that degrades. Assigning here only updates it.
      window._spSources = _sources;
      // Intelligent LLM path first (grounded); deterministic heuristic as fallback.
      detectedProfile = await spDetectProfileSmart(_sources);
      verticals = detectedProfile.verticals;
      /* The industry is what the BUSINESS does, never what its FORMS cover.
       *
       * This took the top-ranked vertical whatever its provenance. On Aspen
       * View Academies, a K-8 charter school, the package policy carries
       * builders-risk and contractors wording, so Construction ranked first and
       * became the industry for the whole report: section 2B expected builders
       * risk, section 6 benchmarked against Construction, section 7 analysed
       * subcontractor and faulty-workmanship exclusions, and section 8 quoted
       * penalties for "Construction organizations". The panel meanwhile said
       * the website does not describe Construction, so the report contradicted
       * itself on its own first page.
       *
       * spSiteCrossCheck already marks each vertical 'confirmed', 'absent' or
       * 'no-site'. A vertical the site does not corroborate may still be listed
       * as a finding, but it may not name the industry. Preference order:
       *
       *   1. the highest-ranked vertical the WEBSITE corroborates
       *   2. the NAICS classification, which reads the insured's business
       *      description rather than its coverage forms
       *   3. the top vertical, only when no site was read at all
       *
       * This is line-agnostic on purpose: an EB review reads handbooks whose
       * vocabulary is just as misleading about the industry as a package
       * policy's is. */
      const _siteConfirmed = verticals.filter(v => v.site === 'confirmed');
      // A trade STATED in the declarations may name the industry too. It is not
      // form language — it is the carrier's own description of the insured,
      // underwritten and printed on the contract — so the objection that
      // produced this rule does not apply to it. Without this branch a
      // declared vertical was neither 'confirmed' nor 'no-site', so a review
      // whose only good evidence was the declarations named no industry at all.
      const _declaredV = verticals.filter(v => v.site === 'declared');
      const _noSiteAtAll = verticals.length > 0
                           && verticals.every(v => v.site === 'no-site' || v.site === 'declared');
      if (_siteConfirmed.length) {
        vertical = _siteConfirmed[0].name;
      } else if (_declaredV.length) {
        vertical = _declaredV[0].name;
      } else if (_noSiteAtAll && verticals[0]) {
        vertical = verticals[0].name;                       // nothing to cross-check against
      } else {
        vertical = '';                                      // resolved from NAICS below
      }
      states = spCanonStates(detectedProfile.states.map(s => s.abbr));
      // Keep the hidden fields + context in sync so all downstream code works.
      document.getElementById('vertical').value = vertical;
      document.getElementById('states').value = states.map(a => (STATE_PROFILES[a]?.name || a)).join(', ');
      window._currentVertical = vertical;
      window._detectedProfile = detectedProfile;
      // Re-run coverage detection now that vertical context is known.
      // Merge FILENAME detection with detection from the parsed document text:
      // a policy whose filename says nothing ("25-26 $20M Policy - Acme.pdf")
      // is still proof the coverage exists, and Section 5 must not report a
      // standalone policy as missing when the insured handed us the policy.
      // Same exclusion Gate 1 applies to evidence sources: a re-uploaded
      // SentriPol report lists every coverage it discussed, so reading
      // coverages out of it would let a prior review assert coverage the
      // insured may not hold.
      const _realDocs = (pdfTexts||[]).filter(d=>{
        const full=(d.pages||[]).map(pg=>pg.text||'').join(' ') || d.text || '';
        return !(window.spIsSelfReport && window.spIsSelfReport(full));
      });
      detectedCoverages = Array.from(new Set([
        ...detectCoverages(uploadedFiles),
        ...detectCoveragesFromPdfText(_realDocs)
      ]));
      showToast('Auto-detected ' + verticals.length + ' vertical(s), ' + states.length + ' state(s) from documents/website.');
      // NAICS-pivot vertical analysis (classify → reference library → derived stack)
      // Never leave this null: spRenderNaicsPanel(null) returns early and the
      // whole NAICS section disappears with nothing said. The unavailable shape
      // renders a panel that states the fault instead.
      try { window._naicsAnalysis = await spNaicsAnalyze(_sources, states); }
      catch (e) {
        console.error('[naics] analysis failed:', e && e.message);
        window._naicsAnalysis = (typeof window.spNaicsUnavailable === 'function')
          ? window.spNaicsUnavailable((e && e.message) || String(e))
          : null;
      }

      /* Step 2 of the industry preference order. The website corroborated
       * nothing, so take the NAICS classification: it reads the insured's
       * business description ("Education with School (K-12)" on Aspen View)
       * rather than the vocabulary of its coverage forms. Only if that is
       * absent too does the report go without an industry, which is the honest
       * outcome and better than naming one the evidence contradicts. */
      if (!vertical) {
        const _nv = ((window._naicsAnalysis || {}).naics || {}).vertical;
        vertical = _nv || '';
        if (vertical) {
          window._spVerticalSource = 'naics';
          console.info('[vertical] no site corroboration; using the NAICS classification: ' + vertical);
        } else {
          window._spVerticalSource = 'none';
        }
        document.getElementById('vertical').value = vertical;
        window._currentVertical = vertical;
      } else {
        window._spVerticalSource = window._spVerticalSource || 'site';
      }
      // BOP review (R1–R4). Reuses the NAICS already classified above rather
      // than re-classifying, and passes the coverages detected across the whole
      // uploaded program so Section 5 never reports a gap the insured has
      // already filled with a standalone policy.
      try {
        window._bopAnalysis = await spBopAnalyze(_sources, states, {
          naics: ((window._naicsAnalysis || {}).naics || {}).code || null,
          presentCoverages: detectedCoverages,
          businessContext: (window._scrapedSite || '') + ' ' + _wsText,
          policyholder: policyholderName
        });
        if (window._bopAnalysis) window._bopAnalysis._states = states;
        if (window._bopAnalysis && window._bopAnalysis.detection && window._bopAnalysis.detection.is_bop) {
          showToast('BOP detected — running the ten-section BOP review (R1–R4).');
        }
      } catch (e) { console.warn('[bop] analysis failed:', e && e.message); window._bopAnalysis = null; }
    }

    // PHASE 1b: Detect coverages from PDF text content (critical for package policies)
    // This scans the actual text of every page for coverage-specific language
    const pdfTextCoverages = detectCoveragesFromPdfText(pdfTexts);
    pdfTextCoverages.forEach(c => {
      if (!detectedCoverages.includes(c)) {
        detectedCoverages.push(c);
      }
    });

    // PHASE 1c: Extract declarations limits and add any found coverage types to detectedCoverages
    // This ensures that if limits were parsed, the coverage is marked as detected
    const earlyDeclLimits = extractDeclarationsLimits(pdfTexts);
    for (const declKey of Object.keys(earlyDeclLimits)) {
      if (!detectedCoverages.includes(declKey)) {
        detectedCoverages.push(declKey);
      }
    }

    // v2.20.12: Extract policy term dates (effective + renewal/expiration) per file.
    // Brokers verify all policies on a program share the same renewal date — mismatched
    // dates are a common error and a coverage-gap risk at renewal.
    /* TIMED, BECAUSE THIS IS THE BLOCK A REAL RUN STOPPED AFTER.
     *
     * Everything here scans every page of every document, and page recovery
     * had just taken four documents from four pages to 243 — sixty times the
     * work, in one synchronous run. Measured on the real program they come to
     * about 130 ms, so they are not the problem today; they are the block most
     * likely to become one when page counts change again, and they were
     * unmeasured when a user's console went quiet exactly here.
     */
    const _spScan = (label, fn, fallback) => {
      const t = Date.now();
      try { return fn(); }
      catch (e) {
        if (typeof console !== 'undefined') console.warn(`[perf] ${label} threw: ${e && e.message}`);
        return fallback;
      }
      finally {
        if (typeof console !== 'undefined' && console.log) console.log(`[perf] ${label}: ${Date.now() - t} ms`);
      }
    };
    const policyTermsByFile = _spScan('policy terms', () => extractPolicyTerms(pdfTexts), {});

    // v2.21: Endorsement detection + classification across all uploaded policies.
    // Each endorsement is tagged GRANT / SUBLIMIT / CARVE-BACK / EXCLUSION_ADD / AMENDATORY /
    // NOTICE / ADDITIONAL_INSURED / WAIVER / OTHER and grouped by coverage part.
    const endorsementData = _spScan('endorsements', () => ((typeof extractEndorsements === 'function') ? extractEndorsements(pdfTexts) : { endorsements: [], byCoverage: {}, byTag: {}, totalDetected: 0 }), { endorsements: [], byCoverage: {}, byTag: {}, totalDetected: 0 });

    // v2.23: Trigger & Retro Date analysis. For every liability coverage part detected,
    // identify whether it's Claims-Made or Occurrence, capture the retro date if claims-made,
    // detect ERP/Tail option presence, and flag retro misalignment across multiple
    // claims-made policies for the same insured. Renders as Section 5C.
    const triggerData = _spScan('triggers', () => ((typeof analyzeTriggers === 'function') ? analyzeTriggers(pdfTexts) : { byCoverage: [], hasAnyClaimsMade: false, hasAnyOccurrence: false, retroDates: [], misalignmentFlag: false }), { byCoverage: [], hasAnyClaimsMade: false, hasAnyOccurrence: false, retroDates: [], misalignmentFlag: false });

    // v2.22: Per-state compliance checklist (Texas first; other states added incrementally).
    const stateCompliance = _spScan('state compliance', () => ((typeof runStateComplianceChecks === 'function') ? runStateComplianceChecks(pdfTexts, states, vertical, endorsementData) : { checks: [], by_state: {}, totalGaps: 0 }), { checks: [], by_state: {}, totalGaps: 0 });

    // PHASE 2: Deep scan for D&O language in policy text
    deepScanResults = _spScan('deep scan D&O', () => deepScanForDO(pdfTexts), {});

    // PHASE 2b: Deep scan for E&O / Professional Liability language in policy text
    deepScanEOResults = _spScan('deep scan E&O', () => deepScanForEO(pdfTexts), {});

    // PHASE 3: If deep scan found D&O, add to detected coverages
    if (deepScanResults.doDetected) {
      const doLabel = deepScanResults.doType === 'standalone' ? 'D&O'
        : deepScanResults.doType === 'embedded_ell' ? 'D&O (Embedded in ELL/EPLI)'
        : deepScanResults.doType === 'embedded_mgmt' ? 'D&O (Embedded in Management Liability)'
        : deepScanResults.doType === 'embedded_board' ? 'D&O (Embedded — Board/Officer Language)'
        : deepScanResults.doType === 'possible' ? 'D&O (Possible — Verify)'
        : 'D&O (Inferred from Policy Language)';
      if (!detectedCoverages.includes('D&O') && !detectedCoverages.some(c => c.startsWith('D&O'))) {
        detectedCoverages.push(doLabel);
      }
      const loc = deepScanResults.doLocation;
      if (loc) {
        showToast(`D&O coverage detected via policy text scan: ${loc.fileName}, Page ${loc.page}`);
      }
    }

    // PHASE 3b: If deep scan found E&O, add to detected coverages
    if (deepScanEOResults && deepScanEOResults.eoDetected) {
      const eoLabel = deepScanEOResults.eoType === 'standalone' ? 'Professional Liability'
        : deepScanEOResults.eoType === 'embedded_ell' ? 'Professional Liability (Embedded in ELL)'
        : deepScanEOResults.eoType === 'embedded_do' ? 'Professional Liability (Embedded in D&O/Mgmt Liability)'
        : deepScanEOResults.eoType === 'endorsement' ? 'Professional Liability (Endorsement)'
        : deepScanEOResults.eoType === 'embedded_pkg' ? 'Professional Liability (Embedded in Package)'
        : deepScanEOResults.eoType === 'embedded_language' ? 'Professional Liability (Embedded — Negligent/Wrongful Act Language)'
        : deepScanEOResults.eoType === 'possible' ? 'Professional Liability (Possible — Verify)'
        : 'Professional Liability (Inferred from Policy Language)';
      if (!detectedCoverages.includes('Professional Liability') && !detectedCoverages.some(c => c.startsWith('Professional Liability'))) {
        detectedCoverages.push(eoLabel);
      }
      const eoLoc = deepScanEOResults.eoLocation;
      if (eoLoc) {
        showToast(`E&O / Professional Liability detected via policy text scan: ${eoLoc.fileName}, Page ${eoLoc.page}`);
      }
    }

    // Small delay to let loading animation complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    clearInterval(stepInterval);
    overlay.classList.remove('active');

    // Run website analysis for operational risks
    const wsUrl = document.getElementById('websiteUrl').value.trim();
    const wsOrgOverview = document.getElementById('orgOverview').value.trim();
    // 7C reasons over the REAL page text. If Auto-Fetch never ran, pull it now —
    // best-effort, never fatal to the review; without it 7C falls back to
    // checklist-only output rather than reporting vertical boilerplate as fact.
    if (wsUrl && !(window._scrapedSite || '').trim()) {
      /* A WAIT NOBODY CAN SEE LOOKS LIKE A FREEZE.
       *
       * This is the last thing between a finished analysis and a rendered
       * report, it is awaited, and it only logs on FAILURE. So a slow site
       * crawl shows as: the last synchronous line in the console, then
       * nothing, then a spinner — which is exactly what a user reported as
       * the tab freezing, after nineteen seconds of backend parsing had
       * already gone by.
       *
       * The crawl walks up to five pages of someone else's website. Its own
       * client timeout is forty-five seconds; the report does not need it that
       * badly. Section 7C falls back to checklist-only output without it, and
       * a report that arrives without website colour beats one that looks
       * hung. Fifteen seconds, and say so both ways.
       */
      const _siteT0 = Date.now();
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[7C] fetching ${wsUrl} (up to 15s; the report does not wait longer)…`);
      }
      try {
        const _s = await Promise.race([
          window.spFetchSite(wsUrl, true),
          new Promise((_, rej) => setTimeout(() => rej(new Error('site fetch exceeded 15s')), 15000))
        ]);
        if (typeof console !== 'undefined' && console.log) {
          console.log(`[7C] site fetch: ${Date.now() - _siteT0} ms`);
        }
        if (_s && (_s.text || '').trim()) {
          window._scrapedSite = _s.text;
          window._siteFetchMeta = { host: _s.host, pages: (_s.pages || []).map(pg => pg.url), title: _s.title,
                                    chars: _s.text.length, profileLines: (_s.profile || []).length };
        }
      } catch (e) {
        console.warn(`[7C] site fetch failed after ${Date.now() - _siteT0} ms:`, e && e.message);
        window._siteFetchError = (e && e.message) || String(e);
      }
    }
    const websiteRisks = analyzeWebsiteContent(
      buildWebsiteRiskSources(window._scrapedSite, wsOrgOverview, wsUrl), vertical);

    // Pre-compute declarations limits for Quick Screen and report generation
    const preComputedDeclLimits = (typeof extractDeclarationsLimits === 'function' && pdfTexts.length > 0) ? extractDeclarationsLimits(pdfTexts) : {};

    const formData = {
      lob, reviewType,
      caseNick: document.getElementById('caseNick').value.trim(),
      incumbent: document.getElementById('incumbent').value.trim(),
      policyholderName,
      websiteUrl: wsUrl,
      states,
      vertical,
      verticals,
      detectedProfile,
      orgOverview: wsOrgOverview,
      files: uploadedFiles,
      pdfTexts: pdfTexts,
      detectedCoverages: detectedCoverages,
      deepScanResults: deepScanResults,
      deepScanEOResults: deepScanEOResults,
      websiteRisks: websiteRisks,
      declLimits: preComputedDeclLimits,
      policyTerms: policyTermsByFile,
      endorsements: endorsementData,
      stateCompliance: stateCompliance,
      triggers: triggerData
    };

    window._lastFormData = formData;
    generateReport(formData);
    try { spRenderProfileCard(detectedProfile); } catch (_e) {}
    try { spRenderNaicsPanel(window._naicsAnalysis); } catch (_e2) {}
    try { spRenderBopPanel(window._bopAnalysis); } catch (_e3) {}
  } catch (err) {
    clearInterval(stepInterval);
    overlay.classList.remove('active');
    console.error('Analysis error:', err);
    showToast('Analysis completed with warnings. Some PDF text extraction may have been skipped.', 'warning');
    // Still generate report even if PDF extraction failed
    const wsUrl2 = document.getElementById('websiteUrl').value.trim();
    const wsOrgOverview2 = document.getElementById('orgOverview').value.trim();
    const websiteRisks2 = analyzeWebsiteContent(
      buildWebsiteRiskSources(window._scrapedSite, wsOrgOverview2, wsUrl2), vertical);

    const formData = {
      lob, reviewType,
      caseNick: document.getElementById('caseNick').value.trim(),
      incumbent: document.getElementById('incumbent').value.trim(),
      policyholderName,
      websiteUrl: wsUrl2,
      states,
      vertical,
      verticals,
      detectedProfile,
      orgOverview: wsOrgOverview2,
      files: uploadedFiles,
      pdfTexts: [],
      detectedCoverages: detectedCoverages,
      deepScanResults: null,
      deepScanEOResults: null,
      websiteRisks: websiteRisks2
    };
    window._lastFormData = formData;
    generateReport(formData);
    try { spRenderProfileCard(detectedProfile); } catch (_e) {}
    try { spRenderNaicsPanel(window._naicsAnalysis); } catch (_e2) {}
    try { spRenderBopPanel(window._bopAnalysis); } catch (_e3) {}
  }
});

// ===== QUICK SCREEN GENERATION =====
function generateQuickScreen(data) {
  const today = new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'});
  const timestamp = new Date().toLocaleString('en-US', {year:'numeric',month:'long',day:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'});
  data.states = spCanonStates(data.states);
  const stateNames = data.states.map(spStateDisplayName).join(', ');
  const isPC = data.lob === 'pc';
  const vl = data.vertical.toLowerCase();
  const isEducation = vl.includes('school') || vl.includes('education') || vl.includes('daycare') || vl.includes('tutor');

  // --- Determine REQUIRED coverages for this vertical ---
  let requiredCoverages = [];
  if (isPC) {
    // Base required P&C coverages — Commercial Property and Auto are core for any entity with physical locations
    requiredCoverages = [
      { type: 'Commercial Property', key: 'Commercial Property', altKeys: ['Property'], critical: true },
      { type: 'General Liability', key: 'General Liability', altKeys: [], critical: true },
      { type: 'Commercial Auto', key: 'Commercial Auto', altKeys: [], critical: false },
      { type: 'Cyber Liability', key: 'Cyber Liability', altKeys: [], critical: false },
      { type: 'Umbrella / Excess', key: 'Umbrella/Excess', altKeys: [], critical: false },
      { type: 'Workers Comp / EL', key: 'Workers Compensation', altKeys: [], critical: true },
      { type: 'EPLI', key: 'EPLI', altKeys: [], critical: false },
      { type: 'D&O / Management Liability', key: 'D&O', altKeys: [], critical: false },
      { type: 'Professional Liability / E&O', key: 'Professional Liability', altKeys: [], critical: false }
    ];
    if (isEducation) {
      requiredCoverages.push({ type: 'Sexual Abuse / Molestation', key: 'Sexual Abuse/Molestation', altKeys: [], critical: true });
      requiredCoverages.push({ type: 'Active Shooter / Violent Event', key: 'Active Shooter', altKeys: [], critical: false });
      requiredCoverages.push({ type: 'Educators Legal Liability (ELL)', key: 'Educators Legal Liability (ELL)', altKeys: ['ELL'], critical: true });
    }
  } else {
    // EB required coverages
    requiredCoverages = [
      { type: 'Medical (Group Health)', key: 'Medical' },
      { type: 'Dental', key: 'Dental' },
      { type: 'Vision', key: 'Vision' },
      { type: 'Life / AD&D', key: 'Life' },
      { type: 'Short-Term Disability', key: 'STD' },
      { type: 'Long-Term Disability', key: 'LTD' },
      { type: '401(k) / Retirement', key: '401k' }
    ];
  }

  // --- Scoring ---
  let score = 100;
  const deductions = [];

  // Check missing required coverages
  const detectedLower = data.detectedCoverages.map(c => c.toLowerCase());
  const missingRequired = [];
  const metRequired = [];
  requiredCoverages.forEach(rc => {
    const found = data.detectedCoverages.includes(rc.key) ||
      data.detectedCoverages.some(c => c.toLowerCase().includes(rc.key.toLowerCase())) ||
      detectedLower.some(c => c.includes(rc.key.toLowerCase())) ||
      (rc.altKeys && rc.altKeys.some(ak => data.detectedCoverages.includes(ak) || data.detectedCoverages.some(c => c.toLowerCase().includes(ak.toLowerCase()))));
    if (!found) {
      missingRequired.push(rc);
      // Critical coverages (Property, GL, WC) get heavier deductions
      const deduction = rc.critical ? 20 : 15;
      score -= deduction;
      deductions.push(`Missing ${rc.type}: -${deduction}${rc.critical ? ' (CRITICAL)' : ''}`);
    } else {
      metRequired.push(rc);
    }
  });

  // Additional deduction: if Property is "detected" but has no actual limits
  const qsPropDetected = data.detectedCoverages.includes('Property') || data.detectedCoverages.includes('Commercial Property');
  const qsDeclLimits = data.declLimits || {};
  const qsPropHasLimits = spHasRealLimits(qsDeclLimits['Commercial Property']);
  if (qsPropDetected && !qsPropHasLimits) {
    score -= 10;
    deductions.push('Property referenced but no limits found: -10 (VERIFY)');
  }

  // Check exclusion alerts
  const exclusionAlerts = isPC ? getExclusionAlerts(data.detectedCoverages, data.vertical) : getEBExclusionAlerts(data.detectedCoverages, data.vertical);
  const fatalAlerts = exclusionAlerts.filter(ea => ea.impact && ea.impact.includes('FATAL FLAW'));
  const nonFatalAlerts = exclusionAlerts.filter(ea => !ea.impact || !ea.impact.includes('FATAL FLAW'));
  fatalAlerts.forEach(() => { score -= 10; });
  nonFatalAlerts.forEach(() => { score -= 10; });
  if (exclusionAlerts.length > 0) {
    deductions.push(`Exclusion alerts (${exclusionAlerts.length}): -${exclusionAlerts.length * 10}`);
  }

  // Check website risks
  const websiteRisks = data.websiteRisks || [];
  websiteRisks.forEach(() => { score -= 5; });
  if (websiteRisks.length > 0) {
    deductions.push(`Website risks (${websiteRisks.length}): -${websiteRisks.length * 5}`);
  }

  // No files uploaded
  if (data.files.length === 0) {
    score -= 20;
    deductions.push('No policy documents uploaded: -20');
  }

  // Clamp score
  score = Math.max(0, Math.min(100, score));

  // Determine verdict
  let verdict, verdictClass, verdictIcon;
  if (score >= 75) {
    verdict = 'PASS';
    verdictClass = 'qs-pass';
    verdictIcon = '\u2705';
  } else if (score >= 50) {
    verdict = 'CAUTION';
    verdictClass = 'qs-caution';
    verdictIcon = '\u26a0\ufe0f';
  } else {
    verdict = 'FAIL';
    verdictClass = 'qs-fail';
    verdictIcon = '\u274c';
  }

  // --- Build HTML ---
  let html = '';

  // Header
  html += `<div class="report-section" style="text-align:center; margin-bottom:12px;">
    <h3 style="border-bottom:none; text-align:center; font-size:18px;">SentriPol\u2122 Quick Screen \u2014 Instant Policy Assessment</h3>
    <p style="font-size:12px; color:var(--text-muted);">${escapeHtml(data.policyholderName)} | ${escapeHtml(data.vertical)} | ${stateNames} | ${isPC ? 'Property & Casualty' : 'Employee Benefits'} | ${today}</p>
  </div>`;

  // Verdict box
  html += `<div class="qs-verdict-box ${verdictClass}" style="text-align:center; color:#fff; padding:24px; border-radius:8px; margin:16px 0;">
    <div class="qs-score-circle" style="display:inline-block; width:80px; height:80px; line-height:80px; border-radius:50%; background:rgba(255,255,255,0.2); font-size:28px; font-weight:800; margin-bottom:8px;">${score}</div>
    <div style="font-size:28px; font-weight:800; letter-spacing:2px; margin-top:4px;">${verdictIcon} ${verdict}</div>
    <div style="font-size:13px; opacity:0.9; margin-top:4px;">Risk Score: ${score} / 100</div>
  </div>`;

  // Summary table
  html += `<table class="report-table qs-category-table"><thead><tr><th>Category</th><th>Status</th><th>Detail</th></tr></thead><tbody>`;

  // Detected coverages (green)
  metRequired.forEach(rc => {
    html += `<tr><td><strong>${rc.type}</strong></td><td style="color:var(--success); font-weight:600;">\u2713 Detected</td><td>Coverage detected in uploaded documents</td></tr>`;
  });

  // Additional detected coverages not in required list
  data.detectedCoverages.forEach(c => {
    const isInRequired = requiredCoverages.some(rc => rc.key === c || c.toLowerCase().includes(rc.key.toLowerCase()));
    if (!isInRequired) {
      html += `<tr><td><strong>${escapeHtml(c)}</strong></td><td style="color:var(--success); font-weight:600;">\u2713 Detected</td><td>Additional coverage detected</td></tr>`;
    }
  });

  // Missing required coverages (red)
  missingRequired.forEach(rc => {
    html += `<tr><td><strong>${rc.type}</strong></td><td style="color:var(--critical); font-weight:600;">\u2717 Missing</td><td>Required for ${escapeHtml(data.vertical)} \u2014 not detected (-15 pts)</td></tr>`;
  });

  // Caution items
  if (data.files.length === 0) {
    html += `<tr><td><strong>Policy Documents</strong></td><td style="color:var(--warning); font-weight:600;">\u26a0 None Uploaded</td><td>No documents provided for analysis (-20 pts)</td></tr>`;
  }
  if (websiteRisks.length > 0) {
    html += `<tr><td><strong>Website Risks</strong></td><td style="color:var(--warning); font-weight:600;">\u26a0 ${websiteRisks.length} Found</td><td>Operational risks identified from website analysis (-${websiteRisks.length * 5} pts)</td></tr>`;
  }
  if (exclusionAlerts.length > 0) {
    html += `<tr><td><strong>Exclusion Alerts</strong></td><td style="color:var(--warning); font-weight:600;">\u26a0 ${exclusionAlerts.length} Triggered</td><td>Policy exclusion patterns flagged (-${exclusionAlerts.length * 10} pts)</td></tr>`;
  }

  html += `</tbody></table>`;

  // Fatal Flaw Alerts
  if (exclusionAlerts.length > 0) {
    html += `<div class="report-section" style="margin-top:16px;">
      <h3 style="font-size:14px;">Fatal Flaw Alerts</h3>`;
    exclusionAlerts.slice(0, 5).forEach(ea => {
      const isFatal = ea.impact && ea.impact.includes('FATAL FLAW');
      html += `<div class="exclusion-alert${isFatal ? '' : ' warning-level'}" style="margin-bottom:8px; padding:8px 12px;">
        <div style="font-size:12px;"><strong>${ea.coverage}</strong> vs. <strong style="color:${isFatal ? 'var(--critical)' : '#C2410C'};">${ea.exclusion}</strong>
        <span class="badge ${isFatal ? 'badge-critical' : 'badge-high'}" style="margin-left:6px;">${isFatal ? 'FATAL FLAW' : 'VERIFY'}</span></div>
      </div>`;
    });
    if (exclusionAlerts.length > 5) {
      html += `<p style="font-size:11px; color:var(--text-muted);">...and ${exclusionAlerts.length - 5} more. Run a Full Review for complete analysis.</p>`;
    }
    html += `</div>`;
  }

  // Website Risk Summary
  if (websiteRisks.length > 0) {
    html += `<div class="report-section" style="margin-top:16px;">
      <h3 style="font-size:14px;">Website Risk Summary (${websiteRisks.length} total)</h3>`;
    websiteRisks.slice(0, 3).forEach(r => {
      const sevBadge = r.severity === 'CRITICAL' ? '<span class="badge badge-critical">CRITICAL</span>'
        : r.severity === 'HIGH' ? '<span class="badge badge-high">HIGH</span>'
        : '<span class="badge badge-moderate">MODERATE</span>';
      html += `<p style="font-size:12px; margin-bottom:6px;">${sevBadge} <strong>${escapeHtml(r.risk)}</strong> \u2014 ${escapeHtml(r.exclusionType)}</p>`;
    });
    if (websiteRisks.length > 3) {
      html += `<p style="font-size:11px; color:var(--text-muted);">...and ${websiteRisks.length - 3} more risks. Run a Full Review for complete analysis.</p>`;
    }
    html += `</div>`;
  }

  // Scoring breakdown
  html += `<div style="margin-top:16px; padding:10px 14px; background:var(--bg-page); border:1px solid var(--border); border-radius:var(--radius-md); font-size:11px; color:var(--text-muted);">`;
  html += `<strong>Score Breakdown:</strong> Starting score: 100`;
  if (deductions.length > 0) {
    html += ` | ` + deductions.join(' | ');
  }
  html += ` | <strong>Final: ${score}/100</strong>`;
  html += `</div>`;

  // Footer
  html += `<div style="margin-top:20px; padding:12px 14px; background:#EFF6FF; border:1px solid #93C5FD; border-radius:var(--radius-md); font-size:12px; color:var(--navy); text-align:center;">
    <strong>This Quick Screen is a preliminary assessment.</strong> Run a Full Review for comprehensive analysis with citations, state regulatory profiles, exclusion contradiction analysis, and industry benchmarking.
  </div>`;

  // Broker disclaimer
  html += `<div class="disclaimer-bar" style="margin-top:12px;">
    <strong>IMPORTANT DISCLAIMER:</strong> SentriPol\u2122 is an AI-powered policy review and analysis tool designed to assist insurance professionals. It is NOT a substitute for professional broker review. The licensed broker or advisor is solely responsible for reviewing, verifying, and validating all findings, recommendations, and conclusions contained in this report before relying upon them or presenting them to any client, prospect, or third party. SentriPol\u2122, AI Blueprints, and their affiliates accept no liability for coverage decisions made based on this analysis.
  </div>`;

  // Timestamp
  html += `<p style="font-size:10px; color:var(--text-muted); text-align:center; margin-top:10px;">Screening performed: ${timestamp}</p>`;

  return html;
}

// ===== REPORT GENERATION =====
function generateReport(data) {
  currentAnalysis = data;
  document.getElementById('emptyState').style.display = 'none';
  document.getElementById('resultsArea').style.display = 'block';

  const title = data.caseNick
    ? `${data.policyholderName} — ${data.caseNick}`
    : `${data.policyholderName} Policy Analysis`;
  document.getElementById('resultsTitle').textContent = title;

  // The E&O record covers P&C and BOP only, so its two buttons follow the line
  // being reviewed. Guarded: eoRecord.js is not present in every build.
  try { if (window.spSyncEoButtons) window.spSyncEoButtons(); } catch (_eo) {}

  const isPC = data.lob === 'pc';
  // Architecture v2: post-bind E&O runs are eliminated. Every non-quick-screen run
  // is the single comprehensive BOR analysis, so there is no pre/post flavour left
  // to branch on. Legacy 'prebind'/'postbind' values saved before the change still
  // land here and resolve to the same comprehensive report.
  // A trial session always gets the (limited) Full Review: the Quick Screen
  // restates findings from sections the trial does not include.
  const trial = spTrialActive();
  const isQuickScreen = !trial && data.reviewType === 'quickscreen';

  // Tab references
  const qsTab = document.querySelector('[data-tab="quickScreen"]');
  const frTab = document.querySelector('[data-tab="fullReview"]');
  const esTab = document.querySelector('[data-tab="execSummary"]');

  if (isQuickScreen) {
    // Quick Screen only — hide Full Review and Exec Summary tabs
    const quickScreenHtml = generateQuickScreen(data);
    document.getElementById('quickScreen').innerHTML = quickScreenHtml;
    document.getElementById('fullReview').innerHTML = '';
    document.getElementById('execSummary').innerHTML = '';

    qsTab.style.display = '';
    frTab.style.display = 'none';
    esTab.style.display = 'none';

    switchTab('quickScreen');
  } else {
    // Full BOR analysis mode
    // Generate full review
    let fullReview = '';
    try {
      fullReview = isPC
        ? generatePCFullReview(data)
        : generateEBFullReview(data);
    } catch(e) {
      console.error('Full Review generation error:', e);
      fullReview = `<div class="report-section" style="border:2px solid var(--critical); padding:16px;"><h3>Report Generation Error</h3><p>An error occurred: ${e.message}</p><pre style="font-size:10px;background:#f5f5f5;padding:8px;overflow:auto;max-height:200px;">${e.stack}</pre><p>Please report this to support.</p></div>`;
    }
    document.getElementById('fullReview').innerHTML = fullReview;

    // Generate executive summary (not for a trial session, which gets a
    // locked tab instead; see spTrialLockedTabHtml)
    let execSummary = '';
    if (!trial) try {
      execSummary = isPC
        ? generatePCExecSummary(data)
        : generateEBExecSummary(data);
    } catch(e) {
      console.error('Executive Summary generation error:', e);
      execSummary = `<div class="report-section" style="border:2px solid var(--critical); padding:16px;"><h3>Executive Board Summary</h3><p>An error occurred generating the executive summary. The Full Review tab contains the complete analysis.</p><p style="font-size:11px; color:var(--text-muted);">Error: ${e.message}</p></div>`;
    }
    if (trial) execSummary = spTrialLockedTabHtml('Executive Board Summary');
    document.getElementById('execSummary').innerHTML = execSummary;

    // Also generate quick screen as a bonus tab
    const quickScreenHtml = trial ? spTrialLockedTabHtml('Quick Screen') : generateQuickScreen(data);
    document.getElementById('quickScreen').innerHTML = quickScreenHtml;

    // Show all 3 tabs
    qsTab.style.display = '';
    frTab.style.display = '';
    esTab.style.display = '';

    switchTab('fullReview');
  }

  document.getElementById('mainContent').scrollTop = 0;
}


// ===== DECLARATIONS LIMITS EXTRACTION ENGINE =====
// Parses PDF text from all uploaded files to extract actual coverage limits from declarations pages
// v2.7: Page-aware extraction — processes declarations pages first, uses periods-as-commas OCR fix,
//       and picks the LARGEST dollar amount when multiple matches exist for the same label.
// Accepts pdfTexts array: [{ fileName, pages: [{ page, text }], totalPages }]
// OR legacy format with f.text string

// v2.18: Extract carrier name per coverage type for P&C card display
// Fixes: (1) expanded pattern list incl. education/charter-school MGAs,
// (2) full-document search instead of first 5000 chars,
// (3) multi-carrier support — different docs can supply different carriers per coverage
/* WHICH UPLOADED POLICY CARRIES THIS COVERAGE.
 *
 * One question, asked in several places, and each place answered it its own
 * way — so a single row could disagree with itself. Tulsa Honor Academy's
 * Commercial Property row named Wright Specialty in its header, cited the AUTO
 * policy's declarations and endorsements beneath it, and reported American
 * Family Home Insurance as the company parsed. Three documents, one finding.
 *
 * Both askers used the weakest signal available — does this coverage's name
 * appear anywhere in the text — and broke the inevitable tie by upload order.
 * Every commercial form mentions property somewhere, so the tie was always
 * there and the first file always won.
 *
 * The signals, strongest first:
 *
 *   4  the page-aware coverage detector finds it in this document
 *   3  it is named on a DECLARATIONS or schedule page, not in form wording
 *   1  it is mentioned somewhere in the document
 *
 * Scores add, so a document that is detected AND declares it on a schedule
 * page beats one that merely does the first. Returns the basis as well as the
 * document, because a caller that shows a citation should be able to say how
 * strong it is — and because a score of 1 across the board means nothing here
 * really carries the coverage, which is worth knowing rather than papering
 * over with a pick.
 */
/* The PRIMARY slot each coverage states, by the label it states it under.
 *
 * Not every figure on a page, just the one whose absence means "no limit".
 * Used by the stage ledger to ask a question specific to the coverage rather
 * than a generic one — asking whether a workers-comp policy states a Building
 * limit answers nothing.
 */
const SP_PRIMARY_SLOT = {
  'Commercial Property':       /^building|^(?:business\s+)?personal\s+property|^all\s+personal\s+property/i,
  'General Liability':         /each\s+occurrence|general\s+aggregate/i,
  'Commercial Auto':           /combined\s+single|covered\s+autos?\s+liability|liability\s+limit/i,
  'Excess / Umbrella Liability': /each\s+occurrence|aggregate\s+limit/i,
  'Umbrella/Excess':           /each\s+occurrence|aggregate\s+limit/i,
  'Workers Compensation':      /each\s+accident|bodily\s+injury\s+by|policy\s+limit/i,
  'Employment Practices Liability': /aggregate\s+limit|each\s+(?:claim|employment)/i,
  'EPLI':                      /aggregate\s+limit|each\s+(?:claim|employment)/i,
  'Directors & Officers':      /aggregate\s+limit|each\s+claim/i,
  'D&O':                       /aggregate\s+limit|each\s+claim/i,
  'Cyber Liability':           /aggregate\s+limit|each\s+claim|limit\s+of\s+liability/i,
  'Professional Liability / E&O': /aggregate\s+limit|each\s+claim/i,
  'Crime / Fidelity':          /employee\s+theft|limit\s+of\s+(?:liability|insurance)/i,
  'Business Income':           /business\s+income|time\s+element/i,
};
function spPrimarySlot(type) {
  const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  if (SP_PRIMARY_SLOT[type]) return SP_PRIMARY_SLOT[type];
  const canon = CT ? CT.canonical(type) : null;
  return (canon && SP_PRIMARY_SLOT[canon]) || null;
}
if (typeof window !== 'undefined') window.spPrimarySlot = spPrimarySlot;

/* WHY A LIMIT DID NOT PARSE, NAMED BY STAGE.
 *
 * "Coverage detected — limits not parsed from declarations" is one sentence
 * for five different situations, and a broker cannot act on any of them. This
 * week each one had to be diagnosed by hand, by reading the PDF:
 *
 *   no policy in the upload carries this coverage
 *   a policy does, but has no declarations or schedule page
 *   it has one, and states no field for this slot
 *   it states a field, and the figure was REFUSED — a sublimit, a retention,
 *     a figure inside a clause, a statutory notice
 *   it parsed
 *
 * The fourth is the one that matters most and the one silence hides
 * completely: a refusal is a decision the engine made, and a decision a
 * reviewer is entitled to see. On the surplus-lines property policy the
 * building sum insured was read correctly and then refused as a notice for a
 * day, and nothing anywhere said so.
 *
 * Returns { stage, detail, doc }. Diagnostic only — nothing downstream
 * branches on it, so it cannot change an answer, only explain one.
 */
function spExtractionStage(pdfTexts, type, slotRe) {
  slotRe = slotRe || spPrimarySlot(type);
  const pick = (typeof spDocumentForCoverage === 'function')
    ? spDocumentForCoverage(pdfTexts || [], type) : null;
  if (!pick) return { stage: 'no-document', detail: 'no uploaded policy carries this coverage' };
  const doc = pick.doc, pages = doc.pages || [];

  const hasSchedule = (typeof window !== 'undefined' && window.spHasSchedulePage)
    ? window.spHasSchedulePage : null;
  const decPages = hasSchedule ? pages.filter(p => hasSchedule(p.text || '')) : pages;
  if (hasSchedule && !decPages.length) {
    return { stage: 'no-declarations', doc,
             detail: 'this policy has no declarations or schedule page — request it to confirm limits' };
  }
  if (!slotRe || typeof window === 'undefined' || !window.spReadFields) {
    return { stage: 'unknown', doc, detail: '' };
  }

  // Every field naming the slot, anywhere in the policy, believable or not.
  let named = [];
  for (const pg of pages) {
    for (const f of (window.spAllFields || window.spReadFields)(pg)) {
      if (f.money && slotRe.test(f.label)) named.push(f);
    }
  }
  if (!named.length) {
    return { stage: 'no-field', doc,
             detail: 'the declarations state no field for this limit' };
  }
  const REFUSED = { clause: 'stated inside a clause rather than a field',
                    notice: 'stated on a statutory notice, which is not this insured\u2019s cover' };
  const believable = named.filter(f =>
    // 'grid' reads the amount from its own cell, with its row label and its
    // column — the strongest provenance available. Left out, this diagnostic
    // reported "the declarations state no field for this limit" over a
    // schedule that states it in a table, which is how a property row came to
    // deny a $26,582,592 building limit printed on page 4.
    (f.provenance === 'field' || f.provenance === 'row' || f.provenance === 'grid')
    && !['sublimit', 'retention', 'cost'].includes(f.kind));
  if (!believable.length) {
    const f = named[0];
    const why = REFUSED[f.provenance]
      || (f.kind === 'sublimit' ? 'a sublimit, which is a carve-out of a limit rather than one'
        : f.kind === 'retention' ? 'a retention or deductible, not a limit'
        : f.kind === 'cost' ? 'a premium or fee'
        : 'not in a position this engine reads as a limit');
    return { stage: 'refused', doc,
             detail: 'a figure was found and not believed: \u201c' + f.label + ' ' + f.value
                   + '\u201d is ' + why };
  }
  return { stage: 'parsed', doc, detail: '' };
}
if (typeof window !== 'undefined') window.spExtractionStage = spExtractionStage;

const _SP_DOC_DETECTED = new WeakMap();
const _SP_DOC_OWNTEXT = new WeakMap();

function spDocumentForCoverage(pdfTexts, type) {
  const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  if (!CT || !pdfTexts || !pdfTexts.length || !type) return null;
  const _norm = (t) => ((typeof window !== 'undefined' && window.spRetroAnchor)
    ? window.spRetroAnchor.spTableLines(t) : String(t || ''));
  const scored = [];
  for (const x of pdfTexts) {
    const pages = x.pages || [];
    const text = _norm(pages.map(p => p.text || '').join('\n'));
    /* A MENTION INSIDE FURNITURE DOES NOT QUALIFY THE DOCUMENT.
     *
     * "mentioned" scored 1 on the bare presence of the name anywhere in the
     * document, with no test of where. On the Tulsa program the only mention
     * of Directors and Officers Liability in the excess policy is a carve-back
     * inside an exclusion --
     *
     *     professional liability insurance (except Directors and Officers
     *     Liability); or farm
     *
     * -- and because no other document named D&O at all, that one parenthetical
     * won by default. The Title IX row was then sourced to the excess policy
     * and the excess carrier, and reported "limits not parsed", while the real
     * Educators Legal Liability declarations sat unread in the package.
     *
     * The coverage must be named at least once where the policy is speaking
     * for itself. Cached per document: this runs for every coverage row, every
     * limit spec and again for carrier attribution. */
    /* PER PAGE, NOT PER DOCUMENT. A furniture heading scopes to the page it
     * is printed on. Run over a whole policy joined into one string, a
     * forms-schedule opener with no ender ran 86,357 characters and blanked
     * 96% of a 49-page auto policy -- losing the document for Commercial Auto
     * entirely. Bounded to a page, a runaway span can cost one page. */
    let own = _SP_DOC_OWNTEXT.get(x);
    if (own === undefined) {
      own = pages.map(pg => spOwnText(pg.text || ''));
      _SP_DOC_OWNTEXT.set(x, own);
    }
    /* A MENTION THAT IS NOT A GRANT DOES NOT QUALIFY THE DOCUMENT.
     *
     * Blanking the furniture SPANS is not enough on its own, because the one
     * piece of furniture with no structural ender is left unspanned: an
     * endorsement's applicability list. Tulsa Honor Academy's package policy
     * names PUBLIC OFFICIALS AND MANAGEMENT LIABILITY COVERAGE PART twice, on
     * pages 9 and 12, both times in the list of parts an Oklahoma cancellation
     * endorsement could modify, beside GOVERNMENT CRIME and INLAND MARINE. The
     * policy's own declarations say what it bought -- General Liability
     * $20,136 and Educators Legal Liability $17,633, summing to the $37,769
     * total -- and management liability is not among it.
     *
     * So this asks the question detection already asks, with the same rule:
     * spInGrantPosition. Reaching only for isInBoilerplateListing was not
     * enough -- it rejected the two applicability lists and still let through
     *
     *     C. The Representations Condition in the Public Management Liability
     *        Coverage Part and Public Management Liability Coverage Part
     *        (Claims Made) is replaced by the following:
     *
     * an endorsement amending a coverage part this policy does not carry. That
     * sentence is what the finiteVerb guard was written for, and the whole
     * point of having one grant rule is that a reader should not pick pieces
     * of it. mentionsOf supplies the offsets it needs.
     *
     * This is why detection got the answer right while attribution did not:
     * detectCoveragesFromPdfText has always run every guard, and this ran
     * none. */
    let grounded = false;
    for (const o of own) {
      if (!o) continue;
      for (const m of CT.mentionsOf(o, type)) {
        if (spInGrantPosition(o, m.index, m.term)) { grounded = true; break; }
      }
      if (grounded) break;
    }
    /* DETECTION OUTRANKS POSITION, BECAUSE THIS QUESTION IS NOT "DOES IT EXIST".
     *
     * A coverage written on its own policy may name itself nowhere but inside
     * an endorsement. The Student Accident policy is exactly that: no
     * declarations page in the file at all, and the carrier, the policy number
     * and the premium readable only from an Administrative Change Endorsement.
     * Refusing that document for want of a grant position deleted all three
     * from the report and left it reading as though the file had never been
     * parsed -- the failure the both-passes rule exists to prevent.
     *
     * Detection answers whether a coverage exists. This answers which document
     * to cite for it, and must be the more generous of the two. So a document
     * qualifies where the coverage is DETECTED in it, whatever position the
     * mention sits in, and otherwise only on a grant-position mention. Where
     * detection says no and every mention is furniture -- Tulsa's D&O, named
     * only in applicability lists and an endorsement amending a coverage part
     * the policy does not carry -- no document is cited, which is the honest
     * answer. */
    if (!grounded) {
      let detectedHere = false;
      try {
        let dh = _SP_DOC_DETECTED.get(x);
        if (!dh) {
          dh = [...detectCoveragesFromPdfText([{ fileName: x.fileName, pages: pages }])];
          _SP_DOC_DETECTED.set(x, dh);
        }
        detectedHere = dh.some(c => CT.sameTerm(c, type));
      } catch (_e) { detectedHere = false; }
      if (!detectedHere) continue;
    }
    let score = 1, basis = ['mentioned'];
    try {
      /* DETECTED ONCE PER DOCUMENT, NOT ONCE PER ASKING.
       *
       * This is asked for every coverage row in the report, for every limit
       * spec, and again for carrier attribution — and each asking ran the full
       * detector over the whole document. On a four-policy program read as 277
       * pages that is dozens of complete passes, and it was the single largest
       * cost in generating a report: a browser tab that stops responding.
       *
       * Keyed by the document object in a WeakMap, so nothing is retained once
       * the upload goes out of scope. The answer cannot change for a document
       * whose pages have not changed.
       */
      let det = _SP_DOC_DETECTED.get(x);
      if (!det) {
        det = [...detectCoveragesFromPdfText([{ fileName: x.fileName, pages: pages }])];
        _SP_DOC_DETECTED.set(x, det);
      }
      if (det.some(c => CT.sameTerm(c, type))) { score += 4; basis.push('detected'); }
    } catch (_e) { /* detection strengthens the answer; it is not required for one */ }
    /* The declarations-page signal is OPTIONAL, not assumed.
     *
     * spHasSchedulePage lives in the report renderer's closure, not at module
     * scope, so calling it from here threw ReferenceError in every consumer
     * that reaches this function outside a report — six test files at once.
     * A shared helper cannot depend on the scope of the first place that
     * happened to need it. Where the test is unavailable the other two signals
     * still rank the documents; it only loses the tie-breaker. */
    const _hasSchedule = (typeof spHasSchedulePage === 'function') ? spHasSchedulePage
      : ((typeof window !== 'undefined' && typeof window.spHasSchedulePage === 'function')
          ? window.spHasSchedulePage : null);
    if (_hasSchedule) {
      const decPage = pages.find(p => _hasSchedule(p.text || ''));
      if (decPage && CT.coveragesIn(_norm(decPage.text || '')).some(c => CT.sameTerm(c, type))) {
        score += 3; basis.push('on a declarations page');
      }
    }
    scored.push({ doc: x, score, basis: basis.join(', ') });
  }
  if (!scored.length) return null;
  scored.sort((a, b) => b.score - a.score);
  return scored[0];
}
if (typeof window !== 'undefined') window.spDocumentForCoverage = spDocumentForCoverage;

/* WHAT THE POLICY SAYS ABOUT A COVERAGE, NOT WHETHER IT SAYS IT.
 *
 * Every over-detection this week was the same mistake. A policy names a
 * coverage in order to say one of several quite different things:
 *
 *   granted    "EQUIPMENT BREAKDOWN COVERAGE PART", a limit in a field
 *   declined   "Equipment Breakdown        No Coverage        per Occurrence"
 *   excluded   "CC EQB 004 04/2025   Equipment Breakdown Exclusion"
 *   elsewhere  a Schedule of Controlling Underlying Insurance — another
 *              carrier's policy, printed so the excess layer knows where it
 *              attaches
 *   silent     the coverage is not in the documents at all
 *
 * The engine recorded only that the name appeared, and "appeared" then meant
 * "has". So Tulsa Honor Academy's excess policy reported ten coverages it does
 * not grant, its property policy reported Cyber Liability while carrying LMA
 * 5401 excluding cyber, and Equipment Breakdown was reported as present with
 * unparsed limits on a policy that declines it in three separate declarations
 * rows. Each was patched where it surfaced. This reads the stance once.
 *
 * A declination outranks everything except a grant. It is the carrier
 * answering the question, in a field, on a declarations page — better evidence
 * than any keyword, and the one thing nothing in the pipeline could read.
 *
 * Returns { stance, quote, fileName, page } — the quote is what a reviewer
 * checks the claim against, so a stance with no quote is never returned.
 */
function spCoverageStance(pdfTexts, type) {
  const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  const readFields = (typeof window !== 'undefined' && window.spReadFields) || null;
  if (!CT) return { stance: 'unknown', quote: null };
  /* A benchmark row is named for a reader, a declarations row for a broker.
   * The property policy answers "Flood  No Coverage"; the row asking is called
   * "Flood Insurance", so the refusal never reached the row and a weaker
   * exclusion sentence was quoted instead.
   *
   * sameTerm now joins those two names, but mentionsOf searches a class's MATCH
   * TERMS, and the Flood class deliberately carries no bare "flood" term —
   * property policies say the word constantly, in flood exclusions and flood
   * zones, and a bare term would make coveragesIn fire on all of it. So this
   * stays. Only the generic trailing noun is stripped; "Liability" and
   * "Coverage" carry meaning in a coverage name and are left alone. */
  const bare = String(type || '').replace(/\s+insurance$/i, '').trim().toLowerCase();
  const names = t => (CT.mentionsOf && CT.mentionsOf(t, type).length > 0)
    || (bare && bare !== String(type || '').toLowerCase()
        && new RegExp('(^|[^a-z0-9])' + bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                      + '([^a-z0-9]|$)', 'i').test(t));
  const labelIsType = (label) => {
    const l = String(label || '').trim().toLowerCase();
    return l === bare || (CT.mentionsOf(label, type).length > 0);
  };
  let granted = null, grantedByName = null, declined = null, excluded = null,
      elsewhere = null, stated = null;

  for (const doc of (pdfTexts || [])) {
    for (const pg of ((doc && doc.pages) || [])) {
      const t = String((pg && pg.text) || '');
      if (!t || !names(t)) continue;

      // GRANTED wins outright and stops the search: a field carrying an amount
      // under this coverage's name, or a mention in a grant position.
      if (readFields) {
        for (const f of readFields(t)) {
          if (!labelIsType(f.label)) continue;
          // A premium is not a limit. "General Liability  $20,136.00" is what
          // the coverage cost, and quoting it as the grant invites a reviewer
          // to check a figure that answers a different question. The stance is
          // the same either way, so an amount still establishes the grant —
          // but a figure typed as a limit is the better thing to quote, and is
          // held back for one pass in case one turns up.
          if (f.kind === 'cost') continue;
          if (f.money && f.provenance !== 'clause' && f.provenance !== 'notice') {
            const g = { stance: 'granted', quote: f.label + ' ' + f.value,
                        fileName: doc.fileName, page: pg.page };
            if (f.kind === 'limit' || f.kind === 'sublimit') return g;
            if (!granted) granted = g;
          }
          // The carrier answered, in the value column, with a refusal.
          if (f.declined && !declined) {
            declined = { stance: 'declined', quote: f.label + ' — ' + f.value,
                         fileName: doc.fileName, page: pg.page };
          }
        }
      }
      for (const hit of CT.mentionsOf(t, type)) {
        /* A figure stated only in prose is worth quoting even though it is not
         * worth parsing. Collected as the scan goes, so a grant found on an
         * earlier page still carries the sentence that says how much. */
        if (!stated) {
          const sent = spSentenceAt(t, hit.index);
          if (/\$\s*[\d][\d,]{2,}/.test(sent)
              && /\b(?:not\s+exceed|maximum|aggregate|sub-?limit|limit\s+of\s+liability|each\s+\w+)\b/i.test(sent)) {
            stated = { quote: sent, fileName: doc.fileName, page: pg.page,
                       amount: ((sent.match(/\$\s*[\d][\d,]{2,}/) || [])[0] || '')
                         .replace(/[^0-9]+$/, '') };
          }
        }
        if (spInGrantPosition(t, hit.index, hit.term)) {
          /* A NAME WITH NO VALUE BESIDE IT IS THE WEAKEST GRANT THERE IS.
           *
           * CoverCap's property declarations print "Equipment Breakdown" on a
           * line of its own inside the limits table, and two lines below give
           * it the value "No Coverage". The bare name reads as a heading — it
           * IS essentially the coverage's name, which is how a heading is
           * recognised — so the policy appeared to grant a coverage it
           * declines in a field on the same page.
           *
           * Evidence carrying a value beats evidence carrying none. A bare
           * heading still establishes a grant where nothing contradicts it,
           * which is how Matrix Head Start's Workplace Violence Expense
           * Coverage Part is found; it just no longer outranks a refusal.
           */
          const hasValue = /\$\s*[\d]/.test(spLineAt(t, hit.index));
          const g = { stance: 'granted', quote: spLineAt(t, hit.index),
                      fileName: doc.fileName, page: pg.page };
          if (hasValue) granted = granted || g;
          else grantedByName = grantedByName || g;
        }
        if (!excluded && spInExclusionTitle(t, hit.index)) {
          excluded = { stance: 'excluded', quote: spLineAt(t, hit.index),
                       fileName: doc.fileName, page: pg.page };
        }
        if (!elsewhere && spInUnderlyingSchedule(t, hit.index)) {
          elsewhere = { stance: 'elsewhere', quote: spLineAt(t, hit.index),
                        fileName: doc.fileName, page: pg.page };
        }
      }
    }
  }
  /* A REFUSAL IS STRONGER THAN A HEADING; AN EXCLUSION TITLE IS NOT.
   *
   * "Equipment Breakdown — No Coverage" is the carrier answering the question,
   * in a field, so it outranks the bare name printed two lines above it. An
   * exclusion TITLE is a weaker thing entirely: a policy carries exclusion
   * forms for coverages it also grants, and carves back through them. Ranking
   * it above a heading made a 200-page auto policy read "excluded" on the
   * strength of one exclusion title, on a program whose auto declarations sit
   * in the same file.
   *
   *   a grant with an amount on the line
   *   a refusal stated in a field
   *   a heading naming the coverage
   *   an exclusion title
   *   a mention inside another carrier's schedule
   */
  const out = granted || declined || grantedByName || excluded || elsewhere
    || { stance: 'silent', quote: null };
  if (stated && out.stance === 'granted') out.stated = stated;
  return out;
}

/* THE SENTENCE A FIGURE SITS IN, REASSEMBLED ACROSS THE LINES IT WRAPS OVER.
 *
 * Some limits are stated in prose and nowhere else. Matrix Head Start's
 * workplace violence sublimit is one:
 *
 *     ... provided that the Company's maximum aggregate liability for all
 *     Workplace Violence Expenses shall not exceed $250,000, which amount is
 *     part of, and not in addition to, the Company's Aggregate Limit ...
 *
 * The field reader will not promote that to a limit, and should not: a clause
 * figure is reportable and never believable, which is the rule that kept a
 * TRIA threshold and an endorsement's defence-cost carve-back out of the
 * limits. But refusing to PARSE a figure is not a reason to refuse to SHOW it.
 * A broker can read a sentence, and the sentence carries its own caveats —
 * here, that the $250,000 is part of and not in addition to the EPL aggregate,
 * which no limits field could have expressed.
 */
function spSentenceAt(text, index, maxLen) {
  const t = String(text || '');
  const cap = maxLen || 520;
  let a = index, b = index;
  while (a > 0 && index - a < cap) {
    if (t[a - 1] === '\n' && t[a - 2] === '\n') break;
    if (t[a - 1] === '.' && /\s/.test(t[a] || '')) break;
    a--;
  }
  while (b < t.length && b - index < cap) {
    if (t[b] === '.' && (b + 1 >= t.length || /\s/.test(t[b + 1]))) { b++; break; }
    if (t[b] === '\n' && t[b + 1] === '\n') break;
    b++;
  }
  let out = t.slice(a, b).replace(/\s+/g, ' ').trim();
  // A quote cut mid-word reads as a transcription error rather than an
  // excerpt. Stop at the last whole word and say it was cut.
  if (b - index >= cap && !/[.]$/.test(out)) {
    out = out.replace(/\s+\S*$/, '') + '\u2026';
  }
  return out;
}

/** The printed line a match sits on, trimmed — what a reviewer checks. */
function spLineAt(text, index) {
  const t = String(text || '');
  const a = t.lastIndexOf('\n', index) + 1;
  let b = t.indexOf('\n', index);
  if (b < 0) b = t.length;
  return t.slice(a, b).trim().replace(/\s{2,}/g, '  ').slice(0, 160);
}

if (typeof window !== 'undefined') {
  window.spCoverageStance = spCoverageStance;
  window.spLineAt = spLineAt;
  window.spSentenceAt = spSentenceAt;
}

/* WHO ISSUED THIS POLICY, AND WHAT IS ITS NUMBER.
 *
 * Tulsa Honor Academy's auto policy was reported as "American Alternative
 * Insurance Co · Policy PRIOR", with Wright Specialty as the carrier. Three
 * wrong answers about one document. Its declarations say:
 *
 *     POLICY NUMBER: 4XX1AB0009876-00        PRIOR POLICY NUMBER: NEW
 *     COMPANY NAME                            PRODUCER NAME
 *     American Family Home Insurance Company  Wright Specialty Insurance
 *
 * "Policy PRIOR" is the giveaway. Backend parsing renders that two-column
 * block as a header row above a value row:
 *
 *     POLICY NUMBER      PRIOR POLICY NUMBER
 *     4XX1AB0009876-00   NEW
 *
 * and a regex that takes the next token after "POLICY NUMBER" captures the
 * neighbouring LABEL. The carrier came from the first "... Insurance Co" in
 * the document, wherever that fell, and Wright Specialty from a keyword search
 * of the whole file — Wright Specialty is the PRODUCER, printed in the column
 * beside the company that actually carries the risk.
 *
 * Same defect as the rest of this week: a window after a label instead of a
 * field. So read fields. Labels and values pair two ways on a declarations
 * page — inline after a colon, and a header row above a value row — and the
 * second pairs by COLUMN, which is what the printed page encodes. Aligning on
 * character offset survives ragged widths and unequal cell counts, where
 * pairing by index does not.
 */
function spReadLabelledPairs(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  const cellsOf = (line) => {
    const res = [];
    const re = /\S(?:.*?\S)?(?=\s{2,}|$)/g;
    let m;
    while ((m = re.exec(line)) !== null) {
      res.push({ text: m[0].trim(), at: m.index });
      if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
    }
    return res;
  };
  const LABELISH = /^[A-Za-z][A-Za-z.&'()\/ -]{2,48}$/;

  for (let i = 0; i < lines.length; i++) {
    /* A MARKDOWN TABLE CELL THAT STACKS ITS LABEL AND VALUE.
     *
     * LlamaParse renders the declarations identity block as one table row,
     * with each cell holding a label and its value separated by <br/>:
     *
     *   | **COMPANY NAME**<br/>Northgate Mutual Insurance Company<br/>1
     *     Example Road<br/>… | **PRODUCER NAME**<br/>Brightline
     *     Brokers<br/>… |
     *
     * None of the three shapes below sees that — not an inline colon, not a
     * header over a row, not a label and value in separate columns — so the
     * carrier read as nothing on every document, the keyword fallback ran, and
     * a subscription property placement was attributed to one of its
     * participating syndicates.
     *
     * Read the cell the way it is drawn: split the row on pipes, split each
     * cell on its breaks, and the first two segments are the pair. The rest of
     * the cell is the address, which nothing here wants.
     */
    if (/^\s*\|/.test(lines[i]) && /<br\s*\/?>/i.test(lines[i])) {
      for (const cell of lines[i].split('|')) {
        const segs = cell.split(/<br\s*\/?>/i).map(x => x.replace(/\*\*/g, '').trim());
        if (segs.length < 2) continue;
        const label = segs[0].replace(/[:\s]+$/, '');
        const value = segs[1];
        if (label && value && LABELISH.test(label) && label.length <= 48 && value.length <= 70) {
          out.push({ label: label, value: value, line: i });
        }
      }
      continue;
    }
    const cells = cellsOf(lines[i]);
    if (!cells.length) continue;

    /* A BOLD MARKDOWN LABEL, WITH ITS VALUE ON THE SAME LINE AND NO COLON.
     *
     *     **POLICY NUMBER** 00-XXX-0000
     *     **CARRIER** Various as per Carrier Participation Page
     *
     * LlamaParse writes a declarations field this way when the source has no
     * punctuation between them. The inline rule below wants a colon and the
     * column rule wants two spaces; this has neither, so a subscription
     * placement's carrier and policy number both read as nothing. */
    {
      const bold = /^\s*\*\*([^*]{2,44}?)\*\*[:\s]+(.{2,80}?)\s*$/.exec(lines[i]);
      if (bold && LABELISH.test(bold[1].replace(/[:\s]+$/, ''))) {
        out.push({ label: bold[1].replace(/[:\s]+$/, ''), value: bold[2].trim(), line: i });
        continue;
      }
    }

    // A. Inline "LABEL: value", possibly several across one printed line.
    let inlineFound = false;
    for (const c of cells) {
      const m = /^(.{2,48}?)\s*:\s*(.+)$/.exec(c.text);
      if (m && LABELISH.test(m[1].trim())) {
        out.push({ label: m[1].trim(), value: m[2].trim(), line: i });
        inlineFound = true;
      }
    }
    if (inlineFound) continue;

    // B. A header row above a value row. Every cell must read as a label and
    //    none may carry a value, or an ordinary data row would be read as
    //    headers for the row beneath it.
    //    A column header is SHORT. Without the cap, "American Family Home
    //    Insurance Company  Wright Specialty Insurance" reads as a header row
    //    for the address line beneath it, and the carrier's own name becomes a
    //    label whose value is a street.
    //    A failed test here must fall through to C, not skip the line: these
    //    are two shapes a line can have, not two guards on one shape.
    const looksLikeHeaderRow = cells.length >= 2
      && cells.every(c => c.text.length <= 30 && LABELISH.test(c.text) && !/\d/.test(c.text));
    if (looksLikeHeaderRow) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      const vals = j < lines.length ? cellsOf(lines[j]) : [];
      if (vals.length && vals.length <= cells.length) {
        //    Each value belongs to the NEAREST header, not the last one before
        //    it: a printed header is centred over its column, so "COMPANY
        //    NAME" starts to the right of the long name sitting under it.
        for (const v of vals) {
          let owner = null, best = Infinity;
          for (const c of cells) {
            const dist = Math.abs(c.at - v.at);
            if (dist < best) { best = dist; owner = c; }
          }
          if (owner && owner.text !== v.text) {
            out.push({ label: owner.text, value: v.text, line: j });
          }
        }
        continue;
      }
    }
    // C. One printed row: a label in the first column, its value in the next.
    //    "CARRIER          Various as per Carrier Participation Page" is a
    //    field, but it is neither an inline colon nor a header over a row, so
    //    it fell through both branches and CoverCap had no carrier at all.
    //    The label cell is SHORT. "American Family Home Insurance Company
    //    Wright Specialty Insurance" is also two cells, and without the cap
    //    the carrier's name becomes a label whose value is the broker.
    if (cells.length === 2 && cells[0].text.length <= 24
        && LABELISH.test(cells[0].text) && !/\d/.test(cells[0].text)
        && cells[1].text.length >= 2 && cells[0].text !== cells[1].text) {
      out.push({ label: cells[0].text, value: cells[1].text, line: i });
      continue;
    }

  }
  return out;
}

const _SP_PRIOR_LABEL   = /\b(?:prior|previous|expiring|preceding|renewal\s+of|replaced)\b/i;
const _SP_PRODUCER_LABEL = /\b(?:producer|agent|agency|broker|brokerage|wholesaler|program\s+manager|mga|surplus\s+lines)\b/i;
const _SP_CARRIER_LABEL  = /\b(?:company\s*name|insurer|issuing\s+(?:company|carrier)|underwritten\s+by|carrier|insurance\s+company)\b/i;
const _SP_POLICY_LABEL   = /^(?:policy\s*(?:no\.?|number|#)|policy)$/i;
const _SP_NOT_A_NUMBER   = /^(?:new|none|n\/?a|nil|pending|tbd|renewal|see\s|various|as\s+per)/i;
const _SP_SUBSCRIPTION   = /^(?:various|as\s+per|see\s+(?:the\s+)?(?:attached|carrier|participation|schedule)|per\s+(?:the\s+)?(?:attached|schedule))\b/i;
const _SP_ORG_NAME       = /\b(?:insurance|assurance|indemnity|casualty|mutual|underwriters?|syndicate|lloyd'?s|reciprocal|exchange|company|companies|corp\.?|corporation|inc\.?|ltd\.?|llc|group|risk\s+retention)\b/i;

/** { policyNumber, carrier, producer } as the declarations state them, each
 *  with the label it was read under. A value nothing labels is not returned. */
function spReadPolicyIdentity(text) {
  const out = { policyNumber: null, carrier: null, producer: null };
  for (const pair of spReadLabelledPairs(text)) {
    const label = pair.label.replace(/\s+/g, ' ').trim();
    const value = pair.value.replace(/\s+/g, ' ').trim();

    // The policy's own number, never the one it replaces. "PRIOR POLICY
    // NUMBER: NEW" is a true statement about a different policy.
    if (!out.policyNumber && _SP_POLICY_LABEL.test(label.replace(/[:\s]+$/, ''))
        && !_SP_PRIOR_LABEL.test(label)
        && /\d/.test(value) && !_SP_NOT_A_NUMBER.test(value)
        && /^[A-Za-z0-9][A-Za-z0-9 .\-\/]{3,28}$/.test(value)) {
      out.policyNumber = { value, label };
    }
    // Producer is tested FIRST: "PRODUCER NAME" would otherwise satisfy a
    // carrier pattern looking for a company, and the broker would be named as
    // the risk-bearer.
    if (_SP_PRODUCER_LABEL.test(label)) {
      // "Producer Code: 42BCC" is a producer field but not a producer's name,
      // and a producer suppresses a carrier — so a code must not stand in for
      // one. A name has letters and either a space or a company word.
      const named = /[A-Za-z]{3}/.test(value) && (/\s/.test(value) || _SP_ORG_NAME.test(value));
      if (!out.producer && named) out.producer = { value, label };
      continue;
    }
    /* A SUBSCRIPTION PLACEMENT HAS NO SINGLE CARRIER, AND SAYS SO.
     *
     * Tulsa Honor Academy's property is a CoverCap/Amwins placement whose
     * declarations state:
     *
     *     CARRIER          Various as per Carrier Participation Page
     *
     * Naming one participant is a real answer to a question the policy
     * declined to answer that way. The report named Chubb, which does
     * participate — Chubb Syndicate 2488 under agreement B1306C503382500 —
     * but so do others, and a broker reading one name would reconcile the
     * wrong balance sheet. The policy's own words are the better answer, and
     * they point at the page that holds the list.
     */
    if (!out.carrier && label.length <= 40 && _SP_CARRIER_LABEL.test(label)
        && _SP_SUBSCRIPTION.test(value) && value.length <= 70) {
      out.carrier = { value, label, subscription: true };
      continue;
    }
    // The value has to name an organisation. Policy paper says "insurance
    // company" and "carrier" constantly, in prose, and a label matched inside
    // a sentence produced "Surplus lines contracts are not subject to" and
    // "Incorporated under the laws of Indiana, a" as carrier names.
    if (!out.carrier && label.length <= 40 && _SP_CARRIER_LABEL.test(label)
        && _SP_ORG_NAME.test(value) && value.length <= 70
        && !/[:;]/.test(value) && !_SP_NOT_A_NUMBER.test(value)) {
      // A printed value can wrap mid-phrase: the Cyber policy's INSURER field
      // reads "Travelers Excess and Surplus Lines Company (Non-Admitted, AM
      // Best" and the rest is on the next line. An unclosed bracket is where
      // the name stopped being a name.
      const cut = value.indexOf('(');
      out.carrier = { value: (cut > 3 && value.indexOf(')') < 0) ? value.slice(0, cut).trim() : value, label };
    }
  }
  /* THE CARRIER ON THE PAGE THAT STATES THE POLICY NUMBER.
   *
   * Two policies of a real four-policy program label nothing. One puts its
   * carrier in the letterhead — "# NORTHGATE MUTUAL INSURANCE COMPANY" — and
   * another draws the identity block as a table row with the labels dropped
   * entirely:
   *
   *     | Northgate Mutual Insurance Company<br/>1 Example Road
   *       … | Brightline Brokers<br/>… |
   *
   * Carrier first, producer second, by position — and reading position is what
   * produced every wrong figure this week, so it is not read here.
   *
   * What can be relied on is the page. A declarations page states the policy
   * number and names the company issuing it, and nothing else of substance.
   * So: find the page carrying the policy number, and take the organisation
   * named on it — but only when exactly ONE is named. That package policy's
   * declarations page names its issuing company and nothing else, which is
   * right; the auto policy's names two, so this says nothing and the labelled
   * value found above stands.
   */
  if (!out.carrier && out.policyNumber) {
    const pages = String(text || '').split(/\n\s*---\s*\n/);
    const page = pages.find(p => p.indexOf(out.policyNumber.value) >= 0);
    if (page) {
      const orgs = new Set();
      /* A company name is a run of CAPITALISED words. Matching any letters
       * case-insensitively, capped at 50 characters, read Texas Mutual's cover
       * letter — "Thank you for placing your account with Texas Mutual
       * Insurance Company" — from mid-word, and the WC row named "Ank You For
       * Placing Your Account With Texas Mutual Insurance Company" as carrier. */
      const re = /\b((?:[A-Z][A-Za-z&.'\-]*\s+){1,6}?)(Insurance\s+Company|Insurance\s+Co\.?|Indemnity\s+Company|Assurance\s+Company|INSURANCE\s+COMPANY|INSURANCE\s+CO\.?|INDEMNITY\s+COMPANY|ASSURANCE\s+COMPANY)\b/g;
      let m;
      while ((m = re.exec(page)) !== null) {
        const nm = m[0].replace(/\s+/g, ' ').replace(/^[^A-Za-z]+/, '').replace(/^(?:The|This|Your|Our|Dear|With|From)\s+/i, '').trim();
        if (nm.length >= 8 && nm.length <= 70) orgs.add(nm.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()));
        if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
      }
      if (orgs.size === 1) out.carrier = { value: [...orgs][0], label: 'declarations page' };
    }
  }

  // One name cannot be both. When the pairing is ambiguous enough to produce
  // the same string twice, the producer label is the more specific statement,
  // and naming the broker as the risk-bearer is the worse error.
  if (out.carrier && out.producer && out.carrier.value === out.producer.value) out.carrier = null;
  return out;
}

if (typeof window !== 'undefined') {
  window.spReadLabelledPairs = spReadLabelledPairs;
  window.spReadPolicyIdentity = spReadPolicyIdentity;
}



function extractCarrierPerCoverage(pdfTexts, files) {
  const carrierMap = {};
  const docCarriers = new Map();   // fileName -> the carrier detected on that document
  if (!pdfTexts || pdfTexts.length === 0) return carrierMap;
  
  // Known carrier patterns — ordered by specificity
  const carrierPatterns = [
    // Education / charter-school / nonprofit specialty carriers & MGAs (check FIRST for specificity)
    [/united\s+educators|\bue\s+insurance|ue\s+risk/i, 'United Educators'],
    [/wright\s+specialty|wright\s+risk/i, 'Wright Specialty'],
    [/glatfelter/i, 'Glatfelter'],
    [/guideone|guide\s+one/i, 'GuideOne'],
    [/brotherhood\s+mutual/i, 'Brotherhood Mutual'],
    [/nonprofits?\s+insurance\s+alliance|nia\s+group|nonprofit\s+risk/i, 'Nonprofits Insurance Alliance'],
    [/charity\s+first/i, 'Charity First'],
    [/gallagher\s+charter|charter\s+school\s+risk|charter\s+school\s+insurance/i, 'Charter School Risk Solutions'],
    [/praesidium|safechurch|safe\s+church/i, 'Praesidium'],
    [/markel\s+specialty|markel\s+american|evanston\s+insurance/i, 'Markel Specialty'],
    [/philadelphia\s+indemnity|phly|phil\.\s*indemnity/i, 'Philadelphia Indemnity (PHLY)'],
    // Main commercial carriers
    // v2.20.8: Beazley listed BEFORE Hartford so Beazley policies based in Hartford CT
    // (which mention "Hartford" in their address block) don't get misidentified.
    [/beazley/i, 'Beazley'],
    [/travelers|the\s+travelers/i, 'Travelers'],
    // v2.20.8: Tightened Hartford regex — requires carrier-specific phrasing, not just
    // the word "Hartford" (which appears in addresses for Beazley, Aetna, Travelers, etc.
    // all of which are headquartered in or near Hartford, Connecticut).
    [/the\s+hartford\s+(?:financial|insurance|fire|casualty|group|companies)|hartford\s+fire\s+insurance|hartford\s+casualty|hartford\s+underwriters|hartford\s+life|\btwin\s+city\s+fire\s+insurance\b|trumbull\s+insurance/i, 'The Hartford'],
    [/chubb|ace\s+american|westchester\s+(fire|surplus)|federal\s+insurance\s+company|vigilant\s+insurance|great\s+northern\s+insurance|pacific\s+indemnity/i, 'Chubb'],
    [/zurich|zurich\s+american|american\s+zurich/i, 'Zurich'],
    [/liberty\s+mutual|liberty\s+insurance|ohio\s+security/i, 'Liberty Mutual'],
    [/nationwide|allied\s+property/i, 'Nationwide'],
    [/state\s+farm/i, 'State Farm'],
    [/usli|u\.?s\.?\s*liability/i, 'USLI'],
    [/markel\s+insurance|\bmarkel\b(?!\s+specialty)/i, 'Markel'],
    [/great\s+american|gaic/i, 'Great American'],
    [/hanover|citizens\s+insurance\s+company\s+of\s+america/i, 'The Hanover'],
    [/amtrust|am\s*trust|wesco\s+insurance|technology\s+insurance\s+company/i, 'AmTrust'],
    [/berkshire\s+hathaway|bhhc|guard\s+insurance/i, 'Berkshire Hathaway'],
    [/tokio\s+marine|first\s+insurance\s+company\s+of\s+hawaii/i, 'Tokio Marine'],
    [/sompo|endurance\s+american/i, 'Sompo International'],
    [/axis\s+(insurance|surplus|specialty)/i, 'AXIS Capital'],
    [/allied\s+world/i, 'Allied World'],
    [/ironshore/i, 'Ironshore'],
    [/hiscox/i, 'Hiscox'],
    [/employers\s+mutual|emcasco|emc\s+insurance/i, 'EMC Insurance'],
    [/society\s+insurance/i, 'Society Insurance'],
    [/acuity/i, 'Acuity'],
    [/erie\s+insurance/i, 'Erie Insurance'],
    [/auto[- ]?owners/i, 'Auto-Owners'],
    [/westfield/i, 'Westfield'],
    [/cincinnati\s+(insurance|financial|indemnity)/i, 'Cincinnati Insurance'],
    [/church\s+mutual/i, 'Church Mutual'],
    [/selective\s+insurance/i, 'Selective Insurance'],
    [/berkley(?:\s+(risk|insurance|public\s+entity|professional|national))?|w\.?r\.?\s*berkley/i, 'W.R. Berkley'],
    [/sentry\s+insurance/i, 'Sentry Insurance'],
    [/frankenmuth/i, 'Frankenmuth Insurance'],
    [/merchants\s+insurance/i, 'Merchants Insurance'],
    [/federated\s+mutual/i, 'Federated Mutual'],
    [/employers\s+holdings|employers\s+insurance/i, 'Employers Holdings'],
    // Surplus lines & specialty
    [/lloyd'?s|underwriters\s+at\s+lloyd/i, "Lloyd's of London"],
    [/scottsdale\s+(insurance|indemnity)/i, 'Scottsdale (Nationwide)'],
    [/starr\s+(indemnity|surplus|specialty)/i, 'Starr Companies'],
    [/rli\s+insurance|rli\s+corp/i, 'RLI'],
    [/allianz/i, 'Allianz'],
    [/aig|american\s+international|lexington\s+insurance|national\s+union\s+fire|new\s+hampshire\s+insurance/i, 'AIG'],
    [/\bcna\b|continental\s+casualty|columbia\s+casualty|american\s+casualty\s+company\s+of\s+reading/i, 'CNA'],
    [/afm|affiliated\s+fm|fm\s+global/i, 'FM Global / AFM'],
    [/xl\s+catlin|xl\s+specialty|indian\s+harbor|greenwich\s+insurance/i, 'AXA XL'],
    [/everest\s+(indemnity|national|re)/i, 'Everest'],
    [/argo\s+group|argonaut/i, 'Argo Group'],
    [/beacon\s+hill|kinsale/i, 'Kinsale'],
    [/crum\s*&?\s*forster/i, 'Crum & Forster'],
    [/old\s+republic/i, 'Old Republic'],
    [/navigators/i, 'Navigators (Hartford)'],
    [/one\s*beacon|intact\s+insurance/i, 'Intact'],
    [/arch\s+(insurance|specialty)/i, 'Arch Insurance'],
    [/atain\s+specialty/i, 'Atain Specialty'],
    // Health carriers (EB)
    [/florida\s+blue|bcbs.*florida/i, 'Florida Blue (BCBS)'],
    [/anthem/i, 'Anthem'],
    [/cigna/i, 'Cigna'],
    [/aetna/i, 'Aetna'],
    [/united\s*health/i, 'UnitedHealthcare'],
    [/kaiser/i, 'Kaiser'],
    [/humana/i, 'Humana'],
    [/blue\s+cross|blue\s+shield|bcbs/i, 'BCBS'],
    // Generic fallback — tries to catch "XYZ Insurance Company" named in declarations
    // This must be LAST — only fires if nothing above matched
    [/\b([A-Z][A-Za-z&.\-]+(?:\s+[A-Z][A-Za-z&.\-]+){0,3})\s+Insurance\s+Company\b/, null], // generic — resolved below
  ];
  
  // Map coverage type keywords to the coverage key used in declLimits/benchmarks
  const covKeywords = {
    // v2.20.11: Tightened occurrence/aggregate — must be on same line, no dotstar.
    'General Liability': ['general liability','commercial general','\\bcgl\\b','each\\s+occurrence[^\\n]{0,80}general\\s+aggregate'],
    'Commercial Property': ['commercial property','property coverage','building.*bpp','property policy'],
    'Commercial Auto': ['commercial auto','business auto','automobile','auto liability','hired.*non-owned'],
    'Workers Compensation': ['workers comp','workers\' comp','wc/el','employers liability','workers compensation'],
    'Umbrella/Excess': ['umbrella','excess liability','follow form excess','commercial umbrella'],
    'EPLI': ['employment practices','epli','wrongful termination','employment-related'],
    'Cyber Liability': ['cyber','network security','data breach','privacy liability'],
    // v2.20.11: Tightened directors/officers pattern same as E&O — prevent dotstar over-matching.
    'D&O': ['directors\\s+(?:and|&)\\s+officers','\\bd&o\\b','management liability','entity coverage','side a'],
    // v2.20.11: Tightened E&O pattern. Old `errors.*omissions` matched "errors or omissions"
    // anywhere in any policy (including cyber exclusion clauses), so a Cyber-only policy got
    // tagged as having Professional Liability and the cyber carrier (Beazley) appeared on the
    // Professional Liability row in Section 5.
    'Professional Liability': ['professional liability','errors\\s+(?:and|&|/)\\s+omissions','\\be&o\\b','malpractice'],
    // v2.20.11: Tightened abuse/molestation pattern.
    'Sexual Abuse/Molestation': ['sexual abuse','molestation','sam coverage','abuse\\s+(?:or|and|&)\\s+molestation','abusive conduct'],
    'Equipment Breakdown': ['equipment breakdown','boiler.*machinery','mechanical breakdown'],
    'Flood Insurance': ['flood','nfip','excess flood'],
    'Crime/Fidelity': ['crime','fidelity','employee theft','employee dishonesty','forgery','computer fraud'],
    'Active Shooter': ['active shooter','active assailant','violent event','workplace violence'],
    'Educators Legal Liability': ['educators legal','ell','educators liability','school board liability'],
  };
  
  // v2.18: For each PDF document, detect carrier and map to coverage types found in that document
  // Changes: search the full document (not just first 5000 chars), support generic fallback,
  // and allow a document's carrier to override an empty map entry from a later doc without the carrier.
  pdfTexts.forEach((doc, docIdx) => {
    const allText = (doc.pages || []).map(p => p.text || '').join(' ');
    const fileName = (files && files[docIdx]) ? files[docIdx].name : (doc.fileName || '');
    // v2.18: Search the FULL document text, not just first 5000 chars
    // Charter school, public entity, and specialty policies often name carrier deep in declarations
    const searchText = fileName + ' ' + allText;
    
    /* THE BROKER IS NOT THE CARRIER.
     *
     * Tulsa Honor Academy's auto policy was attributed to Wright Specialty,
     * which matches a carrier pattern and appears twice in the file — as the
     * PRODUCER, in the column beside the company that carries the risk:
     *
     *     COMPANY NAME                            PRODUCER NAME
     *     American Family Home Insurance Company  Wright Specialty Insurance
     *
     * The patterns below search the whole document for a name, so a producer,
     * an MGA or a wholesaler beats the issuing company whenever it sits higher
     * in the list. The declarations already say which is which.
     */
    let docCarrier = null;
    let producerName = null;
    if (typeof spReadPolicyIdentity === 'function') {
      /* THE RAW TEXT, NOT THE NORMALISED ONE.
       *
       * spNorm flattens markdown tables into printed rows, which is what every
       * LIMIT reader wants and the opposite of what this one needs: the
       * identity block is a table cell stacking a label and its value with
       * <br/>, and normalising dissolves exactly the structure that says which
       * is which. Reading the normalised text returned no carrier on any of
       * four policies, the keyword fallback ran, and a subscription property
       * placement was attributed to a syndicate and an auto policy to its
       * broker.
       *
       * Raw first, normalised second — a pdftotext-style extraction has no
       * markup to lose and reads the same either way. */
      let ident = spReadPolicyIdentity(allText);
      if (!ident.carrier && !ident.producer) ident = spReadPolicyIdentity(spNorm(allText));
      if (ident.producer) producerName = ident.producer.value;
      if (ident.carrier) docCarrier = ident.carrier.value;
    }
    /* A CARRIER NAME IS A PROPER NOUN NEXT TO A COMPANY WORD.
     *
     * Tulsa Honor Academy's property and D&O rows were attributed to
     * Travelers. The word appears exactly once in the four policies:
     *
     *     ... means currency, coins, bank notes and bullion; and travelers
     *     checks, register checks and money orders ...
     *
     * the definition of Money in the property form. The patterns below search
     * the whole document, so any common noun that happens to be a carrier's
     * name identifies the carrier — the same mistake that once read the
     * conjunction "or" as Oregon.
     *
     * Two things are true of a real carrier mention and false of this one: it
     * is capitalised, and a company word sits beside it.
     */
    const inCarrierPosition = (m) => {
      const hit = m[0];
      if (!/[A-Z]/.test(hit)) return false;          // "travelers checks"
      const a = Math.max(0, m.index - 70), b = Math.min(searchText.length, m.index + hit.length + 70);
      return /\b(?:insurance|assurance|indemnity|casualty|underwriters?|syndicate|mutual|company|companies|group|specialty|lloyd'?s)\b/i
        .test(searchText.slice(a, b));
    };
    for (const [pattern, name] of (docCarrier ? [] : carrierPatterns)) {
      const m = pattern.exec(searchText);
      if (m && !inCarrierPosition(m)) continue;
      if (m) {
        if (name) {
          docCarrier = name;
        } else if (m[1]) {
          // Generic fallback — captured "XYZ Insurance Company" from declarations
          // Clean up: trim, title-case preservation, skip false positives
          const candidate = m[1].trim();
          const badWords = /^(The|This|That|Such|Any|All|Said|No|Policy|Form|Coverage|Section|Named|Insured)$/i;
          if (candidate.length >= 3 && candidate.length <= 60 && !badWords.test(candidate)) {
            docCarrier = candidate + ' Insurance Company';
          }
        }
        if (docCarrier && producerName && pattern.test(producerName)
            && !pattern.test(String(allText).replace(new RegExp(producerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' '))) {
          // Every occurrence of this pattern in the document IS the producer.
          docCarrier = null;
          continue;
        }
        if (docCarrier) break;
      }
    }
    
    // Detect which coverage types are in this document (search full text, not just first 10000)
    const fnLower = fileName.toLowerCase();
    const textLower = allText.toLowerCase();
    for (const [covType, keywords] of Object.entries(covKeywords)) {
      for (const kw of keywords) {
        const kwRe = new RegExp(kw, 'i');
        if (kwRe.test(fnLower) || kwRe.test(textLower)) {
          // v2.18: Allow a later doc to fill in a carrier if the map entry is still empty,
          // and prefer a specific detection over a generic fallback
          if (docCarrier && !carrierMap[covType]) {
            carrierMap[covType] = docCarrier;
          }
          break;
        }
      }
    }
    if (docCarrier) docCarriers.set(fileName, docCarrier);
  });

  /* The same chooser the parse ledger uses, for the same reason.
   *
   * The loop above binds a coverage to a carrier by a keyword hit on the whole
   * text OR the file name, first document wins. That is the weak signal again:
   * "property" is in every commercial form, so Commercial Property bound to
   * whichever file was uploaded first and the row's header disagreed with its
   * own evidence block.
   *
   * Only a CONFIDENT pick overrides — the detector agreeing, or a declarations
   * page naming it. A bare mention is exactly what produced the wrong answer,
   * so it is not allowed to produce a different wrong answer here.
   */
  if (typeof spDocumentForCoverage === 'function') {
    for (const covType of Object.keys(carrierMap)) {
      const pick = spDocumentForCoverage(pdfTexts, covType);
      if (!pick || pick.score < 4) continue;
      const better = docCarriers.get(pick.doc.fileName);
      if (better && better !== carrierMap[covType]) {
        carrierMap[covType] = better;
      }
    }
  }

  return carrierMap;
}

// v2.20.12: Extract policy term dates from each uploaded PDF.
// Returns an object: { byFile: [{fileName, effectiveDate, expirationDate, page}], allMatch, programRenewalDate, mismatches }
function extractPolicyTerms(pdfTexts) {
  const result = { byFile: [], allMatch: true, programRenewalDate: null, mismatches: [] };
  if (!pdfTexts || pdfTexts.length === 0) return result;
  // Read the same normalised shape as every other extractor. GuideOne's
  // declarations put the label and the dates on separate lines with markdown
  // decoration between them, so the Package and Auto policies were absent
  // from the program renewal table while Cyber — which prints both on one
  // line — appeared. A renewal table that silently omits two of six policies
  // is worse than no table: it reads as "these are all the policies".
  const _norm = (t) => ((typeof window !== 'undefined' && window.spRetroAnchor)
    ? window.spRetroAnchor.spTableLines(t) : String(t || ''));

  // Patterns we expect to see on dec pages.
  // Common formats:
  //   "Policy Period: 08/24/2025 - 08/24/2026"
  //   "Policy Period: From 08/24/2025 To 08/24/2026"
  //   "Effective Date: 08/24/2025  Expiration Date: 08/24/2026"
  //   "From: 24-Aug-2025 To: 24-Aug-2026"
  // We capture both dates and treat the second as the renewal date.
  const datePat = '(?:\\d{1,2}[/\\-]\\d{1,2}[/\\-]\\d{2,4}|\\d{1,2}[\\-\\s][A-Za-z]{3,9}[\\-\\s]\\d{2,4}|[A-Za-z]{3,9}\\s+\\d{1,2},?\\s*\\d{2,4})';
  const patterns = [
    new RegExp('policy\\s+period\\s*:?\\s*from\\s*(' + datePat + ')\\s*to\\s*(' + datePat + ')', 'i'),
    new RegExp('policy\\s+period\\s*:?\\s*(' + datePat + ')\\s*(?:[\\-\\u2013\\u2014]|to)\\s*(' + datePat + ')', 'i'),
    new RegExp('from\\s*:?\\s*(' + datePat + ')\\s*to\\s*:?\\s*(' + datePat + ')', 'i'),
    new RegExp('effective\\s+date\\s*:?\\s*(' + datePat + ')[\\s\\S]{0,120}expiration\\s+date\\s*:?\\s*(' + datePat + ')', 'i')
  ];

  function normalizeDate(raw) {
    if (!raw) return null;
    const s = raw.trim();
    // Try Date.parse for various formats. Returns ISO date string YYYY-MM-DD.
    let d = new Date(s);
    if (isNaN(d.getTime())) {
      // Try common alt formats: DD-Mon-YYYY
      const m = s.match(/^(\d{1,2})[\-\s]([A-Za-z]{3,9})[\-\s](\d{2,4})$/);
      if (m) {
        d = new Date(m[2] + ' ' + m[1] + ', ' + (m[3].length === 2 ? '20' + m[3] : m[3]));
      }
    }
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    if (yyyy < 1990 || yyyy > 2050) return null;
    return d.toISOString().slice(0, 10);
  }

  function findTermsInPage(pageText) {
    if (!pageText) return null;
    for (const pat of patterns) {
      const m = pat.exec(pageText);
      if (m) {
        const eff = normalizeDate(m[1]);
        const exp = normalizeDate(m[2]);
        if (eff && exp && eff !== exp) {
          return { effectiveDate: eff, expirationDate: exp };
        }
      }
    }
    return null;
  }

  pdfTexts.forEach(f => {
    if (!f.pages || !Array.isArray(f.pages)) return;
    // Search the first 30 pages — dec pages live near the front.
    let found = null;
    let pageNum = null;
    const limit = Math.min(f.pages.length, 30);
    for (let i = 0; i < limit; i++) {
      const t = _norm((f.pages[i] && f.pages[i].text) || '');
      const r = findTermsInPage(t);
      if (r) { found = r; pageNum = (f.pages[i].page || (i + 1)); break; }
    }
    if (found) {
      result.byFile.push({
        fileName: f.fileName || 'unknown',
        effectiveDate: found.effectiveDate,
        expirationDate: found.expirationDate,
        page: pageNum
      });
    }
  });

  // Determine program-wide renewal date and flag mismatches.
  if (result.byFile.length > 0) {
    const expDateCounts = {};
    result.byFile.forEach(f => {
      expDateCounts[f.expirationDate] = (expDateCounts[f.expirationDate] || 0) + 1;
    });
    // Program renewal date = most common expiration
    const sorted = Object.entries(expDateCounts).sort((a, b) => b[1] - a[1]);
    result.programRenewalDate = sorted[0][0];
    // Files whose expiration differs from the program-wide one are mismatches
    result.byFile.forEach(f => {
      if (f.expirationDate !== result.programRenewalDate) {
        result.mismatches.push(f);
        result.allMatch = false;
      }
    });
  }

  if (typeof console !== 'undefined' && console.log) {
    console.log('[v2.20.12 PolicyTerms]', JSON.stringify(result));
  }
  return result;
}

// ============================================================================
// v2.21: ENDORSEMENT INTELLIGENCE
// ============================================================================
//
// Walks every page across every uploaded PDF and detects endorsement pages by
// telltale phrasing. For each endorsement found, extracts:
//   - title       : human-readable endorsement name
//   - formNumber  : e.g. "PE GL 625 TX", "CG 21 46", "CA 23 84"
//   - fileName    : source PDF
//   - page        : page number for citation
//   - tag         : classification (GRANT / SUBLIMIT / CARVE-BACK / EXCLUSION_ADD /
//                   AMENDATORY / NOTICE / ADDITIONAL_INSURED / WAIVER / OTHER)
//   - coveragePart: which coverage part it modifies (General Liability, D&O, etc.)
//   - impact      : short human description of what it does
//   - severity    : INFO / NOTICE / WARN / CRITICAL
//   - text        : raw extracted text (truncated for memory)
//   - _retroDate  : v2.21 hook — reserved slot for retro-date logic landing in v2.23
//
// ========================================================================
// v2.23 — Trigger & Retro Date Analyzer (per broker direction May 6, 2026)
// ========================================================================
// Universal rule: every liability policy needs explicit trigger language
// ("Claims-Made" OR "Occurrence"). If neither is found, that's a flag.
//
// For Claims-Made policies:
//   - Retro Date is required (renewals)
//   - "Full Prior Acts" is best — essentially unlimited retro
//   - New business in Year 1 → no retro is acceptable, do NOT flag
//   - ERP / Tail option must be present → flag if absent
//   - Across all claims-made policies for this insured, retro dates SHOULD align
//
// For Occurrence policies:
//   - No retro check; presence of the word "Occurrence" is the signal
//
// SentriPol reports findings; broker decides what's adequate (e.g., 5-10 yrs back).
//
// THE DECLARATIONS ARE THE ANSWER, WHEN THERE ARE ANY.
//
// analyzeTriggers() reads the coverage FORMS: it looks for a coverage-part
// heading and then weighs the trigger words around it. That works on a policy
// whose forms carry headings, and finds nothing on one whose declarations
// state the trigger in a table — which is how the Base Power GL states both of
// its parts:
//
//     Coverage Parts Attached (indicated with "X")     Policy Type
//     X  Commercial General Liability                  Per Occurrence
//     X  Contractors Pollution Liability               Per Occurrence
//
//     Deductible or Self Insured Retention (SIR):
//     Coverage                         Amount   Type                        Retroactive Date
//     Commercial General Liability     $10,000  Per Occurrence Deductible   NOT APPLICABLE
//     Contractors Pollution Liability  $10,000  Per Pollution Condition     NOT APPLICABLE
//
// Read from the forms alone that policy produced "General Liability — trigger
// NOT STATED" (cited to the Common Policy Conditions page) and "Pollution —
// Claims-Made, retro 11/03/2025". The declarations say Per Occurrence for
// both, and say the retroactive date is NOT APPLICABLE. A stated trigger
// reported as unstated, and a retro date the policy explicitly disclaims, are
// worse than a blank section: a broker would quote them.
//
// So the declarations are parsed first and win. NOT APPLICABLE is carried
// through as the explicit negative it is, rather than sending the scanner off
// to find a date somewhere else in the document.
// Keep an ERP's TERM only for the coverage whose form states it.
//
// The package's best-scoring ERP is "Automatic Extended Reporting Period of 30
// days" inside GCG 78 01, the Cyber Suite Coverage Endorsement. Unbound, it
// printed "ERP 30 days" against General Liability — a coverage that
// endorsement does not touch, on a row whose trigger is Occurrence and which
// has no tail question at all.
//
// This lived only in analyzeTriggers, and the declarations path wins over
// analyzeTriggers, so the row a reader actually sees was the unbound one.
// Both paths now bind through here.
function spBindErp(erp, schedule, partLabel) {
  if (!erp) return null;
  if (!erp.form) return erp;
  const owner = (schedule || []).find(f => f.form === erp.form);
  if (!owner || !owner.coverage) return erp;
  if (spSameCoverage(owner.coverage, partLabel)) return erp;
  return { label: null, stated: true, months: null, duration: null,
           premiumPct: null, unbound: true, form: erp.form };
}
if (typeof window !== 'undefined') window.spBindErp = spBindErp;

// The identity of one policy document.
//
// Section 7 is scoped to ONE policy at a time, so it needs a stable handle for
// "this policy". The backend's canonical_id is authoritative where it is
// present; the file name is the fallback, normalised so the same upload does
// not read as two policies because of case or a copy suffix.
function spPolicyCanonicalId(doc) {
  if (!doc) return '';
  const given = doc.canonical_id || doc.canonicalId || null;
  if (given) return String(given);
  return String(doc.fileName || doc.name || 'uploaded document')
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .replace(/\s*\(\d+\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
if (typeof window !== 'undefined') window.spPolicyCanonicalId = spPolicyCanonicalId;

// Which coverage part does an endorsement actually modify?
//
// The alert catalogue labels each exclusion with the coverage it USUALLY
// attaches to, and that label is printed as fact. On Aspen View the
// abuse/molestation alert rendered as
//
//     General Liability  vs.  Abuse/Molestation Exclusion   VERIFY
//     [Auto Policy.pdf ...]
//
// The exclusion is real — GCA 42 09 is attached — but it is an AUTO
// endorsement, and it says so itself in the preamble this engine already
// learned to read:
//
//     This endorsement modifies insurance provided under the following:
//        BUSINESS AUTO COVERAGE PART
//     A. The following exclusion is added to Paragraph B. Exclusions of
//        Section II - Covered Autos Liability Coverages...
//
// The package policy that carries the General Liability has NO abuse or
// molestation exclusion anywhere in it. So the alert named the wrong coverage,
// cited a different document to prove it, and told a broker to go and verify
// GL wording that does not exist — while the genuine finding, that the SCHOOL
// BUS liability is abuse-excluded with no duty to defend, went unsaid.
// The scope is declared in the endorsement BODY, and an alert often grounds on
// the forms schedule instead — a row of form titles, which proves the form is
// attached but carries no wording and no scope. So when the hit has no
// preamble above it, find the endorsement in the document whose operative text
// is about this exclusion, and read the scope it declares there.
function spExclusionScopeInDoc(text, terms) {
  const src = String(text || '');
  const re = new RegExp(_SP_ENDT_SCOPE.source, 'gi');
  const words = (terms || []).filter(Boolean).map(t => String(t).toLowerCase());
  if (!words.length) return [];
  let m;
  while ((m = re.exec(src)) !== null) {
    // The endorsement's title sits just above its preamble; its operative
    // text just below.
    const body = src.slice(Math.max(0, m.index - 400), m.index + 1800).toLowerCase();
    if (!words.some(w => body.indexOf(w) >= 0)) continue;
    const parts = spScopeListAfter(src, m.index + m[0].length);
    if (parts.length) return parts;
  }
  return [];
}

// The coverage parts listed immediately AFTER an applicability preamble.
// A TARGETED endorsement names the one or two coverage parts it changes. The
// boilerplate amendatory - Colorado civil union, terrorism, cancellation -
// names every coverage part the carrier writes, fifteen of them, identically
// on every policy. Reading the first entry of that list as "the scope of this
// exclusion" is how an abuse exclusion inside the AUTO policy came to be
// labelled General Liability: the list happens to start with the automobile
// and general liability parts.
//
// So a list long enough to be boilerplate states nothing about this
// endorsement, and the scope is left alone.
const SP_MAX_SCOPED_PARTS = 3;

function spScopeListAfter(text, from) {
  const listed = String(text || '').slice(from, from + 1200);
  const stop = listed.search(/^\s*(?:[A-Z]\.|\d+\.)\s/m);
  const zone = stop > 0 ? listed.slice(0, stop) : listed;
  const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  if (!CT) return [];
  const lines = zone.split('\n')
    .map(l => l.trim())
    .filter(l => /coverage\s+(?:part|form)\s*$|\bpolicy\s*$/i.test(l));
  // Count the LINES, not the resolved coverages: a fifteen-line ISO list is
  // boilerplate even if the vocabulary only recognises four of its entries.
  if (lines.length > SP_MAX_SCOPED_PARTS) return [];
  return lines
    .flatMap(l => CT.coveragesIn(l))
    .filter((v, i, a) => a.indexOf(v) === i);
}

function spEndorsementScopeAt(text, index) {
  const before = String(text || '').slice(Math.max(0, index - 4000), index);
  const re = new RegExp(_SP_ENDT_SCOPE.source, 'gi');
  let last = -1, m;
  while ((m = re.exec(before)) !== null) last = m.index + m[0].length;
  if (last < 0) return [];
  const listed = before.slice(last);
  // The list runs until the endorsement's first lettered or numbered clause.
  const stop = listed.search(/^\s*(?:[A-Z]\.|\d+\.)\s/m);
  const zone = stop > 0 ? listed.slice(0, stop) : listed;
  const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  if (!CT) return [];
  return zone.split('\n')
    .map(l => l.trim())
    .filter(l => /coverage\s+(?:part|form)\s*$|policy\s*$/i.test(l))
    .flatMap(l => CT.coveragesIn(l))
    .filter((v, i, a) => a.indexOf(v) === i);
}
if (typeof window !== 'undefined') {
  window.spEndorsementScopeAt = spEndorsementScopeAt;
  window.spExclusionScopeInDoc = spExclusionScopeInDoc;
}

// Do two coverage names refer to the same coverage?
//
// The first cut compared them with bare substring containment, which is both
// too loose ("Crime" sits inside "Crime / Fidelity", but so does any fragment)
// and too strict (the umbrella declarations head their section "COMMERCIAL
// LIABILITY UMBRELLA" while the report's label is "Excess / Umbrella
// Liability", and neither string contains the other).
//
// Compare content words instead: strip the filler that appears in almost every
// coverage name, then require the shorter name's words to be a subset of the
// longer's. "Directors And Officers Liability" and "Directors & Officers"
// both reduce to {directors, officers}; "COMMERCIAL LIABILITY UMBRELLA"
// reduces to {umbrella}, which is a subset of {excess, umbrella}.
const _SP_COV_FILLER = new Set(['coverage','liability','commercial','policy',
  'part','form','section','insurance','and','or','the','of','a','an']);

function spCovTokens(name) {
  return new Set(String(name || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(t => t && !_SP_COV_FILLER.has(t)));
}

function spSameCoverageOne(a, b) {
  const A = spCovTokens(a), B = spCovTokens(b);
  if (!A.size || !B.size) return false;
  const [small, large] = A.size <= B.size ? [A, B] : [B, A];
  for (const t of small) if (!large.has(t)) return false;
  return true;
}

/* A slash in a report label separates ALTERNATIVE names for one coverage,
 * not words of one name. "Professional Liability / E&O" reduces to
 * {professional, e, o} as a whole, so Hanover's "Eldercare Professional
 * Liability" — {eldercare, professional} — matched neither way round, and the
 * part's own Retroactive Date 07/20/2021 was refused to it. Each alternative
 * is also tried on its own. */
function spSameCoverage(a, b) {
  if (spSameCoverageOne(a, b)) return true;
  const alts = s => String(s || '').split('/').map(x => x.trim()).filter(Boolean);
  const A = alts(a), B = alts(b);
  if (A.length < 2 && B.length < 2) return false;
  return A.some(x => B.some(y => spSameCoverageOne(x, y)));
}

window.spDeclarationsTriggers = function(pdfTexts){
  const rows = [];
  const TRIG = /(claims[\s\-]*made(?:\s+and\s+reported)?|per\s+occurrence|occurrence)/i;
  (pdfTexts || []).forEach(doc => {
    (doc.pages || []).forEach(pg => {
      const text = spNorm(pg.text || '');
      if (!/coverage\s+parts?\s+attached|policy\s+type/i.test(text)) return;
      // The attachment table: from its header to whatever ends it.
      const startRe = /coverage\s+parts?\s+attached[^\n]*/i.exec(text);
      if (!startRe) return;
      const after = text.slice(startRe.index + startRe[0].length);
      const endRe = /limits\s+of\s+insurance|deductible|premium\s+schedule|form\s+of\s+business/i.exec(after);
      const table = endRe ? after.slice(0, endRe.index) : after.slice(0, 1200);
      // Rows, however the extractor laid them out. PDF.js frequently emits a
      // declarations table one CELL per line, so a line-based parse finds the
      // part name and its Policy Type on different lines and matches neither.
      // Splitting on the "X" that marks a part as attached survives both
      // layouts, and the line pass below catches tables that use no marker.
      const chunks = table.split(/(?:^|[\n\s])[Xx✓]\s+(?=[A-Z])/).slice(1);
      const candidates = chunks.length ? chunks : table.split('\n');
      candidates.forEach(line => {
        const l = String(line).replace(/\s+/g, ' ').trim();
        if (!l) return;
        const m = TRIG.exec(l);
        if (!m) return;
        // The part name is what precedes the Policy Type column, minus the
        // "X" that marks the row as attached.
        // Extractors leave table furniture behind — a trailing "|" from a
        // ruled table kept "Commercial General Liability |" from matching the
        // report's own label for it, so the row printed the policy's wording
        // and never mapped.
        const name = l.slice(0, m.index)
          .replace(/^[Xx✓•\-\s|]+/, '')
          .replace(/[\s|:.\-–—]+$/, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (name.length < 4 || name.length > 60) return;
        rows.push({
          fileName: doc.fileName || 'uploaded document',
          page: pg.page || 1,
          part: name,
          trigger: /claims[\s\-]*made/i.test(m[0]) ? 'Claims-Made' : 'Occurrence',
          retroDate: null,
          retroStated: false
        });
      });
      // The retro-date column, matched back to the rows by part name.
      // The retro-date column, matched back to the rows by part name. Read
      // through spRetroAnchor so a declarations table that says "Continuity
      // Date" is understood, and so the value grammar covers dd-Mon-yyyy.
      const RAd = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
      if (RAd) {
        const anchors = spUsableAnchors(text, RAd.readAttributedAnchors(text));
        rows.forEach(r => {
          if (r.fileName !== (doc.fileName || 'uploaded document')) return;
          // Block attribution first: an anchor printed under a coverage
          // heading belongs to that coverage.
          let a = anchors.find(x => spSameCoverage(x.coverage, r.part));
          if (!a) {
            // Then the COLUMN form, which block attribution cannot see. A
            // declarations table puts the label in a header row and the value
            // in each coverage's own row:
            //
            //   Coverage                      Amount  Type       Retroactive Date
            //   Commercial General Liability  $10,000 Per Occ.   NOT APPLICABLE
            //
            // There is no heading above "NOT APPLICABLE" to bind it to, so the
            // part name is matched inside the zone after the header instead.
            // Dropping this in favour of block attribution alone lost the
            // explicit NOT APPLICABLE that keeps the scanner from going off to
            // find a retro date elsewhere in the document.
            const zoneRe = /retroactive\s+date|continuity\s+date|prior\s+acts?\s+date/i.exec(text);
            if (zoneRe) {
              const zone = text.slice(zoneRe.index, zoneRe.index + 900);
              const at = zone.toLowerCase().indexOf(String(r.part).toLowerCase());
              if (at >= 0) {
                const cell = zone.slice(at + r.part.length, at + r.part.length + 160);
                const span = zoneRe[0] + ' ' + cell;
                const col = spUsableAnchors(span, RAd.readRetroAnchors(span, { window: 200 }))[0];
                if (col) a = col;
              }
            }
          }
          if (!a) return;
          r.retroDate = a.value;
          r.retroLabel = a.label;
          r.retroStated = true;
        });
      }
    });
  });

  // A POLICY-LEVEL statement, for declarations that carry no attachment table.
  // The cyber policy says "This Policy is written on a CLAIMS-MADE AND
  // REPORTED basis" and "Item 9. Retroactive Date: Full Prior Acts Coverage" —
  // unmistakable, and nowhere near a coverage-part heading, so the form-reading
  // scan only found it when the extractor happened to keep that heading
  // intact. It was missing from the browser's report for exactly that reason.
  const KIND_LABEL = { cyber: 'Cyber Liability', gl: 'General Liability',
    umbrella: 'Excess / Umbrella Liability', auto: 'Commercial Auto Liability',
    property: 'Property', wc: 'Workers Compensation' };
  (pdfTexts || []).forEach(doc => {
    const file = doc.fileName || 'uploaded document';
    if (rows.some(r => r.fileName === file)) return;          // table already read
    const kind = (typeof window !== 'undefined' && window.spClassifyPolicy) ? window.spClassifyPolicy(doc) : 'other';
    const label = KIND_LABEL[kind];
    if (!label || kind === 'wc' || kind === 'property') return;  // no liability trigger to state
    const head = (doc.pages || []).slice(0, 25);
    for (const pg of head) {
      const text = pg.text || '';
      // "...if you switch from an occurrence policy to a claims-made policy..."
      // is the consumer notice every package and umbrella policy carries. It
      // is not a statement about THIS policy, and reading it as one is how the
      // declarations path — which wins over analyzeTriggers — produced two
      // General Liability rows marked Claims-Made, one of them sourced to the
      // umbrella. The GL is CG 00 01, occurrence.
      const RAn = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
      let cm = /claims[\s\-]*made(?:\s+and\s+reported)?\s+(?:basis|policy|form|coverage)|this\s+is\s+a\s+claims[\s\-]*made/i.exec(text);
      let oc = /\bthis\s+is\s+an?\s+occurrence\s+(?:policy|form|coverage)/i.exec(text);
      if (cm && RAn && RAn.inGenericNotice(text, cm.index)) cm = null;
      if (oc && RAn && RAn.inGenericNotice(text, oc.index)) oc = null;
      if (!cm && !oc) continue;
      // The retro date is stated on its own declarations item — "Item 9.
      // Retroactive Date  Full Prior Acts Coverage" — which is often a page or
      // two away from the claims-made statement. Reading only the page that
      // carried the trigger dropped it.
      // This branch WINS over analyzeTriggers — "the declarations are the
      // answer, when there are any" — so it is the one that has to know the
      // vocabulary. Reading it with a hardcoded /retroactive date/ is how
      // Aspen View's cyber row printed "Retro Date Not Found" even after the
      // anchor module landed: the module fixed the path that loses.
      let retro = null, retroLabel = null;
      const headText = spNorm(head.map(x => x.text || '').join('\n'));
      const RAp = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
      if (RAp) {
        const anchors = spUsableAnchors(headText, RAp.readRetroAnchors(headText));
        // A monoline policy's declarations state one anchor for the whole
        // policy. More than one distinct value means the document is telling
        // us which coverage owns which, and this branch has no coverage to
        // bind them to, so it declines rather than picking one.
        const distinct = [...new Map(anchors.map(a => [a.normalized, a])).values()];
        if (distinct.length === 1) {
          const a = distinct[0];
          // Normalised for the column, label kept for the reader. Mixing
          // 01-Jul-2020 with 07/01/2014 in one column reads as two different
          // kinds of fact; the "(stated as ...)" note carries the provenance.
          retro = a.kind === 'date' ? a.normalized : a.value;
          retroLabel = a.label;
        }
      }
      let erpP = RAp ? RAp.readErp(headText) : null;
      if (erpP && RAp) {
        erpP = spBindErp(erpP, RAp.readFormsSchedule(headText), label);
      }
      // The forms schedule still outranks a prose statement, here as well.
      let trig = cm ? 'Claims-Made' : 'Occurrence';
      let trigSource = null;
      if (RAp) {
        const whole = (doc.pages || []).map(x => x.text || '').join('\n');
        const forms = RAp.readFormTriggers(whole) || [];
        const fm = forms.find(f => spSameCoverage(f.coverage, label));
        if (fm) { trig = fm.trigger; trigSource = fm.form; }
        else if (forms.length) {
          // The label on this row came from classifying the document, and the
          // document's own forms schedule does not list that coverage. On
          // Aspen View the umbrella classified as GL and produced a second
          // "General Liability — Claims-Made — 07/01/2014" row that outscored
          // the real one. Its schedule lists CU 00 01 Commercial Liability
          // Umbrella and nothing resembling a GL, which is the document
          // telling us the classifier is wrong.
          //
          // Decline rather than guess. analyzeTriggers reads the same file
          // through its coverage-part headings and produces the right row; a
          // path that wins on precedence has to earn it with evidence.
          continue;
        }
      }
      if (trig !== 'Claims-Made') { retro = null; retroLabel = null; }
      rows.push({
        fileName: file,
        page: pg.page || 1,
        part: label,
        trigger: trig,
        triggerSource: trigSource,
        retroDate: retro,
        retroLabel,
        retroStated: !!retro,
        hasERP: !!erpP,
        erp: erpP
      });
      break;
    }
  });

  return rows;
};

// A policy naming another policy is not that policy.
//
// Aspen View's Section 5C carried a "Crime / Fidelity — Claims-Made" row
// sourced to the CYBER policy. Crime is not in the cyber policy; it is CR 00
// 20 in the package. What matched was a Beazley EXCLUSION:
//
//     ...of the type or kind covered by the Insured Organization's financial
//     institution bond or commercial crime policy;
//
// An exclusion that carves out what your crime policy covers is evidence that
// crime coverage lives somewhere ELSE. Read as a coverage part present in this
// document, it invents a whole row, gives it the cyber policy's trigger and
// retro date, and cites it to a document that does not contain the coverage.
// Same family as the $250 life benefit taken off a dental table: the words
// were really there, and they were not about this.
//
// So a coverage-part heading is rejected when it sits inside language that is
// pointing AT another policy rather than declaring this one.
const _SP_XREF_LEAD = /\b(?:covered\s+(?:by|under)|insured\s+under|of\s+the\s+type\s+or\s+kind|available\s+under|payable\s+under|recoverable\s+under|provided\s+(?:by|under)\s+(?:any\s+)?other|any\s+other|under\s+(?:any|your|the\s+insured'?s?))[^.;]{0,120}$/i;
const _SP_XREF_TAIL = /^[^.;]{0,80}\b(?:bond|policy|policies|insurance)\b[^.;]{0,40}[;,]\s*(?:\d+\.|or\b|and\b)/i;

// An umbrella's Schedule of Underlying Insurance lists every policy it sits
// above. "Commercial General Liability Coverage Part" appears there because
// the GL is UNDERLYING, not because the umbrella is a GL. Read as a coverage
// part of this document it produced a second General Liability row, sourced to
// Umb Policy.pdf, carrying the umbrella's claims-made schedule and a retro
// date — and it outscored the real GL row from the package, which is CG 00 01
// occurrence with no retro date. Same scoping rule that kept the umbrella's
// own limits out of this schedule.
const _SP_UNDERLYING_HDR = /\bschedule\s+of\s+(?:controlling\s+)?underlying\s+insurance\b|\bunderlying\s+insurance\s+schedule\b/i;
const _SP_UNDERLYING_END = /\b(?:limits?\s+of\s+insurance\s+declarations|forms?\s+(?:and\s+endorsements?\s+)?schedule|coverage\s+form\s+index|in\s+witness\s+whereof)\b/i;

/* ANCHORS THAT BELONG TO NOBODY, REMOVED IN ONE PLACE.
 *
 * Two kinds, and the retro-date column in the declarations reader is as
 * exposed to them as the forms scan is — it called readAttributedAnchors
 * itself and applied neither test, so a defect fixed in one reader survived
 * in the other and the row came back with the same wrong date by a different
 * route. There is one rule and now one implementation of it.
 *
 * An excess policy carries a schedule of the CONTROLLING UNDERLYING
 * INSURANCE, and each row states that underlying policy's carrier, limits,
 * trigger and retroactive date. Those are facts about a different contract.
 * Read as this policy's own, Section 5C reported the retro date of the
 * underlying educators legal liability policy against the excess row — right
 * date, wrong policy, and the same defect the underlying schedule produced in
 * Section 6.
 *
 * The second is an UNFILLED field. The Underlying Claims-Made Coverage
 * endorsement prints "Retroactive Date:" above the instruction
 * (Enter Date Or "NONE" If No Retroactive Date Applies.) and leaves the space
 * blank. Scanning forward from the label then reaches whatever prose follows,
 * which is how an excess policy came to claim Full Prior Acts — the most
 * generous retro there is — off a field the carrier never filled in. A value
 * found beyond that instruction, or not found inside the window at all, came
 * from past the blank.
 */
function spUsableAnchors(text, anchors, rejected) {
  const src = String(text || '');
  return (anchors || []).filter(a => {
    if (spInUnderlyingSchedule(src, a.index)) return false;
    const after = src.slice(a.index, a.index + 260);
    const blank = /\(\s*enter\s+date\b[^)]{0,80}\)/i.exec(after);
    if (!blank) return true;
    const at = after.indexOf(String(a.value));
    const usable = at >= 0 && at < blank.index;
    /* A field the carrier printed and left empty is not the same as a field
     * the policy never had. Dropping it silently told the report the second
     * story. Recorded here so the row can say the schedule exists and is
     * incomplete, which is a question for the underwriter rather than a gap
     * in the cover. */
    if (!usable && rejected) rejected.push({ label: a.label || 'Retroactive Date', index: a.index });
    return usable;
  });
}

if (typeof window !== 'undefined') window.spUsableAnchors = spUsableAnchors;

/* THE PRIOR-ACTS ANCHOR: ONE VERDICT, NINE STATES, NO OVERLAP.
 *
 * Section 5C's third column is not really "the retroactive date". It is the
 * thing that decides how far back cover reaches, and a retroactive date is
 * only its most common form. Across the programs reviewed this week the same
 * question was answered six different ways:
 *
 *   GuideOne   Retroactive Date: 11/09/2025
 *   Beazley    Continuity Date: 01-Jul-2020
 *   Chubb      Pending or Prior Proceedings Date: May 1, 2005
 *   Corvus     known prior to the Inception Date ... by the Control Group
 *   Excess     See: Underlying Claims-Made Coverage Endorsement
 *   Excess     (Enter Date Or "NONE" If No Retroactive Date Applies.) — blank
 *
 * The cell that printed those was a chain of ifs that answered three
 * different questions at once — does an anchor APPLY, what does the document
 * SAY, and could we READ it — and collapsed the answers into shared labels.
 * That is how "N/A" came to mean both "occurrence form, no anchor exists" and
 * "we never determined the trigger", printed identically; and how "Retro Date
 * Not Found" came to mean both "the policy states none" and "this is a Corvus
 * form that limits prior acts by a condition instead".
 *
 * Both confusions report an unknown as a settled fact, which is the failure
 * this product cannot afford. So the three questions are separated and the
 * result is one status from a closed set. Adding a carrier means adding a
 * reader, never a branch.
 */
/* WHICH PAGE THE ANCHOR WAS READ FROM.
 *
 * The Source column cites where the COVERAGE PART matched, which is not where
 * the anchor is. On a Chubb portfolio the row cited page 1 — the declarations
 * notice that settled the trigger — while both Pending or Prior Proceedings
 * dates are printed on page 2. A broker who turns to the cited page does not
 * find the quoted fact, which is the one thing a citation exists to prevent.
 *
 * Anchor indexes are offsets into the whole file's normalised text, so they
 * cannot be turned back into a page directly. The anchor's own label and value
 * can be: find the page that carries both.
 */
function spPageOfAnchor(file, a) {
  if (!a || !file) return null;
  const label = a.label ? String(a.label) : null;
  const value = a.value ? String(a.value) : null;
  if (!label && !value) return null;
  /* Where a program renews every part on one date, two coverages carry the
   * same label AND the same value — Tulsa's educators legal liability and its
   * employee benefits liability both read "Retroactive Date 11/09/2025". The
   * first page matching both is then whichever comes first in the document,
   * and one of the two rows cites the other's declarations.
   *
   * The anchor already knows whose it is, because it was attributed to a
   * coverage block when it was read. A page naming that coverage wins; the
   * bare label-and-value match is only the fallback. */
  const cov = a.coverage ? String(a.coverage) : null;
  let fallback = null;
  for (const pg of (file.pages || [])) {
    const t = spNorm(pg.text || '');
    if (!t) continue;
    if (label && t.indexOf(label) < 0) continue;
    if (value && t.indexOf(value) < 0) continue;
    if (cov && spCoverageTermsNamesIn(t, cov)) return pg.page || null;
    if (fallback === null) fallback = pg.page || null;
  }
  return fallback;
}

// Does this page name that coverage? Case-insensitive on the words the
// attribution used, so "EDUCATORS LEGAL LIABILITY" matches the heading the
// anchor was read under however the page cases it.
function spCoverageTermsNamesIn(pageText, coverage) {
  const words = String(coverage).toLowerCase().split(/[^a-z0-9]+/).filter(x => x.length > 3);
  if (!words.length) return false;
  const t = String(pageText).toLowerCase();
  return words.every(x => t.indexOf(x) >= 0);
}

/* THE DECLARATIONS' OWN ROSTER OF COVERAGE PARTS.
 *
 * A portfolio policy says outright which parts it contains:
 *
 *   Item 4. Coverage(s) Applicable to this Policy:
 *       Directors & Officers and Entity Liability Coverage Part
 *       Employment Practices Liability Coverage Part
 *       Fiduciary Liability Coverage Part
 *       Crime Coverage Part
 *       Kidnap, Ransom & Extortion Coverage Part
 *       Employed Lawyers Liability Coverage Part
 *       Workplace Violence Expense Coverage Part
 *
 * Seven parts, stated by the insurer, in one place. Section 5C showed two of
 * them, because it inferred the roster from headings scattered through 149
 * pages and only two of those headings matched its vocabulary. The other five
 * were simply absent from the report — and an absent coverage part cannot be
 * questioned by whoever reads it.
 *
 * Reading the roster inverts that: the policy states what it contains, and
 * anything the scan cannot describe is reported as a part whose terms were
 * not read, rather than not reported at all.
 */
const _SP_ROSTER_HDR = /coverage\(?s?\)?\s+applicable\s+to\s+this\s+policy|schedule\s+of\s+coverage\s+parts\b/i;

function spDeclaredCoverageParts(file) {
  const out = [];
  for (const pg of (file && file.pages ? file.pages : []).slice(0, 15)) {
    const t = spNorm(pg.text || '');
    const m = _SP_ROSTER_HDR.exec(t);
    if (!m) continue;
    const after = t.slice(m.index + m[0].length, m.index + m[0].length + 1500);
    for (const raw of after.split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      // The list ends where the declarations move on, or the form footer runs.
      if (/^item\s+\d+\b/i.test(line) || /\bpage\s+\d{1,3}\s+of\s+\d{1,3}\b/i.test(line)) break;
      const hit = /^([A-Z][A-Za-z&,'\-. ]{3,60}?)\s+Coverage\s+Part\b/i.exec(line);
      if (hit) out.push({ name: hit[1].replace(/\s+/g, ' ').trim(), page: pg.page || 1 });
    }
    if (out.length) break;
  }
  return out;
}

if (typeof window !== 'undefined') window.spDeclaredCoverageParts = spDeclaredCoverageParts;

/* THE TRIGGER STATED ON A COVERAGE PART'S OWN PAGES.
 *
 * A portfolio prints each part's name as a running header — "The ForeFront
 * Portfolio" over "Crime" — rather than as a coverage-part heading, so the
 * block attribution that serves the declarations finds nothing to attach the
 * part's insuring clause to. The clause is unambiguous where it sits:
 *
 *   the Company agrees to pay for loss any Insured shall sustain ... which is
 *   Discovered during the Policy Period
 *
 * So the part's pages are found by its own name, which the declarations
 * roster just told us, and its terms are read there. This is the same move as
 * scoping a limit to its declarations page: read the field, not the window.
 */
function spTermsTriggerForPart(file, partName) {
  const RA = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
  if (!RA || !RA.readTermsTriggers || !partName) return null;
  const name = String(partName).trim();
  if (name.length < 4) return null;
  const re = new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'), 'i');
  const owned = [];
  for (const pg of (file && file.pages ? file.pages : [])) {
    const t = spNorm(pg.text || '');
    if (!t) continue;
    // The running header sits at the top of the page. Requiring the name
    // there keeps a passing mention in the body of another part's form from
    // claiming the page.
    if (re.test(t.slice(0, 300))) owned.push(t);
  }
  if (!owned.length) return null;
  const hits = RA.readTermsTriggers(owned.join('\n'));
  return hits.length ? hits[0] : null;
}

/* WHAT THE DOCUMENTS STATE, AGAINST WHAT THE REPORT PUBLISHES.
 *
 * The limits path is spec-driven: a figure reaches the report only when one
 * of the coverage specs names its label. Anything the spec authors did not
 * anticipate is dropped — not flagged, not logged, just gone — so a label
 * nobody thought of is structurally guaranteed to be missed, and the only way
 * anyone finds out is a broker noticing an absence.
 *
 * On one four-policy program the documents state 138 amounts on pages the
 * reader can parse and the report publishes 27 of them. Among the 111 is an
 * entire property sublimit schedule: debris removal, extra expense,
 * expediting expense, valuable papers, service interruption, accounts
 * receivable.
 *
 * This measures that gap instead of waiting to be told about it. It is not a
 * guard — nothing is refused here — it is the number that makes the leak
 * visible, so a change can be shown to shrink it and a test can stop it
 * growing.
 *
 * Categories come from the field reader's own kindOf vocabulary, so a
 * deductible, a premium, a tax and a rating basis are counted as accounted
 * for rather than as missing limits. What remains — an amount on a
 * declarations page that is neither published nor identifiable as something
 * other than cover — is the leak.
 */
/* A LIMIT THE POLICY STATES AND NO SPEC ASKED FOR.
 *
 * The limits path publishes a figure only when one of the coverage specs
 * names its label, so a label nobody anticipated is dropped in silence. On
 * one program that is an entire property sublimit schedule — debris removal,
 * extra expense, expediting expense, valuable papers, accounts receivable,
 * service interruption — none of which a broker reviewing a property
 * placement would expect to be missing.
 *
 * These are published under the POLICY'S OWN WORDING rather than mapped to a
 * canonical slot, because the whole point is that the vocabulary did not
 * anticipate them. Benchmarking still uses the mapped slots; this is the
 * secondary list beneath them.
 *
 * The guard matters more than the capture. A declarations page also carries
 * premiums, deductibles, rating bases and, in the property forms, worked
 * examples of coinsurance arithmetic — "$70,000 ÷ $80,000 = .875 Step (1)".
 * Publishing those as stated limits would be the wrong-value failure this
 * engine keeps designing out, so anything that is not typed as cover by the
 * field reader has to look like a coverage name to get through.
 */
/* WHAT A STATED LIMIT'S LABEL IS NOT.
 *
 * The first cut published, against Excess / Umbrella, the figures from the
 * Terrorism Risk Insurance Act notice — "$100,000,000 2008 through 2015",
 * rising to "$200,000,000 Beginning January 1, 2020". Those are the FEDERAL
 * PROGRAM's caps, printed identically in every policy sold in the country,
 * and presenting one as an umbrella limit would be the most damaging kind of
 * wrong figure this report can produce.
 *
 * Alongside them: "$ 0 Terrorism Premium (Certified Acts)", which the
 * start-anchored premium test missed; "$ 35 See Item Four for Hired or
 * Borrowed Autos", a cross-reference; and "The lesser of 14 days or", a
 * sentence fragment left by a wrapped cell.
 *
 * So the label must read like the NAME OF A COVERAGE: words, not a date
 * range, not a cost word anywhere in it, not a pointer to somewhere else.
 */
const _SP_NOT_A_LIMIT_LABEL = new RegExp([
  '^(?:total|sub-?total|estimated|minimum|maximum|rating basis|deposit|the lesser|the greater|see\\b|if any)',
  '\\b(?:premium|deductible|retention|surcharge|tax(?:es)?|fees?|commission|assessment)\\b',
  '\\b(?:attachment|detachment)\\s+point\\b',
  '^\\W*(?:19|20)\\d{2}\\b',                    // "2008 through 2015"
  '\\b(?:beginning|effective|through|prior to)\\s+(?:january|february|march|april|may|june|july|august|september|october|november|december|\\d)',
  '[=÷×]|\\bstep\\s*\\(?\\d|\\bper\\s*\\$\\d',
  'coverage part$'
].join('|'), 'i');

function spOtherStatedLimits(pdfTexts, published) {
  const out = new Map();   // coverage -> [{label,value,file,page}]
  if (typeof window === 'undefined' || !window.spReadFields) return out;
  const CT = window.spCoverageTerms;
  for (const doc of (pdfTexts || [])) {
    // The coverage this DOCUMENT is about, for the many schedule pages that
    // carry no heading of their own. A monoline property policy has one
    // subject and every page of it belongs to that subject.
    const kind = (typeof window.spClassifyPolicy === 'function') ? window.spClassifyPolicy(doc) : null;
    const docCoverage = { property: 'Commercial Property', cyber: 'Cyber Liability', gl: 'General Liability',
                          auto: 'Commercial Auto', umbrella: 'Umbrella/Excess' }[kind] || null;
    for (const pg of ((doc && doc.pages) || [])) {
      const text = (pg && pg.text) || '';
      if (text.indexOf('$') < 0) continue;
      for (const f of (window.spAllFields || window.spReadFields)(pg)) {
        if (!f.money) continue;
        const v = String(f.value).replace(/\s+/g, '');
        if (published.has(v)) continue;
        const typed = f.kind === 'limit' || f.kind === 'sublimit';
        const label = String(f.label || '').trim();
        // Applied to typed and untyped alike: a heading can type a whole
        // block as limits, and the terrorism notice sits under one.
        if (_SP_NOT_A_LIMIT_LABEL.test(label)) continue;
        /* A LABEL CONTAINING ITS OWN AMOUNT IS NOT A LABEL.
         *
         * A wrapped or merged cell leaves the whole row in the label —
         * "1. Accounts Receivable $250,000 PROPERTY DAMAGE COVERAGE -
         * Extensions of Coverage" — which then appears in the secondary list
         * beside the clean reading of the same figure. It is not wrong, it is
         * unreadable, and it is always a duplicate of a row that read
         * properly. */
        if (/\$\s?\d/.test(label)) continue;
        if (label.length < 4 || label.length > 60) continue;
        if (!/^[A-Za-z]/.test(label)) continue;        // a label, not a fragment
        if ((label.match(/[A-Za-z]/g) || []).length < 4) continue;
        /* ONE AMOUNT PER ENTRY, WHATEVER TYPED IT.
         *
         * A merged or wrapped cell holds a whole row, and publishing it whole
         * produced entries like "1. Accounts Receivable $250,000" and
         * "$1,000,000 each Occurrence $3,000,000 General Aggregate (other
         * than Products)" — two limits and a qualifier presented as one
         * figure. The check was applied only to untyped money, so a heading
         * that typed the block as limits exempted exactly the rows most likely
         * to be stacked. A reader cannot act on a value that is a sentence. */
        if (!/^\$[\d][\d,]*(?:\.\d{2})?$/.test(v)) continue;
        /* A PLACEMENT STRUCTURE IS NOT COVER.
         *
         * A subscription policy carries a Carrier Support Page describing how
         * the risk is layered between the subscribing insurers:
         *
         *     Carrier Designation
         *     Windstorm
         *     Attachment Point   $5,000,000   $5,000,000
         *     Detachment Point   $28,995,637  $28,995,637
         *     % Share of Risk    50.00%       50.00%
         *
         * Read as stated limits that produced three "Windstorm" entries at
         * $0, $5,000,000 and $28,995,637 — figures that are real, and are
         * about who pays which layer rather than what the insured is covered
         * for. The label guard catches the rows named Attachment Point; this
         * catches the ones that inherit the group label above them. */
        const placement = /carrier\s+(?:designation|support|participation)|share\s+of\s+risk|attachment\s+point|detachment\s+point/i;
        if (placement.test(label) || (f.scope && placement.test(f.scope))) continue;
        let cov = null;
        if (CT && f.scope) { const owners = CT.coveragesIn(f.scope); if (owners && owners.length === 1) cov = owners[0]; }
        cov = cov || docCoverage;
        if (!cov) continue;
        if (!out.has(cov)) out.set(cov, []);
        const list = out.get(cov);
        if (list.length >= 40) continue;
        if (list.some(x => x.label === label && x.value === f.value)) continue;
        list.push({ label, value: f.value, file: (doc && doc.fileName) || '?', page: (pg && pg.page) || 0 });
      }
    }
  }
  return out;
}

if (typeof window !== 'undefined') window.spOtherStatedLimits = spOtherStatedLimits;

/* DOES THIS COVERAGE ACTUALLY HAVE A LIMIT?
 *
 * The limits object carries metadata beside the figures — _coverageType,
 * _coinsurance, _valuation, _readBy, and now _other — and six places asked
 * "are there any keys?" to decide whether limits were found. A coverage whose
 * only entries were metadata therefore counted as having limits.
 *
 * On a subscription property policy that read {_coverageType, _coinsurance}
 * and nothing else, so the benchmark row reported status MEETS — the
 * strongest statement the section makes — over a row displaying no limit at
 * all, beside a message saying the declarations state no field for it. The
 * row contradicted itself and the status was the half a reader would trust.
 *
 * A published figure is a key that does not start with an underscore.
 */
function spHasRealLimits(limitsObj) {
  return !!limitsObj && typeof limitsObj === 'object'
    && Object.keys(limitsObj).some(k => k.charAt(0) !== '_');
}

if (typeof window !== 'undefined') window.spHasRealLimits = spHasRealLimits;

/* TWO PUBLISHED FIGURES THAT CANNOT BOTH BE TRUE.
 *
 * Every wrong limit this engine has shipped was a real number from the wrong
 * position, and every fix so far has been positional: a better reader, or a
 * guard against one more kind of furniture. Both require knowing the layout.
 * This does not. An aggregate is the most the policy will pay for the sum of
 * all claims; a per-occurrence limit is the most it will pay for one. An
 * aggregate BELOW the per-occurrence limit it caps is not a limit set that
 * exists -- it says one full occurrence cannot be paid.
 *
 * It costs nothing to check and it is carrier-, layout- and parser-
 * independent, which is the point: it catches a class rather than an instance.
 * Two the engine had already published when this landed:
 *
 *   - An umbrella page returned eachOccurrence $5,000,000 with
 *     generalAggregate $3,000,000, the first from the umbrella's own limits
 *     block and the second from the schedule of underlying insurance below it.
 *   - A package policy returned E&O aggregate $25,000 against eachClaim
 *     $3,000,000. The $25,000 is a conference-cancellation sublimit from an
 *     ancillary endorsement schedule, published as the aggregate of the
 *     professional liability coverage.
 *
 * BOTH figures are dropped, not the smaller one. Which of the pair is wrong is
 * exactly what the engine does not know -- inferring it would be another
 * positional guess, and a confident wrong limit is worse than a missing one.
 * The conflict is recorded on _conflicts so a row can say so rather than
 * silently showing nothing.
 */
const _SP_EACH_FIELDS = ['eachOccurrence', 'perOccurrence', 'eachClaim',
                         'eachWrongfulAct', 'eachAct', 'eachIncident'];
const _SP_AGG_FIELDS  = ['aggregate', 'generalAggregate'];

function spMoneyNumber(v) {
  const s = String(v == null ? '' : v).replace(/[^0-9.]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  return isFinite(n) ? n : null;
}

function spLimitConflicts(fields) {
  const out = [];
  if (!fields || typeof fields !== 'object') return out;
  for (const a of _SP_AGG_FIELDS) {
    const av = spMoneyNumber(fields[a]);
    if (av == null) continue;
    for (const e of _SP_EACH_FIELDS) {
      const ev = spMoneyNumber(fields[e]);
      if (ev == null) continue;
      if (av < ev) out.push({ rule: 'aggregate-below-each-occurrence',
                              fields: [a, e], values: [fields[a], fields[e]] });
    }
  }
  // Medical expense is a sublimit of the occurrence limit, never above it.
  const mp = spMoneyNumber(fields.medicalExpense);
  const eo = spMoneyNumber(fields.eachOccurrence);
  if (mp != null && eo != null && mp > eo) {
    out.push({ rule: 'medical-expense-above-each-occurrence',
               fields: ['medicalExpense', 'eachOccurrence'],
               values: [fields.medicalExpense, fields.eachOccurrence] });
  }
  return out;
}

function spApplyLimitInvariants(limits) {
  for (const fields of Object.values(limits || {})) {
    if (!fields || typeof fields !== 'object') continue;
    const conflicts = spLimitConflicts(fields);
    if (!conflicts.length) continue;
    const drop = new Set();
    conflicts.forEach(c => c.fields.forEach(f => drop.add(f)));
    drop.forEach(f => { delete fields[f]; });
    fields._conflicts = (fields._conflicts || []).concat(conflicts);
  }
  return limits;
}

if (typeof window !== 'undefined') {
  window.spLimitConflicts = spLimitConflicts;
  window.spApplyLimitInvariants = spApplyLimitInvariants;
}

function spLimitLeakage(pdfTexts) {
  const out = { total: 0, published: 0, accounted: 0, leaked: 0, byKind: {}, rows: [] };
  if (typeof window === 'undefined' || !window.spReadFields) return out;
  let limits = {};
  try { limits = extractDeclarationsLimits(pdfTexts) || {}; } catch (e) { /* measure what we can */ }
  const shown = new Set();
  for (const cov of Object.values(limits)) {
    if (!cov || typeof cov !== 'object') continue;
    for (const [k, v] of Object.entries(cov)) {
      if (k.charAt(0) === '_') continue;          // metadata, not a published figure
      shown.add(String(v).replace(/\s+/g, ''));
    }
  }
  // Anything that is not cover. A retention and a premium are stated on the
  // same page as the limits and are not missing from the report by mistake.
  const NOT_COVER = new Set(['retention', 'cost', 'date', 'attribute']);
  for (const doc of (pdfTexts || [])) {
    for (const pg of ((doc && doc.pages) || [])) {
      const text = (pg && pg.text) || '';
      if (text.indexOf('$') < 0) continue;
      for (const f of (window.spAllFields || window.spReadFields)(pg)) {
        if (!f.money) continue;
        out.total++;
        const kind = f.kind || 'text';
        out.byKind[kind] = (out.byKind[kind] || 0) + 1;
        if (shown.has(String(f.value).replace(/\s+/g, ''))) { out.published++; continue; }
        if (NOT_COVER.has(kind)) { out.accounted++; continue; }
        out.leaked++;
        out.rows.push({ file: (doc && doc.fileName) || '?', page: (pg && pg.page) || 0,
                        label: f.label, value: f.value, kind, scope: f.scope || null,
                        provenance: f.provenance || null });
      }
    }
  }
  return out;
}

if (typeof window !== 'undefined') window.spLimitLeakage = spLimitLeakage;

function spResolvePriorActsAnchor(f) {
  const t = (f && f.trigger) || null;

  // 1. Could the document be read at all? Nothing below is meaningful if not.
  if (f && f.unreadable) {
    return { status: 'unreadable',
      note: 'This document was extracted column by column, so no value can be attributed to a label. Not that the policy is silent — that it could not be read.' };
  }

  // 2. Does an anchor apply? Only the trigger answers this, and only when the
  //    trigger is known. An unread trigger makes the question open, not moot.
  if (!t) {
    /* Say whose limitation this is. The policy may well state a trigger; what
     * is true is that THIS READER did not find one, and a broker needs to
     * know the difference between "the document is silent" and "we could not
     * read it" before deciding whether to chase the carrier. */
    return { status: 'unknown-trigger',
      note: 'The reader could not determine this coverage\u2019s trigger, so it could not establish whether a retroactive date applies — this is a limitation of the parse, not a statement that the policy is silent. Confirm the trigger from the declarations; if the part is claims-made, a missing retroactive date and a missing ERP are both findings.' };
  }
  if (t === 'Discovery' || t === 'Loss Sustained') {
    return { status: 'not-applicable', reason: t.toLowerCase() + ' form',
      note: 'A ' + t.toLowerCase() + ' form responds to when a loss is discovered, so it has no retroactive date.' };
  }
  if (t !== 'Claims-Made') {
    return { status: 'not-applicable', reason: 'occurrence form',
      note: 'An occurrence form responds to when the injury or damage occurred, so there is no prior-acts cut-off and no tail to buy.' };
  }

  // 3. Claims-made: what does the document state? Most specific first.
  if (f.isFullPrior) return { status: 'full-prior-acts', label: f.retroLabel || null, page: f.retroPage || null };
  /* "Retroactive Date: N/A" beside a Prior or Pending Date: no retroactive
   * cut-off, and the pending-proceedings date is the anchor that limits
   * prior acts. Say both, in the policy's words. */
  if (f.retroNotApplicable && f.priorProceedings) {
    return { status: 'equivalent', kind: 'pending-or-prior',
      label: f.priorProceedings.label || 'Pending or Prior Proceedings Date',
      value: f.priorProceedings.value,
      page: f.priorProceedings.page || null,
      note: 'Retroactive Date stated as N/A: no retroactive cut-off. Claims arising from proceedings pending on that date are excluded; earlier wrongful acts are not.' };
  }
  if (f.retroDate)   return { status: 'stated', value: f.retroDate, label: f.retroLabel || null, page: f.retroPage || null };
  if (f.priorProceedings) {
    return { status: 'equivalent', kind: 'pending-or-prior',
      label: f.priorProceedings.label || 'Pending or Prior Proceedings Date',
      value: f.priorProceedings.value,
      page: f.priorProceedings.page || null,
      note: 'Claims arising from proceedings already pending on that date are excluded; earlier wrongful acts are not.' };
  }
  if (f.priorKnowledge) {
    return { status: 'equivalent', kind: 'prior-knowledge',
      label: f.priorKnowledge.label || 'Prior Knowledge condition',
      value: f.priorKnowledge.value,
      page: f.priorKnowledge.page || null,
      note: 'Prior acts are limited by what the Control Group knew before inception rather than by a dated cut-off. Confirm nothing was known that could give rise to a claim.' };
  }
  if (f.retroPointer) {
    return { status: 'cross-reference',
      label: f.retroPointer.label || 'Retroactive Date',
      value: f.retroPointer.pointer };
  }
  if (f.blankRetroField) {
    return { status: 'blank', label: f.blankRetroField.label || 'Retroactive Date',
      note: 'The schedule prints this field and leaves it empty. That is a question for the underwriter, not a silent gap.' };
  }
  return { status: 'absent',
    note: 'This coverage is claims-made and no prior-acts anchor of any kind was found — no retroactive date, no continuity date, no pending-or-prior proceedings date, and no prior-knowledge condition.' };
}

if (typeof window !== 'undefined') window.spResolvePriorActsAnchor = spResolvePriorActsAnchor;

function spInUnderlyingSchedule(text, index) {
  const before = String(text).slice(0, index);
  const hdr = before.search(_SP_UNDERLYING_HDR);
  if (hdr < 0) return false;
  // Last occurrence of the heading before this match.
  let last = hdr, m;
  const re = new RegExp(_SP_UNDERLYING_HDR.source, 'gi');
  while ((m = re.exec(before)) !== null) last = m.index;
  const between = before.slice(last);
  return !_SP_UNDERLYING_END.test(between);
}

/* A COVERAGE NAMED ONLY IN AN UNDERLYING SCHEDULE IS NOT THIS POLICY'S.
 *
 * An excess or umbrella policy carries a schedule of what sits beneath it, and
 * each row names a coverage, its carrier, its limits and its retro date. Those
 * are facts about a DIFFERENT contract. The declarations readers apply this
 * rule already, through spInUnderlyingSchedule; the v2.20 page scopers did
 * not, and it cost twice on real programs:
 *
 *   - An umbrella declarations page states its own Each Occurrence $5,000,000
 *     above a SCHEDULE OF UNDERLYING INSURANCE whose GL row states General
 *     Aggregate $3,000,000. The GL scoper took the page and returned
 *     eachOccurrence $5,000,000 WITH generalAggregate $3,000,000 - a pair that
 *     exists in neither policy, and one no aggregate can ever satisfy.
 *   - An excess policy's Schedule of Controlling Underlying Insurance names
 *     Educators Legal Liability. The row was sourced to the excess policy and
 *     its carrier, when the ELL declarations - carrying a Coverage B and three
 *     deductibles the schedule never mentions - are in the package policy.
 *
 * The test is deliberately ALL-or-nothing. A page is rejected only when EVERY
 * mention of the coverage sits inside a schedule. A declarations page that
 * names its own coverage and then lists it again underneath is still that
 * coverage's page, and must keep scoring.
 */
function spCoverageOnlyInUnderlyingSchedule(text, keywordRe) {
  const t = String(text || '');
  if (!_SP_UNDERLYING_HDR.test(t)) return false;
  const flags = keywordRe.flags.includes('g') ? keywordRe.flags : keywordRe.flags + 'g';
  const re = new RegExp(keywordRe.source, flags);
  let m, seen = false;
  while ((m = re.exec(t)) !== null) {
    seen = true;
    if (!spInUnderlyingSchedule(t, m.index)) return false;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return seen;
}

if (typeof window !== 'undefined') window.spCoverageOnlyInUnderlyingSchedule = spCoverageOnlyInUnderlyingSchedule;

// Nearly every ISO endorsement opens with a boilerplate list of every coverage
// part it COULD modify:
//
//     THIS ENDORSEMENT CHANGES THE POLICY. PLEASE READ IT CAREFULLY.
//     This endorsement modifies insurance provided under the following:
//        COMMERCIAL AUTOMOBILE COVERAGE PART
//        ...
//        MEDICAL PROFESSIONAL LIABILITY COVERAGE PART
//        LIQUOR LIABILITY COVERAGE PART
//        UNDERGROUND STORAGE TANK POLICY
//
// Aspen View is a charter school. It has no liquor liability, no underground
// storage tanks and no medical professional liability — that list is printed
// identically on a Colorado civil-union endorsement whoever the insured is.
// Read as coverage parts of this policy it produced a "Professional Liability
// / E&O — NOT STATED" row, which reads as a gap in a program that never had
// the coverage.
const _SP_ENDT_SCOPE = /this\s+endorsement\s+(?:modifies|changes)[^\n]{0,80}(?:provided\s+under\s+the\s+following|the\s+following)\s*:?/i;

function spInEndorsementScopeList(text, index) {
  const before = String(text).slice(0, index);
  let last = -1, m;
  const re = new RegExp(_SP_ENDT_SCOPE.source, 'gi');
  while ((m = re.exec(before)) !== null) last = m.index + m[0].length;
  if (last < 0) return false;
  // The list is a run of heading-like lines. It ends at the first line that
  // reads as prose — a lettered or numbered clause, or a sentence.
  const between = before.slice(last);
  if (between.length > 1500) return false;
  return !/^\s*(?:[A-Z]\.|\d+\.|With\s+respect|The\s+following|SCHEDULE\b)/m.test(between);
}

// The preamble wording varies, and some endorsements print the list with no
// preamble on the page at all. The LIST ITSELF is the reliable signal: a run
// of consecutive all-caps "... COVERAGE PART" lines. A real declarations page
// heads one coverage part at a time; only an applicability list stacks
// fifteen of them, and Aspen View's stack runs from COMMERCIAL INLAND MARINE
// through LIQUOR LIABILITY to RAILROAD PROTECTIVE LIABILITY.
const _SP_PART_LINE = /^[\s|]*[A-Z][A-Z&'\-\/ ]{6,70}\s+(?:COVERAGE\s+PART|POLICY)\s*$/;

function spInPartListRun(text, index) {
  const lines = String(text).slice(0, index).split('\n');
  const hereIdx = lines.length - 1;
  const all = String(text).split('\n');
  if (!_SP_PART_LINE.test(all[hereIdx] || '')) return false;
  let run = 1;
  for (let i = hereIdx - 1; i >= 0 && _SP_PART_LINE.test(all[i] || ''); i--) run++;
  for (let i = hereIdx + 1; i < all.length && _SP_PART_LINE.test(all[i] || ''); i++) run++;
  return run >= 4;
}

function spCrossReference(text, index) {
  if (spInUnderlyingSchedule(text, index)) return true;
  if (spInEndorsementScopeList(text, index)) return true;
  if (spInPartListRun(text, index)) return true;
  const lead = String(text).slice(Math.max(0, index - 160), index);
  if (_SP_XREF_LEAD.test(lead)) return true;
  // "...or commercial crime policy; 2. any actual or alleged..." — an item in
  // an exclusion list rather than a coverage-part heading.
  const tail = String(text).slice(index, index + 140);
  return _SP_XREF_TAIL.test(tail) && /\b(?:exclusion|we\s+will\s+not|does\s+not\s+(?:apply|cover)|shall\s+not\s+cover)\b/i
    .test(String(text).slice(Math.max(0, index - 900), index));
}

function analyzeTriggers(pdfTexts) {
  const out = { byCoverage: [], hasAnyClaimsMade: false, hasAnyOccurrence: false, retroDates: [], misalignmentFlag: false };
  if (!pdfTexts || pdfTexts.length === 0) return out;

  // Liability coverage parts we expect to have a defined trigger.
  // Each entry: label shown in report + regex patterns for matching coverage-part headings.
  /* A heading may print the part's trigger between its name and "Coverage
   * Part" — Hanover's "Commercial General Liability Claims-Made Coverage
   * Part". And "coverage parts", plural, is prose ABOUT parts, not a heading
   * for one: Hanover's policyholder notice says the common terms apply "to
   * both General Liability and Professional Liability coverage parts", and on
   * a whole-document page that was the first Professional Liability match, so
   * the notice's "Incident means an Occurrence" made a claims-made part read
   * Occurrence. */
  const _TRIG = '(?:[-–—]\\s*)?(?:(?:claims[\\s\\-]*made(?:\\s+and\\s+reported)?|occurrence)\\s+)?';
  const partHeading = name => new RegExp(name + '\\s+' + _TRIG + 'coverage\\s+part(?!s)', 'i');
  const liabilityParts = [
    { label: 'General Liability',          patterns: [partHeading('commercial\\s+general\\s+liability'), /\bcgl\b\s+coverage/i, partHeading('general\\s+liability')] },
    // ISO's own wording is "Commercial Liability Umbrella Coverage Part" —
    // liability BEFORE umbrella — which none of the original patterns matched.
    { label: 'Excess / Umbrella Liability', patterns: [/(commercial\s+)?excess\s+liability\s+coverage\s+part/i, /umbrella\s+liability\s+coverage\s+part/i, /commercial\s+umbrella/i, /commercial\s+liability\s+umbrella/i] },
    // "Mortgageholders Errors And Omissions Coverage Form" is a PROPERTY form
    // — it covers the mortgageholder's interest when someone forgets to file
    // paperwork. It is on every commercial property policy ever written, and
    // matching it produced a "Professional Liability / E&O — NOT STATED" row
    // on Aspen View, reading as a missing coverage the school never bought.
    { label: 'Professional Liability / E&O', patterns: [partHeading('professional\\s+liability'), /errors\s+(and|&)\s+omissions\s+coverage/i, /\be&o\b\s+coverage/i],
      anti: [/mortgage\s*holders?/i, /\bmortgagee/i] },
    { label: 'Educators Legal Liability', patterns: [/educators?\s+legal\s+liability/i, /\bell\b\s+coverage\s+part/i] },
    { label: 'Employee Benefits Liability', patterns: [/employee\s+benefits?\s+liability/i, /\bebl\b\s+coverage/i] },
    { label: 'Employment Practices Liability', patterns: [/employment\s+practices\s+liability/i, /\bepli\b\s+coverage/i] },
    /* Chubb's ForeFront wording puts the entity between the officers and the
     * liability — "Directors & Officers AND ENTITY Liability Coverage Part" —
     * which none of the patterns matched, so a program whose D&O is its own
     * policy had no D&O row at all.
     *
     * What DID match, on that same document, was a page of Chubb marketing
     * bound into the policy: "Directors and Officers Liability Loss Prevention
     * Manuals". So Section 5C carried a D&O row reading NOT STATED, cited to
     * the D&O policy, sourced from a list of brochures — right coverage name,
     * right file, and no relation to the contract. Requiring the words
     * "Coverage Part" is what separates a heading from prose that happens to
     * name the coverage. */
    { label: 'Directors & Officers',      patterns: [/directors?\s+(?:and|&)\s+officers?\s+(?:and\s+entity\s+)?liability\s+coverage\s+part/i, /directors?\s+(and|&)\s+officers?\s+liability/i, /\bd&o\b\s+coverage\s+part/i, /management\s+liability\s+coverage\s+part/i] },
    { label: 'Cyber Liability',            patterns: [/cyber\s+liability\s+coverage/i, /privacy\s+(and|&)\s+network\s+security/i, /information\s+security\s+(and|&)\s+privacy/i] },
    { label: 'Pollution / Environmental Liability', patterns: [/pollution\s+legal\s+liability/i, /environmental\s+impairment\s+liability/i, /contractors?\s+pollution\s+liability/i] },
    // "Crime Coverage Form" is the heading the form itself prints; only the
    // declarations say "Coverage Part", so a crime form uploaded without them
    // had no row.
    { label: 'Crime / Fidelity',           patterns: [/crime\s+coverage\s+part/i, /crime\s+coverage\s+form/i, /commercial\s+crime\s+policy/i, /employee\s+dishonesty/i] },
    { label: 'Sexual Abuse / Molestation', patterns: [/sexual\s+abuse\s+(and|&|or)?\s*molestation/i, /\bsam\b\s+coverage/i, /abuse\s+(and|or)\s+molestation\s+liability/i] },
    /* A liability part like any other, and Ohio Eastern Star buys it as its
     * own AmWins policy — which 5C never listed, though the section says every
     * liability policy must declare a trigger. Headings only: a package's
     * endorsement applicability list names "LIQUOR LIABILITY COVERAGE PART"
     * without the client buying it, and spCrossReference vetoes those. */
    { label: 'Liquor Liability', patterns: [partHeading('liquor\\s+liability(?:\\s+insurance)?'), /liquor\s+liability\s+insurance\s+policy/i] },
    /* Three liability lines the section had no row for. Ohio Eastern Star's
     * Hanover declarations list "Stop-Gap Employers Liability – Occurrence
     * Coverage Endorsement", and nothing reported it. Headings only, as for
     * every part: a mention in an exclusion or an applicability list is vetoed
     * by spCrossReference. The stop-gap pattern keeps a trigger printed after
     * the name, so the heading can state it. */
    /* GuideOne's "Sexual Misconduct Liability Coverage (Occurrence)" (GCG
     * 74 10): its own abuse line with its own limits, which none of the
     * abuse/molestation wordings above match. Its trigger comes from the forms
     * schedule title. */
    { label: 'Sexual Misconduct Liability', patterns: [/sexual\s+misconduct\s+liability\s+coverage\b/i] },
    { label: 'Fiduciary Liability', patterns: [partHeading('fiduciary\\s+liability'), /fiduciary\s+liability\s+coverage\s+(?:form|section)/i, /fiduciary\s+liability\s+(?:insurance\s+)?policy/i] },
    { label: 'Stop-Gap / Employers Liability', patterns: [
        /stop[\s\-]?gap\s*(?:[–—\-]\s*)?employers'?\s+liability(?:\s+coverage)?(?:\s*[–—\-]\s*(?:occurrence|claims[\s\-]*made))?/i,
        /employers'?\s+liability\s+(?:coverage\s+(?:part|endorsement|form)|insurance\s+coverage\s+part)/i] },
    { label: 'Auto Liability', patterns: [/business\s+auto\s+coverage\s+form/i, partHeading('(?:commercial|business)\\s+auto(?:mobile)?'), /\bauto(?:mobile)?\s+liability\s+coverage\s+(?:part|form)\b/i],
      anti: [/hired\s+(?:and|&)\s+non[\s\-]?owned/i, /garage/i] }
  ];

  // Detect trigger words within a window of text.
  // Patterns are intentionally tight so we don't catch boilerplate definitions in the policy form.
  const claimsMadeRe = /\bclaims[\-\s]*made\b/i;
  const occurrenceRe = /\boccurrence\b/i;
  const fullPriorRe  = /\bfull\s+prior\s+acts\b|\bunlimited\s+(prior|retro)/i;
  const erpRe        = /(extended\s+reporting\s+period|\berp\b|\btail\s+coverage|run[\-\s]?off)/i;

  // v2.23.5: Structural trigger signals — these are policy-form headers/limits,
  // not background mentions. They beat keyword frequency when present.
  //
  // Per broker (May 6, 2026): GL is almost always 100% Occurrence. The Employee
  // Benefits Liability endorsement (PE GL 224) attached to GL provides claims-made
  // coverage for the EB sub-coverage, but the GL coverage part itself is Occurrence.
  // Same pattern with Excess follow-form on Occurrence GL.
  //
  // Strong Occurrence signals:
  //   - "Each Occurrence" near a dollar limit (the standard CGL limits structure)
  //   - "THIS IS AN OCCURRENCE POLICY" header
  // Strong Claims-Made signals (only override if appearing in the dec-page header):
  //   - "THIS IS A CLAIMS-MADE AND REPORTED POLICY"
  //   - "THIS IS A CLAIMS-MADE POLICY"
  //   - "Each Wrongful Act" or "Each Claim" near a dollar limit (claims-made limits structure)
  const eachOccurrenceRe       = /each\s+occurrence[\s\S]{0,80}\$[\d,]/i;
  const occurrencePolicyHdrRe  = /\bthis\s+is\s+an?\s+occurrence\s+(policy|coverage|form)/i;
  const claimsMadeHdrRe        = /\bthis\s+is\s+a?\s*claims[\-\s]*made\s+(and\s+reported\s+)?(policy|coverage|form)/i;
  // A declarations notice asserting the trigger for every liability coverage
  // part the policy contains. Deliberately narrow: it must say the parts
  // PROVIDE claims-made cover, not merely describe what claims-made means.
  const _SP_POLICY_CLAIMS_MADE_NOTICE = /\b(?:the\s+)?liability\s+coverage\s+parts?\s+provides?\s+claims[\s\-]*made\s+coverage\b/i;
  /* The notice governs the LIABILITY coverage parts, and says so. A ForeFront
   * portfolio also contains Crime, Kidnap Ransom & Extortion and Workplace
   * Violence parts, which are not liability parts and are not claims-made: a
   * crime form responds to when the loss is DISCOVERED. Letting the notice
   * reach Crime printed "Crime / Fidelity — Claims-Made" and behind it a
   * retro-date gap for a form that cannot have a retroactive date at all.
   *
   * The notice is also a DECLARATIONS statement, so it answers only for parts
   * the declarations name — hence the page bound below. Employee Benefits
   * Liability is mentioned on page 119 of this policy and is not one of its
   * seven coverage parts; without the bound the notice handed that mention a
   * trigger and turned it back into a row. */
  const _SP_NOTICE_EXCLUDES = new Set(['Crime / Fidelity']);
  const eachWrongfulActRe      = /each\s+(wrongful\s+act|claim|action|incident)[\s\S]{0,80}\$[\d,]/i;

  // Coverage parts whose default trigger is Occurrence unless explicit claims-made header.
  // GL and Excess/Umbrella that follow-form on GL are virtually always Occurrence in real life.
  const occurrenceDefaultParts = ['General Liability', 'Excess / Umbrella Liability'];

  // ============================================================
  // v2.23.2: File-level retro date scan.
  // PDF.js reads two-column dec pages in funky order, so "Retroactive Date:" and
  // "08/24/2025" frequently end up 50-300 chars apart in the extracted text.
  // The old per-page strict-proximity regex missed nearly every real retro date.
  //
  // New approach: for EACH FILE, find every mention of "retroactive" in the entire
  // text, scan a ±300 char window around it for a date pattern (mm/dd/yyyy or
  // mm-dd-yyyy or written month-day-year), tally the dates, and pick the most
  // common one as the program's retro date for that file. Per the broker:
  // "If it is paired with a date, that is a correct version."
  //
  // Also detect Full Prior Acts at file level — if any retro mention is paired with
  // "full prior acts" or "unlimited prior", treat the whole file as Full Prior Acts.
  // ============================================================
  const datePat = /(\b(?:0?[1-9]|1[0-2])[\/\-](?:0?[1-9]|[12]\d|3[01])[\/\-](?:19|20)?\d{2}\b)|(\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+(?:19|20)\d{2}\b)/i;

  // v2.23.3: Normalize date strings so 08/24/25, 08/24/2025, 8-24-2025, and
  // "August 24, 2025" all collapse to a single canonical form (08/24/2025) for
  // alignment comparison. This eliminates false-positive misalignment alerts.
  function normalizeRetroDate(s) {
    if (!s) return '';
    const cleaned = s.trim().replace(/\s+/g, ' ');
    // Numeric forms: m/d/yy, mm/dd/yyyy, m-d-yyyy, etc.
    const numMatch = cleaned.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (numMatch) {
      let mm = numMatch[1].padStart(2, '0');
      let dd = numMatch[2].padStart(2, '0');
      let yy = numMatch[3];
      if (yy.length === 2) {
        // Two-digit year → assume 20xx for 00-79, 19xx for 80-99
        yy = (parseInt(yy, 10) < 80 ? '20' : '19') + yy;
      }
      return `${mm}/${dd}/${yy}`;
    }
    // Written forms: "August 24, 2025" → 08/24/2025
    const months = { january:'01', february:'02', march:'03', april:'04', may:'05', june:'06', july:'07', august:'08', september:'09', october:'10', november:'11', december:'12' };
    const wMatch = cleaned.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
    if (wMatch) {
      const m = months[wMatch[1].toLowerCase()];
      if (m) {
        return `${m}/${wMatch[2].padStart(2,'0')}/${wMatch[3]}`;
      }
    }
    return cleaned;
  }
  // v2.24: read the prior-acts anchor through spRetroAnchor rather than by
  // matching the literal token "retroactive".
  //
  // The scan this replaces did three things wrong at once, and Aspen View's
  // report showed all three. It matched only the word "retroactive", so a
  // Beazley cyber policy declaring "Continuity Date: 01-Jul-2020" reported
  // "Retro Date Not Found". Its date grammar could not read 01-Jul-2020
  // anyway. And it tallied one date for the WHOLE FILE and gave it to every
  // claims-made part in that file, which is how 07/01/2012 — the retro date
  // of three Educators Management Liability sub-coverages — ended up printed
  // against General Liability, a CG 00 01 occurrence form with no retro date
  // at all and no claims-made trigger either.
  //
  // So anchors are now attributed to the nearest coverage block, and the
  // file-level fallback applies ONLY when the file states exactly one anchor.
  // A document that states four has told us which coverage owns which, and
  // spreading any of them across the rest is inventing a finding.
  const RA = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
  const fileRetroDates = new Map();   // fileName → the single file-wide anchor, when there is exactly one
  const filePriorProceedings = new Map();  // fileName → Pending-or-Prior Proceedings dates stated instead
  const fileRetroPointer = new Map();      // fileName → a retro field answered with a cross-reference
  const fileAnchors    = new Map();   // fileName → [attributed anchors]
  const fileErp        = new Map();   // fileName → ERP or null
  const fileForms      = new Map();   // fileName → [{form,coverage,trigger}] from the forms schedule
  const fileTriggerNotice = new Map();  // fileName → trigger stated for ALL this policy's liability parts
  const fileBlankRetro = new Map();     // fileName → a retro field printed and left empty
  const filePriorKnowledge = new Map(); // fileName → a prior-acts limit expressed as a condition, not a date
  const fileContinuityKnowledge = new Map(); // fileName → its Continuity Date is used only as a knowledge cut-off
  const fileContinuityDates = new Map();     // fileName → [{value, normalized, coverage, page}]
  const fileDiscovery = new Map();           // fileName → {term, basis, quote, page} for a crime form's discovery period
  const fileSubRetro = new Map();            // fileName → [{coverage, date, page}] claims-made parts of an occurrence policy
  const fileRetroByLocation = new Map();     // fileName → [{coverage, rows:[{location,date}], page}] per-location schedules
  const fileRetroByNamedInsured = new Map(); // fileName → {rows:[{namedInsured,date}], page} from a Named Insured Schedule
  const fileUnreadable = new Map();     // fileName → true when the extraction cannot carry a field at all
  const fileTermsTriggers = new Map();  // fileName → triggers a coverage part states in its own insuring clause
  const fileSchedule   = new Map();   // fileName → every scheduled form, for binding a provision to its coverage
  pdfTexts.forEach(file => {
    if (!file || !file.pages) return;
    const fileName = file.fileName || 'Unknown File';
    const fullText = spNorm(file.pages.map(p => p.text || '').join('\n'));
    if (!fullText || !RA) return;
    /* THE POLICY STATES ITS TRIGGER ONCE, ABOVE EVERYTHING.
     *
     * A portfolio policy does not repeat the trigger under each coverage
     * part. Chubb's ForeFront opens its declarations with one notice:
     *
     *   NOTICE: THE LIABILITY COVERAGE PARTS PROVIDE CLAIMS-MADE COVERAGE,
     *   WHICH APPLIES ONLY TO "MATTERS" FIRST MADE DURING THE "POLICY PERIOD"
     *
     * That is the insurer stating the trigger for every liability coverage
     * part in the document. The per-part scan cannot see it, because it reads
     * forward from a coverage's own heading and the notice sits above all of
     * them — so on this program Directors & Officers scored nothing on every
     * page it appears, was dropped as unevidenced, and the report showed no
     * D&O row at all for a placement whose own policy document it is.
     *
     * Read from the declarations only. The same words appear in definitions
     * and in consumer notices further in, where they describe claims-made
     * cover in the abstract rather than asserting it of this contract. */
    const declHead = (file.pages || []).slice(0, 15).map(p => p.text || '').join('\n');
    if (_SP_POLICY_CLAIMS_MADE_NOTICE.test(spNorm(declHead))) fileTriggerNotice.set(fileName, 'Claims-Made');

    /* A DOCUMENT WHOSE ROWS WERE TAKEN APART CANNOT ANSWER THIS QUESTION.
     *
     * The limits path already refuses such a document out loud — "the limits
     * would be wrong, not missing". Section 5C did not, so the same pypdf
     * extraction that stops a limit from being published still produced a
     * retro cell, and an empty one reads as "this policy states none". Same
     * rule, same words, so the two sections cannot disagree about whether a
     * document was read. */
    if ((file.pages || []).some(pg => pg && pg.text && spColumnSplitPage(pg.text))) {
      fileUnreadable.set(fileName, true);
    }
    const blanks = [];
    const anchors = spUsableAnchors(fullText, RA.readAttributedAnchors(fullText), blanks);
    anchors.forEach(a => { a.page = spPageOfAnchor(file, a); });
    if (blanks.length) fileBlankRetro.set(fileName, blanks[0]);
    if (RA.readPriorKnowledge) {
      const pk = RA.readPriorKnowledge(fullText);
      if (pk) { pk.page = spPageOfAnchor(file, { label: 'Prior Knowledge', value: pk.value }); filePriorKnowledge.set(fileName, pk); }
    }
    fileAnchors.set(fileName, anchors);
    if (RA.readRetroByLocation) {
      const byLoc = RA.readRetroByLocation(fullText) || [];
      byLoc.forEach(s => {
        // The page that carries THIS coverage's schedule: its name, a
        // by-location or insured-locations heading, and its dates. Matching a
        // location alone finds the wrong page — the same addresses are on
        // every one of these schedules.
        const pg = (file.pages || []).find(x => {
          const t = String(x.text || '').replace(/\s+/g, ' ');
          return t.toLowerCase().includes(s.coverage.toLowerCase())
            && /retroactive\s+dates?\s*(?:\(\s*s\s*\))?\s*by\s+location|schedule\s+of\s+insured\s+locations/i.test(t)
            && s.rows.every(r => t.includes(r.date));
        });
        s.page = pg ? pg.page : null;
      });
      if (byLoc.length) fileRetroByLocation.set(fileName, byLoc);
    }
    if (RA.readRetroByNamedInsured) {
      // The original text, not fullText: spNorm flattens the grid, and the
      // empty Effective / Termination cells are what place the date.
      const ni = RA.readRetroByNamedInsured((file.pages || []).map(p => p.text || '').join('\n')) || [];
      if (ni.length) {
        const pg = (file.pages || []).find(x => /named\s+insured\s+schedule/i.test(String(x.text || '')) && ni.every(r => String(x.text || '').includes(r.date)));
        fileRetroByNamedInsured.set(fileName, { rows: ni, page: pg ? pg.page : null });
      }
    }
    if (RA.readSubCoverageRetro) {
      const sr = RA.readSubCoverageRetro(fullText) || [];
      if (sr.length) {
        const pg = (file.pages || []).find(x => /retroactive\s+dates?\s*\(\s*for\s+claims/i.test(String(x.text || '')));
        sr.forEach(r => { r.page = pg ? pg.page : null; });
        fileSubRetro.set(fileName, sr);
      }
    }
    if (RA.readDiscoveryPeriod) {
      const dp = RA.readDiscoveryPeriod(fullText);
      if (dp) {
        const q = dp.quote.slice(0, 40).toLowerCase();
        const pg = (file.pages || []).find(x => spNorm(String(x.text || '')).replace(/\s+/g, ' ').toLowerCase().includes(q));
        dp.page = pg ? pg.page : null;
        fileDiscovery.set(fileName, dp);
      }
    }
    if (RA.readContinuityDates) {
      const cds = RA.readContinuityDates(fullText) || [];
      // The page that states THIS part's date: the same date sits on the D&O
      // and the EPL declarations, and the first match cited D&O for both.
      cds.forEach(c => {
        const withDate = (file.pages || []).filter(pg => /continuity\s+date/i.test(pg.text || '') && String(pg.text || '').includes(c.value));
        const own = c.coverage ? withDate.find(pg => String(pg.text || '').toLowerCase().includes(String(c.coverage).toLowerCase())) : null;
        c.page = (own || withDate[0] || {}).page || spPageOfAnchor(file, { label: 'Continuity Date', value: c.value });
      });
      if (cds.length) fileContinuityDates.set(fileName, cds);
    }
    if (RA.readContinuityKnowledgeOnly) {
      const ck = RA.readContinuityKnowledgeOnly(fullText);
      if (ck) fileContinuityKnowledge.set(fileName, ck);
    }
    if (RA.readTermsTriggers) fileTermsTriggers.set(fileName, RA.readTermsTriggers(fullText) || []);
    fileErp.set(fileName, RA.readErp(fullText));
    fileForms.set(fileName, RA.readFormTriggers(fullText));
    fileSchedule.set(fileName, RA.readFormsSchedule(fullText));
    // How this FILE anchors prior acts when it states no retro date. Recorded
    // either way, because it is what turns "we found no retro date" into a
    // finding a broker can act on.
    const pps = RA.readPriorProceedings(fullText) || [];
    pps.forEach(x => { x.page = spPageOfAnchor(file, x); });
    filePriorProceedings.set(fileName, pps);
    fileRetroPointer.set(fileName, RA.readRetroPointer(fullText));
    const full = anchors.find(a => a.kind === 'full-prior-acts');
    if (full) {
      fileRetroDates.set(fileName, { date: 'Full Prior Acts', isFullPrior: true, normalized: 'FULL_PRIOR_ACTS', label: full.label });
      return;
    }
    const distinct = [...new Map(anchors.map(a => [a.normalized, a])).values()];
    if (distinct.length === 1) {
      const a = distinct[0];
      fileRetroDates.set(fileName, { date: a.value, isFullPrior: false, normalized: a.normalized, label: a.label });
    }
  });

  // The ERP that belongs to one coverage part.
  //
  // A file-wide ERP applied to every claims-made part in the file is the same
  // defect as a file-wide retro date, and on Aspen View it was worse. The
  // package's best-scoring ERP reads "Automatic Extended Reporting Period of
  // 30 days", and it sits inside GCG 78 01 — the CYBER SUITE COVERAGE
  // ENDORSEMENT. The report printed "ERP 30 days" against Educators Legal
  // Liability, D&O, EPLI and Employee Benefits Liability, none of which that
  // endorsement touches.
  //
  // So the ERP is bound to the form it was read from. Where the form maps to a
  // coverage and that coverage is not this row's, the term is dropped and the
  // row reports only that the policy contains an ERP provision — which is
  // true, and is all that was actually read.
  function erpFor(fileName, partLabel) {
    return spBindErp(fileErp.get(fileName), fileSchedule.get(fileName), partLabel);
  }

  // The trigger the policy's own forms schedule states for a coverage part.
  // Authoritative: a form number is a fact about the contract, where keyword
  // weighting is an inference from prose that happens to sit nearby.
  function formTriggerFor(fileName, partLabel) {
    const forms = fileForms.get(fileName) || [];
    const hit = forms.find(f => spSameCoverage(f.coverage, partLabel));
    return hit || null;
  }

  // An anchor this file attributes to THIS coverage part by name — no
  // file-wide fallback. A policy that prints a retroactive date under a
  // coverage's own heading has said that coverage part exists, whatever the
  // trigger scan made of the prose around it, and that is the evidence the
  // row is weighed on below.
  /* The proceedings date that belongs to THIS part. A portfolio states one
   * per coverage part and they differ — 2005 for D&O and EPL, 2008 for
   * Fiduciary on the policy reviewed here. The file-wide fallback applies
   * only where every stated date agrees, because then there is nothing to get
   * wrong; where they differ and none is attributed to this part, the honest
   * answer is that this part's date was not read. */
  function proceedingsFor(fileName, partLabel) {
    const all = filePriorProceedings.get(fileName) || [];
    if (!all.length) return null;
    const owned = all.find(x => x.coverage && spSameCoverage(x.coverage, partLabel));
    if (owned) return owned;
    if (all.some(x => x.coverage)) {
      const distinct = new Set(all.map(x => x.normalized || x.value));
      return distinct.size === 1 ? all[0] : null;
    }
    return all[0];
  }

  /* A trigger the coverage part states about itself. Authoritative in the same
   * way a form number is — it is the contract describing its own operation —
   * so it answers wherever the forms schedule did not. */
  function termsTriggerFor(fileName, partLabel) {
    const hits = fileTermsTriggers.get(fileName) || [];
    const owned = hits.find(h => h.coverage && spSameCoverage(h.coverage, partLabel));
    if (owned) return owned;
    /* A clause no heading claims, in a policy that IS one coverage part. The
     * AmWins liquor policy states "This insurance applies only to injury which
     * occurs during the policy period" further below its part heading than
     * any lookback reaches, so the trigger was taken from the word
     * "occurrence" in a definition instead. In a monoline policy the clause
     * can only be that part's; in a package it is left unattributed. */
    const parts = fileParts.get(fileName);
    if (parts && parts.size === 1 && parts.has(partLabel)) return hits.find(h => !h.coverage) || null;
    return null;
  }

  // Which liability parts each file names by heading, so a clause no heading
  // claims can be given to the only part a monoline policy has.
  const fileParts = new Map();
  pdfTexts.forEach(file => {
    if (!file || !file.pages) return;
    const t = spNorm(file.pages.map(p => p.text || '').join('\n'));
    const set = new Set();
    liabilityParts.forEach(lp => {
      for (const pat of lp.patterns) {
        const re = new RegExp(pat.source, pat.flags.includes('g') ? pat.flags : pat.flags + 'g');
        let m;
        while ((m = re.exec(t)) !== null) {
          const near = t.slice(Math.max(0, m.index - 60), m.index + m[0].length);
          if (!spCrossReference(t, m.index) && !(lp.anti || []).some(rx => rx.test(near))) { set.add(lp.label); break; }
        }
        if (set.has(lp.label)) break;
      }
    });
    fileParts.set(file.fileName || 'Unknown File', set);
  });

  function retroOwnedBy(fileName, partLabel) {
    return (fileAnchors.get(fileName) || []).some(a => a.coverage && spSameCoverage(a.coverage, partLabel));
  }

  // The anchor that belongs to one coverage part, by name. Falls back to the
  // file-wide anchor only when the file states exactly one (see above).
  function retroFor(fileName, partLabel) {
    const anchors = fileAnchors.get(fileName) || [];
    const owned = anchors.filter(a => spSameCoverage(a.coverage, partLabel));
    if (owned.length) {
      const a = owned[0];
      return { date: a.value, isFullPrior: a.kind === 'full-prior-acts', normalized: a.normalized, label: a.label, page: a.page || null };
    }
    // A file that names coverages for its anchors has answered the question
    // for every coverage in it. Not finding this part among them means this
    // part has no retro date, which is a finding, not a gap to fill from
    // somewhere else in the document.
    if (anchors.some(a => a.coverage)) return null;
    return fileRetroDates.get(fileName) || null;
  }

  // For each file/page, slice text around any liability-part heading and analyze.
  // We collect the highest-confidence finding per (file, coveragePart) pair and dedupe to one row per coverage type per file.
  const findings = new Map(); // key = `${fileName}::${coveragePart}` → finding

  pdfTexts.forEach(file => {
    if (!file || !file.pages) return;
    const fileName = file.fileName || 'Unknown File';

    file.pages.forEach(p => {
      const text = spNorm(p.text || '');
      if (!text) return;

      /* A SCHEDULE OF FORMS IS AN INDEX, NOT A STATEMENT OF TERMS.
       *
       * Every coverage part in the policy is NAMED on this one page, and so is
       * every trigger word, because the page lists form titles: "Abuse Or
       * Molestation Liability Coverage", "... (Claims Made)". Weighing those
       * words against each other says nothing about any particular part.
       *
       * On this package policy the abuse endorsement is attached to an
       * OCCURRENCE general liability form, and the endorsement itself reads
       * Occurrence. But its name also appears on the forms schedule, that page
       * happens to carry claims-made wording for a different part, and the two
       * findings tied on score — so the index page won on document order and
       * Section 5C reported Sexual Abuse / Molestation as Claims-Made with no
       * retroactive date. A missing retro date on a claims-made row reads as a
       * coverage gap; here the row should not have been claims-made at all.
       *
       * The page is not vetoed, because on some policies it is the only place
       * a part is named at all. It is demoted, so any page that actually
       * states the part's terms outranks it. */
      const isFormsIndex = /schedule\s+of\s+(forms|endorsements)|forms?\s+(and|&)\s+endorsements\s+schedule/i
        .test(text.slice(0, 300));

      liabilityParts.forEach(lp => {
        let matched = false;
        let matchIdx = 0;
        let headingTrigger = null;
        for (const pat of lp.patterns) {
          const m = pat.exec(text);
          if (!m || spCrossReference(text, m.index)) continue;
          // Per-part veto for a title that contains the part's words and names
          // a different coverage.
          const near = text.slice(Math.max(0, m.index - 60), m.index + m[0].length);
          if ((lp.anti || []).some(rx => rx.test(near))) continue;
          matched = true; matchIdx = m.index;
          headingTrigger = /claims[\s\-]*made/i.test(m[0]) ? 'Claims-Made'
            : /occurrence/i.test(m[0]) ? 'Occurrence' : null;
          break;
        }
        if (!matched) return;

        // Slice a generous window AT the coverage-part heading to look for
        // trigger language.
        //
        // This read `text.substring(0, 4000)` — "pages are usually ~3K" — which
        // was true when every page came from pdf.js. It is not true now: the
        // browser sends every PDF to the backend first, and the backend returns
        // the WHOLE DOCUMENT as a single page. On this policy that is 830,000
        // characters, so the trigger scan examined the first 4,000 of them and
        // nothing else. Educators Legal Liability, Employment Practices and
        // D&O all sit far below that line, which is why they reported NOT
        // STATED while the same policy read correctly page by page.
        //
        // Slicing from the heading is what the comment always claimed, and it
        // is better on a paginated document too: a coverage part's trigger is
        // stated beneath its own heading, not at the top of whatever page it
        // happens to land on.
        const window = text.substr(matchIdx, 4000);

        const isClaimsMade = claimsMadeRe.test(window);
        const isOccurrence = occurrenceRe.test(window);

        // v2.23.5: Use structural signals first (limits-section patterns and dec-page
        // headers), keyword frequency only as a fallback. This prevents GL from being
        // mislabeled Claims-Made when an Employee Benefits endorsement is attached.
        let trigger = null;
        let triggerSource = null;

        // Step 1: Strong Occurrence signal — "Each Occurrence $N" limits structure or
        // explicit "THIS IS AN OCCURRENCE POLICY" header.
        const hasEachOccurrenceLimit = eachOccurrenceRe.test(window);
        const hasOccurrenceHeader = occurrencePolicyHdrRe.test(window);

        // Step 2: Strong Claims-Made signal — "THIS IS A CLAIMS-MADE..." header or
        // "Each Wrongful Act / Each Claim $N" limits structure.
        const hasClaimsMadeHeader = claimsMadeHdrRe.test(window);
        const hasEachWrongfulActLimit = eachWrongfulActRe.test(window);

        const strongOccurrence = hasEachOccurrenceLimit || hasOccurrenceHeader;
        const strongClaimsMade = hasClaimsMadeHeader || hasEachWrongfulActLimit;

        if (strongClaimsMade && !strongOccurrence) {
          trigger = 'Claims-Made';
        } else if (strongOccurrence && !strongClaimsMade) {
          trigger = 'Occurrence';
        } else if (strongOccurrence && strongClaimsMade) {
          // Both signals present — means a claims-made endorsement is attached to an
          // occurrence coverage part (very common with EB on GL). Prefer Occurrence
          // for occurrence-default parts; prefer Claims-Made for claims-made-default parts.
          if (occurrenceDefaultParts.includes(lp.label)) {
            trigger = 'Occurrence';
          } else {
            trigger = 'Claims-Made';
          }
        } else {
          // No strong signal — fall back to keyword frequency on the same page.
          if (isClaimsMade && isOccurrence) {
            const cmIdx = window.search(claimsMadeRe);
            const ocIdx = window.search(occurrenceRe);
            trigger = (cmIdx < ocIdx) ? 'Claims-Made' : 'Occurrence';
          } else if (isClaimsMade) {
            trigger = 'Claims-Made';
          } else if (isOccurrence) {
            trigger = 'Occurrence';
          }
        }

        /* The declarations notice answers only where the prose did not. It is
         * the policy's own statement about its coverage parts, so it beats
         * silence — but it is a statement about the document as a whole, so
         * anything read at the coverage part itself outranks it, and the
         * forms schedule below outranks both. */
        if (!trigger && fileTriggerNotice.get(fileName)
            && !_SP_NOTICE_EXCLUDES.has(lp.label)
            && (p.page || 0) <= 15) {
          trigger = fileTriggerNotice.get(fileName);
          triggerSource = 'declarations notice';
        }

        // v2.23.5 — Final guardrail: GL and Excess default to Occurrence if no explicit
        // claims-made header was found. "Almost always 100% Occurrence" per broker.
        if (occurrenceDefaultParts.includes(lp.label) && !hasClaimsMadeHeader && trigger === 'Claims-Made') {
          trigger = 'Occurrence';
        }

        // Retro date detection (claims-made only matters)
        // v2.23.2: Use file-level retro date scan results (handles PDF.js two-column
        // layout quirks) instead of per-page strict-proximity regex.
        let retroDate = null;
        let isFullPrior = false;
        // The forms schedule overrides whatever the prose weighting decided.
        // Aspen View's GL scored Claims-Made off an Employee Benefits
        // Liability endorsement printed on the same declarations page; the
        // schedule says CG 00 01, the ISO occurrence form, and that settles
        // it. A Discovery or Loss Sustained trigger also lands here, and both
        // correctly stop the retro lookup below: neither kind of form has a
        // retroactive date to find.
        /* A trigger printed in the part's own heading is the policy stating
         * it, not prose near it. Hanover's declarations read "Commercial
         * General Liability Claims-Made Coverage Part"; the "GL is almost
         * always Occurrence" guardrail above is a default for when the policy
         * says nothing, and must not overrule it. Only the forms schedule and
         * the part's insuring clause outrank it. */
        if (headingTrigger) { trigger = headingTrigger; triggerSource = 'coverage part heading'; }
        const formSays = formTriggerFor(fileName, lp.label);
        if (formSays) { trigger = formSays.trigger; triggerSource = formSays.form; }
        else {
          // The part's own insuring clause, where the forms schedule is silent
          // or the form is carrier-proprietary with no trigger in its title.
          const says = termsTriggerFor(fileName, lp.label);
          if (says) { trigger = says.trigger; triggerSource = 'coverage part terms'; }
        }

        let retroLabel = null;
        let retroPage = null;
        if (trigger === 'Claims-Made') {
          const fileRetro = retroFor(fileName, lp.label);
          if (fileRetro) {
            // v2.23.3: Display the normalized 4-digit-year form so the table reads
            // consistently — the broker doesn't need to see different formats of the
            // same date side by side.
            retroDate = fileRetro.isFullPrior || fileRetro.normalized === fileRetro.date
              ? fileRetro.date : (fileRetro.normalized || fileRetro.date);
            isFullPrior = fileRetro.isFullPrior;
            retroPage = fileRetro.page || null;
            // The carrier's own wording, carried through to the report. A row
            // that reads "Continuity Date — 07/01/2020" can be checked against
            // the declarations in one glance; one that silently renames it to
            // "Retroactive Date" cannot.
            retroLabel = fileRetro.label || null;
          }
        }

        // ERP detection. Read from the whole file through the ERP vocabulary,
        // which knows Beazley's "Optional Extension Period" and a Lloyd's
        // "Discovery Period" are ERPs, and knows that a crime discovery form's
        // "Extended Period To Discover Loss" and the consumer notice
        // explaining ERPs in the abstract are not.
        const fe = erpFor(fileName, lp.label);
        const hasERP = fe ? true : erpRe.test(window);

        // Build finding
        const key = `${fileName}::${lp.label}`;
        const existing = findings.get(key);
        /* WHAT COUNTS AS EVIDENCE THAT THIS COVERAGE PART EXISTS.
         *
         * A trigger, a retroactive date, or an anchor the policy prints under
         * this part's own heading. Each is worth 2, and a row needs one of
         * them (see the filter below). An ERP provision is worth 1 and can
         * never carry a row by itself, because nearly every liability policy
         * contains one somewhere.
         *
         * The anchor is counted separately from `retroDate` because the retro
         * lookup only runs for claims-made rows. A part whose trigger the scan
         * could not read still has its coverage part named, and printing it
         * with the trigger NOT STATED is the finding the section exists for —
         * dropping it would hide a real gap. */
        const score = (trigger ? 2 : 0) + (retroDate ? 2 : 0) + (hasERP ? 1 : 0)
          + (!retroDate && retroOwnedBy(fileName, lp.label) ? 2 : 0)
          - (isFormsIndex ? 1 : 0);
        if (!existing || score > existing._score) {
          findings.set(key, {
            fileName,
            coveragePart: lp.label,
            page: p.page || 0,
            trigger,
            triggerSource,
            retroDate,
            retroLabel,
            retroPage,
            isFullPrior,
            // Carried so the row can say WHY there is no retro date, instead
            // of only that there is none. See the retro cell in 5C.
            priorProceedings: proceedingsFor(fileName, lp.label),
            priorKnowledge: filePriorKnowledge.get(fileName) || null,
            blankRetroField: fileBlankRetro.get(fileName) || null,
            unreadable: !!fileUnreadable.get(fileName),
            retroPointer: fileRetroPointer.get(fileName) || null,
            hasERP,
            erp: fe || null,
            _score: score
          });
        }
      });
    });
  });

  // ============================================================
  // v2.23.1: Dedupe per coverage TYPE, not per (file, coverage) pair.
  // Same coverage appearing in multiple files — the higher-score finding wins.
  // (e.g., ELL appearing in Excess Policy as boilerplate AND in Package Policy
  // as the actual coverage — keep only Package Policy row.)
  //
  // Suppress false positives: if a coverage match has score 0 (no trigger word
  // found in the same window), it's almost certainly a passing mention, not a real
  // coverage part. Drop it.
  // ============================================================
  const allFindings = Array.from(findings.values());
  const byCoverageType = new Map();
  allFindings.forEach(f => {
    /* AN ERP PROVISION IS NOT EVIDENCE THAT A COVERAGE PART EXISTS.
     *
     * The old test dropped a finding only when it scored ZERO, and a finding
     * scores 1 for the file containing an extended-reporting-period clause
     * anywhere in it — which nearly every liability policy does. So a coverage
     * part matched on nothing but a passing mention survived on that point
     * alone, with no trigger and no retro date to report.
     *
     * That is how Section 5C published a Directors & Officers row on a program
     * with no D&O coverage part. The match was the parenthetical inside a
     * standard exclusion list — "professional liability insurance (except
     * Directors and Officers Liability)" — which is boilerplate in both the
     * excess and the package forms. The row printed NOT STATED for the trigger
     * and cited the excess policy, so a broker reading it saw a management
     * liability placement with an unstated trigger rather than a coverage the
     * insured does not buy.
     *
     * A row now has to carry what the section exists to report: a trigger, or
     * a retroactive date. Either scores 2 on its own. */
    if (f._score < 2) return; // nothing but an ERP mention — not a coverage part
    const existing = byCoverageType.get(f.coveragePart);
    if (!existing || f._score > existing._score) {
      byCoverageType.set(f.coveragePart, f);
    }
  });

  // The declarations override the forms. A coverage part listed in the
  // attachment table with its Policy Type is the insurer's own statement of
  // the trigger — it beats anything inferred from wording elsewhere, and it
  // is the only place that says a part is attached AT ALL.
  const declRows = (typeof window !== 'undefined' && window.spDeclarationsTriggers)
    ? window.spDeclarationsTriggers(pdfTexts) : [];
  declRows.forEach(r => {
    // Map the declarations' own wording onto the report's coverage labels
    // where one matches; otherwise keep the policy's name for it.
    const match = liabilityParts.find(lp => lp.patterns.some(rx => rx.test(r.part + ' coverage part')));
    const label = match ? match.label : r.part;
    // The declarations settle the TRIGGER. Where they are silent on the retro
    // date or the ERP option, whatever the forms found still stands — losing
    // "Full Prior Acts" because the declarations stated the trigger somewhere
    // else would be a step backwards.
    const prior = byCoverageType.get(label);
    // The file-scoped anchor for this part, on the same terms the forms path
    // uses: a coverage-attributed anchor when the file names one, otherwise
    // the file-wide anchor only when the file states exactly one.
    const declRetro = r.trigger === 'Claims-Made' ? retroFor(r.fileName, label) : null;
    byCoverageType.set(label, {
      fileName: r.fileName,
      coveragePart: label,
      page: r.page,
      trigger: r.trigger,
      triggerSource: r.triggerSource || (prior && prior.triggerSource) || null,
      // The declarations settle the trigger, and where they state no retro
      // date the forms finding supplies one. But a part named ONLY in the
      // declarations has no forms finding to inherit from — `prior` is
      // undefined — and the row then printed an empty retroactive date over a
      // policy that states one plainly.
      //
      // That is what emptied the Excess / Umbrella row on this program. The
      // excess declarations name the part and give its trigger as claims-made;
      // no page matched the part by heading, so nothing was carried forward;
      // and the file's own anchor — Retroactive Date 11/09/2025, the same date
      // the underlying package policy carries — was never consulted. Ask the
      // file-scoped reader the forms path would have asked.
      retroDate: r.trigger === 'Claims-Made'
        ? (r.retroDate || (prior && prior.retroDate) || (declRetro && declRetro.date) || null)
        : r.retroDate,
      // The carrier's own wording for the anchor travels with the value. The
      // merge dropped it, so a row settled by the declarations printed a bare
      // date and lost the one thing that makes it checkable.
      retroLabel: r.trigger === 'Claims-Made'
        ? (r.retroLabel || (prior && prior.retroLabel) || (declRetro && declRetro.label) || null) : null,
      isFullPrior: r.trigger === 'Claims-Made'
        && (/full\s+prior/i.test(r.retroDate || (prior && prior.retroDate) || '')
            || !!(declRetro && declRetro.isFullPrior)),
      hasERP: r.trigger === 'Claims-Made' && (!!r.hasERP || !!(prior && prior.hasERP)),
      erp: r.erp || (prior && prior.erp) || null,
      // Why there is no retro date, carried through the declarations merge as
      // it is through the forms path. This object was built field by field and
      // these two were never listed, so a row settled by the declarations lost
      // them — and the excess row, whose whole story is that the policy
      // answers Item 5 with a cross-reference, printed a bare "not found".
      priorProceedings: (prior && prior.priorProceedings) || proceedingsFor(r.fileName, label),
      priorKnowledge: (prior && prior.priorKnowledge) || filePriorKnowledge.get(r.fileName) || null,
      blankRetroField: (prior && prior.blankRetroField) || fileBlankRetro.get(r.fileName) || null,
      unreadable: !!(prior && prior.unreadable) || !!fileUnreadable.get(r.fileName),
      retroPointer: (prior && prior.retroPointer) || fileRetroPointer.get(r.fileName) || null,
      fromDeclarations: true,
      _score: 99
    });
  });

  /* THE POLICY'S OWN ROSTER FILLS WHAT THE SCAN COULD NOT NAME.
   *
   * Everything above infers which coverage parts exist from headings it
   * recognises. A portfolio states them outright, and states more of them
   * than the vocabulary knows — so five of this program's seven parts were
   * missing from the table entirely, including Fiduciary Liability, which is
   * claims-made and carries its own proceedings date.
   *
   * A part named by the declarations is reported whatever the scan made of
   * it. Where the scan already produced a row, that row stands: it was built
   * from the coverage's own pages and knows more than a list does. */
  (pdfTexts || []).forEach(file => {
    if (!file || !file.pages) return;
    const fileName = file.fileName || 'Unknown File';
    for (const part of spDeclaredCoverageParts(file)) {
      const known = liabilityParts.find(lp => lp.patterns.some(rx => rx.test(part.name + ' coverage part')));
      const label = known ? known.label : part.name;
      if (byCoverageType.has(label)) continue;
      /* The notice governs the LIABILITY parts and says so, so it is applied
       * by the part's own name. A Crime or Kidnap & Ransom part is not
       * claims-made and must not inherit the notice — printing a retro-date
       * gap against a crime form is a finding about a field it cannot have. */
      const isLiability = /\bliability\b/i.test(part.name);
      /* The part's own terms first: a coverage part that says it responds to
       * loss DISCOVERED during the policy period has answered the question
       * itself, and that beats an inference from the part's name. The notice
       * only covers the liability parts, so without this a crime part could
       * report nothing at all. */
      const terms = spTermsTriggerForPart(file, part.name);
      const trigger = (terms && terms.trigger)
        || (isLiability && fileTriggerNotice.get(fileName)) || null;
      const ppp = trigger === 'Claims-Made' ? proceedingsFor(fileName, label) : null;
      byCoverageType.set(label, {
        fileName,
        coveragePart: label,
        page: part.page,
        trigger,
        triggerSource: terms ? 'coverage part terms' : (trigger ? 'declarations notice' : null),
        retroDate: null,
        retroLabel: null,
        retroPage: null,
        isFullPrior: false,
        priorProceedings: ppp,
        priorKnowledge: trigger === 'Claims-Made' ? (filePriorKnowledge.get(fileName) || null) : null,
        blankRetroField: fileBlankRetro.get(fileName) || null,
        unreadable: !!fileUnreadable.get(fileName),
        retroPointer: fileRetroPointer.get(fileName) || null,
        hasERP: !!erpFor(fileName, label),
        erp: erpFor(fileName, label) || null,
        fromDeclarationsRoster: true,
        _score: 50
      });
    }
  });

  out.byCoverage = Array.from(byCoverageType.values());

  // A Continuity Date the policy uses only as a knowledge cut-off is still
  // shown, under its own label, but it is not a retroactive date and is not
  // compared with one below. Done here, once, because a row can be built by
  // the forms path or the declarations merge, and both must agree.
  /* A per-location schedule REPLACES the part's single retroactive date —
   * the endorsement says so in terms. Hanover's EBL declarations state
   * 09/20/2003; form 970-0015 replaces it with 07/20/2021 for two locations
   * and 01/06/2025 for the third. Every date is reported, by location. */
  out.byCoverage.forEach(f => {
    if (f.trigger !== 'Claims-Made' || f.isFullPrior) return;
    const sched = (fileRetroByLocation.get(f.fileName) || []).find(s => spSameCoverage(s.coverage, f.coveragePart));
    if (!sched) return;
    const dates = Array.from(new Set(sched.rows.map(r => r.date)));
    /* The date the schedule replaced, said out loud. Hanover's EBL
     * declarations (p.4) and endorsement schedule (p.71) state 09/20/2003;
     * 970-0015 replaces it by location. Reporting only the location dates
     * left a broker checking the declarations with a date the report never
     * mentioned. A date that is also a location date replaced nothing. */
    const replaced = new Map();
    (fileAnchors.get(f.fileName) || [])
      .filter(a => a.kind === 'date' && a.coverage && spSameCoverage(a.coverage, f.coveragePart) && !dates.includes(a.normalized))
      .forEach(a => {
        const r = replaced.get(a.normalized) || { date: a.normalized, pages: [] };
        if (a.page && !r.pages.includes(a.page)) r.pages.push(a.page);
        replaced.set(a.normalized, r);
      });
    // Every page that states it, not the first: the EBL date is on the
    // declarations and again in the endorsement's own schedule.
    const srcFile = (pdfTexts || []).find(x => x && (x.fileName || 'Unknown File') === f.fileName);
    replaced.forEach(r => {
      ((srcFile && srcFile.pages) || []).forEach(pg => {
        const t = String(pg.text || '');
        if (pg.page && t.includes(r.date) && /retroactiv/i.test(t) && !r.pages.includes(pg.page)) r.pages.push(pg.page);
      });
    });
    f.retroReplaced = Array.from(replaced.values()).map(r => Object.assign(r, { pages: r.pages.sort((x, y) => x - y) }));
    f.retroByLocation = sched.rows;
    f.retroDate = dates.join(' / ');
    f.retroLabel = dates.length > 1 ? 'Retroactive Date by location' : 'Retroactive Date';
    f.retroPage = sched.page || f.retroPage || null;
  });

  /* The Named Insured Schedule applies to every claims-made part in its
   * policy. It sits beside a location schedule or a single date rather than
   * replacing either: which one governs a claim depends on who the claim is
   * against and where it arose, which is the carrier's call, not the
   * reader's. Shown in full, and kept out of the alignment comparison. */
  out.byCoverage.forEach(f => {
    if (f.trigger !== 'Claims-Made') return;
    const ni = fileRetroByNamedInsured.get(f.fileName);
    if (ni) { f.retroByNamedInsured = ni.rows; f.retroByNamedInsuredPage = ni.page; }
  });

  /* "Retroactive Date: N/A" on a claims-made part is the policy answering
   * the question — no retroactive cut-off — not a date. It printed as the
   * internal token NOT_APPLICABLE and, compared as if it were a date, would
   * raise a misalignment against every real retro date in the program. The
   * Continuity Date stated beside it is carried separately so it is shown. */
  out.byCoverage.forEach(f => {
    if ((f.trigger === 'Discovery' || f.trigger === 'Loss Sustained') && fileDiscovery.get(f.fileName)) {
      f.discoveryPeriod = fileDiscovery.get(f.fileName);
    }
  });

  /* An occurrence umbrella's claims-made coverages, each compared with the
   * underlying part's own retro date in another policy. A later umbrella
   * date than the underlying leaves acts in between covered below and not
   * above. */
  out.byCoverage.forEach(f => {
    const sr = fileSubRetro.get(f.fileName);
    if (!sr || f.coveragePart !== 'Excess / Umbrella Liability') return;
    f.retroBySubCoverage = sr.map(r => {
      const under = out.byCoverage.find(u => u !== f && u.fileName !== f.fileName && u.retroDate && !u.retroByLocation
        && !/continuity/i.test(u.retroLabel || '') && spSameCoverage(u.coveragePart, r.coverage));
      return Object.assign({}, r, under ? { underlying: { date: under.retroDate, part: under.coveragePart } } : {});
    });
  });

  out.byCoverage.forEach(f => {
    if (f.trigger !== 'Claims-Made') return;
    if (f.retroDate && /^not[\s_]applicable/i.test(f.retroDate)) {
      f.retroNotApplicable = true;
      f.retroDate = 'Not applicable (stated)';
    }
    /* NAMING CONVENTION: "Continuity Date" is an alternative name for the
     * Retro Date, and is reported as the retro date finding. Cincinnati's D&O
     * and EPL declarations print "Retroactive Date: N/A" beside "Continuity
     * Date: 11-16-2005"; the N/A field does not leave the part without a retro
     * date — the Continuity Date is it. A part that states a real Retroactive
     * Date keeps that date. */
    if (!/continuity/i.test(f.retroLabel || '')) {
      const c = (fileContinuityDates.get(f.fileName) || []).find(x => x.coverage && spSameCoverage(x.coverage, f.coveragePart));
      if (c && (f.retroNotApplicable || !f.retroDate) && !f.retroByLocation && !f.isFullPrior) {
        f.retroDate = c.normalized || c.value;
        f.retroLabel = 'Continuity Date';
        f.retroPage = c.page || f.retroPage || null;
        f.retroFieldNA = !!f.retroNotApplicable;
        f.retroNotApplicable = false;
      } else if (c) {
        f.continuity = Object.assign({}, c, { knowledgeOnly: !!fileContinuityKnowledge.get(f.fileName) });
      }
    }
  });

  out.byCoverage.forEach(f => {
    const ck = fileContinuityKnowledge.get(f.fileName);
    if (ck && f.retroDate && !f.isFullPrior && /continuity/i.test(f.retroLabel || '')) {
      f.retroKnowledgeOnly = ck;
    }
  });

  // Roll up: any claims-made? any occurrence? Multi-policy alignment check.
  out.hasAnyClaimsMade = out.byCoverage.some(f => f.trigger === 'Claims-Made');
  out.hasAnyOccurrence = out.byCoverage.some(f => f.trigger === 'Occurrence');

  // v2.23.3: Compare on NORMALIZED retro date (08/24/25 == 08/24/2025).
  // The displayed retroDate is whatever was actually extracted from each policy;
  // the alignment check uses the canonical form so format variations don't
  // false-positive as misalignment.
  // A part with retro dates by location is compared on its SET of dates: two
  // parts scheduling the same locations at the same dates are aligned.
  /* Rule A (broker team): a date stated as "Continuity Date" is that
   * coverage's retro date but is never compared here — different carriers'
   * continuity dates are expected to differ. Excluded by its label, not only
   * when the wording makes it a knowledge cut-off: Aspen View's Beazley
   * continuity date 07/01/2020 was still raising the banner. */
  const _spCompared = f => f.trigger === 'Claims-Made' && f.retroDate && !f.isFullPrior && !f.retroKnowledgeOnly
    && !f.retroNotApplicable && !/continuity/i.test(f.retroLabel || '');
  const cmRetrosNormalized = out.byCoverage
    .filter(_spCompared)
    .map(f => f.retroByLocation
      ? Array.from(new Set(f.retroByLocation.map(r => normalizeRetroDate(r.date)))).sort().join(' + ')
      : normalizeRetroDate(f.retroDate));
  // The claims-made coverages inside an occurrence umbrella are compared too.
  out.byCoverage.forEach(f => (f.retroBySubCoverage || []).forEach(r => cmRetrosNormalized.push(normalizeRetroDate(r.date))));
  const uniqueNormalized = Array.from(new Set(cmRetrosNormalized));
  // Show the human-readable variants in the misalignment banner if there really is one
  out.retroDates = Array.from(new Set(
    out.byCoverage
      .filter(_spCompared)
      .map(f => f.retroByLocation && f.retroByLocation.length > 1 ? f.retroDate + ' (by location)' : f.retroDate)
      .concat([].concat(...out.byCoverage.map(f => (f.retroBySubCoverage || []).map(r => r.date + ' (umbrella)'))))
  ));
  if (uniqueNormalized.length >= 2) {
    out.misalignmentFlag = true;
  }

  return out;
}

function extractEndorsements(pdfTexts) {
  const result = { endorsements: [], byCoverage: {}, byTag: {}, totalDetected: 0, truncated: false };
  if (!pdfTexts || pdfTexts.length === 0) return result;

  // ----- Helpers -----

  // Form number patterns. ISO uses 2-letter prefix + digits (CG 21 46, CA 99 03).
  // Carriers add their own prefixes: PE GL 625 TX (Munich Re Wright Specialty),
  // PHLY 100 (Philadelphia), MEL 1234 (Markel), etc.
  const formNumberPatterns = [
    /\b([A-Z]{2,4}\s+(?:[A-Z]{1,3}\s+)?\d{2,4}(?:\s+\d{2})?(?:\s+[A-Z]{2})?(?:\s+\d{2})?)\b/g,
    /\b([A-Z]{2,5}\s*\(\d{2}\/\d{2}\))\b/g,
    // HYPHENATED carrier forms. Zurich numbers its endorsements
    // "U-BMC-104-C CW (10/20)", and none of the patterns above admit a hyphen,
    // so every endorsement on the Blanket Accident policy came back with no
    // form number at all and the form string landed in the TITLE slot instead.
    // Carrier-proprietary numbering is the rule outside ISO lines, and the
    // next verticals will bring more of it.
    /\b([A-Z]{1,4}-[A-Z0-9]{2,8}(?:-[A-Z0-9]{1,4})+(?:\s+[A-Z]{2})?(?:\s*\(\d{2}\/\d{2}\))?)/g,
    /Form\s+(?:Number\s+)?:?\s*([A-Z][A-Z0-9\s\-]+\d)/i
  ];

  function extractFormNumber(text, footerText) {
    // Form numbers commonly appear in the footer or top-right of the page.
    // Try the footer first (last 200 chars), then the top of the page.
    const candidates = [];
    const sources = [footerText, text.substring(0, 300), text.substring(Math.max(0, text.length - 300))];
    for (const src of sources) {
      if (!src) continue;
      for (const pat of formNumberPatterns) {
        /* AN INFINITE LOOP, AND IT FROZE A BROWSER TAB.
         *
         * `while ((m = re.exec(src)) !== null)` only terminates for a GLOBAL
         * regex: exec advances lastIndex, and eventually returns null. A
         * non-global one restarts from zero every time and returns the same
         * match forever.
         *
         * Three of the four patterns here carry /g. The fourth carries /i and
         * nothing else, and was copied into the loop with `new RegExp(source,
         * flags)` — which faithfully preserved the missing flag.
         *
         * It needs one page whose text matches it. On Tulsa Honor Academy's
         * package policy, page 80 of 117 — a 736 character endorsement — says
         * "GENERAL LIABILITY COVERAGE FORM" followed by "The following is
         * added to Paragraph 2", and /Form\s+...([A-Z][A-Z0-9\s\-]+\d)/i
         * matches it, case-insensitively, right down to the trailing 2. The
         * tab then spins at full CPU and never returns. Every other page in
         * four policies passes through; this one does not, which is why three
         * of the four documents behaved perfectly.
         *
         * The flag is forced here rather than in the table, so the invariant
         * lives where the loop that depends on it is.
         */
        const flags = pat.flags.indexOf('g') >= 0 ? pat.flags : pat.flags + 'g';
        const re = new RegExp(pat.source, flags);
        let m;
        while ((m = re.exec(src)) !== null) {
          // A zero-length match does not advance lastIndex either.
          if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
          const candidate = m[1].trim().replace(/\s+/g, ' ');
          // Filter out things that look like dates or non-form patterns
          if (/^\d{2,4}\s+\d{2,4}$/.test(candidate)) continue;
          if (candidate.length < 4 || candidate.length > 30) continue;
          // Reject pure year patterns
          if (/^\d{4}$/.test(candidate.replace(/\s+/g, ''))) continue;
          // Must have at least one letter
          if (!/[A-Z]/.test(candidate)) continue;
          candidates.push(candidate);
        }
      }
      if (candidates.length > 0) break;
    }
    return candidates[0] || null;
  }

  function extractTitle(text) {
    // Endorsement titles are typically uppercase, near the top of the page.
    // v2.21.3: Tightened title extraction to avoid grabbing body-text fragments.
    // Strategy: find the FIRST line of text that's a centered ALL-CAPS title
    // (12-100 chars, most-uppercase, not starting with "In consideration",
    // "This endorsement", or other body-text triggers).
    const head = text.substring(0, 1500);

    // Helper: validate a candidate title line
    function isValidTitle(s) {
      if (!s) return false;
      s = s.trim();
      if (s.length < 8 || s.length > 120) return false;
      // Reject if the candidate starts with body-text phrasing
      if (/^(?:in\s+consideration|this\s+endorsement|with\s+regard|subject\s+to|notwithstanding|this\s+coverage|except|provided|whereas|in\s+respect|as\s+used|the\s+following|effective\s+date|named\s+insured|policy\s+number|policyholder|attached\s+to)/i.test(s)) return false;
      // v2.21.5: Reject coverage-part HEADINGS (these are the form's section header, not
      // the endorsement's actual title). e.g. "COMMERCIAL EXCESS LIABILITY COVERAGE PART"
      // v2.21.6: Loosened — reject ANY line ending in "COVERAGE PART" or "COVERAGE FORM".
      if (/coverage\s+part\s*$/i.test(s)) return false;
      if (/coverage\s+form\s*$/i.test(s)) return false;
      // Also reject pure section headers like "DECLARATIONS PAGE", "SCHEDULE", "ENDORSEMENT"
      if (/^(?:declarations(?:\s+page)?|schedule|endorsement|policy\s+number|named\s+insured)\s*$/i.test(s)) return false;
      // A line that ENDS in an endorsement noun is a title whatever its case.
      //
      // The uppercase test below is a good proxy for ISO paper, where titles
      // are set in caps. Zurich sets them in title case - "Administrative
      // Change Endorsement", "Civil Union Endorsement" - so every endorsement
      // on the Blanket Accident policy was rejected as a title and the page
      // fell back to the form number, which is why that policy reported
      // "Untitled Endorsement". Carriers outside the ISO lines set titles
      // however they like, and the next verticals will bring more of them.
      const namesItself = /\b(?:endorsement|exclusion|amendatory|amendment|rider|schedule\s+of\s+\w+|notice)\s*$/i.test(s);
      if (!namesItself) {
        // Reject if it has too much lowercase (real titles are mostly uppercase)
        const upperCount = (s.match(/[A-Z]/g) || []).length;
        const letterCount = (s.match(/[A-Za-z]/g) || []).length;
        if (letterCount > 0 && (upperCount / letterCount) < 0.7) return false;
      }
      // Reject obvious form numbers / page numbers
      if (/^[A-Z]{2,4}\s+\d/.test(s) && s.length < 20) return false;
      /* AN ENDORSEMENT'S TITLE IS NOT A LIST OF INSURERS.
       *
       * A subscription placement carries an Assignment endorsement whose body
       * is the names of the carriers whose consent is required:
       *
       *     THIS ENDORSEMENT CHANGES THE POLICY. PLEASE READ IT CAREFULLY.
       *     ASSIGNMENT
       *     This Policy ... shall not be assigned ... without the prior
       *     written consent of the respective insurer:
       *     PALMS SPECIALTY INSURANCE COMPANY, INC OR PALMS INSURANCE
       *     COMPANY, LIMITED
       *     SUTTON SPECIALTY INSURANCE COMPANY
       *
       * "ASSIGNMENT" was rejected by the single-word rule below, the walk
       * carried on to the next line in capitals, and the report listed the
       * subscribing carriers as an endorsement of this policy. An endorsement
       * that genuinely names a carrier also names itself — "... AMENDATORY
       * ENDORSEMENT" — and is kept by the namesItself test above. */
      if (!namesItself && /\b(?:insurance\s+compan|indemnity\s+compan|assurance\s+compan|underwriters\s+at\s+lloyd|compan(?:y|ies),?\s*(?:inc|ltd|limited))/i.test(s)) return false;
      /* A ONE-WORD TITLE IS STILL A TITLE.
       *
       * "ASSIGNMENT", "SUBROGATION", "CANCELLATION" are whole endorsements,
       * and rejecting them did not merely lose the name — it sent the reader
       * looking further down the page and it found something worse. A single
       * word in capitals, long enough not to be an abbreviation, is accepted;
       * the section-header list above already removes SCHEDULE, ENDORSEMENT
       * and DECLARATIONS. */
      if (!s.includes(' ')) return /^[A-Z][A-Z'\-]{5,}$/.test(s);
      return true;
    }

    // Pattern A0: the endorsement names itself on its FIRST line.
    //
    // Every pattern below looks for the title AFTER the "please read it
    // carefully" notice, which is where ISO paper puts it. Zurich puts it
    // FIRST, above the carrier's own letterhead:
    //
    //     Administrative Change Endorsement
    //     ZURICH(r)
    //     Zurich American Insurance Company
    //     ...
    //     THIS ENDORSEMENT CHANGES THE POLICY. PLEASE READ IT CAREFULLY.
    //
    // so the Blanket Accident policy reported "Untitled Endorsement" three
    // times over while its titles sat on line one. Only a line that names
    // itself an endorsement is taken this way, so a stray heading cannot
    // claim the slot.
    {
      const first = head.split(/\n/).map(l => l.trim()).filter(Boolean).slice(0, 3);
      for (const line of first) {
        if (!/\b(?:endorsement|exclusion|amendatory|amendment|rider)\s*$/i.test(line)) continue;
        if (isValidTitle(line)) return line.replace(/\s+/g, ' ');
      }
    }

    // Pattern A: After "PLEASE READ IT/THIS CAREFULLY." — the title is the next line
    // (or whitespace-separated text in PDF.js extraction)
    const mA = head.match(/please\s+read\s+(?:it|this(?:\s+endorsement)?)\s+carefully\.?\s+([A-Z][A-Z\s,&'\-/()0-9\u2013\u2014]{8,120}?)(?=\s+(?:THIS\s+ENDORSEMENT|This\s+endorsement|PLEASE\s+READ|This\s+coverage|SCHEDULE|Coverage|Effective|In\s+consideration|A\.|B\.|1\.|\n))/i);
    if (mA && isValidTitle(mA[1])) return mA[1].trim().replace(/\s+/g, ' ');

    // Pattern A2: "THIS ENDORSEMENT CHANGES THE POLICY. PLEASE READ IT CAREFULLY. <TITLE>"
    // (single-line variant with title immediately following the dot)
    const mA2 = head.match(/this\s+endorsement\s+(?:changes|modifies|provides)\s+(?:claims[\-\s]?made\s+coverage|the\s+policy)\.\s*(?:please\s+read[^.]+\.\s*)?([A-Z][A-Z\s,&'\-/()0-9\u2013\u2014]{10,120}?)(?=\s+(?:THIS\s+|This\s+|PLEASE\s+|SCHEDULE|Coverage|Effective|In\s+consideration|\n))/i);
    if (mA2 && isValidTitle(mA2[1])) return mA2[1].trim().replace(/\s+/g, ' ');

    // Pattern B: Walk the first 30 lines, return first valid ALL-CAPS title line
    const lines = head.split(/\n/).map(l => l.trim()).filter(l => l.length > 0);
    for (let i = 0; i < Math.min(lines.length, 30); i++) {
      const line = lines[i];
      // Match a line that looks like a title (mostly uppercase, no body-text triggers)
      if (/^[A-Z][A-Z\s,&'\-/()0-9\u2013\u2014]{8,120}$/.test(line) && isValidTitle(line)) {
        // If a continuation line follows that's also uppercase, join them
        let title = line;
        if (i + 1 < lines.length) {
          const next = lines[i + 1];
          if (/^[A-Z][A-Z\s,&'\-/()0-9\u2013\u2014]{4,80}$/.test(next) && isValidTitle(next) && (title.length + next.length) < 160) {
            title += ' ' + next;
          }
        }
        return title.trim().replace(/\s+/g, ' ');
      }
    }

    // v2.21.6: Pattern C removed. Previously returned "COMMERCIAL X COVERAGE PART" as the
    // endorsement title, but those strings are the form's section heading, not the
    // endorsement's actual title. Letting these through caused 5 different exclusion
    // endorsements on the Excess policy (CX 21 36, CX 21 56, CXE 01, CXE 02, CX 21 30)
    // to all be labeled "COMMERCIAL EXCESS LIABILITY COVERAGE PART" — four of them
    // tagged CRITICAL and surfaced in the banner with no useful name. By returning null
    // here, these endorsements fall through to the form-number lookup table, which has
    // human-readable titles (Asbestos, Lead, Nuclear, TRIA, etc.).
    return null;
  }

  // v2.21.5: Form-number lookup — maps common ISO and carrier-specific form numbers
  // to their human-readable titles. Used as a fallback when title extraction fails.
  // Only includes high-confidence, well-known forms. Adding more is safe and additive.
  const knownFormTitles = {
    // ISO Workers Compensation
    'WC 00 04 22': 'Waiver of Our Right to Recover from Others',
    'WC 00 04 25': 'Voluntary Compensation and Employers Liability',
    'WC 00 03 13': 'Waiver of Our Right to Recover from Others (Specific)',
    // ISO Commercial General Liability
    'CG 21 46': 'Abuse or Molestation Exclusion',
    'CG 21 47': 'Employment-Related Practices Exclusion',
    'CG 21 70': 'Cap on Losses From Certified Acts of Terrorism',
    'CG 21 60': 'Texas Changes — Amendatory Endorsement',
    'CG 21 73': 'Exclusion of Certified Acts of Terrorism',
    'CG 20 10': 'Additional Insured — Owners, Lessees or Contractors',
    'CG 20 26': 'Additional Insured — Designated Person or Organization',
    'CG 20 37': 'Additional Insured — Owners, Lessees, or Contractors (Completed Operations)',
    'CG 24 04': 'Waiver of Transfer of Rights of Recovery Against Others',
    // ISO Commercial Auto
    'CA 99 03': 'Auto Medical Payments Coverage',
    'CA 23 84': 'Drive Other Car Coverage',
    'CA 99 33': 'Employees as Insured',
    'CA 04 49': 'Texas Uninsured/Underinsured Motorists',
    // ISO Commercial Property
    'CP 10 30': 'Causes of Loss — Special Form',
    'CP 10 32': 'Water Exclusion',
    'CP 04 05': 'Ordinance or Law Coverage',
    // Common amendatory
    'IL 02 71': 'Texas Changes — Cancellation and Nonrenewal',
    'IL 09 53': 'Exclusion of Certified Acts of Terrorism',
    'IL 09 85': 'Disclosure Pursuant to Terrorism Risk Insurance Act',
    // ISO Commercial Excess Liability (CX 21 ## series — common exclusions)
    'CX 21 01': 'Nuclear Energy Liability Exclusion',
    'CX 21 30': 'Asbestos Exclusion',
    'CX 21 36': 'Silica or Silica-Related Dust Exclusion',
    'CX 21 56': 'Lead Liability Exclusion',
    'CX 21 70': 'Cap on Losses From Certified Acts of Terrorism (Excess)',
    'CX 21 73': 'Exclusion of Certified Acts of Terrorism (Excess)',
    'CX 02 13': 'Texas Changes — Excess Liability Amendatory',
    // Wright Specialty / Munich Re carrier-specific
    'PE GL 620': 'Honor Roll Elite General Liability Enhancement',
    'PE GL 624 TX': 'Texas Crisis Management and Public Relations Expense',
    'PE GL 625 TX': 'Texas Counseling Professional Liability Coverage',
    'PE GL 633 TX': 'Texas Changes — General Liability Amendatory',
    'PE EL 303': 'Consent to Settle Condition',
    'PE EL 500': 'ELL Carve-Back / Restoration of Coverage',
    'PE EL 306': 'ELL Sub-Limit Endorsement',
    'PE CO 300': 'Common Policy Conditions — Cancellation and Nonrenewal Notice'
  };
  function lookupFormTitle(formNumber) {
    if (!formNumber) return null;
    // Try exact match first
    if (knownFormTitles[formNumber]) return knownFormTitles[formNumber];
    // Try without trailing edition date (e.g. "CG 21 46 07 98" → "CG 21 46")
    const base = formNumber.replace(/\s+\d{2}\s+\d{2}\s*$/, '').trim();
    if (knownFormTitles[base]) return knownFormTitles[base];
    // Try first 3 parts only (e.g. "WC 00 04 22 01 24" → "WC 00 04 22")
    const parts = formNumber.split(/\s+/);
    if (parts.length >= 4) {
      const fourPart = parts.slice(0, 4).join(' ');
      if (knownFormTitles[fourPart]) return knownFormTitles[fourPart];
    }
    if (parts.length >= 3) {
      const threePart = parts.slice(0, 3).join(' ');
      if (knownFormTitles[threePart]) return knownFormTitles[threePart];
    }
    return null;
  }

  // v2.21: Form-number prefix → coverage inference for endorsements that
  // PDF.js extracts without enough heading context.
  function coverageFromFormNumber(formNumber) {
    if (!formNumber) return null;
    const f = formNumber.toUpperCase();
    // ISO standard prefixes
    if (/^CG\s/.test(f)) return 'General Liability';      // Commercial General Liability
    if (/^CA\s/.test(f)) return 'Commercial Auto';
    if (/^CP\s/.test(f)) return 'Commercial Property';
    if (/^CR\s/.test(f)) return 'Crime/Fidelity';
    if (/^CM\s/.test(f)) return 'Inland Marine';
    if (/^CU\s/.test(f)) return 'Umbrella/Excess';        // Commercial Umbrella
    if (/^EB\s/.test(f)) return 'Equipment Breakdown';
    if (/^WC\s/.test(f)) return 'Workers Compensation';
    if (/^IL\s/.test(f)) return null;                     // IL = Common policy conditions
    // Wright Specialty / Munich Re prefixes
    if (/^PE\s+GL\b/.test(f)) return 'General Liability';
    if (/^PE\s+EL\b/.test(f)) return 'Educators Legal Liability';
    if (/^PE\s+EB\b/.test(f)) return 'Employee Benefits Liability';
    if (/^PE\s+CO\b/.test(f)) return null;                 // Common policy conditions
    if (/^PE\s+CR\b/.test(f)) return 'Crime/Fidelity';
    if (/^PE\s+IM\b/.test(f)) return 'Inland Marine';
    if (/^PE\s+PR\b/.test(f)) return 'Commercial Property';
    if (/^PE\s+UM\b/.test(f)) return 'Umbrella/Excess';
    return null;
  }

  // Coverage-part identifier — which coverage line does this endorsement touch?
  // Looks for phrases like "GENERAL LIABILITY COVERAGE PART" near the top of the endorsement.
  function identifyCoveragePart(text) {
    const head = text.substring(0, 1500);
    const checks = [
      [/general\s+liability\s+coverage\s+part|commercial\s+general\s+liability/i, 'General Liability'],
      [/sexual\s+abuse|abuse\s+(?:or|and|&)\s+molestation|molestation\s+liability/i, 'Sexual Abuse/Molestation'],
      [/professional\s+liability/i, 'Professional Liability'],
      [/educators?\s+legal\s+liability/i, 'Educators Legal Liability'],
      [/employee\s+benefits?\s+liability/i, 'Employee Benefits Liability'],
      [/employment\s+practices/i, 'EPLI'],
      [/cyber|network\s+security|privacy\s+liability|data\s+breach/i, 'Cyber Liability'],
      [/(?:directors?\s+(?:and|&)\s+officers?|management\s+liability|d&o)\s+(?:coverage|liability)/i, 'D&O'],
      [/commercial\s+property|property\s+coverage\s+part/i, 'Commercial Property'],
      [/(?:commercial|business)\s+auto|automobile\s+(?:coverage|liability)/i, 'Commercial Auto'],
      [/workers?\s+compensation|employer'?s?\s+liability/i, 'Workers Compensation'],
      [/umbrella|excess\s+liability|follow\s+form\s+excess/i, 'Umbrella/Excess'],
      [/equipment\s+breakdown|boiler.{0,5}machinery/i, 'Equipment Breakdown'],
      [/crime|fidelity|employee\s+theft|forgery/i, 'Crime/Fidelity'],
      [/inland\s+marine/i, 'Inland Marine']
    ];
    for (const [re, label] of checks) {
      if (re.test(head)) return label;
    }
    return null;
  }

  // ----- Classifier -----
  // Tag each endorsement by what it does. Order matters — more-specific patterns first.
  function classifyEndorsement(text, title) {
    const t = text.toLowerCase();
    const head = t.substring(0, 2000);

    // THE TITLE WINS WHEN IT NAMES WHAT THE ENDORSEMENT DOES.
    //
    // Zurich's "SANCTIONS EXCLUSION ENDORSEMENT" was tagged ADDITIONAL_INSURED
    // because its body says "Insured" and "schedule" in the ordinary course,
    // and the body rules run in order without ever asking what the endorsement
    // calls itself. An endorsement that names itself an exclusion is an
    // exclusion, whatever words appear beneath it.
    const ti = String(title || '').toLowerCase();
    if (/\bexclusion\b/.test(ti)) {
      return { tag: 'EXCLUSION_ADD', impact: 'Adds or broadens an exclusion', severity: 'HIGH' };
    }
    if (/\bwaiver\s+of\s+subrogation\b/.test(ti)) {
      return { tag: 'WAIVER', impact: 'Waives the carrier\u2019s right of recovery', severity: 'INFO' };
    }
    if (/\badditional\s+insured\b/.test(ti)) {
      return { tag: 'ADDITIONAL_INSURED', impact: 'Adds named entities as additional insureds', severity: 'INFO' };
    }

    // ADDITIONAL INSURED — named entities granted insured status
    if (/\badditional\s+insureds?\b/.test(head) && (/\bschedule\b/.test(head) || /\bdesignated\s+person\b/.test(head))) {
      return { tag: 'ADDITIONAL_INSURED', impact: 'Adds named entities as additional insureds', severity: 'INFO' };
    }

    // WAIVER — subrogation, transfer of rights
    if (/waiver\s+of\s+(?:transfer\s+of\s+rights|subrogation|our\s+right\s+to\s+recover)/.test(head)) {
      return { tag: 'WAIVER', impact: 'Waives carrier rights of subrogation against scheduled parties', severity: 'INFO' };
    }

    // STATE AMENDATORY
    if (/\b(?:state|texas|california|new\s+york|florida|illinois|oklahoma|louisiana|massachusetts|new\s+jersey|pennsylvania|georgia|virginia|north\s+carolina|south\s+carolina)\s+(?:changes|amendatory|amendment)\b/.test(head)
        || /\b(?:amendatory|amendment)\s+(?:endorsement|\u2014\s+)?(?:texas|california|new\s+york|florida|illinois|oklahoma|louisiana)\b/.test(head)
        || (/changes\s+\u2013\s+[a-z\s]+/i.test(text.substring(0, 200)) && /amend/.test(head))) {
      return { tag: 'AMENDATORY', impact: 'State-mandated form changes (cancellation, fraud, venue, regulatory wording)', severity: 'NOTICE' };
    }

    // NOTICE — cancellation/non-renewal notice provisions
    if (/(?:notice\s+of\s+cancellation|cancellation\s+(?:notice|provisions?)|non[\-\s]renewal\s+notice|advance\s+notice)/.test(head)
        && !/each\s+act\s+limit|aggregate\s+limit|\$\s*[\d,]+/.test(head.substring(0, 1500))) {
      return { tag: 'NOTICE', impact: 'Modifies cancellation/non-renewal notice requirements', severity: 'NOTICE' };
    }

    // CARVE-BACK — deletes or modifies an existing exclusion to RESTORE coverage
    if ((/(?:exclusion\s+(?:[a-z]\.|[\u2014\-]\s*\w+)\s+(?:is|are)\s+deleted|do\s+not\s+apply|shall\s+not\s+apply)/.test(head)
         || /carve[\-\s]?back/.test(head))
        && /(?:abuse|molestation|sexual|professional|criminal|intentional|breach\s+of\s+contract|punitive)/.test(head)) {
      return { tag: 'CARVE_BACK', impact: 'Restores coverage that would otherwise be excluded (carve-back)', severity: 'INFO' };
    }

    // EXCLUSION ADD — introduces a new exclusion
    if (/(?:absolute|specific|total)\s+(?:exclusion|exclusion\s+of)/.test(head)
        || (/\bthe\s+following\s+exclusion\s+is\s+added\b/.test(head))
        || (/this\s+insurance\s+does\s+not\s+apply\s+to/.test(head) && !/coverage|covered\s+claim|covered\s+occurrence/.test(head.substring(0, 800)))) {
      const sevTriggers = /(?:abuse|molestation|sexual|cyber|fungi|asbestos|lead|silica|mold|war|terrorism|professional)/.test(head);
      return { tag: 'EXCLUSION_ADD', impact: 'Adds an exclusion that removes coverage', severity: sevTriggers ? 'CRITICAL' : 'WARN' };
    }

    // GRANT — adds a new coverage with its own limit schedule
    const dollarMatches = (text.match(/\$\s*[\d,]{4,}/g) || []).length;
    if (dollarMatches >= 2 && /\b(?:schedule|limits?\s+of\s+(?:insurance|liability))\b/.test(head)
        && (/\beach\s+(?:act|claim|wrongful\s+act|occurrence|incident)\b/.test(head) || /\baggregate\s+limit\b/.test(head))) {
      return { tag: 'GRANT', impact: 'Adds new coverage line with its own limit structure', severity: 'INFO' };
    }

    // SUBLIMIT — dollar amounts inside an existing coverage (typically smaller than headline limit)
    if (dollarMatches >= 1 && /(?:sub[\-\s]?limit|sublimit|limit\s+of\s+insurance\s+for|maximum\s+(?:amount|limit))/.test(head)) {
      return { tag: 'SUBLIMIT', impact: 'Adds sub-limit within an existing coverage line', severity: 'INFO' };
    }

    // SUBLIMIT — catch-all for endorsements that have $ amounts but don't fit GRANT or other tags
    if (dollarMatches >= 1 && (title || '').match(/(?:enhancement|enhanced|extra|broadening|extension)/i)) {
      return { tag: 'SUBLIMIT', impact: 'Adds sub-limits / coverage enhancements within an existing coverage', severity: 'INFO' };
    }

    return { tag: 'OTHER', impact: 'Endorsement modifies policy \u2014 broker review recommended', severity: 'NOTICE' };
  }

  // ----- Detection — walk every page -----

  /* A BUDGET, BECAUSE THIS IS WHERE A BROWSER TAB STOPPED.
   *
   * A user's console ended at "[perf] policy terms: 4 ms" and never printed
   * the endorsements line. Every other stage on that program is measured and
   * fast — detection 202 ms over 243 pages, limits 566 ms, the full review a
   * second — so the run reached this function and did not leave it.
   *
   * Reproducing it needed the one document not available here, and reading the
   * code did not find it: the form-number patterns were tested against
   * adversarial input and none backtracks, and the dedupe is a Set. So rather
   * than guess at a cause a fourth time, this bounds the damage. Endorsements
   * are ONE section of the report; an unbounded scan of them is not worth a
   * tab that never comes back.
   *
   * The budget is per call, checked per page, and says what it dropped. A
   * report missing some endorsements, and saying so, beats no report.
   */
  const _endT0 = Date.now();
  const _END_BUDGET_MS = 6000;
  let _endStopped = 0;

  pdfTexts.forEach(file => {
    if (!file.pages || !Array.isArray(file.pages)) return;
    file.pages.forEach(p => {
      if (!p.text) return;
      if (Date.now() - _endT0 > _END_BUDGET_MS) { _endStopped++; return; }
      // Normalised, like every other reader. LlamaParse sets an endorsement
      // title as a markdown heading - "# **Administrative Change
      // Endorsement**" - and the raw line starts with furniture the title
      // validator rejects, so three real endorsements on the Blanket Accident
      // policy reported as "Untitled Endorsement".
      const text = spNorm(p.text);
      const head = text.substring(0, 600);

      // Endorsement detection signature — is this an endorsement page?
      const isEndorsement =
        /this\s+endorsement\s+(?:changes|modifies)\s+(?:the\s+)?policy/i.test(head)
        || /this\s+endorsement\s+is\s+attached\s+to/i.test(head)
        || /effective\s+date\s+of\s+this\s+endorsement/i.test(head)
        || /\bendorsement\s+(?:no\.|number)\s*\d/i.test(head);
      if (!isEndorsement) return;

      // Skip obviously-noise pages
      if (text.length < 100) return;

      // Footer with form number is typically the last 250 chars
      const footerText = text.substring(Math.max(0, text.length - 350));
      const formNumber = extractFormNumber(text, footerText);
      // v2.21.5: Title resolution — try text extraction first, then form-number lookup,
      // then fall back to 'Untitled Endorsement'. The lookup table rescues common ISO
      // forms (WC 00 04 22, CG 21 46, etc.) where PDF.js extraction misses the heading.
      let title = extractTitle(text);
      if (!title || title === 'Untitled Endorsement') {
        const lookup = lookupFormTitle(formNumber);
        if (lookup) title = lookup;
      }
      if (!title) title = 'Untitled Endorsement';
      // v2.21: Coverage attribution — try text-based first, fall back to form-number prefix
      let coveragePart = identifyCoveragePart(text);
      if (!coveragePart && formNumber) {
        coveragePart = coverageFromFormNumber(formNumber);
      }
      const classification = classifyEndorsement(text, title);

      // v2.21.6: Demote routine boilerplate exclusions on Excess policies. Lead, asbestos,
      // silica, and nuclear exclusions are universal on excess/umbrella forms (every carrier
      // includes them; they are not negotiable in this market). Per Dayna: these are not
      // meaningful findings for a charter school review — they would generate noise on every
      // single policy and bury the actual issues. Drop their severity to INFO and route
      // them out of the high-signal table into the routine appendix.
      const routineExcessExclusions = [
        'CX 21 01', 'CX 21 30', 'CX 21 36', 'CX 21 56', // ISO Excess: Nuclear, Asbestos, Silica, Lead
        'CXE 01', 'CXE 02'                                 // Wright/Munich Re carrier-specific (per user, also deal with lead/asbestos/silica/nuclear)
      ];
      const baseForm = formNumber ? formNumber.replace(/\s+\d{2}\s+\d{2}\s*$/, '').trim() : '';
      const baseFormShort = formNumber ? formNumber.split(/\s+/).slice(0, 3).join(' ') : '';
      const baseFormShorter = formNumber ? formNumber.split(/\s+/).slice(0, 2).join(' ') : '';
      const isRoutineExcess = routineExcessExclusions.some(rf =>
        rf === baseForm || rf === baseFormShort || rf === baseFormShorter
      );
      if (isRoutineExcess) {
        classification.tag = 'OTHER';
        classification.severity = 'INFO';
        classification.impact = 'Routine excess/umbrella boilerplate exclusion (lead/asbestos/silica/nuclear) — universal on excess forms, not actionable';
        classification._routine = true;  // Force into routine appendix, not the high-signal table
      }

      const endorsement = {
        fileName: file.fileName || 'unknown',
        page: p.page || 0,
        title,
        formNumber: formNumber || null,
        coveragePart: coveragePart || 'Unidentified',
        tag: classification.tag,
        impact: classification.impact,
        severity: classification.severity,
        text: text.substring(0, 1200), // for downstream analysis (carve-back checker)
        _retroDate: null,  // v2.21 hook — reserved for retro-date logic in v2.23
        _routine: classification._routine === true  // v2.21.6 — routes to appendix, hides from high-signal table
      };
      result.endorsements.push(endorsement);
      // Group by coverage and tag for easy lookup
      const cov = endorsement.coveragePart;
      if (!result.byCoverage[cov]) result.byCoverage[cov] = [];
      result.byCoverage[cov].push(endorsement);
      const tag = endorsement.tag;
      if (!result.byTag[tag]) result.byTag[tag] = [];
      result.byTag[tag].push(endorsement);
    });
  });

  // De-duplicate — same form number appearing on consecutive pages (multi-page endorsement)
  // is one endorsement, not many. Keep only the first occurrence per (fileName, formNumber).
  if (_endStopped && typeof console !== 'undefined' && console.warn) {
    console.warn(`[endorsements] budget of ${_END_BUDGET_MS} ms reached — ${_endStopped} page(s) not scanned. ` +
      'The endorsement sections are incomplete; every other section is unaffected.');
  }
  if (result.endorsements.length > 0) {
    const seen = new Set();
    const unique = [];
    result.endorsements.forEach(e => {
      const key = e.fileName + '|' + (e.formNumber || e.title || '') + '|' + e.coveragePart;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(e);
      }
    });
    result.endorsements = unique;
    result.truncated = _endStopped > 0;
    // Rebuild groupings
    result.byCoverage = {};
    result.byTag = {};
    unique.forEach(e => {
      const cov = e.coveragePart;
      if (!result.byCoverage[cov]) result.byCoverage[cov] = [];
      result.byCoverage[cov].push(e);
      const tag = e.tag;
      if (!result.byTag[tag]) result.byTag[tag] = [];
      result.byTag[tag].push(e);
    });
  }

  result.totalDetected = result.endorsements.length;

  if (typeof console !== 'undefined' && console.log) {
    console.log('[v2.21 Endorsements] Detected ' + result.totalDetected + ' endorsements across ' + (pdfTexts.length || 0) + ' file(s)');
    if (result.totalDetected > 0) {
      const tagCounts = {};
      result.endorsements.forEach(e => { tagCounts[e.tag] = (tagCounts[e.tag] || 0) + 1; });
      console.log('[v2.21 Endorsements] By tag:', tagCounts);
    }
  }
  return result;
}

// ============================================================================
// v2.22: STATE COMPLIANCE LAYER
// ============================================================================
//
// Per-state checklist of common compliance items that brokers are expected to
// verify. The framework is generic so additional states can be added as needed.
// Initial coverage: Texas. Other states will be added in subsequent passes.
//
function runStateComplianceChecks(pdfTexts, states, vertical, endorsements) {
  const result = { checks: [], by_state: {}, totalGaps: 0 };
  if (!states || states.length === 0) return result;

  // Build a single concatenated text for keyword scans (we already do this elsewhere
  // but keeping it local ensures we don't depend on global state)
  let allText = '';
  if (pdfTexts && pdfTexts.length > 0) {
    pdfTexts.forEach(f => {
      if (f.pages && Array.isArray(f.pages)) {
        f.pages.forEach(p => { if (p.text) allText += p.text + '\n'; });
      } else if (typeof f.text === 'string') {
        allText += f.text + '\n';
      }
    });
  }
  const allTextLower = allText.toLowerCase();

  // Helper: check if any of an array of patterns appear in the text
  function hasAnyPattern(patterns) {
    return patterns.some(p => p.test(allTextLower));
  }
  // Helper: check if an endorsement matching a regex was detected
  function hasEndorsement(matcher) {
    if (!endorsements || !endorsements.endorsements) return false;
    return endorsements.endorsements.some(e => matcher.test((e.title || '') + ' ' + (e.formNumber || '')));
  }

  // ----- TEXAS -----
  if (states.includes('TX')) {
    const tx = [];

    // 1. Texas Anti-Indemnity Act (Texas Civil Practice & Remedies Code Ch. 151)
    // Construction contracts cannot indemnify a party for its OWN negligence. Brokers
    // verify the policy supports the contractual liability obligations the insured
    // has under TX law — commonly via an Indemnity in Contract endorsement.
    const isConstruction = (vertical || '').toLowerCase().includes('construction')
      || (vertical || '').toLowerCase().includes('contractor')
      || (vertical || '').toLowerCase().includes('builder');
    const isOilGas = (vertical || '').toLowerCase().includes('oil')
      || (vertical || '').toLowerCase().includes('gas')
      || (vertical || '').toLowerCase().includes('energy');
    if (isConstruction || isOilGas) {
      const hasAntiIndemEndorsement = hasEndorsement(/anti[\-\s]?indemnity|indemnity\s+in\s+contract|tx\s+indemnity|texas\s+indemnity/i);
      tx.push({
        check: 'TX Anti-Indemnity Act compliance (Ch. 151)',
        status: hasAntiIndemEndorsement ? 'VERIFIED' : 'GAP',
        relevance: 'CRITICAL for construction/oil-gas contractors. Texas voids hold-harmless clauses for a party\u2019s own negligence in construction contracts. Policy must include Indemnity-in-Contract or TOLI endorsement to support the broker\u2019s contractual liability obligations.',
        action: hasAntiIndemEndorsement ? 'Verify endorsement language matches contractual obligations' : 'Confirm whether contractual liability is covered for TX-domiciled risk; consider adding TX Anti-Indemnity / Indemnity in Contract endorsement'
      });
    }

    // 2. TWIA / WPI-8 windstorm certificate (coastal counties only)
    const coastalCounties = /\b(galveston|harris|brazoria|chambers|jefferson|orange|matagorda|calhoun|refugio|aransas|nueces|kleberg|kenedy|willacy|cameron|san\s+patricio)\s+county/i;
    const inCoastalCounty = coastalCounties.test(allText);
    if (inCoastalCounty) {
      const hasTWIA = hasAnyPattern([/\btwia\b/, /texas\s+windstorm\s+insurance\s+association/, /wpi[\-\s]?8/]);
      tx.push({
        check: 'TWIA / WPI-8 windstorm coverage (Tier 1 coastal county)',
        status: hasTWIA ? 'VERIFIED' : 'GAP',
        relevance: 'CRITICAL for properties in 14 Tier 1 coastal counties. TWIA is the wind/hail insurer of last resort. Properties without TWIA coverage and a WPI-8 certificate may have no wind/hail coverage at all.',
        action: hasTWIA ? 'Verify WPI-8 certificate is on file with carrier' : 'Determine if property is in Tier 1; if yes, secure TWIA coverage and WPI-8 certificate'
      });
    }

    // 3. Texas Cancellation Notice (10 days non-pay / 60 days mid-term)
    const hasTXCancel = hasEndorsement(/texas\s+(?:changes|amendatory)|cancellation|policy\s+changes\s+\u2013\s+texas/i)
      || hasAnyPattern([/10\s+days[\s\S]{0,30}(?:non[\-\s]?payment|nonpayment)/, /60\s+days[\s\S]{0,40}(?:other\s+reason|cancellation)/]);
    tx.push({
      check: 'Texas Cancellation Notice provisions (10-day non-pay / 60-day mid-term)',
      status: hasTXCancel ? 'VERIFIED' : 'VERIFY',
      relevance: 'STANDARD. Texas Insurance Code requires specific notice periods for cancellation. The Texas Amendatory endorsement must align cancellation provisions with TIC \u00a7551.052/551.053.',
      action: hasTXCancel ? 'Confirm endorsement language matches TX statutory periods' : 'Verify Texas Amendatory endorsement is attached'
    });

    // 4. TX Hail/Wind percentage deductible (post-Ike standard)
    const hasPctDeductible = hasAnyPattern([/\b(?:1|2|3|5)\s*%\s+(?:wind|hail|named\s+storm)\s+deductible/i, /percentage\s+deductible/i]);
    const hasFlatWindHail = hasAnyPattern([/\bwind\s+(?:and|&)\s+hail\s+deductible[^%\n]{0,30}\$\s*[\d,]+/i]);
    if (hasPctDeductible || hasFlatWindHail) {
      tx.push({
        check: 'TX Wind/Hail Deductible structure',
        status: hasPctDeductible ? 'VERIFIED' : 'WARN',
        relevance: hasPctDeductible
          ? 'Percentage-based deductible is the post-Ike Texas standard. Insured\u2019s out-of-pocket scales with insured value.'
          : 'Flat dollar deductible on wind/hail is non-standard for Texas commercial property post-Hurricane Ike. Verify whether the carrier intentionally offered flat or whether it should be percentage-based.',
        action: hasPctDeductible
          ? 'Confirm the percentage applies to total insured value (not just damaged section)'
          : 'Verify if flat deductible was negotiated; if not, request percentage deductible quote for comparison'
      });
    }

    // 5. TDI admitted carrier check
    // Look for evidence of surplus lines (E&S, non-admitted, surplus lines tax) in the policy.
    const isSurplusLines = hasAnyPattern([
      /\bsurplus\s+lines?\b/i,
      /non[\-\s]?admitted/i,
      /\bslsa\b/i,
      /surplus\s+lines?\s+(?:tax|stamping\s+fee)/i,
      /this\s+(?:insurance\s+contract|policy)\s+is\s+with\s+an\s+insurer\s+not\s+licensed/i
    ]);
    const hasTXStampingFee = /(?:texas\s+surplus\s+lines\s+stamping\s+office|tx\s+slsa)/i.test(allText);
    if (isSurplusLines) {
      tx.push({
        check: 'TDI surplus lines compliance (TX SLSA stamping fee)',
        status: hasTXStampingFee ? 'VERIFIED' : 'VERIFY',
        relevance: 'IMPORTANT. Surplus lines policies in Texas require TX SLSA stamping fee + 4.85% surplus lines tax. Missing documentation can void coverage in disputes.',
        action: hasTXStampingFee ? 'Retain stamping certificate with policy file' : 'Request stamping documentation from surplus lines broker; verify 4.85% TX surplus lines tax was paid'
      });
    }

    // 6. Post-Uri freeze coverage carve-back (added after Feb 2021 winter storm)
    const isPropertyRelevant = (vertical || '').toLowerCase().includes('real estate')
      || (vertical || '').toLowerCase().includes('property management')
      || (vertical || '').toLowerCase().includes('habitational')
      || (pdfTexts || []).some(f => /commercial\s+property\s+coverage\s+part/i.test((f.pages || []).map(p => p.text || '').join(' ')));
    if (isPropertyRelevant) {
      const hasFreezeExclusion = hasAnyPattern([/freeze\s+exclusion/i, /\bextreme\s+(?:cold|temperature)\s+exclusion/i]);
      const hasFreezeCarveBack = hasAnyPattern([/freeze\s+carve[\-\s]?back/i, /weather[\-\s]?related\s+freeze/i, /winter\s+storm\s+coverage/i]);
      if (hasFreezeExclusion || hasFreezeCarveBack) {
        tx.push({
          check: 'Post-Uri freeze coverage (Feb 2021 winter storm exposure)',
          status: hasFreezeCarveBack ? 'VERIFIED' : (hasFreezeExclusion ? 'GAP' : 'VERIFY'),
          relevance: 'IMPORTANT for TX property risks. After Winter Storm Uri (Feb 2021), some carriers added freeze exclusions. Verify whether policy language has a carve-back for weather-related freeze claims.',
          action: hasFreezeCarveBack ? 'Document the carve-back in policy notes' : (hasFreezeExclusion ? 'Negotiate freeze carve-back endorsement' : 'Confirm freeze coverage is intact')
        });
      }
    }

    if (tx.length > 0) {
      result.by_state['TX'] = tx;
      result.checks.push({ state: 'TX', items: tx });
      result.totalGaps += tx.filter(c => c.status === 'GAP').length;
    }
  }

  if (typeof console !== 'undefined' && console.log) {
    console.log('[v2.22 StateCompliance] checks: ' + result.checks.length + ', total gaps: ' + result.totalGaps);
  }
  return result;
}

/* A COVERAGE-PART ELECTION TABLE.
 *
 * Management liability portfolios (The Hartford's Private Choice Premier,
 * Holloway Houston) declare each part on one row with a box:
 *
 *   | ☐ **Directors, Officers and Entity Liability** | $ NOT COVERED | … |
 *   | ☑ **Employment Practices Liability**           | $2,000,000    | $25,000 | 01/13/2010 |
 *   | ☑ **Fiduciary Liability**                      | $1,000,000    | $0      | 04/15/2016 |
 *
 * The field reader typed the $2,000,000 as a RETENTION (the table has a
 * Retention(s) column), so no EPL limit was set and the row quoted a garbled
 * sentence; and D&O, unticked and NOT COVERED, was reported as coverage
 * detected. The box and the first value column answer both questions.
 * Returns [{ part, elected, limit, retention, priorPending, quote, fileName, page }]. */
function spReadElectionTable(pdfTexts) {
  const out = [];
  (pdfTexts || []).forEach(doc => (doc.pages || []).forEach(pg => {
    String(pg.text || '').split('\n').forEach(line => {
      if (!/^\s*\|/.test(line)) return;
      const cells = line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.replace(/\\([*_])/g, '$1').replace(/[*_]/g, '').replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim());
      const m = /^(☑|☒|☐|\[\s*[xX]?\s*\])\s*(.+?)$/.exec(cells[0] || '');
      if (!m || !/liability$/i.test(m[2]) || cells.length < 2) return;
      const elected = /☑|☒|\[\s*[xX]\s*\]/.test(m[1]);
      const money = v => { const x = /\$\s*([\d][\d,]{2,})/.exec(v || ''); return x ? '$' + x[1] : null; };
      const notCovered = /not\s+covered/i.test(cells[1] || '');
      out.push({
        part: m[2].trim(), elected: elected && !notCovered,
        limit: notCovered ? null : money(cells[1]),
        retention: money(cells[2]),
        priorPending: ((cells[3] || '').match(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/) || [null])[0],
        quote: m[2].trim() + ' \u2014 ' + (cells[1] || '').trim(),
        fileName: doc.fileName, page: pg.page
      });
    });
  }));
  return out;
}

/* KIDNAP & RANSOM. A Lloyd's K&R schedule states its limits per insured event
 * in prose: "Ransom … USD 1,000,000 per Insured Event / USD 1,000,000 in the
 * aggregate", "Transit USD 1,000,000 per Insured Event". Section 6 had no row
 * for it, so Holloway Houston's K&R placement appeared nowhere. */
function spReadKidnapRansom(pdfTexts) {
  for (const doc of (pdfTexts || [])) {
    const t = (doc.pages || []).map(p => String(p.text || '')).join('\n');
    if (!/\bkidnap\b/i.test(t) || !/\bransom\b/i.test(t)) continue;
    const at = t.search(/limits?\s+of\s+liability\**\s*\n\s*i\.\s*\**\s*ransom/i);
    const sched = at >= 0 ? t.slice(at, at + 1500) : '';
    if (!sched) continue;
    const per = /ransom[^\n]*?(?:USD|US\$|\$)\s*([\d,]{5,})\s+per\s+insured\s+event/i.exec(sched);
    if (!per) continue;
    const agg = /(?:USD|US\$|\$)\s*([\d,]{5,})\s+in\s+the\s+aggregate/i.exec(sched.slice(per.index, per.index + 300));
    const transit = /transit[^\n]*?(?:USD|US\$|\$)\s*([\d,]{5,})\s+per\s+insured\s+event/i.exec(sched);
    const out = { perOccurrence: '$' + per[1] };
    if (agg) out.aggregate = '$' + agg[1];
    if (transit) out.transit = '$' + transit[1];
    if (/legal\s+liability\W+(?:\*\*)?\s*not\s+covered/i.test(sched)) out._note = 'Legal Liability: not covered under this insurance';
    out._fileName = doc.fileName;
    return out;
  }
  return null;
}

function extractDeclarationsLimits(pdfTexts) {
  const _spT0 = Date.now();
  /* A page whose labels and values were extracted separately is not read for
   * limits — see spColumnSplitPage. Blanking the text rather than dropping the
   * page keeps page numbers, and therefore citations, intact. */
  /* A DOCUMENT WHOSE ROWS WERE TAKEN APART IS NOT READ FOR LIMITS AT ALL.
   *
   * Blanking only the declarations pages was the first attempt and it made
   * things worse: the readers fell back to whatever else in the document sat
   * near the right words, and the auto combined single limit went from a wrong
   * $3,000,000 to a wrong $25,000,000. The column split is not confined to one
   * page. pypdf takes the rows apart everywhere, so every reader that depends
   * on a label being near its value — which is all of them — is reading noise.
   *
   * There is no way to tell, from inside, which of the surviving figures are
   * right. Tulsa Honor Academy's property limit happens to be correct and its
   * general liability limits are not, and nothing distinguishes them.
   *
   * So the document is refused, and the report says limits were not parsed and
   * why. A coverage with no limit says so on the page and a broker goes and
   * looks; a coverage with the wrong limit reads as a finding and is acted on.
   * That asymmetry has been the whole of this week.
   *
   * This is not the fix. The fix is upstream: the browser asks for LlamaParse
   * and the backend answers with pypdf, whose output cannot carry a
   * declarations table. Restore LlamaParse and every reader here works again —
   * they were built and verified against exactly that shape.
   */
  if (Array.isArray(pdfTexts)) {
    const usable = [], refused = [];
    for (const doc of pdfTexts) {
      const split = doc && Array.isArray(doc.pages) && doc.pages.some(p => p && p.text && spColumnSplitPage(p.text));
      (split ? refused : usable).push(doc);
    }
    if (refused.length) {
      if (typeof console !== 'undefined' && console.warn) {
        /* NAME THE PARSER THAT ACTUALLY ANSWERED.
         *
         * This said "the backend answering with pypdf instead of LlamaParse",
         * which was true when pypdf was the only way a document arrived
         * column-split. It is not true now: evaluating Azure Document
         * Intelligence, this fired on an excess policy DI had flattened into a
         * list — value, then its labels three lines later — and the message
         * blamed a parser that had not run. During a comparison that is worse
         * than unhelpful, because the line is the first thing anyone reads
         * when a document goes unread.
         *
         * The engine is on the document, so it is reported. The refusal itself
         * is unchanged and correct: labels and amounts really were separated,
         * and withholding a limit beats publishing a wrong one. */
        const engines = [...new Set(refused.map(d =>
          (d && (d.engine || (d.pages || []).find(p => p && p.engine))) || null).filter(Boolean))];
        console.warn(`[limits] ${refused.length} document(s) not read: ` +
          refused.map(d => (d && d.fileName) || '?').join(', ') +
          '. Their declarations pages were extracted column by column, so no amount can be ' +
          'attributed to a label — the limits would be wrong, not missing.' +
          (engines.length ? ` Parsed by: ${engines.join(', ')}.` : '') +
          ' A parser that returns rows — LlamaParse markdown or its per-page json — reads these pages correctly.');
      }
      pdfTexts = usable;
    }
  }
  const _spDone = () => {
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[perf] extract declarations limits: ${Date.now() - _spT0} ms`);
    }
  };
  const limits = {};
  // v2.20.6: Trust map — records the confidence level of each stored value so a
  // lower-trust source (legacy regex fallback) cannot overwrite a higher-trust
  // source (scoped per-page extraction). Trust levels:
  //   2 = scoped extraction from a real coverage-part dec page (highest)
  //   1 = legacy all-text extraction (default)
  //   0 = unset
  const limitsTrust = {};
  function setLimit(coverage, field, value, trust) {
    if (!value) return;
    if (!limits[coverage]) limits[coverage] = {};
    if (!limitsTrust[coverage]) limitsTrust[coverage] = {};
    const existingTrust = limitsTrust[coverage][field] || 0;
    if (trust >= existingTrust) {
      limits[coverage][field] = value;
      limitsTrust[coverage][field] = trust;
    }
  }
  function setLimits(coverage, valueObj, trust) {
    if (!valueObj || typeof valueObj !== 'object') return;
    Object.entries(valueObj).forEach(([field, value]) => {
      if (field.startsWith('_')) {
        // Metadata fields — just set without trust check
        if (!limits[coverage]) limits[coverage] = {};
        limits[coverage][field] = value;
      } else {
        setLimit(coverage, field, value, trust);
      }
    });
  }
  
  if (!pdfTexts || pdfTexts.length === 0) return limits;
  
  // Helper: clean OCR dollar amounts (handles line breaks, spaces, periods-as-commas inside numbers)
  function cleanDollar(raw) {
    if (!raw || typeof raw !== 'string') return null;
    // Remove whitespace/newlines inside number (handles OCR: "1, 000,000" → "1,000,000")
    let cleaned = raw.replace(/[\s\n\r]+/g, '').replace(/^[,.]|[,.]$/g, '');
    // OCR often reads commas as periods: 5.000 → 5,000, 2.000.000 → 2,000,000
    // Apply repeatedly to handle chained thousands: 2.000.000 → 2,000.000 → 2,000,000
    let prev;
    do {
      prev = cleaned;
      cleaned = cleaned.replace(/(\d)\.(\d{3})(?=\.|,|$)/g, '$1,$2');
    } while (cleaned !== prev);
    // v2.17: Strip trailing coinsurance percentages that got captured (e.g., "37287671100" from "$37,287,671 100%")
    // If the number doesn't parse to a standard dollar amount, check for trailing percentage
    if (/^\d[\d,]*$/.test(cleaned) && cleaned.length >= 1) {
      // Check for trailing 100, 90, 80, 50 that might be coinsurance captured with the amount
      const numVal = parseInt(cleaned.replace(/,/g, ''), 10);
      if (numVal > 99999999) { // Suspiciously large — check if trailing digits are coinsurance
        const noCommas = cleaned.replace(/,/g, '');
        for (const pct of ['100', '90', '80', '50']) {
          if (noCommas.endsWith(pct) && noCommas.length > pct.length + 3) {
            const trimmed = noCommas.slice(0, -pct.length);
            const trimVal = parseInt(trimmed, 10);
            if (trimVal >= 10000) { // Reasonable dollar amount after trimming
              // Format with commas
              return '$' + trimVal.toLocaleString('en-US');
            }
          }
        }
      }
      return '$' + cleaned;
    }
    return '$' + raw.trim();
  }
  
  // Helper: parse a dollar string to numeric value for comparison
  function dollarToNum(dollarStr) {
    if (!dollarStr) return 0;
    return parseInt(dollarStr.replace(/[$,\s]/g, ''), 10) || 0;
  }
  
  // Helper: extract ALL dollar amounts near a label and return the LARGEST
  // This handles cases where deductibles ($50) appear before limits ($5,000)
  // v2.10: Added minValue parameter to filter out noise (e.g. $100 from terrorism endorsements)
  // Money on a declarations page that is a cost, not a cover.
  const _SP_NOT_A_LIMIT_MONEY = /\b(?:premium|surcharge|tax(?:es)?|fee|expense\s+constant|assessment|installment|commission)\b[^a-z0-9]{0,12}$/i;

  // "…shall be shared with: Employment Practices Liability, Fiduciary…" — the
  // coverage is NAMED here, and its own declarations are somewhere else.
  const _SP_SHARED_LIMIT_LEAD = /\b(?:shared|sharing)\s+with\b[^$]{0,120}$|\bshall\s+be\s+shared\b[^$]{0,120}$|\bpart\s+of(?:\s+and\s+not\s+in\s+addition\s+to)?\b[^$]{0,80}$/i;

  // "(2) Sublimit for Tax Matters: $100,000" — a carve-out of a limit, never a
  // limit. The property reader has a section-level version of this; the inline
  // form appears with no section heading to find.
  const _SP_SUBLIMIT_LEAD = /\bsub-?limits?\b[^$]{0,60}$/i;

  /* A LIMIT INSIDE A SENTENCE IS THE FORM TALKING, NOT THE DECLARATIONS.
   *
   * Matrix Human Services' EPL aggregate was reported as $100,000. The real
   * figure is $5,000,000, on the declarations. $100,000 came from page 102:
   *
   *   "...the Employment Practices Liability Coverage Part shall also not
   *    apply to Defense Costs in an aggregate amount not to exceed $100,000,
   *    which amount is part of and not in addition to..."
   *
   * That is an exclusion carve-back, and it contains the coverage name and the
   * word "aggregate" within sixty characters of a dollar sign, which is all
   * the scan needed. A declarations page states a limit in a FIELD; a form
   * states one in a CLAUSE, and the clause is always about something narrower.
   *
   * Same distinction the vertical classifier draws between a declarations
   * field and coverage wording, applied to money.
   */
  const _SP_NARRATIVE_LIMIT_LEAD = /\b(?:in\s+an?\s+(?:aggregate\s+)?amount\s+not\s+to\s+exceed|not\s+to\s+exceed|shall\s+not\s+exceed|up\s+to\s+a\s+maximum\s+of|shall\s+also\s+not\s+apply|does\s+not\s+apply)\b[^$]{0,60}$/i;

  // The state guaranty association notice is not a schedule of benefits.
  //
  // Aspen View's student accident PDF carries the Colorado Life and Health
  // Insurance Protection Association notice, which lists $500,000 hospital,
  // medical and surgical, $300,000 death, $300,000 disability, $100,000 other
  // health. Those are statutory caps on what the ASSOCIATION would pay if the
  // carrier became insolvent. They are printed on nearly every accident and
  // health policy sold in the state, identically, whoever the insured is.
  //
  // Nothing reads them today, and nothing should ever start: a carrier
  // insolvency backstop presented as a student's medical benefit would be the
  // worst misattribution this engine could make, and it would look entirely
  // plausible — round numbers, in a policy document, beside the word
  // "benefits".
  const _SP_GUARANTY_NOTICE = /\b(?:guaranty\s+association|insurance\s+protection\s+association|life\s+and\s+health\s+insurance\s+guaranty|becomes\s+financially\s+unable\s+to\s+meet\s+its\s+obligations)\b/i;

  function spInGuarantyNotice(text, index) {
    const span = String(text || '').slice(Math.max(0, index - 2500), index + 400);
    return _SP_GUARANTY_NOTICE.test(span);
  }

  function extractBestDollar(text, labelRegex, minValue) {
    const threshold = minValue || 0;
    // Find all matches of the label pattern — use matchAll with global flag
    const globalRegex = new RegExp(labelRegex.source, 'gi');
    let best = null;
    let bestVal = 0;
    let m;
    while ((m = globalRegex.exec(text)) !== null) {
      // A premium is never a limit.
      //
      // "Coverage  Building  Blanket Group 001 ... Premium  $21,638.00" is one
      // line on a property declarations page, and a pattern allowing sixty
      // characters between the word "building" and a dollar sign walks
      // straight across it. That is where "Coverage B Each Action: $21,638"
      // came from on the Educators Legal Liability row — the building's
      // premium, printed as a limit, cited to the declarations.
      const dollarAt = m.index + m[0].lastIndexOf('$');
      const lead = text.slice(Math.max(0, dollarAt - 30), dollarAt);
      if (_SP_NOT_A_LIMIT_MONEY.test(lead)) continue;
      if (spInGuarantyNotice(text, dollarAt)) continue;
      /* A COVERAGE NAMED IN A SHARED-LIMIT LIST IS A CROSS-REFERENCE.
       *
       * Matrix Human Services' Chubb declarations, D&O coverage part:
       *
       *   (A) Aggregate Limit of Liability: $5,000,000
       *       (2) Sublimit for Tax Matters: $100,000
       *   (B) Aggregate Limit of Liability in (A) above shall be shared with:
       *         Employment Practices Liability  Fiduciary Liability  ...
       *
       * The EPL label matched inside THAT list — the D&O part telling you what
       * it shares its limit with — and took $100,000 from the line above it.
       * EPL's own part, further down, states $5,000,000. So the report showed a
       * $5m employment practices tower as $100,000: a tax-matters sublimit
       * belonging to a different coverage, printed as EPL's aggregate.
       *
       * A wrong limit is worse than a missing one. A missing one is visibly a
       * gap; this one reads as a finding. */
      if (_SP_SHARED_LIMIT_LEAD.test(text.slice(Math.max(0, m.index - 140), m.index))) continue;
      /* A COVERAGE PART HEADING BETWEEN THE NAME AND THE AMOUNT OWNS THE
       * AMOUNT.
       *
       * A management liability declarations page opens by listing the parts
       * the policy bought, then heads each one and states its limit:
       *
       *     Item 4.  Coverage(s) Applicable to this Policy:
       *        Employment Practices Liability Coverage Part      <- a listing
       *        ...
       *     DIRECTORS & OFFICERS AND ENTITY LIABILITY COVERAGE PART
       *     (A) Aggregate Limit of Liability: $5,000,000         <- D&O's
       *
       * The EPL label matches in the listing and the nearest amount is D&O's,
       * six lines below a heading that says whose it is. The EPL row took it.
       *
       * Three narrower guards were tried and reverted before this one. Each
       * asked whether the figure LOOKED like someone else's, and each deleted
       * real limits, because equal round numbers under several headings is the
       * normal case in insurance — a shared group limit is exactly that shape,
       * and correct. This asks a structural question instead, and a heading is
       * the one thing that unambiguously reassigns what follows it.
       *
       * The label's own line is excluded: "EMPLOYMENT PRACTICES LIABILITY
       * COVERAGE PART" followed by its own limit is the ordinary case.
       */
      const _between = text.slice(m.index, dollarAt);
      const _nl = _between.indexOf('\n');
      if (_nl >= 0 && _between.slice(_nl + 1).split('\n').some(l => {
        const t2 = l.trim();
        return t2.length > 0 && t2.length <= 80 && /\bcoverage\s+(?:part|form)\b/i.test(t2);
      })) continue;
      /* AND A SUBLIMIT IS NOT A PRIMARY LIMIT.
       *
       * Defence in depth for the same document, and the general form of a rule
       * this engine has now needed twice — Tulsa Honors filled its building
       * slot with a $100,000 supplemental sublimit for Outdoor Property.
       * spInPropertySublimitSchedule catches the SECTION-heading form of it;
       * this catches the inline "Sublimit for X: $n" form, which no section
       * heading announces. */
      if (_SP_SUBLIMIT_LEAD.test(lead)) continue;
      if (_SP_NARRATIVE_LIMIT_LEAD.test(text.slice(Math.max(0, dollarAt - 90), dollarAt))) continue;
      const candidate = cleanDollar(m[1]);
      const val = dollarToNum(candidate);
      if (val > bestVal && val >= threshold) {
        bestVal = val;
        best = candidate;
      }
    }
    return best;
  }
  
  // v2.10: Extract bare numbers (without $ prefix) near a label.
  // Many PDF policies (especially PHLY) render $ on a separate line from the number in tables.
  // PDF.js extracts: "EACH OCCURRENCE LIMIT\n$\n...\n  3,000,000" — splitting $ from digits.
  // Also handles: "Aggregate Limit of Liability:  1,000,000" (no $ at all, e.g. AmTrustCyber)
  // A bare number near a label is the loosest evidence this engine accepts:
  // no dollar sign, and the LARGEST candidate on the line wins. That is how
  // D&O came to report a limit of "$010039405" — the policy number, which is
  // 10,039,405 and therefore beat the real $1,000,000.
  //
  // The rule that already separates a limit from a policy number elsewhere
  // applies here with more force, not less: a limit is chosen, so it is round.
  // A leading zero settles it on its own — no limit is ever written 010039405
  // — and a few labels are never followed by an amount.
  const _SP_NOT_A_LIMIT_LEAD = /\b(?:policy|account|certificate|form|endorsement|invoice|agent|group|class|code|naic|fein|ein)\s*(?:number|no\.?|#)?\s*:?\s*$/i;

  function spBareLooksLikeLimit(raw, text, at) {
    const str = String(raw || '').trim();
    if (/^0\d/.test(str.replace(/,/g, ''))) return false;      // leading zero
    const n = spMoneyWhole(str);
    if (!isFinite(n) || n < 1000) return false;
    if (n % 500 !== 0) return false;                            // not a chosen figure
    const lead = String(text || '').slice(Math.max(0, at - 40), at);
    return !_SP_NOT_A_LIMIT_LEAD.test(lead);
  }

  function extractBareNumber(text, labelPattern, minValue) {
    const threshold = minValue || 10000; // Default min $10K to filter noise
    let best = null;
    let bestVal = 0;
    let m;
    
    // Strategy 1: Label on same line as number (e.g. "Aggregate Limit of Liability:  1,000,000")
    // v2.17: Filter out address numbers (PO Box, Suite, ZIP codes, phone numbers)
    const addressFilter = /(?:P\.?\s*O\.?\s*Box|\bBox\b|\bSuite\b|\bSte\.?\b|\bZip\b|\bPhone\b|\bFax\b|\bTel\b)\s*(?:#?\s*)?$/i;
    const zipFilter = /(?:[A-Z]{2}\s+)$/;
    const sameLineRegex = new RegExp(labelPattern + '[^\\n]{0,80}?(\\d[\\d,]{2,})', 'gi');
    while ((m = sameLineRegex.exec(text)) !== null) {
      // Check if the number is preceded by address-related text
      const preContext = text.substring(Math.max(0, m.index + m[0].length - m[1].length - 30), m.index + m[0].length - m[1].length);
      if (addressFilter.test(preContext)) continue;
      // The same two rules extractBestDollar applies, and for the same
      // document: a coverage NAMED in a shared-limit list is a cross-reference
      // to declarations that live elsewhere, and a sublimit is a carve-out of
      // a limit rather than one. Matrix Human Services' EPL tower is
      // $5,000,000 and this path reported $100,000 — the Tax Matters sublimit
      // from the D&O part, reached through the "shall be shared with" list
      // that names Employment Practices Liability.
      if (_SP_SHARED_LIMIT_LEAD.test(text.slice(Math.max(0, m.index - 140), m.index))) continue;
      if (_SP_SUBLIMIT_LEAD.test(text.substring(Math.max(0, m.index + m[0].length - m[1].length - 60), m.index + m[0].length - m[1].length))) continue;
      // v2.17: Skip ZIP codes (5 digits or 5+4 format near state abbreviations)
      const numStr = m[1].replace(/,/g, '');
      if (/^\d{5}$/.test(numStr) || /^\d{9}$/.test(numStr)) {
        const preZip = text.substring(Math.max(0, m.index + m[0].length - m[1].length - 15), m.index + m[0].length - m[1].length);
        if (/[A-Z]{2}\s*$/.test(preZip)) continue; // State abbreviation before number = ZIP code
      }
      const candidate = cleanDollar(m[1]);
      const val = dollarToNum(candidate);
      const _numAt = m.index + m[0].length - m[1].length;
      if (!spBareLooksLikeLimit(m[1], text, _numAt)) continue;
      if (spInGuarantyNotice(text, _numAt)) continue;
      if (val >= threshold && val > bestVal) {
        bestVal = val;
        best = candidate;
      }
    }
    if (best) return best;
    
    // Strategy 2: Tabular PDF extraction — labels and values in separate columns
    // In PHLY/AXA-style PDFs, PDF.js extracts all labels first, then all values.
    // Pattern: Find label position, then find its corresponding $ and value.
    // The $ may appear right after the label, and the number appears later in the text block.
    const labelRegex = new RegExp(labelPattern, 'gi');
    while ((m = labelRegex.exec(text)) !== null) {
      const labelPos = m.index;
      // Get text window after the label (up to 2000 chars to cover full page)
      const windowAfter = text.substring(labelPos, Math.min(text.length, labelPos + 2000));
      // Find ALL dollar-ish numbers (with or without $) in this window
      const numMatches = [...windowAfter.matchAll(/(?:\$\s*)?\b(\d[\d,]{3,})\b/g)];
      for (const nm of numMatches) {
        // v2.17: Skip address/PO Box/phone/fax numbers
        const preCtx = windowAfter.substring(Math.max(0, nm.index - 40), nm.index);
        if (/(?:P\.?\s*O\.?\s*Box|\bBox\b|\bSuite\b|\bSte\.?\b|\bZip\b|\bPhone\b|\bFax\b|\bTel\b|\bExt\b)\s*(?:#?\s*)?$/i.test(preCtx)) continue;
        // v2.17: Skip ZIP codes
        const numStr2 = nm[1].replace(/,/g, '');
        if (/^\d{5}$/.test(numStr2) || /^\d{9}$/.test(numStr2)) {
          if (/[A-Z]{2}\s*$/.test(preCtx)) continue;
        }
        // Also skip if followed by city/state pattern (address context)
        const postCtx = windowAfter.substring(nm.index + nm[0].length, nm.index + nm[0].length + 40);
        if (/^\s*[,.]?\s*(?:San\s+Francisco|New\s+York|Los\s+Angeles|Chicago|[A-Z]{2}\s+\d{5})/i.test(postCtx)) continue;
        /* THE SAME GUARD STRATEGY 1 APPLIES.
         *
         * Strategy 1 runs spBareLooksLikeLimit on every candidate; strategy 2
         * did not, and it is the looser of the two — it sweeps a 2,000
         * character window after the label and takes the largest number in it.
         *
         * A cyber policy therefore reported an each-claim limit of
         * $108,247,020. That is the policy number, CYB-108247020-00, sitting
         * under "POLICY NUMBER" on the declarations page, and it beat the real
         * $1,000,000 by being bigger. A limit is CHOSEN, so it is round; a
         * policy number is not. 108247020 is not a multiple of 500 and would
         * have been rejected in the other strategy of the same function.
         *
         * Two paths to one answer is fine. Two paths with different standards
         * of evidence is how the looser one becomes the answer.
         */
        const _numAt2 = labelPos + nm.index + nm[0].length - nm[1].length;
        if (!spBareLooksLikeLimit(nm[1], text, _numAt2)) continue;
        if (spInGuarantyNotice(text, _numAt2)) continue;
        const candidate = cleanDollar(nm[1]);
        const val = dollarToNum(candidate);
        if (val >= threshold && val > bestVal) {
          bestVal = val;
          best = candidate;
        }
      }
    }
    
    return best;
  }
  
  // Dollar capture pattern: digits, commas, periods, spaces — handles OCR artifacts
  // [\d,. ]+\d captures: "5,000" "5.000" "5 000" "2,000,000" "1.000.000"
  const D = '[\\d,. ]+\\d'; // dollar amount capture pattern string
  
  // Page-quality classifier: is this a declarations/schedule page?
  function isDeclarationsPage(text) {
    if (!text || text.length < 50) return false;
    const hasDecHeading = /(?:^|\n)\s*(?:COMMERCIAL\s+GENERAL\s+LIABILITY|PROPERTY|INLAND\s+MARINE|UMBRELLA|EXCESS|AUTO|WORKERS|EQUIPMENT|BUSINESS\s+OWNERS|PACKAGE)[\s\S]{0,40}DECLARATIONS/im.test(text)
      || /DECLARATIONS\s+PAGE/i.test(text)
      || /SCHEDULE\s+OF\s+(?:COVERAGES|FORMS|LIMITS)/i.test(text)
      || /COVERAGE\s+SUMMARY/i.test(text)
      || /POLICY\s+DECLARATIONS/i.test(text)
      || /LIMITS\s+OF\s+INSURANCE/i.test(text);
    const hasDollarAmounts = (text.match(/\$\s*[\d,.]{3,}/g) || []).length >= 2;
    const hasLimitLabels = /(?:EACH\s+OCCURRENCE|AGGREGATE|(?:PER|EACH)\s+CLAIM|LIMIT(?:S)?\s+OF)/i.test(text);
    return hasDecHeading && (hasDollarAmounts || hasLimitLabels);
  }
  

  console.log('=== PRIORITY EXTRACTION START ===');
  console.log('pdfTexts count:', pdfTexts.length);
  pdfTexts.forEach((doc,idx) => { console.log('  Doc', idx, ':', doc.fileName, 'pages:', doc.pages ? doc.pages.length : 'NO PAGES'); });
  // Limits stated on a line that names its own coverage.
  //
  // Declarations print sub-coverages as one labelled line each:
  //
  //     Sexual Misconduct Liability Each Claim Limit        $ 1,000,000
  //     Sexual Misconduct Liability Aggregate Limit         $ 3,000,000
  //     Sexual Misconduct Medical Expense Limit             $    10,000
  //
  // Nothing read those, so a coverage the policy carries at $1M/$3M showed as
  // "limits not parsed" even after the vocabulary fix stopped it showing as a
  // CRITICAL GAP. The line carries its own coverage name and its own label, so
  // it needs no surrounding context and no proximity guess — which is what
  // makes it safe to read generically.
  function spReadNamedCoverageLimits(text) {
    const out = {};
    const CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
    if (!CT) return out;
    const LABELS = { 'each claim': 'eachClaim', 'per claim': 'eachClaim',
      'each occurrence': 'perOccurrence', 'aggregate': 'aggregate',
      'medical expense': 'medicalExpense', 'each act': 'eachAct',
      'each person': 'eachPerson', 'each employee': 'eachEmployee' };
    const normText = spNorm(text);
    const re = new RegExp('^\\s*([A-Za-z][A-Za-z&\'\\-/ ]{4,60}?)\\s+(' +
      Object.keys(LABELS).join('|') + ')\\s+limit\\s+\\$\\s*([\\d][\\d,]*)', 'gim');
    let m;
    while ((m = re.exec(normText)) !== null) {
      const cov = CT.canonical(m[1]);
      if (!cov) continue;
      const key = LABELS[m[2].toLowerCase().replace(/\s+/g, ' ')];
      if (!key) continue;
      if (!out[cov]) out[cov] = {};
      if (!out[cov][key]) out[cov][key] = '$' + m[3];
    }

    // A GROUP limit shared by the sub-coverages printed beneath it.
    //
    //     Educators Management Liability Coverage
    //         Educators Management Liability Each Claim Limit   $1,000,000
    //         Educators Management Liability Aggregate Limit    $1,000,000
    //             Coverage EL - Educators Legal Liability
    //             Coverage DO - Directors And Officers Liability
    //             Coverage EP - Employment Practices Liability
    //
    // The limit is stated once, at the group, and the three sub-coverages
    // share it. Nothing read that, so D&O fell through to a whole-document
    // search for the word "aggregate" and came back with $3,000,000 — the
    // Sexual Misconduct aggregate, a different coverage entirely.
    //
    // Attaching the group limit to each sub-coverage is the correct reading,
    // and it has to say SHARED. Three coverages each showing $1,000,000 with
    // no note reads as $3,000,000 of available limit, which is exactly the
    // overstatement a broker-of-record review exists to prevent.
    const src2 = normText;
    const grpRe = /^[ \t]*([A-Z][A-Za-z ]{6,50}?)\s+Coverage\s*$/gm;
    let g;
    while ((g = grpRe.exec(src2)) !== null) {
      const groupName = g[1].replace(/\s+/g, ' ').trim();
      const after = src2.slice(g.index + g[0].length, g.index + g[0].length + 1400);
      const grpLimits = {};
      const lr = new RegExp('^\\s*' + groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
        '\\s+(each\\s+claim|aggregate)\\s+limit\\s+\\$\\s*([\\d][\\d,]*)', 'gim');
      let lm;
      while ((lm = lr.exec(after)) !== null) {
        grpLimits[/aggregate/i.test(lm[1]) ? 'aggregate' : 'eachClaim'] = '$' + lm[2];
      }
      if (!Object.keys(grpLimits).length) continue;
      // The sub-coverages listed under it, before the next group heading.
      const stop = after.search(/^[ \t]*[A-Z][A-Za-z ]{6,50}?\s+Coverage\s*$/m);
      const zone = stop > 0 ? after.slice(0, stop) : after;
      const subRe = /^\s*coverage\s+[A-Z]{1,3}\s*[-\u2013\u2014:]\s*([A-Za-z][A-Za-z&'\/ ]{3,60})/gim;
      const subs = [];
      let sm;
      while ((sm = subRe.exec(zone)) !== null) {
        const c = CT.canonical(sm[1]);
        if (c) subs.push(c);
      }
      if (subs.length < 2) continue;
      const shareNote = 'Shared ' + (grpLimits.aggregate || grpLimits.eachClaim) + ' ' + groupName +
        ' limit, shared with ' + subs.length + ' coverages';
      subs.forEach(c => {
        if (!out[c]) out[c] = {};
        Object.keys(grpLimits).forEach(k => { out[c][k] = grpLimits[k]; });
        out[c]._sharedLimit = shareNote;
      });
    }

    return out;
  }

  // Property deductibles, as stated on the declarations.
  //
  // Every deductible path in this engine was EB-side — plan copays, individual
  // versus family. On the P&C side the word appeared only in advice text, so
  // Aspen View's report said nothing about a retention the declarations state
  // five times over:
  //
  //     Deductible                     $5,000
  //     Deductible Exceptions
  //         Wind Or Hail Deductible    2%
  //
  // The $5,000 is the ordinary answer. The 2% is the finding. CP 03 21 applies
  // it to "the value(s) of the property that has sustained loss or damage" at
  // time of loss — not to the limit — and calculates it separately for each
  // building and separately for the personal property in each building. On a
  // $25,722,000 blanket across three buildings in Castle Rock, Colorado, that
  // is a materially different retention from $5,000 on the single peril the
  // school is most likely to suffer.
  //
  // Scope matters here more than anywhere. The crime coverage part declares
  // $2,500 deductibles and Educators Management Liability declares $5,000 and
  // $10,000; reading the whole document would blend three coverage parts into
  // one number. So the read is confined to the property declarations.
  function spReadPropertyDeductibles(text) {
    const src = spNorm(text);
    const s0 = src.search(/commercial\s+property\s+coverage\s+part\s+declarations/i);
    if (s0 < 0) return null;
    const rest = src.slice(s0);
    const endRe = /\n[^\n]{0,40}(?:commercial\s+(?:general\s+liability|crime|automobile|inland\s+marine)|commercial\s+liability\s+umbrella|equipment\s+breakdown)\s+coverage\s+part\s+declarations/i;
    const e0 = rest.slice(60).search(endRe);
    const zone = e0 >= 0 ? rest.slice(0, e0 + 60) : rest;

    const out = { items: 0, exceptions: [] };
    const flat = new Map();
    let m;
    const flatRe = /^\s*deductible\s+\$\s*([\d][\d,]*)\s*$/gim;
    while ((m = flatRe.exec(zone)) !== null) {
      const v = '$' + m[1];
      flat.set(v, (flat.get(v) || 0) + 1);
      out.items++;
    }
    if (flat.size) {
      const sorted = [...flat.entries()].sort((a, b) => b[1] - a[1]);
      out.allOtherPerils = sorted[0][0];
      // More than one flat deductible means the items differ, and saying "the
      // deductible is $5,000" would be true of some items and false of others.
      if (sorted.length > 1) out.varies = sorted.map(x => x[0] + ' \u00d7' + x[1]);
    }

    const excRe = /\b(wind(?:storm)?\s*(?:or|\/)?\s*hail|named\s+storm|hurricane|earthquake|theft|water\s+damage|sinkhole|flood)\s+deductible\s+(\d+(?:\.\d+)?\s*%|\$\s*[\d][\d,]*)/gi;
    const seen = new Set();
    while ((m = excRe.exec(zone)) !== null) {
      const peril = m[1].replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
      const value = m[2].replace(/\s+/g, '');
      const key = peril.toLowerCase() + '|' + value;
      if (seen.has(key)) continue;
      seen.add(key);
      out.exceptions.push({ peril, value, percentage: value.indexOf('%') >= 0 });
    }

    // The basis is in the endorsement, not the declarations. A percentage
    // without what it is a percentage OF is not an answer.
    if (/\bCP\s?03\s?21\b/.test(src) || /windstorm\s+or\s+hail\s+percentage\s+deductible/i.test(src)) {
      out.form = 'CP 03 21';
      // CP 03 21 states the value basis in two different sentences — "an
      // amount equal to a percentage of the value(s) of the property at time
      // of loss" and "equal to 1%, 2% or 5% ... of the value(s) of the
      // property that has sustained loss or damage". Anchoring on the word
      // "percentage" reads one and misses the other. The phrase that is
      // actually distinctive, and common to both, is the value-of-property
      // clause itself.
      if (/of\s+the\s+value\(?s?\)?\s+of\s+the\s+property/i.test(src)) {
        out.basisOfValue = 'value of the damaged property at time of loss';
      }
      out.perBuilding = /calculated\s+separately\s+for,?\s+and\s+applies\s+separately\s+to/i.test(src);
    }
    return (out.allOtherPerils || out.exceptions.length) ? out : null;
  }

  // Read blanketed property by following the policy's own group references.
  //
  // The declarations name the group for each coverage; the blanket schedule
  // holds each group's limit. Both halves are needed: the schedule alone
  // cannot say which group is buildings and which is contents when, as here,
  // every group is typed "Building and/or Personal Property Combined".
  function spReadBlanketProperty(text) {
    const src = spNorm(text);
    if (!/blanket\s+group/i.test(src)) return null;
    const AMT = '\\$\\s*([\\d][\\d,]{4,})';
    const out = {};

    const covToGroup = new Map();
    const covRe = /coverage\s+(building|all\s+personal\s+property|business\s+personal\s+property|personal\s+property)\b[^\n]{0,120}?blanket\s+group\s+0*(\d{1,4})/gi;
    let m;
    while ((m = covRe.exec(src)) !== null) {
      const kind = /building/i.test(m[1]) ? 'building' : 'bpp';
      if (!covToGroup.has(kind)) covToGroup.set(kind, m[2]);
    }
    if (!covToGroup.size) return null;

    // Read group -> amount ONLY from the blanket schedule.
    //
    // "Blanket Group 002" appears twice in this policy: once on the
    // declarations, as the pointer in the Limit Of Insurance column, and once
    // on the schedule, beside its amount. Scanning the whole document for a
    // group followed by a dollar figure matched the declarations occurrence
    // first and took the next dollar figure on the page — which is Business
    // Income, $418,744. It happened to give the right answer on the real
    // document only because the two pages are far apart. That is proximity
    // doing the work, and proximity is what put the wrong number in half the
    // findings this section has had to fix.
    const schedAt = src.search(/blanket\s+(?:schedule|statement\s+of\s+values)/i);
    const groups = new Map();
    // No schedule, no group amounts. Falling back to the whole document finds
    // the declarations occurrence of "Blanket Group 001" and takes the next
    // dollar figure on that page, which on this layout is the building
    // PREMIUM — $21,638. The amount lives on the schedule; if the schedule was
    // not read, the honest answer is that we do not have it.
    if (schedAt >= 0) {
      // Bind the amount to the group's own LINE, plus the two below it:
      //
      //     Blanket Group 001 Limit of Insurance
      //     Blanket Type: Building and/or Personal Property Combin $ 25,722,000
      //
      // A character window instead of a line window let the scan that started
      // at one group run past its own amount and consume the NEXT group's,
      // so group 002 came back empty and the personal property limit
      // disappeared. The schedule repeats the group number in prose
      // ("Covered Property Included Within Blanket Group 001:"), which is
      // exactly the kind of second mention a window swallows.
      // The next few lines that CARRY SOMETHING, not the next few lines.
      //
      // Group 001 is rendered as a markdown table and its amount sits one line
      // below its heading. Group 002 is rendered as an HTML table, and
      // stripping <thead>/<tbody> leaves four blank lines between the heading
      // and the amount — so a flat three-line window found group 001 and not
      // group 002, and the personal property limit disappeared from the
      // property row while the building limit read fine.
      const schedLines = src.slice(schedAt).split('\n');
      const amtRe = new RegExp(AMT);
      for (let i = 0; i < schedLines.length; i++) {
        const gm = /blanket\s+group\s+0*(\d{1,4})/i.exec(schedLines[i]);
        if (!gm || groups.has(gm[1])) continue;
        const near = [schedLines[i]];
        for (let j = i + 1; j < schedLines.length && near.length < 4; j++) {
          if (schedLines[j].trim()) near.push(schedLines[j]);
        }
        const am = amtRe.exec(near.join('\n'));
        if (am) groups.set(gm[1], '$' + am[1]);
      }
    }

    covToGroup.forEach((grp, kind) => {
      if (groups.has(grp)) out[kind] = groups.get(grp);
      out['_group_' + kind] = 'Blanket Group ' + String(grp).padStart(3, '0');
    });

    // Business income is stated on the declarations, not in the blanket.
    const decs = schedAt >= 0 ? src.slice(0, schedAt) : src;
    const bi = new RegExp('business\\s+income\\s+' + AMT, 'i').exec(decs);
    if (bi) out.businessIncome = '$' + bi[1];
    if (/extra\s+expense\s+included/i.test(src.replace(/\s+/g, ' '))) out.extraExpense = 'Included';

    if (!out.building && !out.bpp && !out.businessIncome) return null;
    out._coverageType = 'Blanket';
    // These are insured values read from labelled fields, not figures found
    // near a label, so the roundness test must not veto them.
    out._valuation = true;
    return out;
  }

  // v2.17: PRIORITY — Extract property from Blanket Statement of Values pages FIRST
  // These pages contain the actual insured values; standard extraction may pick up policy numbers or sublimits
  //
  // v2.24: follow the group reference instead of guessing by position.
  //
  // Aspen View's Section 6 said "Commercial Property — coverage detected,
  // limits not parsed from declarations". Every figure is in the document. The
  // property declarations simply do not print a dollar amount in the Limit Of
  // Insurance column, because the property is BLANKETED across three
  // buildings:
  //
  //     Coverage       Building               Blanket Group 001
  //     Coverage       All Personal Property  Blanket Group 002
  //     Business Income                       $418,744
  //
  // and the amounts live on a later page:
  //
  //     Blanket Group 001   Building and/or Personal Property Combined  $25,722,000
  //     Blanket Group 002   Building and/or Personal Property Combined   $2,068,700
  //
  // That is a one-hop indirection, not a parse failure, and reporting it as
  // "limits not parsed" is the same inversion as issue #15: the document gave
  // a clear answer and we called it silence. Worse here, because it sends a
  // broker to check declarations that are already complete.
  //
  // Two things had to change. The page detector demanded the literal phrases
  // "blanket statement of values" or "blanket limits"; GuideOne titles the
  // page "COMMERCIAL PROPERTY COVERAGE PART - BLANKET SCHEDULE", so it found
  // nothing on every document in the set. And the fallback assigned amounts by
  // ORDINAL position — first dollar figure is Building, second is BPP — which
  // would have been wrong here anyway: both of these groups are typed
  // "Building and/or Personal Property Combined", and only the declarations
  // say which group carries the buildings and which the contents.
  pdfTexts.forEach(doc => {
    if (limits['Commercial Property']) return; // Already found
    if (!doc.pages || !Array.isArray(doc.pages)) return;
    const wholeDoc = doc.pages.map(p => p.text || '').join('\n');
    const followed = spReadBlanketProperty(wholeDoc);
    if (followed && (followed.building || followed.bpp || followed.businessIncome)) {
      limits['Commercial Property'] = followed;
      return;
    }
    console.log('  Checking doc for blanket pages:', doc.fileName);
    const blanketPages = doc.pages.filter(p => {
      const pl = (p.text || '').toLowerCase();
      return (pl.includes('blanket statement of values') || pl.includes('blanket limits')
              || pl.includes('blanket schedule'))
             && (pl.includes('building') || pl.includes('bus per prop'));
    });
    console.log('  Found', blanketPages.length, 'blanket pages');
    if (blanketPages.length === 0) return;
    const blanketText = blanketPages.map(p => p.text).join(' ');
    const bPropLimits = {};
    // v2.17: PDF.js extracts tables in COLUMN order, not row order.
    // Text may appear as: "1 2 3 $ 37,287,671 $ 3,550,000 $ 1,000,000 100%"
    // OR with coverage names: "1 Building $ 37,287,671 2 Bus Per Prop $ 3,550,000"
    // Strategy: try row format first, then column format
    
    // Strategy 1: Row format (pdfplumber/some PDF renderers)
    let bm = blanketText.match(/1\s+Building\s+\$\s*([\d][\d,]*)/i)
      || blanketText.match(/Building\s+\$\s*([\d][\d,]*)/i);
    if (bm) bPropLimits.building = '$' + bm[1];
    
    bm = blanketText.match(/2\s+Bus\s+Per\s+Prop\s+\$\s*([\d][\d,]*)/i)
      || blanketText.match(/Bus\s+Per\s+Prop\s+\$\s*([\d][\d,]*)/i);
    if (bm) bPropLimits.bpp = '$' + bm[1];
    
    bm = blanketText.match(/3\s+B\s*us\s+Income[^$]*?\$\s*([\d][\d,\s]*\d)/i)
      || blanketText.match(/Bus(?:iness)?\s+Income[^$]*?\$\s*([\d][\d,\s]*\d)/i);
    if (bm) bPropLimits.businessIncome = '$' + bm[1].replace(/\s/g, '');
    
    // Strategy 2: Column format (PDF.js) — extract all $ amounts in order
    // In column format, blanket 1=Building, 2=BPP, 3=Business Income
    if (!bPropLimits.building) {
      const dollarAmounts = [];
      const dollarRe = /\$\s*([\d][\d,\s]*\d)/g;
      let dm;
      while ((dm = dollarRe.exec(blanketText)) !== null) {
        const cleaned = dm[1].replace(/\s/g, '');
        const val = parseInt(cleaned.replace(/,/g, ''), 10);
        if (val >= 100000) dollarAmounts.push(cleaned); // Only significant amounts (>$100K)
      }
      console.log('  Column extraction dollar amounts:', dollarAmounts);
      if (dollarAmounts.length >= 1) bPropLimits.building = '$' + dollarAmounts[0];
      if (dollarAmounts.length >= 2) bPropLimits.bpp = '$' + dollarAmounts[1];
      if (dollarAmounts.length >= 3) bPropLimits.businessIncome = '$' + dollarAmounts[2];
    }
    // Coinsurance
    const coinsMatches = [...blanketText.matchAll(/(100|90|80|50)%/g)];
    if (coinsMatches.length > 0) bPropLimits._coinsurance = coinsMatches[0][1] + '%';
    bPropLimits._coverageType = 'Blanket';
    console.log('  Blanket extraction results:', JSON.stringify(bPropLimits));
    console.log('  blanketText first 200 chars:', blanketText.substring(0, 200));
    if (bPropLimits.building || bPropLimits.bpp || bPropLimits.businessIncome) {
      console.log('  >>> SETTING limits[Commercial Property] from blanket!');
      limits['Commercial Property'] = bPropLimits;
    }
  });

  // A coverage written INSIDE another form has no declarations limit to find.
  //
  // Aspen View's Equipment Breakdown is PCP 73 57, attached as "Additional
  // Coverage - Equipment Breakdown" on the property form. Breakdown loss is
  // payable within the Commercial Property limit, with internal sublimits, and
  // no separate limit appears on any declarations page. Reporting that as
  // "limits not parsed" points a broker at a declarations line that does not
  // exist. The document's answer is that there is no separate limit, and that
  // is a different statement from not knowing.
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages) return;
    const CTx = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
    if (!CTx) return;
    const txt = doc.pages.map(p => p.text || '').join('\n');
    const re = /additional\s+coverage\s*[\u2013\u2014:\-]?\s*([A-Za-z][A-Za-z&'\-/ ]{4,50})/gi;
    let m;
    while ((m = re.exec(txt)) !== null) {
      const cov = CTx.canonical(m[1]);
      if (!cov || cov === 'Commercial Property') continue;
      const form = (window.spRetroAnchor && window.spRetroAnchor.formAt(txt, m.index)) || null;
      if (limits[cov] && Object.keys(limits[cov]).some(k => !k.startsWith('_'))) continue;
      limits[cov] = Object.assign({}, limits[cov] || {}, {
        _writtenAs: 'Additional Coverage under the property form'
          + (form ? ' (' + form.replace(/^([A-Z]+)(\d{2})(\d{2})$/, '$1 $2 $3') + ')' : '')
          + ' \u2014 no separate limit; loss is payable within the Commercial Property limit, subject to the internal sublimits in that form.'
      });
    }
  });

  // A coverage written INSIDE another form has no declarations limit to find.
  //
  // Aspen View's Equipment Breakdown is PCP 73 57, attached as "Additional
  // Coverage - Equipment Breakdown" on the property form. Breakdown loss is
  // payable within the Commercial Property limit, with internal sublimits, and
  // no separate limit appears on any declarations page. Reporting that as
  // "limits not parsed" points a broker at a declarations line that does not
  // exist. The document's answer is that there is no separate limit, and that
  // is a different statement from not knowing.
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages) return;
    const CTx = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
    if (!CTx) return;
    const txt = doc.pages.map(p => p.text || '').join('\n');
    const re = /additional\s+coverage\s*[\u2013\u2014:\-]?\s*([A-Za-z][A-Za-z&'\-/ ]{4,50})/gi;
    let m;
    while ((m = re.exec(txt)) !== null) {
      const cov = CTx.canonical(m[1]);
      if (!cov || cov === 'Commercial Property') continue;
      const form = (window.spRetroAnchor && window.spRetroAnchor.formAt(txt, m.index)) || null;
      if (limits[cov] && Object.keys(limits[cov]).some(k => !k.startsWith('_'))) continue;
      limits[cov] = Object.assign({}, limits[cov] || {}, {
        _writtenAs: 'Additional Coverage under the property form'
          + (form ? ' (' + form.replace(/^([A-Z]+)(\d{2})(\d{2})$/, '$1 $2 $3') + ')' : '')
          + ' \u2014 no separate limit; loss is payable within the Commercial Property limit, subject to the internal sublimits in that form.'
      });
    }
  });


  const RA_PAIRS = (typeof window !== 'undefined' && window.spRetroAnchor) || null;

  // A SUBLIMIT MAY NOT FILL A PRIMARY LIMIT SLOT.
  //
  // Tulsa Honors reported "Building $100,000" on a school carrying
  // $26,582,592 of buildings. The $100,000 is real and correctly read — it is
  // the Outdoor Property sublimit from SECTION 2 - SUPPLEMENTAL COVERAGE,
  // fourteen entries down a schedule of named extensions. Every plausibility
  // guard passed it, because it IS a limit; it is simply not that one.
  //
  // No guard based on the figure can catch this, which is why it needs a
  // guard based on WHERE the figure came from. A supplemental or extensions
  // schedule states what the policy adds around its primary cover; a headline
  // limit never lives there.
  //
  // Scoped to property, deliberately. The same words head sections that DO
  // carry primary limits elsewhere — Aspen View states its Educators
  // Management Liability and Sexual Misconduct limits under "ADDITIONAL
  // COVERAGES" on the CGL declarations, and those are the real ones.
  const _SP_SUBLIMIT_SECTION = /\b(?:supplemental\s+coverages?|coverage\s+extensions?|sublimits?\s+of\s+(?:liability|insurance)|schedule\s+of\s+sublimits)\b/i;
  const _SP_SUBLIMIT_END = /\b(?:limits?\s+of\s+liability|declarations?\s+page|policy\s+limit\s+of\s+liability|in\s+witness\s+whereof)\b/i;

  function spInPropertySublimitSchedule(text, index) {
    const before = String(text || '').slice(0, index);
    let last = -1, m;
    const re = new RegExp(_SP_SUBLIMIT_SECTION.source, 'gi');
    while ((m = re.exec(before)) !== null) last = m.index;
    if (last < 0) return false;
    return !_SP_SUBLIMIT_END.test(before.slice(last));
  }

  // PROPERTY STATED AS A GRID — structure beats proximity.
  //
  // A surplus-lines or London-market property declarations page puts its
  // labels and its amounts in different columns, so no character window can
  // reach from "Building" to its own limit. Read as a table the pairing is
  // unambiguous, so where the grid yields a figure it OVERRIDES anything a
  // proximity scan produced: on Tulsa Honors that scan had filled the
  // building slot with a $100,000 supplemental sublimit for Outdoor Property.
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages || !RA_PAIRS) return;
    const raw = doc.pages.map(p => p.text || '').join('\n');
    const pairs = RA_PAIRS.readPairedTableValues(raw);
    if (!pairs.length) return;
    const got = {};
    pairs.forEach(({ label, value }) => {
      const l = label.toLowerCase();
      if (/^building/.test(l) && !got.building) got.building = value;
      else if (/(?:business\s+)?personal\s+property/.test(l) && !got.bpp) got.bpp = value;
      else if (/time\s+element|business\s+income/.test(l) && !got.businessIncome) got.businessIncome = value;
      else if (/policy\s+limit\s+of\s+liability/.test(l) && !got._policyLimit) got._policyLimit = value;
    });
    if (!got.building && !got.bpp && !got.businessIncome) return;
    // The context a sum insured needs: what it is valued on, and against what.
    const norm = spNorm(raw);
    const tiv = /total\s+insured\s+value[^$\n]{0,40}\$\s*([\d][\d,]{4,})/i.exec(norm);
    if (tiv) got._tiv = '$' + tiv[1];
    const val = /valuation[^\n]{0,60}?(replacement\s+cost|actual\s+cash\s+value|agreed\s+value)/i.exec(norm);
    if (val) got._valuationBasis = val[1].replace(/\s+/g, ' ');
    // These are SUMS INSURED, not chosen limits: $26,582,592 is an appraised
    // value and no multiple of 500. Without this flag the roundness rule that
    // keeps policy numbers out of limit slots would discard all three.
    got._valuation = true;
    limits['Commercial Property'] = Object.assign({}, limits['Commercial Property'] || {}, got);
  });

  /* PROPERTY STATED AS A SCHEDULE OF LOCATIONS — the third layout.
   *
   * Matrix Human Services' package policy states its property limits sixteen
   * times over, once per location, with the coverage name in the middle column
   * and the amount at the end of the row. readPairedTableValues returns
   * nothing for that shape — every label cell says "Coverage" — so the report
   * said the limits could not be parsed on a policy that states them
   * repeatedly, and Section 6 benchmarked a $10.6m schedule as a gap.
   *
   * Runs only where the grid found nothing, so a document that has a real
   * Building/BPP grid keeps it: a schedule is the weaker statement of the two
   * because the total is a sum we computed rather than a figure the carrier
   * printed, and it is labelled as such.
   */
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages || !RA_PAIRS || !RA_PAIRS.readScheduledProperty) return;
    const cur = limits['Commercial Property'] || {};
    if (cur.building || cur.bpp) return;
    const sch = RA_PAIRS.readScheduledProperty(doc.pages.map(p => p.text || '').join('\n'));
    if (!sch) return;
    const money = n => '$' + Number(n).toLocaleString('en-US');
    const got = { _scheduled: {
      locations: sch.locations, contentsOnly: sch.contentsOnly, counts: sch.counts
    } };
    if (sch.building) got.building = money(sch.building);
    if (sch.bpp) got.bpp = money(sch.bpp);
    if (sch.businessIncome) got.businessIncome = money(sch.businessIncome);
    // A sum of scheduled values is not a chosen limit, so it is exempt from the
    // multiple-of-500 rule for the same reason a sum insured is.
    got._valuation = true;
    got._tiv = money(sch.building + sch.bpp + sch.businessIncome);
    limits['Commercial Property'] = Object.assign({}, cur, got);
  });

  /* PROPERTY LIMITS, READ BY POSITION RATHER THAN BY LAYOUT.
   *
   * The first consumer migrated to the field reader. It runs LAST and
   * overrides, which is the same shape the grid pass already used to override
   * proximity — a more structural reader wins over a less structural one.
   * Everything before it stays as the fallback, so this can only match or
   * improve on what shipped, and reverting is deleting this block.
   *
   * What it replaces is five readers each written against one LAYOUT. What it
   * asks instead is one question about POSITION: is this figure in a labelled
   * field, inside the property scope, typed as a limit rather than a sublimit,
   * a retention, a premium, a clause or a notice.
   *
   * The corpus pins the answer across all five layouts — declarations grid,
   * LlamaParse markdown, per-location schedule, surplus-lines sums insured,
   * and the two-line page — which is what makes swapping the reader under a
   * live extractor a thing that can be checked rather than hoped.
   */
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages || typeof window === 'undefined' || !window.spReadFields) return;
    /* PAGE BY PAGE, AND THE FIRST PAGE THAT ANSWERS WINS.
     *
     * Read across a whole document the uniqueness rule below is meaningless:
     * "Building" appears dozens of times in a 200-page policy — in the
     * coverage form, in exclusions, in endorsements — so every slot looked
     * like a schedule and the reader declined every one of them.
     *
     * A declarations page is a page. Reading per page restores what the
     * uniqueness rule is actually asking — does THIS page state the slot once
     * — and it reaches the right page first because declarations come at the
     * front.
     */
    const pages = doc.pages || [];
    let fields = null;
    for (const pg of pages) {
      const f = (window.spAllFields || window.spReadFields)(pg);
      if (f.some(x => x.money && /^building|personal\s+property|business\s+income/i.test(x.label))) {
        fields = f; break;
      }
    }
    if (!fields || !fields.length) return;
    const believable = f =>
      // 'grid' is a structural read — the amount's own cell, its row label and
      // its column — so it is at least as strong as a labelled field and
      // stronger than a row reconstructed from whitespace. Omitting it here
      // silently rejected every limit the grid reader found, which on a
      // subscription property policy meant all of them.
      (f.provenance === 'field' || f.provenance === 'row' || f.provenance === 'grid')
      && !['sublimit', 'retention', 'cost', 'date', 'attribute'].includes(f.kind)
      && f.money;
    // Scoped to property where the document says which part it is, and
    // unscoped where it does not — a monoline property policy has one scope
    // and it is the whole document.
    /* SCOPE NARROWS, IT DOES NOT EXCLUDE.
     *
     * The first cut accepted only fields under a PROPERTY heading. A
     * surplus-lines property policy states its sums insured under "LIMITS OF
     * LIABILITY" — the document is monoline, so no heading ever says the word
     * "property" — and the building sum insured was skipped on a policy that
     * is nothing but property.
     *
     * Prefer the property scope where the document HAS one; fall back to the
     * whole document where it does not. A heading that narrows is useful; a
     * heading that is required is a layout assumption wearing a structural
     * disguise.
     */
    const inProp = f => !!(f.scope && /PROPERTY|SCHEDULE OF VALUES/i.test(f.scope));
    /* THE FIELD READER READS FIELDS. IT DOES NOT AGGREGATE.
     *
     * The corpus caught this on the first migration attempt. A per-location
     * schedule states Business Personal Property three times — $200,000,
     * $1,000,000, $150,000 — and the right answer is the sum, $1,350,000,
     * because no single "BPP limit" exists on that placement. Taking the first
     * field gave $200,000 and would have understated a schedule by a million
     * dollars while looking entirely plausible.
     *
     * So: this reader answers only where the document states the slot ONCE.
     * Several statements mean the page is a schedule, and aggregating a
     * schedule is a different job with its own rules — which readScheduledProperty
     * already does, and which runs before this and is left to own the answer.
     *
     * Reading and aggregating being separate concerns is the general form of
     * that, and worth keeping when the remaining consumers migrate.
     */
    const pick = (re) => {
      const all = fields.filter(f => believable(f) && re.test(f.label));
      const scoped = all.filter(inProp);
      const hits = scoped.length ? scoped : all;
      const distinct = [...new Set(hits.map(f => f.value.replace(/\s+/g, '')))];
      return distinct.length === 1 ? distinct[0] : null;
    };
    const got = {};
    const building = pick(/^building(?:\s|$)/i);
    const bpp = pick(/^(?:business\s+)?personal\s+property|^all\s+personal\s+property|^contents(?:\s|$)/i);
    const bi = pick(/^business\s+income|^time\s+element/i);
    if (building) got.building = building;
    if (bpp) got.bpp = bpp;
    if (bi) got.businessIncome = bi;
    if (!Object.keys(got).length) return;
    // Sums insured are not multiples of 500, and neither is a schedule total.
    got._valuation = true;
    got._readBy = 'field-reader';
    limits['Commercial Property'] = Object.assign({}, limits['Commercial Property'] || {}, got);
  });



  // Deductibles attach to whatever property limits were found, and stand on
  // their own when none were: a retention is a fact about the program even if
  // the limit did not parse.
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages) return;
    const ded = spReadPropertyDeductibles(doc.pages.map(p => p.text || '').join('\n'));
    if (!ded) return;
    const cur = limits['Commercial Property'];
    if (cur && !cur._deductibles) cur._deductibles = ded;
    else if (!cur) limits['Commercial Property'] = { _deductibles: ded };
  });

  // v2.17: PRIORITY — Extract HNOA from GL endorsement pages FIRST
  // v2.20.14: Skip if a high-trust Auto extraction already populated this from a real Auto policy
  pdfTexts.forEach(doc => {
    if (limitsTrust['Commercial Auto'] && limitsTrust['Commercial Auto'].perOccurrence === 2) return;
    if (limitsTrust['Commercial Auto'] && limitsTrust['Commercial Auto'].combinedSingleLimit === 2) return;
    if (limits['Commercial Auto'] && limits['Commercial Auto'].combinedSingleLimit) return;
    if (!doc.pages || !Array.isArray(doc.pages)) return;
    // Broad search: look for any page with "hired" and "auto" (non-owned may be hyphenated differently)
    const hnoaPages = doc.pages.filter(p => {
      const pl = (p.text || '').toLowerCase();
      return pl.includes('hired') && pl.includes('auto') && 
             (pl.includes('non-owned') || pl.includes('non owned') || pl.includes('nonowned') || pl.includes('non-'));
    });
    console.log('  HNOA pages found:', hnoaPages.length, 'in', doc.fileName);
    if (hnoaPages.length === 0) {
      // Fallback: search ALL pages of this doc for the HNOA endorsement title
      const hnoaFallback = doc.pages.filter(p => {
        const pl = (p.text || '').toLowerCase();
        return /hired\s+and\s+non/i.test(p.text || '') || /hnoa/i.test(p.text || '') || /hired\s+auto/i.test(p.text || '');
      });
      console.log('  HNOA fallback pages:', hnoaFallback.length);
      if (hnoaFallback.length === 0) return;
      // Use the first fallback page
      const fbText = hnoaFallback[0].text || '';
      console.log('  HNOA fallback text (200):', fbText.substring(0, 200));
      // Extract $1M limit - find all dollar amounts on this page
      const amounts = [];
      const re = /\$\s*([\d][\d,]*)/g;
      let m;
      while ((m = re.exec(fbText)) !== null) {
        const v = parseInt(m[1].replace(/,/g,''), 10);
        if (v >= 500000 && v <= 10000000) amounts.push(m[1]);
      }
      // Also find standalone large numbers
      const numRe = /(?:^|\s)([1-9][\d,]{5,})(?:\s|$)/gm;
      while ((m = numRe.exec(fbText)) !== null) {
        const v = parseInt(m[1].replace(/,/g,''), 10);
        if (v >= 500000 && v <= 10000000 && !amounts.includes(m[1])) amounts.push(m[1]);
      }
      console.log('  HNOA fallback amounts:', amounts);
      if (amounts.length >= 1) {
        // v2.21.4: Same fix as primary path — pick smallest value
        const numericAmounts = amounts.map(a => ({ raw: a, num: parseInt(a.replace(/,/g, ''), 10) }));
        numericAmounts.sort((a, b) => a.num - b.num);
        const chosen = numericAmounts[0].raw;
        setLimits('Commercial Auto', { perOccurrence: '$' + chosen, _hnoaEndorsement: true }, 1);
        console.log('  HNOA limit from fallback (smallest):', '$' + chosen);
      }
      return;
    }
    // Search ALL HNOA pages for dollar amounts (first match may be a form schedule with no limits)
    let hnoaAmounts = [];
    for (const hp of hnoaPages) {
      const hpText = hp.text || '';
      const dollarRe = /\$\s*([\d][\d,]*)/g;
      let dm;
      while ((dm = dollarRe.exec(hpText)) !== null) {
        const val = parseInt(dm[1].replace(/,/g, ''), 10);
        if (val >= 500000 && val <= 10000000) hnoaAmounts.push(dm[1]);
      }
      // Also standalone numbers
      const numRe2 = /(?:^|\s)([1-9][\d,]{5,})(?:\s|$)/gm;
      while ((dm = numRe2.exec(hpText)) !== null) {
        const val = parseInt(dm[1].replace(/,/g, ''), 10);
        if (val >= 500000 && val <= 10000000 && !hnoaAmounts.includes(dm[1])) hnoaAmounts.push(dm[1]);
      }
      if (hnoaAmounts.length > 0) {
        console.log('  HNOA limit found on page:', hpText.substring(0, 100));
        break; // Found amounts, stop searching
      }
    }
    console.log('  HNOA amounts:', hnoaAmounts);
    if (hnoaAmounts.length >= 1) {
      // v2.21.4: Pick the SMALLEST value among the HNOA amounts. HNOA endorsements
      // typically grant $1M of liability — if the page also mentions $3M aggregate
      // (which appears on many GL endorsement pages because GL aggregate applies to
      // HNOA too), the per-occurrence limit is the smaller of the two.
      // OLD behavior took hnoaAmounts[0] which could be $3M aggregate.
      const numericAmounts = hnoaAmounts.map(a => ({ raw: a, num: parseInt(a.replace(/,/g, ''), 10) }));
      numericAmounts.sort((a, b) => a.num - b.num);
      const chosen = numericAmounts[0].raw;
      // v2.20.14: Use setLimits with trust=1 so scoped Auto extraction (trust=2) can upgrade
      setLimits('Commercial Auto', { perOccurrence: '$' + chosen, _hnoaEndorsement: true }, 1);
      console.log('  HNOA limit extracted (smallest):', '$' + chosen);
    }
  });
  
  // v2.17: Mark if ANY document contains HNOA text (to prevent standard auto from adding UM/UIM)
  // v2.20.13: Only flag HNOA-only mode when HNOA appears in a NON-Auto document.
  // A real Auto policy (Business Auto, Commercial Auto, ISO CA DS form) inherently mentions
  // hired + non-owned autos. The previous logic blocked Auto extraction whenever an Auto
  // policy was uploaded — leaving Section 5 with "limits not parsed from declarations".
  let hasHNOAText = false;
  pdfTexts.forEach(doc => {
    if (!doc.pages) return;
    const allDocText = doc.pages.map(p => p.text || '').join(' ').toLowerCase();
    const isStandaloneAutoPolicy =
      /business\s+auto\s+(?:coverage|policy|declarations)/i.test(allDocText)
      || /commercial\s+auto\s+(?:coverage|policy|declarations)/i.test(allDocText)
      || /\bca\s+ds\s+\d/i.test(allDocText) // ISO Commercial Auto Declarations form
      || /schedule\s+of\s+coverages\s+and\s+covered\s+autos/i.test(allDocText);
    if (isStandaloneAutoPolicy) return; // Don't flag HNOA-mode based on a real Auto policy
    if (allDocText.includes('hired') && (allDocText.includes('non-owned') || allDocText.includes('non owned') || allDocText.includes('non-'))) {
      hasHNOAText = true;
    }
  });


  pdfTexts.forEach(f => {
    // Split pages into declarations vs. other, process dec pages first
    let decText = '';
    let allText = '';
    
    if (f.pages && Array.isArray(f.pages)) {
      const decPages = [];
      const otherPages = [];
      f.pages.forEach(p => {
        if (p.text && p.text.trim().length > 20) {
          if (isDeclarationsPage(p.text)) {
            decPages.push(p.text);
          } else {
            otherPages.push(p.text);
          }
        }
      });
      // Normalised once, here, for every extractor in this function.
      //
      // The umbrella declares its limit as a markdown table row whose dollar
      // sign is its OWN COLUMN:
      //
      //     | Each Occurrence Limit | $ | 5,000,000 |
      //
      // so a pattern reading "$ then digits" cannot match across the pipe. It
      // fell through to a whole-file search and returned $1,000,000/$3,000,000
      // — the SCHEDULE OF UNDERLYING INSURANCE, which is the primary GL, not
      // the umbrella. The policy provides $5,000,000/$5,000,000, so the row
      // understated the umbrella fivefold and then marked it BELOW STANDARD
      // for being what it is not.
      //
      // Every other reader in this engine was taught the table shapes one at a
      // time. This is the text the rest of them work from, and it was still
      // raw.
      decText = spNorm(decPages.join('\n'));
      allText = spNorm(decPages.concat(otherPages).map(t => t).join('\n'));
      if (!allText) return;
    } else if (f.text) {
      allText = spNorm(f.text);
      decText = allText; // legacy format: treat entire text as potential declarations
    } else {
      return;
    }
    
    // Try declarations text first; fall back to all text for any missing limits
    // v2.10: Added bare number fallback + minimum value threshold ($5K) to filter noise
    // The minVal filter prevents picking up $100 from terrorism endorsement text
    function tryExtract(labelRegex, bareLabel, minVal) {
      const mv = minVal || 5000;
      // Phase 1: Standard $-prefixed extraction with minimum value filter
      if (decText) {
        const result = extractBestDollar(decText, labelRegex, mv);
        if (result) return result;
      }
      const result2 = extractBestDollar(allText, labelRegex, mv);
      if (result2) return result2;
      // Phase 2: Bare number fallback (handles split $ or missing $ in PDF tables)
      if (bareLabel) {
        if (decText) {
          const bare = extractBareNumber(decText, bareLabel, mv);
          if (bare) return bare;
        }
        return extractBareNumber(allText, bareLabel, mv);
      }
      return null;
    }
    
    // Coverage-scoped extraction: ONLY search pages that contain the specific coverage header.
    // This prevents EPLI ($25K) from picking up GL's $1M aggregate on a different dec page.
    // Falls back to tryExtract if no scoped pages found.
    // v2.10: Added bare number fallback + minimum value threshold
    function tryExtractScoped(labelRegex, scopeKeywords, bareLabel, minVal) {
      const mv = minVal || 5000;
      if (f.pages && Array.isArray(f.pages)) {
        const scopedPages = f.pages.filter(p => {
          if (!p.text) return false;
          const pl = p.text.toLowerCase();
          return scopeKeywords.some(kw => pl.includes(kw));
        });
        if (scopedPages.length > 0) {
          const scopedText = scopedPages.map(p => p.text).join('\n');
          // Phase 1: Standard extraction
          const result = extractBestDollar(scopedText, labelRegex, mv);
          if (result) return result;
          // Phase 2: Bare number fallback on scoped pages
          if (bareLabel) {
            const bare = extractBareNumber(scopedText, bareLabel, mv);
            if (bare) return bare;
          }
        }
      }
      // Fall back to general extraction if no scoped pages found
      return tryExtract(labelRegex, bareLabel, minVal);
    }
    
    const txtLower = allText.toLowerCase();
    
    // --- General Liability declarations ---
    // v2.20: PRIORITY scoped extraction. The previous logic searched every "declarations-like" page
    // across the entire policy and grabbed sub-limit values from GL endorsement schedules
    // (e.g. Honor Roll Elite enhancement) that listed sublimits like "Property Damage To
    // Borrowed Equipment $10,000 Each Occurrence". To prevent that, we now find the actual
    // GL Coverage Part Declarations page first and scope all extraction to its text. Falls
    // back to the previous all-text behavior only if no scoped page is found.
    if (txtLower.includes('commercial general liability') || txtLower.includes('general liability') || 
        ((txtLower.includes('each occurrence') || txtLower.includes('ach occurrence')) && txtLower.includes('aggregate'))) {
      const glLimits = {};

      // v2.20.5: Per-page candidate scoring instead of filtering.
      // Earlier versions tried to filter pages with regex (too strict missed real dec
      // pages, too loose caught Crime/EL/Umbrella decs and endorsements). The new
      // approach: scan EVERY page, run the tier-based GL extraction on each, and pick
      // the page that produces the most plausible GL limits structure. A real GL dec
      // page produces 4-6 limits with a clear tier pattern; non-GL pages produce noise.
      let glScopedText = null;
      const glScopedDebug = [];
      if (f.pages && Array.isArray(f.pages)) {
        const candidates = [];
        f.pages.forEach(p => {
          if (!p.text) return;
          const pt = spScoreText(p);
          // Must mention GL somewhere
          if (!/general\s+liability/i.test(pt)) return;
          // Named only in an underlying schedule -> it belongs to the policy beneath.
          if (spCoverageOnlyInUnderlyingSchedule(pt, /general\s+liability/i)) return;
          // Must have at least Each Occurrence AND General Aggregate as labels
          if (!/each\s+occurrence/i.test(pt)) return;
          if (!/general\s+aggregate/i.test(pt)) return;
          // Reject endorsement pages outright — they can technically have these labels
          // but they're sublimit schedules, not the headline GL dec page.
          if (/this\s+endorsement\s+(?:changes|modifies)\s+the\s+policy/i.test(pt)) return;
          if (/\bhonor\s+roll\s+elite\b/i.test(pt)) return;
          // Reject other coverage parts whose dec page also references GL.
          if (/(?:crime|workers?\s+(?:compensation|comp))\s+coverage\s+part\s+declarations/i.test(pt)) return;
          candidates.push(p);
        });
        glScopedDebug.push('GL candidate pages: ' + candidates.length);
        // Pick the SHORTEST candidate — the real dec page is typically a focused 1-page
        // declarations sheet (~500-1500 chars). Endorsement schedules and reference
        // pages that mention GL+each-occurrence+aggregate are usually much longer.
        // If we have multiple short candidates, concatenating them is safe because
        // the tier algorithm aggregates dollar values across them.
        if (candidates.length > 0) {
          // Sort by length, prefer shorter pages first
          candidates.sort((a, b) => (a.text.length - b.text.length));
          // Take pages up to ~2000 chars total (typical GL dec is < 1000)
          const picked = [];
          let totalLen = 0;
          for (const p of candidates) {
            if (p.text.length > 3000) continue; // Skip obviously-too-long pages
            picked.push(p);
            totalLen += p.text.length;
            if (totalLen > 2500) break;
          }
          if (picked.length > 0) {
            glScopedText = picked.map(p => spScoreText(p)).join('\n');
            glScopedDebug.push('picked: ' + picked.length + ' page(s)');
          }
        }
      }
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 GL scoping]', f.fileName || 'unknown', glScopedDebug.join(' | '), 'scopedTextLen:', glScopedText ? glScopedText.length : 0);
      }

      // Helper: run all 6 GL extractions against a target text. Returns {} if nothing found.
      // v2.20.2: Now handles BOTH row-format ("Each Occurrence $1,000,000") AND column-format
      // (PDF.js extracts: "Each Occurrence Damage... Med Expense Personal... General Aggregate
      //  Prod-Comp $1,000,000 $1,000,000 $10,000 $1,000,000 $3,000,000 $3,000,000")
      // by detecting positional alignment when the proximity regex fails.
      const runGLExtractions = (targetText) => {
        if (!targetText) return {};
        const out = {};
        const findBest = (re1, re2, minVal) => {
          const r1 = extractBestDollar(targetText, re1, minVal || 5000);
          if (r1) return r1;
          if (re2) return extractBestDollar(targetText, re2, minVal || 5000);
          return null;
        };

        // --- STRATEGY 1: Row-format proximity matching (works for clean text) ---
        // Each Occurrence (also handles OCR dropping the 'E' → "ach Occurrence")
        let v = findBest(
          new RegExp('e?ach\\s+occurrence[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,20}e?ach\\s+occurrence', 'i')
        );
        if (v) out.eachOccurrence = v;
        v = findBest(
          new RegExp('general\\s+aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,20}general\\s+aggregate', 'i')
        );
        if (v) out.generalAggregate = v;
        v = findBest(
          new RegExp('products[\\s\\-\\u2010-\\u2014]*(?:completed|comp)\\s+op[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,40}products[\\s\\-\\u2010-\\u2014]*(?:completed|comp)', 'i')
        );
        if (v) out.productsCompletedOps = v;
        v = findBest(
          new RegExp('personal\\s+(?:&|and)\\s+advert[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          null
        );
        if (v) out.personalAdvertising = v;
        v = findBest(
          new RegExp('(?:damage\\s+to\\s+(?:rented|premises)|fire\\s+damage\\s+legal)[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          null,
          1000
        );
        if (v) out.premisesDamage = v;
        v = findBest(
          new RegExp('med(?:ical)?\\s+exp(?:ense)?[^$]{0,80}\\$\\s*(' + D + ')', 'i'),
          null,
          1000
        );
        if (v) {
          const numeric = parseInt(v.replace(/[^0-9]/g, ''), 10);
          if (!(numeric >= 1900 && numeric <= 2050 && !/[,]/.test(v))) {
            out.medicalExpense = v;
          }
        }

        // --- STRATEGY 2: Domain-aware value assignment for PDF.js column extraction ---
        // PDF.js extracts 2-column dec pages as [all labels] then [all values], but the
        // values may NOT come out in label order (they come out in PDF text-run order,
        // which often groups by visual column rather than row). So we cannot pair
        // positionally. Instead, we use insurance domain knowledge:
        //   • Med Expense is ALWAYS the smallest value (~$5K–$25K)
        //   • General Aggregate and Products-Completed Aggregate are the LARGEST values
        //     (typically equal, e.g. both $3M) and almost always >= 2x Each Occurrence
        //   • Each Occurrence, Damage to Premises, Personal & Adv are typically equal
        //     to each other (the "per-occurrence" tier, e.g. all $1M)
        // Algorithm: detect which labels are present, collect all $-amounts on the page,
        // then assign by sorted value tiers.
        const labelsPresent = {
          eachOccurrence: /e?ach\s+occurrence/i.test(targetText),
          premisesDamage: /damage\s+to\s+(?:rented|premises)|fire\s+damage\s+legal/i.test(targetText),
          medicalExpense: /med(?:ical)?\s+exp(?:ense)?/i.test(targetText),
          personalAdvertising: /personal\s+(?:&|and)\s+advert/i.test(targetText),
          generalAggregate: /general\s+aggregate/i.test(targetText),
          productsCompletedOps: /products[\s\-\u2010-\u2014]*(?:completed|comp)\s+op/i.test(targetText)
        };
        // Collect every plausible $-amount on the scoped text. Reject premium-like
        // values (containing decimals like $9,432.00) and noise.
        const dollarRe = /\$\s*([\d][\d,. ]+\d)\b/g;
        const allDollars = [];
        let dm;
        while ((dm = dollarRe.exec(targetText)) !== null) {
          const raw = dm[1];
          // Skip values with decimals (those are premiums, not limits)
          if (/\.\d{2}\b/.test(raw)) continue;
          const cleaned = cleanDollar(raw);
          const num = dollarToNum(cleaned);
          // Insurance limits are typically $1K to $50M; reject anything outside
          if (num >= 1000 && num <= 100000000) {
            allDollars.push({ val: cleaned, num: num });
          }
        }
        if (allDollars.length >= 2) {
          // Bucket dollar values into TIERS (limits with the same numeric value go together)
          const tierMap = {};
          allDollars.forEach(d => {
            const k = d.num;
            if (!tierMap[k]) tierMap[k] = { val: d.val, num: k, count: 0 };
            tierMap[k].count++;
          });
          const tiers = Object.values(tierMap).sort((a, b) => a.num - b.num); // ascending
          // Tier interpretation:
          //   smallest tier   → Medical Expense
          //   largest tier    → Aggregate(s) (General Agg + Products-Comp share)
          //   middle/repeated → Each Occurrence + Premises + Personal & Adv
          const smallest = tiers[0];
          const largest = tiers[tiers.length - 1];
          const middle = tiers.length >= 3 ? tiers[Math.floor((tiers.length - 1) / 2)] : tiers[0];

          if (labelsPresent.medicalExpense && !out.medicalExpense && smallest && smallest.num <= 50000) {
            // Reject year-like values
            if (!(smallest.num >= 1900 && smallest.num <= 2050 && !/[,]/.test(smallest.val))) {
              out.medicalExpense = smallest.val;
            }
          }
          if (labelsPresent.generalAggregate && !out.generalAggregate && largest) {
            out.generalAggregate = largest.val;
          }
          if (labelsPresent.productsCompletedOps && !out.productsCompletedOps && largest) {
            out.productsCompletedOps = largest.val;
          }
          // For per-occurrence tier, pick the highest non-aggregate, non-medical tier.
          // If only 2 tiers exist, the middle/per-occ tier IS the smaller one above smallest.
          let perOccTier = null;
          if (tiers.length >= 3) {
            // Skip smallest (med) and largest (agg). Pick the most common in-between, or
            // simply the second-largest as the per-occ tier.
            for (let i = tiers.length - 2; i >= 1; i--) {
              if (tiers[i].num !== largest.num && tiers[i].num !== smallest.num) {
                perOccTier = tiers[i];
                break;
              }
            }
          } else if (tiers.length === 2) {
            // Two tiers: smallest is med, largest is everything else (or aggregate).
            // If largest is >= 2 * smallest, treat smallest as med, largest as per-occ AND agg
            if (largest.num >= smallest.num * 2) {
              perOccTier = smallest.num <= 50000 ? largest : smallest;
            } else {
              perOccTier = largest;
            }
          }
          if (perOccTier) {
            if (labelsPresent.eachOccurrence && !out.eachOccurrence) out.eachOccurrence = perOccTier.val;
            if (labelsPresent.premisesDamage && !out.premisesDamage) out.premisesDamage = perOccTier.val;
            if (labelsPresent.personalAdvertising && !out.personalAdvertising) out.personalAdvertising = perOccTier.val;
          }
        }
        return out;
      };

      // v2.20: Try scoped GL dec pages first. If we got at least Each Occurrence OR an aggregate
      // on a real GL dec page, accept the scoped result as authoritative. Otherwise fall back
      // to full-text behavior.
      // v2.20.1: Relaxed acceptance — if the scoped page produced ANY headline limit, trust it
      // over the legacy all-text path which is far more likely to grab endorsement sublimits.
      let scoped = runGLExtractions(glScopedText);
      const scopedHasCore = scoped.eachOccurrence || scoped.generalAggregate || scoped.productsCompletedOps;
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 GL scoped result]', JSON.stringify(scoped), 'accepted:', !!scopedHasCore);
      }
      // v2.20.6: Check if a previous file has already provided high-trust GL data.
      // If so, skip the legacy fallback entirely — it would only contaminate good data.
      const hasHighTrustGL = limitsTrust['General Liability']
        && (limitsTrust['General Liability'].eachOccurrence === 2
            || limitsTrust['General Liability'].generalAggregate === 2);
      if (scopedHasCore) {
        // Trust = 2 (scoped extraction from this file)
        setLimits('General Liability', scoped, 2);
      } else if (!hasHighTrustGL) {
        // No high-trust data yet — try legacy fallback at trust = 1
        const legacyGL = {};
        let v = tryExtract(new RegExp('e?ach\\s+occurrence[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'e?ach\\s+occurrence')
             || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,20}e?ach\\s+occurrence', 'i'));
        if (v) legacyGL.eachOccurrence = v;
        v = tryExtract(new RegExp('general\\s+aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'general\\s+aggregate')
         || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,20}general\\s+aggregate', 'i'));
        if (v) legacyGL.generalAggregate = v;
        v = tryExtract(new RegExp('products[\\s\\-]*completed\\s+op[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'products[\\s\\-]*completed\\s+op')
         || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,40}products[\\s\\-]*completed', 'i'));
        if (v) legacyGL.productsCompletedOps = v;
        v = tryExtract(new RegExp('personal\\s+(?:&|and)\\s+advert[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'personal\\s+(?:&|and)\\s+advert');
        if (v) legacyGL.personalAdvertising = v;
        v = tryExtract(new RegExp('(?:damage\\s+to\\s+(?:rented|premises)|fire\\s+damage\\s+legal)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:damage\\s+to\\s+(?:rented|premises)|fire\\s+damage\\s+legal)', 1000);
        if (v) legacyGL.premisesDamage = v;
        v = tryExtract(new RegExp('med(?:ical)?\\s+exp(?:ense)?[^$]{0,80}\\$\\s*(' + D + ')', 'i'), 'med(?:ical)?\\s+exp(?:ense)?', 1000);
        if (v) {
          const numeric = parseInt(v.replace(/[^0-9]/g, ''), 10);
          if (!(numeric >= 1900 && numeric <= 2050 && !/[,]/.test(v))) {
            legacyGL.medicalExpense = v;
          }
        }
        setLimits('General Liability', legacyGL, 1);
      } else if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 GL] Skipping legacy fallback — high-trust GL data already exists from a prior file');
      }
      // Note: we no longer write `glLimits` directly to `limits` since setLimit() handles it
    }
    
    // --- Umbrella / Excess declarations ---
    // v2.10: Added bare number fallback for PHLY umbrella where $ is on separate line
    if (txtLower.includes('commercial excess') || txtLower.includes('umbrella') || txtLower.includes('excess liability')) {
      const exLimits = {};

      /* An umbrella's declarations and its SCHEDULE OF UNDERLYING INSURANCE both
       * say "Each Occurrence Limit", and the underlying one is the primary GL's.
       * Aspen View Academies carries a $5,000,000 umbrella and the report showed
       * $1,000,000 / $3,000,000 — the underlying GL limits — then graded it
       * BELOW STANDARD against a $5M-$10M standard. The policy meets it.
       *
       * Two causes, both fixed here.
       *
       * Scope: the search now stops at the schedule of underlying insurance, so
       * a limit belonging to the primary cannot be read as the umbrella's.
       *
       * Window: the label and the figure sit in one padded table row, and the
       * gap on the real limit measured 103 characters against a 60-character
       * window, so the correct value was unreachable while a looser fallback
       * matched something else. The window is wider and now excludes newlines,
       * which is the stronger constraint: it can stretch across a padded column
       * but never jump to another row. */
      const _umbCut = txtLower.search(/schedule\s+of\s+underlying\s+insurance/i);
      const _umbScope = _umbCut > 0 ? allText.slice(0, _umbCut) : allText;

      const _umb = (re) => {
        const m = re.exec(_umbScope);
        return m ? '$' + m[1] : null;
      };

      /* A LONDON MARKET CONTRACT STATES ITS LAYER IN WORDS.
       *
       * Holloway Houston's excess is a Lloyd's Market Reform Contract:
       *   Limit:  USD 20,000,000 each "Occurrence" subject to USD 20,000,000
       *           aggregate limit …
       *   Excess: USD 9,000,000 each "Occurrence" …
       * The readers below look for "$" after "Each Occurrence Limit", find
       * neither, and a looser pass printed "Aggregate: $9,000,000" — the
       * attachment point read as the limit, on a $20M layer. Read the layer
       * as the contract states it, and say what it sits excess of. */
      const _mrcLimit = /\blimit(?:\(s\))?(?:\s+of\s+liability)?\s*\**\s*:\s*\**\s*(?:USD|US\$|\$)\s*([\d,]{5,})\s+(?:each|any\s+one)\b/i.exec(_umbScope);
      const _mrcExcess = /\bexcess\s*\**\s*:\s*\**\s*(?:USD|US\$|\$)\s*([\d,]{5,})\s+(?:each|any\s+one)\b/i.exec(_umbScope);
      if (_mrcLimit) {
        exLimits.eachOccurrence = '$' + _mrcLimit[1];
        // The aggregate is stated with the layer's own Limit line, which may
        // not be the one matched above: the broker's cover note states only
        // "$ 20,000,000 any one accident or occurrence".
        const agg = /\blimit\s*\**\s*:\s*\**\s*(?:USD|US\$|\$)\s*[\d,]{5,}\s+each\b[^\n]{0,120}?subject\s+to\s+(?:USD|US\$|\$)\s*([\d,]{5,})\s+aggregate/i.exec(_umbScope);
        if (agg) exLimits.aggregate = '$' + agg[1];
        if (_mrcExcess) exLimits.excessOf = '$' + _mrcExcess[1];
      }
      // A layer read with its attachment point is complete; the looser passes
      // below found the UNDERLYING layer's $9,000,000 aggregate and reported it
      // as this policy's.
      const _mrcLayer = !!(exLimits.eachOccurrence && exLimits.excessOf);

      let v = exLimits.eachOccurrence ? null : _umb(new RegExp('each\\s+occurrence\\s+limit[^$\\n]{0,200}\\$\\s*(' + D + ')', 'i'))
           || tryExtract(new RegExp('each\\s+occurrence\\s+limit[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'each\\s+occurrence\\s+limit');
      if (v) exLimits.eachOccurrence = v;

      v = (exLimits.aggregate || _mrcLayer) ? null : _umb(new RegExp('(?:general\\s+)?aggregate\\s+limit[^$\\n]{0,200}\\$\\s*(' + D + ')', 'i'))
       || tryExtract(new RegExp('(?:general\\s+)?aggregate\\s+limit[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:general\\s+)?aggregate\\s+limit');
      if (v) exLimits.aggregate = v;
      
      // v2.17: Retained limit — only extract from scoped umbrella declaration pages to avoid noise
      // Skip bare number fallback which picks up addresses/phone numbers
      if (f.pages && Array.isArray(f.pages)) {
        const umbrellaDecPages = f.pages.filter(p => {
          const pt = (p.text || '').toLowerCase();
          return (pt.includes('umbrella') || pt.includes('excess')) && 
                 (pt.includes('declarations') || pt.includes('limits of insurance')) &&
                 pt.includes('retained');
        });
        if (umbrellaDecPages.length > 0) {
          const umbDecText = umbrellaDecPages.map(p => p.text).join('\n');
          const retMatch = umbDecText.match(/retained\s+limit[^\$]{0,40}\$\s*([\d][\d,]*)/i)
            || umbDecText.match(/self[\s-]*insured\s+retention[^\$]{0,40}\$\s*([\d][\d,]*)/i);
          if (retMatch) {
            const retVal = parseInt(retMatch[1].replace(/,/g,''), 10);
            if (retVal >= 10000) exLimits.retainedLimit = '$' + retMatch[1];
          }
        }
      }
      
      if (Object.keys(exLimits).length > 0) {
        limits['Umbrella/Excess'] = exLimits;
      }
    }
    
    // --- Workers Compensation / Employers Liability ---
    if (txtLower.includes('workers compensation') || txtLower.includes("workers' compensation") || 
        txtLower.includes('employers liability') || txtLower.includes("employers' liability")) {
      const wcLimits = {};
      
      let v = tryExtract(new RegExp('(?:bodily\\s+injury\\s+by\\s+accident|each\\s+accident)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:bodily\\s+injury\\s+by\\s+accident|each\\s+accident)');
      if (v) wcLimits.eachAccident = v;
      
      v = tryExtract(new RegExp('(?:bodily\\s+injury\\s+by\\s+disease|disease[\\s\\-]+each\\s+employee)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:bodily\\s+injury\\s+by\\s+disease|disease[\\s\\-]+each\\s+employee)');
      if (v) wcLimits.diseaseEachEmployee = v;
      
      v = tryExtract(new RegExp('disease[\\s\\-]+policy\\s+limit[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'disease[\\s\\-]+policy\\s+limit');
      if (v) wcLimits.diseasePolicyLimit = v;
      
      if (Object.keys(wcLimits).length > 0) {
        limits['Workers Compensation'] = wcLimits;
      }
    }
    
    // --- Commercial Property ---
    if (txtLower.includes('commercial property') || txtLower.includes('building limit') || 
        txtLower.includes('business personal property') || txtLower.includes('blanket statement of values') ||
        txtLower.includes('ultimatecover property') || txtLower.includes('blanket limits') ||
        txtLower.includes('property coverage part declarations') || txtLower.includes('bus per prop')) {
      
      // Standard extraction (only if blanket didn't already find values)
      if (!limits['Commercial Property']) {
      const propLimits = {};
      
      // Where the paired-grid pass already read the declarations, it wins;
      // and any figure lifted from a supplemental schedule is refused.
      // Cut BOTH working texts at the supplemental schedule. tryExtract reads
      // decText first and allText second, so scoping only one of them let the
      // Outdoor Property sublimit through the other.
      const _cutAtSublimits = (t) => {
        const re = new RegExp(_SP_SUBLIMIT_SECTION.source, 'gi');
        const m = re.exec(String(t || ''));
        return m ? String(t).slice(0, m.index) : t;
      };
      const _decScope = _cutAtSublimits(decText);
      const _allScope = _cutAtSublimits(allText);
      const tryExtractProp = (rx, bare, minV) => {
        const sd = decText, sa = allText;
        try {
          // Only narrow when something remains to read; a policy whose whole
          // property section sits under an "extensions" heading must not be
          // reduced to nothing.
          if (_decScope && _decScope.length > 200) decText = _decScope;
          if (_allScope && _allScope.length > 200) allText = _allScope;
          return tryExtract(rx, bare, minV);
        } finally { decText = sd; allText = sa; }
      };
      let v = tryExtractProp(new RegExp('\\b1\\s+Building\\s+\\$\\s*(' + D + ')', 'i'), '1\\s+Building')
           || tryExtractProp(new RegExp('Building\\s+\\$\\s*(' + D + ')', 'i'), 'Building')
           || tryExtractProp(new RegExp('building[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'building')
           || tryExtractProp(new RegExp('building\\s+limit[^$]{0,40}\\$\\s*(' + D + ')', 'i'), 'building\\s+limit');
      if (v) propLimits.building = v;
      
      v = tryExtractProp(new RegExp('\\b2\\s+Bus\\s+Per\\s+Prop\\s+\\$\\s*(' + D + ')', 'i'), '2\\s+Bus\\s+Per\\s+Prop')
           || tryExtractProp(new RegExp('(?:Bus\\s+Per\\s+Prop|BPP)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:Bus\\s+Per\\s+Prop|BPP)')
           || tryExtractProp(new RegExp('(?:business\\s+personal\\s+property|contents)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:business\\s+personal\\s+property|contents)');
      if (v) propLimits.bpp = v;
      
      v = tryExtractProp(new RegExp('business\\s+income[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'business\\s+income')
           || tryExtractProp(new RegExp('(?:Bus?\\s*(?:us)?\\s+Income|BI|Business\\s+Income)[-\\s]*(?:Ext?|Extra|Expense)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:Bus\\s+Income|BI)[-\\s]*(?:Ext|Extra)')
           || tryExtractProp(new RegExp('\\b3\\s+B\\s*us\\s+Income[-\\s]*Ex\\s*t\\s+\\$\\s*(' + D + ')', 'i'), 'B\\s*us\\s+Income');
      if (v) propLimits.businessIncome = v;
      
      // v2.17: Detect blanket vs specific and coinsurance from declarations
      const blanketMatch = allText.match(/(?:blanket|specific)/i);
      if (blanketMatch) propLimits._coverageType = /blanket/i.test(blanketMatch[0]) ? 'Blanket' : 'Specific';
      const coinsMatch = allText.match(/Coinsurance[^\n]*?(?:X\s+)?(100%|90%|80%)/i)
        || allText.match(/(100%|90%|80%)\s*(?:Coinsurance|Co-\s*Insurance)/i);
      if (coinsMatch) propLimits._coinsurance = coinsMatch[1];
      // Detect deductible
      const propDedMatch = allText.match(/(?:Building|Property)\s*\$\s*([\d,]+)\s*(?:Business\s+Personal|BPP)/i)
        || allText.match(/Deductible[^\n]*?\$\s*([\d,]+)/i);
      // Also check for transit deductible
      const transitDedMatch = allText.match(/Transit\s*\$\s*([\d,]+)/i);
      if (transitDedMatch) propLimits._transitDeductible = '$' + transitDedMatch[1].replace(/,/g,'');
      
      if (Object.keys(propLimits).length > 0) {
        limits['Commercial Property'] = propLimits;
      }
      } // end standard extraction fallback (if !limits['Commercial Property'])
    }
    
    // --- Commercial Auto ---
    // v2.17: Skip if priority extraction already found limits OR if HNOA text detected anywhere
    // v2.20.13: Added scoped extraction for ISO Business Auto Schedule (CA DS 03 form).
    // v2.20.14: Removed guard — always run scoped Auto extraction, let trust-tier system
    // decide whether to overwrite an HNOA-only value with a real Auto policy's limits.
    // The HNOA priority extraction at line ~5065 sets a perOccurrence value with trust=0,
    // which the new scoped extraction (trust=2) can correctly upgrade.
    {
      const isFullAuto = txtLower.includes('commercial auto') || txtLower.includes('business auto') || 
          (txtLower.includes('automobile') && txtLower.includes('liability'));

      if (isFullAuto) {
        // v2.20.13: Find the Auto Schedule of Coverages page first
        let autoScopedText = null;
        const autoScopedDebug = [];
        if (f.pages && Array.isArray(f.pages)) {
          const candidates = [];
          f.pages.forEach(p => {
            if (!p.text) return;
            const pt = spScoreText(p);
            // Reject endorsement pages
            if (/this\s+endorsement\s+(?:changes|modifies)\s+the\s+policy/i.test(pt)) return;
            // Named only in an underlying schedule -> it belongs to the policy beneath.
            if (spCoverageOnlyInUnderlyingSchedule(pt, /(?:covered\s+autos?\s+liability|business\s+auto|automobile\s+liability)/i)) return;
            // Score the page
            let score = 0;
            if (/schedule\s+of\s+coverages\s+and\s+covered\s+autos/i.test(pt)) score += 10;
            if (/covered\s+autos\s+liability/i.test(pt)) score += 6;
            if (/business\s+auto\s+(?:coverage|policy|declarations)/i.test(pt)) score += 4;
            if (/uninsured\s+motorists/i.test(pt)) score += 2;
            if (/limit\s+or\s+deductible/i.test(pt)) score += 4;
            if (/\bca\s+ds\s+\d+/i.test(pt)) score += 5; // ISO form number
            if (score === 0) return;
            candidates.push({ page: p, score });
          });
          autoScopedDebug.push('Auto candidate pages: ' + candidates.length);
          if (candidates.length > 0) {
            candidates.sort((a, b) => b.score - a.score || a.page.text.length - b.page.text.length);
            const top = candidates[0];
            const picked = [top.page];
            // Take adjacent pages too (auto schedule can span 2-3 pages)
            for (let i = 1; i < candidates.length && picked.length < 3; i++) {
              if (candidates[i].score >= top.score - 6) picked.push(candidates[i].page);
              else break;
            }
            autoScopedText = picked.map(p => spScoreText(p)).join('\n');
            autoScopedDebug.push('picked: ' + picked.length + ' page(s), top score: ' + top.score);
          }
        }
        if (typeof console !== 'undefined' && console.log) {
          console.log('[v2.20 Auto scoping]', f.fileName || 'unknown', autoScopedDebug.join(' | '), 'scopedTextLen:', autoScopedText ? autoScopedText.length : 0);
        }

        const runAutoExtractions = (targetText) => {
          if (!targetText) return {};
          const out = {};
          // Strategy 1: Standard CSL pattern (some carriers use it)
          let v = extractBestDollar(targetText, new RegExp('(?:combined\\s+single\\s+limit|CSL)[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
          if (!v) {
            // Strategy 2: "Covered Autos Liability" label — ISO Business Auto form
            v = extractBestDollar(targetText, new RegExp('covered\\s+autos\\s+liability[^$\\n]{0,200}\\$\\s*(' + D + ')', 'i'), 50000);
          }
          if (!v) {
            // Strategy 3: Tier-based — ISO Auto pages have $1M+ limits and small premium values.
            // Filter all dollars > $50K and pick the largest distinct limit value.
            const dollarRe = /\$\s*([\d,]+)\b/g;
            const limits = [];
            let dm;
            while ((dm = dollarRe.exec(targetText)) !== null) {
              const num = parseInt(dm[1].replace(/[^0-9]/g, ''), 10);
              if (num >= 50000 && num <= 50000000) limits.push({ raw: dm[1], num });
            }
            // Most common large value = the auto liability limit
            if (limits.length > 0) {
              const counts = {};
              limits.forEach(l => { counts[l.num] = (counts[l.num] || 0) + 1; });
              // Sort by frequency desc, then by value desc
              const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1] || +b[0] - +a[0]);
              const top = ranked[0];
              v = limits.find(l => l.num === +top[0]).raw;
            }
          }
          if (v) out.combinedSingleLimit = v;

          // Uninsured/Underinsured Motorists — typically same as liability limit.
          // v2.20.15: Two strategies. (1) Same-line label→$ pattern (works if PDF preserves
          // table rows). (2) Tier-fallback — if the page has both UM and UIM labels AND
          // we found a CSL via the tier algorithm, the same most-common limit value applies
          // to UM/UIM (standard for ISO Business Auto where UM/UIM = Auto Liability limit).
          const umRe = new RegExp('(?:uninsured|underinsured)\\s+motorists?[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i');
          let umV = extractBestDollar(targetText, umRe, 50000);
          if (!umV) {
            // Tier fallback: if the page mentions UM/UIM AND we have a CSL value, assume
            // the same limit applies. This is the case for ISO Business Auto schedules
            // where Covered Autos Liability + UM + UIM all share the same headline limit.
            const hasUMLabel = /uninsured\s+motorists?/i.test(targetText);
            const hasUIMLabel = /underinsured\s+motorists?/i.test(targetText);
            if ((hasUMLabel || hasUIMLabel) && out.combinedSingleLimit) {
              umV = out.combinedSingleLimit;
            }
          }
          if (umV) out.umUim = umV;

          return out;
        };

        const scopedAuto = runAutoExtractions(autoScopedText);
        const scopedAutoHasCore = scopedAuto.combinedSingleLimit;
        if (typeof console !== 'undefined' && console.log) {
          console.log('[v2.20 Auto scoped result]', JSON.stringify(scopedAuto), 'accepted:', !!scopedAutoHasCore);
        }
        const hasHighTrustAuto = limitsTrust['Commercial Auto']
          && (limitsTrust['Commercial Auto'].combinedSingleLimit === 2);
        if (scopedAutoHasCore) {
          setLimits('Commercial Auto', scopedAuto, 2);
        } else if (!hasHighTrustAuto) {
          // Legacy fallback at trust = 1
          const legacyAuto = {};
          let v = tryExtract(new RegExp('(?:combined\\s+single\\s+limit|CSL)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:combined\\s+single\\s+limit|CSL)');
          if (v) legacyAuto.combinedSingleLimit = v;
          v = tryExtract(new RegExp('(?:uninsured|underinsured)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:uninsured|underinsured)');
          if (v) legacyAuto.umUim = v;
          if (Object.keys(legacyAuto).length > 0) setLimits('Commercial Auto', legacyAuto, 1);
        } else if (typeof console !== 'undefined' && console.log) {
          console.log('[v2.20 Auto] Skipping legacy fallback — high-trust Auto data already exists');
        }
      }
    }
    
    // --- D&O / Management Liability ---
    // Tightened trigger: require D&O-specific language, not bare 'directors'/'officers' which appears in all GL policies
    // v2.10: For Arizona Autism, D&O is embedded in ELL policy — also detect via ELL+D&O co-occurrence
    if (txtLower.includes('d&o') || txtLower.includes('directors and officers') || 
        txtLower.includes('directors & officers') || /management\s+liability\s+(?:declarations|coverage)/i.test(allText)) {
      const doLimits = {};
      const doScope = ['directors and officers', 'directors & officers', 'd&o', 'management liability'];
      
      let v = tryExtractScoped(new RegExp('(?:each\\s+(?:claim|wrongful\\s+act|loss))[^$]{0,60}\\$\\s*(' + D + ')', 'i'), doScope, '(?:each\\s+(?:claim|wrongful\\s+act|loss))');
      if (v) doLimits.eachClaim = v;
      
      v = tryExtractScoped(new RegExp('aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), doScope, 'aggregate');
      if (v) doLimits.aggregate = v;
      
      if (Object.keys(doLimits).length > 0) {
        limits['D&O'] = doLimits;
      }
    }
    
    // --- Educators Legal Liability ---
    // NOTE: Must use specific ELL phrases — never bare 'ell' which matches 'well', 'umbrella', 'cancellation', etc.
    // v2.10: Enhanced with bare number fallback + broader scope keywords for Indian Harbor-style ELL
    if (txtLower.includes('educators legal liability') || txtLower.includes('school board legal') || 
        txtLower.includes('educators wrongful act') || txtLower.includes('ell coverage') || txtLower.includes('ell policy')) {
      const ellLimits = {};
      const ellScope = ['educators legal liability', 'school board legal', 'educators wrongful act', 'insuring agreement a.1'];
      
      // A single-letter coverage designation must be a whole token.
      //
      // "coverage\s+b" matches the property declarations' own column layout —
      // "Coverage   Building   Blanket Group 001" — and "coverage\s+a" matches
      // "Coverage   All Personal Property". The bare-number fallback then takes
      // the largest number on that line, which is how Educators Legal
      // Liability came to report "Coverage B Each Action: $21,638". That
      // figure is Building 001's PREMIUM. A premium printed as a limit, cited
      // to the declarations, is the same defect as the $250 life benefit taken
      // off a dental table.
      //
      // With the boundary, neither pattern matches anything in this policy at
      // all, which is the correct answer: GuideOne designates these coverages
      // EL, DO and EP, and has no Coverage A or Coverage B.
      const COV_A = 'coverage\\s+a(?![a-z])';
      const COV_B = 'coverage\\s+b(?![a-z])';
      let v = tryExtractScoped(new RegExp('(?:' + COV_A + '|each\\s+(?:wrongful\\s+act|claim)|item\\s+\\d[^$]{0,30}(?:each|per)\\s+claim)[^$]{0,60}\\$\\s*(' + D + ')', 'i'), ellScope, '(?:' + COV_A + '|each\\s+(?:wrongful\\s+act|claim))');
      if (v) ellLimits.eachWrongfulAct = v;
      
      v = tryExtractScoped(new RegExp('(?:' + COV_A + '[^$\\n]{0,20})?aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), ellScope, 'aggregate');
      if (v) ellLimits.aggregate = v;
      
      v = tryExtractScoped(new RegExp(COV_B + '[^$]{0,60}\\$\\s*(' + D + ')', 'i'), ellScope, COV_B);
      if (v) ellLimits.coverageBEachAction = v;
      
      if (Object.keys(ellLimits).length > 0) {
        limits['Educators Legal Liability (ELL)'] = ellLimits;
      }
    }
    
    // --- EPLI (Employment Practices) ---
    // v2.20.9: Scoped page detection + trust-tier protection. Previous logic triggered on
    // any "employment practices" mention — which appears in EXCLUSION endorsements on policies
    // that DON'T have EPLI coverage. The scoped detector requires a true EPLI dec page
    // (with both an EPLI title and limit labels). If no scoped page found AND no high-trust
    // EPLI from a prior file, the legacy fallback is skipped to avoid phantom limits.
    if (txtLower.includes('employment practices') || txtLower.includes('epli')) {
      // Find EPLI dec pages by signature scoring
      let epliScopedText = null;
      const epliScopedDebug = [];
      if (f.pages && Array.isArray(f.pages)) {
        const candidates = [];
        f.pages.forEach(p => {
          if (!p.text) return;
          const pt = spScoreText(p);
          // Page must mention EPLI or Employment Practices
          if (!/employment\s+practices(?:\s+liability)?|epli\b/i.test(pt)) return;
          // Named only in an underlying schedule -> it belongs to the policy beneath.
          if (spCoverageOnlyInUnderlyingSchedule(pt, /employment\s+practices(?:\s+liability)?|epli\b/i)) return;
          // Reject endorsement EXCLUSION pages — they say "Employment Practices" but exclude it
          if (/exclusion\s+(?:[\u2014\-]|of)?\s*(?:employment\s+practices|epli)/i.test(pt)) return;
          if (/this\s+endorsement\s+(?:changes|modifies)\s+the\s+policy/i.test(pt)
              && /excludes?/i.test(pt)) return;
          // Score the page
          let score = 0;
          if (/employment\s+practices\s+liability\s+(?:coverage|declarations|insurance)/i.test(pt)) score += 8;
          if (/\bepli\b/i.test(pt)) score += 4;
          if (/limits?\s+of\s+(?:insurance|liability)/i.test(pt)) score += 3;
          if (/each\s+(?:claim|wrongful\s+act|occurrence)/i.test(pt)) score += 3;
          if (/aggregate/i.test(pt)) score += 1;
          if (/declarations/i.test(pt)) score += 2;
          /* A FIELD BEATS A CLAUSE, AND AN ENDORSEMENT IS NOT A DECLARATIONS PAGE.
           *
           * Matrix Human Services' EPL aggregate is $5,000,000, stated on page
           * 2: "(A) Aggregate Limit of Liability: $5,000,000". The report said
           * $100,000, taken from page 102 — an endorsement reading "the
           * Employment Practices Liability Coverage Part shall also not apply
           * to Defense Costs in an aggregate amount not to exceed $100,000".
           *
           * That endorsement scored 14 and the declarations scored 12, because
           * every signal here is a WORD COUNT and the endorsement happens to
           * use more of the words. Both pages name the coverage part; only one
           * of them states a limit in a labelled field, and only one of them
           * announces itself as an endorsement. Those are the two facts that
           * separate them, so they are worth more than the vocabulary. */
          if (/(?:aggregate|each\s+claim)[^\n$]{0,40}limit[^\n$]{0,20}:?\s*\$\s*[\d]/i.test(pt)
              || /limit\s+of\s+(?:liability|insurance)\s*:?\s*\$\s*[\d]/i.test(pt)) score += 8;
          if (/this\s+endorsement\s+(?:changes|modifies)\s+the\s+policy/i.test(pt)) score -= 6;
          if (score < 6) return; // High threshold to avoid exclusion endorsement noise
          candidates.push({ page: p, score });
        });
        epliScopedDebug.push('EPLI candidate pages: ' + candidates.length);
        if (candidates.length > 0) {
          candidates.sort((a, b) => b.score - a.score || a.page.text.length - b.page.text.length);
          const top = candidates[0];
          const picked = [top.page];
          for (let i = 1; i < candidates.length && picked.length < 2; i++) {
            if (candidates[i].score >= top.score - 2) picked.push(candidates[i].page);
            else break;
          }
          epliScopedText = picked.map(p => spScoreText(p)).join('\n');
          epliScopedDebug.push('picked: ' + picked.length + ' page(s), top score: ' + top.score);
        }
      }
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 EPLI scoping]', f.fileName || 'unknown', epliScopedDebug.join(' | '), 'scopedTextLen:', epliScopedText ? epliScopedText.length : 0);
      }

      const runEPLIExtractions = (targetText) => {
        if (!targetText) return {};
        const out = {};
        let v = extractBestDollar(targetText, new RegExp('(?:each\\s+(?:claim|wrongful\\s+act|occurrence|employment))[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
        if (!v) v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')\\s+each\\s+(?:claim|wrongful\\s+act|occurrence|employment)', 'i'), 50000);
        if (v) out.eachClaim = v;
        v = extractBestDollar(targetText, new RegExp('aggregate[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
        if (!v) v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,40}aggregate', 'i'), 50000);
        if (v) out.aggregate = v;
        return out;
      };

      // Narrowed to the EPL coverage part where the page heads several — see
      // spSectionForCoverage. Without it this reads the first "aggregate ... $"
      // on the page, whoever it belongs to.
      const scopedEPLI = runEPLIExtractions(
        spSectionForCoverage(epliScopedText, /employment[\s-]+practices|\bEPL\b/i));
      const scopedEPLIHasCore = scopedEPLI.eachClaim || scopedEPLI.aggregate;
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 EPLI scoped result]', JSON.stringify(scopedEPLI), 'accepted:', !!scopedEPLIHasCore);
      }
      const hasHighTrustEPLI = limitsTrust['EPLI']
        && (limitsTrust['EPLI'].eachClaim === 2 || limitsTrust['EPLI'].aggregate === 2);
      if (scopedEPLIHasCore) {
        setLimits('EPLI', scopedEPLI, 2);
      } else if (!hasHighTrustEPLI) {
        // v2.20.9: NO LEGACY FALLBACK. The previous fallback grabbed values from anywhere
        // "employment practices" appeared, including exclusion endorsements. If the scoped
        // detector finds no real EPLI dec page, EPLI simply isn't in this policy.
        if (typeof console !== 'undefined' && console.log) {
          console.log('[v2.20 EPLI] No EPLI dec page detected — not flagging EPLI as covered');
        }
      } else if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 EPLI] Skipping legacy fallback — high-trust EPLI data already exists');
      }
    }

    // --- Professional Liability (incl. Counseling, Texas Counseling Professional, Educator PL) ---
    // v2.20.9: New extraction block. Many education programs include Professional Liability
    // as an endorsement to GL (e.g. "Texas Counseling Professional Liability Coverage" PE GL
    // 625 TX) with separate Each Wrongful Act / Aggregate limits. The page typically has the
    // values rendered in heavily-spaced form fields ($ 1 , 0 0 0,000) so we use the same
    // tier-based domain-aware extraction strategy as GL.
    const hasProfLiabTrigger = txtLower.includes('counseling professional liability')
      || txtLower.includes('educator professional liability')
      || txtLower.includes('educators professional liability')
      || txtLower.includes('professional liability coverage')
      || (txtLower.includes('professional liability') && txtLower.includes('each wrongful act'));
    if (hasProfLiabTrigger) {
      // Find candidate Professional Liability dec/endorsement pages — scored similarly
      // to the cyber coverage schedule logic.
      let plScopedText = null;
      const plScopedDebug = [];
      if (f.pages && Array.isArray(f.pages)) {
        const candidates = [];
        f.pages.forEach(p => {
          if (!p.text) return;
          const pt = spScoreText(p);
          // Page must mention Professional Liability somewhere
          if (!/(?:counseling|educator|educators)?\s*professional\s+liability/i.test(pt)) return;
          // Named only in an underlying schedule -> it belongs to the policy beneath.
          if (spCoverageOnlyInUnderlyingSchedule(pt, /(?:counseling|educator|educators)?\s*professional\s+liability/i)) return;
          // Must have an Each-Wrongful-Act or Each-Claim label
          if (!/each\s+(?:wrongful\s+act|claim|incident|occurrence)/i.test(pt)) return;
          // Score the page
          let score = 0;
          if (/professional\s+liability\s+(?:coverage|endorsement|declarations)/i.test(pt)) score += 6;
          if (/counseling\s+professional\s+liability/i.test(pt)) score += 8;
          if (/educators?\s+professional\s+liability/i.test(pt)) score += 5;
          if (/limits?\s+of\s+insurance/i.test(pt)) score += 3;
          if (/each\s+wrongful\s+act/i.test(pt)) score += 4;
          if (/aggregate/i.test(pt)) score += 1;
          if (score === 0) return;
          candidates.push({ page: p, score });
        });
        plScopedDebug.push('PL candidate pages: ' + candidates.length);
        if (candidates.length > 0) {
          candidates.sort((a, b) => b.score - a.score || a.page.text.length - b.page.text.length);
          const top = candidates[0];
          const picked = [top.page];
          for (let i = 1; i < candidates.length && picked.length < 3; i++) {
            if (candidates[i].score >= top.score - 4) picked.push(candidates[i].page);
            else break;
          }
          plScopedText = picked.map(p => spScoreText(p)).join('\n');
          plScopedDebug.push('picked: ' + picked.length + ' page(s), top score: ' + top.score);
        }
      }
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 PL scoping]', f.fileName || 'unknown', plScopedDebug.join(' | '), 'scopedTextLen:', plScopedText ? plScopedText.length : 0);
      }

      const runPLExtractions = (targetText) => {
        if (!targetText) return {};
        const out = {};
        // Strategy 1: Standard label→$ pattern (when values are on same line as labels)
        let v = extractBestDollar(targetText, new RegExp('each\\s+wrongful\\s+act[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
        if (!v) v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,40}each\\s+wrongful\\s+act', 'i'), 50000);
        if (!v) v = extractBestDollar(targetText, new RegExp('each\\s+(?:claim|incident|occurrence)[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
        if (v) out.eachWrongfulAct = v;
        v = extractBestDollar(targetText, new RegExp('aggregate[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'), 50000);
        if (!v) v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,40}aggregate', 'i'), 50000);
        if (v) out.aggregate = v;
        // Strategy 2: Tier-based assignment when values are extracted as separate text runs
        // (PDF.js form-field rendering produces "$ Each Wrongful Act $ Aggregate ... 1,000,000 3,000,000")
        if (!out.eachWrongfulAct || !out.aggregate) {
          const dollarRe = /\b(\d{1,3}(?:,\d{3})+)\b/g;
          const dollars = [];
          let dm;
          while ((dm = dollarRe.exec(targetText)) !== null) {
            const num = parseInt(dm[1].replace(/[^0-9]/g, ''), 10);
            if (num >= 50000 && num <= 50000000) dollars.push({ raw: dm[1], num });
          }
          // Filter unique values, sorted ascending
          const uniq = [...new Map(dollars.map(d => [d.num, d])).values()].sort((a, b) => a.num - b.num);
          if (uniq.length >= 2) {
            // Smallest = Each Wrongful Act, Largest = Aggregate (typical 1M/3M structure)
            if (!out.eachWrongfulAct) out.eachWrongfulAct = uniq[0].raw;
            if (!out.aggregate) out.aggregate = uniq[uniq.length - 1].raw;
          } else if (uniq.length === 1) {
            // Only one value found — use as Each Wrongful Act
            if (!out.eachWrongfulAct) out.eachWrongfulAct = uniq[0].raw;
          }
        }
        return out;
      };

      const scopedPL = runPLExtractions(plScopedText);
      const scopedPLHasCore = scopedPL.eachWrongfulAct || scopedPL.aggregate;
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 PL scoped result]', JSON.stringify(scopedPL), 'accepted:', !!scopedPLHasCore);
      }
      const hasHighTrustPL = limitsTrust['Professional Liability']
        && (limitsTrust['Professional Liability'].eachWrongfulAct === 2
            || limitsTrust['Professional Liability'].aggregate === 2);
      if (scopedPLHasCore) {
        setLimits('Professional Liability', scopedPL, 2);
      } else if (!hasHighTrustPL) {
        // Legacy fallback at trust = 1 — simple keyword extraction
        const legacyPL = {};
        let v = tryExtract(new RegExp('each\\s+wrongful\\s+act[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'each\\s+wrongful\\s+act');
        if (v) legacyPL.eachWrongfulAct = v;
        v = tryExtract(new RegExp('professional\\s+liability[^$]{0,200}aggregate[^$]{0,40}\\$\\s*(' + D + ')', 'i'));
        if (v) legacyPL.aggregate = v;
        if (Object.keys(legacyPL).length > 0) setLimits('Professional Liability', legacyPL, 1);
      } else if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 PL] Skipping legacy fallback — high-trust PL data already exists');
      }
    }
    
    // --- Crime / Fidelity ---
    if (txtLower.includes('crime') || txtLower.includes('employee theft') || txtLower.includes('fidelity')) {
      const crimeLimits = {};
      
      let v = tryExtract(new RegExp('employee\\s+(?:theft|dishonesty)[^$]{0,60}\\$\\s*(' + D + ')', 'i'));
      if (v) crimeLimits.employeeTheft = v;
      
      v = tryExtract(new RegExp('forgery[^$]{0,60}\\$\\s*(' + D + ')', 'i'));
      if (v) crimeLimits.forgery = v;
      
      v = tryExtract(new RegExp('computer\\s+(?:fraud|crime)[^$]{0,60}\\$\\s*(' + D + ')', 'i'));
      if (v) crimeLimits.computerFraud = v;
      
      if (Object.keys(crimeLimits).length > 0) {
        limits['Crime'] = crimeLimits;
      }
    }
    
    // --- Sexual Abuse / Molestation (SAM) ---
    // v2.20: PRIORITY scoped extraction to a SAM endorsement page only.
    // The previous logic searched all-text/all-dec-pages and could pick up unrelated
    // "$5,000 Bail Bonds" from the GL Honor Roll Elite enhancement when looking for
    // "Supplementary Payment". We now find the actual Abuse/Molestation endorsement
    // schedule page first and scope all SAM limit extraction to that page. The
    // pre-v2.20 logic remains as a fallback for non-standard layouts.
    if (txtLower.includes('abuse or molestation') || txtLower.includes('sexual abuse') || 
        txtLower.includes('molestation liability') || txtLower.includes('pe gl ok5') ||
        txtLower.includes('abusive conduct')) {
      const samLimits = {};

      // v2.20: Find the canonical SAM endorsement page(s).
      // We require an Abuse/Molestation endorsement title AND a SCHEDULE / limits
      // structure with at least one "each act" / "aggregate" label.
      let samScopedText = null;
      if (f.pages && Array.isArray(f.pages)) {
        const samPages = f.pages.filter(p => {
          if (!p.text) return false;
          const pt = spScoreText(p);
          const hasSAMTitle =
            /abuse\s+or\s+molestation\s+(?:liability\s+)?coverage/i.test(pt)
            || /sexual\s+abuse[\s\S]{0,40}(?:liability|coverage|endorsement)/i.test(pt)
            || /abusive\s+conduct[\s\S]{0,40}(?:liability|coverage|endorsement)/i.test(pt);
          const hasScheduleAndLimits =
            /\bschedule\b/i.test(pt)
            && /(?:each\s+act|aggregate|each\s+abusive\s+conduct)/i.test(pt);
          return hasSAMTitle && hasScheduleAndLimits;
        });
        if (samPages.length > 0) {
          samScopedText = samPages.map(p => p.text).join('\n');
        }
      }

      // Helper: run all SAM extractions against target text.
      const runSAMExtractions = (targetText) => {
        if (!targetText) return {};
        const out = {};
        const findBest = (re1, re2, minVal) => {
          const r1 = extractBestDollar(targetText, re1, minVal || 5000);
          if (r1) return r1;
          if (re2) return extractBestDollar(targetText, re2, minVal || 5000);
          return null;
        };
        // Each Act
        let v = findBest(
          new RegExp('(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)\\s+(?:liability\\s+)?(?:[^$\\n]{0,60})?each\\s+act[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}each\\s+act[^$\\n]{0,60}(?:abuse|molestation|abusive)', 'i')
        );
        if (!v) {
          // Same-line: "$1,000,000 Each Act Limit" right after "Abuse Or Molestation Liability"
          v = extractBestDollar(targetText, new RegExp('abuse[^$\\n]{0,60}\\$\\s*(' + D + ')\\s*each\\s+act', 'i'), 5000);
        }
        if (!v) {
          // Pure same-line pattern: "$1,000,000 Each Act"
          v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')\\s+each\\s+act\\s+limit', 'i'), 5000);
        }
        if (v) out.eachAct = v;

        // Aggregate
        v = findBest(
          new RegExp('(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)[^$\\n]{0,80}aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'),
          new RegExp('\\$\\s*(' + D + ')\\s+aggregate\\s+limit', 'i')
        );
        if (v) out.aggregate = v;

        // Alleged Participant Each Act
        v = findBest(
          new RegExp('alleged\\s+participant[^$\\n]{0,60}\\$\\s*(' + D + ')\\s*each\\s+act', 'i'),
          new RegExp('alleged\\s+participant[^$\\n]{0,60}each\\s+act[^$]{0,60}\\$\\s*(' + D + ')', 'i')
        );
        if (v) out.allegedParticipantEachAct = v;

        // Alleged Participant Aggregate
        v = findBest(
          new RegExp('alleged\\s+participant[^$\\n]{0,200}\\$\\s*(' + D + ')\\s*aggregate', 'i'),
          new RegExp('alleged\\s+participant[^$\\n]{0,200}aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i')
        );
        if (v) out.allegedParticipantAggregate = v;

        // Special Supplementary Payment Limit
        v = extractBestDollar(targetText, new RegExp('(?:special\\s+)?supplementary\\s+payment\\s+limit[^$]{0,40}\\$\\s*(' + D + ')', 'i'), 1000);
        if (v) out.supplementaryPayment = v;

        return out;
      };

      // v2.20: Try scoped SAM endorsement pages first. Accept the scoped result if we
      // got at least one of the primary limits (Each Act or Aggregate).
      let scopedSAM = runSAMExtractions(samScopedText);
      const scopedSAMHasCore = scopedSAM.eachAct || scopedSAM.aggregate;
      if (scopedSAMHasCore) {
        Object.assign(samLimits, scopedSAM);
      } else {
        // Legacy fallback: original logic
        let v = tryExtract(new RegExp('(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)\\s+(?:liability\\s+)?each\\s+act[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)[^\\n]{0,40}each\\s+act')
             || tryExtract(new RegExp('each\\s+act[^$\\n]{0,30}\\$\\s*(' + D + ')[^\\n]{0,60}(?:abuse|molestation)', 'i'))
             || tryExtract(new RegExp('each\\s+abusive\\s+conduct[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'each\\s+abusive\\s+conduct')
             || tryExtract(new RegExp('each\\s+abusive\\s+conduct\\s+limit[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'each\\s+abusive\\s+conduct\\s+limit');
        if (v) samLimits.eachAct = v;

        v = tryExtract(new RegExp('(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)\\s+(?:liability\\s+)?aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)[^\\n]{0,40}aggregate')
         || tryExtract(new RegExp('(?:abuse|molestation)[^\\n]{0,40}aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'));
        if (v) samLimits.aggregate = v;

        v = tryExtract(new RegExp('alleged\\s+participant[^$\\n]{0,40}each\\s+act[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'alleged\\s+participant[^\\n]{0,40}each\\s+act');
        if (v) samLimits.allegedParticipantEachAct = v;

        v = tryExtract(new RegExp('alleged\\s+participant[^$\\n]{0,40}aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'alleged\\s+participant[^\\n]{0,40}aggregate');
        if (v) samLimits.allegedParticipantAggregate = v;

        // Tightened: require the literal phrase "Supplementary Payment Limit" to avoid
        // matching unrelated "Supplementary Payments" sections in GL endorsements.
        v = tryExtract(new RegExp('(?:special\\s+)?supplementary\\s+payment\\s+limit[^$]{0,40}\\$\\s*(' + D + ')', 'i'), '(?:special\\s+)?supplementary\\s+payment\\s+limit');
        if (v) samLimits.supplementaryPayment = v;

        if (Object.keys(samLimits).length === 0) {
          v = tryExtract(new RegExp('(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)[^$]{0,120}\\$\\s*(' + D + ')', 'i'), '(?:abuse\\s+(?:or|&)\\s+molestation|abusive\\s+conduct)');
          if (v) samLimits.eachAct = v;
        }
      }

      if (Object.keys(samLimits).length > 0) {
        limits['Sexual Abuse/Molestation'] = samLimits;
      }
    }
    
    // --- Cyber Liability ---
    // v2.20.7: Scoped extraction with trust tier + retention column awareness.
    // Beazley/Coalition/AmTrust style policies use a "Coverage Schedule" with a Limit
    // and a Retention column. Earlier logic grabbed values from the wrong column or
    // from unrelated endorsement language ($100K terrorism threshold, $100K endorsement
    // sublimits, etc.). This version scopes to the actual cyber dec/coverage-schedule page.
    if (txtLower.includes('cyber') || txtLower.includes('data breach') || txtLower.includes('network security')) {
      const cyberLimits = {};

      // v2.20.7: Find candidate cyber dec pages — score by signature strength.
      // The headline cyber dec page has "Coverage Schedule" OR "Policy Aggregate Limit
      // of Liability". We pick the page with the strongest signature, not just shortest.
      let cyberScopedText = null;
      const cyberScopedDebug = [];
      if (f.pages && Array.isArray(f.pages)) {
        const candidates = [];
        f.pages.forEach(p => {
          if (!p.text) return;
          const pt = spScoreText(p);
          // Must mention cyber/breach/network/privacy somewhere on the page
          if (!/cyber|breach\s+response|network\s+security|privacy\s+liability|data\s+breach/i.test(pt)) return;
          // Named only in an underlying schedule -> it belongs to the policy beneath.
          if (spCoverageOnlyInUnderlyingSchedule(pt, /cyber|breach\s+response|network\s+security|privacy\s+liability|data\s+breach/i)) return;
          // Reject endorsement pages of any flavor
          if (/this\s+endorsement\s+(?:changes|modifies)\s+the\s+policy/i.test(pt)) return;
          if (/this\s+endorsement\s+is\s+attached\s+to/i.test(pt)) return;
          if (/effective\s+date\s+of\s+this\s+endorsement/i.test(pt)) return;
          if (/certified\s+acts?\s+of\s+terrorism/i.test(pt)) return;
          // Score the page by how strongly it looks like the cyber coverage schedule
          let score = 0;
          if (/policy\s+aggregate\s+limit\s+of\s+liability\s*:?\s*\$/i.test(pt)) score += 10;
          if (/coverage\s+schedule/i.test(pt)) score += 6;
          if (/aggregate\s+limit\s+of\s+liability/i.test(pt)) score += 4;
          if (/limits?\s+of\s+insurance/i.test(pt)) score += 3;
          if (/each\s+(?:claim|incident)/i.test(pt)) score += 2;
          if (/\bdeclarations\b/i.test(pt)) score += 1;
          if (score === 0) return;
          candidates.push({ page: p, score });
        });
        cyberScopedDebug.push('Cyber candidate pages: ' + candidates.length);
        if (candidates.length > 0) {
          // Sort by score DESC, then by length ASC (prefer shorter when tied)
          candidates.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.page.text.length - b.page.text.length;
          });
          // Pick the top-scored page; if multiple tied with score >= 6, take all of them
          const top = candidates[0];
          const picked = [top.page];
          for (let i = 1; i < candidates.length; i++) {
            if (candidates[i].score >= 6 && picked.length < 3) {
              picked.push(candidates[i].page);
            } else break;
          }
          cyberScopedText = picked.map(p => spScoreText(p)).join('\n');
          cyberScopedDebug.push('picked: ' + picked.length + ' page(s), top score: ' + top.score);
        }
      }
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 Cyber scoping]', f.fileName || 'unknown', cyberScopedDebug.join(' | '), 'scopedTextLen:', cyberScopedText ? cyberScopedText.length : 0);
      }

      // Extract from scoped text using domain-aware logic.
      // v2.22 (broker rule): Cyber main limits must clear realistic floors.
      //   - Aggregate floor: $500,000 (rejects $100K/$300K sublimits like notification or crisis-management caps)
      //   - Each-claim floor: $100,000 (rejects $10K/$25K retention or sublimit values)
      // Wright Specialty / Munich Re cyber programs commonly include sublimit tables
      // (notification $10K, crisis $300K) that earlier regexes were misreading as the
      // policy aggregate. The floor change rejects those sublimits and forces a NOT FOUND
      // → VERIFY flag instead of displaying a misleading low number to the broker.
      // v2.22 floors raised after Wright Specialty test: a $250K each-claim sublimit was
      // slipping through at $100K floor. Real commercial cyber each-claim is $500K+.
      // If the dec page can't be confidently parsed, we want VERIFY — not a misleading number.
      const CYBER_AGG_MIN = 500000;
      const CYBER_EACH_MIN = 500000;
      const runCyberExtractions = (targetText) => {
        if (!targetText) return {};
        const out = {};
        // Policy Aggregate Limit of Liability (Beazley style — the headline limit)
        let v = extractBestDollar(targetText, new RegExp('policy\\s+aggregate\\s+limit\\s+of\\s+liability[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'), CYBER_AGG_MIN);
        if (!v) {
          v = extractBestDollar(targetText, new RegExp('aggregate\\s+limit\\s+of\\s+liability[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'), CYBER_AGG_MIN);
        }
        if (!v) {
          v = extractBestDollar(targetText, new RegExp('aggregate\\s+limit[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'), CYBER_AGG_MIN);
        }
        if (v) out.aggregate = v;
        // Each Claim limit — in cyber Coverage Schedules, the LIMIT typically comes
        // BEFORE the words "each Claim" and the RETENTION comes AFTER. So prioritize
        // the "$X each claim" pattern (limit-first), then fall back to "each claim $X".
        // Use extractBestDollar to get the LARGEST match — retention is always smaller
        // than the limit it applies to.
        v = extractBestDollar(targetText, new RegExp('\\$\\s*(' + D + ')\\s+each\\s+(?:claim|incident|loss|occurrence)', 'i'), CYBER_EACH_MIN);
        if (!v) {
          v = extractBestDollar(targetText, new RegExp('(?:each\\s+(?:claim|incident|occurrence|loss)|per\\s+(?:claim|occurrence))[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'), CYBER_EACH_MIN);
        }
        if (v) out.eachClaim = v;
        return out;
      };

      const scopedCyber = runCyberExtractions(cyberScopedText);
      const scopedHasCore = scopedCyber.aggregate || scopedCyber.eachClaim;
      if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 Cyber scoped result]', JSON.stringify(scopedCyber), 'accepted:', !!scopedHasCore);
      }
      const hasHighTrustCyber = limitsTrust['Cyber Liability']
        && (limitsTrust['Cyber Liability'].aggregate === 2 || limitsTrust['Cyber Liability'].eachClaim === 2);
      if (scopedHasCore) {
        setLimits('Cyber Liability', scopedCyber, 2);
      } else if (!hasHighTrustCyber) {
        // Legacy fallback at trust = 1 — must enforce the same cyber floors as the scoped path.
        // v2.22 (broker rule): without an explicit minVal the legacy path was happily accepting
        // $10K each-claim and $300K aggregate values from sublimit tables. Floors below.
        const legacyCyber = {};
        const cyberScope = ['cyber', 'data breach', 'network security', 'amtrustcyber'];
        let v = tryExtractScoped(new RegExp('(?:each\\s+(?:claim|occurrence)|per\\s+(?:claim|occurrence))[^$]{0,60}\\$\\s*(' + D + ')', 'i'), cyberScope, '(?:each\\s+(?:claim|occurrence)|per\\s+(?:claim|occurrence))', CYBER_EACH_MIN)
             || tryExtract(new RegExp('(?:each\\s+(?:claim|occurrence)|per\\s+(?:claim|occurrence))[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:each\\s+(?:claim|occurrence)|per\\s+(?:claim|occurrence))', CYBER_EACH_MIN);
        if (v) legacyCyber.eachClaim = v;
        v = tryExtractScoped(new RegExp('aggregate\\s+limit[^$]{0,80}\\$\\s*(' + D + ')', 'i'), cyberScope, 'aggregate\\s+limit', CYBER_AGG_MIN)
         || tryExtract(new RegExp('aggregate\\s+limit[^$]{0,80}\\$\\s*(' + D + ')', 'i'), 'aggregate\\s+limit', CYBER_AGG_MIN);
        if (v) legacyCyber.aggregate = v;
        if (Object.keys(legacyCyber).length > 0) setLimits('Cyber Liability', legacyCyber, 1);
      } else if (typeof console !== 'undefined' && console.log) {
        console.log('[v2.20 Cyber] Skipping legacy fallback — high-trust Cyber data already exists');
      }
    }
    
    // --- E&O / Professional Liability ---
    if (txtLower.includes('errors & omissions') || txtLower.includes('errors and omissions') || 
        txtLower.includes('professional liability') || txtLower.includes('e&o')) {
      const eoLimits = {};
      
      let v = tryExtract(new RegExp('(?:each\\s+(?:claim|occurrence|wrongful\\s+act))[^$]{0,60}\\$\\s*(' + D + ')', 'i'), '(?:each\\s+(?:claim|occurrence|wrongful\\s+act))');
      if (v) eoLimits.eachClaim = v;
      
      v = tryExtract(new RegExp('aggregate[^$]{0,60}\\$\\s*(' + D + ')', 'i'), 'aggregate');
      if (v) eoLimits.aggregate = v;
      
      if (Object.keys(eoLimits).length > 0) {
        limits['E&O'] = eoLimits;
      }
    }
    
    // --- Sexual Abuse / Molestation (generic fallback) ---
    if (!limits['Sexual Abuse/Molestation'] && (txtLower.includes('abuse') || txtLower.includes('molestation'))) {
      const samLimits = {};
      
      let v = tryExtract(new RegExp('(?:abuse|molestation)[^$]{0,80}(?:each\\s+(?:occurrence|claim|incident))[^$]{0,40}\\$\\s*(' + D + ')', 'i'))
           || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}(?:each\\s+(?:occurrence|claim))[^$\\n]{0,40}(?:abuse|molestation)', 'i'));
      if (v) samLimits.eachOccurrence = v;
      
      v = tryExtract(new RegExp('(?:abuse|molestation)[^$]{0,80}aggregate[^$]{0,40}\\$\\s*(' + D + ')', 'i'))
       || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate[^$\\n]{0,40}(?:abuse|molestation)', 'i'));
      if (v) samLimits.aggregate = v;
      
      if (Object.keys(samLimits).length > 0) {
        limits['Sexual Abuse/Molestation'] = samLimits;
      }
    }
    
    // --- Flood Insurance ---
    if (txtLower.includes('flood') && (txtLower.includes('coverage') || txtLower.includes('limit') || txtLower.includes('premium'))) {
      const floodLimits = {};
      
      let v = tryExtract(new RegExp('(?:flood)[^$\\n]{0,80}(?:building|structure)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
      if (v) floodLimits.building = v;
      
      v = tryExtract(new RegExp('(?:flood)[^$\\n]{0,80}(?:contents|personal\\s+property)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
      if (v) floodLimits.contents = v;
      
      if (Object.keys(floodLimits).length > 0) {
        limits['Flood Insurance'] = floodLimits;
      }
    }
    
    // --- Equipment Breakdown ---
    if (txtLower.includes('equipment breakdown') || txtLower.includes('boiler') || txtLower.includes('machinery')) {
      const ebLimits = {};
      
      let v = tryExtract(new RegExp('(?:equipment\\s+breakdown|boiler)[^$\\n]{0,80}\\$\\s*(' + D + ')', 'i'));
      if (v) ebLimits.limit = v;
      
      if (Object.keys(ebLimits).length > 0) {
        limits['Equipment Breakdown'] = ebLimits;
      }
    }
    
    // --- Inland Marine ---
    if (txtLower.includes('inland marine') || txtLower.includes('scheduled equipment')) {
      const imLimits = {};
      
      let v = tryExtract(new RegExp('(?:inland\\s+marine|scheduled)[^$\\n]{0,80}\\$\\s*(' + D + ')', 'i'));
      if (v) imLimits.limit = v;
      
      if (Object.keys(imLimits).length > 0) {
        limits['Inland Marine'] = imLimits;
      }
    }
    
    // --- Counseling Professional Liability ---
    if (txtLower.includes('counseling professional') || txtLower.includes('counseling liability')) {
      const cpLimits = {};
      let v = tryExtract(new RegExp('counseling\\s+professional[^$\\n]{0,80}\\$\\s*(' + D + ')[^$\\n]{0,30}(?:each|occurrence)', 'i'))
           || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}each\\s+occurrence[^$\\n]{0,80}counseling', 'i'));
      if (v) cpLimits.eachOccurrence = v;
      
      v = tryExtract(new RegExp('counseling\\s+professional[^$\\n]{0,80}\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate', 'i'))
       || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate[^$\\n]{0,80}counseling', 'i'));
      if (v) cpLimits.aggregate = v;
      
      if (Object.keys(cpLimits).length > 0) {
        limits['Counseling Professional Liability'] = cpLimits;
      }
    }

    // --- Employee Benefits Liability ---
    // NOTE: Must use specific phrase — bare 'employee benefit' matches WC audit provisions ("employee benefit plans")
    if (txtLower.includes('employee benefits liability') || /employee\s+benefits?\s+liability\s+coverage/i.test(allText)) {
      const eblLimits = {};
      const eblScope = ['employee benefits liability'];
      let v = tryExtractScoped(new RegExp('employee\\s+benefits[^$\\n]{0,60}\\$\\s*(' + D + ')[^$\\n]{0,30}each\\s+employee', 'i'), eblScope)
           || tryExtractScoped(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}each\\s+employee', 'i'), eblScope);
      if (v) eblLimits.eachEmployee = v;
      
      v = tryExtractScoped(new RegExp('employee\\s+benefits[^$\\n]{0,60}aggregate[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'), eblScope)
       || tryExtractScoped(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate', 'i'), eblScope);
      if (v) eblLimits.aggregate = v;
      
      if (Object.keys(eblLimits).length > 0) {
        limits['Employee Benefits Liability'] = eblLimits;
      }
    }
    
    // --- Police Professional Liability ---
    if (txtLower.includes('police professional')) {
      const ppLimits = {};
      let v = tryExtract(new RegExp('police\\s+professional[^$\\n]{0,80}\\$\\s*(' + D + ')[^$\\n]{0,30}(?:each|incident)', 'i'))
           || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}each\\s+incident', 'i'));
      if (v) ppLimits.eachIncident = v;
      
      v = tryExtract(new RegExp('police\\s+professional[^$\\n]{0,80}\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate', 'i'))
       || tryExtract(new RegExp('\\$\\s*(' + D + ')[^$\\n]{0,30}aggregate', 'i'));
      if (v) ppLimits.aggregate = v;
      
      if (Object.keys(ppLimits).length > 0) {
        limits['Police Professional Liability'] = ppLimits;
      }
    }
    
    // --- Active Shooter ---
    if (txtLower.includes('active shooter') || txtLower.includes('active assailant') || txtLower.includes('violent event')) {
      const asLimits = {};
      let v = tryExtract(new RegExp('(?:active\\s+(?:shooter|assailant)|violent\\s+event)[^$\\n]{0,80}\\$\\s*(' + D + ')', 'i'));
      if (v) asLimits.limit = v;
      if (Object.keys(asLimits).length > 0) {
        limits['Active Shooter'] = asLimits;
      }
    }
    
    // --- Student Accident ---
    if (txtLower.includes('student accident') || txtLower.includes('student medical')) {
      const saLimits = {};
      let v = tryExtract(new RegExp('(?:student\\s+accident|student\\s+medical)[^$\\n]{0,80}\\$\\s*(' + D + ')', 'i'));
      if (v) saLimits.limit = v;
      if (Object.keys(saLimits).length > 0) {
        limits['Student Accident Insurance'] = saLimits;
      }
    }
  });
  


  // Coverages whose declarations line names them.
  //
  // This WINS over the scoped extractors. A line that reads "Educators
  // Management Liability Each Claim Limit  $1,000,000" states the coverage,
  // the label and the amount together, so there is nothing to infer. A scoped
  // regex is inferring from what happens to sit nearby, and on this policy it
  // inferred $3,000,000 for a coverage the declarations put at $1,000,000 —
  // the Sexual Misconduct aggregate, found by a whole-document fallback after
  // the scoped search came back empty.
  //
  // It also writes to whatever key the rest of the engine already uses for
  // that coverage, so a correct value replaces an incorrect one rather than
  // sitting beside it under a second name.
  const _CTl = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  // Labels that name the same limit. Declarations use whichever the carrier
  // prefers, and two of them holding different numbers is a contradiction
  // rather than two findings.
  const LABEL_EQUIV = {
    eachClaim:     ['eachClaim', 'eachWrongfulAct', 'eachAct', 'perClaim', 'eachIncident'],
    perOccurrence: ['perOccurrence', 'eachOccurrence'],
    aggregate:     ['aggregate', 'generalAggregate'],
    eachEmployee:  ['eachEmployee', 'eachPerson']
  };
  Object.keys(LABEL_EQUIV).forEach(k => LABEL_EQUIV[k].forEach(a => { if (!LABEL_EQUIV[a]) LABEL_EQUIV[a] = LABEL_EQUIV[k]; }));
  pdfTexts.forEach(doc => {
    if (!doc || !doc.pages) return;
    const named = spReadNamedCoverageLimits(doc.pages.map(p => p.text || '').join('\n'));
    Object.keys(named).forEach(cov => {
      const existingKey = _CTl
        ? (Object.keys(limits).find(k => _CTl.sameTerm(k, cov)) || cov)
        : cov;
      if (!limits[existingKey]) limits[existingKey] = {};
      Object.keys(named[cov]).forEach(k => { limits[existingKey][k] = named[cov][k]; });
      // A stale value under a DIFFERENT LABEL FOR THE SAME CONCEPT is not
      // corrected by this line, and left alone it contradicts the value that
      // was. "Each Wrongful Act" and "Each Claim" are one limit on a
      // management liability coverage, so the object was about to report
      // $3,000,000 each wrongful act beside $1,000,000 each claim, both cited
      // to the same declarations. The stated label wins and its synonyms go.
      Object.keys(named[cov]).forEach(k => {
        (LABEL_EQUIV[k] || []).forEach(alias => {
          if (alias !== k && limits[existingKey][alias] !== undefined && named[cov][alias] === undefined) {
            delete limits[existingKey][alias];
          }
        });
      });
      // And a label the policy does not use at all. GuideOne designates these
      // coverages EL, DO and EP; it has no Coverage A or Coverage B, so a
      // value under one came from a pattern matching something else.
      if (/^Educators Legal Liability/i.test(existingKey)) delete limits[existingKey].coverageBEachAction;
    });
  });

  /* RUNS LAST, DELIBERATELY. spReadNamedCoverageLimits and the other
   * consumers below write into the same slots, so a migration placed beside
   * the property one was read correctly and then overwritten — the D&O
   * aggregate came out right and the neighbour's each-claim came back. */
  /* LIMITS READ FROM THE COVERAGE PART THAT STATES THEM.
   *
   * Matrix Head Start's D&O row reported "Each Claim: $1,000,000". The
   * ForeFront Portfolio states one D&O limit — an aggregate of $5,000,000 —
   * and the only "Each Claim" anywhere on the policy is a $0 retention. The
   * $1,000,000 is the FIDUCIARY LIABILITY COVERAGE PART aggregate, two
   * headings down the same page:
   *
   *     DIRECTORS & OFFICERS AND ENTITY LIABILITY COVERAGE PART
   *     (A) Aggregate Limit of Liability: $5,000,000
   *     ...
   *     FIDUCIARY LIABILITY COVERAGE PART
   *     (A) Aggregate Limit of Liability: $1,000,000
   *
   * A management liability policy stacks four or five coverage parts on one
   * page with identical field labels under each. Proximity cannot separate
   * them, so a window-based reader takes its neighbour's figure and presents
   * it with a citation. The heading is the only thing that distinguishes them,
   * and the field reader already records it as the field's scope.
   *
   * Crime sits on that same page and has the same problem; Cyber has the
   * mirror of it, a page of insuring agreements that each state a limit. One
   * reader, one spec per coverage — the differences between these coverages
   * are their field labels, and nothing else.
   *
   * Rules, each of which cost a defect to learn:
   *
   *   The reader reads fields; it does not aggregate. Two different statements
   *   of one slot mean this page is not the one to answer from.
   *
   *   Scope narrows, it does not exclude. A monoline policy has one scope and
   *   it is the whole document.
   *
   *   But a page that names coverage parts and does not name THIS one is not
   *   silent about it — it is saying these figures belong to something else,
   *   and falling back to the whole page there is the original bug.
   *
   *   A limit is an amount, not a line that happens to contain one.
   *
   *   The reader owns its slots outright. Merging keeps the figure it exists
   *   to remove.
   */
  const SP_SCOPED_LIMIT_SPECS = [
    { key: 'D&O', ownSlots: true, slots: [
      ['aggregate', /^(?:aggregate(?:\s+limit\s+of\s+liability)?|limit\s+of\s+liability)$/i],
      ['eachClaim', /^each\s+(?:claim|wrongful\s+act|loss)\b/i]
    ] },
    { key: 'Crime', ownSlots: true, slots: [
      ['employeeTheft', /^employee\s+theft\b/i],
      ['forgery',       /^forgery\b/i],
      ['computerFraud', /^computer\s+(?:system\s+)?fraud\b/i]
    ] },
    /* GL states six limits and prints most of them again under every
     * additional coverage on the page. Matrix Head Start's Each Occurrence
     * limit was reported as $3,000,000 — the general aggregate, one line
     * above it — because a window around "each occurrence" reached the wrong
     * row. Each label here names exactly one of the six.
     *
     * medicalExpense is deliberately tight: the same page carries "Medical
     * Expenses Each Person Limit $50,000" under Crisis Management, and the two
     * together would trip the uniqueness rule and silence the slot entirely.
     */
    { key: 'General Liability', slots: [
      [['eachOccurrence', 'perOccurrence'], /^each\s+occurrence(?:\s+limit)?$/i],
      ['generalAggregate',    /^general\s+aggregate(?:\s+limit)?\b/i],
      ['productsCompletedOps', /^products\s*[-\u2013]?\s*completed\s+operations(?:\s+aggregate)?(?:\s+limit)?\b/i],
      ['personalAdvertising', /^personal\s+(?:and|&)\s+advertising\s+injury/i],
      ['damageToPremises',    /^damage\s+to\s+premises\s+rented/i],
      ['medicalExpense',      /^medical\s+expense\s+limit\b/i]
    ] },
    /* Business auto declarations state the limit in a row, beside the covered
     * auto symbols and the premium:
     *
     *     Covered Autos Liability      8,9      $1,000,000      $1,874
     *
     * The limit column comes first, which is what the row reader takes.
     */
    { key: 'Commercial Auto', slots: [
      ['combinedSingleLimit', /^(?:covered\s+autos\s+liability|combined\s+single\s+limit|liability\s+combined\s+single\s+limit)$/i],
      ['umUim',               /^(?:uninsured|underinsured)\s+motorists?\b/i]
    ] },
    // EPLI sits on the same stacked page as D&O and Crime, under its own
    // heading, with the same field labels beneath it.
    { key: 'EPLI', ownSlots: true, slots: [
      ['aggregate', /^(?:aggregate(?:\s+limit\s+of\s+liability)?|limit\s+of\s+liability)$/i],
      ['eachClaim', /^each\s+(?:employment\s+)?claim\b/i]
    ] },
    /* SAM states its limits in a schedule whose rows name the limit AFTER the
     * amount, and whose second row omits the label:
     *
     *     Abuse Or Molestation Liability    $1,000,000    Each Act Limit
     *                                       $2,000,000    Aggregate Limit
     *
     * Tulsa Honor Academy's each-act limit was reported as $2,000,000 — the
     * aggregate, from the row nothing read. The reader now qualifies a label
     * with the trailing descriptor and carries the label down, so these bind
     * to the right slots. Alleged Participant is a separate, smaller coverage
     * on the same schedule, and the labels below keep it out.
     */
    { key: 'Sexual Abuse/Molestation', slots: [
      ['eachAct',        /^(?!.*alleged\s+participant).*\b(?:abuse|molestation|misconduct)\b.*\beach\s+act\b/i],
      ['eachClaim',      /^(?!.*alleged\s+participant).*\b(?:abuse|molestation|misconduct)\b.*\beach\s+claim\b/i],
      ['aggregate',      /^(?!.*alleged\s+participant).*\b(?:abuse|molestation|misconduct)\b.*\baggregate\b/i],
      ['medicalExpense', /^(?!.*alleged\s+participant).*\b(?:abuse|molestation|misconduct)\b.*\bmedical\s+expense\b/i]
    ] },
    { key: 'Educators Legal Liability', slots: [
      // Value-first rows carry the coverage's label in front of the slot
      // name — "Educators Legal Liability Each Wrongful Act" — so these match
      // anywhere in the label rather than at its start.
      ['eachWrongfulAct', /\beach\s+wrongful\s+act\b/i, /^coverage\s*a\b.*\beach\s+wrongful\s+act\b/i],
      ['aggregate',       /\baggregate\b(?!\s*deductible)/i, /^coverage\s*a\b.*\baggregate\b/i]
    ] },
    /* The excess declarations state the layer plainly, which is the whole
     * point of an excess policy: one occurrence limit and one aggregate. */
    { key: 'Umbrella/Excess', slots: [
      ['eachOccurrence', /^each\s+occurrence(?:\s+limit)?$/i],
      ['aggregate',      /^aggregate(?:\s+limit)?$/i]
    ] },
    /* EBL states its limit in a schedule row whose amount and kind share a
     * cell — "$1,000,000 each \u201cemployee\u201d" — with the aggregate on a
     * continuation line below. Tulsa Honor Academy's aggregate was reported as
     * $3,000,000; the schedule says $2,000,000, on the line nothing read. */
    { key: 'Employee Benefits Liability', slots: [
      // The aggregate row carries the same coverage name in front of it, so
      // without this both rows answer here and the slot goes silent.
      ['eachEmployee', /^(?!.*\baggregate\b).*employee\s+benefits?\s+program/i],
      ['aggregate',    /employee\s+benefits?\s+program.*\baggregate\b/i]
    ] },
    /* Professional liability and E&O are ONE coverage. Tulsa Honor Academy
     * carried both as separate rows with different figures — $1,000,000 each
     * wrongful act under one and $3,000,000 each claim under the other — and
     * the benchmarking table rendered them both. Its counselling professional
     * liability endorsement states $1,000,000 each wrongful act over
     * $3,000,000 aggregate, and that is the whole of it. */
    { key: 'Professional Liability / E&O', slots: [
      ['eachWrongfulAct', /^each\s+wrongful\s+act$/i],
      ['aggregate',       /^aggregate$/i]
    ] },
    { key: 'Cyber Liability', ownSlots: true, slots: [
      // A cyber policy states its ceiling once and its insuring agreements
      // many times. The ceiling is the limit; the agreements are sublimits,
      // and the reader already types those out.
      ['aggregate', /^(?:policy\s+aggregate\s+limit|aggregate\s+limit(?:\s+of\s+liability)?|policy\s+limit|maximum\s+policy\s+(?:aggregate\s+)?limit)$/i],
      ['eachClaim', /^each\s+(?:claim|incident|event|occurrence)\b/i]
    ] }
  ];

  SP_SCOPED_LIMIT_SPECS.forEach(spec => pdfTexts.forEach(doc => {
    if (!doc || !doc.pages || typeof window === 'undefined' || !window.spReadFields) return;
    const CTd = window.spCoverageTerms;
    if (!CTd) return;
    /* THE PAGE THAT ANSWERS THE MOST OF THEM WINS, NOT THE FIRST TO ANSWER
     * ANY.
     *
     * The property migration reads page by page and stops at the first page
     * that answers, because a declarations page comes at the front. A general
     * liability program does not oblige: Matrix Head Start's package policy
     * carries an endorsement schedule at page 105 stating "Each Occurrence
     * $50,000", and its real declarations — six limits together — sit much
     * later. Stopping at the first answer took the schedule and the whole
     * declarations page stayed invisible.
     *
     * A declarations page states the SET. A schedule states one or two. Count
     * how many distinct slots each page answers and take the best, earliest
     * first on a tie.
     */
    const ownersOf = (scope) => {
      if (!scope) return null;
      const owners = CTd.coveragesIn(scope);
      return owners.length ? owners : null;
    };
    const target0 = CTd.canonical(spec.key) || spec.key;
    const pageNames = f => f.some(x => (ownersOf(x.scope) || [])
      .some(c => CTd.sameTerm(c, spec.key)));
    /* AND A PAGE THAT NAMES THE COVERAGE OUTRANKS ONE THAT ANSWERS MORE.
     *
     * Counting slots alone sent the D&O spec to the page holding FIDUCIARY's
     * aggregate and its "Each Claim" retention — two slots answered, neither
     * of them D&O's — over the page that actually heads the D&O coverage part
     * and states its one limit. On a stacked page the heading is the whole
     * point, so it dominates the count.
     */
    let fields = null, bestScore = 0, chosenText = '';
    for (const pg of (doc.pages || [])) {
      // A page with no amount on it cannot answer a limit slot, and most pages
      // of a policy are forms with no amounts. Checked before parsing, because
      // parsing is the expensive part.
      if (String(pg.text || '').indexOf('$') < 0) continue;
      const f = window.spReadFields(pg.text || '');
      const answered = spec.slots.filter(([, re]) => f.some(x => x.money && re.test(x.label))).length;
      if (!answered) continue;
      const score = answered + (pageNames(f) ? 10 : 0);
      if (score > bestScore) { bestScore = score; fields = f; chosenText = pg.text || ''; }
    }
    if (!fields || !fields.length) return;
    const target = CTd.canonical(spec.key) || spec.key;
    const isOurs = f => (ownersOf(f.scope) || []).some(c => CTd.sameTerm(c, spec.key) || c === target);
    const scopedFields = fields.filter(f => ownersOf(f.scope));
    const pageNamesIt = scopedFields.some(isOurs);
    if (scopedFields.length && !pageNamesIt) return;
    /* AND THE DOCUMENT HAS TO BE ABOUT THIS COVERAGE AT ALL.
     *
     * Without this the monoline fallback writes a row onto any policy with an
     * unscoped "Limit of Liability" field. Tulsa Honor Academy, which carries
     * no management liability, grew a D&O row reading "15. Preservation of
     * Property $100,000" — a wrapped clause line, on a program where the
     * correct answer is that the coverage does not exist. */
    if (!pageNamesIt) {
      const pick2 = (typeof spDocumentForCoverage === 'function')
        ? spDocumentForCoverage(pdfTexts, spec.key) : null;
      if (!pick2 || pick2.score < 4 || pick2.doc !== doc) return;
    }

    const believable = f =>
      // 'grid' is a structural read — the amount's own cell, its row label and
      // its column — so it is at least as strong as a labelled field and
      // stronger than a row reconstructed from whitespace. Omitting it here
      // silently rejected every limit the grid reader found, which on a
      // subscription property policy meant all of them.
      (f.provenance === 'field' || f.provenance === 'row' || f.provenance === 'grid')
      && !['sublimit', 'retention', 'cost', 'date', 'attribute'].includes(f.kind)
      && f.money;
    const pickBy = (re) => {
      const all = fields.filter(f => believable(f) && re.test(f.label));
      const hits = pageNamesIt ? all.filter(isOurs) : all;
      // Take the AMOUNT the reader parsed, not the raw cell. A schedule
      // writes "$1,000,000 each \u201cemployee\u201d" and "$ 3,000,000", and a
      // shape test on the raw text rejects both — so a real limit was refused
      // here and then withdrawn as a blank row below.
      const distinct = [...new Set(hits.map(f => '$' + f.money))];
      return distinct.length === 1 ? distinct[0] : null;
    };
    /* A COVERAGE PART CAN STATE THE SAME SLOT FOR SEVERAL INSURING CLAUSES.
     *
     * Tulsa Honor Academy's educators legal liability declarations:
     *
     *     Coverage A   $ 1,000,000   Each Wrongful Act or Offense
     *     Coverage A   $ 3,000,000   Aggregate
     *     Coverage B   $   100,000   Each Action
     *     Coverage B   $   300,000   Aggregate
     *
     * Two aggregates, so the uniqueness rule correctly refused to choose and
     * a stale $2,000,000 from an older reader stayed on the row. The primary
     * insuring clause is the coverage; the rest are its parts. A slot may name
     * the clause it prefers, and falls back to the plain label where the
     * policy does not use that vocabulary.
     */
    const pick = (re, prefer) => (prefer && pickBy(prefer)) || pickBy(re);

    const got = {};
    for (const [slot, re, prefer] of spec.slots) {
      const v = pick(re, prefer);
      // One limit, two internal names. Different consumers spell the GL
      // per-occurrence slot differently, and a reader that writes only one of
      // them silently blanks whichever table reads the other.
      if (v) for (const name of [].concat(slot)) got[name] = v;
    }
    /* A COVERAGE ROW WITH NO AMOUNT ON IT IS A COVERAGE NOT BOUGHT.
     *
     * Tulsa Honor Academy's auto row reported UM/UIM at $1,000,000. Its
     * business auto declarations print the rows and leave them empty:
     *
     *     Uninsured Motorists
     *     Underinsured Motorists
     *     (When Not Included In Uninsured Motorists Coverage)
     *
     * no limit, no premium — on a page that states the rule itself: "This
     * Policy provides only those coverages where a charge is shown in the
     * premium column below." The coverage is named in order to record that it
     * was NOT purchased, which is what "No Coverage" says in a property
     * schedule, printed as an absence instead of a word.
     *
     * So where the chosen page names a slot and gives it no amount, the slot
     * is empty, and a figure standing there came from somewhere else.
     */
    const withdrawn = [];
    for (const [slot, re] of spec.slots) {
      for (const name of [].concat(slot)) {
        if (got[name] !== undefined) continue;
        // If any field on this page states the slot WITH an amount, the row is
        // not blank — the reader simply could not choose between several, and
        // silence is not a declination.
        if (fields.some(f => f.money && re.test(f.label))) continue;
        const blank = String(chosenText || '').split('\n').some(l => {
          const t = l.trim();
          return t && t.length <= 60 && t.indexOf('$') < 0 && re.test(t);
        });
        if (blank) withdrawn.push(name);
      }
    }
    if (!Object.keys(got).length && !withdrawn.length) return;

    /* OWNING THE SLOTS OUTRIGHT IS RIGHT FOR SOME COVERAGES AND WRONG FOR
     * OTHERS, AND THE DIFFERENCE IS WHERE THE DANGER IS.
     *
     * On a stacked management liability page the danger is a NEIGHBOUR: four
     * coverage parts with identical labels, so a slot this reader cannot see
     * under our heading is a slot the policy does not grant, and anything
     * standing there came from the part above. Those specs own their slots.
     *
     * A GL or auto program spreads its limits over several declarations pages
     * and endorsements — Tulsa Honor Academy's UM/UIM limit is not on the page
     * carrying its Covered Autos Liability row. Owning outright there deletes
     * real limits this reader simply did not reach, so those specs merge.
     */
    /* WRITE TO THE KEY THE PROGRAM ALREADY USES.
     *
     * Matrix Head Start's limits call this coverage "Sexual Abuse /
     * Molestation" and Tulsa Honor Academy's call it "Sexual Abuse/
     * Molestation" — the same coverage, spelled by whichever reader got there
     * first. A spec that writes its own spelling produced TWO rows for one
     * coverage, both rendered, one of them stale. The same holds for
     * "Educators Legal Liability (ELL)".
     */
    const slotKey = Object.keys(limits).find(k => CTd.sameTerm(k, spec.key) || k === target0)
      || spec.key;
    const existing = limits[slotKey] || {};
    const kept = spec.ownSlots
      ? Object.fromEntries(Object.entries(existing).filter(([k]) => k.startsWith('_')))
      : Object.assign({}, existing);
    Object.assign(kept, got);
    for (const name of withdrawn) {
      if (kept[name] !== undefined && typeof console !== 'undefined' && console.log) {
        console.log('[limits] withdrawing ' + slotKey + '.' + name + ' ' + kept[name] +
          ' \u2014 the declarations print that row with no amount on it');
      }
      delete kept[name];
    }
    /* A STALE VALUE UNDER A DIFFERENT NAME FOR THE SAME CONCEPT CONTRADICTS
     * THE ONE JUST READ.
     *
     * Matrix Head Start's GL came out with damageToPremises $1,000,000, which
     * is what the declarations say, sitting beside premisesDamage $3,000,000
     * left by the earlier reader. Both rendered. Where this reader has stated
     * a concept, the other spellings of it are gone.
     */
    const SLOT_EQUIV = {
      damageToPremises: ['premisesDamage', 'fireDamage', 'damageToRentedPremises'],
      eachOccurrence:   ['perOccurrence'],
      perOccurrence:    ['eachOccurrence'],
      medicalExpense:   ['medExpense', 'medicalPayments'],
      combinedSingleLimit: ['csl'],
      aggregate:        ['generalAggregate', 'aggregateLimit']
    };
    for (const name of Object.keys(got)) {
      for (const alias of (SLOT_EQUIV[name] || [])) {
        if (got[alias] === undefined) delete kept[alias];
      }
    }
    kept._readBy = 'field-reader';
    limits[slotKey] = kept;
  }));

  /* ONE COVERAGE, ONE ROW.
   *
   * sameTerm('E&O', 'Professional Liability') is true — they are one class —
   * and Tulsa Honor Academy's limits carried both keys, with different figures
   * under each. The benchmarking table rendered two rows for one coverage and
   * a reviewer had no way to tell which was the policy's. Where two keys name
   * the same coverage, the one the structural reader filled is the answer and
   * the other is a spelling.
   */
  try {
    const CTc = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
    if (CTc) {
      const keys = Object.keys(limits);
      for (let i = 0; i < keys.length; i++) {
        for (let j = i + 1; j < keys.length; j++) {
          const a = keys[i], b = keys[j];
          if (!limits[a] || !limits[b] || !CTc.sameTerm(a, b)) continue;
          const readA = (limits[a] || {})._readBy, readB = (limits[b] || {})._readBy;
          // Neither read structurally: leave both alone rather than guess.
          if (!readA && !readB) continue;
          const keep = readA ? a : b, drop = readA ? b : a;
          if (typeof console !== 'undefined' && console.log) {
            console.log('[limits] ' + drop + ' is another spelling of ' + keep);
          }
          delete limits[drop];
          if (limitsTrust) delete limitsTrust[drop];
        }
      }
    }
  } catch (_e) { /* a tidy-up that throws must not take the limits with it */ }

  /* A LIMIT READ OUT OF AN UNDERLYING SCHEDULE OR AN EXCLUSION.
   *
   * Tulsa Honor Academy's excess policy reported ten coverages: Workers
   * Compensation at $3,000,000, Commercial Auto at $3,000,000, D&O at
   * $5,000,000. Every figure is real and correctly transcribed — they are the
   * limits in its Schedule of Controlling Underlying Insurance, which is other
   * carriers' cover, listed so the excess layer knows where it attaches. Its
   * property policy did the same with an exclusion: LMA 5401, "Property Cyber
   * and Data Exclusion", produced Cyber Liability at $1,000,000, so a gap that
   * is real was reported as closed. Over-detection is worse than the reverse:
   * a coverage that fails to parse says so on the page, while a phantom one
   * reads as a finding and carries into the benchmarking table.
   *
   * Two things have to be true before a limit is withdrawn, and neither is
   * sufficient alone.
   *
   *   Detection does not find the coverage in ANY document. Detection is the
   *   better-guarded pass, but it is not proof of absence — the Chubb
   *   ForeFront excerpt names its D&O coverage part in an ordinary
   *   declarations heading and detection still does not see it. So on its own
   *   this would withdraw real limits.
   *
   *   AND no mention of the coverage anywhere in the documents sits in a
   *   grant position — a coverage-part heading, an insuring agreement, a
   *   declarations row with a limit beside it. On its own this is too strict,
   *   because a policy can grant cover in a layout spInGrantPosition does not
   *   recognise; detection is what stops that from mattering.
   *
   * Together they say: nothing in these documents detects this coverage, and
   * nowhere in them does it appear in a position that grants it. The excess
   * policy's five Workers' Compensation mentions are all exclusion headings;
   * its one D&O mention is mid-sentence inside a definition.
   */
  try {
    const CTg = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
    const detected = CTg ? [...detectCoveragesFromPdfText(pdfTexts)] : [];
    if (CTg) {
      for (const key of Object.keys(limits)) {
        const entry = limits[key] || {};
        const vals = Object.keys(entry).filter(k => !k.startsWith('_'));
        if (!vals.length) continue;
        if (detected.some(c => CTg.sameTerm(c, key))) continue;
        // A value the structural reader took from a (label, value) field was
        // not grabbed by proximity, which is the whole failure this gate is
        // for. Its provenance outranks the line heuristic below — and on a
        // short declarations excerpt, detection has too little to work with
        // while the field is still plainly a field.
        if (entry._readBy || entry._scheduled || entry._valuation || entry._tiv) continue;
        let granted = false;
        for (const doc of (pdfTexts || [])) {
          for (const pg of ((doc && doc.pages) || [])) {
            const t = String((pg && pg.text) || '');
            if (!t) continue;
            for (const hit of CTg.mentionsOf(t, key)) {
              if (spInGrantPosition(t, hit.index, hit.term)) { granted = true; break; }
            }
            if (granted) break;
          }
          if (granted) break;
        }
        if (granted) continue;
        if (typeof console !== 'undefined' && console.log) {
          console.log('[limits] withdrawing ' + key +
            ' — no document detects it and no mention of it sits in a grant position');
        }
        delete limits[key];
        delete limitsTrust[key];
      }
    }
  } catch (_e) { /* a gate that throws must not take the limits with it */ }

  /* Coverage-part elections and K&R, read from their own tables. Added after
   * the withdrawal gate: a declined part must survive it, because "declined"
   * is the finding. */
  try {
    const keyOf = p => /employment\s+practices/i.test(p) ? 'EPLI'
      : /fiduciary/i.test(p) ? 'Fiduciary Liability'
      : /directors|officers|management\s+liability/i.test(p) ? 'D&O' : null;
    for (const row of spReadElectionTable(pdfTexts)) {
      const key = keyOf(row.part);
      if (!key) continue;
      const cur = limits[key] && Object.keys(limits[key]).some(k => k.charAt(0) !== '_') ? limits[key] : null;
      if (row.elected && row.limit) {
        limits[key] = Object.assign({}, cur || {}, { aggregate: row.limit },
          row.retention && row.retention !== '$0' ? { retention: row.retention } : {},
          row.priorPending ? { _priorPending: row.priorPending } : {});
      } else if (!row.elected && !cur) {
        limits[key] = { _declined: { stance: 'declined', quote: row.quote, fileName: row.fileName, page: row.page } };
      }
    }
    const kr = spReadKidnapRansom(pdfTexts);
    if (kr && !limits['Kidnap & Ransom']) limits['Kidnap & Ransom'] = kr;
  } catch (_e) { /* a secondary reader must never cost the primary limits */ }

  /* The limits the policy states that no spec asked for, attached to the
   * coverage they belong to. Added last, after the withdrawal gate, so a
   * coverage the program does not carry cannot acquire a list of stated
   * limits on its way out. */
  try {
    const shownVals = new Set();
    for (const cov of Object.values(limits)) {
      if (!cov || typeof cov !== 'object') continue;
      for (const [k, v] of Object.entries(cov)) {
        if (k.charAt(0) !== '_') shownVals.add(String(v).replace(/\s+/g, ''));
      }
    }
    const extra = spOtherStatedLimits(pdfTexts, shownVals);
    for (const [cov, list] of extra) {
      if (!limits[cov] || !list.length) continue;   // only for coverages the report already carries
      limits[cov]._other = list;
    }
  } catch (_e) { /* a secondary list must never cost the primary ones */ }

  spApplyLimitInvariants(limits);
  _spDone();
  return limits;
}

// Format extracted limits into display string for benchmarking table
function formatExtractedLimits(limitsObj) {
  if (!limitsObj || typeof limitsObj !== 'object') return null;
  const parts = [];
  const labels = {
    eachOccurrence: 'Each Occ', eachClaim: 'Each Claim', eachWrongfulAct: 'Each Wrongful Act',
    eachIncident: 'Each Incident', eachEmployee: 'Each Employee', eachAccident: 'Each Accident',
    generalAggregate: 'Gen Agg', aggregate: 'Aggregate', excessOf: 'Excess of', retention: 'Retention', transit: 'Transit', productsCompletedOps: 'Prod/Comp Ops Agg',
    personalAdvertising: 'Personal & Advertising Injury', premisesDamage: 'Premises Damage',
    damageToPremises: 'Damage to Premises Rented',
    medicalExpense: 'Med Expense', combinedSingleLimit: 'CSL', umUim: 'UM/UIM',
    perOccurrence: 'Per Occurrence', autoLiability: 'Auto Liability',
    building: 'Building', bpp: 'BPP', contents: 'Contents', businessIncome: 'Bus Income',
    extraExpense: 'Extra Expense',
    employeeTheft: 'Employee Theft', forgery: 'Forgery', computerFraud: 'Computer Fraud',
    limit: 'Limit', diseaseEachEmployee: 'Disease/Employee', diseasePolicyLimit: 'Disease Policy Limit',
    coverageBEachAction: 'Cov B Each Action',
    eachAct: 'Each Act', allegedParticipantEachAct: 'Alleged Participant Each Act',
    allegedParticipantAggregate: 'Alleged Participant Agg', supplementaryPayment: 'Supplementary Payment'
  };
  const _valuation = !!(limitsObj && limitsObj._valuation);
  for (const [key, val] of Object.entries(limitsObj)) {
    if (key.startsWith('_')) continue; // Skip metadata fields
    const clean = spPlausibleLimit(val, { valuation: _valuation });
    if (!clean) continue;                       // not a limit — see below
    /* A KEY WITH NO LABEL IS NOT A LABEL.
     *
     * The fallback printed the field name verbatim, so "damageToPremises:
     * $1,000,000" appeared beside "Gen Agg" and "Med Expense" in three rows of
     * a live report. The map will always lag the readers — a slot added to a
     * coverage spec does not know about this list — so the fallback has to
     * degrade to something readable rather than to source code. */
    const label = labels[key]
      || key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/^./, c => c.toUpperCase());
    parts.push(`${label}: <strong>${clean}</strong>`);
  }
  return parts.length ? parts.join('<br>') : null;
}

// IS THIS A LIMIT, OR A NUMBER THAT HAPPENED TO BE NEARBY?
//
// The extractors read declarations pages where numbers of every kind sit
// together, and printed whatever they found next to a label. On the Base Power
// report that produced "CSL: $3180182" and "UM/UIM: $3180182" — which is the
// AUTO POLICY NUMBER, ECA318018210, with its letters stripped — and "Premises
// Damage: $2018", which is the copyright year in the form's footer.
//
// A limit is a round number. Insurers write $1,000,000 and $25,000; they do
// not write $3,180,182 of combined single limit. So a value must be at least
// $1,000 and a multiple of 500 to print. A genuine odd limit would be
// suppressed by this — the trade is deliberate: a suppressed limit shows as
// "limits not parsed" and sends the broker to the declarations, while a policy
// number printed as a limit is quoted to a client as coverage.
// Whole dollars of a money string. "$1,000,000.00" is one million: stripping
// every non-digit read it as 100,000,000, which is how Texas Mutual's employers
// liability printed as "Each Accident: $100,000,000" on Holloway Houston.
function spMoneyWhole(str) {
  const s = String(str == null ? '' : str).trim().replace(/\.\d{1,2}\s*$/, '');
  return parseInt(s.replace(/[^\d]/g, ''), 10);
}

function spPlausibleLimit(val, opts) {
  if (val == null) return null;
  const raw = String(val).trim();
  if (!raw) return null;
  // Anything that is not purely a number — "Included", "Statutory", "Full
  // Prior Acts" — is a policy's own words and prints as written.
  if (!/^[\s$]*[\d,.]+[\s]*$/.test(raw)) return raw;
  const n = spMoneyWhole(raw);
  if (!isFinite(n) || n < 1000) return null;
  // A limit is chosen, so it is round; a policy number is not. That rule
  // stops a policy number printing as a limit, and it is right for a figure
  // scraped from NEAR a label.
  //
  // It is wrong for a VALUATION. A blanket schedule states insured values,
  // and an appraised building is $25,722,000 while its contents are
  // $2,068,700 and the business income worksheet says $418,744. Only the
  // first of those is a multiple of 500, so Aspen View's property row printed
  // the building and silently dropped the other two — a number the document
  // states plainly, discarded by a heuristic meant for numbers whose identity
  // is in doubt. Where the document labels the field, identity is not in
  // doubt and roundness proves nothing.
  if (!(opts && opts.valuation) && n % 500 !== 0) return null;
  return '$' + n.toLocaleString('en-US');
}
try { window.spPlausibleLimit = spPlausibleLimit; } catch (e) { /* no window */ }

// Parse a dollar string to numeric value for comparison
function parseDollarAmount(str) {
  if (!str || typeof str !== 'string') return 0;
  const m = str.replace(/[,$\s]/g, '').match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

// Get the primary (highest) limit value from a limits object
function getPrimaryLimit(limitsObj) {
  if (!limitsObj) return 0;
  let maxVal = 0;
  for (const [key, val] of Object.entries(limitsObj)) {
    if (key.startsWith('_')) continue; // Skip metadata fields
    const n = parseDollarAmount(val);
    if (n > maxVal) maxVal = n;
  }
  return maxVal;
}

// Compare extracted limits against a minimum threshold string and return status
// Returns: MEETS, EXCEEDS, BELOW MINIMUM, or null if no data
function compareLimitToThreshold(limitsObj, thresholdStr) {
  if (!limitsObj) return null;
  const primaryLimit = getPrimaryLimit(limitsObj);
  if (primaryLimit === 0) return null;
  
  // Parse threshold - handle formats like "$1M", "$1M–$2M", "$500K", "$1,000,000", "Statutory + $1M EL"
  // Extract all dollar amounts, convert M/K suffixes, take the lowest as the minimum standard
  function parseThresholdAmounts(str) {
    const amounts = [];
    // Match patterns like $1M, $2.5M, $500K, $25K, $1,000,000, etc.
    const regex = /\$?([\d,.]+)\s*(M|m|million|K|k|thousand)?/g;
    let m;
    while ((m = regex.exec(str)) !== null) {
      let val = parseFloat(m[1].replace(/,/g, ''));
      if (isNaN(val)) continue;
      const suffix = (m[2] || '').toLowerCase();
      if (suffix === 'm' || suffix === 'million') val *= 1000000;
      else if (suffix === 'k' || suffix === 'thousand') val *= 1000;
      // Only consider values that look like real dollar amounts (>= $1000)
      if (val >= 1000) amounts.push(val);
    }
    return amounts;
  }
  
  const thresholdAmounts = parseThresholdAmounts(thresholdStr);
  if (thresholdAmounts.length === 0) return 'MEETS'; // No numeric threshold to compare against
  // Use the LOWEST amount as the minimum industry standard to beat
  const threshold = Math.min(...thresholdAmounts);
  
  if (primaryLimit >= threshold * 1.5) return 'EXCEEDS';
  if (primaryLimit >= threshold) return 'MEETS';
  return 'BELOW MINIMUM';
}


// ===== EB BENEFITS EXTRACTION ENGINE =====
// Mirrors extractDeclarationsLimits() for P&C but targets employee benefits data:
// carriers, plan types, deductibles, copays, coinsurance, premiums, annual maximums, etc.

// ===== EB RENEWAL PACKAGE & SBC MULTI-PLAN EXTRACTION (v2.17) =====
// Detects carrier renewal packages with multiple plan options and SBC documents.
// Returns array of plan objects with structured benefit data per plan.
function extractRenewalPackagePlans(pdfTexts) {
  if (!pdfTexts || pdfTexts.length === 0) return null;
  const plans = [];
  
  pdfTexts.forEach(doc => {
    if (!doc.pages || !Array.isArray(doc.pages)) return;
    
    // Find plan benefit pages (EMPLOYEE GROUP HEALTH BENEFITS or rate/benefit grid pages)
    for (let i = 0; i < doc.pages.length; i++) {
      const pageText = doc.pages[i].text || '';
      if (pageText.length < 100) continue;
      
      // Detect benefit summary pages — carrier-formatted plan grids
      const isBenefitPage = (/EMPLOYEE\s+GROUP\s+HEALTH\s+BENEFITS/i.test(pageText) || /RENEWAL\s+PLANS?\s+AND\s+RATES/i.test(pageText) || /PLAN\s+OPTIONS/i.test(pageText))
        && /Deductible/i.test(pageText) && /Coinsurance/i.test(pageText);
      
      if (!isBenefitPage) continue;
      
      const plan = {};
      const txt = pageText;
      
      // Plan name — carrier name + plan variant
      // Format 1: same line "BlueOptions / Predictable Cost 14054"
      const headerMatch = txt.match(/PLAN\s+OPTIONS[\s\S]*?((?:Blue|Aetna|Cigna|United|Kaiser|Humana|Anthem|Florida|Oxford|Oscar|Molina|WellCare|Ambetter|AvMed)\w*\s*[\/\-]?\s*[A-Za-z\s\(\)]*?)\s+(\d{4,6})/i);
      if (headerMatch) {
        plan.planName = headerMatch[1].replace(/\s+/g, ' ').trim();
        plan.planNumber = headerMatch[2];
      }
      // Format 2: multi-line "Plan Name/Family Name BlueOptions / Predictable Cost\n# 14054"
      if (!plan.planName) {
        const nameLineMatch = txt.match(/Plan\s+Name[^\n]*?\s+((?:Blue|Aetna|Cigna|United|Kaiser|Humana|Anthem|Florida|Oxford|Oscar|Molina|WellCare|Ambetter|AvMed)\w*\s*[\/\-]?\s*[A-Za-z\s\(\)]*)/i);
        if (nameLineMatch) {
          plan.planName = nameLineMatch[1].replace(/\s+/g, ' ').trim();
        }
        const numLineMatch = txt.match(/(?:^|\n)\s*#\s*(\d{4,6})/m);
        if (numLineMatch) {
          plan.planNumber = numLineMatch[1];
        }
      }
      
      // Metal tier
      const metalMatch = txt.match(/(?:Metal\s+Category|Tier)\s*[:\s]*(Platinum|Gold|Silver|Bronze|Catastrophic)/i);
      if (metalMatch) plan.metalTier = metalMatch[1];
      
      // Deductible — In-Network individual/family
      const dedMatch = txt.match(/Deductible\s+\$?(\d[\d,]*)\s*\/\s*\$?(\d[\d,]*)\s+\$?(\d[\d,]*)\s*\/\s*\$?(\d[\d,]*)/i);
      if (dedMatch) {
        plan.deductibleIN = '$' + dedMatch[1] + '/$' + dedMatch[2];
        plan.deductibleOON = '$' + dedMatch[3] + '/$' + dedMatch[4];
      } else {
        const dedSimple = txt.match(/Deductible\s+\$?(\d[\d,]*)\s*\/\s*\$?(\d[\d,]*)/i);
        if (dedSimple) plan.deductibleIN = '$' + dedSimple[1] + '/$' + dedSimple[2];
      }
      
      // Coinsurance
      const coinsMatch = txt.match(/Coinsurance\s+(\d+)%\s*\/\s*(\d+)%\s+(\d+)%\s*\/\s*(\d+)%/i);
      if (coinsMatch) {
        plan.coinsuranceIN = coinsMatch[1] + '%/' + coinsMatch[2] + '%';
        plan.coinsuranceOON = coinsMatch[3] + '%/' + coinsMatch[4] + '%';
      } else {
        const coinsSimple = txt.match(/Coinsurance\s+(\d+)%\s*\/\s*(\d+)%/i);
        if (coinsSimple) plan.coinsuranceIN = coinsSimple[1] + '%/' + coinsSimple[2] + '%';
      }
      
      // Out of Pocket Maximum
      const oopMatch = txt.match(/Out\s+of\s+Pocket\s+Maximum\s+\$?(\d[\d,]*)\s*\/\s*\$?(\d[\d,]*)\s+\$?(\d[\d,]*)\s*\/\s*\$?(\d[\d,]*)/i);
      if (oopMatch) {
        plan.oopMaxIN = '$' + oopMatch[1] + '/$' + oopMatch[2];
        plan.oopMaxOON = '$' + oopMatch[3] + '/$' + oopMatch[4];
      }
      
      // PCP Copay (various formats)
      const pcpMatch = txt.match(/(?:Family\s+Physician|PCP|Primary\s+Care)\s+\$?(\d+)\s+Copay/i);
      if (pcpMatch) plan.pcpCopay = '$' + pcpMatch[1];
      if (/Value\s+Choice\s+PCP\s+\$0\s+Copay/i.test(txt)) plan.vcPcpCopay = '$0';
      
      // Specialist Copay
      const specMatch = txt.match(/Specialist\s+\$?(\d+)\s+Copay/i);
      if (specMatch) plan.specialistCopay = '$' + specMatch[1];
      
      // ER Copay
      const erMatch = txt.match(/Emergency\s+Room\s+\$?(\d+)\s+Copay/i);
      if (erMatch) plan.erCopay = '$' + erMatch[1];
      const erDedMatch = txt.match(/Emergency\s+Room\s+(DED\s*\+\s*\$?\d+\s+Copay)/i);
      if (erDedMatch) plan.erCopay = erDedMatch[1];
      
      // Inpatient
      const inpMatch = txt.match(/Inpatient\s+((?:Option\s+\d:\s*)?DED\s*\+\s*\d+%|DED\s*\+\s*\$[\d,]+\s+Copay|\$[\d,]+\s+Copay)/i);
      if (inpMatch) plan.inpatient = inpMatch[1].replace(/\s+/g, ' ');
      
      // Rx Benefit tiers
      const rxMatch = txt.match(/Rx\s+Benefit\s+\$?(\d+)\s*\/\s*\$?(\d+)\s*\/\s*\$?(\d+)\s*\/?\s*\$?(\d*)/i);
      if (rxMatch) {
        plan.rxTiers = '$' + rxMatch[1] + '/$' + rxMatch[2] + '/$' + rxMatch[3];
        if (rxMatch[4]) if (rxMatch[4].length > 0) plan.rxTiers += '/$' + rxMatch[4];
      }
      
      // Extract key rate points from age-banded table
      let rateText = txt;
      if (i + 1 < doc.pages.length) {
        const nextPage = doc.pages[i + 1].text || '';
        if (!/Deductible/i.test(nextPage) && /^\d{1,2}\s+\$/m.test(nextPage)) {
          rateText += '\n' + nextPage;
        }
      }
      const rates = {};
      const rateRegex = /(?:^|\n)\s*(\d{1,2}(?:\+)?|0-14)\s+\$?([\d,]+\.?\d*)/gm;
      let rm;
      while ((rm = rateRegex.exec(rateText)) !== null) {
        rates[rm[1]] = '$' + rm[2];
      }
      if (Object.keys(rates).length > 3) {
        plan.rate21 = rates['21'] || null;
        plan.rate30 = rates['30'] || null;
        plan.rate40 = rates['40'] || null;
        plan.rate50 = rates['50'] || null;
        plan.rate60 = rates['60'] || null;
        plan.rateChild = rates['0-14'] || null;
      }
      
      // Carrier detection
      if (/florida\s+blue|bcbs.*florida|blue\s+cross.*blue\s+shield.*florida/i.test(txt)) plan.carrier = 'Florida Blue (BCBS)';
      else if (/anthem/i.test(txt)) plan.carrier = 'Anthem';
      else if (/cigna/i.test(txt)) plan.carrier = 'Cigna';
      else if (/aetna/i.test(txt)) plan.carrier = 'Aetna';
      else if (/united\s*health/i.test(txt)) plan.carrier = 'UnitedHealthcare';
      else if (/kaiser/i.test(txt)) plan.carrier = 'Kaiser';
      else if (/humana/i.test(txt)) plan.carrier = 'Humana';
      else if (/blue\s+cross|blue\s+shield|bcbs/i.test(txt)) plan.carrier = 'BCBS';
      
      // Group info
      const groupMatch = txt.match(/Group\s+Name:\s*([^\n]+)/i);
      if (groupMatch) plan.groupName = groupMatch[1].trim();
      const groupNumMatch = txt.match(/Group\s+Number:\s*(\d+)/i);
      if (groupNumMatch) plan.groupNumber = groupNumMatch[1];
      const annivMatch = txt.match(/Anniversary\s+Date:\s*([\d\/]+)/i);
      if (annivMatch) plan.anniversaryDate = annivMatch[1];
      
      if (plan.deductibleIN || plan.oopMaxIN || plan.planName) {
        plans.push(plan);
      }
    }
    
    // PASS 2: Enrich with SBC data
    for (let i = 0; i < doc.pages.length; i++) {
      const pageText = doc.pages[i].text || '';
      if (!/Summary\s+of\s+Benefits\s+and\s+Coverage/i.test(pageText)) continue;
      const sbcPlanNum = pageText.match(/((?:Blue|Aetna|Cigna|United|Kaiser|Humana|Anthem)\w*)\s+(\d{4,6})/i);
      if (sbcPlanNum) {
        const matched = plans.find(p => p.planNumber === sbcPlanNum[2]);
        if (matched) {
          const cp = pageText.match(/Coverage\s+Period:\s*([\d\/]+\s*-\s*[\d\/]+)/i);
          if (cp) matched.coveragePeriod = cp[1];
          matched.hasSBC = true;
        }
      }
    }
  });
  
  return plans.length > 0 ? plans : null;
}

// Format multi-plan renewal data for display in Benefits Competitiveness table
function formatRenewalPlansDisplay(plans) {
  if (!plans || plans.length === 0) return null;
  let html = '';
  plans.forEach((plan, idx) => {
    const label = plan.planName ? (plan.planName + (plan.planNumber ? ' (' + plan.planNumber + ')' : '')) : ('Plan ' + (idx + 1));
    const tier = plan.metalTier ? ' — <span style="font-weight:600;">' + plan.metalTier + '</span>' : '';
    html += '<div style="margin-bottom:8px;padding:6px 8px;background:' + (idx % 2 === 0 ? '#f0f7f7' : '#f7f0f0') + ';border-radius:4px;border-left:3px solid ' + (idx % 2 === 0 ? '#01696F' : '#8B4513') + ';">';
    html += '<strong>' + label + '</strong>' + tier + '<br>';
    if (plan.carrier) html += 'Carrier: <strong>' + plan.carrier + '</strong><br>';
    if (plan.deductibleIN) html += 'Deductible (In): <strong>' + plan.deductibleIN + '</strong>';
    if (plan.deductibleOON) html += ' | Out: <strong>' + plan.deductibleOON + '</strong>';
    if (plan.deductibleIN) html += '<br>';
    if (plan.coinsuranceIN) html += 'Coinsurance (In): <strong>' + plan.coinsuranceIN + '</strong>';
    if (plan.coinsuranceOON) html += ' | Out: <strong>' + plan.coinsuranceOON + '</strong>';
    if (plan.coinsuranceIN) html += '<br>';
    if (plan.oopMaxIN) html += 'OOP Max (In): <strong>' + plan.oopMaxIN + '</strong>';
    if (plan.oopMaxOON) html += ' | Out: <strong>' + plan.oopMaxOON + '</strong>';
    if (plan.oopMaxIN) html += '<br>';
    if (plan.pcpCopay) html += 'PCP: <strong>' + plan.pcpCopay + '</strong> | ';
    if (plan.specialistCopay) html += 'Specialist: <strong>' + plan.specialistCopay + '</strong> | ';
    if (plan.erCopay) html += 'ER: <strong>' + plan.erCopay + '</strong>';
    if (plan.pcpCopay || plan.specialistCopay || plan.erCopay) html += '<br>';
    if (plan.rxTiers) html += 'Rx Tiers: <strong>' + plan.rxTiers + '</strong><br>';
    if (plan.rate21) html += '<span style="font-size:11px;color:#666;">Sample Rates — Age 21: ' + plan.rate21 + ' | 40: ' + (plan.rate40||'N/A') + ' | 60: ' + (plan.rate60||'N/A') + '</span><br>';
    if (plan.hasSBC) html += '<span style="font-size:10px;color:#01696F;">✓ SBC document verified</span>';
    html += '</div>';
  });
  return html;
}


function extractEBBenefitsData(pdfTexts) {
  const benefits = {};
  if (!pdfTexts || pdfTexts.length === 0) return benefits;

  // Concatenate all page text for scanning
  let allText = '';
  pdfTexts.forEach(doc => {
    if (doc.pages && Array.isArray(doc.pages)) {
      doc.pages.forEach(p => { allText += ' ' + (p.text || ''); });
    } else if (doc.text) {
      allText += ' ' + doc.text;
    }
  });
  const txt = allText;
  const txtLower = txt.toLowerCase();

  // Helpers
  const D = '[\\d][\\d,\\.\\s]{0,15}';
  function cleanDollar(raw) {
    if (!raw || typeof raw !== 'string') return null;
    let c = raw.replace(/[\s\n\r]+/g, '').replace(/^[,.]|[,.]$/g, '');
    let prev; do { prev = c; c = c.replace(/(\d)\.(\d{3})(?=\.|,|$)/g, '$1,$2'); } while (c !== prev);
    return '$' + c;
  }
  function tryMatch(regex) {
    const m = txt.match(regex);
    return m ? m[1] : null;
  }
  function tryMatchDollar(regex) {
    const raw = tryMatch(regex);
    return raw ? cleanDollar(raw) : null;
  }

  // Benefit guides put their plan summaries in TABLES, and LlamaParse renders
  // them as markdown: the label is the first cell and the value sits several
  // columns away, often past an empty cell (the DHMO column is blank for
  // PPO-only features). A flat-text regex with a {0,30} character window
  // cannot cross that gap — which is why an Equitable dental plan reported
  // "100/?/?" with no annual maximum: Basic and Major were unreachable and the
  // 100% came from an unrelated sentence ("preventive care ... covered at
  // 100%"), not from the schedule at all.
  function tableCells(line) {
    return line.split('|').map(c => c.replace(/\*\*/g, '').replace(/<br\s*\/?>/gi, ' ').trim());
  }
  // Scan the row whose FIRST cell matches `labelRe`, then return the first
  // later cell matching `valueRe`. Empty cells and non-numeric ones ("No
  // Deductible", "Unlimited") are skipped, so the DHMO column falls away and
  // the PPO in-network value is what comes back.
  function tableRowValue(source, labelRe, valueRe) {
    const lines = (source || '').split('\n');
    for (const line of lines) {
      if (line.indexOf('|') < 0) continue;
      const cells = tableCells(line);
      if (cells.length < 3) continue;
      if (!labelRe.test(cells[1] || '')) continue;
      for (let i = 2; i < cells.length; i++) {
        const m = valueRe.exec(cells[i] || '');
        if (m) return m[1] !== undefined ? m[1] : m[0];
      }
    }
    return null;
  }
  // Text of one benefit's own section. Carrier detection against the WHOLE
  // document is how "Choose a guardian for your children" — a will-preparation
  // bullet — made Guardian the dental carrier of an Equitable plan.
  function sectionText(headingRe, chars) {
    const m = headingRe.exec(txt);
    if (!m) return '';
    return txt.slice(m.index, m.index + (chars || 6000));
  }

  // --- MEDICAL / GROUP HEALTH ---
  if (txtLower.includes('medical') || txtLower.includes('health') || txtLower.includes('epo') || txtLower.includes('ppo') || txtLower.includes('hmo') || txtLower.includes('hdhp') || txtLower.includes('hsa') || txtLower.includes('anthem') || txtLower.includes('blue cross') || txtLower.includes('blue shield') || txtLower.includes('cigna') || txtLower.includes('aetna') || txtLower.includes('united health') || txtLower.includes('kaiser') || txtLower.includes('humana')) {
    const med = {};
    // Carrier detection
    const carriers = [];
    if (txtLower.includes('anthem')) carriers.push('Anthem');
    if (txtLower.includes('blue cross') || txtLower.includes('blue shield') || txtLower.includes('bcbs')) carriers.push('BCBS');
    if (txtLower.includes('cigna')) carriers.push('Cigna');
    if (txtLower.includes('aetna')) carriers.push('Aetna');
    if (txtLower.includes('united health') || txtLower.includes('unitedhealthcare') || txtLower.includes('uhc')) carriers.push('UnitedHealthcare');
    if (txtLower.includes('kaiser')) carriers.push('Kaiser');
    if (txtLower.includes('humana')) carriers.push('Humana');
    if (carriers.length > 0) med.carrier = carriers.join(', ');

    // Plan type detection
    const planTypes = [];
    if (txtLower.includes('epo')) planTypes.push('EPO');
    if (txtLower.includes('ppo')) planTypes.push('PPO');
    if (txtLower.includes('hmo')) planTypes.push('HMO');
    if (/hdhp|high\s*deductible/i.test(txt)) planTypes.push('HDHP');
    if (txtLower.includes('hsa') || txtLower.includes('health savings')) planTypes.push('HSA');
    if (planTypes.length > 0) med.planType = planTypes.join(' / ');

    // Deductible
    let deductible = tryMatchDollar(new RegExp('(?:individual|employee|in[- ]?network)?\\s*(?:deductible|ded)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (deductible) med.deductible = deductible;

    // Coinsurance
    let coins = tryMatch(/(?:coinsurance|co[- ]?insurance|plan\s+pays)[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (coins) med.coinsurance = coins + '%';

    // Out-of-pocket max
    let oop = tryMatchDollar(new RegExp('(?:out[- ]?of[- ]?pocket|oop|max[- ]?oop)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (oop) med.oopMax = oop;

    // Monthly premium
    let prem = tryMatchDollar(new RegExp('(?:employee\\s+(?:only|cost|premium|rate)|ee\\s+(?:only|cost|rate)|monthly\\s+(?:premium|rate|cost))[^$\\n]{0,50}\\$\\s*(' + D + ')', 'i'));
    if (prem) med.premium = prem + '/mo';

    // RX copay
    let rx = tryMatchDollar(new RegExp('(?:rx|prescription|pharmacy|copay)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (rx) med.rxCopay = rx;

    // Employer contribution
    let empContrib = tryMatch(/employer\s+(?:pays|covers|contributes|contribution)[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (!empContrib && (txtLower.includes('employer pays 100%') || txtLower.includes('employer covers 100%') || txtLower.includes('employer paid') || txtLower.includes('fully paid by employer'))) {
      empContrib = '100';
    }
    if (empContrib) med.employerContribution = empContrib + '%';

    if (Object.keys(med).length > 0) benefits['Medical (Group Health)'] = med;
  }

  // --- DENTAL ---
  if (txtLower.includes('dental')) {
    const dental = {};
    // Carrier — searched ONLY inside the dental section.
    const dTxt = sectionText(/#+\s*Dental\s+Benefits|Dental\s+Plans?\s+Summary/i, 6000) || '';
    const dLower = dTxt.toLowerCase();
    const DENTAL_CARRIERS = [
      ['delta dental', 'Delta Dental'], ['united concordia', 'United Concordia'],
      ['equitable', 'Equitable'], ['solstice', 'Solstice'], ['ameritas', 'Ameritas'],
      ['metlife', 'MetLife'], ['guardian', 'Guardian'], ['cigna', 'Cigna'],
      ['aetna', 'Aetna'], ['humana', 'Humana'], ['principal', 'Principal'],
      ['lincoln', 'Lincoln Financial'], ['sun life', 'Sun Life'], ['renaissance', 'Renaissance'],
      ['unum', 'Unum'], ['mutual of omaha', 'Mutual of Omaha']
    ];
    const dCarriers = DENTAL_CARRIERS.filter(([k]) => dLower.includes(k)).map(([, name]) => name);
    if (dCarriers.length) dental.carrier = dCarriers.join(' / ');

    // Annual maximum — table first, then flat text.
    let annMax = tableRowValue(dTxt || txt, /annual|calendar\s+year\s+max|plan\s+year\s+max|benefit\s+max/i,
                               new RegExp('\\$\\s*(' + D + ')'));
    if (annMax) annMax = cleanDollar(annMax);
    if (!annMax) annMax = tryMatchDollar(new RegExp('(?:annual\\s+(?:max|maximum)|calendar\\s+year\\s+max|benefit\\s+max)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (annMax) dental.annualMax = annMax;

    // Deductible
    const dDed = tableRowValue(dTxt, /deductible/i, /(\$\s*[\d,]+(?:\s*\/\s*\$?\s*[\d,]+)?)/);
    if (dDed) dental.deductible = dDed.replace(/\s+/g, ' ');

    // Preventive / Basic / Major schedule — the table row is authoritative;
    // the loose sentence match is only a fallback.
    const PCT = /(\d{1,3})\s*%/;
    let prev  = tableRowValue(dTxt, /preventive|class\s*[i1]\b|diagnostic/i, PCT);
    let basic = tableRowValue(dTxt, /basic|class\s*(?:ii|2)\b/i, PCT);
    let major = tableRowValue(dTxt, /major|class\s*(?:iii|3)\b/i, PCT);
    if (!prev)  prev  = tryMatch(/(?:preventive|class\s*[i1])[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (!basic) basic = tryMatch(/(?:basic|class\s*(?:ii|2))[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (!major) major = tryMatch(/(?:major|class\s*(?:iii|3))[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (prev || basic || major) dental.schedule = [prev || '?', basic || '?', major || '?'].join('/');
    // Track completeness so the benchmark row cannot claim MEETS on '?' values.
    dental._complete = !!(prev && basic && major && dental.annualMax);

    // Orthodontia — a benchmark line item, and absent/limited ortho is a real
    // finding rather than something to infer from silence.
    const ortho = tableRowValue(dTxt, /orthodont/i, PCT);
    if (ortho) dental.orthodontia = ortho + '%';
    let orthoMax = tableRowValue(dTxt, /orthodont\w*\s+lifetime/i, new RegExp('\\$\\s*(' + D + ')'));
    if (orthoMax) dental.orthodontiaMax = cleanDollar(orthoMax);

    // Monthly premium
    let dprem = tryMatchDollar(new RegExp('(?:dental)[^$\\n]{0,60}(?:employee|ee|monthly|rate|premium|cost)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (!dprem) dprem = tryMatchDollar(new RegExp('(?:employee|ee|monthly|rate|premium|cost)[^$\\n]{0,40}(?:dental)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (dprem) dental.premium = dprem + '/mo';

    // Deductible
    let dded = tryMatchDollar(new RegExp('(?:dental)[^$\\n]{0,60}(?:deductible)[^$\\n]{0,30}\\$\\s*(' + D + ')', 'i'));
    if (dded) dental.deductible = dded;

    if (Object.keys(dental).length > 0) benefits['Dental'] = dental;
  }

  // --- VISION ---
  if (txtLower.includes('vision')) {
    const vision = {};
    // Carrier
    if (txtLower.includes('metlife') && txtLower.includes('vision')) vision.carrier = 'MetLife';
    else if (txtLower.includes('vsp')) vision.carrier = 'VSP';
    else if (txtLower.includes('eyemed')) vision.carrier = 'EyeMed';
    else if (txtLower.includes('davis vision')) vision.carrier = 'Davis Vision';
    else if (txtLower.includes('superior vision')) vision.carrier = 'Superior Vision';

    // Frame allowance
    let frame = tryMatchDollar(new RegExp('(?:frame|frames)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (frame) vision.frameAllowance = frame;

    // Exam copay
    let examCo = tryMatchDollar(new RegExp('(?:exam|eye\\s+exam)[^$\\n]{0,40}(?:copay|co-pay|co pay)[^$\\n]{0,20}\\$\\s*(' + D + ')', 'i'));
    if (!examCo) examCo = tryMatchDollar(new RegExp('(?:copay|co-pay)[^$\\n]{0,30}(?:exam)[^$\\n]{0,20}\\$\\s*(' + D + ')', 'i'));
    if (examCo) vision.examCopay = examCo;

    // Monthly premium
    let vprem = tryMatchDollar(new RegExp('(?:vision)[^$\\n]{0,60}(?:employee|ee|monthly|rate|premium|cost)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (!vprem) vprem = tryMatchDollar(new RegExp('(?:employee|ee|monthly|rate|premium|cost)[^$\\n]{0,40}(?:vision)[^$\\n]{0,40}\\$\\s*(' + D + ')', 'i'));
    if (vprem) vision.premium = vprem + '/mo';

    if (Object.keys(vision).length > 0) benefits['Vision'] = vision;
  }

  // --- LIFE / AD&D ---
  if (txtLower.includes('life insurance') || txtLower.includes('life/ad&d') || txtLower.includes('group life') || txtLower.includes('basic life') || txtLower.includes('ad&d')) {
    const life = {};
    // Benefit amount
    let lifeBen = tryMatchDollar(new RegExp('(?:life|ad&d|accidental\\s+death)[^$\\n]{0,60}\\$\\s*(' + D + ')', 'i'));
    if (lifeBen) life.benefit = lifeBen;
    // Salary multiple
    let salMult = tryMatch(/(?:life|ad&d)[^\d\n]{0,40}(\d\.?\d?)\s*[xX×]\s*(?:salary|annual|base)/i);
    if (salMult) life.salaryMultiple = salMult + 'x salary';
    if (Object.keys(life).length > 0) benefits['Life / AD&D'] = life;
  }

  // --- SHORT-TERM DISABILITY ---
  if (txtLower.includes('short-term disability') || txtLower.includes('short term disability') || txtLower.includes('std') || txtLower.includes('std plan')) {
    const std = {};
    let stdPct = tryMatch(/(?:short[- ]?term|std)[^\d\n]{0,40}(\d{1,3})\s*%/i);
    if (stdPct) std.benefitPct = stdPct + '% of salary';
    let stdWeek = tryMatch(/(?:short[- ]?term|std)[^\d\n]{0,40}(\d{1,3})\s*(?:week|wk)/i);
    if (stdWeek) std.benefitPeriod = stdWeek + ' weeks';
    let stdElim = tryMatch(/(?:elimination|waiting)[^\d\n]{0,30}(\d{1,3})\s*(?:day|calendar)/i);
    if (stdElim) std.eliminationPeriod = stdElim + ' days';
    if (Object.keys(std).length > 0) benefits['Short-Term Disability'] = std;
  }

  // --- LONG-TERM DISABILITY ---
  if (txtLower.includes('long-term disability') || txtLower.includes('long term disability') || txtLower.includes('ltd')) {
    const ltd = {};
    let ltdPct = tryMatch(/(?:long[- ]?term|ltd)[^\d\n]{0,40}(\d{1,3})\s*%/i);
    if (ltdPct) ltd.benefitPct = ltdPct + '% of salary';
    let ltdMax = tryMatchDollar(new RegExp('(?:long[- ]?term|ltd)[^$\\n]{0,60}(?:max|maximum|up\\s+to)\\s*\\$\\s*(' + D + ')', 'i'));
    if (ltdMax) ltd.monthlyMax = ltdMax + '/mo max';
    let ltdElim = tryMatch(/(?:long[- ]?term|ltd)[^\d\n]{0,40}(?:elimination|waiting)[^\d\n]{0,20}(\d{1,3})\s*(?:day|calendar)/i);
    if (ltdElim) ltd.eliminationPeriod = ltdElim + ' days';
    if (Object.keys(ltd).length > 0) benefits['Long-Term Disability'] = ltd;
  }

  /* A WORD IS NOT A PLAN.
   *
   * The Jackson Hole Classical Academy guide offers no retirement plan and no
   * HSA or FSA, yet "retirement" appears in LTD's "until you reach Social
   * Security Normal Retirement Age" and "401(k)" / "HSA" in the ID-theft
   * plan's "401(k), HSA and Investment Account Activity Alerts". The triggers
   * below fired on those, and Section 6 printed "— 401(k)" as if a plan had
   * been found. Test the triggers on the text with those phrases taken out. */
  const planLower = txtLower
    .replace(/(?:social security\s+)?(?:normal\s+)?retirement age/g, ' ')
    .replace(/401\(k\),?\s+hsa,?\s+and\s+investment account\s+activity alerts/g, ' ');

  // --- 401(k) / RETIREMENT ---
  if (planLower.includes('401(k)') || planLower.includes('401k') || planLower.includes('retirement') || planLower.includes('403(b)') || planLower.includes('pension') || planLower.includes('profit sharing')) {
    const ret = {};
    let matchPct = tryMatch(/(?:match|employer\s+match|company\s+match)[^\d\n]{0,30}(\d{1,3})\s*%/i);
    if (matchPct) ret.employerMatch = matchPct + '%';
    let vestSch = tryMatch(/(?:vest|vesting)[^\n]{0,50}(immediate|\d+[- ]?year|cliff|graded)/i);
    if (vestSch) ret.vesting = vestSch;
    // Plan type
    if (planLower.includes('403(b)')) ret.planType = '403(b)';
    else if (planLower.includes('401(k)') || planLower.includes('401k')) ret.planType = '401(k)';
    if (Object.keys(ret).length > 0) benefits['401(k) / Retirement'] = ret;
  }

  // --- HSA / FSA ---
  if (planLower.includes('hsa') || planLower.includes('health savings') || planLower.includes('fsa') || planLower.includes('flexible spending')) {
    const hsa = {};
    let seed = tryMatchDollar(new RegExp('(?:employer|company|seed|contribution)[^$\\n]{0,40}(?:hsa)[^$\\n]{0,30}\\$\\s*(' + D + ')', 'i'));
    if (!seed) seed = tryMatchDollar(new RegExp('(?:hsa)[^$\\n]{0,40}(?:employer|seed|contribution)[^$\\n]{0,30}\\$\\s*(' + D + ')', 'i'));
    if (seed) hsa.employerSeed = seed;
    if (planLower.includes('fsa') || planLower.includes('flexible spending')) hsa.fsaAvailable = 'Yes';
    if (planLower.includes('dcfsa') || planLower.includes('dependent care')) hsa.dcfsaAvailable = 'Yes';
    if (Object.keys(hsa).length > 0) benefits['HSA / FSA'] = hsa;
  }

  return benefits;
}

// Format extracted EB benefits data into display string for benchmarking table
function formatEBBenefitsDisplay(benefitObj) {
  if (!benefitObj || Object.keys(benefitObj).length === 0) return null;
  // v2.17: Card format matching renewal plan display
  let html = '<div style="padding:6px 8px;background:#f0f7f7;border-radius:4px;border-left:3px solid #01696F;">';
  if (benefitObj.carrier) html += '<strong>' + benefitObj.carrier + '</strong>';
  if (benefitObj.planType) html += ' — ' + benefitObj.planType;
  if (benefitObj.carrier || benefitObj.planType) html += '<br>';
  if (benefitObj.deductible) html += 'Deductible: <strong>' + benefitObj.deductible + '</strong><br>';
  if (benefitObj.coinsurance) html += 'Coinsurance: <strong>' + benefitObj.coinsurance + '</strong><br>';
  if (benefitObj.oopMax) html += 'OOP Max: <strong>' + benefitObj.oopMax + '</strong><br>';
  if (benefitObj.copay) html += 'Copay: <strong>' + benefitObj.copay + '</strong><br>';
  if (benefitObj.rxCopay) html += 'Rx: <strong>' + benefitObj.rxCopay + '</strong><br>';
  if (benefitObj.annualMax) html += 'Annual Max: <strong>' + benefitObj.annualMax + '</strong><br>';
  if (benefitObj.schedule) html += 'Schedule: <strong>' + benefitObj.schedule + '</strong><br>';
  if (benefitObj.benefit) html += 'Benefit: <strong>' + benefitObj.benefit + '</strong><br>';
  if (benefitObj.salary) html += '<strong>' + benefitObj.salary + '</strong><br>';
  if (benefitObj.match) html += 'Match: <strong>' + benefitObj.match + '</strong><br>';
  // Fallback: show any remaining keys not yet displayed
  const shown = ['carrier','planType','deductible','coinsurance','oopMax','copay','rxCopay','annualMax','schedule','benefit','salary','match'];
  Object.keys(benefitObj).forEach(k => {
    if (!shown.includes(k) && benefitObj[k]) {
      html += k.charAt(0).toUpperCase() + k.slice(1) + ': <strong>' + benefitObj[k] + '</strong><br>';
    }
  });
  html += '</div>';
  return html;
}


// ===== EB PLAN TABLES (medical / dental / vision) =====
// A benefit guide states its plans as TABLES with one column per plan. The
// older field-by-field regex layer collapsed that into a single flat object,
// which cannot represent a two-plan handbook: the Cigna guide's medical
// deductible came back "$25" (real: $5,000 Base / $3,000 Buy-Up), it invented a
// "$10/mo" premium from a copay cell in a guide that quotes no premiums at all,
// vision extracted nothing, and dental reported the Low Plan's 100/25/25 as if
// it were the whole program — which made ebStatus() assert BELOW STANDARD off a
// plan the employee may not even be on.
//
// These parsers read the table as a table: plan columns stay separate, every
// row of the source keeps its own output field, and a value that is not in the
// document stays absent rather than being pattern-matched out of a neighbour.

// Split one markdown table row into cells (leading/trailing pipes dropped).
function ebTableCells(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(c =>
    c.replace(/<br\s*\/?>/gi, ' ')
     .replace(/\*\*/g, '')
     .replace(/\\([*_])/g, '$1')
     .replace(/^\s*\*(.*)\*\s*$/, '$1')
     .replace(/<\/?u>/gi, '')
     .replace(/\s+/g, ' ')
     .trim());
}

const EB_TABLE_DIVIDER = /^\s*\|?\s*:?-{2,}/;

// The first markdown table that follows `headingRe`. Returns {headers, rows}
// with rows as raw cell arrays, or null when the heading or table is absent.
/* LlamaParse emits some tables as HTML rather than markdown pipes.
 *
 * On the Arizona Autism benefit guide it returned "## Medical Plans Summary
 * <table><thead><tr><th>Cigna Medical Plans</th>..." while Dental, Vision and
 * Life came back as pipe tables in the same document. The pipe walker below
 * found no '|' before the next heading and returned null, so Medical alone fell
 * through to the scalar regex parser and the report showed a $25 deductible
 * read out of a copay line.
 *
 * Converted to the same {headers, rows} shape the pipe parser produces, so
 * everything downstream is unchanged. Tags are matched rather than parsed with
 * a DOM: this runs over extractor output, not over a page, and the shapes
 * LlamaParse emits are narrow.
 */
function ebHtmlTableAfter(txt, headingRe) {
  const m = headingRe.exec(txt || '');
  if (!m) return null;
  const after = txt.slice(m.index);
  // Only the table belonging to THIS heading: stop at the next one.
  const nextHeading = after.slice(1).search(/\n#{1,6}\s/);
  const scope = nextHeading > 0 ? after.slice(0, nextHeading + 1) : after;
  const tm = /<table[\s\S]*?<\/table>/i.exec(scope);
  if (!tm) return null;
  const cell = c => c.replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ').trim();
  const trs = tm[0].match(/<tr[\s\S]*?<\/tr>/gi) || [];
  const parsed = trs.map(tr => (tr.match(/<t[hd][\s\S]*?<\/t[hd]>/gi) || []).map(cell));
  const nonEmpty = parsed.filter(r => r.some(c => c !== ''));
  if (nonEmpty.length < 2) return null;
  return { headers: nonEmpty[0], rows: nonEmpty.slice(1) };
}

function ebParseTableAfter(txt, headingRe) {
  const m = headingRe.exec(txt || '');
  if (!m) return null;
  const lines = txt.slice(m.index).split('\n');
  let i = 0;
  // Walk to the first pipe row (skip prose between heading and table).
  while (i < lines.length && lines[i].indexOf('|') < 0) {
    // A second heading before any table means this section has no PIPE table.
    // It may still have an HTML one, which is a different shape, not an absence.
    if (i > 0 && /^#{1,6}\s/.test(lines[i])) return ebHtmlTableAfter(txt, headingRe);
    i++;
  }
  if (i >= lines.length) return ebHtmlTableAfter(txt, headingRe);
  const headers = ebTableCells(lines[i]);
  i++;
  if (i < lines.length && EB_TABLE_DIVIDER.test(lines[i])) i++;
  const rows = [];
  for (; i < lines.length; i++) {
    if (lines[i].indexOf('|') < 0) break;      // table ends at the first non-pipe line
    const cells = ebTableCells(lines[i]);
    if (EB_TABLE_DIVIDER.test(lines[i])) continue;
    if (cells.some(c => c !== '')) rows.push(cells);
  }
  if (rows.length) return { headers, rows };
  // No pipe table under this heading — the same section may be HTML.
  return ebHtmlTableAfter(txt, headingRe);
}

/* THE TABLE THAT STATES THE BENEFITS, NOT THE FIRST TABLE ON THE PAGE.
 *
 * ebParseTableAfter walks to the first pipe row under a heading and reads that
 * table. Under "## Dental Plans Summary" the Arlington Classics guide prints
 * two: a plan-description table --
 *
 *     | Plan     | Plan Features                                          |
 *     | **DHMO** | Provides benefits only if you see an in-network dentist |
 *
 * -- and, below it, the one carrying the figures: Calendar Year Deductible
 * $50/$100, Orthodontics Lifetime Maximum $1,000, Annual Calendar Year Maximum
 * $1,200. The first won, so the dental block came back holding two rows of
 * prose and no limit at all. Medical escaped only because its limits table
 * happens to come first.
 *
 * A benefits table is the one whose cells carry money, percentages or a
 * deductible-style pair. That is what the section is read for, and it does not
 * depend on which table a designer put first.
 */
const _EB_BENEFIT_CELL = /\$\s?[\d,]+|\d+\s?%|\bunlimited\b|\bno deductible\b|\bcovered (?:in full|100%)\b/i;

/* A RATE SCHEDULE IS NOT A BENEFIT TABLE.
 *
 * Counting benefit-looking cells favours the wrong table wherever a section
 * prints its premium rates beside its benefits. The Voluntary Short-Term
 * Disability section states the benefit once --
 *
 *     | Benefits Paid | You receive 60% of your pay, up to a maximum
 *                       benefit of $1,500 per week. |
 *
 * -- three money cells, against an age-banded rate table below it holding
 * thirty-three: $0.375 under 25, $0.428 at 25-29, down the page to 70+. The
 * same shape cost the vision block its table when the section bound was wide
 * enough to reach the life rates.
 *
 * A rate schedule announces itself twice: in a header that says what the rate
 * is per, and in row labels that are age bands rather than benefit terms.
 * Either is enough. */
const _EB_RATE_HEADER = /\brate\b|\bper\s*\$\s?[\d,]+\b|\bper\s+\$\d+\s+of\b|\bcost\s+per\b/i;
const _EB_AGE_BAND = /^\s*(?:under\s*\d{2}|\d{2}\s*-\s*\d{2}|\d{2}\s*\+|over\s*\d{2})\s*$/i;

function ebIsRateTable(table) {
  if (!table) return false;
  if ((table.headers || []).some(h => _EB_RATE_HEADER.test(String(h || '')))) return true;
  const rows = table.rows || [];
  if (!rows.length) return false;
  const banded = rows.filter(r => _EB_AGE_BAND.test(String((r || [])[0] || ''))).length;
  return banded >= 3 && banded >= rows.length / 2;
}

function ebBenefitScore(table) {
  if (!table || !table.rows || !table.rows.length) return 0;
  if (ebIsRateTable(table)) return 0;
  let n = 0;
  for (const r of table.rows) for (const c of (r || [])) if (_EB_BENEFIT_CELL.test(String(c || ''))) n++;
  return n;
}

/* Every pipe table belonging to this heading, best first.
 *
 * The section ends at the next heading, not after a fixed number of
 * characters. A 6000-character window let the vision fallback run past the
 * vision table and into the life-insurance age-rate schedule -- fourteen rows
 * of dollar amounts, $0.045 at age 0-29 -- which outscored the real table on
 * count and was reported as the vision benefit. A rate table is a benefit
 * table by every test except the one that matters: whose section it is in. */
/* AN HTML TABLE, WITH ITS GROUPED HEADER RESOLVED.
 *
 * LlamaParse does not always return pipe tables. On the 2025-26 Arlington
 * Classics guide it returned both dental tables as HTML, and the figures table
 * has a two-row header:
 *
 *     <th>Key Features</th><th colspan="2">DHMO Plan</th><th colspan="2">PPO Plan</th>
 *     <th></th><th>In-Network Only</th><th>In-Network</th><th>Out-of-Network</th>
 *
 * DHMO's colspan is wrong — it spans one column, not two — so expanding the
 * spans misaligns every figure. The sub-header row is the one that matches
 * the data. Each group owns sub-columns until the next one starts over at
 * "In-Network": DHMO = In-Network Only; PPO = In-Network, Out-of-Network.
 * Columns that are empty in every data row (PPO Out-of-Network here) are
 * dropped, so they do not turn into a phantom plan. */
function ebHtmlTablesIn(span) {
  const out = [];
  const cellText = c => c.replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ').trim();
  const re = /<table[\s\S]*?<\/table>/gi;
  let tm;
  while ((tm = re.exec(String(span || ''))) !== null) {
    const trs = tm[0].match(/<tr[\s\S]*?<\/tr>/gi) || [];
    const parsed = trs.map(tr => {
      const cells = tr.match(/<t[hd][^>]*>[\s\S]*?<\/t[hd]>/gi) || [];
      return {
        th: cells.length > 0 && cells.every(c => /^<th/i.test(c)),
        cells: cells.map(c => ({ text: cellText(c), span: parseInt((/colspan\s*=\s*"?(\d+)/i.exec(c) || [0, 1])[1], 10) || 1 })),
        bold: cells.length > 0 && /<(?:b|strong)\b/i.test(cells[0])
      };
    }).filter(r => r.cells.some(c => c.text !== ''));
    if (parsed.length < 2) continue;
    let headers = parsed[0].cells.map(c => c.text);
    let body = parsed.slice(1);
    const grouped = parsed[0].cells.some(c => c.span > 1);
    if (grouped && parsed[1] && parsed[1].th) {
      const groups = parsed[0].cells.slice(1).map(c => c.text);
      const subs = parsed[1].cells.slice(1).map(c => c.text);
      const named = [];
      const groupOf = [];
      let g = -1, used = 0;
      subs.forEach(sub => {
        if (g < 0 || (used > 0 && /^in[\s\-]?network/i.test(sub))) { g++; used = 0; }
        used++;
        named.push(((groups[g] || '') + ' ' + sub).trim());
        groupOf.push(g);
      });
      /* A SUB-COLUMN THE VALUES SPAN. On the Arlington Classics page every
       * PPO figure is one cell across In-Network AND Out-of-Network: the PPO
       * pays the same either way. LlamaParse drops the colspan and leaves the
       * Out-of-Network column empty in every row. A plan does not print a blank
       * column, so an always-empty sub-column is read as spanned, and the
       * column it belongs to is named for both. */
      const dataRows = parsed.slice(2).map(r => r.cells.map(c => c.text));
      const emptyCol = i => dataRows.every(r => !(r[i + 1] || ''));
      named.forEach((n, i) => {
        if (emptyCol(i)) return;
        const spanned = [];
        for (let j = i + 1; j < named.length && groupOf[j] === groupOf[i] && emptyCol(j); j++) spanned.push(subs[j]);
        if (spanned.length) named[i] = n + ' / ' + spanned.join(' / ');
      });
      headers = [parsed[0].cells[0].text || parsed[1].cells[0].text].concat(named);
      body = parsed.slice(2);
    }
    let rows = body.map(r => r.cells.map(c => c.text));
    const bold = body.map(r => !!r.bold);
    /* A CELL THAT SPANS ROWS. The Arlington Classics DHMO column prints one
     * cell — "Benefits based on fee schedule. Full schedule available on
     * Employee Navigator" — down five rows, Preventive through Orthodontics
     * Lifetime Maximum. LlamaParse drops the rowspan and scatters the sentence:
     * Basic gets its first half, Major its second, and Preventive and both
     * orthodontics rows come back blank, which reads as "not covered".
     *
     * In a column, a run of blanks and two or more figure-free fragments,
     * between two filled cells, is one spanned cell: rejoin it and give every
     * row in the run the whole text. A single fragment, or any figure, is left
     * alone — "Not covered" beside a blank is two different answers. */
    for (let c = 1; c < headers.length; c++) {
      let r = 0;
      while (r < rows.length) {
        if (!(rows[r][c] || '')) { r++; continue; }
        // rows[r] is a filled anchor. Read: blanks*, fragments+, blanks*,
        // and the next filled cell after that closes the run.
        let k = r + 1, phase = 0;
        const run = [];
        while (k < rows.length) {
          const x = rows[k][c] || '';
          if (!x) { if (phase === 1) phase = 2; run.push(k); k++; continue; }
          if (phase === 2 || /\d/.test(x)) break;       // the closing anchor, or a figure
          phase = 1; run.push(k); k++;
        }
        const frags = run.filter(j => rows[j][c]);
        const blanks = run.length - frags.length;
        if (k < rows.length && frags.length >= 2 && blanks >= 1) {
          const joined = frags.map(j => rows[j][c]).join(' ').replace(/\s+/g, ' ').trim();
          run.forEach(j => { rows[j][c] = joined; });
        }
        r = Math.max(k, r + 1);
      }
    }
    // Drop columns no data row fills.
    const width = headers.length;
    const keep = headers.map((_, i) => i === 0 || rows.some(r => (r[i] || '') !== ''));
    if (keep.some(k => !k)) {
      headers = headers.filter((_, i) => keep[i]);
      rows = rows.map(r => r.filter((_, i) => i >= width || keep[i]));
    }
    if (rows.length) out.push({ headers, rows, bold });
  }
  return out;
}

// Whether a pipe row's first cell is bold: LlamaParse bolds a table's group
// headers ("**Deductible**", "**Out-of-Pocket Maximum**") and not the rows
// beneath them ("Individual", "Facility", "Generic").
function ebFirstCellBold(line) {
  const first = String(line || '').trim().replace(/^\|/, '').split('|')[0] || '';
  return /\*\*[^*]+\*\*|<\/?(?:b|strong)\b/i.test(first);
}

function ebBestTableAfter(txt, headingRe) {
  const t = String(txt || '');
  const m = headingRe.exec(t);
  if (!m) return null;
  const afterHeading = t.indexOf('\n', m.index);
  const rest = afterHeading < 0 ? '' : t.slice(afterHeading);
  const nextHeading = rest.search(/\n#{1,2}\s/);
  const span = nextHeading < 0 ? t.slice(m.index)
                               : t.slice(m.index, afterHeading + nextHeading);
  const lines = span.split('\n');
  const best = { table: null, score: -1 };
  let i = 0;
  while (i < lines.length) {
    if (lines[i].indexOf('|') < 0) { i++; continue; }
    const start = i;
    while (i < lines.length && lines[i].indexOf('|') >= 0) i++;
    const block = lines.slice(start, i);
    const headers = ebTableCells(block[0]);
    const rows = [];
    const bold = [];
    for (let k = 1; k < block.length; k++) {
      if (EB_TABLE_DIVIDER.test(block[k])) continue;
      const cells = ebTableCells(block[k]);
      if (cells.some(c => c !== '')) { rows.push(cells); bold.push(ebFirstCellBold(block[k])); }
    }
    if (rows.length) {
      const cand = { headers, rows, bold };
      const sc = ebBenefitScore(cand);
      // Strictly greater keeps the first table on a tie, which is the old
      // behaviour wherever this makes no difference.
      if (sc > best.score) { best.table = cand; best.score = sc; }
    }
  }
  // HTML tables in the same section are candidates on the same terms.
  ebHtmlTablesIn(span).forEach(cand => {
    const sc = ebBenefitScore(cand);
    if (sc > best.score) { best.table = cand; best.score = sc; }
  });
  return best.score > 0 ? best.table : null;
}

// Concatenated text of every uploaded document.
function ebAllText(pdfTexts) {
  let all = '';
  (pdfTexts || []).forEach(doc => {
    if (doc.pages && Array.isArray(doc.pages)) doc.pages.forEach(p => { all += '\n' + (p.text || ''); });
    else if (doc.text) all += '\n' + doc.text;
  });
  return all;
}

// Carrier named in this benefit's OWN section — never the whole document, so a
// will-preparation bullet cannot make Guardian the dental carrier.
const EB_CARRIERS = [
  ['cigna','Cigna'], ['delta dental','Delta Dental'], ['united concordia','United Concordia'],
  ['equitable','Equitable'], ['solstice','Solstice'], ['ameritas','Ameritas'],
  ['metlife','MetLife'], ['guardian','Guardian'], ['aetna','Aetna'], ['humana','Humana'],
  ['principal','Principal'], ['lincoln','Lincoln Financial'], ['sun life','Sun Life'],
  ['renaissance','Renaissance'], ['unum','Unum'], ['mutual of omaha','Mutual of Omaha'],
  ['anthem','Anthem'], ['blue cross','BCBS'], ['blue shield','BCBS'], ['kaiser','Kaiser'],
  ['unitedhealthcare','UnitedHealthcare'], ['united health','UnitedHealthcare'],
  ['symetra','Symetra'], ['standard insurance','The Standard'], ['reliance standard','Reliance Standard'],
  ['vsp','VSP'], ['eyemed','EyeMed'], ['davis vision','Davis Vision'], ['superior vision','Superior Vision']
];
function ebCarrierIn(sectionTxt) {
  const s = (sectionTxt || '').toLowerCase();
  const hits = [];
  EB_CARRIERS.forEach(([k, name]) => { if (s.includes(k) && hits.indexOf(name) < 0) hits.push(name); });
  return hits.length ? hits.join(' / ') : null;
}

// Text from a heading up to the next heading of the same-or-higher level.
/* Bounded by the next top-level heading as well as the cap. Four thousand
 * characters from "# Dental Benefits" ran onto the Vision page — "in
 * partnership with the VSP network" — and the dental row named VSP as a
 * dental carrier. */
function ebSection(txt, headingRe, cap) {
  const m = headingRe.exec(txt || '');
  if (!m) return '';
  const s = txt.slice(m.index, m.index + (cap || 4000));
  const next = s.slice(1).search(/\n#\s/);
  return next >= 0 ? s.slice(0, next + 1) : s;
}

// Rows like "Individual" / "Family" / "Tier 1" mean nothing on their own — they
// belong to the group row above them ("Deductible", "Out-of-Pocket Maximum",
// "RETAIL PRESCRIPTIONS"). Only those generic sub-labels inherit a group; every
// other row stands alone and becomes the new group for whatever follows.
//
// Boldness cannot be used for this: the medical table bolds its group headers
// ("**Deductible**") while the Rx table bolds the opposite ("**Tier 1**" under a
// plain "RETAIL PRESCRIPTIONS"). And a group header may still carry text in its
// value cells — "Out-of-Pocket Maximum" holds the note "Includes deductible,
// coinsurance, copays, and Rx copays" — so an empty-value test misses it and
// both of its Individual/Family rows land under "Deductible" instead.
const EB_SUB_LABEL = /^(?:individual|family|single|spouse|child(?:ren)?|member|employee(?:\s*\+\s*(?:spouse|child(?:ren)?|family|1))?|ee(?:\s*\+\s*(?:sp|ch|f|1))?|two[- ]party|tier\s*\d+|in[- ]network|out[- ]of[- ]network|ind(?:ividual)?\s*\+\s*(?:child(?:ren)?|spouse)(?:\s+or\s+(?:child(?:ren)?|spouse))?)$/i;

function ebLabelledRows(table) {
  if (!table) return [];
  const out = [];
  let group = null;
  // Set when the current group came from a PURE header — a row with nothing in
  // any value cell, as "Deductible", "Hospitalization" or "Prescription Drugs"
  // print in the Friendship guide. Every row beneath such a header belongs to
  // it until the next one: "Prescription Drugs — Deductible: None" is not the
  // medical deductible.
  let pure = false;
  const bold = Array.isArray(table.bold) ? table.bold : null;
  table.rows.forEach((cells, ri) => {
    const label = cells[0] || '';
    const values = cells.slice(1);
    // A bold label is a group header of its own; only a plain row can belong
    // to the pure header above it. Without bold information, nothing inherits
    // beyond the named sub-labels — the behaviour every earlier guide relied on.
    const childOfPure = pure && bold && bold[ri] === false;
    if (!label) {
      // A value row with no label of its own, under a pure header: the
      // header's values. "Physician Visit" with its copays on the next line.
      if (group && pure && values.some(v => v !== '')) {
        out.push({ label: group, group: null, display: group, values: values });
      }
      return;
    }
    if (EB_SUB_LABEL.test(label) || (childOfPure && values.some(v => v !== ''))) {
      out.push({ label: label, group: group, display: group ? group + ' \u2014 ' + label : label, values: values });
      return;
    }
    // Any other row becomes the group for the sub-labels beneath it.
    group = label;
    // A row with nothing in its value cells is a pure header, not a data row.
    pure = values.every(v => v === '');
    if (pure) return;
    out.push({ label: label, group: null, display: label, values: values });
  });
  return out;
}

// Plan column names, taken from the table header (first cell is the label
// column). "Low Plan In-Network" keeps its network qualifier.
function ebPlanNames(table) {
  if (!table) return [];
  return table.headers.slice(1).filter(h => h !== '');
}

// One benefit's table, normalised: the label column plus one column per plan.
function ebBuildBlock(txt, headingRe, sectionRe, find) {
  /* A section may not carry the summary heading at all.
   *
   * LlamaParse wrote "# Vision Benefits" for the Arlington Classics guide and
   * no "Vision Plans Summary" under it -- the source page overlays that
   * heading with the table's first row, so there is nothing to recognise. The
   * limits table is present and unremarkable; only its label is missing, and
   * the whole vision block was dropped for it.
   *
   * So fall back to the benefit's own section heading. It is the same section
   * either way, and the table choice below decides which table to read. */
  let table = ebBestTableAfter(txt, headingRe) || ebParseTableAfter(txt, headingRe);
  if (!table && sectionRe) {
    table = ebBestTableAfter(txt, sectionRe) || ebParseTableAfter(txt, sectionRe);
  }
  let foundRe = null;
  if (!table && find) {
    const hit = ebFindBenefitTable(txt, find);
    if (hit) { table = hit.table; foundRe = hit.headingRe; }
  }
  if (!table) return null;
  return {
    carrier: ebCarrierIn(ebSection(txt, foundRe || sectionRe || headingRe, 4000)),
    columns: ebPlanNames(table),
    rows: ebLabelledRows(table)
  };
}

/* THE SECTION IS FOUND BY WHAT IT IS ABOUT, NOT BY ITS EXACT TITLE.
 *
 * The block readers above know the headings of the guides they were written
 * against — "Medical Plans Summary", "Dental Benefits", "Vision Plans Summary".
 * Friendship Public Charter School's guide says "# Medical & Prescription
 * Drugs", "# Dental" and "# Vision"; none matched, every block came back empty,
 * and Section 6 fell through to the flat keyword layer, which printed the $15
 * generic-drug copay as the medical deductible.
 *
 * So, failing the known titles, try every heading that names the benefit and
 * take the first whose best table actually carries that benefit's rows. A
 * heading that names the benefit for some other reason — the glossary, the
 * per-pay cost table, "Medical Transportation" — is skipped by `exclude`, and
 * a table without the benefit's rows is skipped by `vocab`. */
function ebFindBenefitTable(txt, find) {
  const t = String(txt || '');
  const re = /^#{1,4}[ \t]*(.+)$/gm;
  let m;
  while ((m = re.exec(t)) !== null) {
    const title = m[1].replace(/[*_]/g, '').trim();
    if (!find.key.test(title) || (find.exclude && find.exclude.test(title))) continue;
    const headingRe = new RegExp('^' + m[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[ \\t]*$', 'm');
    const table = ebBestTableAfter(t, headingRe);
    if (table && (table.rows || []).some(r => find.vocab.test(String((r || [])[0] || '')))) return { table, headingRe };
  }
  return null;
}

// Value of the first row whose label (or group — label) matches.
function ebRowValues(block, labelRe) {
  if (!block) return null;
  for (const r of block.rows) {
    if (labelRe.test(r.display) || labelRe.test(r.label)) return r.values;
  }
  return null;
}

/* ===== EB TABLE INDEX: EVERY TABLE, READ ONCE, CLASSIFIED BY CONTENT =====
 *
 * The benefit readers used to FIND their table by its title: a heading regex
 * per benefit ("Medical Plans Summary", "# Dental Benefits", "# Disability
 * Insurance"), a second regex when a guide titled it differently, a third for
 * the next guide, and a keyword layer underneath for when none matched. Every
 * new guide added a heading. Friendship's "# Disability Income Benefits" table
 * was found only by the last of three disability fallbacks, its LTD column
 * never reached the table bridge (the row said "Detected", not "Extracted"),
 * and the Short-Term Disability Buy-Up table beneath it was not read at all.
 *
 * A guide's tables say what they are in their own cells. This reads every
 * table in every document once — markdown pipe tables and HTML tables alike,
 * a table broken across a page rejoined — and classifies each by its row
 * labels and column headers against a vocabulary per benefit. The heading is
 * kept as context and as a tiebreak, never as the gate. Readers then take
 * their benefit's table from the index, and the old heading paths remain only
 * as a fallback for a guide whose tables classify as nothing.
 *
 * Each entry keeps its provenance — file, page, heading — so a figure in the
 * report can be traced to the cell it came from. */
const EB_TABLE_KINDS = [
  { kind: 'medical',    rows: /deductible|out[\s\-]of[\s\-]pocket|co-?pay|coinsurance|office\s+visit|physician|primary\s+care|specialist|emergency|urgent\s+care|hospital|inpatient|outpatient/i,
                        head: /\bmedical\b|\bhealth\s+plan|\b(?:hmo|ppo|pos|epo|hdhp)\b/i },
  { kind: 'rx',         rows: /\bgeneric\b|\bbrand\b|formulary|\bpreferred\b|specialty|tier\s*\d|\bretail\b|mail[\s\-]?order/i,
                        head: /prescription|pharmacy|\brx\b/i },
  { kind: 'dental',     rows: /preventive|\bbasic\b|\bmajor\b|orthodon|annual\s+(?:calendar\s+year\s+)?max|cleaning|endodont|periodont/i,
                        head: /dental/i },
  { kind: 'vision',     rows: /\bexams?\b|\bframes?\b|\blens(?:es)?\b|contacts?\b/i,
                        head: /vision|\beye\b/i },
  { kind: 'life',       rows: /\blife\b|ad&d|accidental\s+death|\bsalary\b|guarantee[d]?\s+issue|age\s+reduction|evidence\s+of\s+insurability/i,
                        head: /\blife\b/i },
  { kind: 'disability', rows: /benefits?\s+begin|elimination|benefits?\s+(?:payable|end|paid)|percentage\s+of|income\s+re-?\s*placed|maximum\s+(?:weekly\s+|monthly\s+)?benefit|benefit\s+(?:duration|period|amount)|who\s+pays|pre[\s\-]?existing/i,
                        head: /disabilit|income\s+protection|\bSTD\b|\bLTD\b/i }
];

// Score one table against every kind; the best kind wins if it has evidence
// in the table itself (two row labels, or one plus the header or heading).
/* A CONTRIBUTION TABLE IS NOT A BENEFIT TABLE. Columns by coverage tier
 * ("Employee Only | Employee + Spouse | Employee + Child(ren) | Family") are
 * what each plan costs per pay period. Arlington's lists every plan in the
 * program, and its two "Hospital Indemnity" rows read as medical. */
const _EB_TIER_HEADER = /^(?:employee|ee|member|you)(?:\s*(?:only|\+|&|and)\s*(?:spouse|sp|child(?:ren)?|ch|family|fam|one|1|dependents?)?)*$|^(?:family|employee\s*\+\s*family)$/i;
function ebIsContributionTable(t) {
  const h = (t.headers || []).slice(1).map(x => String(x || '').replace(/[*_]/g, '').trim()).filter(Boolean);
  return h.length >= 2 && h.filter(x => _EB_TIER_HEADER.test(x)).length >= Math.max(2, Math.ceil(h.length * 0.6));
}

function ebClassifyTable(t) {
  if (ebIsRateTable(t) || ebIsContributionTable(t)) return { kind: 'rates', score: 0 };
  const labels = (t.rows || []).map(r => String((r || [])[0] || '').trim()).filter(Boolean);
  const headerTxt = (t.headers || []).join(' | ');
  const headTxt = (t.headings || []).slice(-2).join(' | ');
  let best = { kind: null, score: 0 };
  EB_TABLE_KINDS.forEach(k => {
    const hits = new Set(labels.filter(l => k.rows.test(l)).map(l => l.toLowerCase())).size;
    const inHeader = k.head.test(headerTxt) ? 1 : 0;
    const inHeading = k.head.test(headTxt) ? 1 : 0;
    if (!(hits >= 2 || (hits >= 1 && (inHeader || inHeading)))) return;
    const score = hits * 2 + inHeader * 3 + inHeading * 2;
    if (score > best.score) best = { kind: k.kind, score };
  });
  return best;
}

const _ebIndexCache = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;

function ebTableIndex(pdfTexts) {
  if (_ebIndexCache && pdfTexts && typeof pdfTexts === 'object' && _ebIndexCache.has(pdfTexts)) return _ebIndexCache.get(pdfTexts);
  const out = [];
  (pdfTexts || []).forEach(doc => {
    const pages = (doc.pages && doc.pages.length) ? doc.pages : [{ page: 1, text: doc.text || '' }];
    let full = '';
    const starts = [];
    pages.forEach(p => { starts.push({ at: full.length, page: p.page, printed: p.printedPage || null }); full += String(p.text || '') + '\n\n'; });
    const pageAt = at => { let r = starts[0]; for (const s of starts) { if (s.at <= at) r = s; else break; } return r || { page: 1 }; };
    const heads = [];
    const hre = /^(#{1,6})[ \t]*(.+)$/gm;
    let hm;
    while ((hm = hre.exec(full)) !== null) heads.push({ at: hm.index, level: hm[1].length, title: hm[2].replace(/[*_]/g, '').trim() });
    const contextAt = at => {
      const chain = [];
      let near = null;
      heads.forEach(h => {
        if (h.at >= at) return;
        while (chain.length && chain[chain.length - 1].level >= h.level) chain.pop();
        chain.push(h);
        near = h;
      });
      // The section a table sits in: from its top-level heading to the next
      // heading at that level or above. The carrier is read from here.
      const top = chain[0] || null;
      let end = full.length;
      if (top) { const nx = heads.find(h => h.at > at && h.level <= top.level); if (nx) end = nx.at; }
      return { headings: chain.map(h => h.title), heading: near ? near.title : '', sectionText: full.slice(top ? top.at : Math.max(0, at - 3000), end) };
    };

    const found = [];
    // Pipe tables.
    const lines = full.split('\n');
    let off = 0, i = 0;
    const lineAt = [];
    lines.forEach(l => { lineAt.push(off); off += l.length + 1; });
    while (i < lines.length) {
      if (lines[i].indexOf('|') < 0 || /<t[dh]\b/i.test(lines[i])) { i++; continue; }
      const start = i;
      while (i < lines.length && lines[i].indexOf('|') >= 0) i++;
      const block = lines.slice(start, i);
      if (block.length < 2) continue;
      const headers = ebTableCells(block[0]);
      const hasDivider = EB_TABLE_DIVIDER.test(block[1] || '');
      const rows = [], bold = [];
      for (let k = 1; k < block.length; k++) {
        if (EB_TABLE_DIVIDER.test(block[k])) continue;
        const cells = ebTableCells(block[k]);
        if (cells.some(c => c !== '')) { rows.push(cells); bold.push(ebFirstCellBold(block[k])); }
      }
      if (!rows.length) continue;
      found.push({ at: lineAt[start], end: lineAt[i - 1] + lines[i - 1].length, headers, rows, bold, hasDivider, firstBold: ebFirstCellBold(block[0]) });
    }
    // HTML tables, one at a time so each keeps its position.
    const tre = /<table[\s\S]*?<\/table>/gi;
    let tm;
    while ((tm = tre.exec(full)) !== null) {
      ebHtmlTablesIn(tm[0]).forEach(t => found.push({ at: tm.index, end: tm.index + tm[0].length, headers: t.headers, rows: t.rows, bold: t.bold, hasDivider: true }));
    }
    found.sort((a, b) => a.at - b.at);

    /* A TABLE BROKEN BY A PAGE. The parser ends a pipe table at the page
     * boundary and starts the remainder on the next page with no header of its
     * own (or a repeat of it). Same width, nothing but whitespace between: one
     * table. */
    const merged = [];
    found.forEach(t => {
      const prev = merged[merged.length - 1];
      if (prev && prev.headers.length === t.headers.length && /^\s*$/.test(full.slice(prev.end, t.at))
          && pageAt(prev.at).page !== pageAt(t.at).page) {
        const sameHeader = prev.headers.join('|').toLowerCase() === t.headers.join('|').toLowerCase();
        // Without a divider its first line was a data row, not a header.
        if (!sameHeader && !t.hasDivider) { prev.rows.push(t.headers); prev.bold.push(!!t.firstBold); }
        t.rows.forEach((r, k) => { prev.rows.push(r); prev.bold.push(!!(t.bold || [])[k]); });
        prev.end = t.end;
        prev.pages.push(pageAt(t.at).page);
        return;
      }
      merged.push(Object.assign(t, { pages: [pageAt(t.at).page] }));
    });

    merged.forEach(t => {
      const ctx = contextAt(t.at);
      const p = pageAt(t.at);
      const entry = {
        fileName: doc.fileName, page: p.page, printedPage: p.printed, pages: t.pages, paginated: pages.length > 1,
        heading: ctx.heading, headings: ctx.headings, sectionText: ctx.sectionText,
        headers: t.headers, rows: t.rows, bold: t.bold
      };
      const c = ebClassifyTable(entry);
      entry.kind = c.kind;
      entry.kindScore = c.score;
      entry.figures = ebBenefitScore(entry);
      out.push(entry);
    });
  });
  if (_ebIndexCache && pdfTexts && typeof pdfTexts === 'object') _ebIndexCache.set(pdfTexts, out);
  return out;
}

// The table for one benefit: the classified table with the most figures; the
// first in the document on a tie.
function ebIndexedTable(pdfTexts, kind) {
  let best = null;
  ebTableIndex(pdfTexts).forEach(t => {
    if (t.kind !== kind || !t.figures) return;
    if (!best || t.figures > best.figures) best = t;
  });
  return best;
}

// The normalised block every benefit reader consumes, from an index entry.
function ebBlockFromEntry(t) {
  return t ? {
    carrier: ebCarrierIn(t.sectionText),
    columns: ebPlanNames(t),
    rows: ebLabelledRows(t),
    source: ebEntrySource(t)
  } : null;
}

// Where a table came from. A page only when the document was paginated: a
// guide parsed as one page would cite "p.1" for every figure in it.
function ebEntrySource(t) {
  return { fileName: t.fileName, page: t.paginated ? (t.printedPage || t.page) : null, heading: t.heading };
}

// Structured medical / dental / vision straight off the guide's own tables.
function extractEBPlanTables(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  const out = {};

  const medical = ebBlockFromEntry(ebIndexedTable(pdfTexts, 'medical')) || ebBuildBlock(txt, /##?#?\s*Medical Plans? Summary/i, /#\s*Medical Plan (?:Options|Benefits)|##?#?\s*Medical Plans? Summary/i,
    { key: /\bmedical\b/i, vocab: /deductible|out[\s\-]of[\s\-]pocket|co-?pay/i, exclude: /glossary|terms|definition|transportation|per\s*pay|cost|premium|contribution|rates?\b|notice|disclosure|faq|question/i });
  if (medical) {
    const rx = ebBlockFromEntry(ebIndexedTable(pdfTexts, 'rx'))
      || ebBuildBlock(txt, /#\s*Prescription Drug Benefits[\s\S]{0,400}?\n\s*\|/i, /#\s*Prescription Drug Benefits/i);
    if (rx) medical.rx = rx;
    out.medical = medical;
  }

  const dental = ebBlockFromEntry(ebIndexedTable(pdfTexts, 'dental')) || ebBuildBlock(txt, /##?#?\s*Dental Plans? Summary/i, /#\s*Dental Benefits|##?#?\s*Dental Plans? Summary/i,
    { key: /\bdental\b/i, vocab: /preventive|basic|major|annual\s+max/i, exclude: /glossary|terms|definition|transportation|per\s*pay|cost|premium|contribution|rates?\b|notice|disclosure|faq|question/i });
  if (dental) out.dental = dental;

  const vision = ebBlockFromEntry(ebIndexedTable(pdfTexts, 'vision')) || ebBuildBlock(txt, /##?#?\s*Vision Plans? Summary/i, /#\s*Vision Benefits|##?#?\s*Vision Plans? Summary/i,
    { key: /\bvision\b|\beye\b/i, vocab: /exam|frame|lens/i, exclude: /glossary|terms|definition|transportation|per\s*pay|cost|premium|contribution|rates?\b|notice|disclosure|faq|question/i });
  if (vision) out.vision = vision;

  const life = extractEBLifeADD(pdfTexts);
  if (life) out.life = life;

  const accounts = extractEBAccounts(pdfTexts);
  if (accounts) out.accounts = accounts;

  const retirement = extractEBRetirement(pdfTexts);
  if (retirement) out.retirement = retirement;

  const disability = extractEBDisability(pdfTexts);
  if (disability) out.disability = disability;

  return out;
}

// ---- Legacy bridge -------------------------------------------------------
// The benchmark table and Section 8's gap analysis consume a flat object per
// benefit type. Derive that from the parsed tables PER PLAN, so a two-plan
// guide is scored on the plan that actually performs best rather than on
// whichever column the old regex happened to reach first.
const ebPct = v => { const m = /(\d{1,3})\s*%/.exec(v || ''); return m ? parseInt(m[1], 10) : null; };
const ebDollar = v => { const m = /\$\s*([\d,]+)/.exec(v || ''); return m ? parseInt(m[1].replace(/,/g, ''), 10) : null; };

// Per-plan dental schedule + maximum, one entry per plan column.
function ebDentalPlans(dental) {
  if (!dental) return [];
  const prev  = ebRowValues(dental, /preventive|class\s*[i1]\b/i) || [];
  // "Child and Adult Orthodontia" with "Coinsurance" / "Lifetime Maximum"
  // beneath it reads as "… Orthodontia — Coinsurance" once grouped.
  const basic = ebRowValues(dental, /^basic|basic services|class\s*(?:ii|2)\b/i) || [];
  const major = ebRowValues(dental, /^major|major services|class\s*(?:iii|3)\b/i) || [];
  const ortho = ebRowValues(dental, /orthodontia\s+—\s+coinsurance/i) || ebRowValues(dental, /orthodontic(?!s lifetime)/i) || [];
  const oMax  = ebRowValues(dental, /orthodontics? lifetime max/i) || ebRowValues(dental, /orthodontia\s+—\s+lifetime max/i) || [];
  const aMax  = ebRowValues(dental, /annual|calendar year max|benefit max/i) || [];
  const ded   = ebRowValues(dental, /deductible/i) || [];
  // A plan whose services are a schedule, not percentages (a DHMO): the
  // services rows carry the same text, and the cell should say so rather than
  // print nothing for preventive, basic and major.
  /* "(You Pay)" columns state the MEMBER's share: Friendship's Base plan reads
   * Basic "20% After Deductible", which is the plan paying 80%. Converted so
   * every guide is scored on what the plan pays. */
  const youPay = i => /you\s+pay|member\s+pays|employee\s+pays/i.test(String(dental.columns[i] || ''));
  const paid = (v, i) => {
    if (v == null || v === '') return v;
    if (!youPay(i)) return v;
    if (/no\s+charge|nothing|covered\s+(?:in\s+full|100%)|^\$?0\b/i.test(v)) return '100%';
    const p = ebPct(v);
    return p == null ? v : (100 - p) + '%';
  };
  const schedText = i => {
    const v = [prev[i], basic[i], major[i]].map(x => String(x || '').trim());
    return v[0] && v.every(x => x === v[0]) && ebPct(v[0]) == null ? v[0] : null;
  };
  // Out-of-network only. "PPO Plan In-Network / Out-of-Network" (Arlington,
  // one value spanning both) is a plan, not an out-of-network column.
  const oonCol = c => /out[\s\-]?of[\s\-]?network/i.test(c || '') && !/\bin[\s\-]?network/i.test(c || '');
  const keepCol = dental.columns.some(c => !oonCol(c)) ? dental.columns.map(c => !oonCol(c)) : dental.columns.map(() => true);
  // The network label is kept ("DHMO Plan In-Network Only" says something);
  // only the "(You Pay)" note goes, since the figures are now the plan's share.
  const dNames = dental.columns.map(c => String(c || '').replace(/\s*\((?:you|member|employee)\s+pays?\)\s*$/i, '').trim());
  return dental.columns.map((name, i) => ({
    plan: dNames[i],
    servicesText: schedText(i),
    deductible: ded[i] || null,
    preventive: ebPct(paid(prev[i], i)),
    basic: ebPct(paid(basic[i], i)),
    major: ebPct(paid(major[i], i)),
    orthodontia: ebPct(paid(ortho[i], i)),
    orthodontiaMax: oMax[i] || null,
    // "If using an Advantage network provider: $1,500 / If using a Total
    // network provider: $1,000" — keep the text, and take the highest figure
    // for scoring since that maximum is genuinely available on this plan.
    annualMaxText: aMax[i] || null,
    annualMax: aMax[i] ? Math.max.apply(null, (aMax[i].match(/\$\s*[\d,]+/g) || []).map(ebDollar).concat([0])) || null : null
  })).filter((_p, i) => keepCol[i]);
}

// Per-plan medical figures.
/* A plan column's name, without the network note a header row glued on.
 * "HMO Plan In-Network OnlyThis plan has no out of network coverage" is a plan
 * called "HMO Plan". Only applied where the shortened names stay distinct. */
function ebCleanPlanNames(names) {
  const cut = (names || []).map(n => String(n || '').replace(/\s*(?:in[\s\-]?network|out[\s\-]?of[\s\-]?network)\b[\s\S]*$/i, '').trim() || n);
  return new Set(cut).size === cut.length ? cut : names;
}

// "$5,000 / $15,000" under an "Individual / Family" label is two figures.
function ebSplitPair(v) {
  const m = /^\s*(\$?[\d,]+(?:\.\d+)?)\s*\/\s*(\$?[\d,]+(?:\.\d+)?)/.exec(v || '');
  return m ? [m[1], m[2]] : null;
}

// A copay cell, without the note printed after the figure.
function ebCopay(v) {
  const s = String(v || '').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const m = /^(\$[\d,]+(?:\s*\/\s*\$[\d,]+)?)/.exec(s);
  return m ? m[1] : s;
}

function ebMedicalPlans(medical) {
  if (!medical) return [];
  const dedI = ebRowValues(medical, /^(?:calendar year\s+)?deductible — individual/i) || ebRowValues(medical, /deductible — individual/i) || ebRowValues(medical, /^individual$/i) || [];
  const dedF = ebRowValues(medical, /^(?:calendar year\s+)?deductible — family/i) || ebRowValues(medical, /deductible — family/i) || [];
  const oopI = ebRowValues(medical, /out-of-pocket max\w* — individual/i) || [];
  const oopF = ebRowValues(medical, /out-of-pocket max\w* — family/i) || [];
  /* One row carrying both figures. The Arlington Classics guide prints
   * "Calendar Year Deductible Individual / Family | $5,000 / $15,000", and with
   * only the two-row shape recognised every deductible and out-of-pocket
   * maximum was dropped from Section 6. */
  const dedB = ebRowValues(medical, /deductible[\s\S]*individual\s*\/\s*family/i) || [];
  const oopB = ebRowValues(medical, /out[\s\-]of[\s\-]pocket[\s\S]*individual\s*\/\s*family/i) || [];
  const coin = ebRowValues(medical, /after deductible, member pays|coinsurance/i) || [];
  const office = ebRowValues(medical, /office visit|physician visit|primary care|\bpcp\b/i) || [];
  const urgent = ebRowValues(medical, /urgent care/i) || [];
  const er = ebRowValues(medical, /emergency room|\ber\b copay/i) || [];
  const oon = c => /out[\s\-]?of[\s\-]?network/i.test(c || '') && !/\bin[\s\-]?network/i.test(c || '');
  const keep = medical.columns.some(c => !oon(c)) ? medical.columns.map(c => !oon(c)) : medical.columns.map(() => true);
  // Names are tidied among the plans kept, so "HMO In-Network (You Pay)" and
  // "HMO Out-of-Network (You Pay)" do not collide and block the tidy-up.
  const keptNames = ebCleanPlanNames(medical.columns.filter((_c, i) => keep[i]));
  let ki = 0;
  const names = medical.columns.map((c, i) => keep[i] ? keptNames[ki++] : c);
  return medical.columns.map((_name, i) => ({
    plan: names[i],
    deductibleIndividual: dedI[i] || (ebSplitPair(dedB[i]) || [])[0] || null,
    deductibleFamily: dedF[i] || (ebSplitPair(dedB[i]) || [])[1] || null,
    oopMaxIndividual: oopI[i] || (ebSplitPair(oopB[i]) || [])[0] || null,
    oopMaxFamily: oopF[i] || (ebSplitPair(oopB[i]) || [])[1] || null,
    coinsurance: ebPct(coin[i]) != null ? ebPct(coin[i]) + '%' : null,
    officeVisit: ebCopay(office[i]),
    urgentCare: ebCopay(urgent[i]),
    emergencyRoom: ebCopay(er[i])
  })).filter((_p, i) => keep[i]);
}

/* What the Rx table says, rather than how many cells it has. "Rx: 36 tier
 * values in the table above" is a count. The retail preferred tiers are what a
 * reader compares. */
function ebRxSummary(rx) {
  if (!rx || !rx.rows || !rx.rows.length) return null;
  const first = re => { for (const r of rx.rows) if (re.test(r.display || r.label || '')) return r.values; return null; };
  const tiers = [['generic', first(/generic[^|]*?[–\-]\s*preferred/i) || first(/generic/i)],
                 ['brand', first(/brand[^|]*?[–\-]\s*preferred/i) || first(/brand/i)],
                 ['specialty', first(/specialty[^|]*?[–\-]\s*preferred/i) || first(/specialty/i)]].filter(t => t[1]);
  if (!tiers.length) return null;
  const names = ebCleanPlanNames(rx.columns);
  const lead = v => String(v || '').split(/\s+\/\s+/)[0].trim();
  return names.map((n, i) => n + ': ' + tiers.map(t => t[0] + ' ' + (lead(t[1][i]) || '—')).join(', ')).join('; ');
}

// Vision is one plan with a value and a frequency column.
/* Columns are found by name. The Arlington Classics vision table is
 * In-Network | Out-of-Network | Frequency, and taking the second column as the
 * frequency printed "Frame allowance is $130 every Up to $70". Its first row is
 * "Eye Exam", which /^exam/ never matched, so the $10 exam copay was dropped. */
function ebVisionFields(vision) {
  if (!vision) return null;
  const cols = vision.columns || [];
  const freqIdx = cols.findIndex(c => /frequen/i.test(c));
  const oonIdx = cols.findIndex(c => /out[\s\-]?of[\s\-]?network|non[\s\-]?network/i.test(c));
  const pick = re => {
    // "Exam Frequency" / "Frames Frequency" rows (Friendship) come before the
    // benefit rows; the benefit is the row that is not a frequency, and the
    // frequency row, if any, supplies its frequency.
    const rows = vision.rows || [];
    const benefit = rows.find(r => (re.test(r.display) || re.test(r.label)) && !/frequency/i.test(r.label));
    const freqRow = rows.find(r => (re.test(r.display) || re.test(r.label)) && /frequency/i.test(r.label));
    if (benefit && freqRow) {
      const vv = benefit.values;
      return { value: vv[0] || null, outOfNetwork: oonIdx > 0 ? (vv[oonIdx] || null) : null, frequency: freqRow.values[0] || null };
    }
    const v = benefit ? benefit.values : ebRowValues(vision, re);
    if (!v) return null;
    return {
      value: v[0] || null,
      outOfNetwork: oonIdx > 0 ? (v[oonIdx] || null) : null,
      frequency: freqIdx > 0 ? (v[freqIdx] || null) : (oonIdx < 0 ? (v[1] || null) : null)
    };
  };
  return {
    exam: pick(/^(?:eye\s+|routine\s+)?exam(?!\s+frequency)/i),
    contactsExam: pick(/^contacts?\s+(?:lens\s+)?exam|contact lens fitting/i),
    lenses: pick(/^lenses/i),
    frames: pick(/^frames/i),
    contacts: pick(/contact lens(?!.*exam)|contact lenses|^contacts,?\s+elective/i)
  };
}

// Text of one section: from its heading to the next heading of the same or
// higher level. A fixed character window either truncates a long section or
// spills into the next one — which is how the life benefit was read out of the
// rates table 500 lines above the Life & AD&D section.
function ebBoundedSection(txt, headingRe) {
  const m = headingRe.exec(txt || '');
  if (!m) return '';
  const level = (/^#+/.exec(m[0].trim()) || ['#'])[0].length;
  const rest = txt.slice(m.index + m[0].length);
  const next = new RegExp('^#{1,' + level + '}\\s', 'm').exec(rest);
  return m[0] + (next ? rest.slice(0, next.index) : rest);
}

// --- LIFE & AD&D ----------------------------------------------------------
// Basic life is stated in PROSE ("provides all full-time employees with $15,000
// of Basic Life and AD&D insurance"), while the supplemental election sits in a
// table. Both must be read inside the Life section: scanning the whole document
// returned "$1,000" here — a child voluntary-life amount from the rates pages,
// against a real $15,000 basic benefit.
function extractEBLifeADD(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  /* Headings arrive wrapped in their own bold markers.
   *
   * LlamaParse writes "## **Basic Life and AD&D**", and a pattern anchored on
   * "#+\s*Basic Life" never reaches the words past the asterisks. The whole
   * life block was dropped from a guide stating $30,000 of employer-paid basic
   * life, voluntary to $500,000, guaranteed issue $250,000. \** after the
   * hashes costs nothing and is how every other heading in these guides
   * arrives. */
  const sec = ebBoundedSection(txt, /^#\s*\**\s*Life\s*&\s*AD&D\s*Insurance.*$/mi)
           || ebBoundedSection(txt, /^#+\s*\**\s*(?:Basic\s+)?Life\s+(?:and|&)\s*AD&D.*$/mi)
           || ebBoundedSection(txt, /^#+\s*\**\s*Life\s+Insurance\s*\**\s*$/mi)
           // "# **Basic Life Insurance and Accidental Death & Dismemberment**"
           || ebBoundedSection(txt, /^#+\s*\**\s*(?:Basic|Group|Employer[- ]Paid)\s+(?:Term\s+)?Life\b.*$/mi);
  if (!sec) return null;

  const out = { carrier: ebCarrierIn(sec) };

  const basic = /\$\s*([\d,]+)\s*(?:of|in)\s+(?:Basic\s+)?Life/i.exec(sec)
             || /(?:Basic\s+Life[^.$]{0,80})\$\s*([\d,]+)/i.exec(sec);
  if (basic) out.basicAmount = '$' + basic[1];
  /* A salary multiple, as Friendship states it: "up to 2 times annual salary
   * to maximum of $500,000". The multiple is the benefit; the dollar figure is
   * its cap, not a flat amount. */
  const mult = /\b(one|two|three|four|five|1(?:\.5)?|2|3|4|5)\s*(?:times|x)\s+(?:your\s+)?(?:annual\s+|base\s+|basic\s+)*(?:salary|earnings|pay|compensation)\b[^.]{0,60}?(?:maximum|max\.?)\s*(?:of\s+)?\$\s*([\d,]+)/i.exec(sec)
            || /\b(one|two|three|four|five|1(?:\.5)?|2|3|4|5)\s*(?:times|x)\s+(?:your\s+)?(?:annual\s+|base\s+|basic\s+)*(?:salary|earnings|pay|compensation)\b/i.exec(sec);
  if (mult && !out.basicAmount) {
    const n = { one: 1, two: 2, three: 3, four: 4, five: 5 }[String(mult[1]).toLowerCase()] || parseFloat(mult[1]);
    out.basicMultiple = n;
    out.basicAmount = n + 'x annual salary' + (mult[2] ? ', to a maximum of $' + mult[2] : '');
  }

  if (/no cost to you|company[- ]paid|employer[- ]paid|at no cost/i.test(sec)) out.employerPaid = 'Yes — no cost to employee';

  const red = /reduc\w*\s+to\s+(\d{1,3})%\s+upon\s+attainment\s+of\s+age\s+(\d{2})\s+and\s+(\d{1,3})%\s+upon\s+attainment\s+of\s+age\s+(\d{2})/i.exec(sec);
  if (red) out.ageReduction = red[1] + '% at age ' + red[2] + ', ' + red[3] + '% at age ' + red[4];

  if (/Accidental Death (?:&|and) Dismemberment/i.test(sec)) {
    out.addIncluded = /additional\s+Accidental Death/i.test(sec)
      ? 'Additional AD&D benefit payable for accidental death'
      : 'Included';
  }

  // Supplemental / voluntary election table (Employee / Spouse / Children).
  const vol = ebParseTableAfter(sec, /^##\s*(?:Supplemental|Voluntary)\s+Life/mi);
  if (vol) out.voluntary = { carrier: out.carrier, columns: ebPlanNames(vol), rows: ebLabelledRows(vol) };

  if (/Evidence of insurability/i.test(sec)) out.evidenceOfInsurability = 'May be required for late enrollees or amounts over Guarantee Issue';

  return Object.keys(out).filter(k => out[k]).length ? out : null;
}

// --- DISABILITY (STD / LTD) ------------------------------------------------
// The guide describes STD in a two-column plan table under "# Disability
// Insurance", and never mentions LTD at all. Section 6 showed neither: the STD
// row fell through to "Benefit document uploaded — details not parsed from
// text", which tells a reviewer nothing and is indistinguishable from a real
// absence.
//
// Read the STD table, and report LTD's absence AS an absence — the guide is a
// complete benefits summary, so a line that appears nowhere in it is not
// covered, which is what Section 11 already calls the largest uncovered income
// exposure.
function extractEBDisability(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  const sec = ebBoundedSection(txt, /^#\s*Disability Insurance.*$/mi)
           || ebBoundedSection(txt, /^#\s*Disability\b.*$/mi);

  const out = { std: null, ltd: { present: /long[- ]term disability|\bLTD\b/i.test(txt) } };

  /* THE BENEFIT NAMES ITSELF; THE SECTION IT SITS UNDER MAY NOT.
   *
   * This required a "# Disability Insurance" or "# Disability" heading. The
   * Arlington Classics guide files both plans under "# Income Protection
   * Benefits", with "## Voluntary Short-Term Disability" and "## Voluntary
   * Long-Term Disability" beneath, so neither was found: STD came back null
   * and LTD came back as a bare present:true from a whole-document regex, on a
   * guide stating 60% of pay to a $1,500 weekly maximum, benefits beginning
   * after 0/14/30 days and running 9 or 13 weeks.
   *
   * So read each plan from its OWN heading, whatever section encloses it, and
   * fall back to the old section-level search. ebBestTableAfter picks the
   * benefits table over the age-rate schedule printed beneath it. */
  const planBlock = (headingRe) => {
    const t = ebBestTableAfter(txt, headingRe);
    if (!t) return null;
    const block = { rows: ebLabelledRows(t), columns: ebPlanNames(t) };
    const val = re => { const v = ebRowValues(block, re); return v && v[0] ? v[0] : null; };
    const plan = {
      planLabel: block.columns[0] || null,
      carrier: ebCarrierIn(ebSection(txt, headingRe, 3000)),
      benefitsBegin: val(/when benefits begin|elimination/i),
      benefitsEnd: val(/when benefits end|benefit (?:period|duration)|maximum benefit (?:period|duration)/i),
      benefitsPaid: val(/benefits paid|benefit amount|monthly benefit|weekly benefit/i),
      whoPays: val(/who pays/i),
      preExisting: val(/pre[- ]?existing/i)
    };
    const stated = Object.keys(plan).some(k => plan[k] && k !== 'planLabel' && k !== 'carrier');
    return stated ? plan : null;
  };
  const fund = (plan, label) => {
    if (!plan) return plan;
    plan.employerPaid = /employer|company/i.test(plan.whoPays || '')
      || (!/employee/i.test(plan.whoPays || '') && !/voluntary/i.test(String(label || '')));
    return plan;
  };

  /* FROM THE TABLE INDEX FIRST: every disability table, column by column.
   * A column is STD or LTD by its own header, or by the heading above a
   * one-plan table ("# Short-Term Disability Buy-Up"). A buy-up column beside
   * a base plan is the buy-up option, not a second plan. */
  {
    const cols = { std: [], ltd: [] };
    ebTableIndex(pdfTexts).filter(t => t.kind === 'disability' && t.figures).forEach(t => {
      const block = { rows: ebLabelledRows(t), columns: t.headers.slice(1) };
      const ctx = (t.headings || []).slice(-2).join(' ');
      block.columns.forEach((h, idx) => {
        const own = /short[\s\-]term|\bSTD\b/i.test(h) ? 'std' : /long[\s\-]term|\bLTD\b/i.test(h) ? 'ltd' : null;
        const byHeading = /short[\s\-]term|\bSTD\b/i.test(t.heading) ? 'std' : /long[\s\-]term|\bLTD\b/i.test(t.heading) ? 'ltd' : null;
        const type = own || (block.columns.length === 1 ? byHeading : null);
        if (!type) return;
        const val = re => { const v = ebRowValues(block, re); return v && v[idx] ? v[idx] : null; };
        const pctRow = val(/percentage of (?:income|salary|earnings|pay)|income\s+re-?\s*placed/i);
        const maxRow = val(/^maximum\s+(?:weekly\s+|monthly\s+)?benefit$/i);
        const plan = {
          planLabel: h || null,
          carrier: ebCarrierIn(t.sectionText),
          benefitsBegin: val(/when benefits begin|benefits? begin|elimination/i),
          benefitsEnd: val(/when benefits end|benefits? (?:end|payable)|benefit (?:period|duration)|maximum benefit (?:period|duration)/i),
          benefitsPaid: val(/benefits paid|benefit amount|monthly benefit|weekly benefit/i)
            || (pctRow ? pctRow + (maxRow ? ' of pay, up to ' + maxRow : ' of pay') : null),
          whoPays: val(/who pays/i),
          preExisting: val(/pre[- ]?existing/i),
          source: ebEntrySource(t)
        };
        if (!['benefitsBegin', 'benefitsEnd', 'benefitsPaid', 'whoPays', 'preExisting'].some(k => plan[k])) return;
        cols[type].push({ plan, buyUp: /buy[\s\-]?up/i.test(h + ' ' + ctx), label: h + ' ' + ctx });
      });
    });
    const take = list => {
      const base = list.find(c => !c.buyUp) || list[0];
      if (!base) return null;
      const plan = fund(base.plan, base.label);
      const up = list.find(c => c !== base && c.buyUp);
      if (up) plan.buyUp = { benefitsPaid: up.plan.benefitsPaid, benefitsEnd: up.plan.benefitsEnd, source: up.plan.source };
      return plan;
    };
    const std = take(cols.std), ltd = take(cols.ltd);
    if (std) out.std = std;
    if (ltd) { out.ltd = Object.assign({ present: true }, ltd); }
  }
  const _ltdFromIndex = !!(out.ltd && out.ltd.benefitsPaid);

  const stdHeading = /^#{1,4}\s*\**\s*(?:voluntary\s+)?short[- ]term disability\b.*$/mi;
  const ltdHeading = /^#{1,4}\s*\**\s*(?:voluntary\s+)?long[- ]term disability\b.*$/mi;

  const stdDirect = out.std ? null : planBlock(stdHeading);
  if (stdDirect) out.std = fund(stdDirect, (stdHeading.exec(txt) || [''])[0] || stdDirect.planLabel);

  let ltdDirect = _ltdFromIndex ? out.ltd : planBlock(ltdHeading);
  if (ltdDirect && !_ltdFromIndex) {
    out.ltd = Object.assign({ present: true }, ltdDirect);
    out.ltd.employerPaid = fund(ltdDirect, (ltdHeading.exec(txt) || [''])[0] || ltdDirect.planLabel).employerPaid;
  }

  /* ONE TABLE, BOTH PLANS.
   *
   * The Jackson Hole Classical Academy guide prints STD and LTD side by side in
   * a single table under "## Disability" (itself under "# Income Protection
   * Benefits"): "Plan | Short-Term Disability Buy Up | Long-Term Disability Buy
   * Up". There is no per-plan heading, and the section search above wants a
   * top-level "# Disability", so both Section 6 rows read "Not found in
   * uploaded documents" beside a guide stating 60% of pay to $1,000 a week for
   * 12 weeks, and 60% to $5,000 a month after 90 days.
   *
   * So read any disability / income-protection table whose COLUMNS name the
   * plans, and take each plan from its own column. */
  if (!out.std || !ltdDirect) {
    const headRe = /^#{1,4}\s*\**\s*(?:[^\n]*\b)?(?:disability|income protection)\b.*$/gmi;
    let hm;
    while ((hm = headRe.exec(txt)) && (!out.std || !ltdDirect)) {
      const from = txt.slice(hm.index);
      const t = ebBestTableAfter(from, /^#[^\n]*/);
      if (!t) continue;
      const cols = t.headers.slice(1);
      const stdIdx = cols.findIndex(h => /short[- ]term|\bSTD\b/i.test(h));
      const ltdIdx = cols.findIndex(h => /long[- ]term|\bLTD\b/i.test(h));
      if (stdIdx < 0 && ltdIdx < 0) continue;
      const block = { rows: ebLabelledRows(t), columns: cols };
      const carrier = ebCarrierIn(ebBoundedSection(from, /^#[^\n]*/));
      const column = idx => {
        const val = re => { const v = ebRowValues(block, re); return v && v[idx] ? v[idx] : null; };
        /* Friendship words the rows "Benefits Begin", "Benefits Payable",
         * "Percentage of Income Replaced" and "Maximum Benefit". The benefit
         * paid is the percentage and the maximum read together. */
        const pctRow = val(/percentage of (?:income|salary|earnings|pay)|income\s+re-?\s*placed/i);
        const maxRow = val(/^maximum\s+(?:weekly\s+|monthly\s+)?benefit$/i);
        const plan = {
          planLabel: cols[idx] || null,
          carrier: carrier,
          benefitsBegin: val(/when benefits begin|benefits? begin|elimination/i),
          benefitsEnd: val(/when benefits end|benefits? (?:end|payable)|benefit (?:period|duration)|maximum benefit (?:period|duration)/i),
          benefitsPaid: val(/benefits paid|benefit amount|monthly benefit|weekly benefit/i)
            || (pctRow ? pctRow + (maxRow ? ' of pay, up to ' + maxRow : ' of pay') : null),
          whoPays: val(/who pays/i),
          preExisting: val(/pre[- ]?existing/i)
        };
        const stated = Object.keys(plan).some(k => plan[k] && k !== 'planLabel' && k !== 'carrier');
        return stated ? fund(plan, cols[idx]) : null;
      };
      if (!out.std && stdIdx >= 0) out.std = column(stdIdx);
      if (!ltdDirect && ltdIdx >= 0) {
        const ltd = column(ltdIdx);
        if (ltd) { out.ltd = Object.assign({ present: true }, ltd); ltdDirect = ltd; }
      }
    }
  }

  if (!sec && !out.ltd.present && !out.std) return null;

  if (sec && !out.std) {
    // Aim at the deepest heading above the grid: ebParseTableAfter() stops at
    // any heading it crosses, and "# Disability Insurance" has a "## Disability"
    // between it and the table.
    const t = ebParseTableAfter(sec, /^##\s*Disability\s*$/mi)
           || ebParseTableAfter(sec, /^#\s*Disability Insurance.*$/mi);
    if (t) {
      const block = { rows: ebLabelledRows(t), columns: ebPlanNames(t) };
      const val = re => { const v = ebRowValues(block, re); return v && v[0] ? v[0] : null; };
      const std = {
        planLabel: (block.columns[0] || 'Short-Term Disability'),
        carrier: ebCarrierIn(sec),
        benefitsBegin: val(/when benefits begin|elimination/i),
        benefitsEnd: val(/when benefits end|benefit (?:period|duration)/i),
        benefitsPaid: val(/benefits paid|benefit amount/i),
        whoPays: val(/who pays/i),
        preExisting: val(/pre[- ]?existing/i)
      };
      // Who funds it decides whether this is employer competitiveness at all.
      std.employerPaid = /employer|company/i.test(std.whoPays || '')
        || (!/employee/i.test(std.whoPays || '') && !/voluntary/i.test(std.planLabel + ' ' + sec));
      if (Object.keys(std).some(k => std[k] && k !== 'planLabel' && k !== 'employerPaid')) out.std = std;
    }
  }
  if (!out.ltd.present) out.ltd.reason = 'No long-term disability coverage appears anywhere in the benefit guide.';
  return out;
}

// --- 401(k) / RETIREMENT ---------------------------------------------------
// The flat layer read `employerMatch` off a whole-document match-percentage
// regex and emitted nothing when the guide states none — so the row went out
// blank beside a "3% employer match" benchmark, leaving the reader to guess
// whether the match was zero, unstated, or simply unparsed.
//
// Two reporting rules, both as the broker asked:
//   1. No employer percentage or amount stated -> say exactly that in the cell.
//   2. No waiting period stated -> the employee is eligible immediately.
//
// Rule 2 is why this is section-scoped rather than a document scan. The
// Arizona guide states a 60-day wait for HOURLY staff to enrol in the HEALTH
// plans; borrowing that into the 401(k) row would invent a retirement waiting
// period out of a medical one — the same FROM/ABOUT confusion the profile
// detector had.
function extractEBRetirement(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  const sec = ebBoundedSection(txt, /^#\s*401\(k\).*$/mi)
           || ebBoundedSection(txt, /^#\s*403\(b\).*$/mi)
           || ebBoundedSection(txt, /^#\s*Retirement\b.*$/mi)
           // "# Welcome to Your Retirement Plan" / "## Our 403(b) Retirement Plan"
           || ebBoundedSection(txt, /^#{1,3}\s*\**[^\n]*\b(?:401\s*\(?k\)?|403\s*\(?b\)?|457\s*\(?b\)?|retirement\s+(?:plan|savings|benefits?))\b[^\n]*$/mi);
  if (!sec) return null;

  const out = {};
  out.planType = /403\(b\)/i.test(sec) ? '403(b)' : (/401\(k\)|401k/i.test(sec) ? '401(k)' : 'Retirement plan');
  const rk = /\b(Empower(?:\s+Retirement)?|Fidelity|Vanguard|Principal|Voya|John Hancock|T\.\s?Rowe Price|TIAA|Transamerica|Nationwide)\b/i.exec(sec);
  if (rk) out.recordkeeper = rk[1];

  // Employer money: a percentage, a flat dollar amount, or a non-elective /
  // safe-harbor contribution. Anything matched must sit in this section.
  const pct = /(?:employer|company)?\s*match\w*[^.\n]{0,40}?(\d{1,3})\s*%/i.exec(sec)
           || /(\d{1,3})\s*%[^.\n]{0,40}?(?:employer|company)\s+match/i.exec(sec);
  const amt = /(?:employer|company)\s+(?:contribut\w+|match\w*)[^.\n]{0,40}?\$\s*([\d,]+)/i.exec(sec);
  const nonElective = /safe[- ]harbor|non[- ]elective contribution|profit[- ]sharing contribution/i.exec(sec);
  /* "a matching contribution of 50% up to 8% of eligible compensation
   * deferred, for a total of 4%" — the formula, and the effective rate the
   * benchmark is measured against. */
  const formula = /match\w*\s+contribution\s+of\s+(\d{1,3})\s*%\s+(?:of\s+[^.]{0,40}?)?up\s+to\s+(\d{1,2}(?:\.\d)?)\s*%[^.]*?(?:total\s+of\s+(\d{1,2}(?:\.\d)?)\s*%)?/i.exec(sec);
  if (formula) {
    const eff = formula[3] || String(Math.round(parseFloat(formula[1]) * parseFloat(formula[2])) / 100);
    out.employerContribution = eff + '% (' + formula[1] + '% match up to ' + formula[2] + '% of pay)';
  } else if (pct) out.employerContribution = pct[1] + '%';
  else if (amt) out.employerContribution = '$' + amt[1];
  else if (nonElective) out.employerContribution = nonElective[0];
  else out.employerContribution = null;

  // Rule 2. A stated wait wins; absent one, eligibility is immediate.
  const wait = /(?:after|following|upon completion of)\s+(\d{1,3})\s*(day|month|year)s?\b/i.exec(sec)
            || /(\d{1,3})\s*(day|month|year)s?\s+(?:of\s+)?(?:service|employment|waiting period)/i.exec(sec)
            || /first of the month following\s+(\d{1,3})\s*(day|month)s?/i.exec(sec);
  out.waitingPeriod = wait ? wait[1] + ' ' + wait[2] + (Number(wait[1]) === 1 ? '' : 's') : null;
  out.eligibility = out.waitingPeriod ? out.waitingPeriod + ' waiting period' : 'Immediate';
  // "There is no age or service requirement to start deferring … After 1 year
  // of service … eligible for the employer match": deferrals are immediate,
  // and only the match waits.
  if (out.waitingPeriod && /no\s+(?:age\s+or\s+)?service\s+requirement|immediately\s+eligible\s+to\s+(?:defer|contribute)/i.test(sec)
      && /eligible\s+for\s+the\s+(?:employer\s+)?match/i.test(sec)) {
    out.eligibility = 'Immediate for employee deferrals; employer match after ' + out.waitingPeriod;
  }

  const vest = /vest\w*[^.\n]{0,60}?(immediate\w*|\d\s*[- ]?year\s+(?:cliff|graded)|cliff|graded)/i.exec(sec);
  if (vest) out.vesting = vest[1];

  const irs = /IRS annual maximum\s*\(?\$\s*([\d,]+)/i.exec(sec) || /up to the IRS[^$\n]{0,40}\$\s*([\d,]+)/i.exec(sec);
  const catchUp = /catch[- ]up contribution of\s*\$\s*([\d,]+)/i.exec(sec);
  if (irs || catchUp) {
    out.employeeContributionLimit = [irs ? '$' + irs[1] + ' IRS annual maximum' : null,
                                     catchUp ? '$' + catchUp[1] + ' catch-up 50+' : null].filter(Boolean).join(' / ');
  }
  return out;
}

// --- HSA / FSA ------------------------------------------------------------
// These live in prose + a small account table, not the plan grid, and the flat
// regex layer reported them as `FsaAvailable: Yes` / `DcfsaAvailable: Yes` —
// true, but it told a reviewer nothing, while the benchmark column next to it
// read "HSA with HDHP, employer seed contribution" as if that were the finding.
// Read both sections instead and report what the guide actually states: what
// the employee is eligible FOR, and the contribution limits it publishes.
//
// The employer contribution is the field that must not be invented. The
// Arizona guide states none, so this reports it as "not stated in the guide" —
// never as an employer seed that exists, and never as one that does not.
function extractEBAccounts(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  const hsaSec = ebBoundedSection(txt, /^#\s*Health Savings Account.*$/mi);
  const fsaSec = ebBoundedSection(txt, /^#\s*Flexible Spending Accounts?\s*\(FSA\).*$/mi)
              || ebBoundedSection(txt, /^#\s*Flexible Spending Account.*$/mi)
              // "# Dependent Care Flexible Spending Account"
              || ebBoundedSection(txt, /^#{1,3}\s*\**[^\n]*Flexible Spending Accounts?\b[^\n]*$/mi);
  if (!hsaSec && !fsaSec) return null;

  const out = {};

  if (hsaSec) {
    const hsa = { eligible: 'Eligible to open HSA' };
    // "such as Cigna Local Plus HSA Base Plan" / "Must be enrolled in the ..."
    const plan = /\b(?:such as|enrolled in)\s+(?:the\s+)?([A-Z][^.,\n]{3,60}?\bPlan)\b/.exec(hsaSec);
    if (plan) hsa.requiresPlan = plan[1].trim();
    if (/High Deductible Health Plan|\bHDHP\b/i.test(hsaSec)) hsa.hdhpRequired = 'Yes — HDHP enrolment required';
    const seed = /(?:employer|company)\s+(?:contribut\w+|seed\w*|fund\w*|deposit\w*)[^.\n]{0,60}?\$\s*([\d,]+)/i.exec(hsaSec);
    hsa.employerContribution = seed ? '$' + seed[1] : null;
    // The IRS maxima are the employee's own ceiling, not an employer benefit —
    // labelled as such so they are never read as money the employer puts in.
    const ind = /\$\s*([\d,]+)\s+for an individual/i.exec(hsaSec);
    const fam = /\$\s*([\d,]+)\s+for an employee and dependents/i.exec(hsaSec);
    const cat = /\$\s*([\d,]+)\s+catch[- ]?up/i.exec(hsaSec);
    if (ind || fam || cat) {
      hsa.employeeContributionLimit = [ind ? '$' + ind[1] + ' individual' : null,
                                       fam ? '$' + fam[1] + ' family' : null,
                                       cat ? '$' + cat[1] + ' catch-up 55+' : null].filter(Boolean).join(' / ');
    }
    out.hsa = hsa;
  }

  if (fsaSec) {
    const fsa = { eligible: 'Eligible for FSA', accounts: [] };
    // The account table: | **Health Care FSA** | what it covers | $3,300 |
    // Take the LAST cell as the limit — the middle column is prose that itself
    // contains dollar figures in some guides.
    fsaSec.split('\n').forEach(line => {
      if (!/^\s*\|/.test(line)) return;
      const cells = line.split('|').map(c => c.trim()).filter(c => c !== '');
      if (cells.length < 2) return;
      const name = cells[0].replace(/\*\*/g, '').trim();
      const limit = cells[cells.length - 1].replace(/\*\*/g, '').trim();
      if (!/FSA|Flexible Spending/i.test(name)) return;
      if (!/\$/.test(limit)) return;
      fsa.accounts.push({ name: name, limit: limit });
    });
    const carry = /roll over up to\s*\$\s*([\d,]+)/i.exec(fsaSec)
               || /any funds over\s*\$\s*([\d,]+)\s+will be lost/i.exec(fsaSec);
    if (carry) fsa.carryover = '$' + carry[1];
    if (/Dependent Care/i.test(fsaSec)) fsa.dependentCare = 'Offered';
    // No account table: the limit in prose ("The maximum that you can
    // contribute to the Dependent Care Flexible Spending Account is $7,500").
    if (!fsa.accounts.length) {
      const dc = /maximum[^.]{0,80}?dependent care[^.]{0,80}?\$\s*([\d,]+)([^.]{0,90})/i.exec(fsaSec)
              || /dependent care[^.]{0,80}?maximum[^.]{0,40}?\$\s*([\d,]+)([^.]{0,90})/i.exec(fsaSec);
      if (dc) {
        const sep = /\$\s*([\d,]+)\s+if\s+(?:you\s+are\s+)?married\s+(?:and\s+)?filing\s+separately/i.exec(fsaSec);
        fsa.accounts.push({ name: 'Dependent Care FSA', limit: '$' + dc[1] + (sep ? ' ($' + sep[1] + ' if married filing separately)' : '') });
      }
      const hc = /health\s*care\s+(?:FSA|flexible spending account)[^.]{0,80}?\$\s*([\d,]+)/i.exec(fsaSec);
      if (hc) fsa.accounts.push({ name: 'Health Care FSA', limit: '$' + hc[1] });
    }
    out.fsa = fsa;
  }

  return Object.keys(out).length ? out : null;
}

// ---- Coverage presence (lines not carried by a plan-summary table) --------
// Disability, retirement, HSA/FSA, EAP and the voluntary suite are described in
// prose sections rather than a plan grid. Detect them section-scoped, and
// record WHY: "not stated in the guide" is different from "does not exist", and
// the enhancement table must say the former.
function ebDetectCoverageLines(pdfTexts) {
  const txt = ebAllText(pdfTexts);
  const sec = re => ebBoundedSection(txt, re);
  const has = re => re.test(txt);

  const disability = sec(/^#\s*Disability Insurance.*$/mi) || sec(/^##?\s*Disability\b.*$/mi);
  const std = /short[- ]term disability|\bSTD\b/i.test(disability) || /short[- ]term disability/i.test(txt);
  const ltd = /long[- ]term disability|\bLTD\b/i.test(txt);

  const retireSec = sec(/^#\s*401\(k\).*$/mi) || sec(/^#\s*(?:403\(b\)|Retirement).*$/mi);
  const retirement = !!retireSec || /401\(k\)|403\(b\)/i.test(txt);

  const hsaSec = sec(/^#\s*Health Savings Account.*$/mi);
  const fsaSec = sec(/^#\s*Flexible Spending Account.*$/mi);

  return {
    std: {
      present: std,
      // "Who Pays | Employee" in the disability grid, or explicit voluntary framing.
      employerPaid: std ? !/voluntary short[- ]term|who pays\s*\|?\s*employee|offers voluntary/i.test(disability || txt) : false,
      detail: std ? (function () {
        // Aim at the deepest heading above the grid: ebParseTableAfter() stops at
        // any heading it crosses, and "# Disability Insurance" has a "## Disability"
        // between it and the table.
        const t = ebParseTableAfter(disability, /^##\s*Disability\s*$/mi)
               || ebParseTableAfter(disability, /^#\s*Disability Insurance.*$/mi);
        const v = t ? ebRowValues({ rows: ebLabelledRows(t), columns: ebPlanNames(t) }, /benefits paid/i) : null;
        return v && v[0] ? v[0] : null;
      })() : null
    },
    ltd: { present: ltd },
    retirement: {
      present: retirement,
      // An employer match is a headline benefit; a guide that describes the plan
      // in detail and never mentions one has not stated a match.
      matchStated: /employer match|company match|matching contribution|safe harbor match|match(?:es)? \d{1,3}%/i.test(retireSec || txt)
    },
    hsa: {
      present: !!hsaSec || /health savings account/i.test(txt),
      employerSeedStated: /employer (?:contribut|seed|fund|deposit)|company contribut|seed contribution/i.test(hsaSec || '')
    },
    fsa: { present: !!fsaSec || /flexible spending account/i.test(txt) },
    eap: { present: /employee assistance program|\bEAP\b/i.test(txt) },
    /* A product counts only where the guide OFFERS it. "Pet" matched the
     * EAP's "locating pet care" and MASA's "Pet Return Transportation", and
     * the report listed pet insurance the employer does not offer. */
    voluntary: [['Accident', /accident/i],
                ['Critical Illness', /critical\s+illness/i],
                ['Hospital Indemnity', /hospital\s+indemnity/i],
                ['Pet', /pet\s+insurance/i],
                ['Legal', /legal\s+(?:plan|insurance|services\s+plan)/i]]
      .filter(([, re]) => re.test(txt)).map(([n]) => n),
    masa: /\bMASA\b/.test(txt),
    telemedicine: /telemedicine|telehealth/i.test(txt),
    supplementalLife: /supplemental\s+life|voluntary\s+life/i.test(txt),
    ltdVoluntary: /voluntary\s+long[- ]term\s+disability|voluntary\s+LTD/i.test(txt)
  };
}

// ---- Recommended enhancements -------------------------------------------
// Emitted ONLY for gaps measurable in the extracted coverage. The list this
// replaces was a fixed eight rows that ran regardless of the plan: it told a
// client with 50% orthodontia to "add orthodontia", a client with a full
// voluntary suite that their options were "limited", and a client with no
// employer match at all to "increase" it — while the real finding, no LTD
// anywhere in the guide, sat in the same undifferentiated list.
function buildEBEnhancements(tables, lines) {
  const rows = [];
  const add = (r) => rows.push(r);
  const money = v => { const m = /\$\s*([\d,]+)/.exec(v || ''); return m ? parseInt(m[1].replace(/,/g, ''), 10) : null; };

  // --- Disability -------------------------------------------------------
  if (lines && !lines.ltd.present) {
    add({ priority: 'CRITICAL', item: 'Long-Term Disability — add',
      gap: 'No LTD coverage appears anywhere in the benefit guide.',
      rec: '60% of salary to $10,000/mo, 90-day elimination, own-occupation 24 months',
      cost: '$12–$25', roi: 'Largest uncovered income exposure; a disability lasting past the STD period is currently unfunded.' });
  }
  /* Not a benchmark gap: the benefit level is measured in Section 6 and may
   * well meet it. Who pays is the finding, so it is MODERATE and says so, and
   * it names no plan term the guide does not state — a "3/12 pre-existing-
   * condition limit" was printed here for a guide that has no such wording. */
  if (lines && lines.std.present && !lines.std.employerPaid) {
    add({ priority: 'MODERATE', item: 'Short-Term Disability — employer-fund',
      gap: 'STD is offered on a voluntary, employee-paid basis' +
           (lines.std.detail ? ' (' + String(lines.std.detail).replace(/\.\s*$/, '') + ')' : '') +
           '. The benefit level is benchmarked in Section 6; the gap is who pays.',
      rec: 'Employer-paid core STD at the current benefit level, with the voluntary plans kept as buy-ups',
      cost: '$8–$18', roi: 'Take-up on voluntary STD is typically well under half of staff, so most employees have no short-term income protection.' });
  }

  // --- Dental -----------------------------------------------------------
  const dental = tables && tables.dental ? ebDentalPlans(tables.dental) : [];
  // Where every plan shares the same shortfall, say so once and name the plans —
  // one row per plan reads as two separate findings for a single defect.
  const planList = ps => ps.length === dental.length && dental.length > 1
    ? 'Both plans' : ps.map(p => p.plan).join(' and ');

  const orthoShort = dental.filter(p => p.orthodontia != null && money(p.orthodontiaMax) != null && money(p.orthodontiaMax) < 1000);
  if (orthoShort.length) {
    const maxes = [...new Set(orthoShort.map(p => p.orthodontiaMax))];
    add({ priority: 'HIGH', item: 'Dental — raise orthodontia lifetime maximum',
      gap: planList(orthoShort) + ': orthodontia is covered at ' + orthoShort[0].orthodontia + '% but the lifetime maximum is ' + maxes.join(' / ') + '.',
      rec: 'Raise the orthodontia lifetime maximum to $1,500–$2,000',
      cost: '$3–$7', roi: 'At ' + maxes.join(' / ') + ' the benefit covers a fraction of a single course of treatment; it reads as a benefit but does not function as one.' });
  }

  /* Recommend only what is short. The Arlington Classics PPO pays basic
   * services at 80% already; asking to raise them "to 80%" was a
   * recommendation to change nothing, and the ROI line claimed the plan sat
   * below a benchmark it meets. */
  const maxShort = dental.filter(p => p.annualMax != null && p.annualMax < 1500);
  if (maxShort.length) {
    const basicShort = maxShort.some(p => p.basic != null && p.basic < 80);
    const who = maxShort.length === dental.length && dental.length > 1 ? 'both plans' : maxShort.map(p => p.plan).join(' and ');
    add({ priority: 'MODERATE', item: 'Dental — raise annual maximum',
      gap: maxShort.map(p => p.plan + ' at ' + (p.annualMaxText || ('$' + p.annualMax)) +
             (p.basic != null && p.basic < 80 ? ' with basic services at ' + p.basic + '%' : '')).join('; ') + '.',
      rec: 'Raise the annual maximum to $2,000' + (basicShort ? ' and basic services to 80%' : ''),
      cost: '$6–$12', roi: 'Market median is a $1,500 annual maximum with 80% basic; ' + who +
             (basicShort ? ' fall' + (/both|and/.test(who) ? '' : 's') + ' short on both.'
                         : ' fall' + (/both|and/.test(who) ? '' : 's') + ' short on the annual maximum only.') });
  }

  // --- Vision -----------------------------------------------------------
  const vf = tables && tables.vision ? ebVisionFields(tables.vision) : null;
  if (vf && vf.frames) {
    const fr = money(vf.frames.value);
    if (fr != null && fr < 150) {
      add({ priority: 'MODERATE', item: 'Vision — raise frame allowance',
        gap: 'Frame allowance is $' + fr.toLocaleString() +
             (vf.frames.outOfNetwork ? ' in-network (' + vf.frames.outOfNetwork + ' out-of-network)' : '') +
             (vf.frames.frequency ? ' every ' + vf.frames.frequency : '') +
             (/costco/i.test(vf.frames.value) ? ' (Costco $80)' : '') + '.',
        rec: '$200 frame allowance, retained 12-month exam frequency',
        cost: '$2–$5', roi: 'Low premium impact, high perceived value; the current allowance is below the $150 market benchmark.' });
    }
  }

  // --- Life -------------------------------------------------------------
  if (tables && tables.life) {
    const basic = money(tables.life.basicAmount);
    if (basic != null && basic < 50000) {
      add({ priority: 'HIGH', item: 'Basic Life / AD&D — increase',
        gap: 'Employer-paid basic life and AD&D is a flat ' + tables.life.basicAmount + '.' +
             (tables.life.ageReduction ? ' Reduces ' + tables.life.ageReduction + '.' : ''),
        rec: '1x annual salary, minimum $50,000, with the voluntary buy-up retained',
        cost: '$3–$8', roi: 'A flat ' + tables.life.basicAmount + ' is below the $50,000 market minimum and does not scale with pay.' });
    }
  }

  // --- Medical ----------------------------------------------------------
  /* Raised from the plans' own deductibles, against the same rule Section 6
   * scores (a plan at or under $3,000 with 20% or less coinsurance). Without
   * it Section 9 fell back to "enhance Medical to market median" priced at a
   * total-premium range, which is not the cost of any change. No cost is
   * invented: the figure depends on the carrier's quote. */
  const med = tables && tables.medical ? ebMedicalPlans(tables.medical) : [];
  const medScored = med.filter(p => money(p.deductibleIndividual) != null);
  if (medScored.length && !medScored.some(p => money(p.deductibleIndividual) <= 3000 && (ebPct(p.coinsurance) == null || ebPct(p.coinsurance) <= 20))) {
    const low = medScored.slice().sort((a, b) => money(a.deductibleIndividual) - money(b.deductibleIndividual))[0];
    const others = medScored.filter(p => p !== low);
    const zeroCo = medScored.filter(p => ebPct(p.coinsurance) === 0).map(p => p.plan);
    add({ priority: 'HIGH', item: 'Medical — bring a plan into the market deductible range',
      gap: 'Lowest individual deductible is ' + low.deductibleIndividual + ' (' + low.plan + ')' +
           (others.length ? '; ' + others.map(p => p.plan + ' ' + p.deductibleIndividual).join(', ') : '') +
           '. Market range is $1,500–$3,000.',
      rec: 'Offer one plan at a $2,000–$3,000 individual deductible, or fund an HRA/HSA that brings the effective deductible into range',
      cost: 'Carrier quote required',
      roi: 'Every plan offered starts with first-dollar exposure above the market range' +
           (zeroCo.length ? '; ' + zeroCo.join(' and ') + ' pay' + (zeroCo.length > 1 ? '' : 's') + ' 0% coinsurance after the deductible, which offsets part of it' : '') + '.' });
  }

  // --- Retirement / accounts -------------------------------------------
  if (lines && lines.retirement.present && !lines.retirement.matchStated) {
    add({ priority: 'HIGH', item: '401(k) — introduce an employer match',
      gap: 'The guide describes the 401(k) and its contribution limits but states no employer match.',
      rec: 'Introduce a 3–4% employer match with immediate or short-cliff vesting',
      cost: '$150–$400', roi: 'The single strongest retention lever in this package; verify against the plan document before presenting.' });
  }
  if (lines && lines.hsa.present && !lines.hsa.employerSeedStated) {
    /* The HSA-qualified plan's own deductible. Taking the highest of all the
     * plans printed $5,000 — the HMO and PPO figure — for an HSA PPO that is
     * $3,500. */
    const medPlans = tables && tables.medical ? ebMedicalPlans(tables.medical) : [];
    const hsaPlan = medPlans.find(p => /\bhsa\b|hdhp|high[\s\-]deductible/i.test(p.plan || ''));
    const hd = hsaPlan && money(hsaPlan.deductibleIndividual) != null ? money(hsaPlan.deductibleIndividual)
      : medPlans.map(p => money(p.deductibleIndividual)).filter(x => x != null).sort((a, b) => b - a)[0];
    add({ priority: 'HIGH', item: 'HSA — add an employer seed contribution',
      gap: 'An HSA is offered with the high-deductible plan but no employer contribution is stated.' +
           (hd ? ' The HSA plan carries a $' + hd.toLocaleString() + ' individual deductible.' : ''),
      rec: '$500–$1,000 annual employer HSA contribution',
      cost: '$40–$85', roi: 'Without a seed the full deductible is the employee\'s first-dollar exposure; a seed is tax-advantaged to both parties.' });
  }

  const severity = { CRITICAL: 0, HIGH: 1, MODERATE: 2 };
  return rows.sort((a, b) => (severity[a.priority] ?? 3) - (severity[b.priority] ?? 3));
}

// ---- Priority gap analysis (Section 9) -----------------------------------
// Section 9 built its list from four benchmark statuses and silently dropped a
// fifth: BELOW STANDARD was never a case, so every line the report had
// MEASURED and found short — an 11-week STD, a $130 frame allowance, a $15,000
// basic life — vanished between Section 6, which showed the shortfall, and
// Section 11, which recommended fixing it. The gap table listed only the lines
// nothing had been extracted for.
//
// It also quoted no numbers: "Below Market or Not Provided" for a plan whose
// own value the report already holds.
//
// Each row now carries the measured value, and says whether a recommendation
// in Section 11 already addresses it. A gap with no recommendation behind it
// is the one a broker must not miss, so it is flagged rather than left to be
// spotted by comparing two tables.
const EB_GAP_PRIORITY = {
  'CRITICAL GAP': 'CRITICAL',
  'HIGH GAP': 'HIGH',
  'BELOW STANDARD': 'HIGH',
  'MODERATE': 'MODERATE',
  'VERIFY': 'VERIFY'
};

// Which Section 11 enhancement, if any, speaks to this benefit line.
const EB_GAP_TO_ENHANCEMENT = [
  ['Long-Term Disability', /long[- ]term disability/i],
  ['Short-Term Disability', /short[- ]term disability/i],
  ['Dental', /^dental/i],
  ['Vision', /^vision/i],
  ['Life / AD&D', /life/i],
  ['401(k) / Retirement', /401\(k\)|403\(b\)|retirement/i],
  ['HSA / FSA', /\bHSA\b|\bFSA\b/i],
  ['Medical (Group Health)', /^medical/i]
];

// The plan's own numbers, for the "Current Status" column. Absent a parsed
// value the caller keeps its generic wording — an unmeasured line must not be
// dressed up as a measured one.
function ebMeasuredSummary(type, obj) {
  if (!obj || !obj._fromTables) return null;
  const best = (plans, fmt) => (plans || []).map(fmt).filter(Boolean).join('; ') || null;
  if (type === 'Medical (Group Health)')
    return best(obj.plans, p => p.deductibleIndividual ? p.plan + ': ' + p.deductibleIndividual + ' deductible' +
      (p.coinsurance ? ', ' + p.coinsurance + ' coinsurance' : '') : null);
  if (type === 'Dental')
    return best(obj.plans, p => p.annualMaxText ? p.plan + ': ' + p.annualMaxText + ' annual max' +
      (p.orthodontiaMax ? ', ortho lifetime ' + p.orthodontiaMax : '') : null);
  if (type === 'Vision') {
    const f = obj.fields || {};
    return f.frames && f.frames.value ? 'Frames: ' + f.frames.value + (f.frames.frequency ? ' every ' + f.frames.frequency : '') : null;
  }
  if (type === 'Life / AD&D')
    return obj.basicAmount ? 'Employer-paid basic life ' + obj.basicAmount : null;
  if (type === 'Short-Term Disability') {
    const bits = [obj.benefitsPaid, obj.benefitsEnd ? 'benefit period ' + obj.benefitsEnd : null,
                  obj.whoPays ? 'paid by ' + obj.whoPays.toLowerCase() : null].filter(Boolean);
    return bits.length ? bits.join('; ') : null;
  }
  if (type === '401(k) / Retirement')
    return (obj.planType || 'Retirement plan') + ' offered; ' +
      (obj.employerContribution ? 'employer contribution ' + obj.employerContribution
                                : 'no employer match or contribution stated in the guide');
  if (type === 'HSA / FSA') {
    if (!obj.hsa) return obj.fsa ? 'FSA offered' : null;
    return 'HSA offered; ' + (obj.hsa.employerContribution
      ? 'employer contribution ' + obj.hsa.employerContribution
      : 'no employer contribution stated in the guide');
  }
  return null;
}

function buildEBGaps(benchmarks, benefitsData, enhancements, opts) {
  const o = opts || {};
  const gaps = [];
  (benchmarks || []).forEach(b => {
    let priority = EB_GAP_PRIORITY[b.status];
    if (!priority) return;                                   // MEETS: not a gap
    if (priority === 'VERIFY' && b.type === 'Medical (Group Health)') return;

    const measured = ebMeasuredSummary(b.type, (benefitsData || {})[b.type]);
    const unread = (benefitsData || {})[b.type] && (benefitsData || {})[b.type]._unread ? (benefitsData || {})[b.type] : null;
    let currentStatus;
    if (b.gapStatus) currentStatus = b.gapStatus;
    // In the guide but not read: never "not found".
    else if (unread && !measured) currentStatus = ebUnreadText(unread);
    else if (measured) currentStatus = measured;
    else if (priority === 'CRITICAL' || priority === 'HIGH') currentStatus = 'Not found in uploaded documents';
    else currentStatus = 'Not found in uploaded documents — request the plan documents';

    const map = EB_GAP_TO_ENHANCEMENT.find(m => m[0] === b.type);
    // Match on the item's HEAD — the text before the em dash — not the whole
    // string: "Dental — raise orthodontia LIFEtime maximum" otherwise answers
    // for the Life / AD&D row.
    const match = map ? (enhancements || []).find(e => map[1].test(String(e.item).split('\u2014')[0])) : null;
    /* One priority per finding. Section 9 rated every BELOW STANDARD line HIGH
     * while Section 11 rated the same Dental and Vision gaps MODERATE — the
     * Vision row even read HIGH beside "Minor competitive gap". Where Section
     * 11 has assessed the gap, its priority is the one reported. */
    if (b.status === 'BELOW STANDARD' && match && match.priority) priority = match.priority;

    gaps.push({
      priority: priority,
      type: b.type,
      benchmarkStatus: b.status,
      currentStatus: currentStatus,
      measured: !!measured,
      exposure: o.exposure ? o.exposure(priority === 'VERIFY' ? 'MODERATE' : priority, b.type) : '',
      action: b.gapAction ? b.gapAction
        : (unread && !measured && priority === 'VERIFY') ? 'Read the ' + b.type + ' terms from the guide' + (unread.pages.length ? ' (p.' + unread.pages.join(', p.') + ')' : '') + ' and confirm against the benchmark'
        : match ? match.item + ' — ' + match.rec
        : priority === 'CRITICAL' ? 'Immediately propose and implement ' + b.type + ' coverage at competitive levels'
        : priority === 'VERIFY' ? 'Request current ' + b.type + ' plan documents (SPD, certificate, or schedule) for detailed analysis'
        : 'Recommend enhancing ' + b.type + ' to meet market median',
      premium: b.gapCost ? b.gapCost : match ? match.cost : (o.premium ? o.premium(b.type) : 'TBD upon review'),
      addressedBy: match ? match.item : null
    });
  });
  const order = { CRITICAL: 0, HIGH: 1, MODERATE: 2, VERIFY: 3 };
  // Within a priority, the gaps nothing has proposed a fix for come first.
  return gaps.sort((a, b) =>
    (order[a.priority] ?? 4) - (order[b.priority] ?? 4) ||
    (a.addressedBy ? 1 : 0) - (b.addressedBy ? 1 : 0));
}

// What is already in place — stated so the section does not recommend adding it.
function ebAlreadyInPlace(tables, lines) {
  const ok = [];
  if (!lines) return ok;
  if (lines.eap.present) ok.push('Employee Assistance Program');
  if (lines.fsa.present) ok.push('Healthcare and Dependent Care FSA');
  if (lines.voluntary.length) ok.push('Voluntary suite (' + lines.voluntary.join(', ') + ')');
  if ((tables && tables.life && tables.life.voluntary) || lines.supplementalLife) ok.push('Supplemental life buy-up for employee, spouse and children');
  if (lines.std.present) ok.push('Short-Term Disability' + (lines.std.employerPaid ? '' : ' (voluntary)'));
  if (lines.ltd.present) ok.push('Long-Term Disability' + (lines.ltdVoluntary ? ' (voluntary)' : ''));
  if (lines.telemedicine) ok.push('Telemedicine');
  if (lines.masa) ok.push('Emergency transportation (MASA)');
  return ok;
}

// ---- Rendering -----------------------------------------------------------
// Stable output-field id for one source row, so every line of the handbook's
// table has a named destination in the section rather than being dropped.
function ebFieldId(benefit, display) {
  return benefit + '.' + String(display)
    .toLowerCase()
    .replace(/—/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

function ebEsc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// One benefit block rendered as its own table: label column + one column per
// plan, in the guide's own row order.
function ebRenderBlock(block, title, benefitKey, note) {
  if (!block || !block.rows.length) return '';
  let h = '<h4 style="margin:16px 0 6px;color:#003366;">' + ebEsc(title) +
          (block.carrier ? ' <span style="font-weight:400;color:#555;font-size:13px;">— ' + ebEsc(block.carrier) + '</span>' : '') +
          '</h4>';
  h += '<table class="report-table"><thead><tr><th>Plan Feature</th>';
  block.columns.forEach(c => { h += '<th>' + ebEsc(c) + '</th>'; });
  h += '</tr></thead><tbody>';
  block.rows.forEach(r => {
    h += '<tr><td><strong>' + ebEsc(r.display) + '</strong></td>';
    block.columns.forEach((_c, i) => {
      const v = r.values[i];
      h += '<td>' + (v ? ebEsc(v) : '<em style="color:var(--text-muted);">—</em>') + '</td>';
    });
    h += '</tr>';
  });
  h += '</tbody></table>';
  if (note) h += '<p style="font-size:11px;color:var(--text-muted);margin:4px 0 0;">' + note + '</p>';
  return h;
}

// Life & AD&D: the basic benefit is stated in prose, the supplemental election
// in a table. Render both, and keep them distinct — the basic amount is what
// the benchmark scores, not the voluntary maximum an employee could buy.
function ebRenderLife(life) {
  if (!life) return '';
  let h = '<h4 style="margin:16px 0 6px;color:#003366;">Life &amp; AD&amp;D Insurance' +
          (life.carrier ? ' <span style="font-weight:400;color:#555;font-size:13px;">— ' + ebEsc(life.carrier) + '</span>' : '') + '</h4>';
  const basicRows = [
    ['Basic Life and AD&D Benefit', life.basicAmount],
    ['Employer Paid', life.employerPaid],
    ['AD&D', life.addIncluded],
    ['Age Reduction Schedule', life.ageReduction],
    ['Evidence of Insurability', life.evidenceOfInsurability]
  ].filter(r => r[1]);
  if (basicRows.length) {
    h += '<table class="report-table"><thead><tr><th>Plan Feature</th><th>Employer-Paid Basic Coverage</th></tr></thead><tbody>';
    basicRows.forEach(r => { h += '<tr><td><strong>' + ebEsc(r[0]) + '</strong></td><td>' + ebEsc(r[1]) + '</td></tr>'; });
    h += '</tbody></table>';
  }
  if (life.voluntary) {
    h += ebRenderBlock(life.voluntary, 'Supplemental (Voluntary) Life and AD&D — employee-paid', 'life.voluntary',
      'Transcribed as the source table is structured. Where the guide merges a cell across rows, the value is reported ' +
      'in the row the document places it in and is not reassigned.');
  }
  return h;
}

// Handbook row -> output field id, so it is auditable that nothing on the
// source table was silently dropped.
function ebFieldMap(tables) {
  const rows = [];
  const add = (key, block) => {
    if (!block) return;
    block.rows.forEach(r => {
      block.columns.forEach((c, i) => {
        rows.push({ benefit: key, source: r.display, plan: c, field: ebFieldId(key, r.display) + '[' + i + ']', value: r.values[i] || null });
      });
    });
  };
  add('medical', tables.medical);
  if (tables.medical && tables.medical.rx) add('medical.rx', tables.medical.rx);
  add('dental', tables.dental);
  add('vision', tables.vision);
  if (tables.life) {
    [['basicAmount','Basic Life and AD&D Benefit'], ['employerPaid','Employer Paid'], ['addIncluded','AD&D'],
     ['ageReduction','Age Reduction Schedule'], ['evidenceOfInsurability','Evidence of Insurability']].forEach(([k, label]) => {
      rows.push({ benefit: 'life', source: label, plan: 'Basic (employer-paid)', field: 'life.' + k, value: tables.life[k] || null });
    });
    add('life.voluntary', tables.life.voluntary);
  }
  return rows;
}

function ebRenderFieldMap(tables) {
  const rows = ebFieldMap(tables);
  if (!rows.length) return '';
  const mapped = rows.filter(r => r.value != null).length;
  let h = '<details style="margin-top:12px;"><summary style="cursor:pointer;font-size:12px;color:#01696F;font-weight:600;">' +
          'Field mapping — ' + mapped + ' of ' + rows.length + ' handbook values mapped to section output fields</summary>';
  h += '<table class="report-table" style="font-size:11px;margin-top:6px;"><thead><tr>' +
       '<th>Handbook row</th><th>Plan column</th><th>Output field</th><th>Value</th></tr></thead><tbody>';
  rows.forEach(r => {
    h += '<tr><td>' + ebEsc(r.source) + '</td><td>' + ebEsc(r.plan) + '</td><td><code>' + ebEsc(r.field) + '</code></td><td>' +
         (r.value ? ebEsc(r.value) : '<em>not stated</em>') + '</td></tr>';
  });
  h += '</tbody></table></details>';
  return h;
}

// The three plan tables, as the section renders them.
function renderEBPlanTables(tables) {
  if (!tables || (!tables.medical && !tables.dental && !tables.vision)) return '';
  let h = '<div style="margin:12px 0 18px;">';
  h += '<p style="font-size:12px;color:var(--text-muted);margin:0 0 4px;">Transcribed from the benefit guide’s own plan-summary tables. ' +
       'Each plan is reported in its own column; a value the guide does not state is left blank rather than inferred.</p>';
  if (tables.medical) {
    h += ebRenderBlock(tables.medical, 'Medical Plan Summary', 'medical');
    if (tables.medical.rx) h += ebRenderBlock(tables.medical.rx, 'Prescription Drug Benefits', 'medical.rx');
  }
  if (tables.dental) h += ebRenderBlock(tables.dental, 'Dental Benefits', 'dental');
  if (tables.vision) h += ebRenderBlock(tables.vision, 'Vision Benefits', 'vision');
  if (tables.life) h += ebRenderLife(tables.life);
  h += ebRenderFieldMap(tables);
  h += '</div>';
  return h;
}

// ---- Benchmark bridge ----------------------------------------------------
// The benchmark table and Section 8's gap analysis take one object per benefit
// type. Build those from the parsed tables so a two-plan guide is scored on the
// plan that actually performs best — and so fields the guide never states stay
// absent. The flat regex layer previously reported a "$10/mo" medical premium
// for a guide that quotes no premiums at all, and a "$25" deductible against a
// real $5,000 / $3,000.
// Pages of the guide that name a benefit, for "in the guide (p.X)" cells.
const EB_BENEFIT_WORDS = {
  'Medical (Group Health)': /\bmedical\b|\bdeductible\b|out[\s\-]of[\s\-]pocket/i,
  'Dental': /\bdental\b/i,
  'Vision': /\bvision\b/i,
  'Life / AD&D': /\blife insurance\b|\bbasic life\b|\bAD&D\b/i,
  'Short-Term Disability': /short[\s\-]term disability|\bSTD\b/i,
  'Long-Term Disability': /long[\s\-]term disability|\bLTD\b/i,
  '401(k) / Retirement': /401\s*\(?k\)?|403\s*\(?b\)?|457\s*\(?b\)?|retirement plan/i,
  'HSA / FSA': /health savings account|flexible spending|\bHSA\b|\bFSA\b/i
};
function ebBenefitPages(pdfTexts, type) {
  const re = EB_BENEFIT_WORDS[type];
  const pages = [];
  if (!re) return pages;
  (pdfTexts || []).forEach(doc => (doc.pages || []).forEach(p => {
    if (p && p.page && re.test(String(p.text || '')) && pages.indexOf(p.page) < 0) pages.push(p.page);
  }));
  return pages.slice(0, 3);
}
const ebUnreadText = obj => 'In the guide' + (obj && obj.pages && obj.pages.length ? ' (p.' + obj.pages.join(', p.') + ')' : '') +
  ' \u2014 figures not read; verify against the guide';

function ebBenefitsFromTables(tables) {
  const out = {};
  if (tables.medical) {
    out['Medical (Group Health)'] = {
      carrier: tables.medical.carrier, planNames: tables.medical.columns,
      plans: ebMedicalPlans(tables.medical),
      rxTiers: tables.medical.rx ? tables.medical.rx.rows.length * tables.medical.rx.columns.length : 0,
      rxSummary: tables.medical.rx ? ebRxSummary(tables.medical.rx) : null,
      _fromTables: true
    };
  }
  if (tables.dental) {
    out['Dental'] = {
      carrier: tables.dental.carrier, planNames: tables.dental.columns,
      plans: ebDentalPlans(tables.dental), _fromTables: true
    };
  }
  if (tables.vision) {
    out['Vision'] = {
      carrier: tables.vision.carrier, fields: ebVisionFields(tables.vision), _fromTables: true
    };
  }
  if (tables.life) out['Life / AD&D'] = Object.assign({}, tables.life, { _fromTables: true });
  if (tables.accounts) out['HSA / FSA'] = Object.assign({}, tables.accounts, { _fromTables: true });
  if (tables.retirement) out['401(k) / Retirement'] = Object.assign({}, tables.retirement, { _fromTables: true });
  // Only STD is published: LTD has no parsed detail to show, and publishing an
  // empty object would make hasEBData() true and print a card with nothing in
  // it. Its absence is carried by the row's status instead.
  if (tables.disability && tables.disability.std)
    out['Short-Term Disability'] = Object.assign({}, tables.disability.std, { _fromTables: true });
  // LTD read from a table is published the same way, so its row is cited as
  // extracted rather than "detected".
  if (tables.disability && tables.disability.ltd && tables.disability.ltd.present && tables.disability.ltd.benefitsPaid)
    out['Long-Term Disability'] = Object.assign({}, tables.disability.ltd, { _fromTables: true });
  return out;
}

// Status for a table-backed benefit: score every offered plan, report the best,
// and name the plan that earned it. An employer offering one market-meeting
// plan MEETS even if a cheaper option sits below — but both columns are on the
// table above, so the reader can see the spread.
function ebTableStatus(type, obj) {
  if (!obj || !obj._fromTables) return null;
  if (type === 'Dental') {
    let best = null;
    (obj.plans || []).forEach(p => {
      if (p.basic == null || p.major == null || p.annualMax == null) return;
      const meets = p.annualMax >= 1500 && p.basic >= 80 && p.major >= 50;
      if (meets && !best) best = { status: 'MEETS', plan: p.plan };
    });
    if (best) return best;
    const scored = (obj.plans || []).filter(p => p.basic != null && p.major != null && p.annualMax != null);
    if (!scored.length) return { status: 'VERIFY', plan: null };
    return { status: 'BELOW STANDARD', plan: scored.map(p => p.plan).join(' / ') };
  }
  if (type === 'Medical (Group Health)') {
    let best = null;
    (obj.plans || []).forEach(p => {
      const ded = ebDollar(p.deductibleIndividual), co = ebPct(p.coinsurance);
      if (ded == null) return;
      // A copay plan states no coinsurance; its absence is not a failure.
      if (ded <= 3000 && (co == null || co <= 20) && !best) best = { status: 'MEETS', plan: p.plan };
    });
    if (best) return best;
    const scored = (obj.plans || []).filter(p => ebDollar(p.deductibleIndividual) != null);
    if (!scored.length) return { status: 'VERIFY', plan: null };
    return { status: 'BELOW STANDARD', plan: scored.map(p => p.plan).join(' / ') };
  }
  if (type === 'Life / AD&D') {
    // Scored on the EMPLOYER-PAID basic benefit. Supplemental coverage the
    // employee buys at their own cost is not employer competitiveness.
    // A salary multiple of 1x or more meets "1x salary, $50K minimum".
    if (obj.basicMultiple != null) return obj.basicMultiple >= 1 ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
    const amt = ebDollar(obj.basicAmount);
    if (amt == null) return { status: 'VERIFY', plan: null };
    return amt >= 50000 ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
  }
  if (type === 'Short-Term Disability') {
    // Benchmark is 60% of salary over a 13-week benefit period. Score the
    // terms, not the presence of a table — an 11-week plan is short of the
    // benchmark whoever pays for it.
    const pct = ebPct(obj.benefitsPaid || '');
    const wk = /(\d{1,3})\s*week/i.exec(obj.benefitsEnd || '');
    if (pct == null && !wk) return { status: 'VERIFY', plan: null };
    const meets = (pct != null && pct >= 60) && (wk ? parseInt(wk[1], 10) >= 13 : false);
    return meets ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
  }
  if (type === '401(k) / Retirement') {
    // Benchmark is a 3% employer match, and this row is scored, never deferred:
    // a benefit guide that describes the plan in full and states no employer
    // money leaves the employee at 100% of their own retirement funding, which
    // is below the benchmark whatever the reason for the silence. The Section 6
    // cell still says "no employer match or contribution stated in the guide",
    // so the status is a measurement and the wording stays honest about what
    // the document does and does not say.
    const c = obj.employerContribution;
    if (!c) return { status: 'BELOW STANDARD', plan: null };
    const pct = ebPct(c);
    if (pct == null) return { status: 'MEETS', plan: null };
    return pct >= 3 ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
  }
  if (type === 'HSA / FSA') {
    // The benchmark is "HSA with HDHP, employer seed contribution". Offering
    // the account is not the same as funding it: an unfunded HSA leaves the
    // whole deductible as the employee's first-dollar exposure, so MEETS needs
    // a stated employer contribution and everything else is scored below it.
    // The cell says which of the two the guide shows.
    const hsa = obj.hsa;
    if (!hsa) return { status: 'BELOW STANDARD', plan: null };
    return hsa.employerContribution ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
  }
  if (type === 'Vision') {
    const f = obj.fields || {};
    // "80% of Cost over $150 (20% Discount)" is a discount, not a $150
    // allowance; it cannot be scored against an allowance benchmark.
    if (f.frames && /%\s*of\s+cost|discount/i.test(f.frames.value || '') && !/^\s*\$[\d,]+\s+allowance/i.test(f.frames.value || '')) return { status: 'VERIFY', plan: null };
    const frame = f.frames && ebDollar(f.frames.value);
    if (frame == null) return { status: 'VERIFY', plan: null };
    // Benchmark is a $150 frame allowance.
    return frame >= 150 ? { status: 'MEETS', plan: null } : { status: 'BELOW STANDARD', plan: null };
  }
  return null;
}

// Benchmark-table cell for a table-backed benefit: every plan, side by side.
function ebFormatFromTables(type, obj) {
  if (!obj || !obj._fromTables) return null;
  let h = '<div style="padding:6px 8px;background:#f0f7f7;border-radius:4px;border-left:3px solid #01696F;">';
  if (obj.carrier) h += '<strong>' + ebEsc(obj.carrier) + '</strong><br>';
  if (type === 'Life / AD&D') {
    if (obj.basicAmount) h += 'Basic Life and AD&D: <strong>' + ebEsc(obj.basicAmount) + '</strong><br>';
    if (obj.employerPaid) h += 'Employer paid: <strong>' + ebEsc(obj.employerPaid) + '</strong><br>';
    if (obj.ageReduction) h += 'Age reduction: <strong>' + ebEsc(obj.ageReduction) + '</strong><br>';
    if (obj.voluntary) h += '<span style="font-size:11px;color:#555;">Supplemental (employee-paid) election table above</span>';
    h += '</div>';
    return h;
  }
  if (type === 'Short-Term Disability' || type === 'Long-Term Disability') {
    // The carrier is already printed by the shared header above.
    h += '<strong>' + ebEsc(obj.planLabel) + '</strong><br>';
    [['Benefits paid', obj.benefitsPaid], ['Benefits begin', obj.benefitsBegin],
     ['Benefits end', obj.benefitsEnd], ['Pre-existing conditions', obj.preExisting],
     ['Who pays', obj.whoPays]].forEach(([lab, v]) => {
      if (v) h += ebEsc(lab) + ': <strong>' + ebEsc(v) + '</strong><br>';
    });
    if (obj.buyUp && obj.buyUp.benefitsPaid)
      h += 'Employee buy-up available: <strong>' + ebEsc(obj.buyUp.benefitsPaid) + '</strong>'
         + (obj.buyUp.source && obj.buyUp.source.page ? ' <span style="font-size:11px;color:#555;">(p.' + ebEsc(String(obj.buyUp.source.page)) + ')</span>' : '') + '<br>';
    if (obj.source && obj.source.page)
      h += '<span style="font-size:11px;color:#555;">From the guide\u2019s table, p.' + ebEsc(String(obj.source.page)) + '</span>';
    h += '</div>';
    return h;
  }
  if (type === '401(k) / Retirement') {
    h += '<strong>' + ebEsc(obj.planType || 'Retirement plan') + '</strong>'
       + (obj.recordkeeper ? ' — ' + ebEsc(obj.recordkeeper) : '') + '<br>';
    h += 'Employer contribution: <strong>' +
         (obj.employerContribution ? ebEsc(obj.employerContribution)
                                   : 'no employer match or contribution stated in the guide') + '</strong><br>';
    h += 'Eligibility: <strong>' + ebEsc(obj.eligibility) + '</strong>'
       + (obj.waitingPeriod ? '' : ' <span style="color:#555;">— no waiting period stated</span>') + '<br>';
    if (obj.vesting) h += 'Vesting: <strong>' + ebEsc(obj.vesting) + '</strong><br>';
    if (obj.employeeContributionLimit)
      h += '<span style="font-size:11px;color:#555;">Employee limit: ' + ebEsc(obj.employeeContributionLimit) + '</span><br>';
    h += '</div>';
    return h;
  }
  if (type === 'HSA / FSA') {
    const hsa = obj.hsa, fsa = obj.fsa;
    if (hsa) {
      h += '<strong>' + ebEsc(hsa.eligible) + '</strong>';
      if (hsa.requiresPlan) h += ' <span style="color:#555;">(' + ebEsc(hsa.requiresPlan) + ')</span>';
      h += '<br>';
      h += 'Employer contribution: <strong>' +
           (hsa.employerContribution ? ebEsc(hsa.employerContribution) : 'not stated in the guide') + '</strong><br>';
      if (hsa.employeeContributionLimit)
        h += '<span style="font-size:11px;color:#555;">Employee IRS limit: ' + ebEsc(hsa.employeeContributionLimit) + '</span><br>';
    }
    if (fsa) {
      h += (hsa ? '<div style="margin-top:4px;"></div>' : '') + '<strong>' + ebEsc(fsa.eligible) + '</strong><br>';
      (fsa.accounts || []).forEach(a => {
        h += ebEsc(a.name) + ': <strong>' + ebEsc(a.limit) + '</strong><br>';
      });
      if (fsa.carryover) h += '<span style="font-size:11px;color:#555;">Health Care FSA carryover: ' + ebEsc(fsa.carryover) + '</span><br>';
    }
    h += '</div>';
    return h;
  }
  if (type === 'Vision') {
    const f = obj.fields || {};
    [['Eye exam', f.exam], ['Contacts exam', f.contactsExam], ['Lenses', f.lenses], ['Frames', f.frames], ['Contacts', f.contacts]].forEach(([lab, v]) => {
      if (!v || !v.value) return;
      const notes = [v.frequency ? 'every ' + v.frequency : null, v.outOfNetwork ? 'out-of-network ' + v.outOfNetwork : null].filter(Boolean);
      h += ebEsc(lab) + ': <strong>' + ebEsc(v.value) + '</strong>' + (notes.length ? ' <span style="color:#666;">(' + ebEsc(notes.join('; ')) + ')</span>' : '') + '<br>';
    });
  } else {
    (obj.plans || []).forEach(p => {
      h += '<div style="margin-top:4px;"><strong>' + ebEsc(p.plan) + '</strong><br>';
      if (type === 'Dental') {
        if (p.deductible) h += 'Deductible: <strong>' + ebEsc(p.deductible) + '</strong><br>';
        if (p.servicesText) h += 'Services: <strong>' + ebEsc(p.servicesText) + '</strong><br>';
        if (p.preventive != null || p.basic != null || p.major != null)
          h += 'Schedule (P/B/M): <strong>' + [p.preventive, p.basic, p.major].map(x => x == null ? '?' : x + '%').join(' / ') + '</strong><br>';
        if (p.annualMaxText) h += 'Annual Max: <strong>' + ebEsc(p.annualMaxText) + '</strong><br>';
        if (p.orthodontia != null) h += 'Orthodontia: <strong>' + p.orthodontia + '%' + (p.orthodontiaMax ? ', lifetime ' + ebEsc(p.orthodontiaMax) : '') + '</strong><br>';
      } else {
        if (p.deductibleIndividual) h += 'Deductible: <strong>' + ebEsc(p.deductibleIndividual) + '</strong> ind. / <strong>' + ebEsc(p.deductibleFamily || '—') + '</strong> fam.<br>';
        if (p.coinsurance) h += 'Coinsurance: <strong>' + ebEsc(p.coinsurance) + '</strong><br>';
        if (p.oopMaxIndividual) h += 'OOP Max: <strong>' + ebEsc(p.oopMaxIndividual) + '</strong> ind. / <strong>' + ebEsc(p.oopMaxFamily || '—') + '</strong> fam.<br>';
        const copays = [['Office/Specialist', p.officeVisit], ['Urgent care', p.urgentCare], ['ER', p.emergencyRoom]].filter(c => c[1]);
        if (copays.length) h += 'Copays: ' + copays.map(c => ebEsc(c[0]) + ' <strong>' + ebEsc(c[1]) + '</strong>').join(' · ') + '<br>';
      }
      h += '</div>';
    });
    if (type === 'Medical (Group Health)' && obj.rxSummary)
      h += '<div style="margin-top:4px;font-size:11px;color:#555;">Rx (retail, preferred tiers): ' + ebEsc(obj.rxSummary) + '</div>';
    else if (type === 'Medical (Group Health)' && obj.rxTiers)
      h += '<div style="margin-top:4px;font-size:11px;color:#555;">Rx: ' + obj.rxTiers + ' tier values in the table above</div>';
  }
  h += '</div>';
  return h;
}

// ===== P&C FULL REVIEW =====
// ===== London-market / scheduled-asset property placements =================
//
// The ISO declarations extractor looks for Building, Business Personal
// Property, Business Income and a replacement-cost valuation. A London-market
// placement has none of those words. Base Power's property policy — the one
// covering 14,240 installed home batteries — states its cover as:
//
//     SUMS INSURED / LIMITS OF LIABILITY (100%):
//       SECTION 3 – OPERATIONAL ALL RISKS      USD 20,000,000
//       COMBINED NATURAL CATASTROPHE LIMIT     USD 20,000,000  (*Portfolio Limit)
//                                              *limit is an annual aggregate
//     DEDUCTIBLES:
//       SECTION 3 – OPERATIONAL ALL RISKS      USD 1,000,000 each and every occurrence
//     PREMIUM (100%): USD 274,120* Calculated at rate 0.154% x Total Insured Values
//       *Based on 14,240 units being installed … Unit per cost being USD 12,500
//
// Every number a broker needs is on page 2 in plain text, and the report said
// "limits not parsed". This reads that shape. The same lesson as the 5C
// declarations table: parse what the policy actually prints, rather than
// looking for the form you expected.
window.spExtractMarketPlacement = function(pdfTexts){
  const money = /(?:USD|US\$|\$)\s*([\d][\d,]{2,})/i;
  const out = {};
  (pdfTexts || []).forEach(doc => {
    const text = (doc.pages || []).map(p => p.text || '').join('\n');
    if (!text) return;
    // A Market Reform Contract, or a placement that talks in sums insured.
    if (!/market\s+reform\s+contract|sums?\s+insured\s*\/?\s*limits?\s+of\s+liability|umr\b/i.test(text)) return;

    const grab = (label, zoneRe, extra) => {
      const z = zoneRe.exec(text);
      if (!z) return null;
      const zone = text.slice(z.index, z.index + (extra || 700));
      const m = money.exec(zone);
      if (!m) return null;
      return { value: '$' + m[1], zone: zone };
    };

    // Sums insured, per section where the policy states sections.
    const sums = /sums?\s+insured\s*\/?\s*limits?\s+of\s+liability[^\n]*/i.exec(text);
    if (sums) {
      const rest = text.slice(sums.index, sums.index + 1600);
      const stop = /deductibles?\s*[:\n]|insurance\s+conditions|premium\s*\(100%\)/i.exec(rest.slice(40));
      const zone = stop ? rest.slice(0, stop.index + 40) : rest.slice(0, 900);
      const secRe = /(section\s+\d+\s*[–\-—]\s*[A-Z][A-Za-z \-/&]{3,40}?|combined\s+natural\s+catastrophe\s+limit|portfolio\s+limit)\s*\*?\s*(?:USD|US\$|\$)\s*([\d][\d,]{2,})/gi;
      let m;
      while ((m = secRe.exec(zone)) !== null) {
        const name = m[1].replace(/\s+/g, ' ').replace(/\s*[–\-—]\s*/g, ' — ').trim();
        out[name] = '$' + m[2];
      }
      if (!Object.keys(out).length) {
        const first = money.exec(zone);
        if (first) out['Sum insured'] = '$' + first[1];
      }
      // An annual aggregate that sits over a whole PORTFOLIO is not the same
      // promise as a per-occurrence limit, and the difference is the finding.
      if (/annual\s+aggregate/i.test(zone)) out._aggregateNote = 'Nat-cat limit is an annual aggregate';
      if (/portfolio\s+limit/i.test(zone)) out._portfolioNote = 'Shared across the insured portfolio, not per location';
    }

    const ded = grab('deductible', /deductibles?\s*[:\n]/i, 500);
    if (ded) {
      out.Deductible = ded.value
        + (/each\s+and\s+every\s+occurrence/i.test(ded.zone) ? ' each and every occurrence' : '');
    }

    // What the premium is rated on — the closest thing this placement has to a
    // property value, and the number to benchmark the limit against.
    const rate = /rate\s*([\d.]+%)\s*x\s*total\s+insured\s+values?/i.exec(text);
    const units = /based\s+on\s+([\d,]+)\s+units[^.]{0,80}?(?:USD|US\$|\$)\s*([\d][\d,]{2,})/i.exec(text);
    if (units) {
      const n = parseInt(units[1].replace(/,/g, ''), 10);
      const each = parseInt(units[2].replace(/,/g, ''), 10);
      if (isFinite(n) && isFinite(each)) {
        out._tivValue = n * each;
        out._tiv = '$' + (n * each).toLocaleString('en-US')
          + ' (' + n.toLocaleString('en-US') + ' units × $' + each.toLocaleString('en-US') + ')';
      }
    }
    if (rate) out._rating = 'Rated at ' + rate[1] + ' of total insured values';
    const prem = /premium\s*\(100%\)\s*:?\s*(?:USD|US\$|\$)\s*([\d][\d,]{2,})/i.exec(text);
    if (prem) out._premium = '$' + prem[1];
    out._source = doc.fileName || 'uploaded document';
  });
  return Object.keys(out).length ? out : null;
};

// ===== Derived data, with a recompute fallback =============================
// The analysis computes the derived layers once — declarations limits, policy
// terms, endorsements, state compliance, triggers — and hands them to the
// report on `formData`. Any path that skips that work leaves the field
// missing, and the analysis ERROR FALLBACK builds its formData without any of
// them. Each consumer then rendered an empty result as though it were a
// finding: Section 5C printed "trigger not parsed" over declarations that
// state the trigger plainly, and the same silent under-reporting was waiting
// in the limits, terms, endorsement and compliance sections.
//
// So every consumer asks for the data through here. Present and non-empty, it
// is used as-is; absent, it is computed from the documents that are right
// there; and where there is no document text, nothing is invented — the
// section still gets an empty result and says so in its own words.
window.spResolveDerived = function(d){
  if (!d) return d;
  const docs = d.pdfTexts || [];
  const hasText = docs.some(x => x && (((x.pages || []).some(p => (p.text || '').trim())) || (x.text || '').trim()));
  if (!hasText) return d;
  const out = Object.assign({}, d);
  const fill = (key, isEmpty, compute) => {
    try {
      if (isEmpty(out[key])) out[key] = compute();
    } catch (e) { /* a derived layer failing must never cost the report */ }
  };
  fill('declLimits', v => !v || !Object.keys(v).length,
       () => (typeof extractDeclarationsLimits === 'function') ? extractDeclarationsLimits(docs) : out.declLimits);
  // A London-market placement states its cover in sums insured, not in ISO
  // declarations fields, so it needs its own read and its own benchmark row.
  try {
    const mp = window.spExtractMarketPlacement ? window.spExtractMarketPlacement(docs) : null;
    if (mp) out.declLimits = Object.assign({}, out.declLimits || {}, { 'Property — Other': mp });
  } catch (e) { /* never at the cost of the report */ }
  fill('policyTerms', v => !v || !v.byFile || !v.byFile.length,
       () => (typeof extractPolicyTerms === 'function') ? extractPolicyTerms(docs) : out.policyTerms);
  fill('endorsements', v => !v || !v.totalDetected,
       () => (typeof extractEndorsements === 'function') ? extractEndorsements(docs) : out.endorsements);
  fill('triggers', v => !v || !Array.isArray(v.byCoverage) || !v.byCoverage.length,
       () => (typeof analyzeTriggers === 'function') ? analyzeTriggers(docs) : out.triggers);
  fill('stateCompliance', v => !v || !v.checks || !v.checks.length,
       () => (typeof runStateComplianceChecks === 'function')
         ? runStateComplianceChecks(docs, out.states || [], out.vertical || '', out.endorsements) : out.stateCompliance);
  fill('deepScanResults', v => !v,
       () => (typeof deepScanForDO === 'function') ? deepScanForDO(docs) : out.deepScanResults);
  fill('deepScanEOResults', v => !v,
       () => (typeof deepScanForEO === 'function') ? deepScanForEO(docs) : out.deepScanEOResults);
  return out;
};

/* ===== PUBLIC TRIAL: SECTIONS 1–4 ONLY =====
 *
 * Visitors from "See a live analysis" on sentripol.ai get a trial session
 * (apps/web/src/lib/accessGate.js, services/backend/src/trial.js). For them the
 * Full Review stops after section 4: the later sections are not generated at
 * all, and the report shows their headings over blurred placeholder lines. Not
 * generating them is the point — a section built and then blurred is still in
 * the page, in every export, and in the browser's inspector.
 *
 * Outside a trial session spIsTrialSession is absent or false and nothing here
 * runs, so every other report, and the single-file parity comparison, is
 * unchanged.
 */
function spTrialActive() {
  return typeof window !== 'undefined'
    && typeof window.spIsTrialSession === 'function'
    && window.spIsTrialSession() === true;
}

const SP_TRIAL_LOCKED_SECTIONS = {
  pc: [
    '5. Comprehensive Regulatory Sources Table',
    '5C. Trigger & Retro Date Verification',
    '6. Industry Benchmarking Analysis',
    '6B. Endorsements Layered on This Program',
    '7. Exclusion & Contradiction Analysis',
    '7C. Website & Operational Risk Analysis',
    '8. Federal Compliance Penalty Structure',
    '9. Priority Gap Analysis & Recommendations',
    '10. Summary of Total Exposure',
    '11. Cyber Risk Intelligence & Market Analysis',
    '12. Conclusion & Next Steps'
  ],
  eb: [
    '5. Comprehensive Regulatory Sources Table',
    '6. Benefits Competitiveness Analysis',
    '7. Exclusion & Contradiction Analysis',
    '7C. Website & Operational Risk Analysis',
    '8. Federal Compliance Penalty Structure',
    '9. Priority Gap Analysis & Recommendations',
    '10. Employee Retention Risk Assessment',
    '11. Recommended Enhancements with Cost per Employee',
    '12. Summary of Total Exposure',
    '13. Conclusion & Next Steps'
  ]
};

const SP_TRIAL_BOOK_A_CALL = 'https://calendly.com/dayna-sentripol';

/* The headings of the sections a trial does not get, each over placeholder
 * lines that are blurred in styles.css. The lines are empty elements, not
 * text, and the call to action is the only live content. */
function spTrialLockedSectionsHtml(kind) {
  const titles = SP_TRIAL_LOCKED_SECTIONS[kind] || SP_TRIAL_LOCKED_SECTIONS.pc;
  const lines = '<div class="sp-trial-lines" aria-hidden="true"><i style="width:92%"></i><i style="width:78%"></i><i style="width:85%"></i><i style="width:64%"></i><i style="width:88%"></i></div>';
  let html = '<div class="sp-trial-locked">';
  titles.forEach(t => {
    html += `<div class="report-section sp-trial-section"><h3>${escapeHtml(t)} <span class="sp-trial-lock" aria-label="Locked">&#128274;</span></h3>${lines}</div>`;
  });
  html += `<div class="sp-trial-cta"><div class="sp-trial-cta-card">
    <h4>${titles.length} more sections in the full analysis</h4>
    <p>Regulatory exposure, benchmarking, exclusions, gap analysis and recommendations for this program are part of every full SentriPol analysis.</p>
    <a class="btn btn-accent" href="${SP_TRIAL_BOOK_A_CALL}" target="_blank" rel="noopener noreferrer">Book a call to see the full analysis</a>
  </div></div>`;
  html += '</div>';
  return html;
}

/* What a trial session sees in the Quick Screen and Executive Board Summary
 * tabs. Both restate findings from sections 5 onward, so they are withheld
 * whole rather than generated. */
function spTrialLockedTabHtml(name) {
  return `<div class="report-section sp-trial-tab"><h3>${escapeHtml(name)} <span class="sp-trial-lock" aria-label="Locked">&#128274;</span></h3>
    <p>The ${escapeHtml(name)} is part of the full SentriPol analysis. This trial shows sections 1&ndash;4 of the Full Review.</p>
    <a class="btn btn-accent" href="${SP_TRIAL_BOOK_A_CALL}" target="_blank" rel="noopener noreferrer">Book a call to see the full analysis</a></div>`;
}

function generatePCFullReview(d) {
  d = window.spResolveDerived(d);
  const _spT0 = Date.now();
  const _spDone = () => {
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[perf] generate full review: ${Date.now() - _spT0} ms`);
    }
  };
  let _spAdvisoryPlaced = false;
  d.states = spCanonStates(d.states);
  const stateNames = d.states.map(spStateDisplayName).join(', ');
  
  // Extract actual coverage limits from declarations pages in uploaded PDFs.
  // Prefer what spResolveDerived() already computed: recomputing from scratch
  // discards its merges, and the scheduled-asset placement is one of them.
  const declLimits = (d.declLimits && Object.keys(d.declLimits).length)
    ? d.declLimits : extractDeclarationsLimits(d.pdfTexts || []);
  const carrierPerCoverage = extractCarrierPerCoverage(d.pdfTexts || [], d.files || []);
  const reviewLabel = 'BOR Analysis \u2014 Comprehensive Coverage Review';
  const today = new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'});

  let html = '';

  // Architecture v2: this is the one comprehensive BOR analysis — it proves the
  // review was done. The producer's remediation and the client's accept/decline
  // decisions live in the separate E&O Mitigation Record.
  html += `<div class="eo-badge" style="background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 28px;">\ud83d\udee1\ufe0f</span>
      <div>
        <div style="font-weight: 700; font-size: 15px; margin-bottom: 2px;">BOR Analysis — Documented Proof of Professional Diligence</div>
        <div style="font-size: 12px; opacity: 0.9;">This analysis is the documented record that the policy program was systematically reviewed. To record what was remediated and which recommended coverages the client accepted or declined, generate the E&O Mitigation Record from the button at the top of this report.</div>
      </div>
    </div>`;

  // --- 1. Organization Profile ---
  html += `<div class="report-section">
    <h3>1. Organization Profile</h3>
    <p><strong>Policyholder:</strong> ${escapeHtml(d.policyholderName)} &nbsp;|&nbsp; <strong>LOB:</strong> Property & Casualty &nbsp;|&nbsp; <strong>Review Type:</strong> ${reviewLabel}</p>
    <p><strong>State(s):</strong> ${stateNames} &nbsp;|&nbsp; <strong>Industry:</strong> ${escapeHtml(d.vertical)} &nbsp;|&nbsp; <strong>Date:</strong> ${today}</p>
    ${d.incumbent ? `<p><strong>Incumbent Carrier/Broker:</strong> ${escapeHtml(d.incumbent)}</p>` : ''}
    ${d.orgOverview ? d.orgOverview.split('\n\n').map(p => `<p>${escapeHtml(p)}</p>`).join('') : `<p>${escapeHtml(d.policyholderName)} is a ${escapeHtml(d.vertical)} organization operating in ${stateNames}. A comprehensive overview was not provided for this analysis.</p>`}
  </div>`;

  // --- v2.20.12: Policy Term Verification ---
  // Brokers verify all policies on a program share the same renewal date. Mismatches
  // create coverage gaps at renewal and are a common error to flag.
  if (d.policyTerms && d.policyTerms.byFile && d.policyTerms.byFile.length > 0) {
    const pt = d.policyTerms;
    function fmtDate(iso) {
      if (!iso) return '—';
      const d2 = new Date(iso + 'T00:00:00');
      return d2.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    const renewalDate = pt.programRenewalDate;
    const today = new Date();
    const renewal = renewalDate ? new Date(renewalDate + 'T00:00:00') : null;
    const daysToRenewal = renewal ? Math.round((renewal - today) / (1000 * 60 * 60 * 24)) : null;
    let renewalNote = '';
    if (daysToRenewal !== null) {
      if (daysToRenewal < 0) renewalNote = ` <span style="color:#B91C1C;font-weight:600;">(expired ${Math.abs(daysToRenewal)} days ago)</span>`;
      else if (daysToRenewal <= 60) renewalNote = ` <span style="color:#B45309;font-weight:600;">(${daysToRenewal} days away)</span>`;
      else if (daysToRenewal <= 120) renewalNote = ` <span style="color:#1F6FEB;">(${daysToRenewal} days away)</span>`;
    }

    html += `<div class="report-section">
      <h3>1B. Policy Term &amp; Program Renewal</h3>
      <p><strong>Program Renewal Date:</strong> ${fmtDate(renewalDate)}${renewalNote}</p>`;

    if (!pt.allMatch) {
      html += `<div style="background:#FEF3C7;border-left:4px solid #F59E0B;padding:10px 14px;margin:10px 0;border-radius:4px;">
        <strong>⚠️ Renewal Date Mismatch Detected.</strong> Not all uploaded policies share the same expiration date. This can create coverage gaps at renewal. Verify the broker's intent (deliberate split program vs. misalignment).
      </div>`;
    } else if (pt.byFile.length > 1) {
      html += `<div style="background:#D1FAE5;border-left:4px solid #10B981;padding:10px 14px;margin:10px 0;border-radius:4px;">
        <strong>✓ All policies share the same renewal date.</strong> Program is aligned for a single renewal cycle.
      </div>`;
    }

    html += `<table class="data-table" style="margin-top:10px;">
      <thead><tr><th>Document</th><th>Effective Date</th><th>Expiration / Renewal Date</th><th>Status</th></tr></thead><tbody>`;
    pt.byFile.forEach(f => {
      const isMatch = f.expirationDate === renewalDate;
      const statusBadge = isMatch
        ? '<span class="status-badge status-meets">✓ Aligned</span>'
        : '<span class="status-badge status-below">⚠ Mismatch</span>';
      html += `<tr><td>${escapeHtml(f.fileName)}</td><td>${fmtDate(f.effectiveDate)}</td><td>${fmtDate(f.expirationDate)}</td><td>${statusBadge}</td></tr>`;
    });
    html += `</tbody></table>`;

    html += `<p style="font-size:12px;color:#6B7280;margin-top:8px;"><em>Source: extracted from Policy Period / Effective Date fields on each policy's declarations page.</em></p>
    </div>`;
  }

  // --- 2. Policies Reviewed ---
  html += `<div class="report-section"><h3>2. Policies Reviewed</h3>`;
  if (d.files.length > 0) {
    if (d.detectedCoverages.length > 0) {
      html += `<div class="coverage-summary"><h4>Detected Coverage Types</h4><div class="coverage-tags">`;
      d.detectedCoverages.forEach(c => {
        // Flag Property if detected but no limits extracted
        const isPropNoLimits = (c === 'Property' || c === 'Commercial Property') && !spHasRealLimits(declLimits['Commercial Property']);
        if (isPropNoLimits) {
          html += `<span class="coverage-tag" style="background:#FFF3CD; border-color:#FFC107; color:#856404;">⚠️ ${c} (No Limits Found)</span>`;
        } else {
          html += `<span class="coverage-tag">✅ ${c}</span>`;
        }
      });
      html += `</div></div>`;
    }
    html += `<table class="report-table"><thead><tr><th>Document</th><th>Size</th><th>Detected Coverage</th><th>Status</th></tr></thead><tbody>`;
    const unrecognizedReport = detectUnrecognizedFiles(d.files);
    const unrecNames = new Set(unrecognizedReport.map(u => u.file.name));
    d.files.forEach(f => {
      // Detect from filename
      const fileNameCoverages = detectCoverages([f]);
      // Also detect from PDF text content for this specific file
      const matchingPdf = (d.pdfTexts || []).find(p => p.fileName === f.name);
      const pdfTextCovs = matchingPdf ? detectCoveragesFromPdfText([matchingPdf]) : new Set();
      // Merge both sources into a deduplicated set
      const allCovs = new Set([...fileNameCoverages, ...pdfTextCovs]);
      const fc = Array.from(allCovs);
      const unrec = unrecognizedReport.find(u => u.file.name === f.name);
      // Was the document actually read? An employee-benefits guide carries no
      // P&C coverage part, so it will never match a coverage pattern — but it
      // parsed cleanly and the benefits analysis ran on it. Reporting that as
      // "Coverage Not Recognized / MANUAL REVIEW" tells the broker the document
      // was not handled, which is false.
      const parsedPages = matchingPdf ? (matchingPdf.totalPages || (matchingPdf.pages || []).length || 0) : 0;
      const parsedChars = matchingPdf ? (matchingPdf.pages || []).reduce((n, pg) => n + ((pg.text || '').length), 0) : 0;
      if (fc.length > 0) {
        html += `<tr><td>${escapeHtml(f.name)}</td><td>${formatBytes(f.size)}</td><td>${fc.join(', ')}</td><td><span class="badge badge-meets">✅ ANALYZED</span></td></tr>`;
      } else if (parsedChars > 0) {
        html += `<tr><td>${escapeHtml(f.name)}</td><td>${formatBytes(f.size)}</td>`
             +  `<td>Parsed and analysed — ${parsedPages} page(s), ${parsedChars.toLocaleString()} characters`
             +  `<br><em style="font-size:10px;">No P&amp;C coverage part in this document; content was used for the benefits and benchmark analysis.</em></td>`
             +  `<td><span class="badge badge-meets">✅ ANALYZED</span></td></tr>`;
      } else if (unrec && unrec.suggestions.length > 0) {
        html += `<tr style="background:var(--warning-bg);"><td>${escapeHtml(f.name)}</td><td>${formatBytes(f.size)}</td><td>💡 Possible: ${unrec.suggestions.join(', ')}<br><em style="font-size:10px;">Heuristic match — broker verification required</em></td><td><span class="badge badge-verify">⚠️ VERIFY</span></td></tr>`;
      } else {
        html += `<tr style="background:var(--critical-bg);"><td>${escapeHtml(f.name)}</td><td>${formatBytes(f.size)}</td><td>Not parsed — no text could be read</td><td><span class="badge badge-critical">❓ MANUAL REVIEW</span></td></tr>`;
      }
    });
    html += `</tbody></table>`;
    // The "Coverage Detection Note" banner that followed this table was
    // removed at the broker team's request: each unrecognised document is
    // already flagged in its own row above (Possible: … / VERIFY).
  } else {
    html += `<p><em>No policy documents were uploaded. Analysis is based on organizational profile and industry benchmarks only. This review should be considered <strong>Preliminary</strong>.</em></p>`;
  }
  html += `</div>`;

  // --- 2B. Policy Program Completeness Check ---
  if (d.files.length > 0) {
    const vlCheck = d.vertical.toLowerCase();
    const isEduCheck = vlCheck.includes('school') || vlCheck.includes('education') || vlCheck.includes('daycare') || vlCheck.includes('charter');
    const isHealthCheck = vlCheck.includes('health') || vlCheck.includes('hospital') || vlCheck.includes('medical') || vlCheck.includes('nursing') || vlCheck.includes('behavioral');
    const isConstructionCheck = vlCheck.includes('construct') || vlCheck.includes('contractor') || vlCheck.includes('builder');
    
    // Build expected coverage lines for this vertical.
    //
    // v2.23.4: EPLI and Professional Liability are functionally substitutable for the
    // "employment-acts and wrongful-acts" exposure across most verticals. Education
    // typically uses Educators Legal Liability (which embeds EPLI). Construction/A&E,
    // healthcare, and consulting use Professional Liability/E&O. Real estate uses
    // Property Managers PL. Treating EPLI as a hard requirement created a confusing
    // "EPLI Not Found" alert on policies that actually carry equivalent coverage under
    // a different name. The merged line accepts ANY of: EPLI, Professional Liability,
    // E&O, ELL, or vertical-specific PL forms.
    let expectedLines = [
      { name: 'Commercial Property', keys: ['Commercial Property', 'Property', 'Package Policy'] },
      { name: 'General Liability', keys: ['General Liability'] },
      { name: 'Commercial Auto', keys: ['Commercial Auto'] },
      { name: 'Workers Compensation', keys: ['Workers Compensation'] },
      { name: 'Umbrella / Excess', keys: ['Umbrella/Excess'] },
      { name: 'Cyber Liability', keys: ['Cyber Liability'] },
      { name: 'EPLI or Professional Liability / E&O', keys: ['EPLI', 'Professional Liability', 'E&O', 'Educators Legal Liability', 'ELL', 'Property Managers Professional Liability', 'Medical Malpractice'] },
      { name: 'D&O / Management Liability', keys: ['D&O'] },
      { name: 'Crime / Fidelity', keys: ['Crime/Fidelity'] }
    ];
    if (isEduCheck) {
      expectedLines.push({ name: 'Educators Legal Liability (ELL)', keys: ['Educators Legal Liability (ELL)', 'ELL'] });
      expectedLines.push({ name: 'Sexual Abuse / Molestation', keys: ['Sexual Abuse/Molestation'] });
      expectedLines.push({ name: 'Active Shooter / Violent Event', keys: ['Active Shooter'] });
      expectedLines.push({ name: 'Student Accident Insurance', keys: ['Student Accident Insurance'] });
    }
    if (isHealthCheck) {
      expectedLines.push({ name: 'Medical Malpractice / Professional Liability', keys: ['Professional Liability', 'Medical Malpractice'] });
    }
    if (isConstructionCheck) {
      expectedLines.push({ name: 'Builders Risk', keys: ['Builders Risk'] });
      expectedLines.push({ name: 'Professional Liability / E&O', keys: ['Professional Liability'] });
    }
    const isRealEstateCheck = vlCheck.includes('real estate') || vlCheck.includes('property management') || vlCheck.includes('residential development') || vlCheck.includes('reit') || vlCheck.includes('landlord');
    if (isRealEstateCheck) {
      expectedLines.push({ name: 'Property Managers Professional Liability', keys: ['Property Managers Professional Liability'] });
      expectedLines.push({ name: 'Environmental / Mold Liability', keys: ['Environmental / Mold Liability'] });
      expectedLines.push({ name: 'Loss of Rents / Rental Income', keys: ['Loss of Rents / Rental Income'] });
      expectedLines.push({ name: 'Ordinance or Law Coverage', keys: ['Ordinance or Law Coverage'] });
    }
    
    const foundLines = [];
    const missingLines = [];
    expectedLines.forEach(el => {
      const detected = el.keys.some(k => 
        d.detectedCoverages.includes(k) || 
        d.detectedCoverages.some(dc => dc.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(dc.toLowerCase()))
      );
      if (detected) { foundLines.push(el); } else { missingLines.push(el); }
    });

    // v2.22 (broker rule): Section 2B must reconcile with Section 6.
    // If Commercial Property is "detected" by filename/coverage tag but no verified dollar
    // limits exist on the declarations page, Section 5 will flag it as a HIGH/CRITICAL GAP.
    // Section 2B must agree — reclassify as NOT FOUND with a Critical Alert.
    const hasPropertyLimits2B = spHasRealLimits(declLimits && declLimits['Commercial Property']);
    let section2BCriticalAlert = null;
    if (!hasPropertyLimits2B) {
      const propIdx = foundLines.findIndex(fl => fl.name === 'Commercial Property');
      if (propIdx > -1) {
        const propLine = foundLines.splice(propIdx, 1)[0];
        missingLines.unshift(propLine);
        section2BCriticalAlert = 'Commercial Property was referenced in uploaded documents but no verified dollar limits could be extracted from the declarations page. Section 5 (Industry Benchmarking) reports this as a HIGH GAP — Section 2B is reconciled accordingly. Treat as NOT FOUND until the property declarations are obtained and verified.';
      }
    }
    
    const completionPct = Math.round((foundLines.length / expectedLines.length) * 100);
    const completionColor = completionPct >= 80 ? 'var(--success, #28a745)' : completionPct >= 60 ? 'var(--warning, #ffc107)' : 'var(--critical, #dc3545)';
    
    /* Publish what 2B concluded, for the E&O Mitigation Record.
     *
     * The record reproduces this section, and it used to recover the numbers by
     * reading the rendered HTML back off the page. That worked only in a real
     * browser and could not be tested at all, so the one place it mattered was
     * the one place nothing checked. The counts are right here; stash them. */
    window._spSection2B = {
      expected: expectedLines.length,
      detected: foundLines.length,
      missing: missingLines.map(ml => ml.name),
      criticalAlert: section2BCriticalAlert || null
    };
    html += `<div class="report-section"><h3>2B. Policy Program Completeness Assessment</h3>`;
    html += `<p>Based on the <strong>${escapeHtml(d.vertical)}</strong> vertical, SentriPol\u2122 expects the following coverage lines in a complete insurance program. This check identifies whether all expected policies were uploaded for review.</p>`;
    html += `<div style="display:flex; align-items:center; gap:16px; margin:12px 0;">`;
    html += `<div style="font-size:36px; font-weight:700; color:${completionColor};">${completionPct}%</div>`;
    html += `<div><strong>${foundLines.length} of ${expectedLines.length}</strong> expected coverage lines detected`;
    if (missingLines.length > 0) {
      html += `<br><span style="color:var(--critical, #dc3545); font-weight:600;">${missingLines.length} coverage line(s) not found in uploaded documents</span>`;
    }
    html += `</div></div>`;
    
    if (section2BCriticalAlert) {
      html += `<div style="background:#FEE2E2; border-left:4px solid #B91C1C; padding:12px 16px; margin:12px 0; border-radius:0 6px 6px 0;">`;
      html += `<strong style="color:#B91C1C;">⚠️ CRITICAL ALERT — Commercial Property:</strong> <span style="font-size:12px;">${section2BCriticalAlert}</span>`;
      html += `</div>`;
    }
    if (missingLines.length > 0) {
      html += `<div style="background:#FFF5F5; border-left:4px solid var(--critical, #dc3545); padding:12px 16px; margin:12px 0; border-radius:0 6px 6px 0;">`;
      html += `<strong style="color:var(--critical, #dc3545);">Not Detected in Uploaded Policies:</strong><ul style="margin:8px 0 0 0;">`;
      missingLines.forEach(ml => {
        html += `<li>${ml.name}</li>`;
      });
      html += `</ul>`;
      html += `<p style="font-size:12px; margin:8px 0 0 0;"><strong>Action Required:</strong> Verify whether these coverages exist on separate policies not included in this review, or whether they have been omitted from the program. Each missing line represents a potential uninsured exposure.</p>`;
      html += `</div>`;
    }
    
    html += `<table class="report-table" style="margin-top:12px;"><thead><tr><th>Expected Coverage Line</th><th>Status</th></tr></thead><tbody>`;
    foundLines.forEach(fl => {
      html += `<tr><td>${fl.name}</td><td><span class="badge badge-meets">\u2705 DETECTED</span></td></tr>`;
    });
    missingLines.forEach(ml => {
      html += `<tr style="background:var(--critical-bg, #fff5f5);"><td><strong>${ml.name}</strong></td><td><span class="badge badge-critical">\u274c NOT FOUND</span></td></tr>`;
    });
    html += `</tbody></table>`;
    html += `<p style="font-size:11px; color:var(--text-muted); margin-top:8px;"><em>Note: This assessment is based on coverage detection from uploaded policy documents. Coverages may exist on policies not included in this review. The broker should verify the completeness of the uploaded document set. [Program Completeness Check, Sec. 2B]</em></p>`;
    // Always the last item in Expected Coverages, never gated on whether
    // anything was flagged above it.
    html += window.spAdvisoryCardHtml('pc', escapeHtml);
    html += `</div>`;
    _spAdvisoryPlaced = true;
  }
  // 2B renders only when documents were uploaded. The recommendation does not
  // depend on that, so it stands alone when there is no assessment.
  if (!_spAdvisoryPlaced) {
    html += `<div class="report-section">` + window.spAdvisoryCardHtml('pc', escapeHtml) + `</div>`;
  }

  // The "Renewal Comparison — Prior Year Policy Not Included" panel is
  // retired. It fired on every analysis with a document attached, said the
  // same thing each time, and carried no finding from this policy — a
  // standing notice, not an analysis result.

  // --- 3. Auto-Detected Profile & NAICS Vertical Analysis ---
  // The panel bodies are rendered later, by spRenderProfileCard / spRenderNaicsPanel,
  // which run after the report HTML is in the DOM (they depend on the async profile
  // and NAICS analyses). This is the host they mount into, so the numbering below is
  // stable whether or not those analyses returned anything.
  html += `<div class="report-section" id="spSection3Host"><h3>3. Auto-Detected Profile &amp; NAICS Vertical Analysis</h3>`;
  html += `<div id="spSection3Body"><p style="font-size:12px; color:var(--text-muted);"><em>Profile and NAICS classification are still resolving \u2014 if this text remains, neither the uploaded documents nor the website produced a usable classification.</em></p></div>`;
  html += `</div>`;

  // --- 4. State-Specific Catastrophic Event Risk Analysis ---
  html += `<div class="report-section"><h3>4. State-Specific Carrier & Catastrophic Event Risk Analysis</h3>`;
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (!sp) return;
    const sevClass = sp.severity === 'EXTREME' ? 'severity-extreme' : sp.severity === 'HIGH' ? 'severity-high' : sp.severity === 'MODERATE' ? 'severity-moderate' : 'severity-low';
    html += `<div class="state-card">
      <div class="state-card-header ${sevClass}">
        <span>${sp.name} (${sp.abbr})</span>
        <span class="badge" style="background:rgba(255,255,255,0.2);color:#fff;">${sp.severity} RISK</span>
      </div>
      <div class="state-card-body">
        <div class="state-field"><div class="state-field-label">Primary Catastrophe</div><div class="state-field-value">${sp.primaryCatastrophe}</div></div>
        <div class="state-field"><div class="state-field-label">State Trust / FAIR Plan</div><div class="state-field-value">${sp.stateTrust}</div></div>
        <div class="state-field"><div class="state-field-label">Trust Risk Assessment</div><div class="state-field-value">${sp.trustRisk}</div></div>
        <div class="state-field"><div class="state-field-label">Historical Events</div><div class="state-field-value">${sp.historicalEvents.join('; ')}</div></div>
        <div class="state-field"><div class="state-field-label">Assessment Risk</div><div class="state-field-value">${sp.assessmentRisk}</div></div>
        <div class="state-field"><div class="state-field-label">Coverage Concerns</div><div class="state-field-value">${sp.coverageConcerns}</div></div>
        <div class="state-field"><div class="state-field-label">Recommendation</div><div class="state-field-value"><strong>${sp.recommendation}</strong></div></div>
        <div style="font-size:11px;color:var(--text-muted);font-style:italic;margin-top:6px;padding-top:6px;border-top:1px solid var(--border-light);">[State Risk Analysis, Sec. 4: "${sp.name} — ${sp.severity} severity; ${sp.primaryCatastrophe}; Trust: ${sp.stateTrust}"]</div>
      </div>
    </div>`;
  });
  html += `</div>`;

  // Trial sessions stop here; see spTrialLockedSectionsHtml.
  if (spTrialActive()) {
    html += spTrialLockedSectionsHtml('pc');
    _spDone();
    return html;
  }

  // --- 4. Comprehensive Regulatory Sources Table ---
  html += `<div class="report-section"><h3>5. Comprehensive Regulatory Sources Table</h3>`;
  html += `<table class="report-table"><thead><tr><th>Source</th><th>Jurisdiction</th><th>Relevance to ${escapeHtml(d.vertical)}</th><th>URL / Reference</th><th>Last Updated</th></tr></thead><tbody>`;
  // State DOI rows
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (!sp) return;
    html += `<tr><td>${sp.regulator || sp.name + ' Department of Insurance'}</td><td>State — ${sp.abbr}</td><td>Primary insurance regulatory authority; filing requirements, rate approvals, consumer complaints</td><td>${sp.regulatorSite || 'doi.' + sp.abbr.toLowerCase() + '.gov'}</td><td>Current</td></tr>`;
  });
  // Education-specific
  const isEducation = d.vertical.toLowerCase().includes('school') || d.vertical.toLowerCase().includes('education') || d.vertical.toLowerCase().includes('daycare');
  if (isEducation) {
    d.states.forEach(abbr => {
      const sp = STATE_PROFILES[abbr]; if (!sp) return;
      html += `<tr><td>${sp.name} Department of Education</td><td>State — ${sp.abbr}</td><td>Education-specific regulatory requirements, safety standards, reporting obligations</td><td>doe.${sp.abbr.toLowerCase()}.gov</td><td>Current</td></tr>`;
    });
  }
  // Federal sources
  const fedSources = [
    ["OSHA", "Federal", "Workplace safety standards, violation penalties, reporting requirements", "osha.gov", "Current"],
    ["EEOC", "Federal", "Employment discrimination enforcement, Title VII compliance, penalty structures", "eeoc.gov", "Current"],
    ["ADA (Americans with Disabilities Act)", "Federal", "Disability accommodation requirements, accessibility standards, civil rights compliance", "ada.gov", "Current"],
    ["Insurance Information Institute (III)", "Federal / Industry", "Industry benchmarking data, loss statistics, emerging risk trends", "iii.org", "Current"],
    ["NAIC", "Federal / Industry", "Model laws and regulations, carrier financial data, market conduct standards", "naic.org", "Current"]
  ];
  if (isEducation) {
    fedSources.unshift(
      ["FERPA", "Federal", "Student records privacy, parental rights, data breach notification requirements", "ed.gov/ferpa", "Current"],
      ["Title IX", "Federal", "Sex-based discrimination prohibition, sexual harassment policies, athletic equity", "ed.gov/titleix", "Current"],
      ["NAPCS (National Alliance for Public Charter Schools)", "Federal / Industry", "Charter-specific governance, accountability standards, best practices", "publiccharters.org", "Current"],
      ["NSBA (National School Boards Association)", "Federal / Industry", "Governance standards, policy development, risk management best practices", "nsba.org", "Current"]
    );
  }
  fedSources.forEach(r => {
    html += `<tr><td>${r[0]}</td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td><td>${r[4]}</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 4B. State Compliance Checklist (v2.22) ---
  // Per-state operator-grade checks that surface as actionable broker tasks.
  if (d.stateCompliance && d.stateCompliance.checks && d.stateCompliance.checks.length > 0) {
    const sc = d.stateCompliance;
    const stateNameLookup = { TX: 'Texas', OK: 'Oklahoma', NY: 'New York', CA: 'California', FL: 'Florida', LA: 'Louisiana', ND: 'North Dakota' };
    sc.checks.forEach(stateCheck => {
      const stateName = stateNameLookup[stateCheck.state] || stateCheck.state;
      const verifiedCount = stateCheck.items.filter(c => c.status === 'VERIFIED').length;
      const gapCount = stateCheck.items.filter(c => c.status === 'GAP').length;
      const verifyCount = stateCheck.items.filter(c => c.status === 'VERIFY').length;
      const warnCount = stateCheck.items.filter(c => c.status === 'WARN').length;
      const totalItems = stateCheck.items.length;

      html += `<div class="report-section">
        <h3>5B. ${stateName} State Compliance Checklist</h3>
        <p>SentriPol™ evaluated ${totalItems} ${stateName}-specific compliance check${totalItems !== 1 ? 's' : ''} based on the uploaded policies, detected endorsements, and the organization's risk profile. Each check identifies a state-specific compliance item brokers are expected to verify.</p>`;

      html += `<div style="display:flex;gap:12px;margin:10px 0;flex-wrap:wrap;">
        <span style="background:#D1FAE5;color:#047857;padding:4px 10px;border-radius:12px;font-size:11px;font-weight:600;">✓ Verified: ${verifiedCount}</span>
        ${gapCount > 0 ? `<span style="background:#FEE2E2;color:#B91C1C;padding:4px 10px;border-radius:12px;font-size:11px;font-weight:600;">Gaps: ${gapCount}</span>` : ''}
        ${warnCount > 0 ? `<span style="background:#FEF3C7;color:#B45309;padding:4px 10px;border-radius:12px;font-size:11px;font-weight:600;">Warnings: ${warnCount}</span>` : ''}
        ${verifyCount > 0 ? `<span style="background:#DBEAFE;color:#1E40AF;padding:4px 10px;border-radius:12px;font-size:11px;font-weight:600;">Verify: ${verifyCount}</span>` : ''}
      </div>`;

      html += `<table class="data-table" style="margin-top:8px; font-size:12px;">
        <thead><tr>
          <th>Compliance Check</th>
          <th>Status</th>
          <th>Why It Matters</th>
          <th>Broker Action</th>
        </tr></thead><tbody>`;
      stateCheck.items.forEach(c => {
        let statusBadge;
        if (c.status === 'VERIFIED') statusBadge = '<span class="badge badge-meets">✓ VERIFIED</span>';
        else if (c.status === 'GAP') statusBadge = '<span class="badge badge-critical">⚠️ GAP</span>';
        else if (c.status === 'WARN') statusBadge = '<span class="badge badge-high">! WARN</span>';
        else statusBadge = '<span class="badge badge-verify">VERIFY</span>';
        html += `<tr>
          <td><strong>${escapeHtml(c.check)}</strong></td>
          <td>${statusBadge}</td>
          <td style="font-size:12px;">${escapeHtml(c.relevance)}</td>
          <td style="font-size:12px;">${escapeHtml(c.action)}</td>
        </tr>`;
      });
      html += `</tbody></table>`;
      html += `<p style="font-size:11px;color:var(--text-muted);margin-top:8px;"><em>Source: SentriPol™ ${stateName} compliance layer. Checks are based on state insurance code, common-law indemnity rules, and post-event regulatory updates. Broker should independently verify against current statutes.</em></p>`;
      html += `</div>`;
    });
  }

  // ============================================================================
  // --- 5C. Trigger & Retro Date Verification (v2.23) ---
  // ============================================================================
  // Per broker direction (May 6, 2026): every liability policy needs explicit
  // trigger language. Claims-Made requires a Retro Date AND an ERP option.
  // Occurrence just needs the word "Occurrence". Across all claims-made policies
  // for one insured, retro dates should align.
  //
  // The section is ALWAYS rendered once documents are uploaded. It used to
  // render only when a trigger had been parsed, so on a program where the
  // scan found none the section simply vanished — indistinguishable from a
  // program that has no claims-made coverage, and the one case where the
  // broker most needs to be told to go and look.
  {
    // The scan is re-run here when the report data carries no result. The
    // analysis pipeline computes `triggers` once, and any path that skips it —
    // the error fallback builds its formData without the field at all — used
    // to print "trigger not parsed" over documents that DO declare one. Run
    // directly on the Base Power set this scan returns three coverage parts,
    // claims-made, retro date 11/03/2025; the report was showing "not parsed"
    // because it never got the answer, not because there was none.
    let t = (d.triggers && Array.isArray(d.triggers.byCoverage)) ? d.triggers : null;
    const scanned = (d.pdfTexts || []).filter(x => x && ((x.pages || []).some(p => (p.text || '').trim()) || (x.text || '').trim()));
    if ((!t || !t.byCoverage.length) && scanned.length && typeof analyzeTriggers === 'function') {
      try { t = analyzeTriggers(d.pdfTexts); } catch (e) { /* keep whatever we had */ }
    }
    t = t || { byCoverage: [], retroDates: [] };
    /* A ROW WITH NO TRIGGER HAS NOTHING TO VERIFY.
     *
     * This section verifies triggers and retroactive dates. A coverage part
     * whose trigger the reader could not establish can report neither, so it
     * occupied a row that said "not determined" three times and pushed the
     * rows carrying findings further down.
     *
     * It is withheld from the TABLE, not from the report. A declared coverage
     * part that simply disappeared is the defect the roster reader was written
     * to fix — Item 4 names seven parts and the report used to show two — so
     * the ones held back are listed underneath, with the document and page
     * that declares them. Nothing the policy declares goes unmentioned; it
     * just stops pretending to be a verification result. */
    const shown = t.byCoverage.filter(f => f.trigger);
    const withheld = t.byCoverage.filter(f => !f.trigger);
    const parsed = shown.length > 0;
    html += `<div class="report-section">`;
    html += `<h3>5C. Trigger & Retro Date Verification</h3>`;
    html += `<p style="font-size:13px; color:#555; margin:6px 0 12px 0;">Every liability policy must declare a trigger — <strong>Claims-Made</strong> or <strong>Occurrence</strong>. Claims-Made coverage requires a Retroactive Date and an Extended Reporting Period (ERP/Tail) option.</p>`;

    // Misalignment banner
    if (t.misalignmentFlag) {
      html += `<div style="background:#FEF3C7; border-left:4px solid #F59E0B; padding:10px 14px; margin:10px 0 14px 0; border-radius:4px;">`;
      html += `<strong style="color:#92400E;">⚠️ Retro Date Misalignment</strong> — Multiple claims-made policies were detected with different retroactive dates: <strong>${t.retroDates.map(escapeHtml).join('  /  ')}</strong>. Across all claims-made coverage on a single insured, retro dates should align.`;
      html += `</div>`;
    }

    if (!parsed) {
      html += `<div style="background:#FEF3C7; border-left:4px solid #F59E0B; padding:10px 14px; margin:10px 0; border-radius:4px; font-size:13px;">`;
      html += `<strong style="color:#92400E;">\u26a0\ufe0f Trigger not parsed \u2014 verify against the declarations.</strong> `;
      html += !(d.files && d.files.length)
        ? `No policy documents were uploaded, so no trigger could be read. Upload the declarations pages to populate this section.`
        : !scanned.length
        ? `No text could be read from the ${d.files.length} uploaded document(s) — a scanned PDF needs OCR before the declarations can be searched. Re-upload a text PDF, or confirm the trigger from the declarations directly.`
        : `No explicit Claims-Made or Occurrence trigger was located in the ${scanned.length} document(s) that were read. Confirm the trigger on every liability coverage part, and for any claims-made part obtain the retroactive date and the ERP/tail option in writing.`;
      html += `</div>`;
    }

    // Build the table
    if (parsed) {
    html += `<table style="width:100%; border-collapse:collapse; font-size:12px; margin-top:8px;">`;
    html += `<thead><tr style="background:var(--primary); color:white;">`;
    html += `<th style="text-align:left; padding:8px 10px;">Coverage Part</th>`;
    html += `<th style="text-align:left; padding:8px 10px;">Trigger</th>`;
    html += `<th style="text-align:left; padding:8px 10px;">Retroactive Date / Prior-Acts Anchor</th>`;
    html += `<th style="text-align:left; padding:8px 10px;">ERP / Tail</th>`;
    html += `<th style="text-align:left; padding:8px 10px;">Source</th>`;
    html += `</tr></thead><tbody>`;

    let alt = false;
    /* One schedule, shown once. The Named Insured Schedule applies to every
     * claims-made part of its policy, so on Ohio Eastern Star the same block
     * repeated under GL, PL, EBL and Abuse/Molestation. Where two or more rows
     * share it, it moves to a single note under the table, naming the parts. */
    const _spNIKey = f => f.fileName + '|' + JSON.stringify(f.retroByNamedInsured);
    const _spNIGroups = new Map();
    shown.forEach(f => {
      if (!f.retroByNamedInsured || !f.retroByNamedInsured.length) return;
      const k = _spNIKey(f);
      if (!_spNIGroups.has(k)) _spNIGroups.set(k, []);
      _spNIGroups.get(k).push(f);
    });
    const _spSharedNI = new Set();
    _spNIGroups.forEach(rows => { if (rows.length >= 2) rows.forEach(f => _spSharedNI.add(f)); });

    shown.forEach(f => {
      // Trigger cell
      let triggerCell;
      if (f.trigger === 'Claims-Made') {
        triggerCell = `<span style="background:#DBEAFE; color:#1E40AF; padding:2px 8px; border-radius:3px; font-weight:600;">Claims-Made</span>`;
      } else if (f.trigger === 'Occurrence') {
        triggerCell = `<span style="background:#D1FAE5; color:#065F46; padding:2px 8px; border-radius:3px; font-weight:600;">Occurrence</span>`;
      } else if (f.trigger === 'Discovery' || f.trigger === 'Loss Sustained') {
        // A crime form is neither occurrence nor claims-made. Forcing it into
        // that binary is what put "Crime / Fidelity — Claims-Made" on this
        // report, and behind it a retro-date gap for a form that cannot have
        // a retroactive date.
        triggerCell = `<span style="background:#EDE9FE; color:#5B21B6; padding:2px 8px; border-radius:3px; font-weight:600;">${escapeHtml(f.trigger)}</span>`;
      } else {
        triggerCell = `<span style="background:#FEE2E2; color:#991B1B; padding:2px 8px; border-radius:3px; font-weight:600;">⚠️ NOT STATED</span>`;
      }
      // Where the trigger came from the policy's own forms schedule, cite the
      // form. "Occurrence (CG 00 01)" is checkable; "Occurrence" is a claim.
      if (f.triggerSource) {
        triggerCell += ` <span style="color:#6B7280; font-size:11px;">${escapeHtml(String(f.triggerSource).replace(/^([A-Z]+)(\d{2})(\d{2})$/, '$1 $2 $3'))}</span>`;
      }

      // Retro cell — v2.23.1: Per broker direction, retro date IS required for claims-made.
      // No more soft "new business may have none" carve-out. If it's claims-made and we
      // didn't detect a retro date, flag it.
      /* One switch over spResolvePriorActsAnchor's closed set. Every arm
       * states which of the three questions it is answering — applicability,
       * content, or legibility — so no two states can share a label. */
      const anchor = spResolvePriorActsAnchor(f);
      /* The citation travels with the evidence. The Source column cites where
       * the coverage part was matched; this anchor is frequently on another
       * page, and a broker checking the cited page would not find it. */
      const cite = anchor.page && anchor.page !== f.page
        ? ` <span style="color:#9CA3AF; font-weight:400; font-size:11px;">(p.${anchor.page})</span>` : '';
      const sub = txt => `<div style="color:#6B7280; font-size:11px; font-weight:400;">${txt}</div>`;
      const asLabelled = lbl => (lbl && !/^retro(active)? date$/i.test(lbl))
        ? ` <span style="color:#6B7280; font-weight:400; font-size:11px;">(stated as &ldquo;${escapeHtml(lbl)}&rdquo;)</span>` : '';
      let retroCell;
      switch (anchor.status) {
        case 'stated':
          retroCell = `<span style="font-weight:600;">${escapeHtml(anchor.value)}</span>${asLabelled(anchor.label)}${cite}`;
          if (f.retroByLocation && new Set(f.retroByLocation.map(r => r.date)).size > 1) {
            const byDate = new Map();
            f.retroByLocation.forEach(r => { if (!byDate.has(r.date)) byDate.set(r.date, []); byDate.get(r.date).push(r.location); });
            retroCell = `<span style="font-weight:600;">By location</span>${cite}`
              + Array.from(byDate.entries()).map(([dt, locs]) =>
                  `<div style="font-size:12px;"><strong>${escapeHtml(dt)}</strong> <span style="color:#6B7280;">&mdash; ${locs.map(escapeHtml).join('; ')}</span></div>`).join('')
              + sub((f.retroReplaced && f.retroReplaced.length)
                  ? 'Per-location schedule replaces the retroactive date stated elsewhere in the policy: '
                    + f.retroReplaced.map(r => escapeHtml(r.date) + (r.pages.length ? ' (p.' + r.pages.join(', p.') + ')' : '')).join('; ') + '.'
                  : 'Per-location schedule replaces the single retroactive date shown elsewhere in the policy.');
          }
          if (/continuity/i.test(f.retroLabel || '')) {
            retroCell += sub((f.retroFieldNA ? 'Retroactive Date field reads N/A; the' : 'The')
              + ' Continuity Date is this coverage\u2019s retro date.'
              + (f.retroKnowledgeOnly ? ' Not compared with other policies\u2019 retro dates for alignment.' : ''));
          }
          if (f.priorProceedings && f.priorProceedings.value) {
            retroCell += sub(escapeHtml(f.priorProceedings.label || 'Prior or Pending Date') + ': <strong>' + escapeHtml(f.priorProceedings.value) + '</strong>'
              + (f.priorProceedings.page && f.priorProceedings.page !== anchor.page ? ' (p.' + f.priorProceedings.page + ')' : '')
              + ' — claims arising from proceedings pending on that date are excluded.');
          }
          break;
        case 'full-prior-acts':
          retroCell = `<span style="color:#065F46; font-weight:600;">Full Prior Acts</span>`;
          break;
        case 'equivalent':
          // The policy anchors prior acts, just not with a retroactive date.
          // Naming the carrier's own mechanism is what makes it checkable.
          retroCell = `<span style="color:#065F46; font-weight:600;">No retroactive date &mdash; prior acts anchored by ${escapeHtml(anchor.label)}</span>${cite}`
            + sub((anchor.value ? `<strong>${escapeHtml(anchor.value)}</strong>. ` : '') + escapeHtml(anchor.note));
          break;
        case 'cross-reference':
          retroCell = `<span style="color:#92400E; font-weight:600;">↪ Stated by reference</span>`
            + sub(`${escapeHtml(anchor.label)}: &ldquo;${escapeHtml(anchor.value)}&rdquo; — follow the reference to confirm the date.`);
          break;
        case 'blank':
          retroCell = `<span style="color:#92400E; font-weight:600;">⚠️ ${escapeHtml(anchor.label)} left blank</span>`
            + sub(escapeHtml(anchor.note));
          break;
        case 'absent':
          retroCell = `<span style="color:#991B1B; font-weight:600;">⚠️ No prior-acts anchor stated</span>`
            + sub(escapeHtml(anchor.note));
          break;
        case 'not-applicable':
          retroCell = `<span style="color:#9CA3AF;">N/A &mdash; ${escapeHtml(anchor.reason)}</span>`;
          break;
        case 'unknown-trigger':
          // NOT "N/A". The applicability of this field is exactly what is
          // unknown, and saying N/A claims it was checked and cleared.
          retroCell = `<span style="color:#92400E; font-weight:600;">Not determined &mdash; reader could not establish the trigger</span>`
            + sub(escapeHtml(anchor.note));
          break;
        case 'unreadable':
          retroCell = `<span style="color:#92400E; font-weight:600;">Not readable from this extraction</span>`
            + sub(escapeHtml(anchor.note));
          break;
        default:
          retroCell = `<span style="color:#9CA3AF;">&mdash;</span>`;
      }
      if (f.retroBySubCoverage && f.retroBySubCoverage.length) {
        const sc = f.retroBySubCoverage;
        retroCell += `<div style="margin-top:4px;"><span style="font-weight:600; color:#374151;">Claims-made umbrella coverages</span>${sc[0].page ? ` <span style="color:#9CA3AF; font-size:11px;">(p.${sc[0].page})</span>` : ''}`
          + sc.map(r => `<div style="font-size:12px;">${escapeHtml(r.coverage)}: <strong>${escapeHtml(r.date)}</strong>`
              + (r.underlying && r.underlying.date !== r.date
                  ? ` <span style="color:#92400E; font-weight:600;">⚠️ underlying ${escapeHtml(r.underlying.part)} is ${escapeHtml(r.underlying.date)}</span>` : '')
              + `</div>`).join('')
          + (sc.some(r => r.underlying && r.underlying.date !== r.date)
              ? `<div style="color:#92400E; font-size:11px;">Where the umbrella's retro date is later than the underlying, acts in between are covered below but not above.</div>` : '')
          + `</div>`;
      }
      if (f.continuity) {
        const cc = f.continuity;
        retroCell += `<div style="margin-top:4px; font-size:12px;"><strong>${escapeHtml(cc.value)}</strong> <span style="color:#6B7280;">(stated as &ldquo;Continuity Date&rdquo;)${cc.page ? ' (p.' + cc.page + ')' : ''}</span>`
          + `<div style="color:#6B7280; font-size:11px;">Continuity Date (alternative name for the retro date) stated beside the retroactive date above.</div>`
          + `</div>`;
      }
      if (f.retroByNamedInsured && f.retroByNamedInsured.length && !_spSharedNI.has(f)) {
        const byDate = new Map();
        f.retroByNamedInsured.forEach(r => { if (!byDate.has(r.date)) byDate.set(r.date, []); byDate.get(r.date).push(r.namedInsured); });
        const niDates = Array.from(byDate.keys());
        const otherDates = f.retroByLocation ? Array.from(new Set(f.retroByLocation.map(r => r.date)))
          : (f.retroDate && !f.isFullPrior ? [f.retroDate] : []);
        const differs = otherDates.length && (niDates.length !== otherDates.length || niDates.some(x => !otherDates.includes(x)));
        const niCite = f.retroByNamedInsuredPage ? ` <span style="color:#9CA3AF; font-weight:400; font-size:11px;">(p.${f.retroByNamedInsuredPage})</span>` : '';
        retroCell += `<div style="margin-top:6px; padding-top:4px; border-top:1px dashed #E5E7EB;"><span style="font-weight:600;">By named insured</span>${niCite}`
          + Array.from(byDate.entries()).map(([dt, names]) =>
              `<div style="font-size:12px;"><strong>${escapeHtml(dt)}</strong> <span style="color:#6B7280;">&mdash; ${names.map(escapeHtml).join('; ')}</span></div>`).join('')
          + (differs
              ? `<div style="color:#92400E; font-size:11px; font-weight:600;">⚠️ Differs from the ${f.retroByLocation ? 'location' : 'stated'} retroactive date${otherDates.length > 1 ? 's' : ''} above. Confirm with the carrier which governs a claim.</div>`
              : `<div style="color:#6B7280; font-size:11px;">Named Insured Schedule retroactive dates; they apply to every claims-made part of this policy.</div>`)
          + `</div>`;
      }

      // ERP cell — only matters for claims-made
      let erpCell;
      if (f.trigger === 'Discovery' || f.trigger === 'Loss Sustained') {
        // The form states how long a loss can still be discovered; say it.
        const dp = f.discoveryPeriod;
        erpCell = dp
          ? `<span style="color:#374151;">Discovery period: <strong>${escapeHtml(dp.term)}</strong> ${escapeHtml(dp.basis)}${dp.planTerm ? ` (${escapeHtml(dp.planTerm)} for employee benefit plans)` : ''}</span>`
            + (dp.page ? ` <span style="color:#9CA3AF; font-size:11px;">(p.${dp.page})</span>` : '')
            + `<div style="color:#6B7280; font-size:11px;">No ERP on a ${escapeHtml(String(f.trigger).toLowerCase())} form.</div>`
          : `<span style="color:#6B7280;">Extended period to discover loss &mdash; verify term</span>`;
      } else if (!f.trigger) {
        // Same rule as the retro cell: an unread trigger leaves applicability
        // open, and "N/A" would claim it was settled.
        erpCell = `<span style="color:#92400E;">Not determined &mdash; trigger not read</span>`;
      } else if (f.trigger !== 'Claims-Made') {
        erpCell = `<span style="color:#9CA3AF;">N/A &mdash; ${escapeHtml(f.trigger === 'Occurrence' ? 'occurrence form' : String(f.trigger).toLowerCase())}</span>`;
      } else if (f.hasERP) {
        // "✓ Detected" is thin for a term that has a length, a price and a
        // deadline. Where the policy states them adjacently, print them.
        const e = f.erp || null;
        const bits = [];
        if (e && e.duration) bits.push(escapeHtml(e.duration));
        if (e && e.premiumPct != null) bits.push('@ ' + e.premiumPct + '% of premium');
        const term = bits.length ? ` <span style="color:#374151; font-weight:600;">${bits.join(' ')}</span>` : '';
        const named = e && e.label && !/^extended reporting period$/i.test(e.label)
          ? ` <span style="color:#6B7280; font-size:11px;">(${escapeHtml(e.label)})</span>` : '';
        erpCell = `<span style="color:#065F46;">✓ Detected</span>${term}${named}`;
      } else {
        erpCell = `<span style="color:#991B1B; font-weight:600;">⚠️ Verify ERP option</span>`;
      }

      const bg = alt ? '#F9FAFB' : 'white';
      html += `<tr style="background:${bg}; border-bottom:1px solid #E5E7EB;">`;
      html += `<td style="padding:8px 10px; font-weight:600;">${escapeHtml(f.coveragePart)}</td>`;
      html += `<td style="padding:8px 10px;">${triggerCell}</td>`;
      html += `<td style="padding:8px 10px;">${retroCell}</td>`;
      html += `<td style="padding:8px 10px;">${erpCell}</td>`;
      html += `<td style="padding:8px 10px; color:#6B7280; font-size:11px;">${escapeHtml(f.fileName)} <span style="color:#9CA3AF;">(p.${f.page})</span></td>`;
      html += `</tr>`;
      alt = !alt;
    });
    html += `</tbody></table>`;
    _spNIGroups.forEach(rows => {
      if (rows.length < 2) return;
      const f0 = rows[0];
      const byDate = new Map();
      f0.retroByNamedInsured.forEach(r => { if (!byDate.has(r.date)) byDate.set(r.date, []); byDate.get(r.date).push(r.namedInsured); });
      const niDates = Array.from(byDate.keys());
      const differs = rows.some(f => {
        const other = f.retroByLocation ? Array.from(new Set(f.retroByLocation.map(r => r.date)))
          : (f.retroDate && !f.isFullPrior && !f.retroNotApplicable ? [f.retroDate] : []);
        return other.length && (niDates.length !== other.length || niDates.some(x => !other.includes(x)));
      });
      html += `<div style="background:#FFFBEB; border-left:3px solid #F59E0B; padding:8px 12px; margin-top:10px; font-size:12px; color:#374151;">`
        + `<strong>Named Insured Schedule</strong>${f0.retroByNamedInsuredPage ? ` <span style="color:#9CA3AF;">(${escapeHtml(f0.fileName)} p.${f0.retroByNamedInsuredPage})</span>` : ''} `
        + `also sets retroactive dates per named insured for ${rows.map(f => escapeHtml(f.coveragePart)).join(', ')}:`
        + Array.from(byDate.entries()).map(([dt, names]) =>
            `<div><strong>${escapeHtml(dt)}</strong> <span style="color:#6B7280;">&mdash; ${names.map(escapeHtml).join('; ')}</span></div>`).join('')
        + (differs ? `<div style="color:#92400E; font-weight:600; margin-top:2px;">⚠️ These differ from the retroactive dates in the table above. Confirm with the carrier which governs a claim.</div>` : '')
        + `</div>`;
    });
    }

    if (withheld.length) {
      html += `<div style="background:#F9FAFB; border-left:3px solid #D1D5DB; padding:8px 12px; margin-top:10px; font-size:12px; color:#4B5563;">`
        + `<strong>Declared but not verified here:</strong> `
        + withheld.map(f => `${escapeHtml(f.coveragePart)} <span style="color:#9CA3AF;">(${escapeHtml(f.fileName)} p.${f.page})</span>`).join('; ')
        + `. The reader could not establish a trigger for ${withheld.length === 1 ? 'this part' : 'these parts'}, so there is nothing to verify above. `
        + `That is a limit of the parse, not a statement that the policy is silent — confirm from the declarations.`
        + `</div>`;
    }

    html += `<p style="font-size:11px; color:#6B7280; margin-top:10px; font-style:italic;">SentriPol reports the trigger and retroactive date as detected. The broker confirms whether the retro date is acceptable based on prior acts, claim history, and renewal context.</p>`;
    html += `</div>`;
  }

  // --- 5. Industry Benchmarking Analysis ---
  html += `<div class="report-section"><h3>6. Industry Benchmarking Analysis</h3>`;
  html += `<p>The following table benchmarks ${escapeHtml(d.policyholderName)}'s insurance program against statutory minimums, industry standards, and current coverages/limits (extracted from policy declarations) for the <strong>${escapeHtml(d.vertical)}</strong> sector.</p>`;

  // Enhanced coverage detection: checks detectedCoverages array AND extracted declarations limits
  // This ensures a coverage found via ANY method (filename, PDF text scan, or limit parsing) is recognized
  // The coverages the policy's own FORMS SCHEDULE attaches.
  //
  // A benchmark row that says CRITICAL GAP is telling the broker to go and buy
  // something. Before saying it, look at what the policy schedules. Aspen View
  // schedules GCG 74 10 Sexual Misconduct Liability Coverage (Occurrence) with
  // $1,000,000 each claim and $3,000,000 aggregate, and PCP 73 57 Equipment
  // Breakdown Coverage. Both were reported as missing, because the benchmark
  // rows are named "Sexual Abuse / Molestation" and the matcher compared
  // strings.
  const _CT = (typeof window !== 'undefined' && window.spCoverageTerms) || null;
  const scheduledCoverages = (() => {
    const set = new Set();
    if (!_CT) return set;
    (d.pdfTexts || []).forEach(doc => {
      const txt = ((doc.pages || []).map(p => p.text || '').join('\n')) || doc.text || '';
      if (!txt) return;
      const RA = (typeof window !== 'undefined' && window.spRetroAnchor) || null;
      // Titles only. A coverage NAMED in an exclusion or an applicability list
      // is not a coverage the policy provides — the same rule Section 5C
      // needed — so only the forms schedule's own titles are read.
      const titles = RA ? (RA.readFormsSchedule(txt) || []).map(f => f.title) : [];
      titles.forEach(t => _CT.coveragesIn(t).forEach(c => set.add(c)));
    });
    return set;
  })();

  /* THE POLICY'S OWN ANSWER, WHERE THERE IS ONE.
   *
   * Tulsa Honor Academy's Equipment Breakdown row read "Coverage detected —
   * limits not parsed from declarations", with the ledger naming the auto
   * policy, whose only two mentions of Equipment Breakdown are inside ISO
   * endorsement boilerplate listing every coverage part the endorsement could
   * modify. The property policy declines it three times:
   *
   *     Equipment Breakdown     No Coverage     per Occurrence
   *
   * and attaches CC EQB 004 04/2025, an Equipment Breakdown Exclusion.
   *
   * That row sent a broker to look for a limit on a policy that says there
   * isn't one, and it hid a real gap behind a parsing complaint. A declination
   * is a finding, not a failure.
   *
   * Only 'declined' and 'excluded' are allowed to overturn anything. 'silent'
   * means this reader found nothing, which is not evidence of absence — the
   * same trap the limits gate had to be pulled back from.
   */
  const _stanceCache = new Map();
  const stanceFor = (type) => {
    if (typeof spCoverageStance !== 'function') return null;
    if (!_stanceCache.has(type)) {
      let st = null;
      try { st = spCoverageStance(d.pdfTexts || [], type); } catch (_e) { st = null; }
      _stanceCache.set(type, st);
    }
    return _stanceCache.get(type);
  };
  const refusedFor = (type) => {
    const st = stanceFor(type);
    return st && (st.stance === 'declined' || st.stance === 'excluded') ? st : null;
  };
  const grantedFor = (type) => {
    const st = stanceFor(type);
    return st && st.stance === 'granted' && st.quote ? st : null;
  };

  // Did any uploaded document actually contain a schedule for this coverage?
  //
  // Aspen View's student accident row read "Coverage detected — limits not
  // parsed from declarations", which sends a broker to re-read a declarations
  // page that is not in the file. The uploaded PDF is nine pages of
  // endorsements and notices — Administrative Change, Colorado Civil Union,
  // Sanctions Exclusion, Privacy Notice, guaranty association — and carries no
  // Schedule of Benefits at all. The only dollar figures in it are the
  // premium and the guaranty association's statutory caps.
  //
  // "We read the schedule and could not parse it" and "no schedule page was
  // uploaded" are different findings with different actions, and the second
  // one is the answer here. Telling the broker to request the schedule is
  // useful; telling them to re-read a page that does not exist is not.
  // A HEADING, not the word in a sentence.
  //
  // The student accident PDF's only match was a privacy-notice sentence
  // defining the term: "The term policy may be comprised of common policy
  // terms and conditions, the declarations, notices, schedule, coverage
  // forms..." That page also carries dollar figures — the guaranty
  // association's caps — so a page-level keyword test called it a schedule
  // page and the row went back to saying "limits not parsed".
  //
  // A declarations or benefit schedule announces itself on a line of its own.
  // Requiring the match to sit on a short or all-caps line, with an amount
  // following it, is the same structural rule that separated a coverage-part
  // heading from an endorsement's applicability list.
  const _SP_SCHEDULE_WORD = /\b(?:declarations?|schedule\s+of\s+(?:benefits|insurance|coverages?)|benefit\s+schedule|limits?\s+of\s+insurance|policy\s+schedule|principal\s+sum)\b/i;

  // Published: spDocumentForCoverage runs at module scope and uses this as its
  // declarations-page signal when it is available.
  function spHasSchedulePage(text) {
    const lines = spNorm(text).split('\n');
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const line = raw.trim();
      if (!line || !_SP_SCHEDULE_WORD.test(line)) continue;
      const headingLike = line.length <= 80 || line === line.toUpperCase();
      if (!headingLike) continue;
      // An amount has to follow it, within the next page-ish of text.
      const after = lines.slice(i, i + 40).join('\n');
      if (/\$\s*[\d][\d,]{2,}/.test(after)) return true;
    }
    return false;
  }
  const scheduleSeenFor = (type) => {
    if (!_CT) return null;
    let sawDoc = false;
    const docs = (d.pdfTexts || []);
    for (const doc of docs) {
      const pages = (doc.pages || []);
      const txt = pages.map(p => p.text || '').join('\n') || doc.text || '';
      if (!txt) continue;
      if (!_CT.coveragesIn(txt).some(c => _CT.sameTerm(c, type))) continue;
      sawDoc = true;
      // A schedule page, not merely the word somewhere in a form body.
      const hit = pages.some(p => spHasSchedulePage(p.text || ''));
      if (hit) return true;
    }
    return sawDoc ? false : null;
  };

  // The limits cell for a vertical benchmark table. Seven of these tables —
  // education, healthcare and the rest — each carried their own copy of this
  // expression, so a message corrected in one stayed wrong in the other six.
  // The student accident row lives in the education table, which is why it
  // kept reading "limits not parsed" after the general table had stopped.
  const benchLimitsCell = (b) => {
    const el = limitsForBenchmark(b);
    const fl = formatExtractedLimits(el);
    if (fl) return fl;
    if (el && el._writtenAs) return '<span style="font-size:11px;color:#333;">' + escapeHtml(el._writtenAs) + '</span>';
    const refused = refusedFor(b.key);
    if (refused) {
      return '<em style="color:var(--critical);" data-stance="' + refused.stance + '">'
        + (refused.stance === 'declined'
            ? 'Not covered \u2014 declined on the declarations'
            : 'Not covered \u2014 excluded by endorsement')
        + '</em><div style="font-size:11px;color:#555;margin-top:2px;">\u201c'
        + escapeHtml(refused.quote || '') + '\u201d'
        + (refused.fileName ? ' \u2014 ' + escapeHtml(refused.fileName) : '')
        + (refused.page ? ', p.' + refused.page : '') + '</div>';
    }
    /* GRANTED, BUT NOT IN A FIELD THIS READER CAN PARSE.
     *
     * Matrix Head Start's D&O policy adds Workplace Violence Expense Coverage
     * by endorsement, with a $250,000 aggregate stated in a sentence rather
     * than a declarations field. The row said "Not found in uploaded
     * policies" — of a coverage the client bought. Saying nothing was found is
     * only true when nothing was found.
     */
    const _g1 = !hasCoverage(b.key) && grantedFor(b.key);
    if (_g1) {
      /* Where the policy states the figure in a sentence, quote the sentence.
       * The field reader will not promote a clause figure to a limit and
       * should not — that rule kept a TRIA threshold out of a D&O row. But
       * refusing to parse a number is not a reason to hide it, and the
       * sentence carries caveats no limits field could: Matrix Head Start's
       * $250,000 is part of and not in addition to the EPL aggregate. */
      const _g = _g1;
      const _src = _g.stated || _g;
      return '<em style="color:var(--warning);" data-stance="granted">'
        + (_g.stated
            ? 'Covered \u2014 ' + escapeHtml(_g.stated.amount)
              + ' stated in the policy wording, not in a declarations field'
            : 'Covered \u2014 granted by the policy, no declarations limit parsed')
        + '</em><div style="font-size:11px;color:#555;margin-top:2px;">\u201c'
        + escapeHtml(_src.quote || '') + '\u201d'
        + (_src.fileName ? ' \u2014 ' + escapeHtml(_src.fileName) : '')
        + (_src.page ? ', p.' + _src.page : '') + '</div>';
    }
    if (!hasCoverage(b.key)) return '<em style="color:var(--critical);">Not found in policies</em>';
    // The hard rule, enforced where it is reported: neither message may be
    // shown without the ledger saying which passes ran on that policy.
    if (scheduleSeenFor(b.key) === false) {
      return '<em style="color:var(--warning);">Coverage detected \u2014 the uploaded document contains no schedule of benefits or declarations page. Request the policy document to confirm limits.</em>'
        + renderParseLedger(b.key);
    }
    return '<em style="color:var(--warning);">Coverage detected \u2014 limits not parsed</em>'
      + renderParseLedger(b.key);
  };

  // THE HARD RULE: every policy gets BOTH passes, always.
  //
  // A coverage written on its own policy has nothing else in the file set to
  // speak for it. When the Student Accident limits would not parse, the
  // report said so and stopped — while the carrier, the policy number, the
  // term, the premium and a Sanctions Exclusion sat unread in that policy's
  // ENDORSEMENTS, because only the declarations pass had run.
  //
  // So for each policy document, independently: read its declarations, then
  // read its endorsements, and record what each pass yielded. Nothing may
  // report "not parsed" without saying which passes ran and what they found.
  // A reader can then tell the three cases apart, which is the whole point,
  // because the action is different for each:
  //
  //   no declarations page in the file  -> request the policy
  //   declarations present, no limits   -> request the declarations page
  //   declarations read, parse failed   -> our defect, not the broker's errand
  const _parseLedgers = new Map();   // canonical id -> ledger
  const policyParseLedger = (doc) => {
    if (!doc) return null;
    const id = spPolicyCanonicalId(doc);
    if (_parseLedgers.has(id)) return _parseLedgers.get(id);
    const pages = doc.pages || [];
    // Both, because they answer different questions: the limits and dates
    // below want the normalised rows, and the identity block wants the markup
    // that says which half of a cell is the label.
    const rawTxt = pages.map(p => p.text || '').join('\n');
    const txt = spNorm(rawTxt);

    // PASS 1 - declarations.
    const decPage = pages.find(p => spHasSchedulePage(p.text || ''));
    const dec = { found: !!decPage, page: decPage ? (decPage.page || null) : null };

    // PASS 2 - endorsements. Always, whether or not pass 1 found anything.
    const all = ((d.endorsements && d.endorsements.endorsements) || [])
      .filter(e => e && e.fileName === (doc.fileName || ''));
    const notable = all.filter(e => e.tag && e.tag !== 'OTHER' && !e._routine);
    const end = { count: all.length, notable: notable.length,
                  titles: all.map(e => e.title).filter(Boolean).slice(0, 6) };

    // What the two passes together yielded about the policy itself.
    const facts = [];
    /* Read the declarations' own fields before falling back to a window after
     * a label. This policy was reported as "American Alternative Insurance Co
     * · Policy PRIOR": the carrier was the first "... Insurance Co" anywhere
     * in the file, and "PRIOR" is the neighbouring LABEL of a two-column block
     * — "POLICY NUMBER  PRIOR POLICY NUMBER" over "4XX1AB0009876-00  NEW" —
     * captured as the value. The field reader pairs by column instead. */
    const ident = (typeof spReadPolicyIdentity === 'function')
      ? spReadPolicyIdentity(rawTxt) : { policyNumber: null, carrier: null };
    if (ident.carrier) facts.push(ident.carrier.value);
    else {
      const carrier = /([A-Z][A-Za-z.&' ]{3,40}(?:Insurance Company|Insurance Co\.?|Indemnity Company|Assurance Company))/.exec(txt);
      if (carrier) facts.push(carrier[1].trim());
    }
    if (ident.policyNumber) facts.push('Policy ' + ident.policyNumber.value);
    else {
      // The fallback keeps the two guards the field reader enforces: a number
      // has a digit in it, and the policy this one REPLACED is not this one.
      const polNo = /(?<!prior\s)(?<!previous\s)policy\s*(?:no\.?|number)\s*:\s*([A-Z0-9][A-Z0-9\- ]{4,24}?)(?=\s|$)/i.exec(txt)
        || /forming\s+a\s+part\s+of\s+policy\s+no\.?\s*([A-Z0-9][A-Z0-9\- ]{4,24})/i.exec(txt);
      if (polNo && /\d/.test(polNo[1])) facts.push('Policy ' + polNo[1].trim());
    }
    const prem = /(?:term\s+premium|annual\s+premium|premium)\s+of\s+\$\s*([\d][\d,]{2,})/i.exec(txt);
    if (prem) facts.push('annual premium $' + prem[1]);

    const ledger = { canonicalId: id, fileName: doc.fileName || 'uploaded document',
                     declarations: dec, endorsements: end, facts: facts };
    _parseLedgers.set(id, ledger);
    return ledger;
  };

  // Rendered wherever a coverage could not be given limits, so the hard rule
  // is visible in the report rather than only performed in the code.
  const renderParseLedger = (type) => {
    const CT = _CT;
    if (!CT) return '';
    // One chooser, shared with the carrier attribution, so the header of a row
    // and the evidence beneath it cannot name different policies. See
    // spDocumentForCoverage.
    const _pick = spDocumentForCoverage(d.pdfTexts || [], type);
    const doc = _pick && _pick.doc;
    if (!doc) return '';
    const L = policyParseLedger(doc);
    if (!L) return '';
    const parts = [];
    parts.push('Declarations: ' + (L.declarations.found
      ? 'read (p.' + (L.declarations.page || '?') + ')'
      : 'no declarations or schedule page in this document'));
    parts.push('Endorsements: ' + (L.endorsements.count
      ? L.endorsements.count + ' read' + (L.endorsements.notable ? ', ' + L.endorsements.notable + ' notable' : '')
      : 'none found'));
    let html = '<div style="font-size:11px;color:#374151;margin-top:4px;">'
      + '<strong>Both passes run on ' + escapeHtml(L.fileName) + ':</strong> '
      + escapeHtml(parts.join(' \u00b7 ')) + '</div>';
    if (L.facts.length) {
      html += '<div style="font-size:11px;color:#374151;">Parsed from this policy: '
        + escapeHtml(L.facts.join(' \u00b7 ')) + '</div>';
    }
    if (L.endorsements.titles.length) {
      html += '<div style="font-size:11px;color:#6B7280;">' + escapeHtml(L.endorsements.titles.join(' \u00b7 ')) + '</div>';
    }
    return html;
  };

  // WHAT WAS PARSED, when the limits could not be.
  //
  // "Coverage detected - no schedule of benefits" is true and thin. It tells a
  // reader what is absent and nothing about what the document actually
  // yielded, which reads as though the policy was not parsed at all. On the
  // Blanket Accident policy it was: the carrier, the policy number, the term,
  // the annual premium and three endorsements including a Sanctions Exclusion
  // are all in those nine pages. Saying so is the difference between "we could
  // not read this" and "we read it; the benefit schedule is not in it".
  const policyFactsFor = (type) => {
    const CT = _CT;
    if (!CT) return null;
    for (const doc of (d.pdfTexts || [])) {
      const raw = (doc.pages || []).map(p => p.text || '').join('\n');
      if (!raw) continue;
      const txt = spNorm(raw);
      if (!CT.coveragesIn(txt).some(c => CT.sameTerm(c, type))) continue;
      const bits = [];
      const carrier = /([A-Z][A-Za-z.&' ]{3,40}(?:Insurance Company|Insurance Co\.?|Indemnity Company|Assurance Company))/.exec(txt);
      if (carrier) bits.push(carrier[1].trim());
      const polNo = /policy\s*(?:no\.?|number)\s*:?\s*([A-Z0-9][A-Z0-9\- ]{4,24}?)(?=\s|$)/i.exec(txt)
        || /forming\s+a\s+part\s+of\s+policy\s+no\.?\s*([A-Z0-9][A-Z0-9\- ]{4,24})/i.exec(txt);
      if (polNo) bits.push('Policy ' + polNo[1].trim());
      const prem = /(?:term\s+premium|annual\s+premium|premium)\s+of\s+\$\s*([\d][\d,]{2,})/i.exec(txt);
      if (prem) bits.push('annual premium $' + prem[1]);
      const ends = ((d.endorsements && d.endorsements.endorsements) || [])
        .filter(e => e && e.fileName === doc.fileName);
      if (ends.length) {
        const notable = ends.filter(e => e.tag && e.tag !== 'OTHER' && !e._routine);
        bits.push(ends.length + ' endorsement' + (ends.length === 1 ? '' : 's') + ' parsed'
          + (notable.length ? ' (incl. ' + escapeHtml(String(notable[0].title || notable[0].tag)).slice(0, 44) + ')' : ''));
      }
      if (bits.length) return bits.join(' \u00b7 ');
    }
    return null;
  };

  // The same fact, reachable from the education table's own status logic.
  const _markScheduleMissing = (b) => {
    const el = limitsForBenchmark(b);
    if (hasCoverage(b.key) && scheduleSeenFor(b.key) === false) {
      return Object.assign({}, el || {}, { _scheduleMissing: true });
    }
    return el;
  };

  // A citation is a promise that the named source supports the statement.
  //
  // Every vertical benchmark table stamped ONE statute list on ALL of its
  // rows. The education table cites FERPA, Title IX and the Clery Act against
  // Student Accident Insurance, Tuition Refund and School Bus alike. Three of
  // its ten rows genuinely rest on those statutes; the rest do not, and a
  // broker following the citation to find a student-accident requirement in
  // FERPA finds nothing, because there is nothing there.
  //
  // So a row cites its OWN basis, which the row already states. Where that
  // basis names a statute or standard, that is the citation. Where the row
  // says there is no mandate, the citation says so rather than borrowing
  // authority from statutes about something else.
  const _SP_NAMES_AUTHORITY = /\u00a7|\bU\.?S\.?C\.?\b|\bC\.?F\.?R\.?\b|\bCPSC\b|\bASTM\b|\bNFPA\b|\bOSHA\b|\bFMCSA\b|\bHIPAA\b|\bFDA\b|\bEPA\b|\bPHMSA\b|\bTCEQ\b|\bRRC\b|\bTitle\s+IX\b|\bFERPA\b|\bClery\b|\bCarmack\b|\bCMS\b|\bHUD\b|\bFair\s+Housing\b/i;

  const benchCitation = (b) => {
    const st = String((b && b.statutory) || '').trim();
    const cite = (txt) => '<td style="font-size:11px;color:var(--text-muted);font-style:italic;">[' + escapeHtml(txt) + ']</td></tr>';
    if (!st) return cite('Sector benchmark');
    if (_SP_NAMES_AUTHORITY.test(st)) {
      // The clause before the dash is the authority; what follows is what it
      // costs you, which is commentary rather than citation.
      return cite(st.split(/\s+[\u2013\u2014-]\s+/)[0].trim());
    }
    // A citation must not be the statutory column read back.
    //
    // The Student Accident row printed "No federal mandate (many states
    // require notification of availability)" in the statutory column and then
    // the same sentence again as its citation. That is not a source, it is an
    // echo, and it takes up the one column whose job is to tell a reader where
    // to go and check.
    //
    // Where a row rests on no statute there is nothing to cite, and saying so
    // in four words is the honest answer. No standards body is named here: the
    // blanket lists that used to be printed were removed precisely because
    // they attributed figures to publications that do not carry them, and
    // replacing one invented attribution with another would be the same error.
    if (/^no\b/i.test(st)) return cite('No federal statute \u2014 sector benchmark');
    return cite(st);
  };

  // One limits lookup for every benchmark table. Key, then display name, then
  // CONCEPT — because the same coverage appears under different keys in the
  // general, education and healthcare lists, and a plain key lookup gave the
  // general row $1M/$3M MEETS while the education row for the SAME coverage
  // read "limits not parsed, VERIFY" in the same report.
  /* A ROW RESOLVES ITS COVERAGE ONCE.
   *
   * The Title IX row satisfied its STATUS through either D&O or Educators
   * Legal Liability, and read its LIMITS from D&O alone. On a charter school
   * with an ELL coverage part and no D&O policy the two halves disagreed: the
   * status said VERIFY off the ELL, and the limits cell said "limits not
   * parsed" off an empty D&O, while a complete ELL declarations page --
   * Coverage A $1,000,000 each wrongful act, $3,000,000 aggregate, a Coverage
   * B, three deductibles and a retroactive date -- sat parsed and unused.
   *
   * `keys` is that row's coverages in preference order, declared once and read
   * by both halves, so a row cannot again be satisfied by one coverage and
   * priced from another. */
  const limitsForBenchmark = (b) => spResolveBenchmarkLimits(declLimits, b, _CT);

  const hasCoverage = (type) => {
    // A coverage the policy declines, or excludes by form, is not a coverage
    // the policy has — however many of the six tests below find the name
    // somewhere. Real extracted limits still win, because a declination read
    // wrongly should not delete a figure that parsed.
    const refused = refusedFor(type);
    if (refused) {
      const realLimits = declLimits[type] && Object.keys(declLimits[type]).some(k => !k.startsWith('_'));
      if (!realLimits) return false;
    }
    // Direct match in detected coverages
    if (d.detectedCoverages.includes(type)) return true;
    // The same coverage under a different trade name. Substring comparison
    // could not see that "Sexual Misconduct Liability" and "Sexual Abuse /
    // Molestation" are one coverage, nor that "Equipment Breakdown" and
    // "Boiler and Machinery" are, while correctly keeping "Employers
    // Liability" apart from "Employment Practices Liability".
    if (_CT && d.detectedCoverages.some(c => _CT.sameTerm(c, type))) return true;
    // Partial / variant match (e.g., "Crime/Fidelity" matches "Crime", "Educators Legal Liability (ELL)" matches "ELL")
    if (d.detectedCoverages.some(c => c.toLowerCase().includes(type.toLowerCase()) || type.toLowerCase().includes(c.toLowerCase()))) return true;
    // Also check if declarations limits were extracted for this type (means coverage IS in the policy)
    if (declLimits[type]) return true;
    if (_CT && Object.keys(declLimits).some(k => _CT.sameTerm(k, type))) return true;
    // The policy schedules a form that provides it.
    if (_CT && [...scheduledCoverages].some(c => _CT.sameTerm(c, type))) return true;
    return false;
  };
  // Determine if SAM is relevant for this vertical (education, healthcare, childcare, religious, or SAM actually detected)
  const vlBench = d.vertical.toLowerCase();
  const isSAMRelevant = isEducation || vlBench.includes('daycare') || vlBench.includes('childcare') || vlBench.includes('youth') || vlBench.includes('camp') || vlBench.includes('religious') || vlBench.includes('church') || vlBench.includes('nonprofit') || vlBench.includes('hospital') || vlBench.includes('medical') || vlBench.includes('healthcare') || vlBench.includes('nursing') || vlBench.includes('behavioral') || vlBench.includes('dental') || hasCoverage('Sexual Abuse/Molestation');
  const pcBenchmarks = [
    // v2.18: Commercial Property is a life-safety-grade gap for ANY entity with physical locations.
    // Never downgrade below HIGH GAP without extracted limits — a Package Policy that does not produce
    // verified property limits is exactly the scenario where a critical coverage lapse hides in plain sight.
    // A scheduled-asset / London-market placement is not the ISO property row
    // and must not be judged against it. Base Power insures 14,240 installed
    // batteries on a Market Reform Contract: sums insured, a nat-cat annual
    // aggregate and a per-occurrence deductible, rated on total insured
    // values. Forced into the Commercial Property row it read "limits not
    // parsed" — every number is on page 2 of the policy.
    // Named so a reader can tell what it is without asking. This row is not
    // "other property"; it is the row for cover written as a SCHEDULE OF
    // ASSETS or a London-market placement, where the contract states sums
    // insured rather than ISO building/BPP/business-income limits.
    { type:"Property — Other (scheduled assets / London-market placement \u2014 cover stated as sums insured, not ISO building/BPP limits)", key:"Property — Other",
      statutory:"None — cover follows the contract, not a statutory minimum",
      standard:"Sum insured at or near total insured values; nat-cat limit sized to the exposure; deductible the insured can absorb",
      best:"Sum insured at full TIV, nat-cat limit per occurrence rather than a shared annual aggregate, and a deductible proportionate to a single-unit loss",
      status: (() => {
        const mp = declLimits['Property — Other'];
        if (!mp) return null;                       // no such placement — row is not shown
        const num = v => spMoneyWhole(v) || 0;
        const tiv = mp._tivValue || 0;
        const sums = Object.keys(mp)
          .filter(k => !k.startsWith('_') && !/deductible|retention/i.test(k))
          .map(k => num(mp[k]));
        const top = sums.length ? Math.max.apply(null, sums) : 0;
        // A DEDUCTIBLE IS NOT A PLACEMENT.
        //
        // Asked of this row: "What does this cover? It only shows a deductible
        // of $10K, not a coverage amount." Exactly right, and the honest
        // answer is that it covers nothing the report can show. This row
        // exists to benchmark a scheduled-asset or market placement against
        // what it insures; with no sum insured there is no placement to
        // benchmark, and a row headed "Property — Other" carrying a lone
        // retention tells a broker nothing while looking like a finding.
        //
        // The deductible is not lost — a property retention belongs on the
        // Commercial Property row, which reports deductibles in its own right.
        if (!top) return null;
        // The comparison that matters on this shape: how much of what is at
        // risk the limit actually covers. Anything else is a guess.
        if (!top || !tiv) return 'VERIFY';
        return top >= tiv * 0.5 ? 'MEETS' : 'BELOW STANDARD';
      })(),
      // Scored against this policy's own total insured values above. The
      // generic threshold comparison reads dollar figures out of the standard
      // TEXT, and on a sentence like "sum insured at or near total insured
      // values" it found none and returned MEETS — overriding a row that had
      // just measured $20M of cover over $178M of exposure.
      selfScored: true },
    { type:"Commercial Property", key:"Commercial Property", statutory:"Required for any entity with physical locations or owned/leased buildings", standard:"Replacement cost — building + BPP + business income/extra expense", best:"Blanket replacement cost with agreed value, ordinance or law, and business income with extended period", status: (() => { const hasPropLimits = spHasRealLimits(declLimits['Commercial Property']); if (hasPropLimits) return 'MEETS'; if (hasCoverage('Commercial Property') || hasCoverage('Property')) return 'HIGH GAP'; if (hasCoverage('Package Policy')) return 'HIGH GAP'; return 'CRITICAL GAP'; })() },
    { type:"Commercial Auto / Hired & Non-Owned", key:"Commercial Auto", statutory:"State-mandated minimums (varies by state)", standard:"$1M CSL or $1M/$1M split; Hired & Non-Owned Auto (HNOA) if no owned vehicles", best:"$2M CSL with hired/non-owned auto; HNOA standalone if fleet-free operation", status: hasCoverage("Commercial Auto") ? "MEETS" : "VERIFY" },
    { type:"General Liability", key:"General Liability", statutory:"$1M/$2M (varies by state)", standard:"$1M occ / $2M agg", best:"$2M occ / $4M agg", status: hasCoverage("General Liability") ? "MEETS" : (d.files.length > 0 ? "VERIFY" : "VERIFY") },
    { type:"Cyber Liability", key:"Cyber Liability", statutory:"No federal mandate (state breach notification laws apply)", standard:"$1M–$3M", best:"$5M+ with full first/third party", status: hasCoverage("Cyber Liability") ? "VERIFY" : "CRITICAL GAP" },
    // SAM only shown for education/healthcare/childcare/religious verticals or when SAM coverage is actually detected
    ...(isSAMRelevant ? [{ type:"Sexual Abuse / Molestation", key:"Sexual Abuse/Molestation", statutory:"No explicit statutory minimum", standard:"$1M/$3M standalone or endorsed", best:"$2M/$5M standalone with duty to defend", status: hasCoverage("Sexual Abuse/Molestation") ? "VERIFY" : (isEducation ? "CRITICAL GAP" : "VERIFY") }] : []),
    { type:"Umbrella / Excess", key:"Umbrella/Excess", statutory:"No statutory requirement", standard:"$5M–$10M excess", best:"$10M–$25M+ layered excess tower", status: hasCoverage("Umbrella/Excess") ? "VERIFY" : "HIGH GAP" },
    { type:"Workers Comp / EL", key:"Workers Compensation", statutory:"Statutory (state-mandated)", standard:"Statutory + $1M EL", best:"Statutory + $1M EL with voluntary coverage", status: hasCoverage("Workers Compensation") ? "MEETS" : "VERIFY" },
    // v2.20.10: EPLI / Professional Liability merged into a single row. For education
    // verticals these are functionally equivalent professional-staff coverage. The row
    // dynamically labels and sources its limits based on which coverage was detected.
    (function buildEPLIRow() {
      const hasEPLI = hasCoverage("EPLI");
      const hasPL = hasCoverage("Professional Liability");
      if (hasEPLI && hasPL) {
        // Both present — prefer the PL data (Section 5 will show whichever has limits via key lookup)
        return { type:"EPLI / Professional Liability", key:"Professional Liability", statutory:"No federal mandate (state-specific rules)", standard:"$1M–$2M per claim / $3M aggregate", best:"$3M–$5M with third-party / prior-acts coverage", status: "VERIFY" };
      }
      if (hasPL && !hasEPLI) {
        return { type:"EPLI / Professional Liability", key:"Professional Liability", statutory:"No federal mandate (state-specific rules)", standard:"$1M–$2M per claim / $3M aggregate", best:"$3M–$5M with third-party / prior-acts coverage", status: "MEETS" };
      }
      if (hasEPLI && !hasPL) {
        return { type:"EPLI / Professional Liability", key:"EPLI", statutory:"No federal mandate (state-specific rules)", standard:"$1M–$2M per claim / $3M aggregate", best:"$3M–$5M with third-party / prior-acts coverage", status: "VERIFY" };
      }
      // Neither detected — flag as gap
      return { type:"EPLI / Professional Liability", key:"EPLI", statutory:"No federal mandate (state-specific rules)", standard:"$1M–$2M per claim / $3M aggregate", best:"$3M–$5M with third-party / prior-acts coverage", status: "HIGH GAP" };
    })(),
    { type:"Active Shooter / Violent Event", key:"Active Shooter", statutory:"No statutory requirement", standard:"$1M with crisis response", best:"$5M+ with pre-event training, crisis management, victim counseling", status: hasCoverage("Active Shooter") ? "VERIFY" : (isEducation ? "CRITICAL GAP" : "HIGH GAP") },
    { type:"Equipment Breakdown", key:"Equipment Breakdown", statutory:"No statutory requirement", standard:"$1M or matched to property limits", best:"Matched to property limits with spoilage and expediting", status: hasCoverage("Equipment Breakdown") ? "MEETS" : "VERIFY" },
    { type:"Flood Insurance", key:"Flood Insurance", statutory:"Required in SFHA zones (federally backed mortgages)", standard:"NFIP limits ($500K building / $500K contents commercial)", best:"Private flood or excess flood above NFIP limits for full replacement cost", status: hasCoverage("Flood Insurance") ? "MEETS" : (d.states.some(a => ['FL','TX','LA','NC','SC','NJ','NY','MS','AL'].includes(a)) ? "HIGH GAP" : "VERIFY") },
    { type:"D&O / Management Liability", key:"D&O", statutory:"No federal mandate", standard:"$1M–$2M", best:"$3M–$5M with entity coverage and Side A DIC", status: hasCoverage("D&O") ? "VERIFY" : (d.detectedCoverages.some(c => c.startsWith('D&O (')) ? "VERIFY — " + (d.detectedCoverages.find(c => c.startsWith('D&O (')) || '').replace('D&O (','').replace(')','').toUpperCase() : (isEducation ? "CRITICAL GAP" : "HIGH GAP")) },
    /* Shown only where a policy carries the line: a benchmark for a coverage
     * the program does not buy would read as a gap on every report. */
    ...((declLimits || {})['Fiduciary Liability'] && !(declLimits['Fiduciary Liability'] || {})._declined
      ? [{ type:"Fiduciary Liability", key:"Fiduciary Liability", statutory:"ERISA \u00a7409 \u2014 plan fiduciaries are personally liable for plan losses", standard:"$1M", best:"$2M+ with voluntary settlement program and HIPAA sublimit", status:"VERIFY" }] : []),
    ...((declLimits || {})['Kidnap & Ransom']
      ? [{ type:"Kidnap & Ransom / Extortion", key:"Kidnap & Ransom", statutory:"No statutory requirement", standard:"$1M per insured event", best:"$5M+ with crisis consultant fees unlimited", status:"VERIFY" }] : [])
  ];

  // Maritime detection flags (used below when rendering benchmarks)
  const isMaritime = d.vertical.toLowerCase().includes('maritime') || d.vertical.toLowerCase().includes('marine') || d.vertical.toLowerCase().includes('shipping') || d.vertical.toLowerCase().includes('dredg') || d.vertical.toLowerCase().includes('offshore') || d.vertical.toLowerCase().includes('oil') || d.vertical.toLowerCase().includes('gas') || d.vertical.toLowerCase().includes('port') || d.vertical.toLowerCase().includes('vessel') || d.vertical.toLowerCase().includes('boat') || d.vertical.toLowerCase().includes('fishing') || d.vertical.toLowerCase().includes('tug') || d.vertical.toLowerCase().includes('barge') || d.vertical.toLowerCase().includes('shipyard');
  const hasAnyMarine = d.detectedCoverages.some(c => ['Marine General Liability','Hull & Machinery','Protection & Indemnity','Maritime Employers Liability','Jones Act / Crew Coverage','USL&H / Longshore','Marine Builders Risk','Marine Cargo / Freight','Marine Pollution Liability','Offshore Energy / OEE','Dredging Insurance'].includes(c));
  // Energy / Oil & Gas detection flags
  const vl = d.vertical.toLowerCase();
  // Renewable / storage / retail-electric verticals contain the word "energy"
  // but carry none of the upstream oil & gas exposures. Without this guard a
  // home-battery installer is handed Control of Well, NORM Liability and Rig &
  // Drilling Equipment benchmarks — 'Renewable Energy' had this defect before
  // 'Battery Storage & Distributed Energy' was added.
  const isRenewable = vl.includes('renewable') || vl.includes('solar') || vl.includes('wind')
    || vl.includes('battery') || vl.includes('distributed energy') || vl.includes('photovoltaic')
    || vl.includes('retail electricity') || vl.includes('electricity provider') || vl.includes('microgrid');
  const isEnergy = (vl.includes('oil') || vl.includes('gas') || vl.includes('energy') || vl.includes('drilling') || vl.includes('refinery') || vl.includes('petrochemical') || vl.includes('pipeline') || vl.includes('midstream') || vl.includes('upstream') || vl.includes('downstream') || vl.includes('petroleum') || vl.includes('wellhead') || vl.includes('e&p') || vl.includes('exploration') || vl.includes('production') || vl.includes('frac') || vl.includes('oilfield') || vl.includes('well service') || vl.includes('compressor') || vl.includes('gathering')) && !isRenewable;
  const hasAnyEnergy = d.detectedCoverages.some(c => ['Control of Well / OEE (Onshore)','Pollution Legal Liability (PLL/EIL)','Rig & Drilling Equipment','Oil Lease Property (OLP)','Pipeline Operators Insurance','Gas Processing & Compression','Terminal & Tank Farm','LNG / Gas Storage','Refinery & Petrochemical','NORM Liability','Underground Resource Damage','RRC Compliance & Bonding','MSA / Contractual Liability (Energy)','Business Interruption (Energy)'].includes(c));
  // Transportation / Logistics detection flags
  const isTransportation = vl.includes('trucking') || vl.includes('fleet') || vl.includes('logistics') || vl.includes('freight') || vl.includes('carrier') || vl.includes('warehousing') || vl.includes('distribution') || vl.includes('last mile') || vl.includes('freight broker') || vl.includes('motor carrier') || vl.includes('trucking/fleet') || vl.includes('transport');
  const hasAnyTransportation = d.detectedCoverages.some(c => ['Motor Carrier Liability','Motor Truck Cargo','MCS-90 Compliance','Bobtail / Non-Trucking Liability','Trailer Interchange','Garagekeepers Liability','Refrigeration Breakdown','Warehousing Legal Liability','Freight Broker E&O','Contingent Cargo'].includes(c));
  // Construction detection flags
  const isConstruction = vl.includes('contractor') || vl.includes('construction') || vl.includes('builder') || vl.includes('subcontractor') || vl.includes('trade') || vl.includes('general contractor') || vl.includes('specialty trade') || vl.includes('residential builder') || vl.includes('heavy/highway') || vl.includes('design-build');
  const isRealEstate = vl.includes('real estate') || vl.includes('property management') || vl.includes('residential development') || vl.includes('reit') || vl.includes('landlord') || vl.includes('commercial property management') || vl.includes('real estate development') || vl.includes('real estate investment');
  const hasAnyConstruction = d.detectedCoverages.some(c => ['Builders Risk','Wrap-Up Programs (OCIP/CCIP)','Subcontractor Default Insurance (SDI)','Contractors Pollution Liability','Installation Floater','Equipment Floater','Railroad Protective Liability','Products-Completed Operations','Contractors Professional Liability (Design-Build)'].includes(c));
  // Healthcare detection flags
  const isHealthcare = vl.includes('hospital') || vl.includes('medical') || vl.includes('physician') || vl.includes('healthcare') || vl.includes('clinic') || vl.includes('nursing') || vl.includes('dental') || vl.includes('behavioral') || vl.includes('ambulatory') || vl.includes('telemedicine') || vl.includes('telehealth') || vl.includes('home health') || vl.includes('long-term care') || vl.includes('clinical lab');
  // Which detected coverages are evidence of a HEALTHCARE account, and which
  // are not. HIPAA/privacy, sexual abuse & molestation and vicarious liability
  // are written for schools, staffing firms, gyms and municipalities too — a
  // single one of them says nothing about the industry. On the Base Power
  // energy account exactly one matched (the CGL's own "vicarious liability
  // coverage" indemnity clause) and it rendered the whole hospital malpractice
  // benchmark table: MPL, HPL, clinical trials, telemedicine, Medicare defence.
  const HEALTHCARE_ONLY_COVERAGES = ['Medical Professional Liability','Hospital Professional Liability',
    'Clinical Trial Liability','Telemedicine/Telehealth Coverage','Medical Directors Liability',
    'Managed Care E&O','Medicare/Medicaid Defense'];
  const healthcareSignals = d.detectedCoverages.filter(c => HEALTHCARE_ONLY_COVERAGES.includes(c)).length;
  // Two independent healthcare-only coverages, or the vertical itself. A real
  // healthcare program carries several; one match is a phrase collision.
  const hasAnyHealthcare = healthcareSignals >= 2;
  // Manufacturing / Industrial detection flags
  const isManufacturing = vl.includes('manufactur') || vl.includes('industrial') || vl.includes('factory') || vl.includes('plant') || vl.includes('assembly') || vl.includes('processing') || vl.includes('food/beverage') || vl.includes('pharmaceutical') || vl.includes('chemical manufacturing') || vl.includes('automotive manufacturing') || vl.includes('electronics manufacturing');
  const isOGSupplier = vl.includes('services supplier') || vl.includes('supplier') || vl.includes('rigging') || vl.includes('lifting') || vl.includes('hoist') || vl.includes('wire rope') || vl.includes('sling') || vl.includes('rental') || (vl.includes('services') && (vl.includes('oil') || vl.includes('gas') || vl.includes('energy'))) || (vl.includes('oilfield') && (vl.includes('service') || vl.includes('supply') || vl.includes('equipment')));
  const hasAnyManufacturing = d.detectedCoverages.some(c => ['Product Recall Insurance','Product Contamination','Equipment Breakdown / Boiler & Machinery','Supply Chain / Contingent BI','Environmental Impairment Liability','OSHA Defense Coverage','Manufacturers E&O','Stock Throughput (STP)','Foreign Product Liability','Inland Marine (Goods in Process)'].includes(c));

  /* Publish what Section 6 concluded, for the E&O Mitigation Record — same
   * reason as 2B. A null status means the row does not apply to this program,
   * so it is excluded here exactly as the table below excludes it. */
  window._spSection6 = (() => {
    const live = pcBenchmarks.filter(b => b.status !== null);
    const tally = {};
    live.forEach(b => { tally[b.status] = (tally[b.status] || 0) + 1; });
    return {
      total: live.length,
      byStatus: tally,
      belowOrMissing: live
        .filter(b => /GAP|BELOW/.test(b.status || ''))
        .map(b => ({ type: b.type, status: b.status }))
    };
  })();

  html += `<table class="report-table"><thead><tr><th>Coverage Type</th><th>Statutory Minimum</th><th>Industry Standard</th><th>Current Coverage / Limits</th><th>Current Status</th><th>Citation</th></tr></thead><tbody>`;
  const renderDeductibles = (ded, states) => {
    // Deductibles. The flat figure is the ordinary answer; a PERCENTAGE
    // deductible is the finding, because what it is a percentage of — and
    // whether it applies once or once per building — decides whether the
    // retention is five thousand dollars or six figures.
    if (!ded) {  return ''; }
    {
      let dedHtml = '<div style="margin-top:6px;padding-top:5px;border-top:1px solid #cfe0e0;font-size:11px;color:#333;">';
      if (ded.varies) dedHtml += 'Deductible: <strong>' + escapeHtml(ded.varies.join(', ')) + '</strong>';
      else if (ded.allOtherPerils) dedHtml += 'Deductible (all other perils): <strong>' + escapeHtml(ded.allOtherPerils) + '</strong>';
      if (ded.items > 1) dedHtml += ' <span style="color:#666;">per item, ' + ded.items + ' scheduled</span>';
      (ded.exceptions || []).forEach(x => {
        const pct = x.percentage;
        dedHtml += '<br><span style="color:' + (pct ? '#92400E' : '#333') + ';font-weight:600;">'
          + escapeHtml(x.peril) + ' deductible: ' + escapeHtml(x.value) + '</span>';
      });
      if ((ded.exceptions || []).some(x => x.percentage)) {
        const basis = [];
        if (ded.basisOfValue) basis.push('applied to the ' + ded.basisOfValue);
        if (ded.perBuilding) basis.push('calculated separately per building and per contents');
        if (ded.form) basis.push(ded.form);
        if (basis.length) dedHtml += '<div style="margin-top:3px;color:#666;">' + escapeHtml(basis.join(' \u00b7 ')) + '</div>';
        // A percentage deductible against the state's own named catastrophe
        // is not a detail in a cell. Colorado's profile calls it the
        // hailstorm capital of the US; a 2% per-building deductible on a
        // blanket in that state is the single most consequential retention
        // in the program, and it was not being reported at all.
        // Match the peril against the operating states the report itself
        // detected, not a guess. "Wind Or Hail" splits so that either word
        // can match a profile that names only one of them.
        let catState = null, cat = '';
        (states || []).forEach(ab => {
          if (catState) return;
          const prof = (typeof STATE_PROFILES !== 'undefined') ? STATE_PROFILES[ab] : null;
          if (!prof || !prof.primaryCatastrophe) return;
          const words = (ded.exceptions || []).filter(x => x.percentage)
            .flatMap(x => x.peril.split(/\s+(?:or|\/)\s+/i))
            .map(x => x.replace(/[^a-z]/gi, '')).filter(w => w.length > 3);
          if (words.length && new RegExp(words.join('|'), 'i').test(prof.primaryCatastrophe)) {
            catState = ab; cat = prof.primaryCatastrophe;
          }
        });
        const hit = catState ? (ded.exceptions || []).find(x => x.percentage) : null;
        if (hit) {
          dedHtml += '<div style="margin-top:5px;padding:5px 7px;background:#FEF3C7;border-left:3px solid #D97706;color:#7C2D12;">'
            + '<strong>Catastrophe deductible</strong> &mdash; ' + escapeHtml(hit.peril) + ' is a named catastrophe peril for '
            + escapeHtml(STATE_PROFILES[catState].name) + ' (' + escapeHtml(cat) + '). '
            + 'At ' + escapeHtml(hit.value) + (ded.basisOfValue ? ' of the ' + escapeHtml(ded.basisOfValue) : '')
            + (ded.perBuilding ? ', applying separately to each building and to its contents' : '')
            + ', the retention on the most likely loss is far above the '
            + escapeHtml(ded.allOtherPerils || 'all-other-perils') + ' figure. Confirm the per-building values on file.</div>';
        }
      }
      dedHtml += '</div>';
      return dedHtml;
    }
    return '';
  };

  pcBenchmarks.forEach(b => {
    // A row with a null status does not apply to this program at all — the
    // scheduled-asset row exists only where such a placement was uploaded.
    if (b.status === null) return;
    // Extract actual declarations limits for this coverage type.
    //
    // The lookup is by key, then by display name, then by CONCEPT. Two rows
    // cover sexual abuse on a school program — "Sexual Abuse / Molestation" in
    // the general list and "Sexual Abuse & Molestation (SAM)" in the K-12 list
    // — and a plain key lookup found the limits for one and not the other, so
    // the same report showed $1M/$3M MEETS on one row and "limits not parsed,
    // VERIFY" on the other. Whatever a reader takes from that, one of the two
    // is wrong, which is the contradiction this section already exists to
    // avoid.
    const extractedLimits = limitsForBenchmark(b);
    const formattedLimits = formatExtractedLimits(extractedLimits);
    const limitComparison = compareLimitToThreshold(extractedLimits, b.standard);
    
    // Determine status based on actual limits comparison (not just presence detection)
    let effectiveStatus = b.status;
    if (extractedLimits && limitComparison && !b.selfScored) {
      if (limitComparison === 'EXCEEDS') effectiveStatus = 'MEETS';
      else if (limitComparison === 'MEETS') effectiveStatus = 'MEETS';
      else if (limitComparison === 'BELOW MINIMUM') effectiveStatus = 'BELOW STANDARD';
    }
    /* The carrier declined it on the declarations: not covered, a gap — never
     * "coverage detected". Holloway Houston's D&O is unticked and NOT COVERED
     * on the Hartford declarations, and was reported as detected. */
    if ((extractedLimits || {})._declined) effectiveStatus = (typeof isEducation !== 'undefined' && isEducation) ? 'CRITICAL GAP' : 'HIGH GAP';
    /* Granted by a policy that states its own figure, with no declarations
     * limit: measured against the standard, not left at "HIGH GAP" beside a
     * cell that says it is covered. Holloway's Workplace Violence Expenses
     * extension is $100,000 against a $1M standard. */
    if (!formattedLimits && !(extractedLimits || {})._declined && !hasCoverage(b.key)) {
      const _gr = grantedFor(b.key);
      const _amt = _gr ? spMoneyWhole(((/\$\s*[\d][\d,]{3,}/.exec(_gr.quote || '') || [])[0]) || '') : NaN;
      const _stdM = /\$\s*([\d.]+)\s*M\b/i.exec(String(b.standard || ''));
      if (isFinite(_amt) && _amt > 0) {
        effectiveStatus = _stdM && _amt < parseFloat(_stdM[1]) * 1e6 ? 'BELOW STANDARD' : 'VERIFY';
      }
    }
    // A BENCHMARK IS MET ONLY IF IT WAS MEASURED.
    //
    // Most rows are defined as `hasCoverage(key) ? 'MEETS' : ...`, so detecting
    // the coverage was enough to claim it meets the standard. The row then read
    // "Coverage detected — limits not parsed from declarations" beside a green
    // MEETS tick: the report saying in one column that it does not know the
    // limits and in the next that they are adequate. Whatever the reader takes
    // from that, one of the two is wrong.
    //
    // Without parsed limits the honest status is VERIFY, and the reason travels
    // with it so the broker knows what to go and get.
    const writtenAs = (extractedLimits || {})._writtenAs;
    if (effectiveStatus === 'MEETS' && !formattedLimits && !writtenAs) {
      effectiveStatus = 'VERIFY — LIMITS NOT PARSED';
    }
    // "We read the schedule and could not parse it" and "no schedule page was
    // uploaded" are different findings, and the badge should say which.
    if (String(effectiveStatus).startsWith('VERIFY') && scheduleSeenFor(b.key) === false) {
      effectiveStatus = 'VERIFY — SCHEDULE NOT UPLOADED';
    }
    // CRITICAL: Update the benchmark's status so Section 8 (Gap Analysis) uses the same
    // effective status. Without this, Section 8 reads the original status (e.g. 'VERIFY')
    // for coverages that Section 5 shows as 'MEETS', creating contradictory messaging.
    b.status = effectiveStatus;
    
    let badge = '';
    if (effectiveStatus === 'MEETS') badge = '<span class="badge badge-meets">✅ MEETS</span>';
    else if (effectiveStatus === 'BELOW STANDARD') badge = '<span class="badge badge-high">⚠️ BELOW STANDARD</span>';
    else if (effectiveStatus.startsWith('VERIFY')) badge = '<span class="badge badge-verify">⚠️ ' + effectiveStatus + '</span>';
    else if (effectiveStatus === 'CRITICAL GAP') badge = '<span class="badge badge-critical">❌ CRITICAL GAP</span>';
    else badge = '<span class="badge badge-high">❌ HIGH GAP</span>';
    
    // v2.17: Show P&C coverage in card format matching EB display
    let limitsCell;
    if (formattedLimits) {
      const carrier = carrierPerCoverage[b.key] || carrierPerCoverage[b.type] || null;
      limitsCell = '<div style="padding:6px 8px;background:#f0f7f7;border-radius:4px;border-left:3px solid #01696F;">';
      if (carrier) limitsCell += '<strong>' + carrier + '</strong><br>';
      // v2.17: Show coverage type and coinsurance for property
      const limitsObj = extractedLimits || {};
      // v2.17: HNOA endorsement note
      // Say what was detected, not where it was assumed to sit.
      //
      // This read "HNOA Endorsement on GL (No Owned Vehicles)" for Aspen View,
      // which holds a STANDALONE Business Auto policy (GCA 10 00) written on
      // symbols 8 and 9 with "ITEM THREE - SCHEDULE OF COVERED AUTOS YOU OWN:
      // Item Three Is Not Applicable To This Policy". The substance was right
      // and the location was invented: the flag is set by the hired/non-owned
      // extraction path whether the limit came from a GL endorsement or from
      // an auto policy of its own.
      if (limitsObj._hnoaEndorsement) limitsCell += '<span style="font-size:11px;color:#01696F;font-weight:600;">Hired &amp; Non-Owned only \u2014 no owned autos scheduled</span><br>';
      if (limitsObj._coverageType) limitsCell += '<span style="font-size:11px;color:#555;">Coverage Basis: <strong>' + limitsObj._coverageType + '</strong></span>';
      // A sum insured means little without what it is valued on and what it
      // sits against.
      if (limitsObj._valuationBasis) limitsCell += '<span style="font-size:11px;color:#555;">Valuation: <strong>' + escapeHtml(limitsObj._valuationBasis) + '</strong></span><br>';
      if (limitsObj._policyLimit) limitsCell += '<span style="font-size:11px;color:#555;">Policy limit of liability: <strong>' + escapeHtml(limitsObj._policyLimit) + '</strong></span><br>';
      if (limitsObj._coinsurance) limitsCell += '<span style="font-size:11px;color:#555;"> | Coinsurance: <strong>' + limitsObj._coinsurance + '</strong></span>';
      if (limitsObj._coverageType || limitsObj._coinsurance) limitsCell += '<br>';
      // Three coverages each showing $1,000,000 with no note reads as
      // $3,000,000 of available limit. Saying it is shared is the difference
      // between a correct row and an overstatement.
      if (limitsObj._sharedLimit) limitsCell += '<div style="font-size:11px;color:#92400E;margin-bottom:3px;">\u26a0\ufe0f ' + escapeHtml(limitsObj._sharedLimit) + '</div>';
      limitsCell += formattedLimits;
      // A sum insured means little without what it sits over. On a
      // scheduled-asset placement the total insured values, the rating basis
      // and whether the nat-cat limit is shared are the finding.
      limitsCell += renderDeductibles(limitsObj._deductibles, d.states);
      const placementNotes = [limitsObj._tiv ? 'Total insured values: <strong>' + limitsObj._tiv + '</strong>' : null,
        limitsObj._rating, limitsObj._premium ? 'Premium ' + limitsObj._premium : null,
        limitsObj._aggregateNote, limitsObj._portfolioNote].filter(Boolean);
      if (placementNotes.length) {
        limitsCell += '<div style="margin-top:5px;font-size:11px;color:#555;">' + placementNotes.join('<br>') + '</div>';
      }
      /* THE LIMITS THE POLICY STATES THAT NO SPEC ASKED FOR.
       *
       * Kept beneath the mapped limits and visually quieter, because there
       * are usually more of them and the benchmark comparison is what the row
       * exists for. But they are the difference between a property row that
       * says "building and contents" and one that tells a broker the debris
       * removal, extra expense and valuable-papers sublimits — which is what
       * a property placement is actually argued over.
       *
       * Printed in the policy's own wording, with its page, because the whole
       * point is that no canonical label was available for them. */
      const otherLimits = (limitsObj._other || []);
      if (otherLimits.length) {
        limitsCell += '<details style="margin-top:6px;">'
          + '<summary style="font-size:11px;color:#01696F;cursor:pointer;">'
          + otherLimits.length + ' further limit' + (otherLimits.length === 1 ? '' : 's')
          + ' stated in this policy</summary>'
          + '<div style="font-size:11px;color:#555;margin-top:4px;">'
          + otherLimits.map(o =>
              '<strong>' + escapeHtml(String(o.value)) + '</strong> &mdash; ' + escapeHtml(String(o.label))
              + ' <span style="color:#9CA3AF;">(' + escapeHtml(String(o.file)) + ' p.' + o.page + ')</span>')
             .join('<br>')
          + '</div></details>';
      }
      limitsCell += '</div>';
    } else if ((extractedLimits || {})._writtenAs) {
      // No separate limit, because the policy writes it inside another form.
      // Stating that is an answer; "limits not parsed" is not.
      const carrier = carrierPerCoverage[b.key] || carrierPerCoverage[b.type] || null;
      limitsCell = '<div style="padding:6px 8px;background:#f0f7f7;border-radius:4px;border-left:3px solid #01696F;">'
        + (carrier ? '<strong>' + carrier + '</strong><br>' : '')
        + '<span style="font-size:11px;color:#333;">'
        + escapeHtml(extractedLimits._writtenAs) + '</span>'
        + renderDeductibles((extractedLimits || {})._deductibles, d.states) + '</div>';
    } else if ((extractedLimits || {})._declined) {
      const _dc = extractedLimits._declined;
      limitsCell = '<em style="color:var(--critical);" data-stance="declined">Not covered \u2014 declined on the declarations</em>'
        + '<div style="font-size:11px;color:#555;margin-top:2px;">\u201c' + escapeHtml(_dc.quote || '') + '\u201d'
        + (_dc.fileName ? ' \u2014 ' + escapeHtml(_dc.fileName) : '') + (_dc.page ? ', p.' + _dc.page : '') + '</div>';
    } else if (hasCoverage(b.key)) {
      const carrier = carrierPerCoverage[b.key] || carrierPerCoverage[b.type] || null;
      /* A STABLE MARKER, because the sentence is now variable.
       *
       * Downstream checks — "no row claims MEETS beside unparsed limits" —
       * identified these rows by grepping the exact wording. Improving the
       * wording broke the check, which is the wrong way round: the fact that
       * a limit did not parse is structural and should not be carried by
       * prose that is meant to change.
       */
      limitsCell = '<div data-limits="unparsed" style="padding:6px 8px;background:#fff8e1;border-radius:4px;border-left:3px solid #B8860B;">';
      if (carrier) limitsCell += '<strong>' + carrier + '</strong><br>';
      const sawSchedule = scheduleSeenFor(b.key) ;
      /* One sentence for five situations is one sentence too few.
       *
       * "Coverage detected — limits not parsed from declarations" covered: no
       * policy carries this coverage, no declarations page, no field for the
       * slot, a figure found and REFUSED, and a genuine parse failure. Each
       * needs a different action from the broker, and each had to be
       * diagnosed by hand this week by reading the PDF.
       *
       * The refusal case matters most and silence hid it completely: a refusal
       * is a decision this engine made, and a reviewer is entitled to see it.
       */
      const _stage = (typeof spExtractionStage === 'function')
        ? spExtractionStage(d.pdfTexts || [], b.type || b.key) : null;
      const _stageSaid = _stage && _stage.detail
        && (_stage.stage === 'no-document' || _stage.stage === 'no-declarations'
            || _stage.stage === 'no-field' || _stage.stage === 'refused');
      limitsCell += _stageSaid
        ? '<em style="color:var(--warning);">Coverage detected — ' + escapeHtml(_stage.detail) + '</em>'
        : (sawSchedule === false
          ? '<em style="color:var(--warning);">Coverage detected — the uploaded document contains no schedule of benefits or declarations page. Request the schedule to confirm limits.</em>'
          : '<em style="color:var(--warning);">Coverage detected — limits not parsed from declarations</em>');
      // Both passes, always - see policyParseLedger.
      limitsCell += renderParseLedger(b.key);
      // The retention is still a fact about the program, and on this row it is
      // the more consequential one: a 2% per-building wind/hail deductible
      // matters whether or not the limit above it parsed.
      limitsCell += renderDeductibles((extractedLimits || {})._deductibles, d.states);
      limitsCell += '</div>';
    } else {
      /* "Not found" and "the carrier declined it" are different findings.
       *
       * Tulsa Honor Academy's property policy answers the Equipment Breakdown
       * question three times — "Equipment Breakdown   No Coverage" — and
       * attaches CC EQB 004, an Equipment Breakdown Exclusion. Reporting that
       * as "not found in uploaded policies" makes a decision the carrier made
       * look like something the documents were silent about, and a broker
       * reads the two very differently. Quote the refusal.
       */
      const _gr = grantedFor(b.key);
      const _ref = refusedFor(b.key);
      if (!_ref && _gr) {
      /* Where the policy states the figure in a sentence, quote the sentence.
       * The field reader will not promote a clause figure to a limit and
       * should not — that rule kept a TRIA threshold out of a D&O row. But
       * refusing to parse a number is not a reason to hide it, and the
       * sentence carries caveats no limits field could: Matrix Head Start's
       * $250,000 is part of and not in addition to the EPL aggregate. */
        const _g = _gr;
        /* The grant's own line wins when it carries a figure. The nearest
         * sentence with a dollar amount is a different coverage as often as
         * not: Holloway's Workplace Violence Expenses extension ($100,000)
         * was reported as "$2,000,000", the EPL Third Party sublimit on the
         * row above. */
        const _own = /\$\s*[\d][\d,]{3,}/.exec(_g.quote || '');
        const _src = _own ? _g : (_g.stated || _g);
        limitsCell = '<em style="color:var(--warning);" data-stance="granted">'
          + (_own
              ? 'Covered \u2014 ' + escapeHtml(_own[0].replace(/\s+/g, '')) + ' stated in the policy'
              : _g.stated
              ? 'Covered \u2014 ' + escapeHtml(_g.stated.amount)
                + ' stated in the policy wording, not in a declarations field'
              : 'Covered \u2014 granted by the policy, no declarations limit parsed')
          + '</em><div style="font-size:11px;color:#555;margin-top:2px;">\u201c'
        + escapeHtml(String(_src.quote || '').replace(/\\(?=[*_])/g, '').replace(/[*_]{1,2}/g, '')) + '\u201d'
        + (_src.fileName ? ' \u2014 ' + escapeHtml(_src.fileName) : '')
        + (_src.page ? ', p.' + _src.page : '') + '</div>';
      } else
      limitsCell = _ref
        ? '<em style="color:var(--critical);" data-stance="' + _ref.stance + '">'
          + (_ref.stance === 'declined'
              ? 'Not covered \u2014 declined on the declarations'
              : 'Not covered \u2014 excluded by endorsement')
          + '</em><div style="font-size:11px;color:#555;margin-top:2px;">\u201c'
          + escapeHtml(_ref.quote || '') + '\u201d'
          + (_ref.fileName ? ' \u2014 ' + escapeHtml(_ref.fileName) : '')
          + (_ref.page ? ', p.' + _ref.page : '') + '</div>'
        : '<em style="color:var(--critical);">Not found in uploaded policies</em>';
    }
    
    const citationText = extractedLimits 
      ? `Limits extracted from policy declarations` 
      : (b.status === 'MEETS' ? 'Detected in uploaded policies' : b.status.startsWith('VERIFY') ? 'Requires verification against policy forms' : 'Not detected in uploaded documents');
    
    // v2.21.5: "Modified by" sub-block — show only HIGH-SIGNAL endorsements (top 3).
    // Routine "Other / Untitled" endorsements are referenced in Section 5B's appendix.
    let modifiedBy = '';
    if (d.endorsements && d.endorsements.byCoverage && d.endorsements.byCoverage[b.key]) {
      const isUntitledM = e => !e.title || e.title === 'Untitled Endorsement';
      const isHighSignalM = e =>
        e.tag === 'EXCLUSION_ADD' || e.tag === 'CARVE_BACK' || e.tag === 'GRANT'
        || e.tag === 'SUBLIMIT' || e.tag === 'AMENDATORY' || e.tag === 'NOTICE'
        || e.tag === 'ADDITIONAL_INSURED' || e.tag === 'WAIVER'
        || (e.tag === 'OTHER' && !isUntitledM(e));
      const fullList = d.endorsements.byCoverage[b.key];
      const eList = fullList.filter(isHighSignalM);
      const routineCount = fullList.length - eList.length;
      if (eList.length > 0) {
        const tagShort = { GRANT: 'Grant', CARVE_BACK: 'Carve-Back', SUBLIMIT: 'Sublimit', EXCLUSION_ADD: '⚠ Excl', AMENDATORY: 'Amend', NOTICE: 'Notice', ADDITIONAL_INSURED: 'AI', WAIVER: 'Waiver', OTHER: 'Other' };
        const tagPriority = { EXCLUSION_ADD: 0, CARVE_BACK: 1, GRANT: 2, SUBLIMIT: 3, AMENDATORY: 4, NOTICE: 5, ADDITIONAL_INSURED: 6, WAIVER: 7, OTHER: 8 };
        const sevPriority = { CRITICAL: 0, WARN: 1, NOTICE: 2, INFO: 3 };
        const sorted = eList.slice().sort((a, b) =>
          (sevPriority[a.severity] || 9) - (sevPriority[b.severity] || 9)
          || (tagPriority[a.tag] || 9) - (tagPriority[b.tag] || 9)
        );
        const topN = sorted.slice(0, 3);
        const items = topN.map(e => {
          const formStr = e.formNumber ? `<code style="font-size:10px;">${escapeHtml(e.formNumber)}</code> ` : '';
          const tagLabel = tagShort[e.tag] || e.tag;
          return `<li><strong>${tagLabel}:</strong> ${formStr}${escapeHtml(e.title || '').substring(0, 60)} <span style="color:#9CA3AF;">(p.${e.page})</span></li>`;
        }).join('');
        const moreSignal = eList.length - topN.length;
        const moreNote = (moreSignal > 0 || routineCount > 0)
          ? `<div style="font-size:10px;color:#6B7280;font-style:italic;margin-top:3px;">${moreSignal > 0 ? `+ ${moreSignal} more notable` : ''}${moreSignal > 0 && routineCount > 0 ? '; ' : ''}${routineCount > 0 ? `${routineCount} routine` : ''} (see Section 5B)</div>`
          : '';
        modifiedBy = `<div style="margin-top:6px;font-size:11px;color:var(--text-muted);"><strong>Modified by ${eList.length} notable endorsement${eList.length !== 1 ? 's' : ''}:</strong><ul style="margin:4px 0 0 0; padding-left:16px; font-size:11px;line-height:1.4;">${items}</ul>${moreNote}</div>`;
      } else if (routineCount > 0) {
        // Only routine endorsements detected — show a single-line note
        modifiedBy = `<div style="margin-top:6px;font-size:10px;color:#9CA3AF;font-style:italic;">${routineCount} routine endorsement${routineCount !== 1 ? 's' : ''} detected (see Section 5B appendix)</div>`;
      }
    }
    html += `<tr><td><strong>${b.type}</strong>${modifiedBy}</td><td>${b.statutory}</td><td>${b.standard}</td><td>${limitsCell}</td><td>${badge}</td><td style="font-size:11px;color:var(--text-muted);font-style:italic;">[Industry Benchmarking, Sec. 6: "${b.type} — ${citationText}"]</td></tr>`;
  });

  // Maritime benchmarks injected into same table (contextual filter: suppress for non-maritime verticals)
  const isNonMaritimeVertical = isEducation || isHealthcare || isConstruction || isManufacturing;
  if ((isMaritime && !isNonMaritimeVertical) || (hasAnyMarine && !isNonMaritimeVertical) || (d.states.includes('LA') && !isNonMaritimeVertical && (isMaritime || hasAnyMarine))) {
    const marineBenchmarks = [
      { type:"Marine General Liability", key:"Marine General Liability", statutory:"No specific statutory minimum", standard:"$1M occ / $2M agg", best:"$2M occ / $5M agg with P&I integration", status: hasCoverage("Marine General Liability") ? "VERIFY" : ((isMaritime || hasAnyMarine) ? "HIGH GAP" : "VERIFY") },
      { type:"Hull & Machinery", key:"Hull & Machinery", statutory:"Lender-required for financed vessels", standard:"Agreed value of vessel + machinery", best:"Agreed value with salvage, sue & labor, and named storm coverage", status: hasCoverage("Hull & Machinery") ? "VERIFY" : ((isMaritime || hasAnyMarine) ? "CRITICAL GAP" : "VERIFY") },
      { type:"Protection & Indemnity (P&I)", key:"Protection & Indemnity", statutory:"Required for commercial vessels (OPA 90)", standard:"$1M–$5M per occurrence", best:"$5M–$25M+ through P&I club or layered program", status: hasCoverage("Protection & Indemnity") ? "VERIFY" : ((isMaritime || hasAnyMarine) ? "CRITICAL GAP" : "VERIFY") },
      { type:"Maritime Employers Liability (MEL)", key:"Maritime Employers Liability", statutory:"Required when employees on non-owned vessels", standard:"$1M per occurrence", best:"$1M+ with Jones Act, DOHSA, and General Maritime Law coverage", status: hasCoverage("Maritime Employers Liability") ? "VERIFY" : (isMaritime ? "HIGH GAP" : "VERIFY") },
      { type:"Jones Act / Crew Coverage", key:"Jones Act / Crew Coverage", statutory:"Federal — Jones Act (46 USC §30104)", standard:"Included in P&I for owned vessels", best:"P&I crew coverage + MEL for non-owned vessel employees; verify no specified-crew limitation", status: hasCoverage("Jones Act / Crew Coverage") ? "VERIFY" : (isMaritime ? "CRITICAL GAP" : "VERIFY") },
      { type:"USL&H / Longshore & Harbor Workers", key:"USL&H / Longshore", statutory:"Federal — 33 USC §901 (mandatory for waterfront employers)", standard:"Statutory benefits via WC endorsement (WC 00 01 01 A)", best:"USL&H endorsement on WC + MEL for vessel-based employees; rates typically 2x state WC", status: hasCoverage("USL&H / Longshore") ? "VERIFY" : (isMaritime ? "CRITICAL GAP" : "VERIFY") },
      { type:"Marine Builders Risk", key:"Marine Builders Risk", statutory:"No statutory minimum", standard:"Full contract value of vessel under construction/repair", best:"Contract value + launch risk + delay in delivery coverage", status: hasCoverage("Marine Builders Risk") ? "VERIFY" : (isMaritime ? "HIGH GAP" : "VERIFY") },
      { type:"Marine Cargo / Freight", key:"Marine Cargo / Freight", statutory:"No statutory minimum (carrier liability limited by Hague-Visby)", standard:"$500 per package (carrier default) — full CIF value recommended", best:"Open cargo policy at CIF+10% with Institute Cargo Clauses (A) — all risks", status: hasCoverage("Marine Cargo / Freight") ? "VERIFY" : (isMaritime ? "HIGH GAP" : "VERIFY") },
      { type:"Marine Pollution Liability", key:"Marine Pollution Liability", statutory:"OPA 90 — COFR required for vessels >300 GT", standard:"$1M–$5M per occurrence", best:"$5M–$25M+ with gradual pollution, COFR compliance, and OPA 90 defense costs", status: hasCoverage("Marine Pollution Liability") ? "VERIFY" : ((isMaritime || d.states.includes('LA')) ? "CRITICAL GAP" : "HIGH GAP") },
      { type:"Offshore Energy / OEE", key:"Offshore Energy / OEE", statutory:"BOEM financial assurance required for OCS operations", standard:"Control of well + $5M–$25M OEE", best:"$25M+ OEE with pollution, evacuation, decommissioning endorsements", status: hasCoverage("Offshore Energy / OEE") ? "VERIFY" : (isMaritime ? "HIGH GAP" : "VERIFY") },
      { type:"Dredging Insurance", key:"Dredging Insurance", statutory:"No statutory minimum (USACE contract bonds may apply)", standard:"$1M–$5M Marine GL + Hull + P&I", best:"$5M+ with subsurface utilities, environmental, and third-party property damage", status: hasCoverage("Dredging Insurance") ? "VERIFY" : (isMaritime ? "HIGH GAP" : "VERIFY") }
    ];
    
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">⚓ MARITIME / MARINE COVERAGE BENCHMARKS ${d.states.includes('LA') ? '— Louisiana Maritime Industry (20% of state employment)' : ''}</td></tr>`;
    marineBenchmarks.forEach(b => {
      const mExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const mComp = compareLimitToThreshold(mExtracted, b.standard);
      let effStatus = b.status;
      if (mExtracted && mComp) { if (mComp === 'EXCEEDS' || mComp === 'MEETS') effStatus = 'MEETS'; else if (mComp === 'BELOW MINIMUM') effStatus = 'BELOW STANDARD'; }
      let mbadge = '';
      if (effStatus === 'MEETS') mbadge = '<span class="badge badge-meets">✅ MEETS</span>';
      else if (effStatus === 'BELOW STANDARD') mbadge = '<span class="badge badge-high">⚠️ BELOW STANDARD</span>';
      else if (effStatus.startsWith('VERIFY')) mbadge = '<span class="badge badge-verify">⚠️ ' + effStatus + '</span>';
      else if (effStatus === 'CRITICAL GAP') mbadge = '<span class="badge badge-critical">❌ CRITICAL GAP</span>';
      else mbadge = '<span class="badge badge-high">❌ HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${mbadge}</td>${benchCitation(b)}`;
    });
  }

  // Energy / Oil & Gas benchmarks injected into same table (contextual filter: suppress for non-energy verticals)
  // Equipment Rental companies may have energy-adjacent coverages (Control of Well, etc.) but should NOT
  // trigger the full Oil & Gas benchmarking section unless user explicitly selects an O&G industry vertical.
  const isEquipmentRental = vl.includes('equipment rental') || vl.includes('heavy equipment') || vl.includes('tool rental') || vl.includes('rental company');
  const isNonEnergyVertical = isEducation || isHealthcare || isConstruction || isManufacturing || isEquipmentRental || isOGSupplier || isRenewable;
  if ((isEnergy && !isNonEnergyVertical) || (hasAnyEnergy && !isNonEnergyVertical) || (d.states.includes('TX') && !isNonEnergyVertical && (isEnergy || hasAnyEnergy))) {
    const energyBenchmarks = [
      { type:"Control of Well / OEE (Onshore)", key:"Control of Well / OEE (Onshore)", statutory:"RRC financial assurance required (P-5/P-5PB bonds)", standard:"$5M\u2013$25M COW + OEE with seepage & pollution", best:"$25M+ OEE with full-limit seepage/pollution, redrilling, evacuation, and third-party property damage", status: hasCoverage("Control of Well / OEE (Onshore)") ? "VERIFY" : ((isEnergy || hasAnyEnergy) ? "CRITICAL GAP" : "VERIFY") },
      { type:"Pollution Legal Liability (PLL/EIL)", key:"Pollution Legal Liability (PLL/EIL)", statutory:"TCEQ permits; EPA RCRA/CERCLA compliance", standard:"$1M\u2013$5M PLL with gradual pollution trigger", best:"$5M\u2013$25M+ standalone PLL/EIL covering gradual pollution, produced water, injection wells, NORM, transportation pollution", status: hasCoverage("Pollution Legal Liability (PLL/EIL)") ? "VERIFY" : ((isEnergy || hasAnyEnergy) ? "CRITICAL GAP" : "HIGH GAP") },
      { type:"Rig & Drilling Equipment", key:"Rig & Drilling Equipment", statutory:"No statutory minimum", standard:"Replacement cost of rig + downhole tools", best:"Agreed value with transit, mobilization/demobilization, and equipment breakdown", status: hasCoverage("Rig & Drilling Equipment") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Oil Lease Property (OLP)", key:"Oil Lease Property (OLP)", statutory:"No statutory minimum", standard:"Replacement cost of lease equipment (wellheads, pumps, tanks, flow lines)", best:"Full replacement cost with extra expense, debris removal, and well re-entry costs", status: hasCoverage("Oil Lease Property (OLP)") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Pipeline Operators Insurance", key:"Pipeline Operators Insurance", statutory:"PHMSA financial responsibility; RRC pipeline permits", standard:"$1M\u2013$5M pipeline liability + property", best:"$5M\u2013$25M+ with pollution, third-party damage, business interruption, and right-of-way coverage", status: hasCoverage("Pipeline Operators Insurance") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Gas Processing & Compression", key:"Gas Processing & Compression", statutory:"TCEQ air permits; OSHA PSM (>10,000 lbs flammables)", standard:"Property + BI matched to facility value", best:"Full property/BI with machinery breakdown, H2S release, and explosion coverage; PSM compliance endorsement", status: hasCoverage("Gas Processing & Compression") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Terminal & Tank Farm", key:"Terminal & Tank Farm", statutory:"EPA SPCC plan required; TCEQ storage tank registration", standard:"Property + pollution for tank farm value", best:"Full property/BI + pollution + tank integrity + product contamination coverage", status: hasCoverage("Terminal & Tank Farm") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Refinery & Petrochemical", key:"Refinery & Petrochemical", statutory:"OSHA PSM; EPA RMP; TCEQ air/water permits", standard:"$50M\u2013$500M+ property/BI (depending on facility size)", best:"Layered property/BI program with turnaround coverage, pollution, and business interruption including CBI/contingent BI", status: hasCoverage("Refinery & Petrochemical") ? "VERIFY" : (isEnergy ? "CRITICAL GAP" : "VERIFY") },
      { type:"NORM Liability", key:"NORM Liability", statutory:"TCEQ NORM disposal rules (25 TAC \u00a7289); DSHS licensing", standard:"Coverage via energy package or standalone", best:"Standalone NORM liability with worker exposure, remediation, disposal, and transportation coverage; carve-back to nuclear exclusion", status: hasCoverage("NORM Liability") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Underground Resource Damage", key:"Underground Resource Damage", statutory:"No statutory minimum (common law trespass applies)", standard:"$1M\u2013$5M underground resource damage", best:"$5M+ with frac hit coverage, induced seismicity, reservoir damage, and subsurface trespass defense costs", status: hasCoverage("Underground Resource Damage") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"RRC Compliance & Bonding", key:"RRC Compliance & Bonding", statutory:"P-5 Organization Report + P-5PB Bond ($25K individual / $250K blanket); well plugging assurance", standard:"Statewide blanket bond + insurance backing", best:"Blanket bond at maximum tier + insurance-backed plugging assurance + orphan well protection", status: hasCoverage("RRC Compliance & Bonding") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"MSA / Contractual Liability", key:"MSA / Contractual Liability (Energy)", statutory:"TX Anti-Indemnity Act (Civ. Prac. & Rem. Code \u00a7127)", standard:"CGL with insured contract exception + AI endorsement", best:"CGL contractual liability confirmed + MSA-specific additional insured + excess/umbrella following form; all MSAs audited against \u00a7127", status: hasCoverage("MSA / Contractual Liability (Energy)") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") },
      { type:"Business Interruption (Energy)", key:"Business Interruption (Energy)", statutory:"No statutory minimum", standard:"12\u201318 month indemnity period matched to production value", best:"24+ month indemnity + contingent BI + loss of production income (LOPI) + wellbore/downhole coverage", status: hasCoverage("Business Interruption (Energy)") ? "VERIFY" : (isEnergy ? "HIGH GAP" : "VERIFY") }
    ];
    
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\u26fd ONSHORE ENERGY / OIL & GAS COVERAGE BENCHMARKS ${d.states.includes('TX') ? '\u2014 Texas Energy Sector (largest U.S. oil & gas producer)' : ''}</td></tr>`;
    energyBenchmarks.forEach(b => {
      const eExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const eComp = compareLimitToThreshold(eExtracted, b.standard);
      let eEffStatus = b.status;
      if (eExtracted && eComp) { if (eComp === 'EXCEEDS' || eComp === 'MEETS') eEffStatus = 'MEETS'; else if (eComp === 'BELOW MINIMUM') eEffStatus = 'BELOW STANDARD'; }
      let ebadge = '';
      if (eEffStatus === 'MEETS') ebadge = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (eEffStatus === 'BELOW STANDARD') ebadge = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (eEffStatus.startsWith('VERIFY')) ebadge = '<span class="badge badge-verify">\u26a0\ufe0f ' + eEffStatus + '</span>';
      else if (eEffStatus === 'CRITICAL GAP') ebadge = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else ebadge = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${ebadge}</td>${benchCitation(b)}`;
    });
  }

  // Transportation / Logistics benchmarks (suppress for non-transportation verticals)
  const isNonTransportVertical = isEducation || isHealthcare;
  if ((isTransportation && !isNonTransportVertical) || (hasAnyTransportation && !isNonTransportVertical)) {
    const transportationBenchmarks = [
      { type:"Motor Carrier Liability", key:"Motor Carrier Liability", statutory:"$750K BIPD (non-hazmat, GVWR \u226510,001 lbs) | $1M\u2013$5M (hazmat) \u2014 49 CFR \u00a7387.9", standard:"$1M\u2013$5M primary auto liability", best:"$5M primary + umbrella/excess to $25M+ for interstate operations", status: hasCoverage("Motor Carrier Liability") ? "VERIFY" : (isTransportation ? "CRITICAL GAP" : "VERIFY") },
      { type:"Motor Truck Cargo (MTC)", key:"Motor Truck Cargo", statutory:"No federal mandate (Carmack Amendment limits carrier liability to declared value)", standard:"$100K\u2013$500K per occurrence (depends on commodity)", best:"All-risk cargo on open form; limits matched to highest-value single shipment", status: hasCoverage("Motor Truck Cargo") ? "VERIFY" : (isTransportation ? "CRITICAL GAP" : "VERIFY") },
      { type:"MCS-90 Endorsement", key:"MCS-90 Compliance", statutory:"Mandatory for all for-hire interstate motor carriers \u2014 49 CFR \u00a7387.15", standard:"Attached to primary auto liability policy", best:"Confirm MCS-90 attached AND underlying auto policy has no voidable exclusions that could trigger insurer reimbursement rights", status: hasCoverage("MCS-90 Compliance") ? "VERIFY" : (isTransportation ? "CRITICAL GAP" : "VERIFY") },
      { type:"Bobtail / Non-Trucking Liability", key:"Bobtail / Non-Trucking Liability", statutory:"No statutory minimum (gap in motor carrier's primary policy)", standard:"$1M per occurrence for owner-operators", best:"$1M bobtail + $1M NTL for all owner-operators; separate coverage for dispatch vs. personal use clearly defined", status: hasCoverage("Bobtail / Non-Trucking Liability") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Trailer Interchange", key:"Trailer Interchange", statutory:"No statutory minimum (contractual UIIA requirement: $15K min)", standard:"$20K\u2013$30K per trailer", best:"$50K+ per trailer for Amazon Relay, JB Hunt 360, and premium freight broker requirements; written interchange agreement on file", status: hasCoverage("Trailer Interchange") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Garagekeepers Liability", key:"Garagekeepers Liability", statutory:"No statutory minimum", standard:"$500K\u2013$1M direct primary", best:"Direct primary form (not legal liability) matched to max value of vehicles in care at one time", status: hasCoverage("Garagekeepers Liability") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Refrigeration Breakdown (Reefer)", key:"Refrigeration Breakdown", statutory:"No statutory minimum (FDA FSMA temperature control requirements apply)", standard:"Endorsed onto MTC policy; limits matched to highest-value reefer load", best:"Standalone reefer breakdown endorsement with no maintenance warranty gaps; confirmed coverage for mechanical failure vs. driver error", status: hasCoverage("Refrigeration Breakdown") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Warehousing Legal Liability (WLL)", key:"Warehousing Legal Liability", statutory:"No statutory minimum (Uniform Commercial Code and warehouse receipt terms may apply)", standard:"$1M\u2013$5M WLL", best:"$5M+ WLL with fire, theft, sprinkler leakage, and employee dishonesty extensions; bailee-specific form", status: hasCoverage("Warehousing Legal Liability") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Freight Broker E&O", key:"Freight Broker E&O", statutory:"$75,000 surety bond / trust fund (BMC-84/BMC-85) \u2014 49 CFR \u00a7387.307", standard:"$100K\u2013$500K E&O per claim", best:"$1M E&O per claim / $2M aggregate with tail coverage; BMC-84 surety maintained separately", status: hasCoverage("Freight Broker E&O") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") },
      { type:"Contingent Cargo", key:"Contingent Cargo", statutory:"No statutory minimum", standard:"$100K\u2013$500K contingent cargo", best:"$1M+ contingent cargo; confirm secondary trigger (carrier policy failure only) is understood and carrier vetting program documented", status: hasCoverage("Contingent Cargo") ? "VERIFY" : (isTransportation ? "HIGH GAP" : "VERIFY") }
    ];
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\ud83d\ude9a TRANSPORTATION / LOGISTICS COVERAGE BENCHMARKS \u2014 FMCSA Regulated Motor Carriers & Freight Brokers</td></tr>`;
    transportationBenchmarks.forEach(b => {
      const tExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const tComp = compareLimitToThreshold(tExtracted, b.standard);
      let tEffStatus = b.status;
      if (tExtracted && tComp) { if (tComp === 'EXCEEDS' || tComp === 'MEETS') tEffStatus = 'MEETS'; else if (tComp === 'BELOW MINIMUM') tEffStatus = 'BELOW STANDARD'; }
      let tbadge = '';
      if (tEffStatus === 'MEETS') tbadge = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (tEffStatus === 'BELOW STANDARD') tbadge = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (tEffStatus.startsWith('VERIFY')) tbadge = '<span class="badge badge-verify">\u26a0\ufe0f ' + tEffStatus + '</span>';
      else if (tEffStatus === 'CRITICAL GAP') tbadge = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else tbadge = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${tbadge}</td>${benchCitation(b)}`;
    });
  }

  // The CONSTRUCTION COVERAGE BENCHMARKS block is removed. It fired on
  // `hasAnyConstruction` — any one of Builders Risk, Wrap-Up, SDI, Contractors
  // Pollution and the rest — so an energy insured carrying a Contractors
  // Pollution Liability coverage part was handed a general contractor's
  // benchmark table: builders risk on completed contract value, OCIP/CCIP
  // enrolment, subcontractor default, the 10-year statute of repose. None of
  // it bore on the account, and a table of gaps that do not apply crowds out
  // the findings that do.

  // ===== REAL ESTATE SPECIALIZED BENCHMARKS =====
  const isRealEstateV = vl.includes('real estate') || vl.includes('property management') || vl.includes('residential development') || vl.includes('reit') || vl.includes('landlord');
  const hasAnyRealEstate = d.detectedCoverages.some(c => ['Property Managers Professional Liability', 'Tenant Discrimination Liability', 'Environmental / Mold Liability', 'Habitability / Code Compliance', 'Loss of Rents / Rental Income', 'Ordinance or Law Coverage'].includes(c));
  if (isRealEstateV || hasAnyRealEstate) {
    const realEstateBenchmarks = [
      { type:"Property Managers Professional Liability", key:"Property Managers Professional Liability", statutory:"No statutory minimum (contractually required by most management agreements)", standard:"$1M per claim / $2M aggregate", best:"$3M\u2013$5M with full prior acts; covers leasing errors, tenant screening mistakes, maintenance oversight, and CAM reconciliation errors", status: hasCoverage("Property Managers Professional Liability") ? "VERIFY" : (isRealEstateV ? "CRITICAL GAP" : "VERIFY") },
      { type:"Tenant Discrimination / Fair Housing", key:"Tenant Discrimination Liability", statutory:"Fair Housing Act / HUD / state fair housing laws apply to all residential property managers", standard:"$1M per claim with FHA defense", best:"$2M+ standalone with HUD administrative proceeding defense, ADA/FHA reasonable accommodation coverage, and tenant screening E&O", status: hasCoverage("Tenant Discrimination Liability") ? "VERIFY" : (isRealEstateV ? "HIGH GAP" : "VERIFY") },
      { type:"Environmental / Mold Liability", key:"Environmental / Mold Liability", statutory:"EPA/state environmental laws; mold disclosure varies by state", standard:"$1M\u2013$3M environmental impairment", best:"$5M+ premises pollution with mold, asbestos, lead paint, radon, and Legionella; includes remediation and tenant relocation", status: hasCoverage("Environmental / Mold Liability") ? "VERIFY" : (isRealEstateV ? "HIGH GAP" : "VERIFY") },
      { type:"Loss of Rents / Rental Income", key:"Loss of Rents / Rental Income", statutory:"No statutory minimum", standard:"12 months actual loss sustained", best:"18\u201324 months with extended period of indemnity; tenant default and lease cancellation provisions; renovation coverage", status: hasCoverage("Loss of Rents / Rental Income") ? "VERIFY" : (isRealEstateV ? "HIGH GAP" : "VERIFY") },
      { type:"Ordinance or Law Coverage", key:"Ordinance or Law Coverage", statutory:"No statutory minimum (lenders may require)", standard:"Coverage A + B + C at 25% of building value", best:"A (undamaged portion), B (demolition), C (increased cost of construction) at 50%+ of building value; critical for buildings over 20 years", status: hasCoverage("Ordinance or Law Coverage") ? "VERIFY" : (isRealEstateV ? "HIGH GAP" : "VERIFY") },
      { type:"Equipment Breakdown / Boiler & Machinery", key:"Equipment Breakdown / Boiler & Machinery", statutory:"Many jurisdictions require boiler inspection certificates", standard:"Replacement cost of all covered equipment", best:"Comprehensive with HVAC, elevator, electrical panel, and fire suppression; service interruption extension", status: hasCoverage("Equipment Breakdown / Boiler & Machinery") ? "VERIFY" : (isRealEstateV ? "HIGH GAP" : "VERIFY") },
    ];
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\ud83c\udfe2 REAL ESTATE COVERAGE BENCHMARKS \u2014 Property Management, Development & Investment</td></tr>`;
    realEstateBenchmarks.forEach(b => {
      const cExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const cComp = compareLimitToThreshold(cExtracted, b.standard);
      let cEffStatus = b.status;
      if (cExtracted && cComp) { if (cComp === 'EXCEEDS' || cComp === 'MEETS') cEffStatus = 'MEETS'; else if (cComp === 'BELOW MINIMUM') cEffStatus = 'BELOW STANDARD'; }
      let cbadge = '';
      if (cEffStatus === 'MEETS') cbadge = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (cEffStatus === 'BELOW STANDARD') cbadge = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (cEffStatus.startsWith('VERIFY')) cbadge = '<span class="badge badge-verify">\u26a0\ufe0f ' + cEffStatus + '</span>';
      else if (cEffStatus === 'CRITICAL GAP') cbadge = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else cbadge = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${cbadge}</td>${benchCitation(b)}`;
    });
  }

  // Healthcare benchmarks (suppress for non-healthcare verticals)
  const isNonHealthcareVertical = isEducation || isConstruction || isManufacturing
    || vl.includes('energy') || vl.includes('utility') || vl.includes('utilities')
    || vl.includes('oil') || vl.includes('gas') || vl.includes('power')
    || vl.includes('mining') || vl.includes('trucking') || vl.includes('logistics')
    || vl.includes('real estate') || vl.includes('hospitality') || vl.includes('restaurant')
    || vl.includes('retail') || vl.includes('agricultur') || vl.includes('saas') || vl.includes('software');
  if ((isHealthcare && !isNonHealthcareVertical) || (hasAnyHealthcare && !isNonHealthcareVertical)) {
    const healthcareBenchmarks = [
      { type:"Medical Professional Liability (MPL)", key:"Medical Professional Liability", statutory:"State-mandated minimums (e.g., IN: $250K/$750K; FL: $250K/$750K; NY: $1.3M/$3.9M)", standard:"$1M\u2013$3M per occurrence claims-made", best:"$3M\u2013$5M occurrence or claims-made with unlimited tail; retroactive date matching inception; CRNA/NP sublimits reviewed", status: hasCoverage("Medical Professional Liability") ? "VERIFY" : (isHealthcare ? "CRITICAL GAP" : "VERIFY") },
      { type:"Hospital Professional Liability (HPL)", key:"Hospital Professional Liability", statutory:"State hospital licensure requirements (varies by state)", standard:"$5M\u2013$25M HPL (facility size dependent)", best:"$25M+ HPL covering all employed/contracted providers; vicarious liability for staffing agencies; per-occurrence trigger", status: hasCoverage("Hospital Professional Liability") ? "VERIFY" : (isHealthcare ? "CRITICAL GAP" : "VERIFY") },
      { type:"HIPAA / Privacy Breach Liability", key:"HIPAA/Privacy Breach Liability", statutory:"HHS OCR HIPAA enforcement ($100\u2013$2M per violation category/year)", standard:"$1M\u2013$5M cyber/privacy liability", best:"$5M+ with HIPAA regulatory defense, breach notification, forensics, business interruption; explicit HIPAA fines coverage", status: hasCoverage("HIPAA/Privacy Breach Liability") ? "VERIFY" : (isHealthcare ? "CRITICAL GAP" : "VERIFY") },
      { type:"Sexual Abuse & Molestation (SAM)", key:"Sexual Abuse/Molestation (SAM)", statutory:"No explicit statutory minimum", standard:"$1M\u2013$3M SAM with separate aggregate", best:"$3M+ standalone SAM; defense costs outside limits; negligent supervision covered; criminal acts carve-back confirmed", status: hasCoverage("Sexual Abuse/Molestation (SAM)") ? "VERIFY" : (isHealthcare ? "CRITICAL GAP" : "HIGH GAP") },
      { type:"Clinical Trial Liability", key:"Clinical Trial Liability", statutory:"FDA IND/IDE requirements; IRB protocol review", standard:"$3M\u2013$5M per occurrence", best:"$5M+ per-occurrence clinical trial liability; IRB approval required; covers investigational drug/device and off-label protocols", status: hasCoverage("Clinical Trial Liability") ? "VERIFY" : (isHealthcare ? "HIGH GAP" : "VERIFY") },
      { type:"Telemedicine / Telehealth Coverage", key:"Telemedicine/Telehealth Coverage", statutory:"State telehealth parity laws; multi-state licensure requirements", standard:"$1M\u2013$3M per occurrence", best:"$3M+ telehealth professional liability covering all states of licensure; HIPAA-compliant platform confirmation; bodily injury affirmed under each coverage grant", status: hasCoverage("Telemedicine/Telehealth Coverage") ? "VERIFY" : (isHealthcare ? "HIGH GAP" : "VERIFY") },
      { type:"Managed Care E&O (MCO)", key:"Managed Care E&O", statutory:"State insurance department requirements for MCOs/HMOs", standard:"$5M\u2013$10M MCO E&O", best:"$10M+ covering utilization review, pre-auth denial, coverage determination, and Medicare/Medicaid regulatory defense", status: hasCoverage("Managed Care E&O") ? "VERIFY" : (isHealthcare ? "HIGH GAP" : "VERIFY") },
      { type:"Vicarious Liability (Staffing)", key:"Vicarious Liability (Staffing)", statutory:"No statutory minimum (state hospital staffing requirements apply)", standard:"Included in HPL; separate endorsement for locum tenens", best:"Explicit vicarious liability for all contracted/staffing agency providers; locum tenens coverage confirmed; staffing agency indemnity agreements reviewed", status: hasCoverage("Vicarious Liability (Staffing)") ? "VERIFY" : (isHealthcare ? "HIGH GAP" : "VERIFY") },
      { type:"Medicare / Medicaid Defense", key:"Medicare/Medicaid Defense", statutory:"False Claims Act; Anti-Kickback Statute; Stark Law", standard:"$500K\u2013$2M regulatory defense", best:"$2M+ Medicare/Medicaid defense covering OIG investigations, RAC audits, CMS proceedings, and False Claims Act qui tam; active billing compliance program required", status: hasCoverage("Medicare/Medicaid Defense") ? "VERIFY" : (isHealthcare ? "HIGH GAP" : "VERIFY") }
    ];
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\u2665\ufe0f HEALTHCARE / MEDICAL MALPRACTICE COVERAGE BENCHMARKS \u2014 Hospitals, Physician Groups & Healthcare Facilities</td></tr>`;
    healthcareBenchmarks.forEach(b => {
      const hExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const hComp = compareLimitToThreshold(hExtracted, b.standard);
      let hEffStatus = b.status;
      if (hExtracted && hComp) { if (hComp === 'EXCEEDS' || hComp === 'MEETS') hEffStatus = 'MEETS'; else if (hComp === 'BELOW MINIMUM') hEffStatus = 'BELOW STANDARD'; }
      let hbadge = '';
      if (hEffStatus === 'MEETS') hbadge = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (hEffStatus === 'BELOW STANDARD') hbadge = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (hEffStatus.startsWith('VERIFY')) hbadge = '<span class="badge badge-verify">\u26a0\ufe0f ' + hEffStatus + '</span>';
      else if (hEffStatus === 'CRITICAL GAP') hbadge = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else hbadge = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${hbadge}</td>${benchCitation(b)}`;
    });
  }

  // Manufacturing / Industrial benchmarks (suppress for non-manufacturing verticals)
  const isNonManufacturingVertical = isEducation || isHealthcare;
  if ((isManufacturing && !isNonManufacturingVertical) || (hasAnyManufacturing && !isNonManufacturingVertical)) {
    const manufacturingBenchmarks = [
      { type:"Product Recall Insurance", key:"Product Recall Insurance", statutory:"CPSC recall authority; FDA mandatory recall (food, drugs, devices)", standard:"$5M\u2013$25M product recall per occurrence", best:"$25M\u2013$100M recall; both first-party (recall costs) and third-party (customer recall costs); brand rehabilitation and crisis management included", status: hasCoverage("Product Recall Insurance") ? "VERIFY" : (isManufacturing ? "CRITICAL GAP" : "VERIFY") },
      { type:"Product Contamination", key:"Product Contamination", statutory:"FDA FSMA compliance; USDA FSIS for meat/poultry", standard:"$5M\u2013$25M contamination per occurrence", best:"$25M+ covering accidental contamination, malicious tampering, and government advisory triggers; lost income and extra expense", status: hasCoverage("Product Contamination") ? "VERIFY" : (isManufacturing ? "CRITICAL GAP" : "VERIFY") },
      { type:"Equipment Breakdown / B&M", key:"Equipment Breakdown / Boiler & Machinery", statutory:"OSHA PSM (29 CFR 1910.119) for processes with highly hazardous chemicals", standard:"$5M\u2013$25M per breakdown occurrence", best:"Full equipment replacement cost + business interruption during downtime + spoilage of goods; service interruption endorsement for utility failures", status: hasCoverage("Equipment Breakdown / Boiler & Machinery") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Supply Chain / Contingent BI (CBI)", key:"Supply Chain / Contingent BI", statutory:"No statutory minimum", standard:"12\u201318 month indemnity period; direct suppliers scheduled", best:"24+ month indemnity; tier-2/tier-3 suppliers added; worldwide territory; confirm physical damage trigger (not pandemic/government action)", status: hasCoverage("Supply Chain / Contingent BI") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Environmental Impairment Liability (EIL)", key:"Environmental Impairment Liability", statutory:"EPA CERCLA/RCRA; state environmental regulations", standard:"$2M\u2013$10M EIL per occurrence", best:"$10M+ standalone EIL/PLL with gradual pollution trigger, on-site cleanup, off-site migration, third-party BI/PD; CERCLA retroactive coverage where available", status: hasCoverage("Environmental Impairment Liability") ? "VERIFY" : (isManufacturing ? "CRITICAL GAP" : "HIGH GAP") },
      { type:"OSHA Defense Coverage", key:"OSHA Defense Coverage", statutory:"OSHA fines not insurable as penalties; defense costs are insurable", standard:"$500K\u2013$1M OSHA defense per proceeding", best:"$1M+ OSHA defense with citation response, informal conference, ALJ appeal, and Occupational Safety Health Review Commission coverage", status: hasCoverage("OSHA Defense Coverage") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Manufacturers E&O (Pure Economic Loss)", key:"Manufacturers E&O", statutory:"No statutory minimum", standard:"$1M\u2013$5M manufacturers E&O per claim", best:"$5M+ covering non-conforming product, specification errors, late delivery, and pure economic loss without BI/PD requirement", status: hasCoverage("Manufacturers E&O") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Stock Throughput Policy (STP)", key:"Stock Throughput (STP)", statutory:"No statutory minimum", standard:"Full inventory value at all stages (raw materials through finished goods)", best:"Open-form all-risk STP covering ocean transit + inland transit + storage; no time limits; limits matching peak inventory value", status: hasCoverage("Stock Throughput (STP)") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Foreign Product Liability", key:"Foreign Product Liability", statutory:"CPSC jurisdiction for imported products; importers treated as manufacturers", standard:"$5M\u2013$25M worldwide products liability", best:"$25M+ worldwide products liability; confirm coverage territory extends to all export markets; suits in foreign jurisdictions covered; CPSC import alert defense costs", status: hasCoverage("Foreign Product Liability") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") },
      { type:"Inland Marine / Goods in Process", key:"Inland Marine (Goods in Process)", statutory:"No statutory minimum", standard:"Full value of WIP inventory at all stages", best:"Open-form inland marine covering WIP + materials at outside processors + finished goods in transit; no per-location sublimits", status: hasCoverage("Inland Marine (Goods in Process)") ? "VERIFY" : (isManufacturing ? "HIGH GAP" : "VERIFY") }
    ];
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\u2699\ufe0f INDUSTRIAL / MANUFACTURING COVERAGE BENCHMARKS \u2014 Product Manufacturers, Processors & Distributors</td></tr>`;
    manufacturingBenchmarks.forEach(b => {
      const mfExtracted = declLimits[b.key] || declLimits[b.type] || null;
      const mfComp = compareLimitToThreshold(mfExtracted, b.standard);
      let mfEffStatus = b.status;
      if (mfExtracted && mfComp) { if (mfComp === 'EXCEEDS' || mfComp === 'MEETS') mfEffStatus = 'MEETS'; else if (mfComp === 'BELOW MINIMUM') mfEffStatus = 'BELOW STANDARD'; }
      let mbadge2 = '';
      if (mfEffStatus === 'MEETS') mbadge2 = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (mfEffStatus === 'BELOW STANDARD') mbadge2 = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (mfEffStatus.startsWith('VERIFY')) mbadge2 = '<span class="badge badge-verify">\u26a0\ufe0f ' + mfEffStatus + '</span>';
      else if (mfEffStatus === 'CRITICAL GAP') mbadge2 = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else mbadge2 = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${mbadge2}</td>${benchCitation(b)}`;
    });
  }


  // Education / K-12 / Higher Education benchmarks
  if (isEducation) {
    const educationBenchmarks = [
      { type:"Educators Legal Liability (ELL)", key:"Educators Legal Liability (ELL)", statutory:"No federal mandate (state tort claims acts may apply to public schools)", standard:"$1M\u2013$2M per claim / $3M aggregate", best:"$5M\u2013$10M standalone ELL with defense-outside-limits, entity coverage for district/board, and Side A for individual board members; duty to defend included", status: hasCoverage("Educators Legal Liability (ELL)") ? "VERIFY" : (hasCoverage("EPLI") ? "VERIFY \u2014 CHECK IF ELL EMBEDDED IN EPLI" : "CRITICAL GAP") },
      { type:"Student Accident Insurance", key:"Student Accident Insurance", statutory:"No federal mandate (many states require notification of availability)", standard:"$25K\u2013$50K medical expense per accident", best:"$100K+ catastrophic student accident with 24-hour coverage (school + extracurricular); NCAA catastrophic excess for colleges ($90K deductible)", status: hasCoverage("Student Accident Insurance") ? "VERIFY" : "CRITICAL GAP" },
      { type:"Sexual Abuse & Molestation (SAM)", key:"Sexual Abuse/Molestation", statutory:"No explicit statutory minimum (Title IX imposes institutional duty)", standard:"$1M/$3M standalone or endorsed onto GL", best:"$2M/$5M standalone SAM with duty to defend; defense outside limits; no sunset clause; first-dollar defense; includes investigation costs and crisis counseling", status: hasCoverage("Sexual Abuse/Molestation") ? "VERIFY" : "CRITICAL GAP" },
      { type:"Active Shooter / Violent Event", key:"Active Shooter", statutory:"No statutory requirement (ALICE/Standard Response Protocol adopted by many states)", standard:"$1M with crisis response", best:"$5M+ with pre-event training, crisis management team, victim counseling, business interruption, and building repair/replacement; includes threat assessment and behavioral intervention", status: hasCoverage("Active Shooter") ? "VERIFY" : "CRITICAL GAP" },
      { type:"Athletic Participant / CTE Liability", key:"Athletic Participant Liability", statutory:"No federal mandate (state concussion return-to-play laws in all 50 states + DC)", standard:"$1M\u2013$5M participant accident + excess catastrophic", best:"$5M+ with CTE/traumatic brain injury coverage; no brain injury exclusion; covers all school-sponsored sports including club and intramural; includes concussion protocol compliance defense", status: hasCoverage("Athletic Participant Liability") ? "VERIFY" : "HIGH GAP" },
      { type:"Tuition Refund / Fee Interruption", key:"Tuition Refund Insurance", statutory:"No statutory requirement", standard:"75% refund of tuition for medical withdrawal", best:"100% tuition + room & board + fees refund for medical, mental health, and involuntary withdrawal; no pre-existing condition exclusion; covers full academic year", status: hasCoverage("Tuition Refund Insurance") ? "VERIFY" : (vl.includes("higher") || vl.includes("university") || vl.includes("college") ? "HIGH GAP" : "VERIFY") },
      { type:"Field Trip / Off-Premises Liability", key:"Field Trip / Off-Premises Liability", statutory:"No federal mandate (state tort immunity varies)", standard:"Covered under GL with off-premises extension", best:"Dedicated off-premises endorsement covering field trips, excursions, international travel, and overnight activities; includes hired/non-owned auto for parent driver coverage; volunteer coverage included", status: hasCoverage("Field Trip / Off-Premises Liability") ? "VERIFY" : "HIGH GAP" },
      { type:"School Bus / Pupil Transportation", key:"School Bus / Fleet Auto", statutory:"$1M\u2013$5M (varies by state; FMVSS safety standards apply)", standard:"$1M\u2013$5M commercial auto per occurrence", best:"$5M+ fleet auto with hired/non-owned; covers district-owned buses + contracted transportation; includes student loading/unloading zone coverage; GPS monitoring endorsement", status: hasCoverage("School Bus / Fleet Auto") ? "VERIFY" : "HIGH GAP" },
      { type:"FERPA / Student Data Breach", key:"Cyber Liability", statutory:"FERPA (20 USC \u00a71232g) \u2014 loss of ALL federal funding for violations", standard:"$1M\u2013$3M cyber with student records coverage", best:"$5M+ cyber with FERPA-specific compliance, student records breach response, regulatory defense, parent notification costs, and EdTech vendor breach coverage", status: hasCoverage("Cyber Liability") ? "VERIFY \u2014 CONFIRM FERPA COVERAGE" : "CRITICAL GAP" },
      { type:"Title IX Defense & Compliance", key:"D&O", keys: _SP_TITLE_IX_KEYS, statutory:"Title IX (20 USC \u00a71681) \u2014 loss of ALL federal funding for non-compliance", standard:"Covered under ELL/D&O with defense costs", best:"Dedicated Title IX endorsement covering investigation costs, OCR defense, independent investigator fees, appeal costs, and interim measure implementation; entity and individual coverage", status: _SP_TITLE_IX_KEYS.some(hasCoverage) ? "VERIFY \u2014 CONFIRM TITLE IX DEFENSE" : "CRITICAL GAP" },
      { type:"Playground / Premises (Minors)", key:"General Liability", statutory:"CPSC playground safety standards (voluntary but used as negligence benchmark)", standard:"$1M/$2M GL with premises coverage", best:"$2M/$4M GL with specific playground equipment coverage; ADA-accessible play area compliance; equipment inspection and maintenance endorsement; trampoline/inflatable exclusion review", status: hasCoverage("General Liability") ? "VERIFY \u2014 CONFIRM MINOR-SPECIFIC PREMISES" : "VERIFY" },
      { type:"Clery Act Compliance (Higher Ed)", key:"D&O", statutory:"Clery Act (20 USC \u00a71092(f)) \u2014 fines up to $69,733 per violation", standard:"Covered under ELL/D&O", best:"D&O/ELL with regulatory proceedings coverage; includes Clery Act audit defense, campus security report defense costs, and DOE compliance review coverage", status: (vl.includes("higher") || vl.includes("university") || vl.includes("college")) ? (hasCoverage("D&O") || hasCoverage("Educators Legal Liability (ELL)") ? "VERIFY \u2014 CONFIRM CLERY COMPLIANCE" : "HIGH GAP") : "N/A" }
    ];

    // Filter out N/A for K-12
    const filteredEdBenchmarks = educationBenchmarks.filter(b => b.status !== "N/A");
    
    html += `<tr><td colspan="6" style="background:var(--navy);color:#fff;font-weight:700;font-size:13px;padding:10px 12px;">\ud83c\udfeb EDUCATION COVERAGE BENCHMARKS \u2014 K-12 School Districts, Charter Schools, Private Schools & Higher Education</td></tr>`;
    filteredEdBenchmarks.forEach(b => {
      const edExtracted = _markScheduleMissing(b);
      const edComp = compareLimitToThreshold(edExtracted, b.standard);
      let edEffStatus = b.status;
      // A bare VERIFY on a row that says "Coverage detected" reads as doubt
      // about the COVERAGE. On Student Accident it is doubt about the
      // PAPERWORK: the coverage is in force and the upload is nine pages of
      // endorsements and notices with no schedule of benefits in it. Those are
      // different findings with different actions, and the badge is where a
      // reader decides which one they are looking at.
      if (edExtracted && edExtracted._scheduleMissing) edEffStatus = 'VERIFY \u2014 SCHEDULE NOT UPLOADED';
      if (edExtracted && edComp) { if (edComp === 'EXCEEDS' || edComp === 'MEETS') edEffStatus = 'MEETS'; else if (edComp === 'BELOW MINIMUM') edEffStatus = 'BELOW STANDARD'; }
      let ebadge3 = '';
      if (edEffStatus === 'MEETS') ebadge3 = '<span class="badge badge-meets">\u2705 MEETS</span>';
      else if (edEffStatus === 'BELOW STANDARD') ebadge3 = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
      else if (edEffStatus.startsWith('VERIFY')) ebadge3 = '<span class="badge badge-verify">\u26a0\ufe0f ' + edEffStatus + '</span>';
      else if (edEffStatus === 'CRITICAL GAP') ebadge3 = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
      else ebadge3 = '<span class="badge badge-high">\u274c HIGH GAP</span>';
      html += `<tr><td><strong>${b.type}</strong></td><td>${b.statutory}</td><td>${b.standard}</td><td>${(() => { return benchLimitsCell(b); })()}</td><td>${ebadge3}</td>${benchCitation(b)}`;
    });
  }

  html += `</tbody></table></div>`;

  // --- 5B. Missing Core Coverage Alert ---
  // Special handling: Property may be "detected" via text pattern (e.g., "PROPERTY COVERAGE PART" in a package policy)
  // but have NO actual declarations limits extracted. This is a critical scenario that must be flagged.
  const propDetected = hasCoverage('Commercial Property') || hasCoverage('Property');
  const propHasLimits = spHasRealLimits(declLimits['Commercial Property']);
  const propMissingLimits = propDetected && !propHasLimits;
  const coreCoverages = [
    { name: 'Commercial Property', key: 'Commercial Property', altKeys: ['Property'], description: propMissingLimits ? 'A reference to property coverage was found in the uploaded policy documents, but NO actual property declarations, building values, contents limits, or business personal property schedules were detected. This may indicate the Property Coverage Part was listed but never activated with actual coverage, or that the property declarations page is missing from the uploaded documents. This requires immediate verification — the organization may have no property insurance despite the coverage part appearing in the policy.' : 'No building, contents, or business personal property limits were found in the uploaded policies. For any commercial entity with physical locations, this represents the most significant coverage gap possible — the organization\'s physical assets are uninsured.' },
    { name: 'General Liability', key: 'General Liability', altKeys: [], description: 'No commercial general liability coverage was detected. This is a foundational coverage for any commercial operation.' },
    { name: 'Workers Compensation', key: 'Workers Compensation', altKeys: [], description: 'No workers compensation coverage was detected. This coverage is statutorily required in virtually every state for employers.' }
  ];
  const missingCore = coreCoverages.filter(cc => {
    // Special case: Commercial Property — flag if NO limits even if coverage text detected
    if (cc.key === 'Commercial Property') {
      return !propHasLimits;
    }
    if (hasCoverage(cc.key)) return false;
    if (cc.altKeys.some(ak => hasCoverage(ak))) return false;
    return true;
  });
  if (missingCore.length > 0) {
    html += `<div class="report-section" style="border:3px solid var(--critical); border-radius:8px; padding:20px; margin-bottom:20px; background:#FFF5F5;">`;
    html += `<h3 style="color:var(--critical); margin-top:0;">⚠️ CRITICAL ALERT — Missing Core Coverage</h3>`;
    html += `<p style="font-size:13px; font-weight:600; color:var(--critical);">The following core commercial coverages were NOT detected in any uploaded policy document. This may indicate a critical gap in the insurance program or that the coverage exists on a separate policy not included in this review.</p>`;
    missingCore.forEach(mc => {
      html += `<div style="background:white; border-left:4px solid var(--critical); padding:12px 16px; margin:12px 0; border-radius:0 6px 6px 0;">`;
      html += `<strong style="color:var(--critical); font-size:14px;">❌ ${mc.name} — NOT FOUND</strong>`;
      html += `<p style="margin:8px 0 4px 0; font-size:13px;">${mc.description}</p>`;
      html += `<p style="margin:4px 0 0 0; font-size:13px; font-weight:600;">Action Required: Immediately verify whether ${mc.name.toLowerCase()} coverage exists on a separate policy not uploaded to this review, or whether this coverage has been omitted from the program entirely.</p>`;
      html += `</div>`;
    });
    html += `</div>`;
  }

  // --- 5B. Endorsements Layered on This Program (REMOVED in v2.21.6) ---
  // Per user direction May 5, 2026: Section 5B was generating noise without actionable signal.
  // The exclusions that matter are surfaced in Section 6 (Exclusion & Contradiction Analysis).
  // The endorsement detection engine still runs — d.endorsements is still populated and used
  // by Section 6 to find carve-back endorsements that demote FATAL FLAW alerts to VERIFIED.
  // The 5B render block below is wrapped in `if (false)` to disable rendering while preserving
  // the code for reference / future use if we ever want to re-enable it.
  if (false && d.endorsements && d.endorsements.totalDetected > 0) {
    const endo = d.endorsements;
    const tagOrder = ['GRANT','CARVE_BACK','SUBLIMIT','EXCLUSION_ADD','AMENDATORY','NOTICE','ADDITIONAL_INSURED','WAIVER','OTHER'];
    const tagDisplay = {
      GRANT: 'Grant',
      CARVE_BACK: 'Carve-Back',
      SUBLIMIT: 'Sublimit',
      EXCLUSION_ADD: 'Exclusion Add',
      AMENDATORY: 'Amendatory',
      NOTICE: 'Notice',
      ADDITIONAL_INSURED: 'Additional Insured',
      WAIVER: 'Waiver',
      OTHER: 'Other'
    };
    const tagColors = {
      GRANT: '#10B981',           // green — adds coverage
      CARVE_BACK: '#10B981',      // green — restores coverage
      SUBLIMIT: '#1F6FEB',        // blue — informational sublimits
      EXCLUSION_ADD: '#DC2626',   // red — removes coverage
      AMENDATORY: '#7C3AED',      // purple — state changes
      NOTICE: '#B45309',          // amber — admin/notice
      ADDITIONAL_INSURED: '#0EA5E9', // sky — AI parties
      WAIVER: '#0EA5E9',
      OTHER: '#6B7280'            // gray — unclassified
    };
    const sevColors = {
      INFO: '#10B981',
      NOTICE: '#1F6FEB',
      WARN: '#B45309',
      CRITICAL: '#DC2626'
    };

    // v2.21.5 Option A: Split into HIGH-SIGNAL rows (shown) and OTHER/UNTITLED rows (collapsed reference).
    // The thesis: a finding that isn't actionable isn't a finding. Hide noise; surface signal.
    const isUntitled = e => !e.title || e.title === 'Untitled Endorsement';
    const isHighSignal = e => {
      // v2.21.6: Explicit routine flag wins — routes to appendix regardless of tag
      if (e._routine === true) return false;
      return e.tag === 'EXCLUSION_ADD'
        || e.tag === 'CARVE_BACK'
        || e.tag === 'GRANT'
        || e.tag === 'SUBLIMIT'
        || e.tag === 'AMENDATORY'
        || e.tag === 'NOTICE'
        || e.tag === 'ADDITIONAL_INSURED'
        || e.tag === 'WAIVER'
        // Keep "Other"-tagged rows IF they have a meaningful title (text classifier missed it but the title gives signal)
        || (e.tag === 'OTHER' && !isUntitled(e));
    };
    const highSignal = endo.endorsements.filter(isHighSignal);
    const lowSignal = endo.endorsements.filter(e => !isHighSignal(e));

    html += `<div class="report-section">
      <h3>6B. Endorsements Layered on This Program</h3>
      <p>SentriPol™ detected <strong>${endo.totalDetected} endorsement${endo.totalDetected !== 1 ? 's' : ''}</strong> across the uploaded policy documents. The table below shows the <strong>${highSignal.length} high-signal endorsement${highSignal.length !== 1 ? 's' : ''}</strong> that meaningfully modify coverage.${lowSignal.length > 0 ? ` ${lowSignal.length} additional endorsement${lowSignal.length !== 1 ? 's were' : ' was'} detected but classified as routine — form numbers listed in the appendix at the bottom of this section.` : ''}</p>`;

    // Tag summary chips (high-signal only)
    const tagCounts = {};
    highSignal.forEach(e => { tagCounts[e.tag] = (tagCounts[e.tag] || 0) + 1; });
    const tagChips = tagOrder.filter(t => tagCounts[t]).map(t =>
      `<span style="display:inline-block;padding:4px 10px;margin:2px 6px 2px 0;background:${tagColors[t]};color:white;border-radius:12px;font-size:11px;font-weight:600;letter-spacing:0.02em;">${tagDisplay[t]}: ${tagCounts[t]}</span>`
    ).join('');
    if (tagChips) {
      html += `<div style="margin:12px 0;">${tagChips}</div>`;
    }

    // Critical / Warning highlights up front — v2.21.6: actually LIST the items so the
    // broker can see what's flagged without scrolling the table. Form number + title +
    // coverage part inline. If a row has no form number AND no real title, skip it from
    // the banner (it's noise the table already handles).
    const critical = highSignal.filter(e => e.severity === 'CRITICAL');
    const warns = highSignal.filter(e => e.severity === 'WARN');
    const renderBannerItem = e => {
      const form = e.formNumber
        ? `<code style="background:#fff;border:1px solid rgba(0,0,0,0.12);padding:1px 6px;border-radius:3px;font-size:11px;font-weight:600;">${escapeHtml(e.formNumber)}</code>`
        : '';
      const title = e.title && e.title !== 'Untitled Endorsement' ? escapeHtml(e.title) : '';
      const cov = e.coveragePart ? ` <span style="color:#6B7280;font-size:11px;">— ${escapeHtml(e.coveragePart)}</span>` : '';
      const cite = ` <span style="color:#9CA3AF;font-size:10px;">(p.${e.page})</span>`;
      const label = title || (e.formNumber ? '' : 'Endorsement');
      const sep = form && label ? ' &nbsp;' : '';
      return `<li style="margin:4px 0;font-size:12px;line-height:1.5;">${form}${sep}${label}${cov}${cite}</li>`;
    };
    if (critical.length > 0) {
      const items = critical.map(renderBannerItem).join('');
      html += `<div style="background:#FEE2E2;border-left:4px solid #DC2626;padding:10px 14px;margin:10px 0;border-radius:4px;">
        <div style="margin-bottom:6px;"><strong>⚠️ Critical Endorsements (${critical.length}):</strong> These endorsements add exclusions that meaningfully remove coverage. Broker must confirm these are intentional and disclosed.</div>
        <ul style="margin:6px 0 0 18px;padding:0;">${items}</ul>
      </div>`;
    } else if (warns.length > 0) {
      const items = warns.map(renderBannerItem).join('');
      html += `<div style="background:#FEF3C7;border-left:4px solid #F59E0B;padding:10px 14px;margin:10px 0;border-radius:4px;">
        <div style="margin-bottom:6px;"><strong>Warning Endorsements (${warns.length}):</strong> These endorsements modify the policy in ways that warrant review.</div>
        <ul style="margin:6px 0 0 18px;padding:0;">${items}</ul>
      </div>`;
    }

    if (highSignal.length > 0) {
      // Full table for high-signal endorsements
      html += `<table class="data-table" style="margin-top:10px; font-size:12px;">
        <thead><tr>
          <th>Form Number</th>
          <th>Title</th>
          <th>Coverage Part</th>
          <th>Type</th>
          <th>Impact</th>
          <th>Citation</th>
        </tr></thead><tbody>`;

      // Sort: by coverage part, then by severity, then by tag
      const severityRank = { CRITICAL: 0, WARN: 1, NOTICE: 2, INFO: 3 };
      const sorted = highSignal.slice().sort((a, b) => {
        if (a.coveragePart !== b.coveragePart) return a.coveragePart.localeCompare(b.coveragePart);
        if (severityRank[a.severity] !== severityRank[b.severity]) return severityRank[a.severity] - severityRank[b.severity];
        return tagOrder.indexOf(a.tag) - tagOrder.indexOf(b.tag);
      });

      sorted.forEach(e => {
        const formCell = e.formNumber
          ? `<code style="background:#F3F4F6;padding:2px 6px;border-radius:3px;font-size:11px;">${escapeHtml(e.formNumber)}</code>`
          : '<span style="color:#9CA3AF;font-size:11px;">—</span>';
        const tagBadge = `<span style="display:inline-block;padding:2px 8px;background:${tagColors[e.tag]};color:white;border-radius:10px;font-size:10px;font-weight:600;letter-spacing:0.02em;">${tagDisplay[e.tag] || e.tag}</span>`;
        const sevDot = e.severity === 'CRITICAL'
          ? '<span style="color:#DC2626;font-weight:700;">⛏ </span>'
          : (e.severity === 'WARN' ? '<span style="color:#B45309;">! </span>' : '');
        html += `<tr>
          <td>${formCell}</td>
          <td><strong>${escapeHtml(e.title || 'Untitled')}</strong></td>
          <td>${escapeHtml(e.coveragePart || 'Unidentified')}</td>
          <td>${tagBadge}</td>
          <td>${sevDot}${escapeHtml(e.impact || '')}</td>
          <td style="font-size:11px;color:#6B7280;">${escapeHtml(e.fileName)}, p.${e.page}</td>
        </tr>`;
      });
      html += `</tbody></table>`;
    } else {
      html += `<p style="color:#6B7280;font-style:italic;">No high-signal endorsements detected. The program contains only routine forms (typical of a clean baseline policy).</p>`;
    }

    // v2.21.5 Option A: Routine endorsements appendix — collapsible / form-numbers-only.
    // These are the "Other / Untitled" rows that the classifier couldn't characterize meaningfully.
    // Listing form numbers preserves the audit trail without adding visual weight.
    if (lowSignal.length > 0) {
      // Group by coverage part for compact display
      const byCov = {};
      lowSignal.forEach(e => {
        const k = e.coveragePart || 'Unidentified';
        if (!byCov[k]) byCov[k] = [];
        byCov[k].push(e);
      });
      html += `<details style="margin-top:14px;background:#F9FAFB;border:1px solid #E5E7EB;border-radius:6px;padding:8px 14px;">
        <summary style="cursor:pointer;font-weight:600;font-size:12px;color:#4B5563;padding:4px 0;">Routine endorsements appendix (${lowSignal.length} forms detected but uncharacterized)</summary>
        <div style="font-size:11px;color:#6B7280;margin:8px 0;">These forms were detected as endorsements but the classifier could not characterize their impact (typical for boilerplate, schedule, or carrier-specific forms). Listed here for audit-trail completeness.</div>`;
      Object.entries(byCov).sort().forEach(([cov, items]) => {
        const forms = items.map(e =>
          e.formNumber
            ? `<code style="background:#fff;border:1px solid #E5E7EB;padding:1px 5px;border-radius:3px;font-size:10px;">${escapeHtml(e.formNumber)}</code> <span style="color:#9CA3AF;font-size:10px;">(p.${e.page})</span>`
            : `<span style="color:#9CA3AF;font-size:10px;">(no form #, p.${e.page})</span>`
        ).join(' &nbsp;· ');
        html += `<div style="margin:6px 0;font-size:11px;"><strong>${escapeHtml(cov)}:</strong> ${forms}</div>`;
      });
      html += `</details>`;
    }

    html += `<p style="font-size:11px;color:#6B7280;margin-top:10px;"><em>Source: SentriPol™ endorsement detection engine. Each endorsement is identified by its title heading + form number + coverage-part attribution. Broker must verify classification against actual policy language.</em></p>
    </div>`;
  }

  // --- 6. Exclusion & Contradiction Analysis ---
  // Split by EVIDENCE, the same way the EB report does. An alert whose
  // language was LOCATED in the uploaded policy is a finding about this
  // policy and is quoted with its real file and page. The canned citation that
  // used to print for every alert — page range, section number and quote
  // written into the alert definition — described documents nobody had read.
  const exclusionAlerts = getExclusionAlerts(d.detectedCoverages, d.vertical, d.endorsements);
  // Only alerts whose language is LOCATED in the documents are reported. An
  // alert that did not ground is dropped rather than listed as a checklist
  // item: the section's own copy says that if the exact wording was not found
  // in the paperwork it is not flagged, and a "not found" card is a flag.
  // Does this program actually carry affirmative abuse cover?
  //
  // The catalogue's impact text says "without standalone SAM coverage, the
  // organization has zero liability protection for these claims". Aspen View
  // carries GCG 74 10 Sexual Misconduct Liability at $1,000,000 each claim and
  // $3,000,000 aggregate. Printing "zero liability protection" over a policy
  // that has $1M/$3M of exactly that cover is the same error as reporting the
  // coverage missing in Section 6, arriving by a different route.
  const samAffirmative = (() => {
    const L = limitsForBenchmark({ key: 'Sexual Abuse/Molestation', type: 'Sexual Abuse / Molestation' });
    if (!L) return null;
    const bits = [];
    if (L.eachClaim) bits.push(L.eachClaim + ' each claim');
    if (L.aggregate) bits.push(L.aggregate + ' aggregate');
    return bits.length ? bits.join(' / ') : null;
  })();

  // ONE POLICY AT A TIME.
  //
  // groundPCExclusion walked every uploaded document and returned the first
  // match, so an alert written about General Liability could be proved by the
  // AUTO policy — which is exactly what happened: "General Liability vs.
  // Abuse/Molestation Exclusion", cited to Auto Policy.pdf, on a program whose
  // GL carries no such exclusion.
  //
  // Each pass now sees one policy's coverage grants, exclusions, endorsements
  // and conditions and nothing else, and no state crosses from one policy to
  // the next. A finding therefore always names the document that proves it,
  // and a policy that is clean of an exclusion cannot be convicted by its
  // neighbour.
  const pcByPolicy = [];
  (d.pdfTexts || []).forEach(policyDoc => {
    const canonicalId = spPolicyCanonicalId(policyDoc);
    const scopeDocs = [policyDoc];          // the whole evidence base for this pass
    const found = [];
    exclusionAlerts.forEach(ea => {
    const hit = window.groundPCExclusion(ea, scopeDocs);
    if (!hit) return;
    // The endorsement states the coverage part it modifies. Where it does, and
    // it is not the one the catalogue assumed, the row says what the paper
    // says — and the recommendation stops pointing at wording that is not
    // there.
    let scoped = null;
    try {
      const doc = policyDoc;
      const txt = doc ? spNorm((doc.pages || []).map(p => p.text || '').join('\n')) : '';
      const at = txt && hit.quote ? txt.indexOf(String(hit.quote).slice(0, 40)) : -1;
      let parts = (txt && at >= 0) ? spEndorsementScopeAt(txt, at) : [];
      if (!parts.length && txt) {
        // Grounded on the forms schedule. Go and read the endorsement.
        parts = spExclusionScopeInDoc(txt, (window.pcExclusionTerms ? window.pcExclusionTerms(ea) : [])
          .concat(hit.term ? [hit.term] : []));
      }
      if (parts.length && !parts.some(c => spSameCoverage(c, ea.coverage))) scoped = parts[0];
    } catch (e) { /* the catalogue label stands */ }
    found.push({ ea: ea, hit: hit, scopedCoverage: scoped });
    });
    if (found.length) pcByPolicy.push({ canonicalId: canonicalId, doc: policyDoc, found: found });
  });
  // Flattened only for the roll-ups that count findings; the rendering below
  // walks pcByPolicy so a reader sees them grouped under the policy they
  // belong to.
  const pcGrounded = pcByPolicy.reduce((acc, p) => acc.concat(p.found), []);
  html += `<div class="report-section"><h3>7. Exclusion & Contradiction Analysis — Critical Review</h3>`;
  {
    const _s7 = window.spSection7Intro((d.pdfTexts || []).length, escapeHtml);
    html += _s7.subtitle + _s7.intro;
  }

  // The cross-policy contradiction block was removed at the client's
  // direction. Section 7 reports one pass per policy, and a finding assembled
  // from two contracts at once is the thing that directive exists to stop.
  const pcCross = [];

  if (pcGrounded.length > 0) {
    html += `<p style="font-size:13px; color:var(--critical); font-weight:600;">⚠️ BROKER ALERT: the exclusionary language below was located in the uploaded policy documents and is quoted with its source. Coverage may appear to exist while being limited or voided by this language.</p>`;
    // AT A GLANCE, BEFORE THE DETAIL.
    //
    // One row per finding: the policy it came out of, what the finding is,
    // the citation with its page, and the action. The policy and its count
    // span their findings, so the grouping stays visible while every finding
    // keeps its own citation — the page numbers are real now that the backend
    // markdown is split back into pages, so each one can be turned to.
    html += `<table class="benchmark-table" style="width:100%;margin:8px 0 14px;font-size:12px;"><thead><tr>`
      + `<th style="text-align:left;padding:6px 8px;">Policy</th>`
      + `<th style="text-align:left;padding:6px 8px;">Findings</th>`
      + `<th style="text-align:left;padding:6px 8px;">Finding</th>`
      + `<th style="text-align:left;padding:6px 8px;">Citation &amp; location</th>`
      + `<th style="text-align:left;padding:6px 8px;">Action required</th>`
      + `</tr></thead><tbody>`;
    pcByPolicy.forEach(({ doc, found }) => {
      found.forEach((f, idx) => {
        const ea = f.ea, hit = f.hit;
        const cov = f.scopedCoverage || ea.coverage;
        const isFatal = String(ea.impact || '').includes('FATAL FLAW');
        const status = f.ea._demoted === true ? 'VERIFIED — CARVE-BACK PRESENT'
          : (isFatal ? 'FATAL FLAW' : 'VERIFY');
        const pageTxt = hit.page
          ? ('p. ' + escapeHtml(String(hit.page)) + (hit.pageEnd && hit.pageEnd !== hit.page ? '–' + escapeHtml(String(hit.pageEnd)) : ''))
          : 'page not resolved';
        const quote = String(hit.quote || '').replace(/\s+/g, ' ').trim();
        html += `<tr style="border-bottom:1px solid #E5E7EB;vertical-align:top;">`;
        if (idx === 0) {
          html += `<td rowspan="${found.length}" style="padding:6px 8px;font-weight:600;">${escapeHtml(doc.fileName || 'uploaded document')}</td>`
            + `<td rowspan="${found.length}" style="padding:6px 8px;text-align:center;font-weight:600;">${found.length}</td>`;
        }
        html += `<td style="padding:6px 8px;"><strong>${escapeHtml(cov)}</strong> vs. ${escapeHtml(ea.exclusion)}`
          + `<br><span style="font-size:11px;color:${isFatal ? 'var(--critical)' : '#92400E'};">${escapeHtml(status)}</span>`
          + (f.scopedCoverage ? `<br><span style="font-size:11px;color:#7C2D12;">endorsement states it modifies ${escapeHtml(f.scopedCoverage)}, not ${escapeHtml(ea.coverage)}</span>` : '')
          // The correction that kept the detail card honest travels into the
          // table with it: an alert whose impact text assumes no abuse cover
          // must not stand unqualified over a programme that carries it.
          + (samAffirmative && /abuse|molestation/i.test(ea.exclusion || '')
              ? `<br><span style="font-size:11px;color:#065F46;">affirmative abuse cover in force \u2014 ${escapeHtml(samAffirmative)}</span>` : '')
          + `</td>`;
        html += `<td style="padding:6px 8px;font-size:11px;">${escapeHtml(hit.fileName || '')}`
          + (hit.section ? `, ${escapeHtml(hit.section)}` : '')
          + `, <strong>${pageTxt}</strong>`
          + (quote ? `<br><span style="color:#6B7280;">&ldquo;${escapeHtml(quote.slice(0, 160))}${quote.length > 160 ? '…' : ''}&rdquo;</span>` : '')
          + (hit.term ? `<br><span style="color:#9CA3AF;">matched &ldquo;${escapeHtml(hit.term)}&rdquo;</span>` : '')
          + `</td>`;
        html += `<td style="padding:6px 8px;font-size:11px;">${escapeHtml(ea.recommendation || '')}</td>`;
        html += `</tr>`;
      });
    });
    html += `</tbody></table>`;

  }
  // Nothing at all located: say that, and say what it does and does not mean.
  // The alerts that did not ground are no longer listed — the section promises
  // "if SentriPol couldn't find the exact wording in the paperwork, it isn't
  // flagged here", and a checklist of unlocated alerts is exactly that flag.
  if (pcGrounded.length === 0 && pcCross.length === 0) {
    html += `<p style="font-size:13px;">No conflicting language was located in the ${(d.pdfTexts || []).length} uploaded document(s). That is not a clean bill of health — forms are frequently attached by reference and a policy can be produced without them — so the broker should still read the full policy, including endorsements, against each coverage grant.</p>`;
  }
  html += `<p style="font-size:11px; color:var(--text-muted); margin-top:10px;"><em>Note: quoted language is reproduced from the uploaded documents. Carve-backs, exceptions and endorsements elsewhere in the policy may modify any exclusion shown here; the broker confirms the final effect.</em></p>`;
  html += `</div>`;

  // --- 6B. DEEP POLICY TEXT SCAN RESULTS (D&O, Embedded Coverages, Exclusions) ---
  // Body shared with the EB and BOP reports — see spDeepScanSectionHtml.
  if (d.deepScanResults) {
    html += `<div class="report-section"><h3>7B. Deep Policy Text Scan — Embedded Coverage Detection</h3>`;
    html += window.spDeepScanSectionHtml(d, escapeHtml);
    html += `</div>`;
  }

  // --- 6C. Website / Operational Risk Analysis ---
  /* --- 7C. Website & Operational Risk Analysis ---
   *
   * The heading renders unconditionally; the FINDINGS stay guarded. Those are
   * two different rules and only one of them was the grounding rule.
   *
   * Before, the whole section was inside the guard, so a report on a site with
   * nothing to flag and a report where the analysis never ran were
   * indistinguishable: no heading at all. On the Base Power review the site was
   * read (1 page, cross-checked in Section 3) and 7C simply was not there. A
   * broker cannot tell "we looked and found nothing" from "we did not look",
   * and on a broker-of-record proposal those are different statements.
   *
   * What must never render is a finding no source evidenced — that is what
   * deleting simulateWebsiteContent was about, and it still holds: everything
   * inside the length check below is unchanged.
   */
  html += `<div class="report-section"><h3>7C. Website & Operational Risk Analysis</h3>`;
  if (d.websiteRisks && d.websiteRisks.length > 0) {
    const _wsMeta = window._siteFetchMeta;
    if (_wsMeta && _wsMeta.host) {
      html += `<p style="font-size:13px;">SentriPol\u2122 read <strong>${escapeHtml(_wsMeta.host)}</strong> (${_wsMeta.pages ? _wsMeta.pages.length : 1} page(s), ${(_wsMeta.chars || 0).toLocaleString()} characters) together with the organization profile. <strong>${d.websiteRisks.length} operational risk(s)</strong> were evidenced in that text and require broker review.</p>`;
    } else {
      html += `<p style="font-size:13px;">SentriPol\u2122 analyzed the organization profile for operational activities that may create coverage gaps or trigger policy exclusions. <strong>${d.websiteRisks.length} operational risk(s)</strong> were evidenced and require broker review.</p>`;
      html += `<div style="margin:8px 0;padding:8px 12px;background:#fff8e1;border-left:3px solid #B8860B;border-radius:4px;font-size:12px;"><strong>No website text was read${window._siteFetchError ? ' (' + escapeHtml(window._siteFetchError) + ')' : ''}.</strong> Findings below rest on the organization profile alone.</div>`;
    }
    // v2.22 (broker rule): de-identify — do not display real client URL in Section 6C output.
    
    // Group risks by category
    const risksByCategory = {};
    d.websiteRisks.forEach(r => {
      if (!risksByCategory[r.category]) risksByCategory[r.category] = [];
      risksByCategory[r.category].push(r);
    });
    
    // Critical risks callout
    const criticalWebRisks = d.websiteRisks.filter(r => r.severity === 'CRITICAL');
    if (criticalWebRisks.length > 0) {
      html += `<div style="margin:12px 0; padding:12px 16px; background:var(--critical-bg, #fef2f2); border-left:4px solid var(--critical); border-radius:var(--radius-sm);">`;
      html += `<strong style="color:var(--critical);">\u26a0\ufe0f CRITICAL: ${criticalWebRisks.length} operational risk(s) identified that commonly trigger policy exclusions:</strong>`;
      html += `<ul style="margin:8px 0 0; font-size:12px;">`;
      criticalWebRisks.forEach(r => {
        html += `<li><strong>${escapeHtml(r.risk)}</strong> \u2014 Cross-reference: <em>${escapeHtml(r.exclusionType)}</em></li>`;
      });
      html += `</ul></div>`;
    }
    
    // Full risk table
    html += `<table class="report-table"><thead><tr><th>Operational Risk</th><th>Category</th><th>Severity</th><th>Policy Exclusion to Verify</th><th>Evidence</th><th>Citation</th></tr></thead><tbody>`;
    d.websiteRisks.forEach(r => {
      const sevBadge = r.severity === 'CRITICAL' ? '<span class="badge badge-critical">\u274c CRITICAL</span>'
        : r.severity === 'HIGH' ? '<span class="badge badge-high">\u26a0\ufe0f HIGH</span>'
        : '<span class="badge badge-moderate">MODERATE</span>';
      html += `<tr><td><strong>${escapeHtml(r.risk)}</strong></td><td>${escapeHtml(r.category)}</td><td>${sevBadge}</td><td style="font-size:12px;">${escapeHtml(r.exclusionType)}</td><td style="font-size:11px; font-style:italic;">\u201c${escapeHtml(r.evidence)}\u201d</td><td style="font-size:11px; color:var(--text-muted); font-style:italic;">[${escapeHtml(r.source)}, Sec. 7C: \u201cOperational activity evidenced in source text\u201d]</td></tr>`;
    });
    html += `</tbody></table>`;
    
    // Nothing is listed that this website did not say. An exposure typical of
    // the sector but absent from the insured's own text is not a finding about
    // the insured, and printing it as one asked the broker to chase nine
    // questions the documents had not raised.

    // Recommendations by category
    html += `<h4 style="font-size:13px; font-weight:700; color:var(--navy); margin:16px 0 8px;">Broker Action Items \u2014 Website Risk Cross-Reference</h4>`;
    html += `<div class="next-steps-grid">`;
    Object.entries(risksByCategory).forEach(([cat, risks]) => {
      const catCritical = risks.filter(r => r.severity === 'CRITICAL').length;
      const catHigh = risks.filter(r => r.severity === 'HIGH').length;
      html += `<div class="next-step-card">`;
      html += `<h5>${catCritical > 0 ? '\u274c' : catHigh > 0 ? '\u26a0\ufe0f' : '\u2139\ufe0f'} ${escapeHtml(cat)} (${risks.length} risk${risks.length > 1 ? 's' : ''})</h5>`;
      html += `<ul style="font-size:12px;">`;
      risks.forEach(r => {
        html += `<li><strong>${escapeHtml(r.risk)}</strong>: Verify that <em>${escapeHtml(r.exclusionType)}</em> does not void coverage for this operational activity.</li>`;
      });
      html += `</ul></div>`;
    });
    html += `</div>`;
    
    html += `<p style="font-size:11px; color:var(--text-muted); margin-top:10px;"><em>Website & Operational Risk Analysis identifies activities, events, and operational exposures from the policyholder\u2019s website and organizational profile that may trigger policy exclusions. The broker must cross-reference each identified risk against actual policy exclusions, endorsements, and sublimits to determine if coverage applies. Page and section citations from policy documents should be verified for each exclusion type listed above.</em></p>`;
  } else {
    // Nothing evidenced. Say which of the two cases this is.
    const _wsMetaNone = window._siteFetchMeta;
    if (_wsMetaNone && _wsMetaNone.host) {
      html += `<p style="font-size:13px;">SentriPol\u2122 read <strong>${escapeHtml(_wsMetaNone.host)}</strong> (${_wsMetaNone.pages ? _wsMetaNone.pages.length : 1} page(s), ${(_wsMetaNone.chars || 0).toLocaleString()} characters) together with the organization profile. <strong>No operational risks were evidenced</strong> in that text \u2014 no activity, event or exposure in the site copy matched a pattern that bears on a policy exclusion.</p>`;
    } else {
      html += `<div style="margin:8px 0;padding:8px 12px;background:#fff8e1;border-left:3px solid #B8860B;border-radius:4px;font-size:12px;"><strong>No website text was read${window._siteFetchError ? ' (' + escapeHtml(window._siteFetchError) + ')' : ''}.</strong> This section reports that nothing was analysed, which is not the same as finding no risk. Supply a reachable website URL to have this section evidenced.</div>`;
    }
  }
  html += `</div>`;

  // --- 7. Federal Compliance Penalty Structure ---
  html += `<div class="report-section"><h3>8. Federal Compliance Penalty Structure</h3>`;
  html += `<p>The following penalties apply to ${escapeHtml(d.vertical)} organizations and represent potential uninsured exposure if coverage is inadequate.</p>`;
  html += `<table class="report-table"><thead><tr><th>Regulation</th><th>Violation Type</th><th>Penalty Range</th><th>Applies To</th></tr></thead><tbody>`;
  const pcPenalties = [
    ["OSHA", "Serious violation", "$16,131 per violation", "All employers"],
    ["OSHA", "Willful/Repeat violation", "$161,323 per violation", "All employers"],
    ["OSHA", "Failure to abate", "$16,131 per day", "All employers"],
    ["ADA", "First violation", "Up to $75,000", "Employers with 15+ employees"],
    ["ADA", "Subsequent violations", "Up to $150,000", "Employers with 15+ employees"],
    ["EEOC", "Title VII / discrimination", "$50,000–$300,000 depending on employer size", "Employers with 15+ employees"],
    ["EEOC", "Retaliation", "$50,000–$300,000 + compensatory/punitive", "All employers"]
  ];
  if (isEducation) {
    pcPenalties.unshift(
      ["FERPA", "Student records violation", "Loss of ALL federal funding", "Educational institutions receiving federal funds"],
      ["FERPA", "Unauthorized data disclosure", "Loss of federal funding + civil liability", "Educational institutions"],
      ["Title IX", "Non-compliance / harassment failure", "Loss of ALL federal funding", "Educational institutions receiving federal funds"],
      ["Title IX", "Failure to investigate", "Loss of federal funding + civil damages + OCR investigation", "Educational institutions"]
    );
  }
  if ((isEnergy || hasAnyEnergy) && !isEquipmentRental) {
    pcPenalties.push(
      ["TX RRC", "Operating without P-5 Organization Report", "$10,000/day + well shut-in + operator status revocation", "Oil & gas operators in Texas"],
      ["TX RRC", "Failure to plug abandoned wells", "$10,000/day per well + forfeiture of P-5PB bond + orphan well liability", "All TX well operators"],
      ["TCEQ", "Unauthorized discharge / pollution event", "Up to $25,000/day per violation + remediation costs", "All industrial facilities in TX"],
      ["TCEQ", "NORM disposal violation", "Up to $25,000/day + facility shutdown + remediation liability", "Oil & gas / energy facilities handling NORM"],
      ["PHMSA", "Pipeline safety violation", "Up to $257,664 per violation / $2.576M maximum", "Pipeline operators (49 CFR 190-199)"],
      ["OSHA PSM", "Process safety management violation", "$16,131 serious / $161,323 willful per violation", "Facilities with >10,000 lbs highly hazardous chemicals"],
      ["TX Anti-Indemnity", "Unenforceable oilfield indemnity (\u00a7127)", "Void contract provisions + uninsured liability exposure", "Oilfield service contracts / MSAs"]
    );
  }
  pcPenalties.forEach(r => {
    html += `<tr><td><strong>${r[0]}</strong></td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 8. Priority Gap Analysis & Recommendations ---
  html += `<div class="report-section"><h3>9. Priority Gap Analysis & Recommendations</h3>`;
  html += `<p>Based on the comprehensive BOR analysis for ${escapeHtml(d.policyholderName)}, the following gaps have been identified:</p>`;

  const gaps = [];
  // v2.18: Commercial Property receives forced-CRITICAL treatment when no verified dollar limits exist.
  // Property losses on commercial buildings routinely run $1M–$50M+ per incident; any absent-or-unverified
  // property line is treated as a life-of-business exposure, not a routine gap.
  const hasPropertyLimits = spHasRealLimits(declLimits && declLimits['Commercial Property']);
  pcBenchmarks.forEach(b => {
    // Force CRITICAL on Commercial Property unless verified dollar limits are present
    if (b.type === 'Commercial Property' && !hasPropertyLimits) {
      const propDetected = hasCoverage('Commercial Property') || hasCoverage('Property') || hasCoverage('Package Policy');
      gaps.push({
        priority: 'CRITICAL',
        type: 'Commercial Property',
        currentStatus: propDetected ? 'Referenced But No Limits Verified' : 'Not Detected / Not Provided',
        exposure: '$1M\u2013$50M+ (varies by building & contents value)',
        action: propDetected
          ? `URGENT: Commercial Property coverage was referenced in the uploaded documents but no building, contents, or business-income limits could be verified. Immediately obtain and verify the Property declarations page. A typical commercial building loss ranges from $1M to $50M+.`
          : `URGENT: No Commercial Property coverage was detected in any uploaded policy. For any entity with physical locations, this is the largest uninsured exposure possible. Immediately propose and bind Commercial Property coverage at full replacement cost with Ordinance or Law and Business Income / Extra Expense.`,
        premium: getPremiumEstimate('Commercial Property')
      });
      return; // Skip default handling for this benchmark
    }
    if (b.status === 'CRITICAL GAP') {
      gaps.push({ priority: 'CRITICAL', type: b.type, currentStatus: 'Not Detected / Not Provided', exposure: getExposureRange('CRITICAL', b.type), action: `Immediately propose and bind standalone ${b.type} coverage at best-practice limits`, premium: getPremiumEstimate(b.type) });
    } else if (b.status === 'HIGH GAP') {
      gaps.push({ priority: 'HIGH', type: b.type, currentStatus: 'Not Detected / Not Provided', exposure: getExposureRange('HIGH', b.type), action: `Recommend adding ${b.type} coverage meeting industry standard minimums`, premium: getPremiumEstimate(b.type) });
    } else if (b.status === 'BELOW STANDARD') {
      gaps.push({ priority: 'HIGH', type: b.type, currentStatus: 'Current limits below industry standard', exposure: getExposureRange('HIGH', b.type), action: `Review ${b.type} limits — current coverage is below the industry standard of ${b.standard}. Recommend increasing to at least industry standard minimums.`, premium: getPremiumEstimate(b.type) });
    } else if (b.status === 'VERIFY') {
      gaps.push({ priority: 'MODERATE', type: b.type, currentStatus: 'Requires Verification', exposure: getExposureRange('MODERATE', b.type), action: `Request and review current ${b.type} declarations page and endorsements to confirm adequacy`, premium: 'TBD upon review' });
    }
  });

  // Add state-specific gaps
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (!sp) return;
    if (sp.severity === 'EXTREME' || sp.severity === 'HIGH') {
      gaps.push({ priority: sp.severity === 'EXTREME' ? 'CRITICAL' : 'HIGH', type: `${sp.name} Catastrophe Risk`, currentStatus: `${sp.primaryCatastrophe} — ${sp.severity} severity`, exposure: sp.severity === 'EXTREME' ? '$5M–$50M+' : '$1M–$10M', action: sp.recommendation, premium: 'Market-dependent' });
    }
  });

  html += `<table class="report-table"><thead><tr><th>Priority</th><th>Coverage Type</th><th>Current Status</th><th>Est. Exposure</th><th>Recommended Action</th><th>Est. Premium</th><th>Citation</th></tr></thead><tbody>`;
  gaps.sort((a, b) => {
    const order = { 'CRITICAL': 0, 'HIGH': 1, 'MODERATE': 2 };
    return (order[a.priority] || 3) - (order[b.priority] || 3);
  });
  // The E&O Mitigation Record needs the same recommendations the report shows,
  // so publish the finished list rather than re-deriving it from the rendered HTML.
  window._spRecommendedGaps = gaps;
  gaps.forEach(g => {
    const badgeClass = g.priority === 'CRITICAL' ? 'badge-critical' : g.priority === 'HIGH' ? 'badge-high' : g.priority === 'VERIFY' ? 'badge-verify' : 'badge-moderate';
    html += `<tr><td><span class="badge ${badgeClass}">${g.priority}</span></td><td><strong>${g.type}</strong></td><td>${g.currentStatus}</td><td>${g.exposure}</td><td>${g.action}</td><td>${g.premium}</td><td style="font-size:11px;color:var(--text-muted);font-style:italic;">[Gap Analysis, Sec. 9: "${g.type} — ${g.priority} priority; ${g.currentStatus}"]</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 9. Summary of Total Exposure ---
  const criticalCount = gaps.filter(g => g.priority === 'CRITICAL').length;
  const highCount = gaps.filter(g => g.priority === 'HIGH').length;
  const moderateCount = gaps.filter(g => g.priority === 'MODERATE').length;

  html += `<div class="report-section"><h3>10. Summary of Total Exposure</h3>
    <div class="summary-box">
      <h4>${escapeHtml(d.policyholderName)} — Risk Exposure Summary</h4>
      <div class="summary-stats">
        <div class="summary-stat critical-stat"><div class="stat-value">${criticalCount}</div><div class="stat-label">Critical Gaps</div></div>
        <div class="summary-stat high-stat"><div class="stat-value">${highCount}</div><div class="stat-label">High Gaps</div></div>
        <div class="summary-stat moderate-stat"><div class="stat-value">${moderateCount}</div><div class="stat-label">Moderate / Verify</div></div>
      </div>
      <p style="font-size:13px;"><strong>Total Estimated Exposure Range:</strong> $${(criticalCount * 2 + highCount).toFixed(0)}M – $${(criticalCount * 15 + highCount * 5 + moderateCount * 2).toFixed(0)}M+</p>
      <p style="font-size:13px;"><strong>Estimated Additional Premium Range:</strong> $${((criticalCount * 8 + highCount * 4 + moderateCount * 2) * 1000).toLocaleString()} – $${((criticalCount * 25 + highCount * 12 + moderateCount * 5) * 1000).toLocaleString()}</p>
      <p style="font-size:12px; color:var(--text-muted); margin-top:8px;"><em>These figures represent both the competitive opportunity in a BOR proposal and the potential E&O exposure if the gaps are not addressed. Record the recommendations and the client's accept/decline responses in the E&O Mitigation Record.</em></p>
    </div>
  </div>`;

  // --- 10. Cyber Risk Intelligence ---
  const hasCyber = d.detectedCoverages.includes('Cyber Liability');
  html += `<div class="report-section"><h3>11. Cyber Risk Intelligence & Market Analysis</h3>`;
  html += `<p style="font-size:13px; color:var(--text-primary);">Cyber risk represents the fastest-growing exposure for organizations across all industries. The following intelligence is provided to inform coverage discussions and demonstrate the critical nature of adequate cyber protection for <strong>${escapeHtml(d.policyholderName)}</strong>.</p>`;

  // Key Stats Dashboard
  html += `<div class="cyber-dashboard">
    <div class="cyber-stat-grid">
      <div class="cyber-stat-card">
        <div class="cyber-stat-number">$20.56B</div>
        <div class="cyber-stat-label">Global Cyber Insurance Market (2025)</div>
        <div class="cyber-stat-source">[Mordor Intelligence / Lockton Re, 2025]</div>
      </div>
      <div class="cyber-stat-card">
        <div class="cyber-stat-number">$115K</div>
        <div class="cyber-stat-label">Average Cyber Claim Cost (2024)</div>
        <div class="cyber-stat-source">[Coalition Cyber Claims Report, 2025]</div>
      </div>
      <div class="cyber-stat-card">
        <div class="cyber-stat-number">$631K</div>
        <div class="cyber-stat-label">Average Ransomware Claim Cost</div>
        <div class="cyber-stat-source">[NetDiligence Cyber Claims Study, 2025]</div>
      </div>
      <div class="cyber-stat-card">
        <div class="cyber-stat-number">129%</div>
        <div class="cyber-stat-label">Increase in Major Cyber Incidents (YoY)</div>
        <div class="cyber-stat-source">[UK NCSC / Heimdal Security, 2025]</div>
      </div>
    </div>
  </div>`;

  // Industry Underinsurance Table
  html += `<h4 style="font-size:14px; font-weight:700; color:var(--navy); margin:16px 0 8px;">Industry Cyber Insurance Adoption & Underinsurance Risk</h4>`;
  html += `<table class="report-table"><thead><tr><th>Industry Sector</th><th>Cyber Insurance Adoption</th><th>Claim Frequency Rank</th><th>Avg. Claim Cost</th><th>Underinsurance Risk</th><th>Citation</th></tr></thead><tbody>`;
  const cyberIndustries = [
    ["Manufacturing", "48%", "#1 — 33% of all claims", "$228K (large) / $79K (SMB)", "CRITICAL", "[Lockton Re Global Cyber Report, 2025; DeepStrike Analytics]"],
    ["Healthcare", "62%", "#2 — highest cost per claim", "$350K–$500K avg.", "CRITICAL", "[Heimdal Security Cyber Insurance Report, 2025]"],
    ["Education (K-12 & Higher Ed)", "35%", "Top 5", "$150K–$300K avg.", "CRITICAL", "[Coalition / ElectroIQ Cyber Industry Report, 2025]"],
    ["Professional Services", "55%", "Top 5", "$120K–$200K avg.", "HIGH", "[NetDiligence Cyber Claims Study, 2025]"],
    ["Retail / Hospitality", "40%", "Top 10", "$100K–$180K avg.", "HIGH", "[Lockton Re / III Market Intelligence, 2025]"],
    ["Nonprofits / Religious Orgs", "22%", "Rising rapidly", "$80K–$150K avg.", "CRITICAL", "[GuideOne / III Nonprofit Risk Report, 2025]"],
    ["Construction", "30%", "Growing target", "$90K–$175K avg.", "HIGH", "[CNA Hardy Cyber Report, 2025]"],
    ["Financial Services", "78%", "Top 3", "$250K–$500K avg.", "MODERATE", "[Zurich / Lockton Re, 2025]"],
    ["Government / Municipal", "45%", "Top 5 for ransomware", "$200K–$1M+ avg.", "CRITICAL", "[Heimdal Security / CISA, 2025]"]
  ];
  cyberIndustries.forEach(r => {
    const riskBadge = r[4] === 'CRITICAL' ? '<span class="badge badge-critical">CRITICAL</span>' : r[4] === 'HIGH' ? '<span class="badge badge-high">HIGH</span>' : '<span class="badge badge-moderate">MODERATE</span>';
    html += `<tr><td><strong>${r[0]}</strong></td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td><td>${riskBadge}</td><td style="font-size:10px;color:var(--text-muted);font-style:italic;">${r[5]}</td></tr>`;
  });
  html += `</tbody></table>`;

  // Current Status Alert
  if (!hasCyber) {
    html += `<div class="exclusion-alert" style="margin-top:12px;">
      <div class="ea-header">
        <span class="ea-coverage">Cyber Liability</span>
        <span style="color:var(--text-muted);">—</span>
        <span class="ea-exclusion">NO COVERAGE DETECTED</span>
        <span class="badge badge-critical">CRITICAL EXPOSURE</span>
      </div>
      <div class="ea-impact"><strong>Impact:</strong> Without cyber liability coverage, ${escapeHtml(d.policyholderName)} faces uninsured exposure of $500,000–$5,000,000+ per breach event. The ${escapeHtml(d.vertical)} sector is among the top targets for ransomware, phishing, and data exfiltration attacks. Only 17% of small businesses and 35% of education organizations currently carry adequate cyber insurance.</div>
      <div class="ea-recommendation"><strong>Immediate Action:</strong> Obtain standalone cyber liability coverage at minimum $3M limit with both first-party (business interruption, data restoration, ransom payment) and third-party (regulatory defense, notification costs, PCI fines) coverage. Consider carriers such as Travelers CyberRisk, Chubb Cyber ERM, AIG CyberEdge, CNA NetProtect, or Coalition.</div>
    </div>`;
  } else {
    html += `<div style="margin-top:12px; padding:10px 14px; background:var(--success-bg); border-left:3px solid var(--success); border-radius:var(--radius-sm); font-size:12px;"><strong>✅ Cyber Liability Coverage Detected:</strong> Verify that current limits, retention, and scope (first-party + third-party) are adequate for the ${escapeHtml(d.vertical)} sector's risk profile. The average ransomware claim now exceeds $631K, and attack sophistication continues to accelerate. Consider increasing limits to $5M+ and adding social engineering, fund transfer fraud, and regulatory proceeding coverage if not already included. <em>[Cyber Risk Intelligence, Sec. 11]</em></div>`;
  }

  // Emerging Threats
  html += `<h4 style="font-size:14px; font-weight:700; color:var(--navy); margin:16px 0 8px;">Emerging Cyber Threats — 2025/2026 Outlook</h4>`;
  html += `<div class="next-steps-grid">
    <div class="next-step-card">
      <h5 style="color:var(--critical);">🚨 Ransomware Evolution</h5>
      <ul style="font-size:12px;">
        <li>Average ransomware payment rose to $631K; double-extortion (data theft + encryption) now standard</li>
        <li>Ransomware-as-a-Service (RaaS) lowering barrier to entry for attackers</li>
        <li>Supply chain attacks increasing — one breach can impact hundreds of downstream organizations</li>
      </ul>
    </div>
    <div class="next-step-card">
      <h5 style="color:var(--warning);">🔒 AI-Powered Attacks</h5>
      <ul style="font-size:12px;">
        <li>Deepfake social engineering — AI-generated voice/video used in fund transfer fraud</li>
        <li>AI-assisted phishing generating highly convincing targeted emails at scale</li>
        <li>Automated vulnerability scanning reducing time from discovery to exploitation</li>
      </ul>
    </div>
  </div>`;

  html += `<p style="font-size:11px; color:var(--text-muted); margin-top:10px;"><em>Sources: Mordor Intelligence Cyber Insurance Market Forecast (2025), Lockton Re Global Cyber Market Update (2025), Coalition Cyber Claims Report (2025), NetDiligence Cyber Claims Study (2025), Heimdal Security Annual Report (2025), CISA Cybersecurity Advisory (2025). All statistics represent industry-wide aggregates and may vary by organization size, sector, and geography.</em></p>`;
  html += `</div>`;

  // --- 10B. Multi-Policy Cross-Reference ---
  if (d.files.length > 1) {
    const dl = d.declLimits || (typeof extractDeclarationsLimits === 'function' ? extractDeclarationsLimits(d.pdfTexts || []) : {});
    const crossRefIssues = [];
    // Extract Umbrella underlying from declarations or text
    const umbrellaDetected = hasCoverage('Umbrella/Excess');
    const glDetected = hasCoverage('General Liability');
    const autoDetected = hasCoverage('Commercial Auto');
    // Check GL limits from declarations
    const glLimits = dl['General Liability'] || {};
    const glOccLimit = glLimits['Each Occurrence'] || glLimits['Occurrence'] || glLimits['Per Occurrence'] || null;
    const glAggLimit = glLimits['General Aggregate'] || glLimits['Aggregate'] || null;
    // Check Auto limits
    const autoLimits = dl['Commercial Auto'] || {};
    const autoCslLimit = autoLimits['Combined Single Limit'] || autoLimits['CSL'] || autoLimits['Each Accident'] || null;
    // Check Umbrella limits
    const umbrellaLimits = dl['Umbrella/Excess'] || dl['Umbrella'] || dl['Excess'] || {};
    const umbrellaOccLimit = umbrellaLimits['Each Occurrence'] || umbrellaLimits['Occurrence'] || null;
    const umbrellaAggLimit = umbrellaLimits['Aggregate'] || null;
    // Cross-reference: Umbrella typically requires minimum underlying GL of $1M/$2M
    if (umbrellaDetected && glDetected) {
      if (glOccLimit) {
        const glOccNum = parseInt(glOccLimit.replace(/[^0-9]/g, ''));
        if (glOccNum > 0 && glOccNum < 1000000) {
          crossRefIssues.push({ severity: 'HIGH', issue: 'GL occurrence limit below typical Umbrella underlying requirement', detail: `General Liability Each Occurrence limit is ${glOccLimit}. Most Umbrella policies require a minimum underlying GL of $1,000,000 per occurrence. Verify the Umbrella\'s schedule of underlying insurance matches the actual GL limits.` });
        }
      }
      if (glAggLimit) {
        const glAggNum = parseInt(glAggLimit.replace(/[^0-9]/g, ''));
        if (glAggNum > 0 && glAggNum < 2000000) {
          crossRefIssues.push({ severity: 'HIGH', issue: 'GL aggregate limit below typical Umbrella underlying requirement', detail: `General Liability General Aggregate is ${glAggLimit}. Most Umbrella policies require a minimum underlying GL aggregate of $2,000,000. A gap between required and actual underlying limits could void Umbrella coverage for a specific claim.` });
        }
      }
    }
    // Cross-reference: Umbrella requires underlying Auto
    if (umbrellaDetected && !autoDetected) {
      crossRefIssues.push({ severity: 'HIGH', issue: 'Umbrella detected but no Commercial Auto found', detail: 'Umbrella/Excess policies typically require scheduled underlying Auto coverage. If Commercial Auto was not uploaded, verify the Umbrella\'s schedule of underlying insurance and confirm Auto limits meet the Umbrella\'s required minimums.' });
    }
    // Cross-reference: Umbrella requires underlying Employers Liability
    if (umbrellaDetected && !hasCoverage('Workers Compensation')) {
      crossRefIssues.push({ severity: 'MEDIUM', issue: 'Umbrella detected but no Workers Compensation/Employers Liability found', detail: 'Umbrella policies typically require scheduled underlying Employers Liability coverage (usually $500K/$500K/$500K or $1M/$1M/$1M). Verify the Umbrella\'s schedule of underlying insurance includes EL limits.' });
    }
    // GL + Umbrella aggregate exhaustion check
    if (glDetected && !umbrellaDetected) {
      crossRefIssues.push({ severity: 'MEDIUM', issue: 'General Liability found but no Umbrella/Excess detected', detail: 'No Umbrella or Excess Liability coverage was detected. If the GL aggregate is exhausted by a single large claim or multiple claims in a policy period, no excess coverage exists to respond. Verify whether an Umbrella/Excess policy exists on a separate document not uploaded.' });
    }
    // Display cross-reference results
    if (crossRefIssues.length > 0) {
      html += `<div class="report-section"><h3>11B. Multi-Policy Cross-Reference</h3>`;
      html += `<p style="font-size:13px;">SentriPol\u2122 analyzed limits across all uploaded policies to identify mismatches between Umbrella/Excess requirements and underlying coverage. The following cross-policy issues were detected:</p>`;
      crossRefIssues.forEach(ci => {
        const borderColor = ci.severity === 'HIGH' ? 'var(--critical, #dc3545)' : 'var(--warning, #ffc107)';
        const bgColor = ci.severity === 'HIGH' ? '#FFF5F5' : '#FFFBF0';
        html += `<div style="background:${bgColor}; border-left:4px solid ${borderColor}; padding:12px 16px; margin:10px 0; border-radius:0 6px 6px 0;">`;
        html += `<strong style="color:${borderColor === 'var(--critical, #dc3545)' ? 'var(--critical, #dc3545)' : '#856404'};">[${ci.severity}] ${ci.issue}</strong>`;
        html += `<p style="margin:6px 0 0 0; font-size:13px;">${ci.detail}</p>`;
        html += `</div>`;
      });
      html += `<p style="font-size:11px; color:var(--text-muted); margin-top:8px;"><em>Note: Cross-reference analysis is based on limits extracted from uploaded policy declarations. The broker should verify all underlying insurance schedules against the actual Umbrella/Excess policy requirements. [Multi-Policy Cross-Reference, Sec. 11B]</em></p>`;
      html += `</div>`;
    }
  }

  // --- 11. Conclusion & Next Steps ---
  html += `<div class="report-section"><h3>12. Conclusion & Next Steps</h3>`;
  html += `<div class="next-steps-grid">
    <div class="next-step-card">
      <h5>⚡ Immediate Action (30 Days)</h5>
      <ul>
        ${criticalCount > 0 ? '<li>Address all CRITICAL gap items — bind or propose coverage immediately</li>' : ''}
        <li>Request all current declarations pages and policy forms for verification</li>
        <li>Confirm carrier financial stability ratings (A.M. Best, Demotech)</li>
        <li>Review all deductible structures against organizational risk tolerance</li>
        ${d.states.some(a => ['FL','TX','CA','LA','OK'].includes(a)) ? '<li>Verify catastrophe-specific coverage adequacy for high-risk states</li>' : ''}
      </ul>
    </div>
    <div class="next-step-card">
      <h5>📋 High Priority (60–90 Days)</h5>
      <ul>
        <li>Address all HIGH priority gap items with carrier submissions</li>
        <li>Conduct detailed coverage benchmarking against ${escapeHtml(d.vertical)} peers</li>
        <li>Review business interruption valuations and waiting periods</li>
        <li>Assess umbrella/excess tower adequacy and layering strategy</li>
        <li>Implement risk management recommendations to improve loss history</li>
      </ul>
    </div>
  </div>`;
  html += `<p style="font-size:12px; color:var(--text-muted);"><strong>Methodology:</strong> This analysis was conducted using SentriPol™ AI-powered policy analysis engine, incorporating state-specific regulatory databases, industry benchmarking data from III and NAIC, federal compliance penalty structures, catastrophic event risk profiles, and cyber risk intelligence from Mordor Intelligence, Lockton Re, Coalition, and NetDiligence for all identified operating states. ${d.files.length > 0 ? `${d.files.length} policy document(s) were analyzed for coverage detection across ${Object.keys(COVERAGE_PATTERNS).length} coverage categories and ${Object.values(COVERAGE_PATTERNS).flat().length}+ carrier-specific naming patterns.` : 'No policy documents were provided; this analysis is based on organizational profile and industry benchmarks only and should be considered Preliminary.'} All recommendations should be verified with current carrier documentation and applicable state regulations.</p>`;
  html += `</div>`;

  // The in-report E&O Compliance Record (formerly the last numbered section of
  // both reports) is retired. Architecture v2 makes the BOR analysis itself the
  // proof the review was performed, and the separate E&O Mitigation Record
  // carries the remediation and the client's accept/decline decisions — this
  // block restated the first and duplicated the second.

  _spDone();
  return html;
}

// ===== EB FULL REVIEW =====
// The statutory pension a school's staff belong to, or null where the client is
// not a school. Texas names it; elsewhere the report says what to confirm.
function ebTeacherRetirementFor(d) {
  if (!d || !/school|education|charter|academy|k-?12|district/i.test(String(d.vertical || '') + ' ' + String(d.policyholderName || ''))) return null;
  return (d.states || []).map(x => String(x).toUpperCase()).includes('TX')
    ? { name: 'TRS (Teacher Retirement System of Texas)', short: 'TRS' }
    : { name: 'The state teacher retirement system', short: 'state teacher retirement system' };
}

function generateEBFullReview(d) {
  d = window.spResolveDerived(d);
  d.states = spCanonStates(d.states);
  const stateNames = d.states.map(spStateDisplayName).join(', ');
  const reviewLabel = 'BOR Analysis \u2014 Comprehensive Coverage Review';
  const today = new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'});

  let html = '';

  // Architecture v2: this is the one comprehensive BOR analysis — it proves the
  // review was done. The producer's remediation and the client's accept/decline
  // decisions live in the separate E&O Mitigation Record.
  html += `<div class="eo-badge" style="background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 28px;">\ud83d\udee1\ufe0f</span>
      <div>
        <div style="font-weight: 700; font-size: 15px; margin-bottom: 2px;">BOR Analysis — Documented Proof of Professional Diligence</div>
        <div style="font-size: 12px; opacity: 0.9;">This analysis is the documented record that the policy program was systematically reviewed. To record what was remediated and which recommended coverages the client accepted or declined, generate the E&O Mitigation Record from the button at the top of this report.</div>
      </div>
    </div>`;

  // --- 1. Organization Profile ---
  html += `<div class="report-section">
    <h3>1. Organization Profile</h3>
    <p><strong>Policyholder:</strong> ${escapeHtml(d.policyholderName)} &nbsp;|&nbsp; <strong>LOB:</strong> Employee Benefits &nbsp;|&nbsp; <strong>Review Type:</strong> ${reviewLabel}</p>
    <p><strong>State(s):</strong> ${stateNames} &nbsp;|&nbsp; <strong>Industry:</strong> ${escapeHtml(d.vertical)} &nbsp;|&nbsp; <strong>Date:</strong> ${today}</p>
    ${d.incumbent ? `<p><strong>Incumbent Carrier/Broker:</strong> ${escapeHtml(d.incumbent)}</p>` : ''}
    ${d.orgOverview ? d.orgOverview.split('\n\n').map(p => `<p>${escapeHtml(p)}</p>`).join('') : `<p>${escapeHtml(d.policyholderName)} is a ${escapeHtml(d.vertical)} organization operating in ${stateNames}.</p>`}
  </div>`;

  // --- 2. Policies Reviewed ---
  html += `<div class="report-section"><h3>2. Policies Reviewed</h3>`;
  if (d.files.length > 0) {
    html += `<table class="report-table"><thead><tr><th>Document</th><th>Size</th><th>Detected Type</th><th>Status</th></tr></thead><tbody>`;
    d.files.forEach((f, idx) => {
      const fc = detectCoverages([f]);
      // v2.17: De-identify filename in demo mode
      const displayName = d.demoMode ? (escapeHtml(d.policyholderName).replace(' (DEMO)','') + ' Benefits Document' + (d.files.length > 1 ? ' (' + (idx+1) + ')' : '') + '.pdf') : escapeHtml(f.name);
      html += `<tr><td>${displayName}</td><td>${formatBytes(f.size)}</td><td>${fc.length ? fc.join(', ') : 'Benefits Document'}</td><td><span class="badge badge-meets">✅ ANALYZED</span></td></tr>`;
    });
    html += `</tbody></table>`;
  } else {
    html += `<p><em>No benefits documents were uploaded. Analysis is based on organizational profile and industry benchmarks. This review should be considered <strong>Preliminary</strong>.</em></p>`;
  }
  // The EB report has no Expected Coverages section — it runs from 2 straight
  // to 3 — so the mirrored advisory closes Policies Reviewed, the same early
  // position 2B holds in the P&C report. Last item, and it renders whether or
  // not any benefits documents were uploaded.
  html += window.spAdvisoryCardHtml('eb', escapeHtml);
  html += `</div>`;

  // --- 3. Auto-Detected Profile & NAICS Vertical Analysis ---
  // The panel bodies are rendered later, by spRenderProfileCard / spRenderNaicsPanel,
  // which run after the report HTML is in the DOM (they depend on the async profile
  // and NAICS analyses). This is the host they mount into, so the numbering below is
  // stable whether or not those analyses returned anything.
  html += `<div class="report-section" id="spSection3Host"><h3>3. Auto-Detected Profile &amp; NAICS Vertical Analysis</h3>`;
  html += `<div id="spSection3Body"><p style="font-size:12px; color:var(--text-muted);"><em>Profile and NAICS classification are still resolving \u2014 if this text remains, neither the uploaded documents nor the website produced a usable classification.</em></p></div>`;
  html += `</div>`;

  // --- 4. State-Specific EB Regulatory Analysis ---
  html += `<div class="report-section"><h3>4. State-Specific Employee Benefits Regulatory Analysis</h3>`;
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (!sp) return;
    html += `<div class="state-card">
      <div class="state-card-header severity-moderate"><span>${sp.name} (${sp.abbr}) — EB Regulatory Profile</span></div>
      <div class="state-card-body">
        <div class="state-field"><div class="state-field-label">State Health Insurance Mandates</div><div class="state-field-value">${sp.name} requires compliance with state-specific health insurance mandates including mental health parity, dependent coverage extensions, and essential health benefit requirements as applicable.</div></div>
        <div class="state-field"><div class="state-field-label">State Leave Requirements</div><div class="state-field-value">${sp.name} ${['CA','NY','NJ','WA','MA','CT','OR','CO','MD','DE','ME','MN','IL','DC'].includes(abbr) ? 'has enacted paid family/medical leave programs requiring employer participation and/or contributions' : 'follows federal FMLA requirements; no state-mandated paid leave program currently in effect'}.</div></div>
        <div class="state-field"><div class="state-field-label">State Disability Requirements</div><div class="state-field-value">${['CA','NY','NJ','HI','RI','PR'].includes(abbr) ? `${sp.name} mandates state disability insurance (SDI/TDI). Employer must provide or contribute to short-term disability coverage.` : `${sp.name} does not mandate state disability insurance. STD/LTD are voluntary but strongly recommended for competitive benefits packages.`}</div></div>
        <div class="state-field"><div class="state-field-label">Continuation Coverage</div><div class="state-field-value">${sp.name} ${['CA','NY','TX','IL','FL','MA','CT','NJ','MN','OR','WA','CO'].includes(abbr) ? 'has state continuation (mini-COBRA) requirements extending beyond federal COBRA provisions for smaller employers' : 'follows federal COBRA requirements for employers with 20+ employees'}.</div></div>
      </div>
    </div>`;
  });
  html += `</div>`;

  // Trial sessions stop here; see spTrialLockedSectionsHtml.
  if (spTrialActive()) {
    html += spTrialLockedSectionsHtml('eb');
    return html;
  }

  // --- 4. Comprehensive Regulatory Sources Table ---
  html += `<div class="report-section"><h3>5. Comprehensive Regulatory Sources Table</h3>`;
  html += `<table class="report-table"><thead><tr><th>Source</th><th>Jurisdiction</th><th>Relevance to Employee Benefits</th><th>URL / Reference</th><th>Last Updated</th></tr></thead><tbody>`;
  const ebSources = [
    ["DOL — ERISA", "Federal", "Employee benefit plan fiduciary standards, reporting (Form 5500), disclosure requirements", "dol.gov/agencies/ebsa", "Current"],
    ["IRS — Section 125 / Cafeteria Plans", "Federal", "Pre-tax benefit elections, FSA/HSA contribution limits, non-discrimination testing", "irs.gov", "Current"],
    ["IRS — Section 401(k)", "Federal", "Retirement plan compliance, contribution limits, non-discrimination testing, employer match rules", "irs.gov", "Current"],
    ["HHS — Affordable Care Act (ACA)", "Federal", "Employer mandate (50+ FTEs), essential health benefits, reporting requirements (1094-C/1095-C)", "healthcare.gov", "Current"],
    ["CMS (Centers for Medicare & Medicaid)", "Federal", "Medicare Secondary Payer rules, coordination of benefits, CHIP requirements", "cms.gov", "Current"],
    ["DOL — COBRA", "Federal", "Continuation coverage requirements for employers with 20+ employees, notice obligations", "dol.gov/cobra", "Current"],
    ["HHS — HIPAA", "Federal", "Health information privacy and security, portability, non-discrimination", "hhs.gov/hipaa", "Current"],
    ["DOL — FMLA", "Federal", "Family and medical leave requirements for employers with 50+ employees", "dol.gov/fmla", "Current"]
  ];
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr]; if (!sp) return;
    ebSources.push([`${sp.regulator || sp.name + ' DOI'} — Employee Benefits`, `State — ${sp.abbr}`, `State-specific health insurance mandates, benefit requirements, carrier licensing`, sp.regulatorSite || `doi.${sp.abbr.toLowerCase()}.gov`, "Current"]);
  });
  ebSources.forEach(r => {
    html += `<tr><td>${r[0]}</td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td><td>${r[4]}</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 5. Benefits Competitiveness Analysis ---
  // Extract actual benefit data from uploaded EB documents (mirrors P&C extractDeclarationsLimits approach)
  const ebBenefitsData = extractEBBenefitsData(d.pdfTexts || []);
  const renewalPlans = extractRenewalPackagePlans(d.pdfTexts || []);
  // A benefit guide states medical/dental/vision as multi-plan tables. Where one
  // is present it is authoritative and REPLACES the flat regex result for that
  // benefit: the regex layer cannot hold two plan columns, and filled the gap by
  // pattern-matching neighbouring cells (a "$25" medical deductible against a
  // real $5,000/$3,000, and a "$10/mo" premium in a guide that quotes no rates).
  const ebPlanTables = extractEBPlanTables(d.pdfTexts || []);
  const ebFromTables = ebBenefitsFromTables(ebPlanTables);
  Object.keys(ebFromTables).forEach(k => { ebBenefitsData[k] = ebFromTables[k]; });
  /* NO FIGURE WITHOUT A TABLE.
   *
   * What is left in ebBenefitsData now came from the flat keyword layer: the
   * first number near a word. On Friendship's guide that was a "$15
   * deductible" (the generic-drug copay), "50% coinsurance" (specialty drugs)
   * and "$2,000" basic life (a guide stating 2x salary to $500,000) — and the
   * numbers changed between runs as LlamaParse's text shifted. A figure with a
   * citation that the guide does not state is worse than no figure.
   *
   * The flat layer may still say that the benefit IS in the guide, and where.
   * It may no longer say what it is. */
  Object.keys(ebBenefitsData).forEach(k => {
    const v = ebBenefitsData[k];
    if (!v || v._fromTables || Object.keys(v).length === 0) return;
    ebBenefitsData[k] = { _unread: true, pages: ebBenefitPages(d.pdfTexts || [], k) };
  });

  html += `<div class="report-section"><h3>6. Benefits Competitiveness Analysis</h3>`;
  html += `<p>The following analysis compares ${escapeHtml(d.policyholderName)}\'s benefits program against industry standards and current coverages/limits (extracted from uploaded benefit documents) for the <strong>${escapeHtml(d.vertical)}</strong> sector in ${stateNames}.</p>`;

  // v2.17: If renewal package with multiple plans detected, show plan comparison first
  if (renewalPlans && renewalPlans.length > 0) {
    const groupName = renewalPlans[0].groupName || escapeHtml(d.policyholderName);
    const groupNum = renewalPlans[0].groupNumber ? ' (Group #' + renewalPlans[0].groupNumber + ')' : '';
    const anniv = renewalPlans[0].anniversaryDate ? ' | Anniversary: ' + renewalPlans[0].anniversaryDate : '';
    html += `<div style="margin-bottom:12px;padding:10px 14px;background:#e8f4f4;border:1px solid #01696F;border-radius:6px;">`;
    html += `<strong style="color:#003366;">📋 Renewal Package Detected — ${renewalPlans.length} Health Plan${renewalPlans.length > 1 ? 's' : ''} Found</strong><br>`;
    html += `<span style="font-size:12px;color:#555;">${groupName}${groupNum}${anniv}</span>`;
    html += `</div>`;
    html += formatRenewalPlansDisplay(renewalPlans);
    html += `<div style="margin:10px 0;padding:8px 12px;background:#fff8e1;border-left:3px solid #B8860B;border-radius:4px;font-size:12px;">`;
    html += `<strong>Note:</strong> This document is a carrier renewal package, not a full policy binder. Deductibles, copays, coinsurance, OOP maximums, and Rx tiers have been extracted from the benefit summary grids. For complete plan details including exclusions, limitations, and covered services, refer to the Summary of Benefits and Coverage (SBC) documents${renewalPlans.some(p => p.hasSBC) ? ' (included in this package)' : ''} or the full plan certificates.`;
    html += `</div>`;
  }

  // v2.17: Detect pediatric-only dental/vision from SBC (no standalone adult plan)
  let pediatricOnlyDental = false;
  let pediatricOnlyVision = false;
  if (d.pdfTexts && d.pdfTexts.length > 0) {
    const allText = d.pdfTexts.map(p => (p.pages || []).map(pg => pg.text || '').join(' ')).join(' ');
    const hasChildDental = /Children'?s\s+dental\s+check-?up/i.test(allText);
    const hasChildVision = /Children'?s\s+eye\s+exam/i.test(allText);
    const hasDentalNotCovered = /(?:Adult|Routine)\s+(?:dental|oral).*Not\s+Covered/i.test(allText) || (/dental\s+check-?up/i.test(allText) && !/dental\s+(?:plan|PPO|HMO|annual\s+max|deductible.*preventive)/i.test(allText));
    const noStandaloneDental = !ebBenefitsData['Dental'] || Object.keys(ebBenefitsData['Dental'] || {}).length === 0;
    const noStandaloneVision = !ebBenefitsData['Vision'] || Object.keys(ebBenefitsData['Vision'] || {}).length === 0;
    if (hasChildDental && noStandaloneDental) pediatricOnlyDental = true;
    if (hasChildVision && noStandaloneVision) pediatricOnlyVision = true;
  }

  // Check if a benefit type has extracted data
  const hasEBData = (type) => {
    if (type === 'Medical (Group Health)' && renewalPlans && renewalPlans.length > 0) return true;
    return !!ebBenefitsData[type] && Object.keys(ebBenefitsData[type]).length > 0;
  };

  // A benchmark row must COMPARE the plan to the benchmark, not merely notice
  // that some field parsed. `hasEBData(type) ? 'MEETS'` declared every line at
  // market median the moment any value was extracted: an Equitable dental plan
  // with a $1,200 annual maximum was reported MEETS against a $1,500
  // benchmark, while its own schedule cell still read "100/?/?".
  //
  // This mirrors what the P&C benchmark table already does with
  // compareLimitToThreshold() — comparison first, MEETS only if it passes, and
  // BELOW STANDARD when it does not.
  const ebNum = (v) => {
    const m = /([\d,]+(?:\.\d+)?)/.exec(String(v == null ? '' : v));
    return m ? parseFloat(m[1].replace(/,/g, '')) : null;
  };
  /* THE DISABILITY PLANS COMPARE OFF THE PLAN TABLE, NOT THE FLAT LAYER.
   *
   * hasEBData reads ebBenefitsData, built by whole-document regexes -- the
   * layer the plan-table reader exists to replace. On the Arlington Classics
   * guide those regexes find nothing for either plan, so Section 9 reported
   * "Long-Term Disability: Not found in uploaded documents - request the plan
   * documents" about a guide that states 60% of pay to a $10,000 monthly
   * maximum after a 90-day elimination period, in a table this engine now
   * reads. Asking the broker for a document already uploaded is worse than
   * saying nothing.
   *
   * These two resolve from ebPlanTables first and COMPARE, rather than
   * returning MEETS because something parsed -- the mistake the note below
   * this one records. Where the guide states no LTD at all, the caller's
   * CRITICAL GAP still stands: an absent benefit is a real gap. */
  const ebDisabilityStatus = (type) => {
    const dis = ebPlanTables && ebPlanTables.disability;
    if (!dis) return null;
    const plan = type === 'Short-Term Disability' ? dis.std
               : (dis.ltd && dis.ltd.present ? dis.ltd : null);
    if (!plan) return null;
    const paid = String(plan.benefitsPaid || '');
    const pct = /(\d{1,3})\s*%/.exec(paid);
    if (!pct) return 'VERIFY';
    const pctN = parseInt(pct[1], 10);
    if (type === 'Short-Term Disability') {
      const wk = /(\d{1,3})\s*weeks?/i.exec(String(plan.benefitsEnd || ''));
      if (!wk) return 'VERIFY';
      return (pctN < 60 || parseInt(wk[1], 10) < 13) ? 'BELOW STANDARD' : 'MEETS';
    }
    const mo = /\$\s*([\d,]+)\s*per\s*month/i.exec(paid);
    if (!mo) return 'VERIFY';
    return (pctN < 60 || parseFloat(mo[1].replace(/,/g, '')) < 5000) ? 'BELOW STANDARD' : 'MEETS';
  };

  const ebStatus = (type) => {
    // In the guide but not read: nothing has been measured.
    if (ebBenefitsData[type] && ebBenefitsData[type]._unread
        && !((type === 'Short-Term Disability' || type === 'Long-Term Disability') && ebDisabilityStatus(type))) return 'VERIFY';
    if (type === 'Short-Term Disability' || type === 'Long-Term Disability') {
      const ds = ebDisabilityStatus(type);
      if (ds) return ds;
    }
    if (!hasEBData(type)) return null;               // caller keeps its gap logic
    const b = ebBenefitsData[type] || {};
    // Parsed from the guide's own plan table: score each plan, best one wins.
    const fromTable = ebTableStatus(type, b);
    if (fromTable) return fromTable.status;
    if (type === 'Dental') {
      const max = ebNum(b.annualMax);
      const sched = String(b.schedule || '').split('/');
      const basic = ebNum(sched[1]), major = ebNum(sched[2]);
      // Unknown values can never be MEETS — that is how "100/?/?" passed.
      if (max === null || basic === null || major === null) return 'VERIFY';
      return (max < 1500 || basic < 80 || major < 50) ? 'BELOW STANDARD' : 'MEETS';
    }
    if (type === 'Life / AD&D') {
      const amt = ebNum(b.benefit);
      if (amt === null) return 'VERIFY';
      return amt < 50000 ? 'BELOW STANDARD' : 'MEETS';
    }
    // Data extracted but no comparison defined for this line yet. Say VERIFY
    // rather than assert a market position nothing checked.
    return 'VERIFY';
  };

  /* SCHOOL EMPLOYEES HAVE A STATE PENSION THE GUIDE DOES NOT DESCRIBE.
   * Texas public and charter school staff belong to TRS by statute; a benefit
   * guide covers the elective benefits and leaves TRS out. Scoring "no 401(k)
   * found" against a 3% match as BELOW STANDARD reported a school's statutory
   * pension as a missing retirement plan. Ask instead. */
  const ebTeacherRetirement = ebTeacherRetirementFor(d);

  /* SAY WHY AN ACCOUNT CANNOT EXIST, WHEN THE GUIDE SHOWS IT.
   *
   * An HSA needs an HSA-qualified high-deductible plan. Where every medical
   * plan the guide prints is something else -- Jackson Hole Classical
   * Academy's only plan is a $1,000-deductible BCBSWY PPO -- "Not found in
   * uploaded documents" reads like a parsing miss. The guide answers the
   * question: no plan qualifies, so no HSA is possible. */
  const ebNoHsaNote = (() => {
    const med = ebPlanTables && ebPlanTables.medical;
    if (!med || !(med.columns || []).length) return null;
    if ((med.columns || []).some(c => /\bHSA\b|HDHP|high[- ]deductible|\bCDHP\b/i.test(c))) return null;
    const names = [];
    med.columns.forEach(c => {
      const n = String(c).replace(/\s*(?:in[- ]network(?: only)?|out[- ]of[- ]network)\s*$/i, '').trim();
      if (n && names.indexOf(n) < 0) names.push(n);
    });
    return 'No HSA-qualified (high-deductible) medical plan in the benefit guide; the medical plan'
      + (names.length > 1 ? 's offered are ' : ' offered is ') + names.join(', ')
      + ', so employees cannot open an HSA. No FSA is described in the guide.';
  })();

  const ebBenchmarks = [
    { type:"Medical (Group Health)", median:"PPO/HDHP with 80/20 cost share", standard:"$1,500–$3,000 deductible, 80/20 coinsurance", status: hasEBData("Medical (Group Health)") ? "MEETS" : (d.files.length > 0 ? "VERIFY" : "VERIFY") },
    { type:"Dental", median:"$1,500 annual max, 80/50/50 schedule", standard:"$1,500 annual max", status: ebStatus("Dental") || (pediatricOnlyDental ? "HIGH GAP" : (d.files.length === 0 ? "HIGH GAP" : "VERIFY")), pediatricOnly: pediatricOnlyDental },
    { type:"Vision", median:"$150 frame allowance, $25 copay exam", standard:"$150 frame allowance", status: hasEBData("Vision") ? "MEETS" : pediatricOnlyVision ? "MODERATE" : (d.files.length === 0 ? "MODERATE" : "VERIFY"), pediatricOnly: pediatricOnlyVision },
    { type:"Life / AD&D", median:"1x salary basic life, $50K minimum", standard:"1x salary, $50K min", status: ebStatus("Life / AD&D") || (d.files.length === 0 ? "HIGH GAP" : "VERIFY") },
    { type:"Short-Term Disability", median:"60% of salary, 13-week benefit period", standard:"60% salary, 13 weeks", status: ebStatus("Short-Term Disability") || (d.files.length === 0 ? "HIGH GAP" : "VERIFY") },
    // A benefit guide that never mentions LTD is not an unparsed document: the
    // line is not covered. Section 11 already reports it as the largest
    // uncovered income exposure, at CRITICAL.
    { type:"Long-Term Disability", median:"60% of salary to $5K/month, 90-day elimination", standard:"60% salary to $5K/mo", status: ebStatus("Long-Term Disability") || ((ebPlanTables.disability && !ebPlanTables.disability.ltd.present) ? "CRITICAL GAP" : (d.files.length === 0 ? "CRITICAL GAP" : "VERIFY")) },
    // Never VERIFY: with documents in hand this row is scored either way — no
    // retirement plan found against a 3% benchmark is below it.
    { type:"401(k) / Retirement", median:"3% employer match, immediate eligibility", standard:"3% employer match", status: ebStatus("401(k) / Retirement") || (d.files.length === 0 ? "HIGH GAP" : ebTeacherRetirement ? "VERIFY" : "BELOW STANDARD"),
      ...(ebTeacherRetirement && !ebStatus("401(k) / Retirement") && d.files.length > 0 ? {
        cellNote: 'Not in the benefit guide. ' + ebTeacherRetirement.name + ' covers school employees by statute and benefit guides rarely describe it. Confirm participation and any supplemental 403(b) or 457(b) before treating retirement as a gap.',
        gapStatus: 'Not in the benefit guide; confirm ' + ebTeacherRetirement.short + ' participation',
        gapAction: 'Confirm ' + ebTeacherRetirement.short + ' participation and whether a supplemental 403(b) or 457(b) is offered',
        gapCost: 'TBD after confirmation' }
      : d.files.length > 0 ? { cellNote: 'No 401(k), 403(b) or other retirement plan is described in the benefit guide. Confirm with the employer whether one is offered.' } : {}) },
    // Never VERIFY, for the same reason as the row above.
    { type:"HSA / FSA", median:"HSA with HDHP, employer seed contribution", standard:"HSA with employer seed", status: ebStatus("HSA / FSA") || (d.files.length === 0 ? "MODERATE" : "BELOW STANDARD"),
      ...(ebNoHsaNote && d.files.length > 0 ? { cellNote: ebNoHsaNote } : {}) }
  ];

  html += renderEBPlanTables(ebPlanTables);

  html += `<table class="report-table"><thead><tr><th>Benefit Type</th><th>Industry Standard</th><th>Current Coverage / Limits</th><th>Current Status</th><th>Citation</th></tr></thead><tbody>`;
  ebBenchmarks.forEach(b => {
    // Extract actual benefit data for this type
    const extractedBenefit = ebBenefitsData[b.type] || null;
    let formattedBenefit = (extractedBenefit && extractedBenefit._unread)
      ? '<em style="color:var(--text-secondary);">' + escapeHtml(ebUnreadText(extractedBenefit)) + '</em>'
      : (extractedBenefit && extractedBenefit._fromTables)
      ? ebFormatFromTables(b.type, extractedBenefit)
      : formatEBBenefitsDisplay(extractedBenefit);
    /* A row cannot say MEETS beside "Not found in uploaded documents".
     *
     * The status for the two disability lines now compares off the plan table,
     * and this cell still read the flat regex layer, so the Arlington Classics
     * LTD row rendered "Not found in uploaded documents" and a green MEETS side
     * by side. One of the two had to be wrong, and a reader has no way to tell
     * which. The cell states what the plan table holds. */
    if (!formattedBenefit
        && (b.type === 'Short-Term Disability' || b.type === 'Long-Term Disability')
        && ebPlanTables && ebPlanTables.disability) {
      const dis = ebPlanTables.disability;
      const plan = b.type === 'Short-Term Disability' ? dis.std
                 : (dis.ltd && dis.ltd.present ? dis.ltd : null);
      if (plan) {
        const bits = [];
        if (plan.benefitsPaid) bits.push(escapeHtml(String(plan.benefitsPaid).replace(/\s+/g, ' ').trim()));
        if (plan.benefitsBegin) bits.push('Begins: ' + escapeHtml(String(plan.benefitsBegin).replace(/\s+/g, ' ').trim()));
        if (plan.benefitsEnd) bits.push('Ends: ' + escapeHtml(String(plan.benefitsEnd).replace(/\s+/g, ' ').trim()));
        if (plan.whoPays) bits.push('Paid by: ' + escapeHtml(String(plan.whoPays).trim()));
        if (bits.length) formattedBenefit = bits.join('<br>');
      }
    }
    // v2.17: Override Medical row with multi-plan renewal data when available
    if (b.type === 'Medical (Group Health)' && renewalPlans && renewalPlans.length > 0) {
      formattedBenefit = formatRenewalPlansDisplay(renewalPlans);
    }
    // v2.17: Flag pediatric-only dental/vision coverage
    if (b.pediatricOnly && !formattedBenefit) {
      const benefitLabel = b.type === 'Dental' ? 'dental' : 'vision';
      formattedBenefit = '<div style="padding:6px 8px;background:#fff3e0;border-radius:4px;border-left:3px solid #B8860B;">' +
        '<strong style="color:#8B4513;">Pediatric Only</strong><br>' +
        '<span style="font-size:11px;">Children\'s ' + benefitLabel + ' services covered under medical plan SBC. ' +
        'No standalone adult ' + benefitLabel + ' plan detected.' +
        (b.type === 'Dental' ? ' No orthodontia coverage detected.' : '') +
        '</span></div>';
    }

    // Determine effective status by COMPARISON, not by presence. This override
    // previously forced 'MEETS' whenever any field parsed, overruling whatever
    // the row computed — and it writes back to b.status, so Section 8's Gap
    // Analysis inherited the same false pass.
    let effectiveStatus = b.status;
    if (extractedBenefit && Object.keys(extractedBenefit).length > 0) {
      effectiveStatus = ebStatus(b.type) || b.status;
    }
    // CRITICAL: Update the benchmark status so Section 8 (Gap Analysis) uses same effective status
    b.status = effectiveStatus;

    let badge = '';
    if (effectiveStatus === 'MEETS') badge = '<span class="badge badge-meets">\u2705 MEETS</span>';
    else if (effectiveStatus === 'BELOW STANDARD') badge = '<span class="badge badge-high">\u26a0\ufe0f BELOW STANDARD</span>';
    else if (effectiveStatus === 'VERIFY') badge = '<span class="badge badge-verify">\u26a0\ufe0f VERIFY</span>';
    else if (effectiveStatus === 'CRITICAL GAP') badge = '<span class="badge badge-critical">\u274c CRITICAL GAP</span>';
    else if (effectiveStatus === 'HIGH GAP') badge = '<span class="badge badge-high">\u274c HIGH GAP</span>';
    else badge = '<span class="badge badge-moderate">\u26a0\ufe0f MODERATE</span>';

    // Show extracted benefits data instead of generic text
    const benefitCell = formattedBenefit
      ? formattedBenefit
      // One fallback, not two. "Benefit document uploaded — details not parsed
      // from text" described the PARSER's state, not the plan's: a reviewer
      // could not tell it from a real absence, and it read as an excuse in a
      // report a carrier may see. Where nothing was extracted, the cell says
      // the line was not found.
      : b.cellNote ? '<em style="color:var(--text-secondary);">' + escapeHtml(b.cellNote) + '</em>'
      : '<em style="color:var(--critical);">Not found in uploaded documents</em>';

    const citationText = extractedBenefit
      ? 'Extracted from uploaded benefit documents'
      : (effectiveStatus === 'MEETS' ? 'Detected in uploaded documents' : effectiveStatus === 'VERIFY' ? 'Requires verification against plan documents' : 'Not detected in uploaded documents');

    html += `<tr><td><strong>${b.type}</strong></td><td>${b.median}</td><td>${benefitCell}</td><td>${badge}</td><td style="font-size:11px;color:var(--text-muted);font-style:italic;">[Benefits Analysis, Sec. 6: "${b.type} \u2014 ${citationText}"]</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 6. Exclusion & Contradiction Analysis (EB) ---
  const ebExclusionAlerts = getEBExclusionAlerts(d.detectedCoverages, d.vertical);
  // Split by EVIDENCE: language actually located in the upload is a finding
  // about this plan; everything else is standard EB guidance to check, and is
  // labelled as such rather than dressed up with a citation.
  // Located language only — see the P&C note above.
  const ebGrounded = [];
  ebExclusionAlerts.forEach(ea => {
    const hit = groundEBExclusion(ea, d.pdfTexts || []);
    if (hit) ebGrounded.push({ ea: ea, hit: hit });
  });
  html += `<div class="report-section"><h3>7. Exclusion & Contradiction Analysis — Critical Review</h3>`;
  {
    const _s7 = window.spSection7Intro((d.pdfTexts || []).length, escapeHtml);
    html += _s7.subtitle + _s7.intro;
  }

  // The same cross-policy pass the P&C report runs. The copy above promises a
  // cross-policy review, so the review has to happen here too — and it is
  // evidence-gated, so on a benefits program that carries no policy wording it
  // simply finds nothing and says nothing.
  const ebCross = (typeof window.spCrossPolicyFindings === 'function')
    ? window.spCrossPolicyFindings(d.pdfTexts || [], { eoScan: d.deepScanEOResults }) : [];
  ebCross.forEach(cf => {
    const cls = cf.severity === 'CRITICAL' ? 'exclusion-alert'
              : cf.severity === 'MODERATE' ? 'exclusion-alert resolved-level' : 'exclusion-alert warning-level';
    const badge = cf.severity === 'CRITICAL' ? 'badge-critical' : cf.severity === 'HIGH' ? 'badge-high' : 'badge-moderate';
    html += `<div class="${cls}">
      <div class="ea-header">
        <span class="ea-coverage">Cross-policy</span>
        <span style="color:var(--text-muted);">—</span>
        <span class="ea-exclusion">${escapeHtml(cf.title)}</span>
        <span class="badge ${badge}">${cf.severity}</span>
      </div>`;
    cf.evidence.forEach(ev => {
      if (!ev || !ev.file) return;
      html += `<div class="ea-citation">${escapeHtml(ev.label)} — [${escapeHtml(ev.file)}${ev.section ? ', ' + escapeHtml(ev.section) : ''}, p. ${ev.page}${ev.pageEnd ? '–' + ev.pageEnd : ''}: “${escapeHtml(ev.quote || '')}”]</div>`;
    });
    html += `<div class="ea-impact"><strong>Why it matters:</strong> ${escapeHtml(cf.mechanism)}</div>`;
    html += `<div class="ea-recommendation"><strong>Action Required:</strong> ${escapeHtml(cf.action)}</div>`;
    html += `</div>`;
  });

  if (ebGrounded.length > 0) {
    html += `<p style="font-size:13px; color:var(--critical); font-weight:600;">⚠️ BROKER ALERT: exclusionary language below was located in the uploaded documents and is quoted with its source. Coverage may appear to exist while being limited or voided by this language.</p>`;
    ebGrounded.forEach(({ ea, hit }) => {
      const isFatal = ea.impact.includes('FATAL FLAW');
      html += `<div class="exclusion-alert${isFatal ? '' : ' warning-level'}">
        <div class="ea-header">
          <!-- The EB grounder runs no endorsement-scope analysis, so there is
               no scoped coverage here. An earlier edit to the P&C renderer
               reached this block and left it referencing a variable that does
               not exist in this scope, which threw at report generation. -->
          <span class="ea-coverage">${escapeHtml(ea.coverage)}</span>
          <span style="color:var(--text-muted);">vs.</span>
          <span class="ea-exclusion">${ea.exclusion}</span>
          <span class="badge ${isFatal ? 'badge-critical' : 'badge-high'}">${isFatal ? 'FATAL FLAW' : 'VERIFY'}</span>
        </div>
        <div class="ea-citation">[${escapeHtml(hit.fileName)}${hit.section ? ', ' + escapeHtml(hit.section) : ''}, p. ${hit.page}${hit.pageEnd ? '\u2013' + hit.pageEnd : ''}${hit.pageNote}: “${escapeHtml(hit.quote)}”]<span style="font-size:10px;color:var(--text-muted);"> matched “${escapeHtml(hit.term)}”</span></div>
        <div class="ea-body">
          <p><strong>Impact:</strong> ${ea.impact}</p>
          <p><strong>Broker Action Required:</strong> ${ea.recommendation}</p>
        </div>
      </div>`;
    });
  }
  if (ebGrounded.length === 0 && ebCross.length === 0) {
    html += `<p style="font-size:13px; color:var(--text-secondary);">No conflicting language was located in the ${(d.pdfTexts || []).length} uploaded document(s). A benefits guide is a summary — the operative language lives in the certificate, SPD or master policy — so the broker should still review those for limitations, waiting periods and coordination-of-benefits provisions.</p>`;
  }
  html += `</div>`;

  // --- 6B. Deep Policy Text Scan (EB) ---
  /* No 7B on an Employee Benefits review.
   *
   * It used to render here for symmetry with the P&C and BOP reports, on the
   * argument that reporting two negatives beats leaving a reader to assume the
   * check never ran. That argument does not survive contact with the documents.
   *
   * 7B scans policy text for D&O and Professional Liability grants written into
   * another form. An EB review is handed handbooks, plan summaries, SBCs and
   * benefit schedules. Those cannot contain a liability coverage grant, so the
   * scan is not finding an absence, it is reporting one about documents that
   * could never carry the answer. A negative implies a meaningful test was
   * made, and here none was possible. On Arlington Classics it read as though
   * the school's D&O had been searched for and not found.
   *
   * The P&C and BOP reports keep 7B: they are handed policy forms, where the
   * scan is answerable and the negative means something.
   */

  // --- 6C. Website / Operational Risk Analysis ---
  /* --- 7C. Website & Operational Risk Analysis ---
   *
   * The heading renders unconditionally; the FINDINGS stay guarded. Those are
   * two different rules and only one of them was the grounding rule.
   *
   * Before, the whole section was inside the guard, so a report on a site with
   * nothing to flag and a report where the analysis never ran were
   * indistinguishable: no heading at all. On the Base Power review the site was
   * read (1 page, cross-checked in Section 3) and 7C simply was not there. A
   * broker cannot tell "we looked and found nothing" from "we did not look",
   * and on a broker-of-record proposal those are different statements.
   *
   * What must never render is a finding no source evidenced — that is what
   * deleting simulateWebsiteContent was about, and it still holds: everything
   * inside the length check below is unchanged.
   */
  html += `<div class="report-section"><h3>7C. Website & Operational Risk Analysis</h3>`;
  if (d.websiteRisks && d.websiteRisks.length > 0) {
    const _wsMeta = window._siteFetchMeta;
    if (_wsMeta && _wsMeta.host) {
      html += `<p style="font-size:13px;">SentriPol\u2122 read <strong>${escapeHtml(_wsMeta.host)}</strong> (${_wsMeta.pages ? _wsMeta.pages.length : 1} page(s), ${(_wsMeta.chars || 0).toLocaleString()} characters) together with the organization profile. <strong>${d.websiteRisks.length} operational risk(s)</strong> were evidenced in that text and require broker review.</p>`;
    } else {
      html += `<p style="font-size:13px;">SentriPol\u2122 analyzed the organization profile for operational activities that may create coverage gaps or trigger policy exclusions. <strong>${d.websiteRisks.length} operational risk(s)</strong> were evidenced and require broker review.</p>`;
      html += `<div style="margin:8px 0;padding:8px 12px;background:#fff8e1;border-left:3px solid #B8860B;border-radius:4px;font-size:12px;"><strong>No website text was read${window._siteFetchError ? ' (' + escapeHtml(window._siteFetchError) + ')' : ''}.</strong> Findings below rest on the organization profile alone.</div>`;
    }
    // v2.22 (broker rule): de-identify — do not display real client URL in Section 6C output.
    
    // Group risks by category
    const risksByCategory = {};
    d.websiteRisks.forEach(r => {
      if (!risksByCategory[r.category]) risksByCategory[r.category] = [];
      risksByCategory[r.category].push(r);
    });
    
    // Critical risks callout
    const criticalWebRisks = d.websiteRisks.filter(r => r.severity === 'CRITICAL');
    if (criticalWebRisks.length > 0) {
      html += `<div style="margin:12px 0; padding:12px 16px; background:var(--critical-bg, #fef2f2); border-left:4px solid var(--critical); border-radius:var(--radius-sm);">`;
      html += `<strong style="color:var(--critical);">\u26a0\ufe0f CRITICAL: ${criticalWebRisks.length} operational risk(s) identified that commonly trigger policy exclusions:</strong>`;
      html += `<ul style="margin:8px 0 0; font-size:12px;">`;
      criticalWebRisks.forEach(r => {
        html += `<li><strong>${escapeHtml(r.risk)}</strong> \u2014 Cross-reference: <em>${escapeHtml(r.exclusionType)}</em></li>`;
      });
      html += `</ul></div>`;
    }
    
    // Full risk table
    html += `<table class="report-table"><thead><tr><th>Operational Risk</th><th>Category</th><th>Severity</th><th>Policy Exclusion to Verify</th><th>Evidence</th><th>Citation</th></tr></thead><tbody>`;
    d.websiteRisks.forEach(r => {
      const sevBadge = r.severity === 'CRITICAL' ? '<span class="badge badge-critical">\u274c CRITICAL</span>'
        : r.severity === 'HIGH' ? '<span class="badge badge-high">\u26a0\ufe0f HIGH</span>'
        : '<span class="badge badge-moderate">MODERATE</span>';
      html += `<tr><td><strong>${escapeHtml(r.risk)}</strong></td><td>${escapeHtml(r.category)}</td><td>${sevBadge}</td><td style="font-size:12px;">${escapeHtml(r.exclusionType)}</td><td style="font-size:11px; font-style:italic;">\u201c${escapeHtml(r.evidence)}\u201d</td><td style="font-size:11px; color:var(--text-muted); font-style:italic;">[${escapeHtml(r.source)}, Sec. 7C: \u201cOperational activity evidenced in source text\u201d]</td></tr>`;
    });
    html += `</tbody></table>`;
    
    // Nothing is listed that this website did not say. An exposure typical of
    // the sector but absent from the insured's own text is not a finding about
    // the insured, and printing it as one asked the broker to chase nine
    // questions the documents had not raised.

    // Recommendations by category
    html += `<h4 style="font-size:13px; font-weight:700; color:var(--navy); margin:16px 0 8px;">Broker Action Items \u2014 Website Risk Cross-Reference</h4>`;
    html += `<div class="next-steps-grid">`;
    Object.entries(risksByCategory).forEach(([cat, risks]) => {
      const catCritical = risks.filter(r => r.severity === 'CRITICAL').length;
      const catHigh = risks.filter(r => r.severity === 'HIGH').length;
      html += `<div class="next-step-card">`;
      html += `<h5>${catCritical > 0 ? '\u274c' : catHigh > 0 ? '\u26a0\ufe0f' : '\u2139\ufe0f'} ${escapeHtml(cat)} (${risks.length} risk${risks.length > 1 ? 's' : ''})</h5>`;
      html += `<ul style="font-size:12px;">`;
      risks.forEach(r => {
        html += `<li><strong>${escapeHtml(r.risk)}</strong>: Verify that <em>${escapeHtml(r.exclusionType)}</em> does not void coverage for this operational activity.</li>`;
      });
      html += `</ul></div>`;
    });
    html += `</div>`;
    
    html += `<p style="font-size:11px; color:var(--text-muted); margin-top:10px;"><em>Website & Operational Risk Analysis identifies activities, events, and operational exposures from the policyholder\u2019s website and organizational profile that may trigger policy exclusions. The broker must cross-reference each identified risk against actual policy exclusions, endorsements, and sublimits to determine if coverage applies. Page and section citations from policy documents should be verified for each exclusion type listed above.</em></p>`;
  } else {
    // Nothing evidenced. Say which of the two cases this is.
    const _wsMetaNone = window._siteFetchMeta;
    if (_wsMetaNone && _wsMetaNone.host) {
      html += `<p style="font-size:13px;">SentriPol\u2122 read <strong>${escapeHtml(_wsMetaNone.host)}</strong> (${_wsMetaNone.pages ? _wsMetaNone.pages.length : 1} page(s), ${(_wsMetaNone.chars || 0).toLocaleString()} characters) together with the organization profile. <strong>No operational risks were evidenced</strong> in that text \u2014 no activity, event or exposure in the site copy matched a pattern that bears on a policy exclusion.</p>`;
    } else {
      html += `<div style="margin:8px 0;padding:8px 12px;background:#fff8e1;border-left:3px solid #B8860B;border-radius:4px;font-size:12px;"><strong>No website text was read${window._siteFetchError ? ' (' + escapeHtml(window._siteFetchError) + ')' : ''}.</strong> This section reports that nothing was analysed, which is not the same as finding no risk. Supply a reachable website URL to have this section evidenced.</div>`;
    }
  }
  html += `</div>`;

  // --- 7. Federal Compliance Penalty Structure ---
  html += `<div class="report-section"><h3>8. Federal Compliance Penalty Structure</h3>`;
  html += `<table class="report-table"><thead><tr><th>Regulation</th><th>Violation Type</th><th>Penalty Range</th><th>Applies To</th></tr></thead><tbody>`;
  const ebPenalties = [
    ["ERISA", "Failure to file Form 5500", "$250/day, up to $150,000 per plan year", "All ERISA-covered plans"],
    ["ERISA", "Failure to provide SPD", "$110/day per participant request", "All ERISA-covered plans"],
    ["ERISA", "Fiduciary breach", "Personal liability for losses + disgorgement of profits", "Plan fiduciaries"],
    ["ACA", "Employer mandate penalty (§4980H(a))", "$2,970 per FTE (2025) × (total FTEs − 30)", "ALEs (50+ FTEs)"],
    ["ACA", "Inadequate coverage penalty (§4980H(b))", "$4,460 per affected employee (2025)", "ALEs (50+ FTEs)"],
    ["ACA", "Reporting failures (1094-C/1095-C)", "$310 per return, up to $3.783M", "ALEs (50+ FTEs)"],
    ["COBRA", "Failure to provide notice", "$110/day per qualified beneficiary", "Employers with 20+ employees"],
    ["COBRA", "Failure to provide coverage", "Excise tax of $100/day per beneficiary", "Employers with 20+ employees"],
    ["HIPAA", "Unknowing violation", "$100–$50,000 per violation, $25,000 annual max per category", "Covered entities and business associates"],
    ["HIPAA", "Willful neglect (corrected)", "$10,000–$50,000 per violation", "Covered entities and business associates"],
    ["HIPAA", "Willful neglect (not corrected)", "$50,000 per violation, $1.5M annual max", "Covered entities and business associates"],
    ["FMLA", "Willful violation", "Back pay + liquidated damages (2x back pay) + attorney fees", "Employers with 50+ employees"]
  ];
  ebPenalties.forEach(r => {
    html += `<tr><td><strong>${r[0]}</strong></td><td>${r[1]}</td><td>${r[2]}</td><td>${r[3]}</td></tr>`;
  });
  html += `</tbody></table></div>`;

  // --- 8. Priority Gap Analysis ---
  // Section 11's enhancements are built FIRST so this section can say which
  // gaps a recommendation already covers, and flag the ones none does.
  const ebLines = ebDetectCoverageLines(d.pdfTexts || []);
  const ebEnhancements = buildEBEnhancements(ebPlanTables, ebLines);
  const ebInPlace = ebAlreadyInPlace(ebPlanTables, ebLines);

  const ebGaps = buildEBGaps(ebBenchmarks, ebBenefitsData, ebEnhancements, {
    exposure: getEBExposureRange, premium: getEBPremiumEstimate
  });
  // A VERIFY line asks for confirmation, not a fix, so it is not counted as a
  // gap nothing addresses.
  const ebUnaddressed = ebGaps.filter(g => !g.addressedBy && g.priority !== 'VERIFY');
  const ebScored = ebGaps.filter(g => g.priority !== 'VERIFY');

  html += `<div class="report-section"><h3>9. Priority Gap Analysis & Recommendations</h3>`;
  html += `<p style="font-size:13px;">Every line below is carried from the benchmark table in Section 6, quoting the value read from the plan documents where one was extracted. A gap with no matching recommendation in Section 11 is highlighted: nothing downstream proposes a fix for it.</p>`;
  if (ebGaps.length === 0) {
    html += `<p style="font-size:13px;">No gap was raised: every benchmark line either meets its standard or has no document to measure.</p>`;
  } else {
    html += ebUnaddressed.length > 0
      ? `<div style="margin:8px 0;padding:8px 12px;border-left:4px solid var(--critical);background:#fff5f5;font-size:13px;"><strong>${ebUnaddressed.length} of ${ebScored.length} gap(s) are not addressed by a Section 11 recommendation:</strong> ${escapeHtml(ebUnaddressed.map(g => g.type).join(', '))}.</div>`
      : `<div style="margin:8px 0;padding:8px 12px;border-left:4px solid var(--success, #2e7d32);background:#f2faf5;font-size:13px;">Every gap below is addressed by a recommendation in Section 11.</div>`;
    html += `<table class="report-table"><thead><tr><th>Priority</th><th>Benefit Type</th><th>Current Status (from the plan documents)</th><th>Est. Impact</th><th>Recommended Action</th><th>Est. Cost/EE/Mo</th><th>Addressed</th><th>Citation</th></tr></thead><tbody>`;
    ebGaps.forEach(g => {
      const badgeClass = g.priority === 'CRITICAL' ? 'badge-critical' : g.priority === 'HIGH' ? 'badge-high' : g.priority === 'VERIFY' ? 'badge-verify' : 'badge-moderate';
      const isVerify = g.priority === 'VERIFY';
      const rowStyle = g.addressedBy || isVerify ? '' : ' style="background:#fff5f5;"';
      const addressed = g.addressedBy
        ? `<span style="font-size:11px;color:var(--text-muted);">Sec. 11 — ${escapeHtml(g.addressedBy)}</span>`
        : isVerify ? `<span style="font-size:11px;color:var(--text-muted);">Confirm before recommending</span>`
        : `<span class="badge badge-critical">⚠️ NOT ADDRESSED</span>`;
      html += `<tr${rowStyle}><td><span class="badge ${badgeClass}">${g.priority}</span></td><td><strong>${escapeHtml(g.type)}</strong></td><td>${escapeHtml(g.currentStatus)}</td><td>${g.exposure}</td><td>${escapeHtml(g.action)}</td><td>${escapeHtml(g.premium)}</td><td>${addressed}</td><td style="font-size:11px;color:var(--text-muted);font-style:italic;">[EB Gap Analysis, Sec. 9: "${escapeHtml(g.type)} — ${g.priority} priority; ${escapeHtml(g.currentStatus)}"]</td></tr>`;
    });
    html += `</tbody></table>`;
  }
  // See the P&C note above — the E&O Mitigation Record reads this list.
  window._spRecommendedGaps = ebGaps;
  html += `</div>`;

  // --- 9. Employee Retention Risk Assessment ---
  html += `<div class="report-section"><h3>10. Employee Retention Risk Assessment</h3>`;
  const criticalEBCount = ebGaps.filter(g => g.priority === 'CRITICAL').length;
  const highEBCount = ebGaps.filter(g => g.priority === 'HIGH').length;
  const riskLevel = criticalEBCount >= 2 ? 'HIGH' : highEBCount >= 3 ? 'ELEVATED' : 'MODERATE';
  html += `<div class="retention-box">
    <h4>⚠️ Employee Retention Risk Level: ${riskLevel}</h4>
    <p style="font-size:13px; color:var(--text-primary);">Based on the identified benefits gaps, ${escapeHtml(d.policyholderName)} faces <strong>${riskLevel.toLowerCase()}</strong> employee retention risk. Organizations in the ${escapeHtml(d.vertical)} sector with below-market benefits packages experience an average of <strong>15–25% higher voluntary turnover</strong> compared to peers offering competitive benefits.</p>
    <p style="font-size:13px; color:var(--text-primary); margin-top:8px;"><strong>Key Retention Risk Factors:</strong></p>
    <ul style="font-size:12px; color:var(--text-secondary); padding-left:20px; margin-top:4px;">
      ${criticalEBCount > 0 ? `<li><strong>${criticalEBCount} critical gap(s)</strong> in core benefits that employees consider essential when evaluating employers</li>` : ''}
      ${highEBCount > 0 ? `<li><strong>${highEBCount} high-priority gap(s)</strong> where benefits fall below market median for the ${escapeHtml(d.vertical)} sector</li>` : ''}
      <li>Average cost of employee turnover: <strong>50–200% of annual salary</strong> per position</li>
      <li>Current labor market conditions favor employees in the ${escapeHtml(d.vertical)} sector, increasing flight risk</li>
    </ul>
  </div></div>`;

  // --- 10. Recommended Enhancements ---
  // Derived from the coverage actually extracted, so each row names the plan
  // value it is measured against. The fixed eight-row list this replaces ran
  // regardless of the program: it told a client with 50% orthodontia to "add
  // orthodontia", called a full voluntary suite "limited options", and asked a
  // client with no employer match at all to "increase" it — while the real
  // finding, no LTD anywhere in the guide, sat undifferentiated among them.
  // ebLines / ebEnhancements / ebInPlace are built above, before Section 9,
  // which cross-references these rows to flag the gaps none of them addresses.
  html += `<div class="report-section"><h3>11. Recommended Enhancements with Cost per Employee</h3>`;
  if (ebEnhancements.length > 0) {
    html += `<p style="font-size:13px;">Each enhancement below is raised against a value read from the benefit documents; the gap column quotes it. Cost ranges are market estimates and must be confirmed by carrier quote.</p>`;
    html += `<table class="report-table enhancement-table"><thead><tr><th>Priority</th><th>Enhancement</th><th>Current Gap (from the plan documents)</th><th>Recommended Enhancement</th><th>Est. Cost/EE/Month</th><th>ROI Justification</th></tr></thead><tbody>`;
    ebEnhancements.forEach(e => {
      const badge = e.priority === 'CRITICAL' ? '<span class="badge badge-critical">\u274c CRITICAL</span>'
        : e.priority === 'HIGH' ? '<span class="badge badge-high">\u26a0\ufe0f HIGH</span>'
        : '<span class="badge badge-moderate">MODERATE</span>';
      html += `<tr><td>${badge}</td><td><strong>${escapeHtml(e.item)}</strong></td><td>${escapeHtml(e.gap)}</td><td>${escapeHtml(e.rec)}</td><td class="cost-cell">${escapeHtml(e.cost)}</td><td>${escapeHtml(e.roi)}</td></tr>`;
    });
    html += `</tbody></table>`;
  } else if ((d.files || []).length > 0) {
    html += `<p style="font-size:13px;">No enhancement was raised: every line measurable from the uploaded documents meets its benchmark. Lines the documents do not describe are listed in the gap analysis as VERIFY rather than assumed adequate.</p>`;
  } else {
    html += `<p style="font-size:13px; color:var(--warning);"><em>No benefit documents were uploaded, so no enhancement can be measured against this program. Upload the benefit guide, SBCs or carrier certificates to populate this section.</em></p>`;
  }

  // Naming what is already in place stops the section recommending it: the
  // previous list proposed a voluntary suite to a client who already had one.
  if (ebInPlace.length > 0) {
    html += `<p style="font-size:12px; color:var(--text-muted); margin-top:10px;"><strong>Already in place \u2014 not recommended for addition:</strong> ${escapeHtml(ebInPlace.join('; '))}.</p>`;
  }
  html += `</div>`;

  // --- 11. Summary of Total Exposure ---
  const modEBCount = ebGaps.filter(g => g.priority === 'MODERATE' || g.priority === 'VERIFY').length;
  html += `<div class="report-section"><h3>12. Summary of Total Exposure</h3>
    <div class="summary-box">
      <h4>${escapeHtml(d.policyholderName)} — Benefits Gap Exposure Summary</h4>
      <div class="summary-stats">
        <div class="summary-stat critical-stat"><div class="stat-value">${criticalEBCount}</div><div class="stat-label">Critical Gaps</div></div>
        <div class="summary-stat high-stat"><div class="stat-value">${highEBCount}</div><div class="stat-label">High Gaps</div></div>
        <div class="summary-stat moderate-stat"><div class="stat-value">${modEBCount}</div><div class="stat-label">Moderate / Verify</div></div>
      </div>
      <p style="font-size:13px;"><strong>Estimated Annual Retention Risk:</strong> $${((criticalEBCount * 50 + highEBCount * 25 + modEBCount * 10) * 1000).toLocaleString()} – $${((criticalEBCount * 200 + highEBCount * 75 + modEBCount * 30) * 1000).toLocaleString()} (based on turnover cost estimates)</p>
      <p style="font-size:13px;"><strong>Estimated Enhancement Investment:</strong> $${((criticalEBCount * 15 + highEBCount * 10 + modEBCount * 5) * 12).toLocaleString()} – $${((criticalEBCount * 40 + highEBCount * 25 + modEBCount * 12) * 12).toLocaleString()} per employee annually</p>
      <p style="font-size:12px; color:var(--text-muted); margin-top:8px;"><em>Addressing these gaps demonstrates value beyond premium savings and positions your brokerage as a strategic benefits partner. They also represent compliance risk: record the recommendations and the client's accept/decline responses in the E&O Mitigation Record.</em></p>
    </div>
  </div>`;

  // --- 12. Conclusion & Next Steps ---
  html += `<div class="report-section"><h3>13. Conclusion & Next Steps</h3>`;
  html += `<div class="next-steps-grid">
    <div class="next-step-card">
      <h5>⚡ Immediate Action (30 Days)</h5>
      <ul>
        ${criticalEBCount > 0 ? '<li>Address all CRITICAL benefit gaps — initiate carrier RFPs or plan amendments</li>' : ''}
        <li>Request all current plan documents, SPDs, and carrier contracts for verification</li>
        <li>Confirm ACA compliance (1094-C/1095-C filing status)</li>
        <li>Review Form 5500 filing history and accuracy</li>
        <li>Verify COBRA administration compliance and documentation</li>
      </ul>
    </div>
    <div class="next-step-card">
      <h5>📋 High Priority (60–90 Days)</h5>
      <ul>
        <li>Address HIGH priority benefit gaps with carrier submissions</li>
        <li>Conduct employee benefits satisfaction survey</li>
        <li>Review non-discrimination testing for all qualified plans</li>
        <li>Assess voluntary benefits opportunities (zero employer cost)</li>
        <li>Develop 3-year benefits strategy roadmap for competitiveness</li>
      </ul>
    </div>
  </div>`;
  html += `<p style="font-size:12px; color:var(--text-muted);"><strong>Methodology:</strong> This employee benefits analysis was conducted using SentriPol™ AI-powered policy analysis engine, incorporating federal regulatory databases (DOL, IRS, HHS, CMS), state-specific benefits mandates, market competitiveness data, and industry benchmarks for the ${escapeHtml(d.vertical)} sector. ${d.files.length > 0 ? `${d.files.length} benefits document(s) were analyzed.` : 'No benefits documents were provided; this analysis is Preliminary.'} All recommendations should be verified with current plan documents and applicable regulations.</p>`;
  html += `</div>`;

  // The in-report E&O Compliance Record (formerly the last numbered section of
  // both reports) is retired. Architecture v2 makes the BOR analysis itself the
  // proof the review was performed, and the separate E&O Mitigation Record
  // carries the remediation and the client's accept/decline decisions — this
  // block restated the first and duplicated the second.

  return html;
}

// ===== Deep Policy Text Scan — one section, three report types ===========
// The D&O and E&O scans run on every review, whatever the line of business,
// because both read the uploaded documents rather than the coverage mix. The
// section that reports them is shared so P&C, Employee Benefits and the BOP
// review print the same thing in the same order:
//
//   1. what was scanned
//   2. D&O — detected, or explicitly not, with the near-misses it saw
//   3. E&O / Professional Liability — the same, either way
//   4. exclusions bearing on that coverage, or explicitly none
//   5. how the scan works and what the page numbers mean
//
// Each half states its own negative. A scan that ran and found nothing is a
// result; silence is indistinguishable from the scan never running, and that
// is what made this section read as broken on the P&C side.
// Advisory recommendation closing the Expected Coverages section.
//
// Every commercial client with employees carries both P&C and Employee
// Benefits exposure, so a client reading one review is told the other should
// be reviewed with the same rigor, and which document to provide. Green is
// reserved for advisory: red is a fatal flaw, yellow is verify, and the three
// never mix.
//
// One function, two mirrored copies, so the P&C and EB reports cannot drift
// apart. Laid out without flex — html2canvas drops flex containers, and the
// compact PDF is one of the three exports this has to survive.
const SP_ADVISORY_COPY = {
  pc: 'Because you have employees, you also have Employee Benefits liability '
    + 'exposure. Provide your current Employee Handbook to have your Employee '
    + 'Benefits coverage reviewed with the same rigor as your Property & '
    + 'Casualty policies.',
  eb: 'Your business also carries Property & Casualty exposure \u2014 property, '
    + 'liability, auto, and professional. Provide your current P&C policies to '
    + 'have that coverage reviewed with the same rigor as your Employee '
    + 'Benefits program.'
};

window.spAdvisoryCardHtml = function(lob, esc){
  const body = SP_ADVISORY_COPY[String(lob || '').toLowerCase()];
  if (!body) return '';
  const e = (typeof esc === 'function') ? esc
          : (typeof escapeHtml === 'function') ? escapeHtml
          : (t => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;'));
  return '<div class="advisory-card" style="background:#EDF5E6; border-left:3px solid #437A22;'
       + ' padding:12px 16px; margin:14px 0 0; border-radius:0 6px 6px 0;">'
       + '<span class="advisory-tag" style="display:inline-block; background:#437A22; color:#ffffff;'
       + ' font-size:10px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase;'
       + ' padding:3px 8px; border-radius:3px; margin-right:10px; vertical-align:2px;">Recommended</span>'
       + '<span style="color:#437A22; font-size:12.5px; line-height:1.55;">'
       + '<strong>Additional Coverage Recommended.</strong> ' + e(body) + '</span>'
       + '</div>';
};

window.spDeepScanSectionHtml = function(d, escapeHtml){
  if (!d || !d.deepScanResults) return '';
  let h = '';

    const dsr = d.deepScanResults;
    const eos = d.deepScanEOResults || null;
    const pdfCount = (d.files || []).filter(f => f && /\.pdf$/i.test(f.name || '')).length;
    h += `<p style="font-size:13px;">SentriPol\u2122 extracted and analyzed the full text of ${pdfCount} PDF policy document(s), looking for coverage that is present but not named on a declarations page \u2014 D&O and Professional Liability written into another form. Both scans are reported below, including where they found nothing.</p>`;

    // D&O Detection Summary
    if (dsr.doDetected) {
      const typeLabel = dsr.doType === 'standalone' ? 'Standalone D&O Policy'
        : dsr.doType === 'embedded_ell' ? 'Embedded in Educators Legal Liability (ELL)'
        : dsr.doType === 'embedded_mgmt' ? 'Embedded in Management Liability Package'
        : dsr.doType === 'embedded_board' ? 'Embedded via Board/Officer Language'
        : dsr.doType === 'possible' ? 'Possible (Requires Verification)'
        : 'Inferred from Policy Language';
      h += `<div style="margin:12px 0; padding:12px 16px; background:var(--info-bg); border-left:4px solid var(--info); border-radius:var(--radius-sm);">`;
      h += `<strong>\ud83d\udcca D&O / Management Liability: ${typeLabel}</strong>`;
      if (dsr.doLocation) {
        h += `<br><span style="font-size:12px;">Primary location: <strong>${escapeHtml(dsr.doLocation.fileName)}</strong>, Page ${dsr.doLocation.page}, ${escapeHtml(dsr.doLocation.section)}</span>`;
      }
      h += `</div>`;

      // v2.22 (broker rule): Indicator details table removed from Section 6B. Keep only 'Exclusions Detected in Policy Text' below.
    } else {
      // Say it. Silence here is indistinguishable from the scan not running.
      const hits = (dsr.indicators || []).length;
      h += `<div style="margin:12px 0; padding:12px 16px; background:#F9FAFB; border-left:4px solid var(--text-muted); border-radius:var(--radius-sm); font-size:13px;">`;
      h += `<strong>D&O / Management Liability: not located in the policy text.</strong> `;
      h += hits
        ? `${hits} D&O-adjacent phrase(s) were seen \u2014 ${(dsr.indicators || []).slice(0, 3).map(i => escapeHtml(i.type) + ' in ' + escapeHtml(i.fileName) + ' p.' + i.page).join('; ')}${hits > 3 ? '; …' : ''} \u2014 but not enough to evidence a coverage grant. If the insured carries D&O, it is on a policy not uploaded here.`
        : `No D&O language was found in the uploaded documents. If the insured carries D&O, it is on a policy not uploaded here.`;
      h += `</div>`;
    }

    // E&O / Professional Liability — the second scan, which this section never
    // reported. It runs on every review and on this program it is the half
    // with an actual finding.
    if (eos) {
      const eoLabel = eos.eoType === 'standalone' ? 'Standalone Professional Liability policy'
        : eos.eoType === 'embedded_ell' ? 'Embedded in Educators Legal Liability (ELL)'
        : eos.eoType === 'embedded_do' ? 'Embedded in D&O / Management Liability'
        : eos.eoType === 'endorsement' ? 'Added by endorsement'
        : eos.eoType === 'embedded_pkg' ? 'Embedded in a package policy'
        : eos.eoType === 'embedded_language' ? 'Embedded — negligent/wrongful act language'
        : eos.eoType === 'embedded_ebl' ? 'Employee Benefits Liability — negligent acts in benefit-plan administration (NOT general professional liability)'
        : eos.eoType === 'possible' ? 'Possible (requires verification)'
        : 'Inferred from policy language';
      const eoHits = (eos.indicators || []).filter(i => i.inForce !== false).length;
      if (eos.eoDetected) {
        h += `<div style="margin:12px 0; padding:12px 16px; background:var(--info-bg); border-left:4px solid var(--info); border-radius:var(--radius-sm);">`;
        h += `<strong>\ud83d\udcca E&O / Professional Liability: ${escapeHtml(eoLabel)}</strong>`;
        if (eos.eoLocation) {
          h += `<br><span style="font-size:12px;">Primary location: <strong>${escapeHtml(eos.eoLocation.fileName)}</strong>, Page ${eos.eoLocation.page}, ${escapeHtml(eos.eoLocation.section)}</span>`;
        }
        if (eoHits) h += `<br><span style="font-size:12px; color:var(--text-muted);">${eoHits} supporting phrase(s) located in the policy text.</span>`;
        h += `</div>`;
      } else {
        h += `<div style="margin:12px 0; padding:12px 16px; background:#F9FAFB; border-left:4px solid var(--text-muted); border-radius:var(--radius-sm); font-size:13px;">`;
        h += `<strong>E&O / Professional Liability: not located in the policy text.</strong> `;
        h += eoHits
          ? `${eoHits} adjacent phrase(s) were seen but did not amount to a coverage grant.`
          : `No professional liability language was found in the uploaded documents.`;
        h += `</div>`;
      }

      // PRINTED BUT NOT PURCHASED. A coverage part on a schedule with no limit
      // is not coverage, and it is the single most misleading thing in a
      // policy: the part is named, the form wording is bound in, and only the
      // limit column says it was not bought.
      if ((eos.notInForce || []).length) {
        h += `<div style="margin:12px 0; padding:12px 16px; background:#FFFBEB; border-left:4px solid #F59E0B; border-radius:var(--radius-sm); font-size:13px;">`;
        h += `<strong>\u26a0\ufe0f Listed on a schedule with no limit — not purchased:</strong>`;
        h += `<ul style="margin:6px 0 0 18px;">`;
        eos.notInForce.forEach(n => {
          h += `<li>${escapeHtml(n.line)} <span style="color:var(--text-muted);font-size:11px;">— ${escapeHtml(n.fileName)}${n.section ? ', ' + escapeHtml(n.section) : ''}, p. ${n.page}</span></li>`;
        });
        h += `</ul>`;
        h += `<div style="margin-top:6px;">The coverage part is named on the schedule and its form wording is bound into the policy, but the limit column reads N/A. Confirm with the carrier whether it can be added, and price it.</div>`;
        h += `</div>`;
      }
    }

    // v2.18: D&O-Related Exclusions — capped at top 10 by severity
    if (dsr.exclusions.length > 0) {
      const sevRank = { 'CRITICAL': 3, 'HIGH': 2, 'MODERATE': 1 };
      const sortedExcl = dsr.exclusions.slice().sort((a, b) => (sevRank[b.severity] || 0) - (sevRank[a.severity] || 0));
      const EXCL_CAP = 10;
      const shownExcl = sortedExcl.slice(0, EXCL_CAP);
      const hiddenExcl = sortedExcl.length - shownExcl.length;
      h += `<h4 style="color:var(--critical); margin-top:16px;">\u26a0\ufe0f Exclusions Detected in Policy Text That May Impact D&O Coverage${hiddenExcl > 0 ? ' (top ' + EXCL_CAP + ' by severity)' : ''}</h4>`;
      h += `<table class="report-table"><thead><tr><th>Exclusion Type</th><th>Severity</th><th>Document</th><th>Page</th><th>Section</th><th>Impact</th></tr></thead><tbody>`;
      shownExcl.forEach(excl => {
        const sevBadge = excl.severity === 'CRITICAL' ? '<span class="badge badge-critical">\u274c CRITICAL</span>'
          : excl.severity === 'HIGH' ? '<span class="badge badge-high">\u26a0\ufe0f HIGH</span>'
          : '<span class="badge badge-moderate">MODERATE</span>';
        h += `<tr><td><strong>${escapeHtml(excl.type)}</strong></td><td>${sevBadge}</td><td>${escapeHtml(excl.fileName)}</td><td style="text-align:center;">${excl.page}</td><td>${escapeHtml(excl.section)}</td><td style="font-size:11px;">${escapeHtml(excl.note)}<br><em>\u201c...${escapeHtml(excl.context.substring(0, 100))}...\u201d</em></td></tr>`;
      });
      if (hiddenExcl > 0) {
        h += `<tr><td colspan="6" style="font-size:11px; font-style:italic; color:var(--text-muted); background:#fafafa; text-align:center; padding:8px;">+ ${hiddenExcl} additional exclusion${hiddenExcl > 1 ? 's' : ''} detected at lower severity. Full exclusion detail available in Section 6 (Exclusion & Contradiction Analysis).</td></tr>`;
      }
      h += `</tbody></table>`;
    } else {
      h += `<p style="font-size:12px; color:var(--text-secondary); margin-top:12px;">No exclusion language bearing on D&O coverage was located in the scanned text.</p>`;
    }

    h += `<p style="font-size:11px; color:var(--text-muted); margin-top:10px;"><em>Deep Policy Text Scan uses PDF text extraction and pattern-matching to identify coverage language, definitions, and exclusions within uploaded policy documents. Results are based on text patterns and must be verified by the broker against the actual policy forms. Page numbers refer to the PDF page count, which may differ from printed page numbers.</em></p>`;
      return h;
};

// ===== P&C EXECUTIVE SUMMARY =====
function generatePCExecSummary(d) {
  d = window.spResolveDerived(d);
  const _spT0 = Date.now();
  const _spDone = () => {
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[perf] generate exec summary: ${Date.now() - _spT0} ms`);
    }
  };
  d.states = spCanonStates(d.states);
  const stateNames = d.states.map(spStateDisplayName).join(', ');
  const today = new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'});
  let html = '';

  // Architecture v2: this is the one comprehensive BOR analysis — it proves the
  // review was done. The producer's remediation and the client's accept/decline
  // decisions live in the separate E&O Mitigation Record.
  html += `<div class="eo-badge" style="background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 28px;">\ud83d\udee1\ufe0f</span>
      <div>
        <div style="font-weight: 700; font-size: 15px; margin-bottom: 2px;">BOR Analysis — Documented Proof of Professional Diligence</div>
        <div style="font-size: 12px; opacity: 0.9;">This analysis is the documented record that the policy program was systematically reviewed. To record what was remediated and which recommended coverages the client accepted or declined, generate the E&O Mitigation Record from the button at the top of this report.</div>
      </div>
    </div>`;

  html += `<div class="report-section">
    <h3>Executive Board Summary — Property & Casualty</h3>
    <p><strong>${escapeHtml(d.policyholderName)}</strong> &nbsp;|&nbsp; ${escapeHtml(d.vertical)} &nbsp;|&nbsp; ${stateNames} &nbsp;|&nbsp; ${today}</p>
    <p style="font-size:12px;">This executive summary consolidates findings from all sections of the SentriPol\u2122 full policy review. Each finding references the source section of the full report for detailed analysis.</p>
  </div>`;

  // === SECTION SUMMARY: What Was Reviewed ===
  html += `<div class="report-section"><h3>Scope of Review</h3>`;
  html += `<table class="report-table"><thead><tr><th>Report Section</th><th>Status</th><th>Key Finding</th></tr></thead><tbody>`;
  // Program Completeness
  const execVlCheck = d.vertical.toLowerCase();
  const execIsEdu = execVlCheck.includes('school') || execVlCheck.includes('education') || execVlCheck.includes('charter');
  const execIsRE = execVlCheck.includes('real estate') || execVlCheck.includes('property management');
  const execIsConst = execVlCheck.includes('construct') || execVlCheck.includes('contractor');
  let execExpected = 9;
  if (execIsEdu) execExpected = 13;
  else if (execIsRE) execExpected = 13;
  else if (execIsConst) execExpected = 11;
  const execFound = d.detectedCoverages.length;
  const execPct = Math.min(100, Math.round((execFound / execExpected) * 100));
  const execPctBadge = execPct >= 80 ? '<span style="color:green;">\u2705 ' + execPct + '%</span>' : execPct >= 60 ? '<span style="color:#c77b00;">\u26a0\ufe0f ' + execPct + '%</span>' : '<span style="color:var(--critical);">\u274c ' + execPct + '%</span>';
  html += `<tr><td>Sec 2B: Program Completeness</td><td>${execPctBadge}</td><td>${execFound} of ${execExpected} expected coverage lines detected</td></tr>`;
  // State risks
  const execStateRisks = d.states.filter(a => STATE_PROFILES[a] && (STATE_PROFILES[a].severity === 'EXTREME' || STATE_PROFILES[a].severity === 'HIGH')).length;
  html += `<tr><td>Sec 3: State Risk Analysis</td><td>${execStateRisks > 0 ? '<span style="color:var(--critical);">\u26a0\ufe0f ' + execStateRisks + ' HIGH/EXTREME</span>' : '\u2705 Low/Moderate'}</td><td>${d.states.map(a => STATE_PROFILES[a]?.name || a).join(', ')}</td></tr>`;
  // Benchmarking gaps
  const execExclAlerts = getExclusionAlerts(d.detectedCoverages, d.vertical, d.endorsements);
  const execFatalFlaws = execExclAlerts.filter(ea => ea.impact && ea.impact.includes('FATAL') || ea.impact && ea.impact.includes('CRITICAL'));
  html += `<tr><td>Sec 5: Industry Benchmarking</td><td>${d.files.length > 0 ? '\u2705 Analyzed' : '\u26a0\ufe0f No policies'}</td><td>Coverage compared against ${escapeHtml(d.vertical)} industry standards</td></tr>`;
  // Exclusions
  html += `<tr><td>Sec 6: Exclusion Analysis</td><td>${execFatalFlaws.length > 0 ? '<span style="color:var(--critical);">\u274c ' + execFatalFlaws.length + ' FATAL FLAW(S)</span>' : execExclAlerts.length > 0 ? '<span style="color:#c77b00;">\u26a0\ufe0f ' + execExclAlerts.length + ' alert(s)</span>' : '\u2705 No alerts'}</td><td>${execFatalFlaws.length > 0 ? 'Exclusions detected that may void coverage' : 'Review for potential conflicts'}</td></tr>`;
  // Website risks
  const execWebRisks = d.websiteRisks ? d.websiteRisks.filter(r => r.severity === 'CRITICAL' || r.severity === 'HIGH').length : 0;
  if (d.websiteUrl) {
    html += `<tr><td>Sec 6C: Website Risk Analysis</td><td>${execWebRisks > 0 ? '<span style="color:var(--critical);">\u26a0\ufe0f ' + execWebRisks + ' risk(s)</span>' : '\u2705 Low risk'}</td><td>Operational activities reviewed against policy exclusions</td></tr>`;
  }
  html += `</tbody></table></div>`;

  // === CRITICAL & HIGH-PRIORITY FINDINGS ===
  html += `<div class="report-section"><h3>Critical & High-Priority Findings</h3>`;

  // Coverage gaps
  const isEducation = d.vertical.toLowerCase().includes('school') || d.vertical.toLowerCase().includes('education');
  const hasCoverage = (type) => d.detectedCoverages.includes(type);

  if (!hasCoverage("Cyber Liability")) {
    html += `<div class="exec-finding">
      <div class="finding-title">❌ CRITICAL: No Cyber Liability Coverage Detected</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: "Cyber Liability — industry standard $1M–$3M; current status: CRITICAL GAP"]</div>
      <div class="finding-exposure">Estimated Exposure: $500,000 – $5,000,000+ per breach event</div>
    </div>`;
  }

  if (isEducation && !hasCoverage("Sexual Abuse/Molestation")) {
    html += `<div class="exec-finding">
      <div class="finding-title">❌ CRITICAL: No Sexual Abuse / Molestation Coverage Detected</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: "Sexual Abuse/Molestation — best practice $2M/$5M standalone; current status: CRITICAL GAP"]</div>
      <div class="finding-exposure">Estimated Exposure: $1,000,000 – $10,000,000+ per occurrence (defense + indemnity)</div>
    </div>`;
  }

  if (isEducation && !hasCoverage("Active Shooter")) {
    html += `<div class="exec-finding">
      <div class="finding-title">❌ CRITICAL: No Active Shooter / Violent Event Coverage Detected</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: "Active Shooter — best practice $5M+ with pre-event training; current status: CRITICAL GAP"]</div>
      <div class="finding-exposure">Estimated Exposure: $2,000,000 – $25,000,000+ including crisis management, victim counseling, legal defense</div>
    </div>`;
  }

  if (!hasCoverage("Umbrella/Excess")) {
    html += `<div class="exec-finding high-finding">
      <div class="finding-title">⚠️ HIGH: No Umbrella / Excess Liability Coverage Detected</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: "Umbrella/Excess — industry standard $5M–$10M; current status: HIGH GAP"]</div>
      <div class="finding-exposure">Estimated Exposure: $5,000,000 – $25,000,000+ (catastrophic claim scenario)</div>
    </div>`;
  }

  if (!hasCoverage("EPLI")) {
    html += `<div class="exec-finding high-finding">
      <div class="finding-title">⚠️ HIGH: No EPLI Coverage Detected</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: "EPLI — industry standard $1M–$2M; current status: HIGH GAP"]</div>
      <div class="finding-exposure">Estimated Exposure: $250,000 – $2,000,000 per claim (defense + settlement)</div>
    </div>`;
  }

  // D&O Detection — Enhanced with Deep Policy Text Scan
  const ds = d.deepScanResults;
  // E&O / Professional Liability Detection Finding
  const hasEOCoverage = hasCoverage('Professional Liability') || 
    d.detectedCoverages.some(c => c.startsWith('Professional Liability'));
  const ds_eo = d.deepScanEOResults;
  if (!hasEOCoverage && !ds_eo?.eoDetected) {
    html += `
      <div class="finding-card finding-critical">
        <div class="finding-title">\u274c CRITICAL: No Professional Liability / E&O Coverage Detected</div>
        <div class="finding-citation">[Industry Benchmarking, Section 5: "Professional Liability / E&O — industry standard $1M–$3M; current status: CRITICAL GAP"]</div>
        <div class="finding-exposure">Without Errors & Omissions / Professional Liability coverage, ${escapeHtml(d.policyholderName)} faces uninsured exposure for claims alleging negligent acts, errors, or omissions in the delivery of professional services. In the education sector, this includes curriculum design failures, credentialing errors, accreditation compliance failures, and advisory mistakes. Average E&O claims range from $50,000 to $2,000,000+.</div>
        <div class="finding-recommendation"><strong>Immediate Action:</strong> Obtain standalone Professional Liability / E&O coverage at minimum $1M per claim / $3M aggregate. For education entities, verify whether Educators Legal Liability (ELL) policy contains embedded professional liability — if so, document the scope. If no ELL exists, standalone educators E&O is recommended. Consider carriers: CHUBB ForeFront, Travelers, CNA, GuideOne (education), United Educators.</div>
      </div>`;
  } else if (ds_eo?.eoDetected && ds_eo.eoType !== 'standalone') {
    const eoTypeLabel = ds_eo.eoType === 'embedded_ell' ? 'Embedded in Educators Legal Liability (ELL)'
      : ds_eo.eoType === 'embedded_do' ? 'Embedded in D&O / Management Liability Package'
      : ds_eo.eoType === 'endorsement' ? 'Via Endorsement on Another Policy'
      : ds_eo.eoType === 'embedded_pkg' ? 'Embedded in Package Policy'
      : ds_eo.eoType === 'embedded_language' ? 'Detected via Negligent/Wrongful Act Language'
      : ds_eo.eoType === 'possible' ? 'Possible — Requires Verification'
      : 'Inferred from Policy Language';
    html += `
      <div class="finding-card finding-moderate">
        <div class="finding-title">\u26a0\ufe0f E&O / Professional Liability Coverage: ${eoTypeLabel}</div>
        <div class="finding-citation">[Policy Text Deep Scan, E&O Detection: "${ds_eo.eoLocation ? ds_eo.eoLocation.fileName + ', Page ' + ds_eo.eoLocation.page : 'Multiple indicators found'}"]</div>
        <div class="finding-exposure">Professional Liability / E&O coverage was detected but appears to be embedded within another policy rather than standalone. Embedded E&O coverage may have reduced limits, narrower definitions of "professional services," or exclusions that do not apply to standalone E&O policies. ${ds_eo.exclusions.length > 0 ? 'WARNING: ' + ds_eo.exclusions.length + ' potential exclusion conflict(s) were detected that could limit E&O protection.' : ''}</div>
        <div class="finding-recommendation"><strong>Action Required:</strong> (1) Verify E&O limits are adequate ($1M–$3M minimum recommended), (2) Confirm the definition of "professional services" covers all services provided by ${escapeHtml(d.policyholderName)}, (3) Check whether embedded E&O shares limits with the host policy (D&O, ELL) or has dedicated sublimits, (4) Review any exclusions that could void professional liability protection. If coverage is inadequate, obtain standalone E&O.</div>
      </div>`;
  } else if (hasEOCoverage || ds_eo?.eoDetected) {
    html += `
      <div class="finding-card finding-low">
        <div class="finding-title">\u2705 Professional Liability / E&O Coverage Detected</div>
        <div class="finding-citation">[Coverage Detection: Professional Liability identified via ${ds_eo?.eoDetected ? 'policy text deep scan' : 'file name pattern matching'}]</div>
        <div class="finding-exposure">Professional Liability / E&O coverage has been identified. Verify: (1) Limits are adequate for the ${escapeHtml(d.vertical)} sector ($1M–$3M minimum recommended), (2) Claims-made vs occurrence trigger is understood, (3) Retroactive date is acceptable, (4) Definition of "professional services" is broad enough, (5) No sublimits or exclusions effectively reduce protection.${ds_eo?.exclusions.length > 0 ? ' NOTE: ' + ds_eo.exclusions.length + ' potential exclusion(s) detected — review carefully.' : ''}</div>
      </div>`;
  }


  const hasDOCoverage = hasCoverage('D&O') || hasCoverage('D&O (Embedded in ELL/EPLI)') ||
    hasCoverage('D&O (Embedded in Management Liability)') || hasCoverage('D&O (Embedded \u2014 Board/Officer Language)') ||
    hasCoverage('D&O (Possible \u2014 Verify)') || hasCoverage('D&O (Inferred from Policy Language)');

  if (hasDOCoverage && ds && ds.doDetected) {
    // We have actual text scan results — show detailed findings with exact citations
    const loc = ds.doLocation;
    const typeLabel = ds.doType === 'standalone' ? 'Standalone D&O Policy'
      : ds.doType === 'embedded_ell' ? 'Embedded in Educators Legal Liability (ELL) / EPLI'
      : ds.doType === 'embedded_mgmt' ? 'Embedded in Management Liability Package'
      : ds.doType === 'embedded_board' ? 'Embedded \u2014 Board/Officer Language Found'
      : ds.doType === 'possible' ? 'Possible D&O Coverage (Requires Verification)'
      : 'Inferred from Policy Language';

    html += `<div class="exec-finding high-finding">
      <div class="finding-title">\u26a0\ufe0f IMPORTANT: D&O Coverage Detected \u2014 ${escapeHtml(typeLabel)}</div>
      <div class="finding-citation">[Deep Policy Text Scan, ${escapeHtml(loc.fileName)}, Page ${loc.page}, ${escapeHtml(loc.section)}: \u201c...${escapeHtml(loc.context)}...\u201d]</div>
      <div class="finding-exposure">`;

    // Show all D&O indicator matches with exact locations
    if (ds.indicators.length > 0) {
      html += `<strong>D&O Language Found (${ds.indicators.length} indicator${ds.indicators.length > 1 ? 's' : ''}):</strong><br>`;
      // Deduplicate by type, keep highest-weight
      const seen = new Set();
      ds.indicators.sort((a, b) => b.weight - a.weight).forEach(ind => {
        if (!seen.has(ind.type)) {
          seen.add(ind.type);
          html += `\u2022 <strong>${escapeHtml(ind.type)}</strong> \u2014 ${escapeHtml(ind.fileName)}, Page ${ind.page}, ${escapeHtml(ind.section)}<br>`;
        }
      });
    }

    // Show D&O-related exclusions found in policy text
    if (ds.exclusions.length > 0) {
      html += `<br><strong style="color:var(--critical);">\u26a0\ufe0f Exclusions That May Impact D&O Coverage (${ds.exclusions.length} found):</strong><br>`;
      ds.exclusions.forEach(excl => {
        const sevColor = excl.severity === 'CRITICAL' ? 'var(--critical)' : excl.severity === 'HIGH' ? '#c77b00' : 'var(--text-muted)';
        html += `\u2022 <strong style="color:${sevColor};">[${excl.severity}] ${escapeHtml(excl.type)}</strong> \u2014 ${escapeHtml(excl.fileName)}, Page ${excl.page}, ${escapeHtml(excl.section)}<br>`;
        html += `<span style="font-size:11px; color:var(--text-muted); margin-left:16px;">${escapeHtml(excl.note)}</span><br>`;
        html += `<span style="font-size:11px; font-style:italic; color:var(--text-muted); margin-left:16px;">\u201c...${escapeHtml(excl.context)}...\u201d</span><br>`;
      });
    }

    html += `<br><strong>Broker Action Required:</strong> Verify (1) D&O limits are adequate ($1M\u2013$3M minimum), (2) Entity coverage is included, (3) Side A coverage exists for non-indemnifiable claims, (4) Exclusions above do not void critical D&O protections. ${ds.doType !== 'standalone' ? 'Consider whether standalone D&O is needed for broader management liability protection.' : ''}`;
    html += `</div></div>`;
  } else if (hasDOCoverage) {
    // D&O detected via file name / context only (no text scan results)
    html += `<div class="exec-finding high-finding">
      <div class="finding-title">\u26a0\ufe0f IMPORTANT: D&O Coverage Detected \u2014 Verify Coverage Scope</div>
      <div class="finding-citation">[Industry Benchmarking, Section 5: \u201cD&O / Management Liability \u2014 coverage detected via policy file analysis. If no standalone D&O policy exists, D&O may be embedded in ELL/EPLI (Insuring Agreement A) covering directors, officers, trustees, regents, and board members under \u2018Educators Wrongful Act\u2019 or similar definitions.\u201d]</div>
      <div class="finding-exposure">D&O coverage was detected but could not be fully text-scanned (non-PDF format or extraction unavailable). Verify: (1) Limits are adequate ($1M\u2013$3M minimum recommended), (2) Entity coverage is included, (3) Side A coverage exists for non-indemnifiable claims, (4) No exclusions effectively void D&O protection (e.g., criminal acts, regulatory proceedings). If D&O is embedded in EPLI/ELL rather than standalone, confirm the scope covers all board members and officers.</div>
    </div>`;
  }

  // Missing Core Coverage (Property, GL, WC)
  const execDl = d.declLimits || (typeof extractDeclarationsLimits === 'function' ? extractDeclarationsLimits(d.pdfTexts || []) : {});
  const execPropDetected = hasCoverage('Commercial Property') || hasCoverage('Property');
  const execPropHasLimits = execDl['Commercial Property'] && Object.keys(execDl['Commercial Property']).length > 0;
  if (!execPropDetected || !execPropHasLimits) {
    html += `<div class="exec-finding">
      <div class="finding-title">\u274c CRITICAL: Commercial Property Coverage ${execPropDetected ? 'Referenced But No Limits Found' : 'Not Detected'}</div>
      <div class="finding-citation">[Missing Core Coverage Alert, Section 5B; Industry Benchmarking, Section 5]</div>
      <div class="finding-exposure">${execPropDetected ? 'A reference to a Property Coverage Part was found in the uploaded policy documents, but no actual property declarations, building values, or contents limits were detected. The Property Coverage Part may have been listed but never activated with actual coverage.' : 'No commercial property coverage was detected in any uploaded policy. For any entity with physical locations, this represents the most significant coverage gap possible \u2014 the organization\u2019s buildings and contents are uninsured.'} <strong>Immediate verification required.</strong></div>
    </div>`;
  }
  if (!hasCoverage('Workers Compensation')) {
    html += `<div class="exec-finding">
      <div class="finding-title">\u274c CRITICAL: No Workers Compensation Coverage Detected</div>
      <div class="finding-citation">[Missing Core Coverage Alert, Section 5B]</div>
      <div class="finding-exposure">Workers compensation is statutorily required in virtually every state for employers. Absence exposes the organization to personal liability for workplace injuries and regulatory penalties.</div>
    </div>`;
  }
  if (!hasCoverage('General Liability')) {
    html += `<div class="exec-finding">
      <div class="finding-title">\u274c CRITICAL: No General Liability Coverage Detected</div>
      <div class="finding-citation">[Missing Core Coverage Alert, Section 5B]</div>
      <div class="finding-exposure">No commercial general liability coverage was detected. This is the most foundational coverage for any commercial operation.</div>
    </div>`;
  }

  // Exclusion Fatal Flaws
  if (execFatalFlaws.length > 0) {
    execFatalFlaws.forEach(ff => {
      html += `<div class="exec-finding">
        <div class="finding-title">\u274c FATAL FLAW: ${escapeHtml(ff.coverage)} vs. ${escapeHtml(ff.exclusion)}</div>
        <div class="finding-citation">[Exclusion & Contradiction Analysis, Section 6]</div>
        <div class="finding-exposure">${escapeHtml(ff.impact.substring(0, 300))}${ff.impact.length > 300 ? '...' : ''}</div>
      </div>`;
    });
  }

  // State-specific risks
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (!sp || (sp.severity !== 'EXTREME' && sp.severity !== 'HIGH')) return;
    const findingClass = sp.severity === 'EXTREME' ? 'exec-finding' : 'exec-finding high-finding';
    html += `<div class="${findingClass}">
      <div class="finding-title">${sp.severity === 'EXTREME' ? '\u274c CRITICAL' : '\u26a0\ufe0f HIGH'}: ${sp.name} Catastrophe Risk \u2014 ${sp.primaryCatastrophe}</div>
      <div class="finding-citation">[State Risk Analysis, Section 4: "${sp.name} \u2014 Severity: ${sp.severity}; Trust: ${sp.stateTrust}"]</div>
      <div class="finding-exposure">${sp.trustRisk}</div>
    </div>`;
  });

  // Multi-Policy Cross-Reference issues
  if (d.files.length > 1) {
    const execUmbrella = hasCoverage('Umbrella/Excess');
    const execGL = hasCoverage('General Liability');
    const execAuto = hasCoverage('Commercial Auto');
    if (execUmbrella && !execAuto) {
      html += `<div class="exec-finding high-finding">
        <div class="finding-title">\u26a0\ufe0f HIGH: Umbrella Detected But No Commercial Auto Found</div>
        <div class="finding-citation">[Multi-Policy Cross-Reference, Section 11B]</div>
        <div class="finding-exposure">Umbrella policies typically require scheduled underlying Auto coverage. Verify the Umbrella\u2019s schedule of underlying insurance.</div>
      </div>`;
    }
    if (execGL && !execUmbrella) {
      html += `<div class="exec-finding high-finding">
        <div class="finding-title">\u26a0\ufe0f HIGH: General Liability Found But No Umbrella/Excess Detected</div>
        <div class="finding-citation">[Multi-Policy Cross-Reference, Section 11B]</div>
        <div class="finding-exposure">If the GL aggregate is exhausted, no excess coverage exists to respond. Verify whether Umbrella/Excess exists on a policy not uploaded.</div>
      </div>`;
    }
  }

  html += `</div>`;

  // Risk distribution
  const hasCov = d.detectedCoverages;
  const critC = (isEducation ? 3 : 1) - (hasCov.includes("Cyber Liability") ? 1 : 0) - (isEducation && hasCov.includes("Sexual Abuse/Molestation") ? 1 : 0) - (isEducation && hasCov.includes("Active Shooter") ? 1 : 0);
  const highC = 2 - (hasCov.includes("Umbrella/Excess") ? 1 : 0) - (hasCov.includes("EPLI") ? 1 : 0);
  const stateRisks = d.states.filter(a => STATE_PROFILES[a] && (STATE_PROFILES[a].severity === 'EXTREME' || STATE_PROFILES[a].severity === 'HIGH')).length;

  html += `<div class="report-section"><h3>Risk Exposure Distribution</h3>
    <div class="summary-box">
      <div class="summary-stats">
        <div class="summary-stat critical-stat"><div class="stat-value">${Math.max(0, critC) + d.states.filter(a => STATE_PROFILES[a]?.severity === 'EXTREME').length}</div><div class="stat-label">Critical Risks</div></div>
        <div class="summary-stat high-stat"><div class="stat-value">${Math.max(0, highC) + d.states.filter(a => STATE_PROFILES[a]?.severity === 'HIGH').length}</div><div class="stat-label">High Risks</div></div>
        <div class="summary-stat moderate-stat"><div class="stat-value">${d.states.filter(a => STATE_PROFILES[a]?.severity === 'MODERATE').length + 3}</div><div class="stat-label">Items to Verify</div></div>
      </div>
    </div>
  </div>`;

  // Website / Operational Risk Findings (Executive Summary)
  if (d.websiteRisks && d.websiteRisks.length > 0) {
    const critWebRisks = d.websiteRisks.filter(r => r.severity === 'CRITICAL');
    const highWebRisks = d.websiteRisks.filter(r => r.severity === 'HIGH');
    if (critWebRisks.length > 0 || highWebRisks.length > 0) {
      html += `<div class="report-section"><h3>Website & Operational Risk Findings</h3>`;
      html += `<p style="font-size:12px; color:var(--text-muted);">SentriPol\u2122 website analysis identified <strong>${d.websiteRisks.length}</strong> operational risk(s) from ${escapeHtml(d.policyholderName)}'s website and organizational profile that may trigger policy exclusions.</p>`;
      critWebRisks.forEach(r => {
        html += `<div class="exec-finding">
          <div class="finding-title">\u274c CRITICAL: ${escapeHtml(r.risk)} \u2014 Potential Exclusion</div>
          <div class="finding-citation">[Website & Org Profile Analysis, Sec. 7C: \u201c${escapeHtml(r.exclusionType)} \u2014 ${escapeHtml(r.evidence)}\u201d]</div>
          <div class="finding-exposure">Cross-reference policy exclusions for: <strong>${escapeHtml(r.exclusionType)}</strong>. This operational activity was confirmed via website analysis and may be excluded under standard policy language.</div>
        </div>`;
      });
      highWebRisks.forEach(r => {
        html += `<div class="exec-finding high-finding">
          <div class="finding-title">\u26a0\ufe0f HIGH: ${escapeHtml(r.risk)}</div>
          <div class="finding-citation">[Website & Org Profile Analysis, Sec. 7C: \u201c${escapeHtml(r.exclusionType)} \u2014 ${escapeHtml(r.evidence)}\u201d]</div>
          <div class="finding-exposure">Verify: <strong>${escapeHtml(r.exclusionType)}</strong></div>
        </div>`;
      });
      html += `</div>`;
    }
  }

  // Board action items
  html += `<div class="report-section"><h3>Board Action Items</h3>`;
  let actionNum = 1;
  const actions = [];
  if (!execPropDetected || !execPropHasLimits) actions.push("URGENT: Verify whether Commercial Property coverage exists. If no property policy is in force, direct broker to immediately obtain quotations for building, contents, and business personal property coverage at replacement cost.");
  if (!hasCov.includes("General Liability")) actions.push("URGENT: Obtain General Liability coverage immediately — this is the most foundational commercial coverage.");
  if (!hasCov.includes("Workers Compensation")) actions.push("URGENT: Verify Workers Compensation coverage status — this is statutorily required in virtually every state.");
  if (execFatalFlaws.length > 0) actions.push(`URGENT: ${execFatalFlaws.length} exclusion fatal flaw(s) detected that may void existing coverage. See Section 6 of the full review for details and required broker action.`);
  if (!hasCov.includes("Cyber Liability")) actions.push("Direct broker to immediately obtain Cyber Liability quotations at minimum $3M limit with full first-party and third-party coverage.");
  if (isEducation && !hasCov.includes("Sexual Abuse/Molestation")) actions.push("Direct broker to bind standalone Sexual Abuse & Molestation coverage at $2M/$5M limits with duty-to-defend provision.");
  if (isEducation && !hasCov.includes("Active Shooter")) actions.push("Direct broker to obtain Active Shooter/Violent Event coverage quotations including pre-event training, crisis management, and victim counseling.");
  if (!hasCov.includes("Umbrella/Excess")) actions.push("Direct broker to market Umbrella/Excess Liability coverage at $10M minimum with underlying alignment confirmed.");
  if (!hasCov.includes("EPLI")) actions.push("Direct broker to obtain EPLI quotations with third-party coverage included, minimum $2M limit.");
  d.states.forEach(abbr => {
    const sp = STATE_PROFILES[abbr];
    if (sp && sp.severity === 'EXTREME') actions.push(`Review ${sp.name} catastrophe exposure: ${sp.recommendation}`);
  });
  // Add website risk-based actions
  if (d.websiteRisks && d.websiteRisks.length > 0) {
    const critWR = d.websiteRisks.filter(r => r.severity === 'CRITICAL');
    if (critWR.length > 0) {
      actions.push(`Review ${critWR.length} CRITICAL operational risk(s) identified via website analysis against policy exclusions — specifically: ${critWR.map(r => r.exclusionType).join('; ')}.`);
    }
  }
  actions.push("Request complete schedule of all coverage declarations pages, endorsements, and policy forms for independent verification.");
  actions.push("Schedule quarterly risk management review to track gap remediation progress.");

  actions.forEach(a => {
    html += `<div class="board-action"><div class="action-num">${actionNum++}</div><div class="action-text">${a}</div></div>`;
  });
  html += `</div>`;

  _spDone();
  return html;
}

// ===== EB EXECUTIVE SUMMARY =====
function generateEBExecSummary(d) {
  d = window.spResolveDerived(d);
  d.states = spCanonStates(d.states);
  const stateNames = d.states.map(spStateDisplayName).join(', ');
  const today = new Date().toLocaleDateString('en-US', {year:'numeric',month:'long',day:'numeric'});
  let html = '';

  // Architecture v2: this is the one comprehensive BOR analysis — it proves the
  // review was done. The producer's remediation and the client's accept/decline
  // decisions live in the separate E&O Mitigation Record.
  html += `<div class="eo-badge" style="background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 28px;">\ud83d\udee1\ufe0f</span>
      <div>
        <div style="font-weight: 700; font-size: 15px; margin-bottom: 2px;">BOR Analysis — Documented Proof of Professional Diligence</div>
        <div style="font-size: 12px; opacity: 0.9;">This analysis is the documented record that the policy program was systematically reviewed. To record what was remediated and which recommended coverages the client accepted or declined, generate the E&O Mitigation Record from the button at the top of this report.</div>
      </div>
    </div>`;

  html += `<div class="report-section">
    <h3>Executive Board Summary — Employee Benefits</h3>
    <p><strong>${escapeHtml(d.policyholderName)}</strong> &nbsp;|&nbsp; ${escapeHtml(d.vertical)} &nbsp;|&nbsp; ${stateNames} &nbsp;|&nbsp; ${today}</p>
    <p style="font-size:12px; color:var(--critical); font-weight:600;">⚠️ This summary contains ONLY identified shortcomings, gaps, and risk exposures. Positive findings are omitted per executive briefing protocol.</p>
  </div>`;

  // Extract actual benefit data to determine real gaps (mirrors Full Review Section 5)
  const execEBData = extractEBBenefitsData(d.pdfTexts || []);
  const execRenewalPlans = extractRenewalPackagePlans(d.pdfTexts || []);
  const execHasEB = (type) => !!execEBData[type] && Object.keys(execEBData[type]).length > 0;

  // Build findings dynamically based on extracted data (not hardcoded)
  const ebFindings = [];
  const ebFindingDetails = {
    'Long-Term Disability': { level: 'CRITICAL', title: 'Long-Term Disability Coverage Gap', citation: 'LTD \u2014 market median 60% of salary to $5K/month', exposure: 'Absence of LTD coverage exposes employees to catastrophic income loss and exposes the organization to potential ERISA and retention liability. Estimated cost to implement: $12\u2013$25/employee/month.', noFileStatus: 'CRITICAL GAP' },
    'Dental': { level: 'HIGH', title: 'Dental Plan Below Market \u2014 No Orthodontia Detected', citation: 'Dental \u2014 market median $1,500 annual max with orthodontia', exposure: 'Orthodontia is a top-3 employee-requested benefit. Absence puts organization at competitive disadvantage for talent with families. Est. cost: $8\u2013$15/employee/month.', noFileStatus: 'HIGH GAP' },
    'Life / AD&D': { level: 'HIGH', title: 'Life/AD&D Coverage Below Market Standard', citation: 'Life/AD&D \u2014 market median 1x salary, $50K minimum', exposure: 'Below-market life insurance signals inadequate employee welfare commitment. Industry standard is 1\u20132x salary basic life with voluntary options to 5x.', noFileStatus: 'HIGH GAP' },
    'Short-Term Disability': { level: 'HIGH', title: 'Short-Term Disability Gap', citation: 'STD \u2014 market median 60% of salary, 13-week period', exposure: `Lack of STD coverage creates employee hardship during illness/injury and may violate state mandates in certain jurisdictions. ${d.states.some(a => ['CA','NY','NJ','HI','RI'].includes(a)) ? 'NOTE: One or more operating states MANDATE disability coverage.' : ''}`, noFileStatus: 'HIGH GAP' },
    '401(k) / Retirement': { level: 'HIGH', title: '401(k) / Retirement Plan Below Competitive Threshold', citation: '401(k) \u2014 market median 3% match', exposure: `Retirement benefits are the #1 driver of long-term employee retention. Below-market matching drives elevated turnover in the ${escapeHtml(d.vertical)} sector.`, noFileStatus: 'HIGH GAP' }
  };

  for (const [type, detail] of Object.entries(ebFindingDetails)) {
    // Only show finding if benefit data was NOT extracted
    if (!execHasEB(type)) {
      // For no-files case, show all; for files-uploaded, show only if still a gap
      if (d.files.length === 0 || detail.level === 'CRITICAL') {
        ebFindings.push(detail);
      }
    }
  }

  html += `<div class="report-section"><h3>Critical & High-Priority Findings</h3>`;
  if (ebFindings.length > 0) {
    ebFindings.forEach(f => {
      const isCritical = f.level === 'CRITICAL';
      html += `<div class="exec-finding${isCritical ? '' : ' high-finding'}">
        <div class="finding-title">${isCritical ? '\u274c CRITICAL' : '\u26a0\ufe0f HIGH'}: ${f.title}</div>
        <div class="finding-citation">[Benefits Competitiveness Analysis, Section 5: "${f.citation}; current status: ${f.noFileStatus}"]</div>
        <div class="finding-exposure">${f.exposure}</div>
      </div>`;
    });
  } else {
    html += `<div class="exec-finding" style="border-left-color: var(--meets);">
      <div class="finding-title">\u2705 Benefits Coverage Confirmed</div>
      <div class="finding-exposure">All core benefit types (Medical, Dental, Vision, Life/AD&D, STD, LTD, Retirement, HSA/FSA) have been identified in uploaded plan documents. See Section 5 of the Full Review for extracted coverage details and comparison against industry standards.</div>
    </div>`;
  }
  html += `</div>`;

  // Retention risk — dynamic based on actual gaps
  const critCount = ebFindings.filter(f => f.level === 'CRITICAL').length;
  const highCount = ebFindings.filter(f => f.level === 'HIGH').length;
  const totalGaps = critCount + highCount;
  const retentionLevel = totalGaps >= 4 ? 'HIGH' : totalGaps >= 2 ? 'MODERATE' : totalGaps >= 1 ? 'LOW-MODERATE' : 'LOW';

  html += `<div class="report-section"><h3>Employee Retention Risk Impact</h3>
    <div class="retention-box">
      <h4>${totalGaps >= 3 ? '\u26a0\ufe0f' : totalGaps >= 1 ? '\u2139\ufe0f' : '\u2705'} Retention Risk: ${retentionLevel}</h4>
      <p style="font-size:13px; color:var(--text-primary);">${totalGaps >= 3 ? `The combined benefits gaps place ${escapeHtml(d.policyholderName)} in the <strong>bottom quartile</strong> of benefits competitiveness for the ${escapeHtml(d.vertical)} sector. Organizations with benefits packages this far below market typically experience <strong>18\u201330% higher voluntary turnover</strong>.` : totalGaps >= 1 ? `${escapeHtml(d.policyholderName)} has ${totalGaps} identified benefit gap(s) that should be addressed to maintain competitive positioning in the ${escapeHtml(d.vertical)} sector.` : `Based on the uploaded benefit documents, ${escapeHtml(d.policyholderName)} maintains a competitive benefits package. Continue monitoring against industry benchmarks to maintain positioning.`}</p>
    </div>
  </div>`;

  // Risk distribution — dynamic counts
  const verifyCount = 8 - Object.keys(execEBData).length - critCount - highCount;
  html += `<div class="report-section"><h3>Risk Exposure Distribution</h3>
    <div class="summary-box">
      <div class="summary-stats">
        <div class="summary-stat critical-stat"><div class="stat-value">${critCount}</div><div class="stat-label">Critical Gaps</div></div>
        <div class="summary-stat high-stat"><div class="stat-value">${highCount}</div><div class="stat-label">High Gaps</div></div>
        <div class="summary-stat meets-stat"><div class="stat-value">${Object.keys(execEBData).length}</div><div class="stat-label">Confirmed</div></div>
        <div class="summary-stat moderate-stat"><div class="stat-value">${Math.max(0, verifyCount)}</div><div class="stat-label">To Verify</div></div>
      </div>
    </div>
  </div>`;

  // Board action items — dynamic based on gaps
  html += `<div class="report-section"><h3>Board Action Items</h3>`;
  const ebActions = [];
  // The same coverage facts the full review reads, so the two reports cannot
  // disagree: the action list asked to "implement" STD a guide offers, and to
  // raise a 401(k) match for a school whose staff are in TRS.
  const execLines = ebDetectCoverageLines(d.pdfTexts || []);
  const execTRS = ebTeacherRetirementFor(d);
  if (!execHasEB('Long-Term Disability') && !execLines.ltd.present) ebActions.push("Direct broker to immediately obtain LTD quotations at 66.67% of salary with own-occupation definition and $10K+ monthly maximum.");
  if (!execHasEB('Dental')) ebActions.push("Direct broker to enhance dental plan to include orthodontia rider and increase annual maximum to $2,000+.");
  if (!execHasEB('Life / AD&D')) ebActions.push("Direct broker to increase basic life/AD&D to minimum 1x salary and add voluntary life option to 5x salary.");
  if (execLines.std.present && !execLines.std.employerPaid) ebActions.push("Direct broker to quote employer-paid core STD at the current benefit level, keeping the voluntary plans as buy-ups.");
  else if (!execHasEB('Short-Term Disability') && !execLines.std.present) ebActions.push("Direct broker to implement or enhance STD plan at 60%+ of salary with 7-day elimination period.");
  if (!execHasEB('401(k) / Retirement')) {
    if (!execLines.retirement.present && execTRS) ebActions.push("Confirm " + execTRS.short + " participation and whether a supplemental 403(b) or 457(b) is offered; the benefit guide does not describe retirement.");
    else if (execLines.retirement.present && !execLines.retirement.matchStated) ebActions.push("Evaluate introducing a 3\u20134% employer match on the existing retirement plan, verified against the plan document.");
    else if (!execLines.retirement.present) ebActions.push("Request the retirement plan documents; no retirement plan is described in the uploaded benefit guide.");
  }
  ebActions.push("Request complete plan documents, SPDs, carrier contracts, and Form 5500 filings for independent compliance review.");
  if (ebFindings.length > 0) ebActions.push("Conduct employee benefits satisfaction survey within 60 days to quantify retention risk.");
  ebActions.push("Schedule quarterly benefits strategy review to track enhancement implementation progress.");
  let aNum = 1;
  ebActions.forEach(a => {
    html += `<div class="board-action"><div class="action-num">${aNum++}</div><div class="action-text">${a}</div></div>`;
  });
  html += `</div>`;

  return html;
}

// ===== HELPER FUNCTIONS =====
function getExposureRange(priority, type) {
  const ranges = {
    "Cyber Liability": { CRITICAL: "$500K–$5M+", HIGH: "$250K–$2M", MODERATE: "$100K–$500K" },
    "Sexual Abuse/Molestation": { CRITICAL: "$1M–$10M+", HIGH: "$500K–$5M", MODERATE: "$250K–$1M" },
    "Active Shooter": { CRITICAL: "$2M–$25M+", HIGH: "$1M–$10M", MODERATE: "$500K–$2M" },
    "Umbrella/Excess": { CRITICAL: "$5M–$25M+", HIGH: "$5M–$25M+", MODERATE: "$2M–$10M" },
    "EPLI": { CRITICAL: "$250K–$2M", HIGH: "$250K–$2M", MODERATE: "$100K–$500K" },
    "General Liability": { CRITICAL: "$1M–$5M", HIGH: "$500K–$2M", MODERATE: "$250K–$1M" },
    "Workers Compensation": { CRITICAL: "$500K–$3M", HIGH: "$250K–$1M", MODERATE: "$100K–$500K" },
    "Equipment Breakdown": { CRITICAL: "$250K–$2M", HIGH: "$100K–$500K", MODERATE: "$50K–$250K" },
    "Flood Insurance": { CRITICAL: "$500K–$10M+", HIGH: "$250K–$5M", MODERATE: "$100K–$1M" }
  };
  return (ranges[type] && ranges[type][priority]) || "$100K–$1M";
}

function getPremiumEstimate(type) {
  const premiums = {
    "Cyber Liability": "$3,000–$15,000",
    "Sexual Abuse/Molestation": "$5,000–$25,000",
    "Active Shooter": "$2,000–$10,000",
    "Umbrella/Excess": "$5,000–$30,000",
    "EPLI": "$4,000–$20,000",
    "General Liability": "$3,000–$12,000",
    "Workers Compensation": "Experience-rated",
    "Equipment Breakdown": "$1,500–$5,000",
    "Flood Insurance": "$2,000–$25,000 (varies by zone)"
  };
  return premiums[type] || "$2,000–$10,000";
}

function getEBExposureRange(priority, type) {
  const ranges = {
    "Long-Term Disability": { CRITICAL: "Catastrophic employee income loss; retention impact", HIGH: "Significant retention risk", MODERATE: "Competitive disadvantage" },
    "Dental": { CRITICAL: "Recruitment/retention impact", HIGH: "Recruitment/retention impact", MODERATE: "Minor competitive gap" },
    "Vision": { CRITICAL: "Minor impact", HIGH: "Minor competitive gap", MODERATE: "Minimal" },
    "Life / AD&D": { CRITICAL: "Employee financial exposure", HIGH: "Employee financial exposure", MODERATE: "Below market" },
    "Short-Term Disability": { CRITICAL: "Employee hardship; possible state mandate violation", HIGH: "Employee hardship; possible state mandate violation", MODERATE: "Below market" },
    "401(k) / Retirement": { CRITICAL: "Primary retention driver", HIGH: "Primary retention driver", MODERATE: "Moderate retention impact" },
    "HSA / FSA": { CRITICAL: "Tax optimization gap", HIGH: "Tax optimization gap", MODERATE: "Minor optimization" },
    "Medical (Group Health)": { CRITICAL: "ACA compliance risk", HIGH: "Competitive disadvantage", MODERATE: "Review plan design" }
  };
  return (ranges[type] && ranges[type][priority]) || "Competitive disadvantage";
}

function getEBPremiumEstimate(type) {
  const premiums = {
    "Long-Term Disability": "$12–$25/EE/mo",
    "Dental": "$8–$15/EE/mo",
    "Vision": "$5–$10/EE/mo",
    "Life / AD&D": "$3–$8/EE/mo",
    "Short-Term Disability": "$10–$20/EE/mo",
    "401(k) / Retirement": "$150–$400/EE/mo",
    "HSA / FSA": "$40–$85/EE/mo",
    "Medical (Group Health)": "$400–$800/EE/mo"
  };
  return premiums[type] || "TBD";
}

// ===== TAB SWITCHING =====
function switchTab(tabId) {
  document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
  document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
  document.querySelector(`[data-tab="${tabId}"]`).classList.add('active');
  document.getElementById(tabId).classList.add('active');
}

// Open or close every <details> in the report on screen. The PDF paths force
// them open regardless (see spOpenAllDetails) — this is for reading and
// verifying the report in the browser, where a reviewer wants the whole
// document rather than eleven separate clicks.
// Open every collapsible in a report, and leave the toolbar toggle telling the
// truth about it. Shared by the toggle and by each export, so the screen and
// the PDF never disagree about what is showing.
function spExpandAllDetails(root) {
  const scope = root || document.querySelector('.tab-content.active');
  if (!scope) return 0;
  const all = scope.querySelectorAll('details');
  all.forEach(el => { el.open = true; });
  const btn = document.getElementById('expandAllBtn');
  if (btn && all.length) btn.textContent = '⤡ Collapse all';
  return all.length;
}

function spToggleAllDetails() {
  const tab = document.querySelector('.tab-content.active');
  const btn = document.getElementById('expandAllBtn');
  if (!tab) { showToast('No report open.', 'error'); return; }
  const all = Array.from(tab.querySelectorAll('details'));
  if (!all.length) { showToast('This report has no collapsible sections.', 'info'); return; }
  // Expand unless every one is already open.
  const expand = all.some(el => !el.open);
  if (expand) { spExpandAllDetails(tab); }
  else {
    all.forEach(el => { el.open = false; });
    if (btn) btn.textContent = '⤢ Expand all';
  }
  showToast((expand ? 'Expanded ' : 'Collapsed ') + all.length + ' section' + (all.length === 1 ? '' : 's'), 'success');
}

// ===== PRINT / PDF HANDLING =====
function handlePrint() {
  const activeTab = document.querySelector('.tab-content.active');
  if (!activeTab) { showToast('No report to print.', 'error'); return; }
  const title = document.getElementById('resultsTitle')?.textContent || 'SentriPol Analysis';
  const disclaimer = document.querySelector('.disclaimer-bar');
  const printWindow = window.open('', '_blank');
  if (!printWindow) { showToast('Pop-up blocked. Please allow pop-ups for this site to save as PDF.', 'warning'); return; }
  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title><style>
    @page { margin: 0.75in; size: letter; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Inter', -apple-system, system-ui, sans-serif; font-size: 12px; color: #1a1a2e; line-height: 1.5; padding: 0; }
    h2 { font-size: 20px; color: #003366; border-bottom: 2px solid #003366; padding-bottom: 6px; margin-bottom: 12px; }
    h3 { font-size: 15px; color: #003366; border-bottom: 1px solid #003366; padding-bottom: 4px; margin: 18px 0 10px 0; }
    h4 { font-size: 13px; color: #003366; margin: 12px 0 6px 0; }
    p { margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px; }
    th { background: #003366; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; }
    td { padding: 5px 8px; border: 1px solid #ccc; }
    tr:nth-child(even) td { background: #f8f9fa; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
    .badge-critical { background: #fde8e8; color: #c33; border: 1px solid #c33; }
    .badge-high { background: #fff3e0; color: #e65100; border: 1px solid #e65100; }
    .badge-moderate { background: #fff8e1; color: #f57f17; border: 1px solid #f57f17; }
    .badge-meets { background: #e8f5e9; color: #2e7d32; border: 1px solid #2e7d32; }
    .badge-verify { background: #fff3e0; color: #cc8800; border: 1px solid #cc8800; }
    .state-card { border: 1px solid #003366; border-radius: 6px; margin: 10px 0; page-break-inside: avoid; overflow: hidden; }
    .state-card-header { background: #003366; color: #fff; padding: 8px 12px; font-weight: 600; }
    .state-card-body { padding: 10px 12px; font-size: 11px; }
    .state-card-body p { margin-bottom: 4px; }
    .report-section { margin-bottom: 12px; }
    .summary-box, .coverage-summary, .retention-box { border: 1px solid #ccc; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #f5f7fa; }
    .coverage-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
    .coverage-tag { background: #e8f5e9; color: #2e7d32; padding: 2px 10px; border-radius: 12px; font-size: 11px; }
    .summary-stat { display: inline-block; background: #f5f7fa; border: 1px solid #ccc; border-radius: 6px; padding: 6px 12px; margin: 4px; text-align: center; }
    .summary-stat .stat-value { font-size: 18px; font-weight: 700; color: #003366; }
    .summary-stat .stat-label { font-size: 10px; color: #666; }
    .exclusion-alert { border: 1px solid #c33; border-left: 4px solid #c33; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #fff5f5; page-break-inside: avoid; }
    .exclusion-alert.warning-level { border-color: #cc8800; border-left-color: #cc8800; background: #fffbf0; }
    .exclusion-alert h4 { color: #c33; margin: 0 0 4px 0; font-size: 12px; }
    .exclusion-alert.warning-level h4 { color: #cc8800; }
    .disclaimer-bar { border: 1px solid #003366; background: #f0f5ff; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 10px; color: #333; page-break-inside: avoid; }
    .data-privacy-notice { border: 1px solid #999; background: #f9f9f9; border-radius: 6px; padding: 10px 14px; margin: 10px 0; font-size: 10px; page-break-inside: avoid; }
    .next-steps-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 10px 0; }
    .next-step-card { border: 1px solid #ccc; border-radius: 6px; padding: 10px; }
    .next-step-card h4 { margin: 0 0 6px 0; }
    .exec-finding { border: 1px solid #ccc; border-left: 4px solid #003366; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #f9fbfd; page-break-inside: avoid; }
    .brand-footer { text-align: center; font-size: 10px; color: #999; margin-top: 30px; padding-top: 12px; border-top: 1px solid #ccc; }
    .qs-verdict-box { text-align: center; color: #fff; padding: 24px; border-radius: 8px; margin: 16px 0; }
    .qs-verdict-box.qs-pass { background: #16A34A; }
    .qs-verdict-box.qs-caution { background: #D97706; }
    .qs-verdict-box.qs-fail { background: #DC2626; }
    .qs-score-circle { display: inline-block; width: 80px; height: 80px; line-height: 80px; border-radius: 50%; background: rgba(255,255,255,0.2); font-size: 28px; font-weight: 800; margin-bottom: 8px; }
    .qs-category-table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
    .qs-category-table th { background: #003366; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; }
    .qs-category-table td { padding: 5px 8px; border: 1px solid #ccc; }
    .qs-category-table tr:nth-child(even) td { background: #f8f9fa; }
    .eo-badge { background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px; }
    ${SP_PRINT_PAGINATION_CSS}
    @media print {
      ${SP_PRINT_PAGINATION_CSS}
      body { font-size: 11px; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .state-card-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .exclusion-alert { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .advisory-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .advisory-tag { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .disclaimer-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .qs-verdict-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .eo-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style></head><body>`);
  if (disclaimer) { printWindow.document.write(disclaimer.outerHTML); }
  printWindow.document.write(`<h2>${title}</h2>`);
  // Expand the report ON SCREEN too, not just the copy being printed. What the
  // button produces and what the page shows should not disagree — a reader
  // checking the PDF against the screen saw collapsed sections in one and
  // expanded sections in the other.
  spExpandAllDetails(activeTab);
  // Expand every collapsible before it goes into the print window. A closed
  // <details> writes its <summary> line and drops everything inside, so the
  // routine endorsements appendix, the NAICS cheat sheet and the per-state
  // detail were all missing from this export with nothing to say so. The CSS
  // rule in SP_PRINT_PAGINATION_CSS is not enough on its own: the UA hides a
  // closed details' children with more than `display`, and here it sits inside
  // an `@media print` block, so it does nothing in the on-screen preview.
  printWindow.document.write(window.spOpenAllDetails(activeTab.innerHTML));
  printWindow.document.write(`<div class="brand-footer">SentriPol\u2122 | Owned by AI Blueprints | Dayna Stewart, CEO<br>This report was generated by SentriPol\u2122, an AI-powered policy review tool. The licensed broker must review and verify all findings.</div>`);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  setTimeout(() => { printWindow.print(); }, 500);
}

// ===== COMPACT PDF EXPORT =====
// v2.18: Accepts optional demoMode flag — when true, de-identifies org name/website
// before rendering AND still produces a compact (email-sized) PDF.
async function handleCompactPDF(demoMode) {
  demoMode = !!demoMode;
  const activeTab = document.querySelector('.tab-content.active');
  if (!activeTab) { showToast('No report to export.', 'error'); return; }

  let title = document.getElementById('resultsTitle')?.textContent || 'SentriPol Analysis';
  const disclaimer = document.querySelector('.disclaimer-bar');
  const btn = document.getElementById(demoMode ? 'demoCompactPdfBtn' : 'compactPdfBtn');
  const origText = btn.textContent;
  btn.textContent = '⏳ Generating...';
  btn.disabled = true;

  // v2.18: If demo mode, pick a fictional profile up-front so title and content match
  let demo = null;
  let realName = '', realUrl = '', realDomain = '', realNick = '';
  if (demoMode) {
    const fd = window._lastFormData;
    if (!fd || !fd.policyholderName) {
      showToast('No report data found. Please run an analysis first.', 'error');
      btn.textContent = origText;
      btn.disabled = false;
      return;
    }
    demo = DEMO_PROFILES[Math.floor(Math.random() * DEMO_PROFILES.length)];
    realName = fd.policyholderName;
    realUrl = fd.websiteUrl || '';
    realDomain = realUrl ? realUrl.replace(/https?:\/\//, '').replace(/\/$/, '') : '';
    realNick = fd.caseNick || '';
    // De-identify the title for display/metadata
    title = title.replace(new RegExp(escapeRegex(realName), 'gi'), demo.name) + ' (DEMO)';
  }

  try {
    // Create an off-screen container with print-optimized styling
    const container = document.createElement('div');
    container.id = 'compact-pdf-render';
    container.style.cssText = 'position:absolute; left:-9999px; top:0; width:816px; background:#fff; font-family:Inter,-apple-system,system-ui,sans-serif; font-size:11px; color:#1a1a2e; line-height:1.5; padding:40px 50px;';

    // Add disclaimer
    if (disclaimer) {
      const dClone = disclaimer.cloneNode(true);
      dClone.style.cssText = 'border:1px solid #003366; background:#f0f5ff; border-radius:6px; padding:10px 14px; margin-bottom:16px; font-size:10px; color:#333;';
      container.appendChild(dClone);
    }

    // v2.18: Demo watermark banner
    if (demoMode) {
      const wm = document.createElement('div');
      wm.style.cssText = 'background:#fff3cd; border:2px solid #ffc107; border-radius:8px; padding:10px 14px; margin-bottom:14px; text-align:center; font-size:11px; color:#856404;';
      wm.innerHTML = '<strong>\uD83D\uDD12 DEMO VERSION</strong> \u2014 This report has been de-identified for demonstration purposes. All organization names and identifying information have been replaced with fictional data. Coverage analysis, limits, gaps, and recommendations reflect a real SentriPol\u2122 scan.';
      container.appendChild(wm);
    }

    // Add title
    const h2 = document.createElement('h2');
    h2.textContent = title;
    h2.style.cssText = 'font-size:20px; color:#003366; border-bottom:2px solid #003366; padding-bottom:6px; margin-bottom:12px;';
    container.appendChild(h2);

    // NAICS-Pivot analysis — print-optimized block (plain tables/lists, fully
    // expanded), so it always renders in the PDF even though the live panel uses
    // <details> + flex that html2canvas drops.
    let _naicsPrintHtml = '';
    try {
      // BOP review leads, above Section 1 — for a BOP the NOT COVERED findings are
      // the headline, and the panel carries its own Section 1-10 numbering.
      const _bh = (typeof buildBopPrintHtml === 'function') ? buildBopPrintHtml() : '';
      if (_bh) { const _bd = document.createElement('div'); _bd.innerHTML = _bh; container.appendChild(_bd); }
      _naicsPrintHtml = (typeof buildNaicsPrintHtml === 'function') ? buildNaicsPrintHtml(demoMode) : '';
    } catch (_e) {}

    // Clone report content
    const contentClone = activeTab.cloneNode(true);
    // Drop the live NAICS/profile panels (replaced by the clean block below) to
    // avoid duplication, and expand any remaining collapsibles so nothing is
    // hidden when the clone is rasterised.
    contentClone.querySelectorAll('[id^="spNaicsPanel"], [id^="spBopPanel"], [id^="spDetectedProfile"]').forEach(el => el.remove());
    contentClone.querySelectorAll('details').forEach(el => { el.open = true; });
    // Profile + NAICS are Section 3 of the report, so the print-optimised block goes
    // back into the host the live panels were just stripped from. Hoisting it above
    // Section 1 (as this did before the panels were numbered) would contradict the
    // report's own numbering. Done before the demo pass so it is de-identified too.
    try {
      const _s3 = contentClone.querySelector('#spSection3Body');
      if (_naicsPrintHtml) {
        if (_s3) _s3.innerHTML = _naicsPrintHtml;
        else { const _nd = document.createElement('div'); _nd.innerHTML = _naicsPrintHtml; container.appendChild(_nd); }
      }
    } catch (_e) {}

    // v2.18: If demo mode, de-identify the cloned HTML BEFORE any styling pass
    if (demoMode && demo) {
      contentClone.innerHTML = window.spDeidentifyReportHtml(contentClone.innerHTML, {
        realName: realName, realUrl: realUrl, realNick: realNick, demo: demo
      });
    }
    // Force all print-color-adjust styles
    contentClone.querySelectorAll('th, .state-card-header, .badge, .eo-badge, .qs-verdict-box, .qs-score-circle').forEach(el => {
      const cs = window.getComputedStyle(document.querySelector(el.tagName.toLowerCase() + (el.className ? '.' + el.className.split(' ')[0] : '')) || el);
      if (el.tagName === 'TH') el.style.cssText += 'background:#003366!important; color:#fff!important; padding:6px 8px; font-weight:600; font-size:11px;';
    });
    // Inline key styles for html2canvas
    contentClone.querySelectorAll('th').forEach(el => { el.style.cssText = 'background:#003366; color:#fff; padding:6px 8px; text-align:left; font-weight:600; font-size:11px;'; });
    contentClone.querySelectorAll('.state-card-header').forEach(el => { el.style.cssText = 'background:#003366; color:#fff; padding:8px 12px; font-weight:600;'; });
    contentClone.querySelectorAll('.badge-critical').forEach(el => { el.style.cssText += 'background:#fde8e8; color:#c33; border:1px solid #c33; padding:2px 8px; border-radius:10px; font-size:10px; font-weight:600; display:inline-block;'; });
    contentClone.querySelectorAll('.badge-high').forEach(el => { el.style.cssText += 'background:#fff3e0; color:#e65100; border:1px solid #e65100; padding:2px 8px; border-radius:10px; font-size:10px; font-weight:600; display:inline-block;'; });
    contentClone.querySelectorAll('.badge-moderate').forEach(el => { el.style.cssText += 'background:#fff8e1; color:#f57f17; border:1px solid #f57f17; padding:2px 8px; border-radius:10px; font-size:10px; font-weight:600; display:inline-block;'; });
    contentClone.querySelectorAll('.badge-meets').forEach(el => { el.style.cssText += 'background:#e8f5e9; color:#2e7d32; border:1px solid #2e7d32; padding:2px 8px; border-radius:10px; font-size:10px; font-weight:600; display:inline-block;'; });
    contentClone.querySelectorAll('.badge-verify').forEach(el => { el.style.cssText += 'background:#fff3e0; color:#cc8800; border:1px solid #cc8800; padding:2px 8px; border-radius:10px; font-size:10px; font-weight:600; display:inline-block;'; });
    contentClone.querySelectorAll('.exclusion-alert').forEach(el => { el.style.cssText += 'border:1px solid #c33; border-left:4px solid #c33; border-radius:6px; padding:10px 14px; margin:8px 0; background:#fff5f5;'; });
    contentClone.querySelectorAll('.exclusion-alert.warning-level').forEach(el => { el.style.cssText += 'border-color:#cc8800; border-left-color:#cc8800; background:#fffbf0;'; });
    contentClone.querySelectorAll('.summary-box, .coverage-summary, .retention-box').forEach(el => { el.style.cssText += 'border:1px solid #ccc; border-radius:6px; padding:10px 14px; margin:8px 0; background:#f5f7fa;'; });
    contentClone.querySelectorAll('.eo-badge').forEach(el => { el.style.cssText = 'background:linear-gradient(135deg,#003366,#004488); color:#fff; padding:16px 20px; border-radius:8px; margin-bottom:20px; display:flex; align-items:center; gap:12px;'; });
    contentClone.querySelectorAll('.qs-verdict-box').forEach(el => { el.style.cssText += 'text-align:center; color:#fff; padding:24px; border-radius:8px; margin:16px 0;'; });
    contentClone.querySelectorAll('table').forEach(el => { el.style.cssText = 'width:100%; border-collapse:collapse; margin:10px 0; font-size:11px;'; });
    contentClone.querySelectorAll('td').forEach(el => { el.style.cssText += 'padding:5px 8px; border:1px solid #ccc;'; });
    contentClone.querySelectorAll('tr:nth-child(even) td').forEach(el => { el.style.background = '#f8f9fa'; });
    container.appendChild(contentClone);

    // Add brand footer
    const footer = document.createElement('div');
    footer.style.cssText = 'text-align:center; font-size:10px; color:#999; margin-top:30px; padding-top:12px; border-top:1px solid #ccc;';
    footer.innerHTML = 'SentriPol\u2122 | Owned by AI Blueprints | Dayna Stewart, CEO<br>This report was generated by SentriPol\u2122, an AI-powered policy review tool. The licensed broker must review and verify all findings.';
    container.appendChild(footer);

    document.body.appendChild(container);

    // Wait for layout
    await new Promise(r => setTimeout(r, 300));

    btn.textContent = '⏳ Rendering pages...';

    // Use jsPDF with html2canvas
    const jsPDFClass = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
    if (!jsPDFClass) { throw new Error('PDF library not loaded. Please refresh the page and try again.'); }
    const pdf = new jsPDFClass({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = 612; // letter width in pts
    const pageH = 792; // letter height in pts
    const margin = 36; // 0.5 inch margins
    const contentW = pageW - (margin * 2); // 540pt
    const scale = 2; // render at 2x for clarity

    // Render entire container to canvas
    const canvas = await html2canvas(container, {
      scale: scale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 816,
      windowWidth: 816
    });

    // Page slicing, break-aware: cut between blocks rather than through them.
    // The measurement is taken from the live container (still in the document)
    // in the same scale the canvas was rendered at.
    const canvasPageH = (pageH - margin * 2) * (canvas.width / contentW);
    const blocks = window.spCollectBreakBlocks(container, canvas.width / 816);
    const cuts = window.spComputePageCuts(blocks, canvasPageH, canvas.height);

    btn.textContent = `⏳ Writing ${cuts.length} pages...`;

    cuts.forEach((cut, i) => {
      if (i > 0) pdf.addPage();

      const sliceH = Math.max(1, Math.round(cut.end - cut.start));
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceH;
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, Math.round(cut.start), canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      // Convert to JPEG and add to PDF. A short page (one that ended early to
      // keep a block whole) is drawn at its own height, not stretched.
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.85);
      const imgH = (sliceH / canvas.width) * contentW;
      pdf.addImage(imgData, 'JPEG', margin, margin, contentW, imgH);
    });

    // Set metadata
    pdf.setProperties({
      title: title,
      author: 'SentriPol by AI Blueprints',
      creator: 'SentriPol v2.12'
    });

    // Generate filename
    const fd = window._lastFormData;
    let filename = 'SentriPol-Report';
    if (demoMode && demo) {
      // v2.18: Use demo nickname so the file itself is de-identified
      filename = 'Demo-' + demo.name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
    } else if (fd && fd.policyholderName) {
      filename = fd.policyholderName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
      if (fd.caseNick) filename += '-' + fd.caseNick.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
    }
    filename += demoMode ? '-Compact-DEMO.pdf' : '-Compact.pdf';

    // Save
    pdf.save(filename);

    // Cleanup
    document.body.removeChild(container);

    // Report size estimate
    const pdfBlob = pdf.output('blob');
    const sizeMB = (pdfBlob.size / (1024 * 1024)).toFixed(1);
    if (demoMode && demo) {
      showToast(`Demo + Compact PDF saved (${sizeMB} MB, ${totalPages} pages) — Org replaced with "${demo.name}"`, 'success');
    } else {
      showToast(`Compact PDF saved (${sizeMB} MB) — ${totalPages} pages`, 'success');
    }

  } catch (err) {
    console.error('Compact PDF error:', err);
    showToast('Error generating compact PDF: ' + err.message, 'error');
  } finally {
    const activeBtn = document.getElementById(demoMode ? 'demoCompactPdfBtn' : 'compactPdfBtn');
    if (activeBtn) { activeBtn.textContent = origText; activeBtn.disabled = false; }
    // Cleanup any orphaned render containers
    const orphan = document.getElementById('compact-pdf-render');
    if (orphan) orphan.remove();
  }
}

// ===== DEMO PDF (DE-IDENTIFICATION) =====
// ===== Compact PDF pagination ============================================
// The compact export rasterises the whole report with html2canvas and slices
// the canvas at fixed page heights, so the CSS page rules the other exports
// use do not apply to it: a cut lands wherever the arithmetic puts it, which
// is routinely through the middle of a table row or a card.
//
// These two functions make it break-aware. The collector measures where the
// atomic blocks actually sit; the planner turns that into page cuts. The
// planner is pure so the rule — never cut through a block, never leave a page
// half empty to avoid it — can be tested without a browser.
window.spCollectBreakBlocks = function(container, scale){
  // Same atomic list the CSS paginator uses, plus the two things only this
  // path can see: a table row, and a heading (protected together with what
  // follows it, below). Grid children are included by position so a new card
  // in an existing dashboard is protected without being named.
  const SEL = ['tr', 'h2', 'h3', 'h4']
    .concat(SP_ATOMIC_SELECTORS)
    .concat(SP_FLOW_CONTAINERS.map(c => c + ' > *'))
    .join(', ');
  const base = container.getBoundingClientRect().top;
  const out = [];
  container.querySelectorAll(SEL).forEach(el => {
    const r = el.getBoundingClientRect();
    if (!r.height) return;
    let top = r.top - base, bottom = r.bottom - base;
    // A heading is only worth protecting together with what follows it —
    // a heading alone at the foot of a page is the artefact worth avoiding.
    if (/^H[2-4]$/.test(el.tagName) && el.nextElementSibling) {
      const nr = el.nextElementSibling.getBoundingClientRect();
      bottom = Math.min(nr.top - base + 80, nr.bottom - base);
    }
    out.push({ top: top * scale, bottom: bottom * scale, kind: el.tagName.toLowerCase() });
  });
  return out;
};

// Page cuts for a canvas of `totalH` px, each page at most `pageH` px.
// A cut that would fall inside a block moves up to that block's top, pushing
// the block whole onto the next page — unless doing so would leave more than
// `1 - minFill` of the page blank, or the block is taller than a page and so
// has to be cut wherever it falls. That guard is the difference between this
// and the CSS rule the report used to carry on `.report-section`: avoiding
// every break is what produced the white space.
window.spComputePageCuts = function(blocks, pageH, totalH, opts){
  const o = opts || {};
  const minFill = o.minFill == null ? 0.6 : o.minFill;
  const cuts = [];
  if (!(pageH > 0) || !(totalH > 0)) return cuts;
  const sorted = (blocks || [])
    .filter(b => b && isFinite(b.top) && isFinite(b.bottom) && b.bottom > b.top)
    .sort((a, b) => a.top - b.top);
  let start = 0, guard = 0;
  while (start < totalH && guard++ < 5000) {
    let end = start + pageH;
    if (end >= totalH) { cuts.push({ start: start, end: totalH }); break; }
    const straddling = sorted.filter(b => b.top < end && b.bottom > end);
    if (straddling.length) {
      const oversized = straddling.some(b => (b.bottom - b.top) >= pageH);
      const candidate = Math.min.apply(null, straddling.map(b => b.top));
      if (!oversized && candidate > start + pageH * minFill) end = candidate;
    }
    if (!(end > start)) end = start + pageH;              // never stall
    cuts.push({ start: start, end: end });
    start = end;
  }
  return cuts;
};

// ===== Print pagination ==================================================
// One rule set for every paper path — the browser's Save/Print dialog and the
// Demo PDF window both apply it, so they cannot drift.
//
// The report used to set `page-break-inside: avoid` on `.report-section`. A
// section is routinely taller than a page (Section 6 is a full benchmark
// table), and a block that cannot fit gets pushed WHOLE onto the next sheet —
// which is why the export read as one section per page with the bottom third
// of the previous page blank. Sections flow across pages now; what stays
// unbroken is the small stuff, where a split actually costs the reader: a
// table row, a card, a heading separated from the text under it.
// The blocks a page break must never fall inside. Both paginators read this
// one list: the CSS rules below (full print / demo print, which let the
// browser paginate) and spCollectBreakBlocks (the compact export, which
// rasterises the report and slices the canvas itself, where CSS page rules do
// not apply at all).
//
// It used to be two lists that had to be kept in step by hand, and they
// drifted: `.next-step-card` was in both, `.cyber-stat-card` in neither, so
// Section 11's stat cards were sliced through the middle of their numbers in
// every compact PDF. A card class that is not listed here is not protected,
// and nothing complains — hence the test that every card-shaped class in the
// stylesheet appears in this array.
const SP_ATOMIC_SELECTORS = [
  '.state-card', '.exclusion-alert', '.exec-finding', '.summary-box',
  '.coverage-summary', '.retention-box', '.next-step-card', '.disclaimer-bar',
  '.data-privacy-notice', '.qs-verdict-box', '.eo-badge', '.finding-card',
  '.summary-stat', '.plan-card', '.cyber-stat-card', '.advisory-card',
  '.sp-trial-cta-card'
];

// Layout containers: the break goes BETWEEN their children, never through one.
const SP_FLOW_CONTAINERS = ['.next-steps-grid', '.summary-stats', '.cyber-stat-grid', '.cyber-dashboard'];

const SP_PRINT_PAGINATION_CSS = `
  .report-section { page-break-inside: auto; break-inside: auto;
                    page-break-before: auto; page-break-after: auto; margin-bottom: 12px; }
  /* A heading must not be the last thing on a page. */
  h2, h3, h4, h5 { page-break-inside: avoid; break-inside: avoid;
                   page-break-after: avoid; break-after: avoid; }
  h3 + p, h3 + table, h3 + div, h4 + p, h4 + table, h4 + div,
  h3 + .report-table, h4 + .report-table { page-break-before: avoid; break-before: avoid; }
  p, li { orphans: 3; widows: 3; }
  /* If a <details> still reaches print collapsed, show its content rather
     than dropping it silently. */
  details > *:not(summary) { display: block !important; }
  /* An expanded appendix may run over a page boundary; forcing it to stay
     whole is what produces a half-empty page followed by a clipped table.
     Its rows stay intact under the tr rule above. */
  details { page-break-inside: auto; break-inside: auto; }
  summary { page-break-after: avoid; break-after: avoid; }
  /* Nothing may be clipped on paper. A scroll container cannot scroll, and a
     max-height silently drops whatever sits past it. .state-card carries
     overflow:hidden for its rounded header, which cuts content in print. */
  .report-section, .state-card, .state-card-body, .advisory-card, .summary-box,
  .coverage-summary, .retention-box, .next-step-card, .exclusion-alert,
  details, table, .report-table, .cyber-dashboard
    { overflow: visible !important; max-height: none !important; }
  /* A long unbroken token — a URL, a form number, a policy number — is what
     pushes a table past the right margin, where print has no scrollbar to
     rescue it. Wrap instead of overflowing; column widths stay automatic so
     table proportions do not change. */
  table, .report-table { max-width: 100% !important; }
  td, th { overflow-wrap: break-word; word-break: break-word; }
  /* Long tables continue across pages, with their header repeated, rather
     than jumping the page whole. Individual rows stay intact. */
  table, .report-table { page-break-inside: auto; break-inside: auto; }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  td, th { page-break-inside: avoid; break-inside: avoid; }
  /* Small, self-contained blocks: splitting these is what looks broken. */
  ${SP_ATOMIC_SELECTORS.join(', ')}
    { page-break-inside: avoid; break-inside: avoid; }
  /* A layout container splits BETWEEN its children; each child stays whole.
     The child rule matters as much as the list: a card added to one of these
     grids later is protected by position rather than by name. */
  ${SP_FLOW_CONTAINERS.join(', ')}
    { page-break-inside: auto; break-inside: auto; }
  ${SP_FLOW_CONTAINERS.map(c => c + ' > *').join(', ')}
    { page-break-inside: avoid; break-inside: avoid; }
  /* Trailing margin on the last block in a section is dead space at a page
     boundary. */
  .report-section > *:last-child { margin-bottom: 0; }
`;

// The static stylesheet is authored above this script; injecting the rules
// here puts them last in the cascade, so they win over the older
// `.report-section { page-break-inside: avoid }` without editing every
// print block that repeats it.
try {
  const _spPrintStyle = document.createElement('style');
  _spPrintStyle.setAttribute('media', 'print');
  _spPrintStyle.textContent = SP_PRINT_PAGINATION_CSS;
  document.head.appendChild(_spPrintStyle);
} catch (e) { /* no DOM (headless test harness) — the constant is what matters */ }

const DEMO_PROFILES = [
  { name: 'Pinnacle Community Services, Inc', nick: 'PCS', website: 'https://pinnacleservices.org' },
  { name: 'Summit Regional Partners LLC', nick: 'SRP', website: 'https://summitregional.org' },
  { name: 'Horizon Valley Group, Inc', nick: 'HVG', website: 'https://horizonvalleygroup.org' },
  { name: 'Crestline Educational Foundation', nick: 'CEF', website: 'https://crestlineedu.org' },
  { name: 'Meridian Health Alliance, Inc', nick: 'MHA', website: 'https://meridianhealthalliance.org' },
  { name: 'Atlas Infrastructure Group LLC', nick: 'AIG-Demo', website: 'https://atlasinfragroup.org' },
  { name: 'Northstar Community Partners', nick: 'NCP', website: 'https://northstarcommunity.org' },
  { name: 'Vanguard Public Services Corp', nick: 'VPS', website: 'https://vanguardpublic.org' }
];

function handleDemoPrint() {
  const activeTab = document.querySelector('.tab-content.active');
  if (!activeTab) { showToast('No report to export as demo.', 'error'); return; }

  const fd = window._lastFormData;
  if (!fd || !fd.policyholderName) {
    showToast('No report data found. Please run an analysis first.', 'error');
    return;
  }

  // Pick a random demo profile. The substitutions themselves live in
  // spDeidentifyReportHtml — the url/domain/nickname forms are derived there.
  const demo = DEMO_PROFILES[Math.floor(Math.random() * DEMO_PROFILES.length)];
  const realName = fd.policyholderName;
  const realUrl = fd.websiteUrl || '';
  const realNick = fd.caseNick || '';

  // Get the original title
  const origTitle = document.getElementById('resultsTitle')?.textContent || 'SentriPol Analysis';
  const demoTitle = origTitle.replace(new RegExp(escapeRegex(realName), 'gi'), demo.name);

  // Clone and de-identify the HTML. One shared routine for both PDF paths:
  // when they each carried their own copy, the demo print grew a broad
  // whole-document word replacement that the compact export never had.
  spExpandAllDetails(activeTab);
  const html = window.spOpenAllDetails(
    window.spDeidentifyReportHtml(activeTab.innerHTML, {
      realName: realName, realUrl: realUrl, realNick: realNick, demo: demo
    }));

  // Add demo watermark notice
  const watermark = '<div style="background:#fff3cd; border:2px solid #ffc107; border-radius:8px; padding:12px 16px; margin-bottom:16px; text-align:center; font-size:12px; color:#856404;"><strong>\uD83D\uDD12 DEMO VERSION</strong> \u2014 This report has been de-identified for demonstration purposes. All organization names and identifying information have been replaced with fictional data. Coverage analysis, limits, gaps, and recommendations reflect a real SentriPol\u2122 scan.</div>';

  // Build the print window
  const disclaimer = document.querySelector('.disclaimer-bar');
  const printWindow = window.open('', '_blank');
  if (!printWindow) { showToast('Pop-up blocked. Please allow pop-ups to generate the Demo PDF.', 'warning'); return; }

  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapeHtml(demoTitle)} (DEMO)</title><style>
    @page { margin: 0.75in; size: letter; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Inter', -apple-system, system-ui, sans-serif; font-size: 12px; color: #1a1a2e; line-height: 1.5; padding: 0; }
    h2 { font-size: 20px; color: #003366; border-bottom: 2px solid #003366; padding-bottom: 6px; margin-bottom: 12px; }
    h3 { font-size: 15px; color: #003366; border-bottom: 1px solid #003366; padding-bottom: 4px; margin: 18px 0 10px 0; }
    h4 { font-size: 13px; color: #003366; margin: 12px 0 6px 0; }
    p { margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px; }
    th { background: #003366; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; }
    td { padding: 5px 8px; border: 1px solid #ccc; }
    tr:nth-child(even) td { background: #f8f9fa; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
    .badge-critical { background: #fde8e8; color: #c33; border: 1px solid #c33; }
    .badge-high { background: #fff3e0; color: #e65100; border: 1px solid #e65100; }
    .badge-moderate { background: #fff8e1; color: #f57f17; border: 1px solid #f57f17; }
    .badge-meets { background: #e8f5e9; color: #2e7d32; border: 1px solid #2e7d32; }
    .badge-verify { background: #fff3e0; color: #cc8800; border: 1px solid #cc8800; }
    .state-card { border: 1px solid #003366; border-radius: 6px; margin: 10px 0; page-break-inside: avoid; overflow: hidden; }
    .state-card-header { background: #003366; color: #fff; padding: 8px 12px; font-weight: 600; }
    .state-card-body { padding: 10px 12px; font-size: 11px; }
    .state-card-body p { margin-bottom: 4px; }
    .report-section { margin-bottom: 12px; }
    .summary-box, .coverage-summary, .retention-box { border: 1px solid #ccc; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #f5f7fa; }
    .coverage-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
    .coverage-tag { background: #e8f5e9; color: #2e7d32; padding: 2px 10px; border-radius: 12px; font-size: 11px; }
    .summary-stat { display: inline-block; background: #f5f7fa; border: 1px solid #ccc; border-radius: 6px; padding: 6px 12px; margin: 4px; text-align: center; }
    .summary-stat .stat-value { font-size: 18px; font-weight: 700; color: #003366; }
    .summary-stat .stat-label { font-size: 10px; color: #666; }
    .exclusion-alert { border: 1px solid #c33; border-left: 4px solid #c33; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #fff5f5; page-break-inside: avoid; }
    .exclusion-alert.warning-level { border-color: #cc8800; border-left-color: #cc8800; background: #fffbf0; }
    .exclusion-alert h4 { color: #c33; margin: 0 0 4px 0; font-size: 12px; }
    .exclusion-alert.warning-level h4 { color: #cc8800; }
    .disclaimer-bar { border: 1px solid #003366; background: #f0f5ff; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 10px; color: #333; page-break-inside: avoid; }
    .data-privacy-notice { border: 1px solid #999; background: #f9f9f9; border-radius: 6px; padding: 10px 14px; margin: 10px 0; font-size: 10px; page-break-inside: avoid; }
    .next-steps-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 10px 0; }
    .next-step-card { border: 1px solid #ccc; border-radius: 6px; padding: 10px; }
    .next-step-card h4 { margin: 0 0 6px 0; }
    .exec-finding { border: 1px solid #ccc; border-left: 4px solid #003366; border-radius: 6px; padding: 10px 14px; margin: 8px 0; background: #f9fbfd; page-break-inside: avoid; }
    .brand-footer { text-align: center; font-size: 10px; color: #999; margin-top: 30px; padding-top: 12px; border-top: 1px solid #ccc; }
    .qs-verdict-box { text-align: center; color: #fff; padding: 24px; border-radius: 8px; margin: 16px 0; }
    .qs-verdict-box.qs-pass { background: #16A34A; }
    .qs-verdict-box.qs-caution { background: #D97706; }
    .qs-verdict-box.qs-fail { background: #DC2626; }
    .qs-score-circle { display: inline-block; width: 80px; height: 80px; line-height: 80px; border-radius: 50%; background: rgba(255,255,255,0.2); font-size: 28px; font-weight: 800; margin-bottom: 8px; }
    .qs-category-table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
    .qs-category-table th { background: #003366; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; }
    .qs-category-table td { padding: 5px 8px; border: 1px solid #ccc; }
    .qs-category-table tr:nth-child(even) td { background: #f8f9fa; }
    .eo-badge { background: linear-gradient(135deg, #003366, #004488); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; display: flex; align-items: center; gap: 12px; }
    ${SP_PRINT_PAGINATION_CSS}
    @media print {
      ${SP_PRINT_PAGINATION_CSS}
      body { font-size: 11px; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .state-card-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .exclusion-alert { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .advisory-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .advisory-tag { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .disclaimer-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .qs-verdict-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .eo-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style></head><body>`);

  if (disclaimer) { printWindow.document.write(disclaimer.outerHTML); }
  printWindow.document.write(watermark);
  printWindow.document.write(`<h2>${escapeHtml(demoTitle)}</h2>`);
  printWindow.document.write(html);
  printWindow.document.write(`<div class="brand-footer">SentriPol\u2122 | Owned by AI Blueprints | Dayna Stewart, CEO<br>This report was generated by SentriPol\u2122, an AI-powered policy review tool. The licensed broker must review and verify all findings.<br><em style="color:#999;">De-identified demo version — organization details replaced with fictional data.</em></div>`);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  setTimeout(() => { printWindow.print(); }, 500);

  showToast(`Demo PDF generated. Org replaced with "${demo.name}".`, 'success');
}

// Helper: escape string for use in RegExp
function escapeRegex(str) {
  if (!str || typeof str !== 'string') return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper: case-insensitive global replace (preserves HTML integrity)
// ===== Demo de-identification ============================================
// Replaces the client's identity in a finished report, and NOTHING else. The
// demo export used to run a broad single-word pass over the whole document:
// for "Arizona Autism Charter Schools" it picked "Arizona" as the identifying
// word and rewrote every text node containing it — so the Operating states
// field printed the demo organisation's name instead of the state, and the
// same substitution reached any finding, citation or benchmark that named the
// state. An output field must survive de-identification unchanged: the demo
// exists to hide WHO the client is, not to alter WHAT the analysis found.
//
// Two rules make that hold:
//   1. Only the identifiers themselves are replaced — the full organisation
//      name, its website, its domain, and the case nickname.
//   2. The single-word pass exists solely to scrub uploaded FILENAMES, so it
//      only fires inside a filename-looking token, and never on a word that
//      names a US state or its abbreviation.
// Every <details> in a report is collapsed by default, and a collapsed
// <details> prints as its <summary> line alone — the content inside is simply
// absent from the PDF, with nothing on the page to say it was dropped. The
// routine endorsements appendix, the NAICS cheat sheet and the per-state
// detail all left the report this way.
//
// String in, string out, so the DOM path (full print) and the innerHTML path
// (compact / demo) can share it.
window.spOpenAllDetails = function(html){
  // Tag-level rewrite rather than a DOM round-trip: this runs on report HTML
  // that has already been through the de-identification pass, and reparsing a
  // megabyte of markup through innerHTML to flip one attribute risks
  // normalising something the print CSS depends on. The negative lookahead
  // leaves a <details open> alone.
  return String(html == null ? '' : html)
    .replace(/<details(?![^>]*\bopen\b)([^>]*)>/gi, '<details open$1>');
};

window.spDeidentifyReportHtml = function(html, ctx){
  const c = ctx || {};
  const demo = c.demo || {};
  let out = String(html == null ? '' : html);
  if (!demo.name) return out;

  const realName = c.realName || '';
  const realUrl = c.realUrl || '';
  const realDomain = realUrl ? realUrl.replace(/https?:\/\//, '').replace(/\/$/, '') : '';
  const demoDomain = String(demo.website || '').replace(/https?:\/\//, '').replace(/\/$/, '');

  if (realName) out = replaceAllCI(out, realName, demo.name);
  if (realUrl && demo.website) out = replaceAllCI(out, realUrl, demo.website);
  if (realDomain && demoDomain) out = replaceAllCI(out, realDomain, demoDomain);
  if (c.realNick) {
    const demoNick = realName ? c.realNick.replace(new RegExp(escapeRegex(realName), 'gi'), demo.name) : c.realNick;
    if (demoNick !== c.realNick) out = replaceAllCI(out, c.realNick, demoNick);
  }

  // The org name as it appears in a file name: "Arizona-Autism-Charter-Schools".
  if (realName) {
    const slugRe = new RegExp(realName.replace(/[^a-zA-Z0-9]/g, '[^a-zA-Z0-9]*'), 'gi');
    out = out.replace(slugRe, demo.name.replace(/[,.]\s*/g, ' ').replace(/\s+/g, '-'));
  }

  // Filenames that carry one identifying word rather than the whole name
  // ("Seaside School.pdf" for "Seaside Charter Schools"). Scoped to a token
  // that ends in a document extension — everywhere else the word is part of a
  // finding, and rewriting it would change what the report says.
  const STOP = ['inc','llc','corp','ltd','the','of','and','for','a','an','in','at','on','to',
                'group','company','services','insurance','school','schools','charter'];
  const isState = w => {
    const u = w.toUpperCase();
    if (typeof STATE_PROFILES === 'undefined') return false;
    if (STATE_PROFILES[u]) return true;
    return Object.keys(STATE_PROFILES).some(k => STATE_PROFILES[k].name.toLowerCase() === w.toLowerCase());
  };
  const words = realName.split(/[\s,.-]+/)
    .filter(w => w.length > 2 && !STOP.includes(w.toLowerCase()) && !isState(w));
  if (words.length) {
    const demoWords = demo.name.split(/[\s,.-]+/).filter(w => w.length > 2 && !STOP.includes(w.toLowerCase()));
    const demoWord = demoWords.length ? demoWords[0] : 'Demo';
    const wordRe = new RegExp('(' + words.map(escapeRegex).join('|') + ')', 'gi');
    // A filename: the run of text before a document extension, bounded by the
    // surrounding markup and capped so a whole paragraph that happens to end
    // in ".pdf" is not swept in. Uploaded names carry spaces ("Autism Benefit
    // Guide.pdf"), so the token cannot stop at whitespace. Every identifying
    // word inside becomes the demo word, and adjacent repeats collapse
    // ("Seaside-Seaside-Report.pdf").
    out = out.replace(
      new RegExp('[^<>]{0,80}\\.(?:pdf|docx?|xlsx?|csv|pptx?|txt)', 'gi'),
      token => token.replace(wordRe, demoWord)
        .replace(new RegExp('(' + escapeRegex(demoWord) + ')(?:[-_ ]+' + escapeRegex(demoWord) + ')+', 'gi'), '$1'));
  }
  return out;
};

function replaceAllCI(html, search, replacement) {
  if (!search) return html;
  const escaped = escapeRegex(search);
  return html.replace(new RegExp(escaped, 'gi'), replacement);
}

// ===== NEW REVIEW =====
function startNewReview() {
  document.getElementById('analysisForm').reset();
  document.getElementById('orgOverview').value = '';
  window._spOrgOverviewGenerated = false;
  window._spExcludedSources = [];
  // The E&O Mitigation Record reads both of these; a new review must not inherit
  // the previous client's recommendations or accept/decline decisions.
  window._spRecommendedGaps = [];
  window._spEoLast = null;
  document.getElementById('states').value = '';
  document.getElementById('policyholderName').value = '';
  document.getElementById('websiteUrl').value = '';
  document.getElementById('vertical').value = '';
  document.getElementById('caseNick').value = '';
  document.getElementById('incumbent').value = '';
  uploadedFiles = [];
  detectedCoverages = [];
  currentAnalysis = null;
  deepScanResults = null;
  window._currentVertical = '';
  window._currentOrgName = '';
  window._lastFormData = null;
  window._spSources = [];
  document.getElementById('fileList').innerHTML = '';
  document.getElementById('quickScreen').innerHTML = '';
  document.getElementById('fullReview').innerHTML = '';
  document.getElementById('execSummary').innerHTML = '';
  document.getElementById('resultsArea').style.display = 'none';
  document.getElementById('emptyState').style.display = 'flex';
  // Reset tab visibility
  const qsTabBtn = document.querySelector('[data-tab="quickScreen"]');
  const frTabBtn = document.querySelector('[data-tab="fullReview"]');
  const esTabBtn = document.querySelector('[data-tab="execSummary"]');
  if (qsTabBtn) qsTabBtn.style.display = '';
  if (frTabBtn) frTabBtn.style.display = '';
  if (esTabBtn) esTabBtn.style.display = '';
  switchTab('fullReview');
  window.scrollTo(0, 0);
}

/* Named exports for the tests and the React layer. The window.*
 * registrations inside stay — they are how the modules find each other. */
export {
  CORE_COVERAGES,
  COVERAGE_PATTERNS,
  DEFINITION_MARKERS,
  DEMO_PROFILES,
  DO_EXCLUSION_PATTERNS,
  DO_TEXT_INDICATORS,
  EB_CARRIERS,
  EB_EXCLUSION_ALERTS,
  EB_GAP_PRIORITY,
  EB_GAP_TO_ENHANCEMENT,
  EB_SUB_LABEL,
  EB_TABLE_DIVIDER,
  EO_EXCLUSION_PATTERNS,
  EO_TEXT_INDICATORS,
  EXCLUSION_ALERTS,
  FALLBACK_KEYWORDS,
  GL_SUBLIMIT_PATTERNS,
  NEGATIVE_CONTEXT_PHRASES,
  PC_EXCLUSION_CONTEXT,
  PC_EXCLUSION_TERM_OVERRIDES,
  SECTION_HEADING_PATTERNS,
  SP_ADVISORY_COPY,
  SP_ATOMIC_SELECTORS,
  SP_CARRIER_MARKETING,
  SP_CONCURRENT,
  SP_CONDITIONAL_PART,
  SP_DEAD_LIMIT,
  SP_DEFINITION_CONTEXT,
  SP_EBL_CONTEXT,
  SP_EXCLUSION_CONTEXT,
  SP_FLOW_CONTAINERS,
  SP_LIVE_LIMIT,
  SP_PRINT_PAGINATION_CSS,
  SP_TESTIMONIAL_HEADING,
  STATE_NAME_MAP,
  STATE_PROFILES,
  WEBSITE_RISK_PATTERNS,
  analyzeTriggers,
  analyzeWebsiteContent,
  buildEBEnhancements,
  buildEBGaps,
  buildWebsiteRiskSources,
  compareLimitToThreshold,
  deepScanForDO,
  deepScanForEO,
  detectCoverages,
  detectCoveragesFromPdfText,
  detectStates,
  spCanonStates,
  spStateDisplayName,
  detectUnrecognizedFiles,
  ebAllText,
  ebAlreadyInPlace,
  ebBenefitsFromTables,
  ebBoundedSection,
  ebBuildBlock,
  ebCarrierIn,
  ebDentalPlans,
  ebDetectCoverageLines,
  ebEsc,
  ebFieldId,
  ebFieldMap,
  ebFormatFromTables,
  ebLabelledRows,
  ebMeasuredSummary,
  ebMedicalPlans,
  ebPageForOffset,
  ebParseTableAfter,
  ebPlanNames,
  ebRenderBlock,
  ebRenderFieldMap,
  ebRenderLife,
  ebRowValues,
  ebSection,
  ebTableCells,
  ebTableStatus,
  ebVisionFields,
  escapeHtml,
  escapeRegex,
  extractAllPdfTexts,
  extractCarrierPerCoverage,
  extractDeclarationsLimits,
  extractEBAccounts,
  extractEBBenefitsData,
  extractEBDisability,
  extractEBLifeADD,
  extractEBPlanTables,
  ebTableIndex,
  ebIndexedTable,
  ebClassifyTable,
  extractEBRetirement,
  extractEndorsements,
  extractPdfText,
  extractPolicyTerms,
  extractRenewalPackagePlans,
  findNearestSection,
  formatBytes,
  formatEBBenefitsDisplay,
  formatExtractedLimits,
  formatRenewalPlansDisplay,
  generateEBExecSummary,
  generateEBFullReview,
  generateOrgOverview,
  generatePCExecSummary,
  generatePCFullReview,
  generateQuickScreen,
  generateReport,
  getEBExclusionAlerts,
  getEBExposureRange,
  getEBPremiumEstimate,
  getExclusionAlerts,
  getExposureRange,
  getPremiumEstimate,
  getPrimaryLimit,
  groundEBExclusion,
  handleCompactPDF,
  handleDemoPrint,
  handleFiles,
  handlePrint,
  isGLSublimitLine,
  isInBoilerplateListing,
  isInNegativeContext,
  parseDollarAmount,
  parseStates,
  removeFile,
  renderEBPlanTables,
  renderFileList,
  replaceAllCI,
  runStateComplianceChecks,
  showToast,
  spAttachLegacyHandlers,
  spExpandAllDetails,
  spFindInKind,
  spInCoveragePartList,
  spOn,
  spPlausibleLimit,
  spScheduleLimitState,
  spToggleAllDetails,
  startNewReview,
  switchTab,
};

/* ---- script-scope compatibility ----------------------------------------
 * The single-file build ran as one script, so every top-level declaration
 * here was a global that the later blocks could see. Two of them do, by
 * feature test rather than by import:
 *
 *   grounding.js:  (typeof STATE_PROFILES !== 'undefined') ? … : []
 *
 * As ES modules those references resolve to nothing, `typeof` returns
 * 'undefined', and state detection degrades silently to an empty list — a
 * wrong answer rather than an error, which is the worst shape of failure.
 * Publishing the declarations restores the scope the code was written
 * against, so the modules keep working without editing 190 call sites.
 *
 * This is the compatibility seam, and it is what phase 2 of MIGRATION.md
 * replaces with real imports.
 * -------------------------------------------------------------------- */
const _spScope = (typeof globalThis !== 'undefined' ? globalThis : window);
try { if (typeof _spScope.CORE_COVERAGES === 'undefined') _spScope.CORE_COVERAGES = CORE_COVERAGES; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.COVERAGE_PATTERNS === 'undefined') _spScope.COVERAGE_PATTERNS = COVERAGE_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.DEFINITION_MARKERS === 'undefined') _spScope.DEFINITION_MARKERS = DEFINITION_MARKERS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.DEMO_PROFILES === 'undefined') _spScope.DEMO_PROFILES = DEMO_PROFILES; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.DO_EXCLUSION_PATTERNS === 'undefined') _spScope.DO_EXCLUSION_PATTERNS = DO_EXCLUSION_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.DO_TEXT_INDICATORS === 'undefined') _spScope.DO_TEXT_INDICATORS = DO_TEXT_INDICATORS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_CARRIERS === 'undefined') _spScope.EB_CARRIERS = EB_CARRIERS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_EXCLUSION_ALERTS === 'undefined') _spScope.EB_EXCLUSION_ALERTS = EB_EXCLUSION_ALERTS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_GAP_PRIORITY === 'undefined') _spScope.EB_GAP_PRIORITY = EB_GAP_PRIORITY; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_GAP_TO_ENHANCEMENT === 'undefined') _spScope.EB_GAP_TO_ENHANCEMENT = EB_GAP_TO_ENHANCEMENT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_SUB_LABEL === 'undefined') _spScope.EB_SUB_LABEL = EB_SUB_LABEL; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EB_TABLE_DIVIDER === 'undefined') _spScope.EB_TABLE_DIVIDER = EB_TABLE_DIVIDER; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EO_EXCLUSION_PATTERNS === 'undefined') _spScope.EO_EXCLUSION_PATTERNS = EO_EXCLUSION_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EO_TEXT_INDICATORS === 'undefined') _spScope.EO_TEXT_INDICATORS = EO_TEXT_INDICATORS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.EXCLUSION_ALERTS === 'undefined') _spScope.EXCLUSION_ALERTS = EXCLUSION_ALERTS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.FALLBACK_KEYWORDS === 'undefined') _spScope.FALLBACK_KEYWORDS = FALLBACK_KEYWORDS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.GL_SUBLIMIT_PATTERNS === 'undefined') _spScope.GL_SUBLIMIT_PATTERNS = GL_SUBLIMIT_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.NEGATIVE_CONTEXT_PHRASES === 'undefined') _spScope.NEGATIVE_CONTEXT_PHRASES = NEGATIVE_CONTEXT_PHRASES; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.PC_EXCLUSION_CONTEXT === 'undefined') _spScope.PC_EXCLUSION_CONTEXT = PC_EXCLUSION_CONTEXT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.PC_EXCLUSION_TERM_OVERRIDES === 'undefined') _spScope.PC_EXCLUSION_TERM_OVERRIDES = PC_EXCLUSION_TERM_OVERRIDES; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SECTION_HEADING_PATTERNS === 'undefined') _spScope.SECTION_HEADING_PATTERNS = SECTION_HEADING_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_ADVISORY_COPY === 'undefined') _spScope.SP_ADVISORY_COPY = SP_ADVISORY_COPY; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_ATOMIC_SELECTORS === 'undefined') _spScope.SP_ATOMIC_SELECTORS = SP_ATOMIC_SELECTORS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_CARRIER_MARKETING === 'undefined') _spScope.SP_CARRIER_MARKETING = SP_CARRIER_MARKETING; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_CONCURRENT === 'undefined') _spScope.SP_CONCURRENT = SP_CONCURRENT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_CONDITIONAL_PART === 'undefined') _spScope.SP_CONDITIONAL_PART = SP_CONDITIONAL_PART; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_DEAD_LIMIT === 'undefined') _spScope.SP_DEAD_LIMIT = SP_DEAD_LIMIT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_DEFINITION_CONTEXT === 'undefined') _spScope.SP_DEFINITION_CONTEXT = SP_DEFINITION_CONTEXT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_EBL_CONTEXT === 'undefined') _spScope.SP_EBL_CONTEXT = SP_EBL_CONTEXT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_EXCLUSION_CONTEXT === 'undefined') _spScope.SP_EXCLUSION_CONTEXT = SP_EXCLUSION_CONTEXT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_FLOW_CONTAINERS === 'undefined') _spScope.SP_FLOW_CONTAINERS = SP_FLOW_CONTAINERS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_LIVE_LIMIT === 'undefined') _spScope.SP_LIVE_LIMIT = SP_LIVE_LIMIT; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_PRINT_PAGINATION_CSS === 'undefined') _spScope.SP_PRINT_PAGINATION_CSS = SP_PRINT_PAGINATION_CSS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.SP_TESTIMONIAL_HEADING === 'undefined') _spScope.SP_TESTIMONIAL_HEADING = SP_TESTIMONIAL_HEADING; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.STATE_NAME_MAP === 'undefined') _spScope.STATE_NAME_MAP = STATE_NAME_MAP; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.STATE_PROFILES === 'undefined') _spScope.STATE_PROFILES = STATE_PROFILES; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.WEBSITE_RISK_PATTERNS === 'undefined') _spScope.WEBSITE_RISK_PATTERNS = WEBSITE_RISK_PATTERNS; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.analyzeTriggers === 'undefined') _spScope.analyzeTriggers = analyzeTriggers; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.analyzeWebsiteContent === 'undefined') _spScope.analyzeWebsiteContent = analyzeWebsiteContent; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.buildEBEnhancements === 'undefined') _spScope.buildEBEnhancements = buildEBEnhancements; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.buildEBGaps === 'undefined') _spScope.buildEBGaps = buildEBGaps; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.buildWebsiteRiskSources === 'undefined') _spScope.buildWebsiteRiskSources = buildWebsiteRiskSources; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.compareLimitToThreshold === 'undefined') _spScope.compareLimitToThreshold = compareLimitToThreshold; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.currentAnalysis === 'undefined') _spScope.currentAnalysis = currentAnalysis; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.deepScanEOResults === 'undefined') _spScope.deepScanEOResults = deepScanEOResults; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.deepScanForDO === 'undefined') _spScope.deepScanForDO = deepScanForDO; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.deepScanForEO === 'undefined') _spScope.deepScanForEO = deepScanForEO; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.deepScanResults === 'undefined') _spScope.deepScanResults = deepScanResults; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.detectCoverages === 'undefined') _spScope.detectCoverages = detectCoverages; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.detectCoveragesFromPdfText === 'undefined') _spScope.detectCoveragesFromPdfText = detectCoveragesFromPdfText; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.detectStates === 'undefined') _spScope.detectStates = detectStates; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spCanonStates === 'undefined') _spScope.spCanonStates = spCanonStates; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spStateDisplayName === 'undefined') _spScope.spStateDisplayName = spStateDisplayName; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.detectUnrecognizedFiles === 'undefined') _spScope.detectUnrecognizedFiles = detectUnrecognizedFiles; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.detectedCoverages === 'undefined') _spScope.detectedCoverages = detectedCoverages; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebAllText === 'undefined') _spScope.ebAllText = ebAllText; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebAlreadyInPlace === 'undefined') _spScope.ebAlreadyInPlace = ebAlreadyInPlace; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebBenefitsFromTables === 'undefined') _spScope.ebBenefitsFromTables = ebBenefitsFromTables; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebBoundedSection === 'undefined') _spScope.ebBoundedSection = ebBoundedSection; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebBuildBlock === 'undefined') _spScope.ebBuildBlock = ebBuildBlock; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebCarrierIn === 'undefined') _spScope.ebCarrierIn = ebCarrierIn; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebDentalPlans === 'undefined') _spScope.ebDentalPlans = ebDentalPlans; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebDetectCoverageLines === 'undefined') _spScope.ebDetectCoverageLines = ebDetectCoverageLines; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebDollar === 'undefined') _spScope.ebDollar = ebDollar; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebEsc === 'undefined') _spScope.ebEsc = ebEsc; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebFieldId === 'undefined') _spScope.ebFieldId = ebFieldId; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebFieldMap === 'undefined') _spScope.ebFieldMap = ebFieldMap; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebFormatFromTables === 'undefined') _spScope.ebFormatFromTables = ebFormatFromTables; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebLabelledRows === 'undefined') _spScope.ebLabelledRows = ebLabelledRows; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebMeasuredSummary === 'undefined') _spScope.ebMeasuredSummary = ebMeasuredSummary; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebMedicalPlans === 'undefined') _spScope.ebMedicalPlans = ebMedicalPlans; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebPageForOffset === 'undefined') _spScope.ebPageForOffset = ebPageForOffset; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebParseTableAfter === 'undefined') _spScope.ebParseTableAfter = ebParseTableAfter; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebPct === 'undefined') _spScope.ebPct = ebPct; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebPlanNames === 'undefined') _spScope.ebPlanNames = ebPlanNames; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebRenderBlock === 'undefined') _spScope.ebRenderBlock = ebRenderBlock; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebRenderFieldMap === 'undefined') _spScope.ebRenderFieldMap = ebRenderFieldMap; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebRenderLife === 'undefined') _spScope.ebRenderLife = ebRenderLife; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebRowValues === 'undefined') _spScope.ebRowValues = ebRowValues; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebSection === 'undefined') _spScope.ebSection = ebSection; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebTableCells === 'undefined') _spScope.ebTableCells = ebTableCells; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebTableStatus === 'undefined') _spScope.ebTableStatus = ebTableStatus; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.ebVisionFields === 'undefined') _spScope.ebVisionFields = ebVisionFields; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.escapeHtml === 'undefined') _spScope.escapeHtml = escapeHtml; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.escapeRegex === 'undefined') _spScope.escapeRegex = escapeRegex; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractAllPdfTexts === 'undefined') _spScope.extractAllPdfTexts = extractAllPdfTexts; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractCarrierPerCoverage === 'undefined') _spScope.extractCarrierPerCoverage = extractCarrierPerCoverage; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractDeclarationsLimits === 'undefined') _spScope.extractDeclarationsLimits = extractDeclarationsLimits; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBAccounts === 'undefined') _spScope.extractEBAccounts = extractEBAccounts; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBBenefitsData === 'undefined') _spScope.extractEBBenefitsData = extractEBBenefitsData; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBDisability === 'undefined') _spScope.extractEBDisability = extractEBDisability; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBLifeADD === 'undefined') _spScope.extractEBLifeADD = extractEBLifeADD; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBPlanTables === 'undefined') _spScope.extractEBPlanTables = extractEBPlanTables; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEBRetirement === 'undefined') _spScope.extractEBRetirement = extractEBRetirement; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractEndorsements === 'undefined') _spScope.extractEndorsements = extractEndorsements; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractPdfText === 'undefined') _spScope.extractPdfText = extractPdfText; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractPolicyTerms === 'undefined') _spScope.extractPolicyTerms = extractPolicyTerms; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.extractRenewalPackagePlans === 'undefined') _spScope.extractRenewalPackagePlans = extractRenewalPackagePlans; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.fileInput === 'undefined') _spScope.fileInput = fileInput; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.findNearestSection === 'undefined') _spScope.findNearestSection = findNearestSection; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.formatBytes === 'undefined') _spScope.formatBytes = formatBytes; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.formatEBBenefitsDisplay === 'undefined') _spScope.formatEBBenefitsDisplay = formatEBBenefitsDisplay; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.formatExtractedLimits === 'undefined') _spScope.formatExtractedLimits = formatExtractedLimits; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.formatRenewalPlansDisplay === 'undefined') _spScope.formatRenewalPlansDisplay = formatRenewalPlansDisplay; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generateEBExecSummary === 'undefined') _spScope.generateEBExecSummary = generateEBExecSummary; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generateEBFullReview === 'undefined') _spScope.generateEBFullReview = generateEBFullReview; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generateOrgOverview === 'undefined') _spScope.generateOrgOverview = generateOrgOverview; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generatePCExecSummary === 'undefined') _spScope.generatePCExecSummary = generatePCExecSummary; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generatePCFullReview === 'undefined') _spScope.generatePCFullReview = generatePCFullReview; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generateQuickScreen === 'undefined') _spScope.generateQuickScreen = generateQuickScreen; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.generateReport === 'undefined') _spScope.generateReport = generateReport; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getEBExclusionAlerts === 'undefined') _spScope.getEBExclusionAlerts = getEBExclusionAlerts; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getEBExposureRange === 'undefined') _spScope.getEBExposureRange = getEBExposureRange; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getEBPremiumEstimate === 'undefined') _spScope.getEBPremiumEstimate = getEBPremiumEstimate; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getExclusionAlerts === 'undefined') _spScope.getExclusionAlerts = getExclusionAlerts; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getExposureRange === 'undefined') _spScope.getExposureRange = getExposureRange; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getPremiumEstimate === 'undefined') _spScope.getPremiumEstimate = getPremiumEstimate; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.getPrimaryLimit === 'undefined') _spScope.getPrimaryLimit = getPrimaryLimit; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.groundEBExclusion === 'undefined') _spScope.groundEBExclusion = groundEBExclusion; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.handleCompactPDF === 'undefined') _spScope.handleCompactPDF = handleCompactPDF; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.handleDemoPrint === 'undefined') _spScope.handleDemoPrint = handleDemoPrint; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.handleFiles === 'undefined') _spScope.handleFiles = handleFiles; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.handlePrint === 'undefined') _spScope.handlePrint = handlePrint; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.isGLSublimitLine === 'undefined') _spScope.isGLSublimitLine = isGLSublimitLine; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.isInBoilerplateListing === 'undefined') _spScope.isInBoilerplateListing = isInBoilerplateListing; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.isInNegativeContext === 'undefined') _spScope.isInNegativeContext = isInNegativeContext; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.isOGSupplier === 'undefined') _spScope.isOGSupplier = isOGSupplier; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.parseDollarAmount === 'undefined') _spScope.parseDollarAmount = parseDollarAmount; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.parseStates === 'undefined') _spScope.parseStates = parseStates; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.removeFile === 'undefined') _spScope.removeFile = removeFile; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.renderEBPlanTables === 'undefined') _spScope.renderEBPlanTables = renderEBPlanTables; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.renderFileList === 'undefined') _spScope.renderFileList = renderFileList; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.replaceAllCI === 'undefined') _spScope.replaceAllCI = replaceAllCI; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.runStateComplianceChecks === 'undefined') _spScope.runStateComplianceChecks = runStateComplianceChecks; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.showToast === 'undefined') _spScope.showToast = showToast; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spAttachLegacyHandlers === 'undefined') _spScope.spAttachLegacyHandlers = spAttachLegacyHandlers; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spExpandAllDetails === 'undefined') _spScope.spExpandAllDetails = spExpandAllDetails; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spFindInKind === 'undefined') _spScope.spFindInKind = spFindInKind; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spInCoveragePartList === 'undefined') _spScope.spInCoveragePartList = spInCoveragePartList; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spOn === 'undefined') _spScope.spOn = spOn; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spPlausibleLimit === 'undefined') _spScope.spPlausibleLimit = spPlausibleLimit; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spScheduleLimitState === 'undefined') _spScope.spScheduleLimitState = spScheduleLimitState; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.spToggleAllDetails === 'undefined') _spScope.spToggleAllDetails = spToggleAllDetails; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.startNewReview === 'undefined') _spScope.startNewReview = startNewReview; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.switchTab === 'undefined') _spScope.switchTab = switchTab; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.uploadZone === 'undefined') _spScope.uploadZone = uploadZone; } catch (e) { /* frozen scope */ }
try { if (typeof _spScope.uploadedFiles === 'undefined') _spScope.uploadedFiles = uploadedFiles; } catch (e) { /* frozen scope */ }
